// UnifiedApp — "linebrkr unified" UI (preview, behind ?ui=unified)
// Bottom nav: Picks / Games / Contact.
//   Picks   — leaderboard ranked by probability-to-go-over (primary), edge secondary
//   Games   — slate board → game modal → player modal
//   Contact — research hub: search a hitter → contact log + insights
// Player modal tabs: Intel / Score / Form / Park / Contact.
//
// Wired to the live API with sample fallback so it previews offline. Design
// tokens per the LINEBRKR UI brief.

import React, { useState, useEffect, useRef, useMemo } from 'react'
import ContactLogTab, { ContactLog, CONTACT_SAMPLE } from '../pages/ContactLogTab'
import { useFetch } from '../hooks/useFetch'
import { getUpcomingMatches, getPlayerContactLog, getPlayerInsights, getPlayerGamelogs, getPlayerSplits, getLeaderboard, getToday, getBookOdds, getScorecardSummary, getScorecardTrend, getTrending, getEliteRecord, getTrustAnchor, getGameRead, getHotCorner, getTrends, getPerfectStack, getPerfectStorm, getStormBacktest, getStormRecord, getPowerSpotsRecord, getPlayoffRecord, getSlateHrProjection, getSlateHrBacktest, getHrPickRecord, getTwoLegRecord, getGridRecord, getClv, getCoverage, getValidation, getLearnings, getLive, getAudit, getTeamAudit, setAccessCode, setAdminCode, listBetaCodes, createBetaCode, revokeBetaCode, enableBetaCode, getReferralsAdmin, referralPayout, makeRefCode, attributeReferral, getReferral, getBookLinks, startCheckout, claimSession, getFoundersStatus, getPlan, setPlanLocal, getPlanLocal, getFreePreview, getPitcherData, getGameOutcomeRecord, getBirthdays, getBallparkEvents, getPitcherIntel, getGamesModel, getHrValidation, getMarketCalibration, getGameModelBacktest, getKAnalytics, getKRecord, getHrSignals, getNlSearch, getValueBoard, getMatchup, getProjectedMatchup, getPlayerMarketAnalytics, getResultsDaily, fixBatsHandAll, fixPitcherHandsAll, setPitcherHand, reslateBoard, refreshLineups, scratchPlayer, readdPlayer, syncRosters, pullOdds, getOddsStatus, todayStr, getRecordDetail, yesterdayStr, mlbFreeSignup, getMLBFreeBoard, getFreeToken } from '../api/client'

// ── Subscription tiers ───────────────────────────────────────────────────────
// The current plan flows through this context. Pro-only tools call needPro():
//   onClick={()=>{ if(needPro()) openTheTool() }}
// needPro() returns true when allowed, else opens the upgrade sheet and returns false.
// founder is a discounted-for-life Pro: SAME access as pro (all tools incl. Trends +
// Contact), just a locked $10 price. Ranks equal to pro so every gate treats it as Pro.
const PLAN_RANK = { free:0, rookie:1, founder:3, pro:3, admin:4 }
const PlanCtx = React.createContext({ plan:'pro', isPro:true, isPremium:true, needPro:()=>true, needPremium:()=>true })
const useProGate = () => React.useContext(PlanCtx)
// Pricing go-live switch. FALSE (pre-launch) → the lock screen shows only the
// "enter your access code" prompt, so testers never see prices. Flip to TRUE to
// reveal the tiered paywall (Free look + Rookie/Pro cards) — all that code stays
// intact below, just gated by this one flag.
const PRICING_LIVE = true

// ── Design tokens (LINEBRKR brief) ──────────────────────────────────────────
const T = {
  bg:"#080C12", card:"#111827", card2:"#1A2130", card3:"#0C1F18",
  border:"#1E2A3A", border2:"#2A3548",
  green:"#00D97E", greenDim:"#00D97E18", greenDk:"#064E3B", greenMid:"#065F46", greenLt:"#DCFCE7",
  amber:"#F59E0B", amberDim:"#F59E0B18",
  red:"#EF4444", redDim:"#EF444418",
  blue:"#3B82F6", blueDim:"#3B82F618", blueLt:"#1E3A5F",
  orange:"#F97316", purple:"#8B5CF6",
  text:"#F1F5F9", muted:"#A6B2C4", muted2:"#CBD5E1", faint:"#475569",
  mono:"'SF Mono','Fira Code',monospace",
}

// Tier badges — icon + plain-English description for the legend
// Color scale is semantic: ELITE tier = blue (premium), STRONG/PLAY = greens
// (good), WATCH = amber (neutral), VALUE SHOT = orange (special longshot value),
// PASS = red. Nothing "good" reads orange.
const TIER = {
  "ELITE":     {bg:"#DBEAFE", fg:"#1E3A8A", bd:"#2563EB", icon:"◆",  desc:"Highest model conviction — high probability + confidence"},
  "BEST BET":  {bg:"#DBEAFE", fg:"#1E40AF", bd:"#3B82F6", icon:"🔥", desc:"High model rating — strong probability + confidence"},
  "STRONG":    {bg:"#DCFCE7", fg:"#005C3A", bd:"#00D97E", icon:"✅", desc:"Strong model rating"},
  "PLAY":      {bg:"#D1FAE5", fg:"#065F46", bd:"#34D399", icon:"👀", desc:"Moderate model rating"},
  "WATCH":     {bg:"#FEF3C7", fg:"#7C3B0A", bd:"#F59E0B", icon:"👁", desc:"Thin data or no line yet"},
  "PRIME SHOT": {bg:"#FEF3C7", fg:"#7C4A03", bd:"#FBBF24", icon:"★",  desc:"Best power spot on the board — elite probability FOR a longshot market (still never as safe as a hit market)"},
  "VALUE SHOT":{bg:"#FFEDD5", fg:"#7C2D12", bd:"#F97316", icon:"↑",  desc:"Low-probability longshot; large gap to the book price"},
  "PASS":      {bg:"#FEE2E2", fg:"#7F1D1D", bd:"#EF4444", icon:"✕",  desc:"Below the model's threshold"},
}
// Display labels — informational, not imperative ("BEST BET"→"TOP RATED", etc.).
// Internal keys above stay unchanged so all ranking/logic is untouched.
const TIER_LABEL = {
  "ELITE":"ELITE", "BEST BET":"TOP RATED", "STRONG":"STRONG", "PLAY":"MODERATE",
  "WATCH":"WATCH", "PRIME SHOT":"PRIME SHOT", "VALUE SHOT":"LONGSHOT", "PASS":"LOW",
}
const tierLabel = t => TIER_LABEL[t] || t
// LINEBRKR Rating fallback (0–100): the heuristic used ONLY when the backend
// confidence_score is absent. Composed from the certainty signals we have —
// lineup confirmed, pitcher confirmed, line posted, plus model strength.
// Deliberately edge-free (price/value is shown separately). The real Rating is
// the backend's 7-factor confidence_score; this just keeps a number on screen.
const confidenceFor = e => {
  let c = 64
  c += (e.lineup_slot!=null) ? 12 : -8            // lineup certainty
  c += e.opp_pitcher_name ? 9 : -10               // pitcher certainty
  c += (e.book_over!=null) ? 9 : -14              // line confirmed (not value)
  if (e.prob_over!=null) c += Math.round(Math.min(10, Math.max(-6, (e.prob_over-0.55)*26)))  // model strength
  return Math.max(42, Math.min(99, Math.round(c)))
}
// Prefer the backend's real 7-factor Confidence Score; fall back to the heuristic
const confOf = e => (e && e.confidence_score!=null) ? e.confidence_score : confidenceFor(e)
// Elite (85+) = blue · Strong (70+) = green · Good (55+) = soft green · Average (42+) = amber · Poor = red
const confColor = c => c>=85?T.blue : c>=70?T.green : c>=55?"#34D399" : c>=42?T.amber : T.red
// ── LINEBRKR Rating — the ONE rating ────────────────────────────────────────
// 0–100: how good this spot is to hit. It IS the backend confidence_score (model
// conviction + matchup + zone fit + form + park + data completeness, thin-sample
// guarded), surfaced everywhere under one name. Price/value (EV/edge%) is shown
// SEPARATELY and never blended in — blending is exactly what spawned the old
// Final / Value / LB scores. Tier is this rating's categorical face.
const lbRating = confOf
const ratingColor = confColor
const ratingLabel = c => c>=85?"Elite" : c>=70?"Strong" : c>=55?"Solid" : c>=42?"Fair" : "Low"
// Tier from probability + confidence — NOT the book line. Confidence already folds
// in whether we have a price/lineup (its data-quality pillar), so the tier reflects
// how good the bet is to HIT. Before lineups/odds post the ceiling is naturally a bit
// lower (data incomplete), so the cream graduates to BEST BET / ELITE once confirmed.
// Low-base markets (HR, 2+ H) can't clear the probability bars, so a high-value one
// becomes a VALUE SHOT (orange) — never ELITE — so it can't look safer than a 73% HRR.
const tierFor = (prob, conf, market, edgePct, hasLine) => {
  const p = prob||0, c = conf||0
  const power = (market==="HR" || market==="2B" || market==="2+ H")
  if (p>=0.75 && c>=90) return "ELITE"
  if (p>=0.70 && c>=82) return "BEST BET"
  if (p>=0.65 && c>=70) return "STRONG"
  // Rating-led STRONG: the board ranks by Rating, so a genuinely high-Rating hit-floor
  // play (72+) with a solid probability shouldn't badge "MODERATE" just for missing the
  // 65% prob gate by a point (a 74-rated 64% HRR is a STRONG read, not a moderate one).
  // Power markets are excluded — they keep their own PRIME/VALUE SHOT ladder below.
  if (!power && c>=72 && p>=0.60) return "STRONG"
  // Market-relative 🔥 ladder for HRR and TB. Their probability ceilings sit well
  // below the 65% STRONG gate (HRR tops ~65%, TB ~55%), so their best plays never
  // light up on an absolute scale. Give them their own PRIME (🔥) / VALUE floors,
  // tuned to each market's distribution, BEFORE the generic PLAY tier intercepts.
  if (market==="HRR") { if (p>=0.58) return "PRIME SHOT"; if (p>=0.50) return "VALUE SHOT" }
  if (market==="TB")  { if (p>=0.50) return "PRIME SHOT"; if (p>=0.44) return "VALUE SHOT" }
  if (p>=0.58 && c>=58) return "PLAY"
  // Power markets are longshots by nature — never promoted to the "likely to hit"
  // tiers above, so a 30% HR can't look as safe as a 70% HRR. But they get their OWN
  // graded ladder so the cream is distinguishable from a dart-throw: PRIME SHOT =
  // elite probability FOR that market (or a big priced edge ON A REAL POWER SPOT);
  // VALUE SHOT = a real, lesser longshot. HR ≥ ~12% (above the ~10% league baseline)
  // is a genuine homer LONGSHOT, not a "LOW"/PASS — a 14% HR with a strong case must
  // not badge red and contradict "The Case". 25%+ ≈ prime homer spot.
  // NOTE: edge only PROMOTES a play that already clears the power floor — a phantom
  // edge on a longshot (common before HR calibration lands) must NOT light up 🔥,
  // otherwise a whole lineup can read as PRIME SHOT.
  if (power) {
    const primeFloor = market==="HR" ? 0.25 : market==="2+ H" ? 0.55 : market==="2B" ? 0.22 : 1.1
    // powerFloor = the market's ~league-average baseline; above it is a graded LONGSHOT,
    // below it is a genuine PASS. HR lowered 0.18 → 0.12 so above-average homers (with a
    // real case) read "LONGSHOT", not red "LOW".
    const powerFloor = market==="HR" ? 0.12 : market==="2+ H" ? 0.40 : market==="2B" ? 0.15 : 1.1
    // Edge can PROMOTE to prime, but ONLY a genuinely strong power spot — it needs a
    // BIG edge (≥15%) AND a probability near the prime bar. Otherwise an uncalibrated
    // edge across a whole lineup lights everyone up (the per-pitcher cap in the grid
    // is the second guard).
    const edgeFloor = market==="HR" ? 0.24 : market==="2+ H" ? 0.50 : market==="2B" ? 0.19 : 1.1
    const bigEdge = edgePct!=null && edgePct>=15 && hasLine && p>=edgeFloor
    if (p>=primeFloor || bigEdge) return "PRIME SHOT"
    if (p>=powerFloor) return "VALUE SHOT"
  }
  if (p>=0.55 || c>=55) return "WATCH"
  return "PASS"
}
// Value flag — completely separate from the tier. The TIER says "will it hit"
// (probability + confidence); this 💰 says "there's also a price edge vs the book".
// 💰 = solid value, 💰💰 = big value. Shown only when the edge is meaningful.
const valueFlag = edgePct => {
  if (edgePct==null) return null
  if (edgePct>=12) return {icon:"💰💰", edge:edgePct}
  if (edgePct>=4)  return {icon:"💰",   edge:edgePct}
  return null
}
// "Why It Rates" bullets from the raw entry — [ok, text] pairs
const buildWhy = (e, conf) => {
  const why=[]
  const edge=(e.edge_pct!=null)?e.edge_pct*100:null
  if(edge!=null) why.push([edge>0, edge>0?`Positive model edge (+${edge.toFixed(1)}%)`:`No edge vs the line (${edge.toFixed(1)}%)`])
  why.push([conf>=75, conf>=75?`High-confidence setup (${conf})`:`Moderate confidence (${conf})`])
  if(e.prob_over!=null) why.push([e.prob_over>=0.6, `Model probability ${Math.round(e.prob_over*100)}% to go over`])
  why.push([e.lineup_slot!=null, e.lineup_slot!=null?"Lineup confirmed":"Lineup not yet confirmed"])
  if((e.park_factor||1)>=1.03) why.push([true, `Park / weather boost (${(e.park_factor).toFixed(2)}x)`])
  return why
}

// Doubles (2B) dropped — replaced by Hits (1+ / 2+). Re-add ["2B","doubles"]
// to bring it back. "1+ H" / "2+ H" map to backend prop_types hits / hits_1.5.
const MARKETS = [
  ["HRR","HRR_over_1.5"], ["HR","HR_anytime"], ["1+ H","hits"], ["2+ H","hits_1.5"], ["TB","total_bases"], ["K","strikeouts"],
]
const MARKET_LABEL = {HRR:"HRR", HR:"HR", "1+ H":"1+ Hits", "2+ H":"2+ Hits", TB:"Bases", K:"K"}
// User-facing market label. The internal key stays "TB" (Total Bases) for all logic, but
// we DISPLAY it as "Bases" so it never collides with the Tampa Bay team abbreviation (also
// "TB") in the same card. Only TB changes; every other market shows its usual short token.
const mDisp = m => m==="TB" ? "Bases" : (m||"")
// Compliance footer appended to every copied/shared export so the disclaimer travels
// with the text when it's pasted into a tweet, Discord, etc. — not just shown on-screen.
const EXPORT_DISCLAIMER = "\n\n— LINEBRKR · research only, not betting advice · 21+ · 1-800-GAMBLER"
// De-accent + lowercase for search matching (so "Pena" finds "Peña"). Module-level so every
// board's search box shares it; a couple of components also define a local copy (harmless).
const _deacc = s => (s||"").normalize("NFD").replace(/[̀-ͯ]/g,"").toLowerCase()
// Canonical over/under line per market (HR anytime is always O0.5). Used to keep
// each market clean — rows whose line doesn't match are filtered out. K varies.
const CANON_LINE = { HRR:1.5, HR:0.5, "1+ H":0.5, "2+ H":1.5, TB:1.5 }
// Markets the Home dashboard scans across (hitter markets; no single-market bias)
const HOME_MARKETS = [["HRR","HRR_over_1.5"], ["HR","HR_anytime"], ["1+ H","hits"], ["2+ H","hits_1.5"], ["TB","total_bases"]]
const TIER_RANK = { ELITE:0, "BEST BET":1, STRONG:2, PLAY:3, WATCH:4, "PRIME SHOT":5, "VALUE SHOT":6, PASS:7, "NO BET":7 }
// Fair American odds from a probability
const toAmerican = p => { if(!p) return "—"; const dec=1/p; return dec>=2 ? `+${Math.round((dec-1)*100)}` : `-${Math.round(100/(dec-1))}` }
// Sort options (Best Contact unlocks with the scoring engine)
const SORT_OPTIONS = [["prob","Highest Probability"], ["overall","Best Overall"], ["conf","Highest Rating"], ["value","Best Value"]]
const SORT_LABEL = Object.fromEntries(SORT_OPTIONS)

const fmtOdds = v => v==null ? "—" : v>0 ? `+${v}` : `${v}`
// Turn a raw board entry (leaderboard/flat pool) into the rich player-card payload
// openPlayer expects — so The Case + odds match the play's market. Shared by every
// surface that opens a card from a board row (Home rankings, Pitchers-to-attack, …).
const playCardPayload = e => { const c=confOf(e); return {
  name:e.full_name, player_id:e.player_id, mlbam_id:e.mlbam_id, market:e.market, tier:e.tier, conf:c, why:buildWhy(e,c),
  prob:e.prob_over, edge:(e.edge_pct!=null)?`${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}%`:undefined,
  odds:fmtOdds(e.book_over), oddsNum:e.book_over, oppHand:e.opp_pitcher_hand,
  opp:e.opp_pitcher_name?`${e.team} · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:`${e.team} · vs TBD`,
  slot:e?.inputs?.lineup_slot, park:e?.inputs?.park_factor, team:e.team, oppPitcher:e.opp_pitcher_name,
} }
// Total return on a $100 stake at american odds (stake + profit). +290 → $390, -150 → $167.
const payout100 = odds => odds==null ? null : Math.round(odds>0 ? 100+odds : 100 + 10000/Math.abs(odds))
// Pretty names for the "best price @ book" line-shopping display.
const BOOK_NAMES = { draftkings:"DraftKings", fanduel:"FanDuel", betmgm:"BetMGM", williamhill_us:"Caesars", barstool:"ESPN BET", fanatics:"Fanatics", betonlineag:"BetOnline", bovada:"Bovada", mybookieag:"MyBookie", lowvig:"LowVig", betus:"BetUS", pointsbetus:"PointsBet", unibet_us:"Unibet", hardrockbet:"Hard Rock", espnbet:"ESPN BET", novig:"Novig", prophetx:"ProphetX", ballybet:"Bally Bet", betrivers:"BetRivers", fliff:"Fliff" }
const bookName = k => k ? (BOOK_NAMES[k] || k) : null
// One-tap line shopping — a book key → its site (book-level, not bet-slip deeplinks).
const BOOK_URLS = { draftkings:"https://sportsbook.draftkings.com/", fanduel:"https://sportsbook.fanduel.com/",
  betmgm:"https://sports.betmgm.com/", williamhill_us:"https://sportsbook.caesars.com/", caesars:"https://sportsbook.caesars.com/",
  espnbet:"https://espnbet.com/", barstool:"https://espnbet.com/", fanatics:"https://sportsbook.fanatics.com/",
  betonlineag:"https://www.betonline.ag/sportsbook", bovada:"https://www.bovada.lv/sports", mybookieag:"https://www.mybookie.ag/sportsbook/",
  lowvig:"https://www.lowvig.ag/", betus:"https://www.betus.com.pa/sportsbook/", pointsbetus:"https://fanatics.com/",
  unibet_us:"https://www.unibet.com/", hardrockbet:"https://app.hardrock.bet/", novig:"https://novig.us/",
  prophetx:"https://www.prophetx.co/", ballybet:"https://play.ballybet.com/", betrivers:"https://betrivers.com/", fliff:"https://www.getfliff.com/" }
let _affilBooks = {}
const setBookAffiliates = m => { _affilBooks = m || {} }
const bookUrl = k => (_affilBooks && _affilBooks[k]) || BOOK_URLS[k] || null
const impliedProb = a => a==null ? null : a>0 ? 100/(a+100) : (-a)/((-a)+100)
const pct = v => `${Math.round((v||0)*100)}%`
const fmtTime = iso => { if(!iso) return ""; try { return new Date(iso).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',timeZone:'America/New_York'})+" ET" } catch { return "" } }
const fmtDate = () => new Date().toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',timeZone:'America/New_York'})
// "2026-07-02" → "Wed, Jul 2" (noon-anchored so the ISO day doesn't shift by TZ)
const fmtDayLabel = iso => { if(!iso) return ""; try { return new Date(iso+"T12:00:00").toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'}) } catch { return iso } }
// Semantic colors: positive → green, negative → red, middle → orange
const probColor = p => p>=0.60 ? T.green : p<=0.45 ? T.red : T.amber
// Market-aware prob coloring. Base rates differ hugely, so the flat 60/45 scale
// mislabels low-base-rate markets — a 24% HR is a STRONG spot, not red. Bands are
// [green_at, red_below] per market; amber between. Accepts the short label ("HR")
// or the db prop_type ("HR_anytime"); an unknown market falls back to the flat scale.
const PROB_BANDS = { HR:[0.18,0.11], HRR:[0.52,0.38], "1+ H":[0.68,0.52], "2+ H":[0.30,0.18], TB:[0.50,0.34], K:[0.58,0.45] }
const _MKT_TO_LABEL = { "HR_anytime":"HR", "HRR_over_1.5":"HRR", "hits":"1+ H", "hits_1.5":"2+ H", "total_bases":"TB", "strikeouts":"K" }
const probColorMkt = (p, mkt) => {
  if(p==null) return T.muted2
  const label = PROB_BANDS[mkt] ? mkt : (_MKT_TO_LABEL[mkt] || mkt)
  const b = PROB_BANDS[label]
  if(!b) return probColor(p)
  return p>=b[0] ? T.green : p<=b[1] ? T.red : T.amber
}
// GE_COLS entries only receive the value, so the shared "Prob" column can't know the
// market — leaving a strong 24% HR painted red. Swap that one column's color for the
// market-aware scale wherever GE_COLS drives a grid (Game Explorer + Compare).
const geProbAwareCols = (cols, mktLabel) => cols.map(c => c && c[0]==="prob_over"
  ? [c[0], c[1], c[2], c[3], v=>v==null?T.muted2:probColorMkt(v, mktLabel), c[5]]
  : c)
// "Volume, not value" trap: a high hit-chance paired with a low overall Rating — the
// raw prob looks juicy but the model doesn't rate the SPOT (bad matchup/park/thin data
// or an unconfirmed lineup). These float up a prob-sorted list and look like sleepers
// when they aren't. True when prob clears the market's green band AND Rating < Solid.
const RATING_TRAP_MAX = 55
const isProbTrap = (prob, rating, mktLabel) => {
  if(prob==null || rating==null) return false
  const band = PROB_BANDS[_MKT_TO_LABEL[mktLabel] || mktLabel]
  const highProb = band ? prob >= band[0] : prob >= 0.55
  return highProb && rating < RATING_TRAP_MAX
}
// ── What-if (client estimate) — mirrors the app's Poisson modeling ───────────
// Invert a prob to the Poisson rate λ it implies at the market's line, rescale λ
// by plate appearances (batting order) and park, recompute the tail. Transparent
// and on-brand, but an ESTIMATE — the nightly model is the source of truth.
const PA_BY_SLOT = [0,4.65,4.55,4.44,4.34,4.23,4.13,4.02,3.92,3.81]  // index = slot 1–9
const poissonTail = (lam, L) => L<1 ? 1-Math.exp(-lam) : 1-Math.exp(-lam)*(1+lam)  // P(≥1) / P(≥2)
const invPoisson = (p, L) => {
  const pc = Math.max(1e-4, Math.min(0.9999, p))
  if(L<1) return -Math.log(1-pc)
  let lo=0, hi=12
  for(let k=0;k<48;k++){ const mid=(lo+hi)/2; if(poissonTail(mid,L)<pc) lo=mid; else hi=mid }
  return (lo+hi)/2
}
const edgeColor = e => e==null ? T.muted2 : e>=1 ? T.green : e<=-1 ? T.red : T.orange   // e in % points
const parkColor = pf => pf==null ? T.muted2 : pf>=1.03 ? T.green : pf<=0.97 ? T.red : T.orange
const num = v => { const n=parseFloat(v); return isNaN(n)?null:n }

// ── Share a play ─────────────────────────────────────────────────────────────
// Draw a branded LINEBRKR card for a single play on an offscreen canvas and hand
// it to the phone's native share sheet (Web Share API). No backend, no deps — the
// PNG is generated client-side. Falls back to text+link share, then to download,
// so it degrades on desktop / unsupported browsers instead of doing nothing.
const _rr = (ctx,x,y,w,h,r) => { r=Math.min(r,h/2,w/2); ctx.beginPath(); ctx.moveTo(x+r,y); ctx.arcTo(x+w,y,x+w,y+h,r); ctx.arcTo(x+w,y+h,x,y+h,r); ctx.arcTo(x,y+h,x,y,r); ctx.arcTo(x,y,x+w,y,r); ctx.closePath() }
const _wrap = (ctx,text,maxW) => { const words=(text||"").split(" "); let line="",out=[]; for(const w of words){ const t=line?line+" "+w:w; if(ctx.measureText(t).width>maxW && line){ out.push(line); line=w } else line=t } if(line) out.push(line); return out }
const _ellip = (ctx,text,maxW) => { if(!text||ctx.measureText(text).width<=maxW) return text||""; let s=text; while(s.length>1 && ctx.measureText(s+"…").width>maxW) s=s.slice(0,-1); return s+"…" }
function drawShareCard(play){
  const W=1080, PAD=72, SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const factors=(play.factors||[]).filter(f=>f&&f.label).slice(0,4)
  const hasForm=play.form && play.form.total>0
  // ── measure pass: name wrap + total height, so the layout is content-driven ──
  const m=document.createElement("canvas").getContext("2d")
  m.font=`900 66px ${SANS}`
  const nameLines=_wrap(m,play.name,W-2*PAD)
  const gap=20, th=178, caseHdr=88, caseRow=78
  let H=PAD+52 + 62          // logo + date row
  H += 8 + nameLines.length*70
  if(play.matchup) H += 42
  H += 40 + 2*th+gap         // stat grid
  if(hasForm) H += 30 + 118
  if(factors.length) H += 24 + caseHdr + factors.length*caseRow + 22
  H += 44 + 84 + 46 + PAD    // CTA + disclaimer + bottom pad
  H=Math.max(H, 1180)
  // ── draw ──
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W,H)
  const glow=ctx.createRadialGradient(W*0.86,-60,40, W*0.86,-60,780)
  glow.addColorStop(0,"rgba(0,217,126,0.16)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W,H)
  const x=PAD; let y=PAD+52
  // logo
  ctx.font=`900 54px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,y)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,y)
  ctx.font=`800 18px ${SANS}`; ctx.fillStyle=T.faint; ctx.textAlign="right"
  ctx.fillText("MLB PROP RESEARCH",W-PAD,PAD+42); ctx.textAlign="left"
  // date + market pill
  y+=52; const dateStr=fmtDate()
  ctx.font=`700 22px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText(dateStr,x,y)
  const mkt=(play.market||"").toString()
  if(mkt){ ctx.font=`800 20px ${SANS}`; const mw=ctx.measureText(mkt).width, px=W-PAD-mw-32, py=y-24
    ctx.fillStyle=T.greenDim; _rr(ctx,px,py,mw+32,36,18); ctx.fill()
    ctx.strokeStyle=T.greenMid; ctx.lineWidth=1.5; _rr(ctx,px,py,mw+32,36,18); ctx.stroke()
    ctx.fillStyle=T.green; ctx.fillText(mkt,px+16,y-1) }
  // name
  y+=8; ctx.fillStyle=T.text; ctx.font=`900 66px ${SANS}`
  for(const ln of nameLines){ y+=70; ctx.fillText(ln,x,y) }
  // matchup
  if(play.matchup){ y+=42; ctx.font=`600 28px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText(_ellip(ctx,play.matchup,W-2*PAD),x,y) }
  // stat grid
  y+=40; const tw=(W-2*PAD-gap)/2
  const tiles=[
    {label:"PROB OVER", value:play.probStr||"—", color:play.probColor||T.text},
    {label:"RATING", value:play.rating!=null?String(play.rating):"—", color:play.ratingColor||T.text},
    {label:"EDGE", value:play.edgeStr||"—", color:play.edgeColor||T.muted2},
    {label:"BEST LINE", value:play.lineStr||"—", color:T.text},
  ]
  tiles.forEach((t,i)=>{ const tx=x+(i%2)*(tw+gap), ty=y+Math.floor(i/2)*(th+gap)
    ctx.fillStyle=T.card; _rr(ctx,tx,ty,tw,th,20); ctx.fill()
    ctx.strokeStyle=T.border2; ctx.lineWidth=1.5; _rr(ctx,tx,ty,tw,th,20); ctx.stroke()
    ctx.font=`800 20px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText(t.label,tx+26,ty+46)
    ctx.font=`900 70px ${MONO}`; ctx.fillStyle=t.color; ctx.fillText(_ellip(ctx,t.value,tw-52),tx+26,ty+128) })
  y+=2*th+gap
  if(play.book){ ctx.font=`700 22px ${SANS}`; ctx.fillStyle=T.muted2; ctx.textAlign="right"
    ctx.fillText(`★ Best price @ ${play.book}`, W-PAD, y-6); ctx.textAlign="left" }
  // recent form + last-N dots
  if(hasForm){ y+=30
    const f=play.form
    ctx.font=`800 20px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText("RECENT FORM",x,y)
    y+=52; ctx.font=`900 40px ${SANS}`; ctx.fillStyle=T.text
    ctx.fillText(`Hit ${f.marketLabel||""} in ${f.hits} of last ${f.total}`,x,y)
    const dots=(f.recent||[]).slice().reverse(); const ds=30, dg=10, dy=y+22
    dots.forEach((w,i)=>{ const dx=x+i*(ds+dg)
      ctx.fillStyle=w?T.green:"rgba(239,68,68,0.55)"; _rr(ctx,dx,dy,ds,ds,8); ctx.fill() })
    y+=66
  }
  // The Case
  if(factors.length){ y+=24
    const gr=play.greens, rd=play.reds
    ctx.font=`900 30px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("The Case",x,y)
    if(play.verdict){ ctx.font=`800 22px ${SANS}`; ctx.fillStyle=T.green; ctx.textAlign="right"; ctx.fillText(play.verdict,W-PAD,y); ctx.textAlign="left" }
    y+=14; ctx.font=`700 18px ${SANS}`; ctx.fillStyle=T.muted
    const tally=[gr!=null?`${gr} green`:null, rd?`${rd} concern`:null].filter(Boolean).join("   ·   ")
    if(tally) ctx.fillText(tally,x,y+22)
    y+=48
    factors.forEach(fc=>{ const good=fc.tone==="good", bad=fc.tone==="bad"
      const col=good?T.green:bad?"#ef6a6a":T.muted2, mark=good?"✓":bad?"✕":"•"
      // chip
      ctx.fillStyle=good?T.greenDim:bad?"rgba(239,106,106,0.12)":T.card; _rr(ctx,x,y,34,34,9); ctx.fill()
      ctx.font=`900 22px ${SANS}`; ctx.fillStyle=col; ctx.textAlign="center"; ctx.fillText(mark,x+17,y+25); ctx.textAlign="left"
      // label + detail
      ctx.font=`800 24px ${SANS}`; ctx.fillStyle=col; ctx.fillText(_ellip(ctx,fc.label,W-2*PAD-52),x+50,y+18)
      if(fc.detail){ ctx.font=`600 20px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText(_ellip(ctx,fc.detail,W-2*PAD-52),x+50,y+48) }
      y+=caseRow })
    y+=22
  }
  // CTA (pinned to bottom)
  const cy=H-PAD-46-84-8
  ctx.fillStyle=T.greenDim; _rr(ctx,PAD,cy,W-2*PAD,84,18); ctx.fill()
  ctx.strokeStyle=T.green; ctx.lineWidth=2; _rr(ctx,PAD,cy,W-2*PAD,84,18); ctx.stroke()
  ctx.textAlign="center"; ctx.font=`900 32px ${SANS}`; ctx.fillStyle=T.green
  ctx.fillText("Full board — free  ·  linebrkr.app",W/2,cy+53)
  ctx.font=`600 20px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Research, not betting advice · 21+ · 1-800-GAMBLER",W/2,H-PAD-14)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function sharePlay(play){
  let blob=null
  try { blob = await drawShareCard(play) } catch(_){}
  const probStr = play.probStr && play.probStr!=="—" ? ` · ${play.probStr}` : ""
  const text = `${play.name} — ${play.market}${probStr} on LINEBRKR. Full board, free → linebrkr.app`
  const file = blob ? new File([blob], `linebrkr-${(play.name||"play").replace(/[^a-z0-9]+/gi,"-").toLowerCase()}.png`, {type:"image/png"}) : null
  try {
    if(file && navigator.canShare && navigator.canShare({files:[file]})){
      await navigator.share({files:[file], text}); return "shared"
    }
  } catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  try {
    if(navigator.share){ await navigator.share({text, url:"https://linebrkr.app"}); return "shared" }
  } catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  // desktop / unsupported → download the image if we have it, else copy the text
  if(blob){
    const url=URL.createObjectURL(blob); const a=document.createElement("a")
    a.href=url; a.download=file.name; document.body.appendChild(a); a.click(); a.remove()
    setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded"
  }
  try { await navigator.clipboard.writeText(text); return "copied" } catch(_){ return "failed" }
}

// ── Share the daily top-10 board ─────────────────────────────────────────────
// A branded, text-only board image (no photos — cross-origin headshots would
// taint the canvas and break toBlob). One tap → phone share sheet. This is the
// daily-post growth engine: post the free top 10 before first pitch.
function drawBoardCard(payload){
  const {title, subtitle, rows} = payload
  const W=1080, PAD=64, rowH=94, rowGap=12, SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const n=rows.length
  let H=PAD+52+58 + 74           // logo + title block
  H += n*(rowH+rowGap)
  H += 30 + 84 + 46 + PAD        // gap + CTA + disclaimer + pad
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W,H)
  const glow=ctx.createRadialGradient(W*0.86,-60,40, W*0.86,-60,820)
  glow.addColorStop(0,"rgba(0,217,126,0.15)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W,H)
  const x=PAD; let y=PAD+50
  ctx.font=`900 50px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,y)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,y)
  ctx.font=`800 18px ${SANS}`; ctx.fillStyle=T.faint; ctx.textAlign="right"; ctx.fillText("MLB PROP RESEARCH",W-PAD,PAD+40); ctx.textAlign="left"
  y+=64; ctx.font=`900 44px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText(title,x,y)
  y+=36; ctx.font=`700 24px ${SANS}`; ctx.fillStyle=T.green; ctx.fillText(subtitle,x,y)
  y+=30
  rows.forEach(r=>{
    const ry=y; y+=rowH
    ctx.fillStyle=r.rank<=3?"rgba(0,217,126,0.07)":T.card; _rr(ctx,x,ry,W-2*PAD,rowH,16); ctx.fill()
    ctx.strokeStyle=r.rank<=3?T.greenMid:T.border2; ctx.lineWidth=1.5; _rr(ctx,x,ry,W-2*PAD,rowH,16); ctx.stroke()
    // rank
    ctx.font=`900 34px ${MONO}`; ctx.fillStyle=r.rank<=3?T.green:T.muted; ctx.textAlign="center"
    ctx.fillText(`${r.rank}`, x+46, ry+rowH/2+12); ctx.textAlign="left"
    // name + sub
    ctx.font=`800 30px ${SANS}`; ctx.fillStyle=T.text
    ctx.fillText(_ellip(ctx,r.name,W-2*PAD-360), x+92, ry+40)
    ctx.font=`600 20px ${SANS}`; ctx.fillStyle=T.muted
    ctx.fillText(_ellip(ctx,`${r.sub} · ${r.market}`,W-2*PAD-360), x+92, ry+70)
    // prob (right) + rating
    ctx.textAlign="right"
    ctx.font=`900 40px ${MONO}`; ctx.fillStyle=r.probColor||T.text; ctx.fillText(r.probStr, W-PAD-118, ry+rowH/2+2)
    ctx.font=`700 15px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText("PROB", W-PAD-118, ry+rowH/2+24)
    if(r.rating!=null){ ctx.font=`900 34px ${MONO}`; ctx.fillStyle=r.ratingColor||T.muted2; ctx.fillText(String(r.rating), W-PAD-16, ry+rowH/2+2)
      ctx.font=`700 15px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText("RATING", W-PAD-16, ry+rowH/2+24) }
    ctx.textAlign="left"
  })
  y+=30
  const cy=H-PAD-46-84-8
  ctx.fillStyle="rgba(0,217,126,0.10)"; _rr(ctx,PAD,cy,W-2*PAD,84,18); ctx.fill()
  ctx.strokeStyle=T.green; ctx.lineWidth=2; _rr(ctx,PAD,cy,W-2*PAD,84,18); ctx.stroke()
  ctx.textAlign="center"; ctx.font=`900 32px ${SANS}`; ctx.fillStyle=T.green
  ctx.fillText("Full board + the why — free · linebrkr.app",W/2,cy+53)
  ctx.font=`600 20px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Research, not betting advice · 21+ · 1-800-GAMBLER",W/2,H-PAD-14)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function shareBoard(payload){
  let blob=null
  try { blob=await drawBoardCard(payload) } catch(_){}
  const text = `${payload.title} — ${payload.subtitle} on LINEBRKR. Full board + the why, free → linebrkr.app`
  const file = blob ? new File([blob], "linebrkr-top10.png", {type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  try { if(navigator.share){ await navigator.share({text, url:"https://linebrkr.app"}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=file.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  try { await navigator.clipboard.writeText(text); return "copied" } catch(_){ return "failed" }
}

// ── Pitchers to attack ───────────────────────────────────────────────────────
// Regroup the whole day's board by the pitcher each hitter faces, then surface the
// single best hitter to target against him. Unlike the vibes-y "targets" cards
// floating around, EVERY hitter here is backed by our calibrated model probability.
// "Best hitter target" = the hitter facing that arm with our highest hit % (the play
// most likely to cash). Ranked by that best target's probability.
const _STRONG_TIERS = new Set(["ELITE","BEST BET","STRONG","PRIME SHOT"])
function buildAttackRows(flat){
  // Group by pitcher + the lineup facing him. A starter faces exactly ONE lineup, so
  // the +team split never wrongly divides a real group — but it DOES separate two
  // different arms that share an abbreviated name ("C. Sale") across two games.
  const groups = new Map()
  for(const e of (flat||[])){
    const pit=(e.opp_pitcher_name||"").trim()
    if(!pit || !e.full_name) continue
    const key=pit+"|"+(e.team||"")
    let g=groups.get(key)
    if(!g){ g={pitcher:pit, hand:(e.opp_pitcher_hand||""), team:(e.team||""), byHitter:new Map()}; groups.set(key,g) }
    if(!g.hand && e.opp_pitcher_hand) g.hand=e.opp_pitcher_hand
    const cur=g.byHitter.get(e.full_name)          // one row per hitter — his single best play
    if(!cur || (e.prob_over||0)>(cur.prob_over||0)) g.byHitter.set(e.full_name, e)
  }
  const rows=[]
  for(const g of groups.values()){
    const hitters=[...g.byHitter.values()].sort((a,b)=>(b.prob_over||0)-(a.prob_over||0))
    const best=hitters[0]
    if(!best) continue
    const targets=hitters.filter(h=>_STRONG_TIERS.has(h.tier)).length   // how many real threats face this arm
    rows.push({pitcher:g.pitcher, hand:(g.hand||"").toUpperCase().slice(0,1), team:g.team, best, targets, nHitters:hitters.length})
  }
  // Rank arms by their best target's hit % — the most likely play to cash sits on top.
  rows.sort((a,b)=>(b.best.prob_over||0)-(a.best.prob_over||0))
  return rows
}

// Branded, text-only 3-column card (Pitcher · Lineup · Best hitter + hit %) — the
// shareable daily-post asset. Text-only keeps the canvas untainted (no cross-origin
// headshots) so toBlob works on every phone.
function drawAttackCard(payload){
  const {subtitle, rows} = payload
  const W=1080, PAD=56, rowH=100, rowGap=12
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const n=rows.length
  let H=PAD+50+64+40+28+40        // logo + title + subtitle + gap + column header
  H += n*(rowH+rowGap)
  H += 30 + 84 + 46 + PAD         // gap + CTA + disclaimer + pad
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W,H)
  const glow=ctx.createRadialGradient(W*0.86,-60,40, W*0.86,-60,820)
  glow.addColorStop(0,"rgba(0,217,126,0.15)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W,H)
  const x=PAD; let y=PAD+50
  ctx.textAlign="left"
  ctx.font=`900 48px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,y)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,y)
  ctx.font=`800 18px ${SANS}`; ctx.fillStyle=T.faint; ctx.textAlign="right"; ctx.fillText("MLB PROP RESEARCH",W-PAD,PAD+38); ctx.textAlign="left"
  y+=58; ctx.font=`900 46px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("HITTERS TO TARGET TODAY",x,y)
  y+=34; ctx.font=`700 23px ${SANS}`; ctx.fillStyle=T.green; ctx.fillText(subtitle,x,y)
  // Column layout
  const bX=x+400, cX=x+540
  y+=44
  ctx.font=`800 17px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("PITCHER TO ATTACK", x+24, y)
  ctx.fillText("LINEUP", bX, y)
  ctx.fillText("BEST HITTER", cX, y)
  ctx.textAlign="right"; ctx.fillText("HIT %", W-PAD-24, y); ctx.textAlign="left"
  y+=18
  rows.forEach((r,i)=>{
    const ry=y; y+=rowH; const top=i<3
    ctx.fillStyle=top?"rgba(0,217,126,0.07)":T.card; _rr(ctx,x,ry,W-2*PAD,rowH,16); ctx.fill()
    ctx.strokeStyle=top?T.greenMid:T.border2; ctx.lineWidth=1.5; _rr(ctx,x,ry,W-2*PAD,rowH,16); ctx.stroke()
    // Col A — pitcher
    ctx.textAlign="left"
    ctx.font=`800 27px ${SANS}`; ctx.fillStyle=T.text
    ctx.fillText(_ellip(ctx,r.pitcher,360), x+24, ry+42)
    ctx.font=`600 18px ${SANS}`; ctx.fillStyle=T.muted
    const sub1=(r.hand?`${r.hand}HP · `:"")+`${r.targets||0} target${r.targets===1?"":"s"}`
    ctx.fillText(sub1, x+24, ry+72)
    // Col B — lineup (team attacking him)
    ctx.font=`900 30px ${MONO}`; ctx.fillStyle=T.muted2
    ctx.fillText(_ellip(ctx,r.team||"—",130), bX, ry+rowH/2+11)
    // Col C — best hitter + market
    ctx.font=`800 27px ${SANS}`; ctx.fillStyle=T.text
    ctx.fillText(_ellip(ctx,r.hitter, W-PAD-cX-140), cX, ry+42)
    ctx.font=`600 18px ${SANS}`; ctx.fillStyle=T.green
    ctx.fillText(_ellip(ctx,r.market, W-PAD-cX-140), cX, ry+72)
    // hit %
    ctx.textAlign="right"
    ctx.font=`900 40px ${MONO}`; ctx.fillStyle=r.probColor||T.green
    ctx.fillText(r.probStr, W-PAD-24, ry+rowH/2+2)
    ctx.font=`700 14px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText("TO HIT", W-PAD-24, ry+rowH/2+24)
    ctx.textAlign="left"
  })
  y+=30
  const cy=H-PAD-46-84-8
  ctx.fillStyle="rgba(0,217,126,0.10)"; _rr(ctx,PAD,cy,W-2*PAD,84,18); ctx.fill()
  ctx.strokeStyle=T.green; ctx.lineWidth=2; _rr(ctx,PAD,cy,W-2*PAD,84,18); ctx.stroke()
  ctx.textAlign="center"; ctx.font=`900 32px ${SANS}`; ctx.fillStyle=T.green
  ctx.fillText("Every target model-backed · free at linebrkr.app",W/2,cy+53)
  ctx.font=`600 20px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Research, not betting advice · 21+ · 1-800-GAMBLER",W/2,H-PAD-14)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function shareAttack(payload){
  let blob=null
  try { blob=await drawAttackCard(payload) } catch(_){}
  const lead = payload.rows?.[0]
  const text = lead
    ? `Hitters to target today — top spot: ${lead.hitter} (${lead.market}, ${lead.probStr}) vs ${lead.pitcher}. Full model-backed board, free → linebrkr.app`
    : `Hitters to target today on LINEBRKR — model-backed. Free → linebrkr.app`
  const file = blob ? new File([blob], "linebrkr-targets.png", {type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  try { if(navigator.share){ await navigator.share({text, url:"https://linebrkr.app"}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=file.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  try { await navigator.clipboard.writeText(text); return "copied" } catch(_){ return "failed" }
}

// Branded market board card (HRR / Homers / Hits) — the daily-post asset, drawn on
// an offscreen canvas from live board rows. Text-only (no cross-origin headshots) so
// toBlob works on every phone. rows = [{name, team, vs, pct}], top 2 highlighted.
async function drawMarketCard({title, sub, dateStr, rows}){
  const W=1080, PAD=56, rowH=116, rowGap=13
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const n=rows.length
  let H=PAD+52+40+90+34         // logo + eyebrow + title + subtitle
  H += 24 + n*(rowH+rowGap)     // gap + rows
  H += 26 + 44 + PAD            // gap + disclaimer + pad
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W,H)
  const glow=ctx.createRadialGradient(W*0.5,-80,60, W*0.5,-80,900)
  glow.addColorStop(0,"rgba(0,217,126,0.16)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W,H)
  const x=PAD; let y=PAD+46
  ctx.textAlign="left"
  ctx.font=`900 44px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,y)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,y)
  ctx.font=`800 17px ${MONO}`; ctx.fillStyle=T.faint; ctx.textAlign="right"; ctx.fillText("MLB PROP MODEL",W-PAD,PAD+40); ctx.textAlign="left"
  y+=40; ctx.font=`800 20px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText(dateStr.toUpperCase()+" · TODAY",x,y)
  y+=64; ctx.font=`900 72px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText(title,x,y)
  y+=34; ctx.font=`700 22px ${SANS}`; ctx.fillStyle=T.muted; ctx.fillText(sub,x,y)
  y+=24
  rows.forEach((r,i)=>{
    const ry=y; y+=rowH+rowGap; const top=i<2
    ctx.fillStyle=top?"rgba(0,217,126,0.08)":T.card; _rr(ctx,x,ry,W-2*PAD,rowH,18); ctx.fill()
    ctx.strokeStyle=top?T.greenMid:T.border2; ctx.lineWidth=1.5; _rr(ctx,x,ry,W-2*PAD,rowH,18); ctx.stroke()
    ctx.textAlign="left"
    ctx.font=`900 38px ${SANS}`; ctx.fillStyle=T.text
    ctx.fillText(_ellip(ctx,r.name, W-2*PAD-260), x+28, ry+52)
    ctx.font=`700 20px ${MONO}`; ctx.fillStyle=T.muted
    ctx.fillText(_ellip(ctx,`${r.team||""}${r.vs?` · vs ${r.vs}`:""}`, W-2*PAD-260), x+28, ry+88)
    ctx.textAlign="right"
    ctx.font=`900 56px ${MONO}`; ctx.fillStyle=top?T.green:T.text
    ctx.fillText(`${r.pct}%`, W-PAD-28, ry+rowH/2+18)
    ctx.textAlign="left"
  })
  y+=26
  ctx.textAlign="center"; ctx.font=`600 19px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Model-ranked · graded after every slate · research, not advice · 21+", W/2, H-PAD-8)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
// Render one market card and hand it to the phone share sheet (or download on desktop).
async function shareMarketCard(payload){
  let blob=null
  try { blob=await drawMarketCard(payload) } catch(_){}
  const lead=payload.rows?.[0]
  const text=lead ? `LINEBRKR ${payload.title} board — top: ${lead.name} ${lead.pct}%. Model-ranked, free → linebrkr.app`
                  : `LINEBRKR ${payload.title} board — model-ranked, free → linebrkr.app`
  const file=blob ? new File([blob], `linebrkr-${payload.slug||"board"}.png`, {type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=file.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  try { await navigator.clipboard.writeText(text); return "copied" } catch(_){ return "failed" }
}

// Board/card selection shared with the daily post cards: dedupe by player, drop TBD
// matchups (no posted pitcher — a placeholder-arm number we don't headline), and cap at
// 2 per team (a whole lineup vs one soft arm shouldn't monopolize a card/board). Same
// rule the record uses. Returns up to `n` rows shaped for the canvas renderers.
function _capRows(entries, n){
  const seen=new Set(), tc={}, out=[]
  for(const e of (entries||[])){
    const nm=e.full_name; if(!nm||seen.has(nm)) continue
    if(!(e.opp_pitcher_name||"").trim()) continue      // skip TBD matchups
    const t=e.team||""; if(t && (tc[t]||0)>=2) continue // 2-per-team cap
    seen.add(nm); tc[t]=(tc[t]||0)+1
    out.push({ name:nm, team:t,
      vs: e.opp_pitcher_name ? e.opp_pitcher_name.split(" ").slice(-1)[0] : "",
      pct: Math.round((e.prob_over||0)*100) })
    if(out.length===n) break
  }
  return out
}

// Render the 4-market Top-10 board (HRR / Hits / Homers / Total Bases) as one landscape
// graphic — the "rankings" card. Columns already carry their capped, TBD-free top-10.
async function drawGridBoardCard({dateStr, columns}){
  const W=2000, PAD=60, GAP=28
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const headH=200, colHeadH=64, rowH=86, rowGap=10, footH=52, nRows=10
  const H=PAD+headH+colHeadH+nRows*(rowH+rowGap)+footH+PAD
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W,H)
  const glow=ctx.createRadialGradient(W*0.15,-100,80, W*0.15,-100,1200)
  glow.addColorStop(0,"rgba(0,217,126,0.14)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W,H)
  let x=PAD, y=PAD+50
  ctx.textAlign="left"; ctx.font=`900 46px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,y)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,y)
  y+=78; ctx.font=`900 78px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("TOP 10 ",x,y)
  const tw=ctx.measureText("TOP 10 ").width; ctx.fillStyle=T.green; ctx.fillText("BOARD.",x+tw,y)
  ctx.textAlign="right"; ctx.font=`800 22px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText((dateStr||"").toUpperCase()+" · TODAY", W-PAD, PAD+38)
  ctx.font=`700 16px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText("Model-ranked · calibrated · all four markets", W-PAD, PAD+68)
  ctx.textAlign="left"
  const gridY=PAD+headH, colW=(W-2*PAD-3*GAP)/4
  columns.forEach((col,ci)=>{
    const cx=PAD+ci*(colW+GAP)
    ctx.textAlign="left"
    ctx.font=`900 30px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText(col.title, cx+4, gridY+30)
    ctx.font=`700 14px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText(col.sub||"", cx+4, gridY+52)
    let ry=gridY+colHeadH
    for(let i=0;i<nRows;i++){
      const r=(col.rows||[])[i], top=i<3
      ctx.fillStyle=top?"rgba(0,217,126,0.07)":T.card; _rr(ctx,cx,ry,colW,rowH,13); ctx.fill()
      ctx.strokeStyle=top?T.greenMid:T.border2; ctx.lineWidth=1.2; _rr(ctx,cx,ry,colW,rowH,13); ctx.stroke()
      if(r){
        ctx.textAlign="left"
        ctx.font=`700 19px ${MONO}`; ctx.fillStyle=top?T.green:T.faint; ctx.fillText(String(i+1), cx+16, ry+rowH/2+7)
        ctx.font=`900 23px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText(_ellip(ctx,r.name, colW-142), cx+46, ry+34)
        ctx.font=`700 13px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(_ellip(ctx,`${r.team||""}${r.vs?` · ${r.vs}`:""}`, colW-142), cx+46, ry+58)
        ctx.textAlign="right"; ctx.font=`900 30px ${MONO}`; ctx.fillStyle=top?T.green:T.text; ctx.fillText(`${r.pct}%`, cx+colW-16, ry+rowH/2+10)
        ctx.textAlign="left"
      }
      ry+=rowH+rowGap
    }
  })
  ctx.textAlign="center"; ctx.font=`600 18px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Top 3 per market highlighted · calibrated model probabilities · research, not advice · 21+", W/2, H-PAD-2)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function shareGridBoard(payload){
  let blob=null
  try { blob=await drawGridBoardCard(payload) } catch(_){}
  const text=`LINEBRKR Top 10 board — model-ranked across HRR, Hits, Homers & Total Bases. Free → linebrkr.app`
  const file=blob ? new File([blob], `linebrkr-top10-board.png`, {type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=file.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  return "failed"
}

// ── Yard Work / Hitting Board — tiered conviction cards ─────────────────────
// Both are the same tiered board off the HRR leaderboard, cross-referenced with the
// Total Bases and HR Anytime boards by player, capped 2/team with TBD matchups held out.
// Yard Work shows HRR/TB/HR chips; the Hitting Board swaps TB for an ATTACK tag
// (POWER / CONTACT / BOTH) derived from the model's HRR% and HR%.
function _attackType(hrr, hr){
  if(hr>=17 && hrr>=53) return "BOTH"
  if(hr>=16) return "POWER"      // the homer number carries it
  if(hr<=12) return "CONTACT"    // weak pop → attack the floor (HRR/bases)
  return "BOTH"
}
// Plain-English "why" from the leaderboard's own Statcast/form/park/platoon fields.
function _whyLine(e){
  const parts=[]
  const b=(e.bats||"").toUpperCase(), h=(e.opp_pitcher_hand||"").toUpperCase()
  if(e.barrel_pct!=null && e.barrel_pct>=0.11) parts.push(`${Math.round(e.barrel_pct*100)}% barrel`)
  else if(e.hard_hit_pct!=null && e.hard_hit_pct>=0.45) parts.push(`${Math.round(e.hard_hit_pct*100)}% hard-hit`)
  if(b && h && b!=="S" && b!==h) parts.push("platoon edge")
  if(typeof e.park_factor==="number" && e.park_factor>=1.08) parts.push(`${e.park_factor.toFixed(2)}x park`)
  if(parts.length<2 && e.xwoba!=null && e.xwoba>=0.36) parts.push(`.${String(Math.round(e.xwoba*1000)).padStart(3,"0")} xwOBA`)
  if(!parts.length) parts.push(typeof e.edge_pct==="number" && e.edge_pct>0 ? `+${Math.round(e.edge_pct*100)}% edge` : "model-ranked")
  return parts.slice(0,3).join(" · ")
}
// Assemble up to `count` capped HRR plays, each carrying HRR/TB/HR %, attack type & why.
async function _buildTieredRows(count){
  // NOTE: the leaderboard endpoint caps `limit` at 200 (le=200); requesting more 422s,
  // which the .catch swallowed → empty TB/HR maps → every TB/HR chip rendered 0%.
  const [hrrD, tbD, hrD] = await Promise.all([
    getLeaderboard("HRR_over_1.5", todayStr(), 150, false).catch(()=>null),
    getLeaderboard("total_bases",  todayStr(), 200, false).catch(()=>null),
    getLeaderboard("HR_anytime",   todayStr(), 200, false).catch(()=>null),
  ])
  const pct = e => Math.round((e?.prob_over||0)*100)
  const tbMap={}, hrMap={}
  for(const e of (tbD?.entries||[])) if(e.full_name) tbMap[e.full_name]=pct(e)
  for(const e of (hrD?.entries||[])) if(e.full_name) hrMap[e.full_name]=pct(e)
  const seen=new Set(), tc={}, rows=[]
  for(const e of (hrrD?.entries||[])){
    const nm=e.full_name; if(!nm||seen.has(nm)) continue
    if(!(e.opp_pitcher_name||"").trim()) continue          // TBD matchup held out
    const t=e.team||""; if(t && (tc[t]||0)>=2) continue    // 2-per-team cap
    seen.add(nm); tc[t]=(tc[t]||0)+1
    const hrr=pct(e), hr=hrMap[nm]??0, tb=tbMap[nm]??0
    rows.push({ name:nm, team:t, vs:(e.opp_pitcher_name||"").split(" ").slice(-1)[0],
      hand:(e.opp_pitcher_hand||"").toUpperCase().slice(0,1),
      hrr, tb, hr, attack:_attackType(hrr,hr), why:_whyLine(e) })
    if(rows.length===count) break
  }
  return rows
}
// One renderer, two modes. cfg={mode, dateStr, title, span, subtitle, tiers:[{name,desc,col,rows}]}.
async function drawTieredBoard({mode, dateStr, title, span, subtitle, tiers}){
  const hit = mode==="hitboard"
  const W = hit?1200:1080, PAD=52
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const rowH=100, rowGap=7, tHeadH=44, tGap=12, headH=196, footH=54
  let H=PAD+headH
  tiers.forEach(t=>{ H += tHeadH + t.rows.length*(rowH+rowGap) + tGap })
  H += footH+PAD
  const cv=document.createElement("canvas"); cv.width=W; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W,H)
  const glow=ctx.createRadialGradient(W*0.15,-90,70, W*0.15,-90,1100)
  glow.addColorStop(0,"rgba(0,217,126,0.14)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W,H)
  // header
  const x=PAD; let y=PAD+42
  ctx.textAlign="left"; ctx.font=`900 34px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,y)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,y)
  ctx.textAlign="right"; ctx.font=`800 19px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText((dateStr||"").toUpperCase()+" · TODAY", W-PAD, PAD+38)
  if(hit){ // attack legend
    const L=[["POWER","#2fe37a"],["CONTACT","#5aa9e6"],["BOTH","#e0b24a"]]; let lx=W-PAD
    for(let i=L.length-1;i>=0;i--){ ctx.font=`800 12px ${MONO}`; const tw=ctx.measureText(L[i][0]).width+18; lx-=tw
      ctx.fillStyle=L[i][1]+"22"; _rr(ctx,lx,PAD+52,tw,22,6); ctx.fill()
      ctx.textAlign="center"; ctx.fillStyle=L[i][1]; ctx.fillText(L[i][0],lx+tw/2,PAD+67); lx-=8 }
  }
  ctx.textAlign="left"
  y+=62; ctx.font=`900 62px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText(title,x,y)
  const tw2=ctx.measureText(title).width; ctx.fillStyle=T.green; ctx.fillText(span,x+tw2,y)
  y+=32; ctx.font=`700 18px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(subtitle,x,y)
  // chip + pill closures
  const chip=(px,py,pw,val,lab,lead)=>{
    ctx.fillStyle=lead?"rgba(0,217,126,0.10)":T.card; _rr(ctx,px,py,pw,52,9); ctx.fill()
    ctx.strokeStyle=lead?T.greenMid:T.border2; ctx.lineWidth=1.2; _rr(ctx,px,py,pw,52,9); ctx.stroke()
    ctx.textAlign="center"; ctx.font=`900 22px ${MONO}`; ctx.fillStyle=lead?T.green:T.text; ctx.fillText(val,px+pw/2,py+27)
    ctx.font=`800 10px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(lab,px+pw/2,py+44); ctx.textAlign="left"
  }
  const pill=(px,py,pw,atk)=>{
    const A={POWER:["#2fe37a","rgba(47,227,122,0.13)"],CONTACT:["#5aa9e6","rgba(90,169,230,0.13)"],BOTH:["#e0b24a","rgba(224,178,74,0.13)"]}[atk]||["#e0b24a","rgba(224,178,74,0.13)"]
    ctx.fillStyle=A[1]; _rr(ctx,px,py,pw,46,10); ctx.fill()
    ctx.strokeStyle=A[0]; ctx.lineWidth=1.5; _rr(ctx,px,py,pw,46,10); ctx.stroke()
    ctx.textAlign="center"; ctx.font=`900 15px ${MONO}`; ctx.fillStyle=A[0]; ctx.fillText(atk,px+pw/2,py+29); ctx.textAlign="left"
  }
  y=PAD+headH
  tiers.forEach(t=>{
    ctx.textAlign="left"; ctx.font=`900 23px ${SANS}`; ctx.fillStyle=t.col; ctx.fillText(t.name,x,y+22)
    const nw=ctx.measureText(t.name).width
    ctx.font=`700 13px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(t.desc,x+nw+14,y+22)
    ctx.beginPath(); ctx.moveTo(x,y+34); ctx.lineTo(W-PAD,y+34); ctx.lineWidth=2; ctx.strokeStyle=(t.col===T.green?T.greenMid:T.border2); ctx.stroke()
    y+=tHeadH
    t.rows.forEach(r=>{
      const rw=W-2*PAD
      ctx.fillStyle=T.card; _rr(ctx,x,y,rw,rowH,12); ctx.fill()
      ctx.strokeStyle=T.border2; ctx.lineWidth=1.2; _rr(ctx,x,y,rw,rowH,12); ctx.stroke()
      const rightBudget = hit?350:250
      ctx.textAlign="left"; ctx.font=`900 24px ${SANS}`; ctx.fillStyle=T.text
      const nm=_ellip(ctx,r.name,rw-rightBudget-40); ctx.fillText(nm,x+20,y+40)
      const nmw=ctx.measureText(nm).width
      ctx.font=`700 13px ${MONO}`; ctx.fillStyle=T.muted
      ctx.fillText(`  ${r.team} · vs ${r.vs}${r.hand?` (${r.hand})`:""}`,x+20+nmw,y+40)
      ctx.font=`700 13px ${MONO}`; ctx.fillStyle="#6f8a7c"
      ctx.fillText(_ellip(ctx,r.why,rw-rightBudget-40),x+20,y+68)
      let cxr=W-PAD
      if(hit){
        cxr-=116; pill(cxr,y+rowH/2-23,116,r.attack); cxr-=10
        cxr-=62;  chip(cxr,y+rowH/2-26,62,`${r.hr}%`,"HR",false); cxr-=8
        cxr-=62;  chip(cxr,y+rowH/2-26,62,`${r.hrr}%`,"HRR",true)
      } else {
        cxr-=62; chip(cxr,y+rowH/2-26,62,`${r.hr}%`,"HR",false); cxr-=8
        cxr-=62; chip(cxr,y+rowH/2-26,62,`${r.tb}%`,"TB",false); cxr-=8
        cxr-=62; chip(cxr,y+rowH/2-26,62,`${r.hrr}%`,"HRR",true)
      }
      y+=rowH+rowGap
    })
    y+=tGap
  })
  ctx.textAlign="center"; ctx.font=`600 17px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Calibrated model probabilities · ranked by conviction · research, not advice · 21+ · 1-800-GAMBLER", W/2, H-PAD-6)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function _shareTiered(cfg){
  let blob=null
  try { blob=await drawTieredBoard(cfg) } catch(_){}
  const fname=`linebrkr-${cfg.mode}.png`
  const text = cfg.mode==="yardwork"
    ? "LINEBRKR Yard Work — tonight's HRR & Total Bases bats, ranked by conviction. Free → linebrkr.app"
    : "LINEBRKR Hitting Board — hot HRR bats + where to attack, with the model's number. Free → linebrkr.app"
  const file=blob ? new File([blob],fname,{type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=fname; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  return "failed"
}
async function shareYardWork(dateStr){
  const rows=await _buildTieredRows(9)
  if(!rows.length) return "empty"
  const tiers=[
    {name:"LOCKED IN", desc:"top of the board", col:T.green, rows:rows.slice(0,3)},
    {name:"STRONG", desc:"live bats in good spots", col:T.text, rows:rows.slice(3,6)},
    {name:"FLOOR PLAYS", desc:"HRR / bases floor", col:T.muted, rows:rows.slice(6,9)},
  ].filter(t=>t.rows.length)
  return _shareTiered({mode:"yardwork", dateStr, title:"YARD ", span:"WORK.",
    subtitle:"Tonight's bats, ranked by conviction · HRR & Total Bases", tiers})
}
async function shareHitBoard(dateStr){
  const rows=await _buildTieredRows(15)
  if(!rows.length) return "empty"
  const tiers=[
    {name:"TIER 1", desc:"top of the board", col:T.green, rows:rows.slice(0,5)},
    {name:"TIER 2", desc:"strong targets", col:T.text, rows:rows.slice(5,10)},
    {name:"TIER 3", desc:"hot bats, tougher spots — attack the floor", col:T.muted, rows:rows.slice(10,15)},
  ].filter(t=>t.rows.length)
  return _shareTiered({mode:"hitboard", dateStr, title:"HITTING ", span:"BOARD.",
    subtitle:"Hottest HRR bats + where to attack — with the model's number", tiers})
}

// ── Results / receipts card ─────────────────────────────────────────────────
// Grades the board's OWN top plays vs the final box scores for a date — the same
// top-N (2-per-team cap) the record uses, straight from /scorecard/results/players.
// Honest by construction: every row is a settled play with its line → actual, and
// the W-L on the card is computed over exactly the plays shown.
const _RESULT_MKTS = [
  {title:"HRR",         db:"HRR_over_1.5"},
  {title:"HOMERS",      db:"HR_anytime"},
  {title:"TOTAL BASES", db:"total_bases"},
  {title:"HITS",        db:"hits"},
]
async function _buildResultsData(iso){
  const data = await Promise.all(
    _RESULT_MKTS.map(m => getResultsDaily(iso, m.db, "top10").catch(()=>null))
  )
  const markets=[]; let W=0, N=0
  _RESULT_MKTS.forEach((m,i)=>{
    const ps = Array.isArray(data[i]?.players) ? data[i].players : []
    if(!ps.length) return
    const shown = ps.slice(0,10)                       // full published top-10 (DNPs already
    const w = shown.filter(p=>p.hit===1).length        // excluded upstream: settled rows only,
    W+=w; N+=shown.length                              // so the 11th auto-fills a scratch)
    markets.push({title:m.title, w, n:shown.length, plays:shown})
  })
  return {markets, W, N}
}
async function drawResultsCard({dateStr, markets, W, N}){
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const RED="#ef5a52"
  const W_PX=1080, PAD=52, rowH=62, rowGap=6, secHead=48, secGap=14, headH=232, footH=54
  let H=PAD+headH
  markets.forEach(m=>{ H += secHead + m.plays.length*(rowH+rowGap) + secGap })
  H += footH+PAD
  const cv=document.createElement("canvas"); cv.width=W_PX; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W_PX,H)
  const glow=ctx.createRadialGradient(W_PX*0.15,-90,70, W_PX*0.15,-90,1100)
  glow.addColorStop(0,"rgba(0,217,126,0.14)"); glow.addColorStop(1,"rgba(0,217,126,0)")
  ctx.fillStyle=glow; ctx.fillRect(0,0,W_PX,H)
  const x=PAD
  // header wordmark + date
  ctx.textAlign="left"; ctx.font=`900 34px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,PAD+42)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,PAD+42)
  ctx.textAlign="right"; ctx.font=`800 19px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText((dateStr||"").toUpperCase()+" · GRADED", W_PX-PAD, PAD+38)
  // title
  ctx.textAlign="left"; ctx.font=`900 62px ${SANS}`; ctx.fillStyle=T.text
  ctx.fillText("RESULTS",x,PAD+118)
  const rw2=ctx.measureText("RESULTS").width; ctx.fillStyle=T.green; ctx.fillText(".",x+rw2,PAD+118)
  ctx.font=`700 18px ${MONO}`; ctx.fillStyle=T.muted
  ctx.fillText("Our top plays, graded vs the final box scores", x, PAD+150)
  // overall record hero chip
  const pct = N? Math.round(W/N*100):0
  const hy=PAD+170, hh=44
  ctx.fillStyle="rgba(0,217,126,0.10)"; _rr(ctx,x,hy,W_PX-2*PAD,hh,11); ctx.fill()
  ctx.strokeStyle=T.greenMid; ctx.lineWidth=1.4; _rr(ctx,x,hy,W_PX-2*PAD,hh,11); ctx.stroke()
  ctx.textAlign="left"; ctx.font=`900 24px ${MONO}`; ctx.fillStyle=T.green
  ctx.fillText(`${W}-${N-W}`, x+18, hy+30)
  const recw=ctx.measureText(`${W}-${N-W}`).width
  ctx.font=`800 15px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(`  ${pct}% top plays cashed`, x+18+recw, hy+30)
  ctx.textAlign="right"; ctx.font=`800 14px ${MONO}`; ctx.fillStyle=T.muted2||T.muted
  ctx.fillText(`${markets.length} markets · ${N} plays`, W_PX-PAD-16, hy+30)
  // sections
  let y=PAD+headH
  markets.forEach(m=>{
    ctx.textAlign="left"; ctx.font=`900 23px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText(m.title,x,y+22)
    const nw=ctx.measureText(m.title).width
    const good=m.w>=Math.ceil(m.n/2)
    ctx.font=`800 15px ${MONO}`; ctx.fillStyle=good?T.green:RED
    ctx.fillText(`  ${m.w}-${m.n-m.w}`, x+nw+8, y+22)
    ctx.beginPath(); ctx.moveTo(x,y+34); ctx.lineTo(W_PX-PAD,y+34); ctx.lineWidth=2; ctx.strokeStyle=T.border2; ctx.stroke()
    y+=secHead
    m.plays.forEach(p=>{
      const hit=p.hit===1, rw=W_PX-2*PAD, col=hit?T.green:RED
      ctx.fillStyle=hit?"rgba(0,217,126,0.08)":"rgba(239,90,82,0.06)"; _rr(ctx,x,y,rw,rowH,11); ctx.fill()
      ctx.strokeStyle=hit?"rgba(0,217,126,0.30)":"rgba(239,90,82,0.28)"; ctx.lineWidth=1.2; _rr(ctx,x,y,rw,rowH,11); ctx.stroke()
      // mark badge
      ctx.fillStyle=col; ctx.beginPath(); ctx.arc(x+30,y+rowH/2,15,0,Math.PI*2); ctx.fill()
      ctx.fillStyle="#04140D"; ctx.textAlign="center"; ctx.font=`900 18px ${SANS}`
      ctx.fillText(hit?"✓":"✕", x+30, y+rowH/2+6)
      // name + team vs opp
      ctx.textAlign="left"; ctx.font=`900 23px ${SANS}`; ctx.fillStyle=T.text
      const nm=_ellip(ctx,p.player||"",rw-360); ctx.fillText(nm,x+58,y+rowH/2-2)
      const nmw=ctx.measureText(nm).width
      ctx.font=`700 12.5px ${MONO}`; ctx.fillStyle=T.muted
      ctx.fillText(`  ${p.team||""}${p.opponent?` vs ${p.opponent}`:""}`, x+58+nmw, y+rowH/2-2)
      ctx.font=`700 12.5px ${MONO}`; ctx.fillStyle="#6f8a7c"
      const need=(p.line!=null)?`${p.line}`:"", got=(p.actual_stat!=null)?`${p.actual_stat}`:"—"
      ctx.fillText(`line ${need} → ${got}`, x+58, y+rowH/2+18)
      // right: prob the board had
      if(p.prob_over!=null){
        ctx.textAlign="right"; ctx.font=`900 21px ${MONO}`; ctx.fillStyle=col
        ctx.fillText(`${Math.round(p.prob_over*100)}%`, W_PX-PAD-18, y+rowH/2+2)
        ctx.font=`700 10px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText("MODEL", W_PX-PAD-18, y+rowH/2+18)
        ctx.textAlign="left"
      }
      y+=rowH+rowGap
    })
    y+=secGap
  })
  ctx.textAlign="center"; ctx.font=`600 17px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Settled vs official box scores · same top-N the public record uses · research, not advice · 21+ · 1-800-GAMBLER", W_PX/2, H-PAD-6)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function shareResults(iso, dateStr){
  const {markets, W, N} = await _buildResultsData(iso)
  if(!markets.length) return "empty"
  let blob=null
  try { blob=await drawResultsCard({dateStr, markets, W, N}) } catch(_){}
  const fname="linebrkr-results.png"
  const pct=N?Math.round(W/N*100):0
  const text=`LINEBRKR results — ${dateStr}: our top plays went ${W}-${N-W} (${pct}%), graded vs the final box scores. Public record, free → linebrkr.app`
  const file=blob ? new File([blob],fname,{type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=fname; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  return "failed"
}

// ── Yard Work Results — the tiered Yard Work board, graded ───────────────────
// Reconstructs a date's Yard Work HRR set (top-N, 2-per-team cap, the same feed the
// live board uses) from /scorecard/results/players, cross-refs each bat's TB + HR
// outcome, and renders it in the Yard Work look with a HIT/MISS badge and the ACTUAL
// numbers in the chips. HRR is the graded bet; W-L is over exactly the bats shown.
async function _buildYardWorkResults(iso){
  const [hrrD, tbD, hrD] = await Promise.all([
    getResultsDaily(iso,"HRR_over_1.5","top10",50).catch(()=>null),
    getResultsDaily(iso,"total_bases","all",300).catch(()=>null),
    getResultsDaily(iso,"HR_anytime","all",300).catch(()=>null),
  ])
  const hrrP = Array.isArray(hrrD?.players)?hrrD.players:[]
  if(!hrrP.length) return null
  const tbMap={}, hrMap={}
  for(const p of (Array.isArray(tbD?.players)?tbD.players:[])) tbMap[p.player]=p
  for(const p of (Array.isArray(hrD?.players)?hrD.players:[])) hrMap[p.player]=p
  const rows = hrrP.slice(0,9).map(p=>{
    const tb=tbMap[p.player], hr=hrMap[p.player]
    return { name:p.player, team:p.team, vs:p.opponent,
      hrrHit:p.hit===1, hrrAct:p.actual_stat, hrrLine:p.line, hrrPct:Math.round((p.prob_over||0)*100),
      tbHit: tb?tb.hit===1:null, tbAct: tb?tb.actual_stat:null,
      hrHit: hr?hr.hit===1:null, hrAct: hr?hr.actual_stat:null }
  })
  return { rows, W: rows.filter(r=>r.hrrHit).length, N: rows.length }
}
async function drawYardWorkResultsCard({dateStr, tiers, W, N}){
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const RED="#ef5a52", W_PX=1080, PAD=52, rowH=94, rowGap=7, tHeadH=44, tGap=14, headH=252, footH=54
  let H=PAD+headH; tiers.forEach(t=>{ H += tHeadH + t.rows.length*(rowH+rowGap) + tGap }); H+=footH+PAD
  const cv=document.createElement("canvas"); cv.width=W_PX; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,W_PX,H)
  const glow=ctx.createRadialGradient(W_PX*0.15,-90,70,W_PX*0.15,-90,1100)
  glow.addColorStop(0,"rgba(0,217,126,0.14)"); glow.addColorStop(1,"rgba(0,217,126,0)"); ctx.fillStyle=glow; ctx.fillRect(0,0,W_PX,H)
  const x=PAD
  ctx.textAlign="left"; ctx.font=`900 34px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,PAD+42)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,PAD+42)
  ctx.textAlign="right"; ctx.font=`800 19px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText((dateStr||"").toUpperCase()+" · GRADED", W_PX-PAD, PAD+38)
  ctx.textAlign="left"; ctx.font=`900 62px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("YARD ",x,PAD+112)
  const yw=ctx.measureText("YARD ").width; ctx.fillStyle=T.green; ctx.fillText("WORK.",x+yw,PAD+112)
  ctx.font=`700 18px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText("How last night's board cashed · HRR graded vs the finals",x,PAD+144)
  const pct=N?Math.round(W/N*100):0, hy=PAD+164, hh=44
  ctx.fillStyle="rgba(0,217,126,0.10)"; _rr(ctx,x,hy,W_PX-2*PAD,hh,11); ctx.fill()
  ctx.strokeStyle=T.greenMid; ctx.lineWidth=1.4; _rr(ctx,x,hy,W_PX-2*PAD,hh,11); ctx.stroke()
  ctx.textAlign="left"; ctx.font=`900 24px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText(`${W}-${N-W}`,x+18,hy+30)
  const rw=ctx.measureText(`${W}-${N-W}`).width; ctx.font=`800 15px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(`  HRR · ${pct}% of the board cashed`,x+18+rw,hy+30)
  ctx.textAlign="right"; ctx.font=`800 14px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(`${N} bats`,W_PX-PAD-16,hy+30)
  // graded chip: value + hit color, small mark
  const gchip=(px,py,pw,val,lab,hit)=>{
    const col = hit===true?T.green : hit===false?RED : T.muted
    ctx.fillStyle = hit===true?"rgba(0,217,126,0.10)" : hit===false?"rgba(239,90,82,0.08)" : T.card
    _rr(ctx,px,py,pw,52,9); ctx.fill()
    ctx.strokeStyle = hit===true?T.greenMid : hit===false?"rgba(239,90,82,0.4)" : T.border2
    ctx.lineWidth=1.2; _rr(ctx,px,py,pw,52,9); ctx.stroke()
    ctx.textAlign="center"; ctx.font=`900 22px ${MONO}`; ctx.fillStyle=col; ctx.fillText(val,px+pw/2,py+27)
    ctx.font=`800 10px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(lab,px+pw/2,py+44); ctx.textAlign="left"
  }
  let y=PAD+headH
  tiers.forEach(t=>{
    const tw=t.rows.filter(r=>r.hrrHit).length
    ctx.textAlign="left"; ctx.font=`900 23px ${SANS}`; ctx.fillStyle=t.col; ctx.fillText(t.name,x,y+22)
    const nw=ctx.measureText(t.name).width
    ctx.font=`800 14px ${MONO}`; ctx.fillStyle=(tw>=Math.ceil(t.rows.length/2)?T.green:RED); ctx.fillText(`  ${tw}-${t.rows.length-tw}`,x+nw+8,y+22)
    ctx.beginPath(); ctx.moveTo(x,y+34); ctx.lineTo(W_PX-PAD,y+34); ctx.lineWidth=2; ctx.strokeStyle=(t.col===T.green?T.greenMid:T.border2); ctx.stroke()
    y+=tHeadH
    t.rows.forEach(r=>{
      const rww=W_PX-2*PAD, col=r.hrrHit?T.green:RED
      ctx.fillStyle=T.card; _rr(ctx,x,y,rww,rowH,12); ctx.fill()
      ctx.strokeStyle=r.hrrHit?"rgba(0,217,126,0.28)":"rgba(239,90,82,0.24)"; ctx.lineWidth=1.2; _rr(ctx,x,y,rww,rowH,12); ctx.stroke()
      // HIT/MISS badge
      ctx.fillStyle=col; ctx.beginPath(); ctx.arc(x+34,y+rowH/2,16,0,Math.PI*2); ctx.fill()
      ctx.fillStyle="#04140D"; ctx.textAlign="center"; ctx.font=`900 19px ${SANS}`; ctx.fillText(r.hrrHit?"✓":"✕",x+34,y+rowH/2+6)
      // name + team vs opp
      ctx.textAlign="left"; ctx.font=`900 24px ${SANS}`; ctx.fillStyle=T.text
      const nm=_ellip(ctx,r.name,rww-330); ctx.fillText(nm,x+64,y+40); const nmw=ctx.measureText(nm).width
      ctx.font=`700 13px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(`  ${r.team||""}${r.vs?` vs ${r.vs}`:""}`,x+64+nmw,y+40)
      ctx.font=`700 13px ${MONO}`; ctx.fillStyle="#6f8a7c"
      ctx.fillText(`HRR ${r.hrrLine} → ${r.hrrAct??"—"} · model had ${r.hrrPct}%`,x+64,y+68)
      // right chips: HRR / TB / HR actuals
      let cxr=W_PX-PAD
      // HR is a bonus, not a Yard Work bet — pop green only on an actual homer, never red.
      cxr-=62; gchip(cxr,y+rowH/2-26,62, r.hrAct!=null?`${r.hrAct}`:"—","HR", r.hrHit===true?true:null); cxr-=8
      cxr-=62; gchip(cxr,y+rowH/2-26,62, r.tbAct!=null?`${r.tbAct}`:"—","TB", r.tbHit); cxr-=8
      cxr-=62; gchip(cxr,y+rowH/2-26,62, r.hrrAct!=null?`${r.hrrAct}`:"—","HRR", r.hrrHit)
      y+=rowH+rowGap
    })
    y+=tGap
  })
  ctx.textAlign="center"; ctx.font=`600 17px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Yard Work board graded vs official box scores · same feed the live board uses · research, not advice · 21+ · 1-800-GAMBLER", W_PX/2, H-PAD-6)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function shareYardWorkResults(iso, dateStr){
  const data=await _buildYardWorkResults(iso)
  if(!data) return "empty"
  const {rows, W, N}=data
  const tiers=[
    {name:"LOCKED IN", desc:"", col:T.green, rows:rows.slice(0,3)},
    {name:"STRONG", desc:"", col:T.text, rows:rows.slice(3,6)},
    {name:"FLOOR PLAYS", desc:"", col:T.muted, rows:rows.slice(6,9)},
  ].filter(t=>t.rows.length)
  let blob=null
  try { blob=await drawYardWorkResultsCard({dateStr, tiers, W, N}) } catch(_){}
  const fname="linebrkr-yardwork-results.png"
  const pct=N?Math.round(W/N*100):0
  const text=`LINEBRKR Yard Work results — ${dateStr}: the board went ${W}-${N-W} on HRR (${pct}%), graded vs the finals. Free → linebrkr.app`
  const file=blob ? new File([blob],fname,{type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=fname; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  return "failed"
}

// ── Power Spots — hardest contact vs homer-prone arms (per side) + main-pitch fit ──
// Uses getToday (the full grid inputs) so it can read pitcher HR/9-to-side, the per-pitch
// breakdown, park, and form — the leaderboard endpoint doesn't carry those.
// Power Spots default filter floors. minHr9 lowered 1.8 → 1.5 (mirror of backend
// settings.power_spot_min_hr9) so near-miss homer arms still produce a board; the drawer
// exposes a live slider over all three.
const PS_DEFAULTS = { minHr9: 1.5, minBrl: 0.10, minHh: 0.44 }
async function _buildPowerSpots(opts){
  const { minHr9 = PS_DEFAULTS.minHr9, minBrl = PS_DEFAULTS.minBrl,
          minHh = PS_DEFAULTS.minHh } = (opts || {})
  const [d, live] = await Promise.all([
    getToday("HR_anytime", todayStr(), 500).catch(()=>null),
    getLive(todayStr()).catch(()=>null),
  ])
  const picks = (d && (d.picks || d.entries)) || []
  const lines = live?.lines || {}
  const rows=[]
  for(const e of picks){
    if(e.void) continue
    const i=e.inputs||{}
    // Drop a bat once its team's lineup is posted WITHOUT it (benched/scratched) — otherwise
    // a guy who didn't play lingers as "PROJ" all night. "unconfirmed" (lineup not up yet)
    // still shows, correctly, as PROJ.
    if(i.avail_status==="out") continue
    // Belt-and-suspenders: also drop a bat whose game is FINAL but who never played (0 PA &
    // 0 AB from the live boxscore) — catches a DNP even if his team's lineup was never
    // captured (so he never registered as "out" above).
    const lv=lines[e.player_id]
    if(lv && lv.final && (lv.pa||0)===0 && (lv.ab||0)===0) continue
    const hand=i.pitcher_hr9_vs_hand, used=(hand!=null && hand!==0)
    const hr9 = used ? hand : (i.pitcher_hr9||0)
    if(!hr9 || hr9<minHr9) continue
    const b=i.barrel_pct||0, hh=i.hard_hit_pct||0, fb=i.batter_fb_pct||0
    if(b<minBrl || hh<minHh) continue
    const pb=i.pitch_breakdown||{}, keys=Object.keys(pb)
    let mp=null, mpu=0, mpx=0
    if(keys.length){ let best=keys[0]; for(const k of keys) if((pb[k]?.u||0)>(pb[best]?.u||0)) best=k; mp=best; mpu=pb[best]?.u||0; mpx=pb[best]?.x||0 }
    const bats=i.bats, ophand=e.opp_pitcher_hand
    const adv=(bats==="L"&&ophand==="R")||(bats==="R"&&ophand==="L")
    const park=i.park_factor||1
    const comp=(b-0.08)/0.05 + 0.8*(fb-0.33)/0.07 + 0.6*(hh-0.40)/0.06 + 0.9*(mpx-0.35)/0.09 + 0.5*((park-1)/0.1)
    rows.push({name:e.full_name, player_id:e.player_id, team:e.team||"?", bats, ophand, adv,
      arm:(e.opp_pitcher_name||"").split(" ").pop(), hr9:Math.round(hr9*100)/100, hr9hot:hr9>=2.0,
      barrel:Math.round(b*100), hh:Math.round(hh*100), fb:Math.round(fb*100),
      mp, mpu:Math.round(mpu*100), mpx:Math.round(mpx*1000), mpcrush:mpx>=0.400,
      hr:Math.round((e.prob_over||0)*100), park, hot:(i.form_l7||0)>=0.4,
      confirmed:!!(i.lineup_confirmed), comp})
  }
  rows.sort((a,b)=>b.comp-a.comp)
  rows.forEach((r,ix)=>{ r.rawRank=ix+1 })   // composite rank IGNORING the per-team cap
  const tc={}, board=[], capped=[]
  for(const r of rows){
    // Qualified the filter but bumped by the ≤2-per-team cap — the "why isn't X here" bats.
    if((tc[r.team]||0)>=2){ if(capped.length<6) capped.push(r); continue }
    if(board.length>=8) continue
    tc[r.team]=(tc[r.team]||0)+1; board.push(r)
  }
  // Name the teammates ahead of each capped bat (drives the "behind Jones & Stanton" line).
  for(const r of capped){ r.ahead = board.filter(b=>b.team===r.team).map(b=>(b.name||"").split(" ").slice(-1)[0]).filter(Boolean).slice(0,2) }
  return {board, capped}
}
async function drawPowerSpots({dateStr, rows}){
  const SANS="-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif", MONO="ui-monospace,'SF Mono',Menlo,monospace"
  const WP=1080,PAD=52,rowH=118,rowGap=9,headH=222,footH=54
  const H=PAD+headH+rows.length*(rowH+rowGap)+footH+PAD
  const cv=document.createElement("canvas"); cv.width=WP; cv.height=H
  const ctx=cv.getContext("2d"); ctx.textBaseline="alphabetic"
  ctx.fillStyle=T.bg; ctx.fillRect(0,0,WP,H)
  const gl=ctx.createRadialGradient(WP*0.15,-90,70,WP*0.15,-90,1150); gl.addColorStop(0,"rgba(0,217,126,0.15)"); gl.addColorStop(1,"rgba(0,217,126,0)"); ctx.fillStyle=gl; ctx.fillRect(0,0,WP,H)
  const x=PAD
  ctx.textAlign="left"; ctx.font=`900 34px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("LINE",x,PAD+42)
  const lw=ctx.measureText("LINE").width; ctx.fillStyle=T.green; ctx.fillText("BRKR",x+lw+2,PAD+42)
  ctx.textAlign="right"; ctx.font=`800 19px ${MONO}`; ctx.fillStyle=T.green; ctx.fillText((dateStr||"").toUpperCase()+" · TODAY",WP-PAD,PAD+38)
  ctx.textAlign="left"; ctx.font=`900 60px ${SANS}`; ctx.fillStyle=T.text; ctx.fillText("POWER ",x,PAD+112)
  const pw=ctx.measureText("POWER ").width; ctx.fillStyle=T.green; ctx.fillText("SPOTS.",x+pw,PAD+112)
  ctx.font=`700 17px ${MONO}`; ctx.fillStyle=T.muted
  ctx.fillText("Hardest contact vs the most homer-prone arms — per side of the bat",x,PAD+146)
  ctx.fillText("+ does he crush the pitcher's most-thrown pitch?",x,PAD+170)
  const chip=(px,py,cw,val,lab,green)=>{
    ctx.fillStyle=green?T.greenDim:T.card; _rr(ctx,px,py,cw,54,9); ctx.fill()
    ctx.strokeStyle=green?T.greenMid:T.border2; ctx.lineWidth=1.2; _rr(ctx,px,py,cw,54,9); ctx.stroke()
    ctx.textAlign="center"; ctx.font=`900 21px ${MONO}`; ctx.fillStyle=green?T.green:T.text; ctx.fillText(val,px+cw/2,py+27)
    ctx.font=`800 9.5px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText(lab,px+cw/2,py+44); ctx.textAlign="left"
  }
  let y=PAD+headH
  rows.forEach((r,idx)=>{
    const rw=WP-2*PAD, top=idx===0
    ctx.fillStyle=top?"rgba(0,217,126,0.07)":T.card; _rr(ctx,x,y,rw,rowH,13); ctx.fill()
    ctx.strokeStyle=top?T.greenMid:T.border2; ctx.lineWidth=top?1.5:1.1; _rr(ctx,x,y,rw,rowH,13); ctx.stroke()
    ctx.fillStyle=top?T.green:"rgba(255,255,255,0.06)"; ctx.beginPath(); ctx.arc(x+36,y+rowH/2,17,0,Math.PI*2); ctx.fill()
    ctx.fillStyle=top?"#04140D":T.muted; ctx.textAlign="center"; ctx.font=`900 17px ${SANS}`; ctx.fillText(String(idx+1),x+36,y+rowH/2+6)
    ctx.textAlign="left"; ctx.font=`900 25px ${SANS}`; ctx.fillStyle=T.text
    const nm=_ellip(ctx,r.name,500); ctx.fillText(nm,x+66,y+38)
    if(r.hot){ const nw=ctx.measureText(nm).width; ctx.font=`900 11px ${MONO}`; ctx.fillStyle=T.amber; ctx.fillText("HOT",x+66+nw+12,y+35) }
    ctx.font=`700 13.5px ${MONO}`; let sx=x+66
    ctx.fillStyle=T.muted; ctx.fillText(`${r.bats} vs `,sx,y+64); sx+=ctx.measureText(`${r.bats} vs `).width
    ctx.fillStyle=r.adv?T.green:T.muted; ctx.fillText(r.ophand,sx,y+64); sx+=ctx.measureText(r.ophand).width
    ctx.fillStyle=T.muted; ctx.fillText(`  ·  ${r.arm}  `,sx,y+64); sx+=ctx.measureText(`  ·  ${r.arm}  `).width
    ctx.fillStyle=r.hr9hot?T.amber:T.muted2; ctx.font=`800 13.5px ${MONO}`; ctx.fillText(`${r.hr9} HR/9`,sx,y+64); sx+=ctx.measureText(`${r.hr9} HR/9`).width
    ctx.font=`700 13.5px ${MONO}`; ctx.fillStyle=(r.park>=1.10?T.green:(r.park<=0.95?"#c98b6f":T.muted2)); ctx.fillText(`  ·  ${r.park.toFixed(2)}x park`,sx,y+64)
    ctx.font=`800 10px ${MONO}`; ctx.fillStyle=T.muted; ctx.fillText("MAIN PITCH",x+66,y+90)
    ctx.font=`800 14px ${MONO}`; ctx.fillStyle=r.mpcrush?T.green:T.muted2
    ctx.fillText(r.mp?`${r.mp} ${r.mpu}% → .${String(r.mpx).padStart(3,"0")}`:"—", x+66+ctx.measureText("MAIN PITCH  ").width, y+91)
    let cxr=WP-PAD
    cxr-=70; chip(cxr,y+rowH/2-27,70,`${r.hr}%`,"MODEL HR",r.hr>=18); cxr-=9
    cxr-=62; chip(cxr,y+rowH/2-27,62,`${r.fb}%`,"FB",r.fb>=40); cxr-=9
    cxr-=62; chip(cxr,y+rowH/2-27,62,`${r.hh}%`,"HARD-HIT",r.hh>=55); cxr-=9
    cxr-=62; chip(cxr,y+rowH/2-27,62,`${r.barrel}%`,"BARREL",r.barrel>=18)
    y+=rowH+rowGap
  })
  ctx.textAlign="center"; ctx.font=`600 14px ${SANS}`; ctx.fillStyle=T.faint
  ctx.fillText("Calibrated model probabilities · research, not advice · 21+ · 1-800-GAMBLER",WP/2,H-PAD-4)
  ctx.textAlign="left"
  return new Promise(res=>cv.toBlob(b=>res(b),"image/png"))
}
async function sharePowerSpots(dateStr){
  const {board:rows}=await _buildPowerSpots()
  if(!rows.length) return "empty"
  let blob=null
  try { blob=await drawPowerSpots({dateStr, rows}) } catch(_){}
  const fname="linebrkr-power-spots.png"
  const top=rows[0]
  const text=`LINEBRKR Power Spots — ${dateStr}: hardest-contact bats vs the most homer-prone arms, per side.${top?` Top: ${top.name} (${top.hr}% HR).`:""} Free → linebrkr.app`
  const file=blob ? new File([blob],fname,{type:"image/png"}) : null
  try { if(file && navigator.canShare && navigator.canShare({files:[file]})){ await navigator.share({files:[file], text}); return "shared" } }
  catch(e){ if(e && e.name==="AbortError") return "cancelled" }
  if(blob){ const url=URL.createObjectURL(blob); const a=document.createElement("a"); a.href=url; a.download=fname; document.body.appendChild(a); a.click(); a.remove(); setTimeout(()=>URL.revokeObjectURL(url),4000); return "downloaded" }
  return "failed"
}

// ── Watchlist (star your guys, persisted) ───────────────────────────────────
const WATCH_KEY = "linebrkr_watch_v1"
const loadWatch = () => { try { return new Set(JSON.parse(localStorage.getItem(WATCH_KEY)||"[]")) } catch { return new Set() } }
const saveWatch = s => { try { localStorage.setItem(WATCH_KEY, JSON.stringify([...s])) } catch {} ; try { window.dispatchEvent(new Event("lb-watch")) } catch {} }
function useWatch(){
  const [w,setW] = useState(loadWatch)
  useEffect(()=>{ const h=()=>setW(loadWatch()); window.addEventListener("lb-watch",h); return ()=>window.removeEventListener("lb-watch",h) },[])
  const toggle = name => { if(!name) return; const s=loadWatch(); s.has(name)?s.delete(name):s.add(name); saveWatch(s); setW(new Set(s)) }
  return { has:n=>w.has(n), toggle, count:w.size }
}

// ── Sample fallbacks ────────────────────────────────────────────────────────
const SAMPLE_PICKS = [
  {player_id:null, full_name:"Jackson Chourio", team:"MIL", bats:"R", prob_over:0.81, edge_pct:0.295, book_over:125,  confidence:"strong", opp_pitcher_name:"A. Nola",   opp_pitcher_hand:"R", line:1.5, park_factor:1.03, lineup_slot:1},
  {player_id:null, full_name:"Aaron Judge",     team:"NYY", bats:"R", prob_over:0.79, edge_pct:0.181, book_over:-145, confidence:"strong", opp_pitcher_name:"C. Sale",   opp_pitcher_hand:"L", line:1.5, park_factor:1.08, lineup_slot:2},
  {player_id:null, full_name:"Yordan Alvarez",  team:"HOU", bats:"L", prob_over:0.74, edge_pct:0.122, book_over:-130, confidence:"high",   opp_pitcher_name:"N. Cameron", opp_pitcher_hand:"R", line:1.5, park_factor:1.01, lineup_slot:3},
  {player_id:null, full_name:"Shohei Ohtani",   team:"LAD", bats:"L", prob_over:0.71, edge_pct:0.064, book_over:-120, confidence:"high",   opp_pitcher_name:"M. King",    opp_pitcher_hand:"R", line:1.5, park_factor:0.98, lineup_slot:1},
  {player_id:null, full_name:"Juan Soto",       team:"NYY", bats:"L", prob_over:0.67, edge_pct:0.031, book_over:-110, confidence:"medium", opp_pitcher_name:"C. Sale",    opp_pitcher_hand:"L", line:1.5, park_factor:1.08, lineup_slot:3},
  {player_id:null, full_name:"Rafael Devers",   team:"BOS", bats:"L", prob_over:0.63, edge_pct:-0.012,book_over:-105, confidence:"medium", opp_pitcher_name:"G. Cole",    opp_pitcher_hand:"R", line:1.5, park_factor:1.08, lineup_slot:2},
]

const SAMPLE_GAMES = [
  { id:"NYY-BOS", away:{abbr:"NYY",city:"New York"}, home:{abbr:"BOS",city:"Boston"}, venue:"Fenway Park", time:"7:10 PM ET", grade:88, env:88, qualified:14, bestBets:3, bestMarket:"HRR", topPlay:{name:"Aaron Judge",market:"HRR",prob:0.73}, wind:"OUT 12mph", windOk:true, sub:{power:84,hits:80,strikeouts:48} },
  { id:"SD-LAD",  away:{abbr:"SD",city:"San Diego"}, home:{abbr:"LAD",city:"Los Angeles"}, venue:"Dodger Stadium", time:"9:10 PM ET", grade:65, env:62, qualified:8, bestBets:2, bestMarket:"TB", topPlay:{name:"Shohei Ohtani",market:"TB",prob:0.66}, wind:"Calm", windOk:false, sub:{power:60,hits:66,strikeouts:58} },
  { id:"HOU-TEX", away:{abbr:"HOU",city:"Houston"}, home:{abbr:"TEX",city:"Texas"}, venue:"Globe Life Field", time:"8:05 PM ET", grade:71, env:71, qualified:6, bestBets:1, bestMarket:"HRR", topPlay:{name:"Yordan Alvarez",market:"HRR",prob:0.69}, wind:"IN 8mph", windOk:false, sub:{power:70,hits:72,strikeouts:40} },
]

const SAMPLE_DETAIL = {
  bets:[
    {tier:"BEST BET", market:"HRR", player:"Aaron Judge", prob:0.73, conf:91, edge:21.4, odds:"-130", oddsNum:-130, reason:"Strong model value · high hit probability · park boost"},
    {tier:"STRONG",   market:"TB",  player:"Juan Soto",   prob:0.66, conf:84, edge:8.1,  odds:"-148", oddsNum:-148, reason:"Positive edge · park boost"},
    {tier:"VALUE SHOT",market:"HR", player:"Aaron Judge", prob:0.31, conf:80, edge:9.2,  odds:"+420", oddsNum:420, reason:"Strong model value · park boost"},
  ],
  park:{ grade:"A+", hrEnv:92, hrrEnv:84, tbEnv:88, wind:"Out to RF · 12 mph", windCall:{io:"out",part:"RF",label:"Out to RF"}, windMph:12, isDome:false, temp:"74°F", windScore:92, lf:"310ft", cf:"390ft", rf:"302ft",
    intel:"Short porch in right, warm weather, wind blowing out — elite HR conditions for left-handed power." },
  pitching:[
    {team:"BOS", name:"Chris Sale",  throws:"L", scores:{hrVuln:42,contact:38,kThreat:88,hrrRisk:40}, summary:"High strikeout threat with suppressed contact. Tougher spot for hitters.", era:"3.82", whip:"1.11", k9:"11.2", hardHit:"38%"},
    {team:"NYY", name:"Gerrit Cole", throws:"R", scores:{hrVuln:48,contact:44,kThreat:81,hrrRisk:46}, summary:"High strikeout threat with average contact. Neutral matchup.", era:"3.15", whip:"1.04", k9:"10.4", hardHit:"34%"},
  ],
  lineups:{ NYY:[
    {n:1,name:"DJ LeMahieu",hand:"RHB",attack:54,bestMarket:"HRR",tag:"Warm"},{n:2,name:"Aaron Judge",hand:"RHB",attack:73,bestMarket:"HRR",tag:"Hot"},
    {n:3,name:"Juan Soto",hand:"LHB",attack:67,bestMarket:"TB",tag:"Hot"},{n:4,name:"Giancarlo S.",hand:"RHB",attack:61,bestMarket:"HR",tag:"Warm"},
  ]},
}

const SAMPLE_PLAYER = {
  "Aaron Judge": {
    opp:"NYY vs BOS", market:"HR", tier:"BEST BET", odds:"+620", proj:"0.71", edge:"+4.1%", zone:"1.04x",
    zoneFit:{ inside:{UP:94,MID:88,DN:71}, middle:{UP:90,MID:97,DN:64}, outside:{UP:62,MID:45,DN:31} },
    pitcherZone:{ inside:{UP:8,MID:6,DN:5}, middle:{UP:7,MID:9,DN:8}, outside:{UP:14,MID:12,DN:7} },
    matchupNotes:["Crushes up & in (94) — Sale works there 8% of the time → edge","Careful down & away (31) — Sale attacks there 7%"],
    pitcherName:"Chris Sale",
    zoneFitScore:67,
    zoneFitWriteup:"Good fit — the hitter grades 67/100 in the zones Sale lives in. He's strong up & in (94) and Sale works there 8%. Watch down & away — vulnerable (31) where the pitcher attacks 7%.",
    vsPitcher:{pitcher:"Chris Sale", ab:14, h:6, hr:2, rbi:5, bb:2, k:4, avg:".429", ops:"1.210", line:"6-for-14"},
    zoneSummary:"Dominates inside pitches. Sale lives outside. Barrel rate spikes when pitchers miss inside.",
    chips:["vs LHP","Barrel Rate ↑"],
    starter:{name:"Chris Sale", throws:"L", era:"3.82", whip:"1.11", barrel:"8%"},
    handednessNote:"Left-handed starter vs right-handed hitter — favorable power split.",
    form:{ l7:0.71, l14:0.68, season:0.61 }, trend:"L7 trending up (10pts above season)",
  },
}
const samplePlayer = name => SAMPLE_PLAYER[name] || SAMPLE_PLAYER["Aaron Judge"]

// ── Live → view-model mappers (Games) ───────────────────────────────────────
const parkGrade = pf => pf>=1.15?"A+":pf>=1.08?"A":pf>=1.0?"B":pf>=0.92?"C":"D"
const clamp100 = v => Math.max(0, Math.min(100, Math.round(v)))
const gradeLetter = g => g>=88?"A+":g>=82?"A":g>=74?"A-":g>=66?"B":g>=58?"B-":g>=50?"C":g>=38?"D":"F"
const parkScore = pf => clamp100((((pf||1)-0.85)/(1.20-0.85))*100)   // park factor → 0-100
// Park grade uses a PARK-appropriate curve where a NEUTRAL park (pf≈1.0) is a C, a
// hitter's park is B/A and a pitcher's park is C-/D — reserving F for the extreme
// suppressors. (The player/game gradeLetter above is calibrated for 0-100 ratings and
// mislabeled every neutral-to-pitcher park as an F.)
const parkEnvGrade = s => { s=clamp100(s); return s>=74?"A":s>=62?"B":s>=50?"C+":s>=40?"C":s>=30?"C-":s>=20?"D":"F" }
const scoreColor = s => s>=80?T.blue : s>=66?T.green : s>=54?"#34D399" : s>=42?T.amber : T.muted2
// Prefer the backend's field-relative wind call (in/out + part of field).
const windIO = w => w?.wind_call?.io || null   // "out" | "in" | "cross" | "calm"
const windLabel = w => {
  if(!w) return "Calm"; if(w.is_dome) return "Dome"
  const wc=w.wind_call
  if(wc){ if(wc.io==="calm") return "Calm"; return w.wind_mph!=null?`${wc.label} · ${w.wind_mph} mph`:wc.label }
  return w.wind_mph!=null?`${w.wind_mph} mph`:"Calm"
}
const windScore = w => { if(!w||w.is_dome) return 50; const m=w.wind_mph||0; const io=windIO(w); if(io==="out") return Math.min(100,Math.round(55+m*3)); if(io==="in") return Math.max(0,Math.round(45-m*3)); return 50 }
// Build the Park panel object from a park factor + weather blob. This is the ONE
// place it's computed — shared by the game modal (mapDetail) and the hitter card —
// so the two Park views can never disagree. Dimensions aren't in this source, so
// they come back "—" and ParkPanel hides them (no invented 310/390/302).
function parkObj(pf, w){
  pf = pf || 1
  const wsc = windScore(w)
  return {
    grade: parkEnvGrade(0.7*parkScore(pf)+0.3*wsc),
    hrEnv: clamp100(0.6*parkScore(pf)+0.4*wsc),
    hrrEnv: clamp100(0.7*parkScore(pf)+0.3*wsc),
    tbEnv: clamp100(0.8*parkScore(pf)+0.2*wsc),
    wind: windLabel(w), windCall: (w&&w.wind_call)||null,
    windMph: w ? (w.wind_mph ?? null) : null, isDome: !!(w&&w.is_dome),
    temp: (w&&w.temp_f!=null) ? `${w.temp_f}°F` : "—",
    windScore: wsc, lf:"—", cf:"—", rf:"—",
  }
}
const formTag = f => f==="hot"?"Hot":f==="cold"?"Cold":"Neutral"
const PROP_SHORT = {"HRR_over_1.5":"HRR",HRR:"HRR","HR_anytime":"HR",total_bases:"Bases",strikeouts:"K",doubles:"2B",hits:"1+ H","hits_1.5":"2+ H"}

function mapCard(g){
  const intel=g.intel||{}, w=g.weather, pf=intel.park_factor||1
  const plays=g.plays||[]
  const top=[...plays].sort((a,b)=>(b.prob_over||0)-(a.prob_over||0))[0]
  const env=clamp100(0.6*parkScore(pf)+0.4*windScore(w))
  return { id:g.id, raw:g,
    away:{abbr:g.away?.abbreviation||g.away?.name||"—", city:g.away?.name||""},
    home:{abbr:g.home?.abbreviation||g.home?.name||"—", city:g.home?.name||""},
    venue:g.venue||"", time:fmtTime(g.commence_time), grade:intel.grade||0,
    env, qualified:plays.length, bestBets:(intel.top_plays||[]).length,
    bestMarket: top ? (PROP_SHORT[top.prop_type]||top.prop_type) : null,
    topPlay: top ? {name:top.name, market:PROP_SHORT[top.prop_type]||top.prop_type, prob:top.prob_over} : null,
    sub: intel.subgrades || null,
    attackSpot: !!intel.attack_spot, attackReason: intel.attack_reason || null,
    wind:windLabel(w), windOk:windIO(w)==="out", ppdRisk:(w&&w.ppd_risk)||null }
}
function betReason(p, market){
  const bits=[]
  if((p.edge_pct||0)>=0.08) bits.push("strong model value")
  else if((p.edge_pct||0)>0) bits.push("positive edge")
  if((p.prob_over||0)>=0.65) bits.push("high hit probability")
  if((p.park_factor||1)>=1.05) bits.push("park boost")
  const s = bits.length ? bits.join(" · ") : `${market} lean`
  return s.charAt(0).toUpperCase()+s.slice(1)
}
function mapDetail(g){
  const intel=g.intel||{}, w=g.weather||{}, pf=intel.park_factor||1
  const awayAb=g.away?.abbreviation||g.away?.name||"AWAY", homeAb=g.home?.abbreviation||g.home?.name||"HOME"
  // A hitter faces the OPPOSING starter, never his own. Fall back by team; if we
  // can't tell, show no pitcher rather than guess (which mislabeled home hitters
  // against their own teammate, e.g. Goodman "vs Sugano").
  const _awayP=g.pitchers?.away?.name||null, _homeP=g.pitchers?.home?.name||null
  const oppPitcherFor=(team)=> team===homeAb?_awayP : team===awayAb?_homeP : null
  const bets=(g.plays||[]).slice(0,8).map(p=>{
    const market=PROP_SHORT[p.prop_type]||p.prop_type
    const ent={...p, opp_pitcher_name:p.opp_pitcher_name||oppPitcherFor(p.team)}
    const conf=confidenceFor(ent), edge=(p.edge_pct||0)*100
    return { player:p.name, player_id:p.player_id, market, prob:p.prob_over, conf, edge,
      tier:tierFor(p.prob_over, conf, market, edge, p.book_over!=null),
      odds:p.book_over!=null?fmtOdds(p.book_over):"—",
      oddsNum:p.book_over, reason:betReason(p, market) }
  })
  // Pitching → LINEBRKR risk scores (derived from era/whip/k9), raw stats supporting
  const pitchers=[{p:g.pitchers?.away,team:awayAb},{p:g.pitchers?.home,team:homeAb}].filter(x=>x.p&&x.p.name)
    .map(({p,team})=>{
      const era=typeof p.era==="number"?p.era:null, whip=typeof p.whip==="number"?p.whip:null, k9=typeof p.k_per_9==="number"?p.k_per_9:null
      const kThreat = k9!=null ? clamp100((k9-5)/(13-5)*100) : null
      const contact = (k9!=null||whip!=null) ? clamp100(100 - (kThreat??50)*0.6 + ((whip??1.25)-1.1)*60) : null
      const hrVuln = (era!=null||k9!=null) ? clamp100(((era??4.2)-3)/(6-3)*70 + (1-(kThreat??50)/100)*30) : null
      const hrrRisk = (contact!=null||hrVuln!=null) ? clamp100(0.6*(contact??50)+0.4*(hrVuln??50)) : null
      let summary=null
      if(kThreat!=null && contact!=null){
        const a = kThreat<45?"Low strikeout threat":kThreat>65?"High strikeout threat":"Moderate strikeout threat"
        const b = contact>=70?"elevated contact allowed":contact<=45?"suppresses contact":"average contact allowed"
        const c = (hrrRisk??0)>=65?"Positive setup for HRR and TB props.":(hrrRisk??0)<=40?"Tougher spot for hitters.":"Neutral matchup."
        summary=`${a} with ${b}. ${c}`
      }
      return {team,name:p.name,throws:p.throws||"?",
        scores:{hrVuln,contact,kThreat,hrrRisk}, summary,
        era:era!=null?era.toFixed(2):"—",whip:whip!=null?whip.toFixed(2):"—",k9:k9!=null?k9.toFixed(1):"—",
        barrel:(typeof p.barrel_pct==="number")?`${p.barrel_pct}%`:"—",
        splits:p.splits||null, arsenal:p.arsenal||null, struggling:!!p.struggling, strugglingReasons:p.struggling_reasons||[], vsTeam:p.vs_opp_team||null}
    })
  // Lineups → attack score + best market (ranked by betting opportunity, not just order)
  const lineups={}
  for(const pl of (g.lineup||[])){
    const tm=pl.team||awayAb, props=pl.props||{}
    let best=null, bestProb=0
    for(const [m,v] of Object.entries(props)){ if((v?.prob||0)>bestProb){bestProb=v.prob; best=m} }
    ;(lineups[tm]=lineups[tm]||[]).push({n:pl.slot??"",name:pl.name,player_id:pl.player_id,
      hand:pl.bats?pl.bats+"HB":(pl.position||"—"), attack:best?clamp100(bestProb):null, bestMarket:best||"—", tag:formTag(pl.form)})
  }
  return { bets,
    park:{...parkObj(pf, w),
      intel:intel.why||`Park factor ${pf.toFixed(2)}.${(intel.warnings||[]).length?` ${intel.warnings.join(", ")}.`:""}`},
    pitching:pitchers, lineups, _teams:[awayAb,homeAb] }
}

// ── Shared UI ───────────────────────────────────────────────────────────────
function TierBadge({tier}){
  const t=TIER[tier]||TIER.WATCH
  return <span style={{fontSize:9,fontWeight:800,color:t.fg,background:t.bg,border:`1px solid ${t.bd}`,borderRadius:20,padding:"3px 9px",letterSpacing:"0.06em",whiteSpace:"nowrap"}}>{t.icon} {tierLabel(tier)}</span>
}

// ▲ heating up / ▼ cooling down — last-14-game total-bases/game vs the hitter's baseline
// (the form-study winner; replaced the noisy L7 HRR rate that mislabeled hot hitters).
function FormTrend({trend, delta, size=10}){
  if(!trend || trend==="flat") return null
  const up = trend==="up", col = up?T.green:T.red
  const p = delta!=null ? `${Math.abs(delta*100).toFixed(0)}%` : ""
  return <span title={`Form ${up?"up":"down"} ${p} vs baseline (last 14 games, total bases/game)`} style={{display:"inline-flex",alignItems:"center",gap:1,color:col,fontSize:size,fontWeight:800,flexShrink:0}}>{up?"▲":"▼"}{p}</span>
}

// A (74+) = blue · B (58+) = green · C (50+) = amber · D (38+) = orange · F = red
const gradeColor = g => g>=74?T.blue:g>=58?T.green:g>=50?T.amber:g>=38?T.orange:T.red
function GradeRing({grade,size=46}){
  const col=gradeColor(grade), r=(size-6)/2, c=2*Math.PI*r, off=c*(1-grade/100)
  return (
    <svg width={size} height={size} style={{transform:"rotate(-90deg)",flexShrink:0}}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border2} strokeWidth="3"/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth="3" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"/>
      <text x="50%" y="50%" dy="0.35em" textAnchor="middle" fill={col} style={{transform:"rotate(90deg)",transformOrigin:"center",fontSize:size*0.3,fontWeight:800,fontFamily:T.mono}}>{grade}</text>
    </svg>
  )
}

const TAG_COLOR = { Hot:T.green, Warm:T.amber, Neutral:T.muted, Cold:T.blue }
const zoneHeat = v => v>=85?{bg:"#3B0D0D",fg:T.red}:v>=70?{bg:"#2D1F00",fg:T.orange}:v>=50?{bg:T.card2,fg:T.amber}:v>=40?{bg:T.card2,fg:T.muted}:{bg:T.blueLt+"55",fg:T.blue}

function Tabs({tabs,active,onSelect}){
  return (
    <div style={{display:"flex",gap:2,borderBottom:`1px solid ${T.border}`,marginBottom:14,overflowX:"auto",scrollbarWidth:"none"}}>
      {tabs.map(t=>{ const on=active===t; return (
        <button key={t} onClick={()=>onSelect(t)} style={{flexShrink:0,padding:"9px 12px",border:"none",background:"transparent",color:on?T.green:T.muted,fontSize:12,fontWeight:on?800:600,cursor:"pointer",borderBottom:`2px solid ${on?T.green:"transparent"}`,marginBottom:-1}}>{t}</button>
      )})}
    </div>
  )
}

function Sheet({children,onClose}){
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",zIndex:300,display:"flex",alignItems:"flex-end"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:460,margin:"0 auto",background:T.card2,borderRadius:"16px 16px 0 0",border:`1px solid ${T.border2}`,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"center",paddingTop:10}}><div style={{width:32,height:4,borderRadius:2,background:T.border2}}/></div>
        {children}
      </div>
    </div>
  )
}

function Stat({label,value,color=T.text,bg=T.card2}){
  return (
    <div style={{background:bg,border:`1px solid ${T.border2}`,borderRadius:8,padding:"8px 6px",textAlign:"center"}}>
      <div style={{fontSize:16,fontWeight:800,color,fontFamily:T.mono,lineHeight:1.1}}>{value}</div>
      <div style={{fontSize:8.5,color:T.muted,letterSpacing:"0.1em",marginTop:3,textTransform:"uppercase"}}>{label}</div>
    </div>
  )
}

// Career line vs the lineup a pitcher faces today, with an expandable per-batter
// drilldown ("draw"). DISPLAY CONTEXT only — BvP is tiny-sample and is NOT a model
// input, so nothing here is color-graded as an edge.
function VsTeamBlock({vsTeam}){
  const [open,setOpen] = useState(false)
  const players = vsTeam.players||[]
  const canOpen = players.length>0
  const th = {fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.06em",textAlign:"right",padding:"0 0 4px"}
  const td = {fontSize:11,color:T.muted2,fontFamily:T.mono,textAlign:"right",padding:"3px 0",whiteSpace:"nowrap"}
  // Color from the HITTER's side: green = he's had success off this arm. Guarded by a
  // min-AB floor so a tiny-sample fluke (2-for-3 lifetime) stays neutral, not green.
  // Still context only — the "not a rating input" label stays; color is a scan aid.
  const avgColor = (h,ab)=>{
    if(ab<5) return T.text            // too few AB to read into — neutral
    const a=h/ab
    return a>=0.300?T.green : a<=0.200?T.red : T.text
  }
  return (
    <div style={{marginTop:12,borderTop:`1px solid ${T.border}`,paddingTop:10}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:7,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
        <span>VS {vsTeam.team||"THIS LINEUP"} BATTERS · CAREER</span>
        <span style={{color:T.muted,fontWeight:600,letterSpacing:0,textTransform:"none",fontStyle:"italic"}}>context, not a rating input</span>
      </div>
      <div style={{display:"flex",gap:16,flexWrap:"wrap",fontFamily:T.mono,alignItems:"baseline"}}>
        {/* Neutral on purpose: noise-level context, not a graded signal. */}
        <span style={{fontSize:19,fontWeight:800,color:T.text}}>{vsTeam.avg}</span>
        <span style={{fontSize:12,color:T.muted2}}>{vsTeam.h}/{vsTeam.ab} · {vsTeam.k} K · {vsTeam.hr} HR{vsTeam.bb?` · ${vsTeam.bb} BB`:""}</span>
      </div>
      {canOpen ? (
        <button onClick={()=>setOpen(o=>!o)} style={{marginTop:8,background:"none",border:"none",padding:0,cursor:"pointer",fontSize:10,color:T.blue,fontWeight:700,letterSpacing:"0.04em",display:"flex",alignItems:"center",gap:5}}>
          <span style={{display:"inline-block",transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>▸</span>
          {open?"Hide":"Show"} {vsTeam.batters} hitter{vsTeam.batters===1?"":"s"} vs him
        </button>
      ) : (
        <div style={{marginTop:8,fontSize:10,color:T.muted}}>{vsTeam.batters} hitter{vsTeam.batters===1?"":"s"} w/ history</div>
      )}
      {open && canOpen && (
        <div style={{marginTop:8,overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:300}}>
            <thead>
              <tr>
                <th style={{...th,textAlign:"left",letterSpacing:"0.06em"}}>BATTER</th>
                <th style={th}>AB</th><th style={th}>H</th><th style={th}>AVG</th>
                <th style={th}>2B</th><th style={th}>HR</th><th style={th}>BB</th><th style={th}>K</th>
              </tr>
            </thead>
            <tbody>
              {players.map((pl,ix)=>(
                <tr key={ix} style={{borderTop:`1px solid ${T.border}`}}>
                  <td style={{fontSize:11,color:T.text,fontWeight:600,textAlign:"left",padding:"3px 6px 3px 0",whiteSpace:"nowrap"}}>
                    {pl.name}{pl.bats?<span style={{color:T.muted,fontWeight:500,marginLeft:4}}>({pl.bats})</span>:null}
                  </td>
                  <td style={td}>{pl.ab}</td>
                  <td style={td}>{pl.h}</td>
                  <td style={{...td,color:avgColor(pl.h,pl.ab),fontWeight:700}}>{pl.avg}</td>
                  <td style={td}>{pl.doubles}</td>
                  {/* A HR off him is real success regardless of sample — green when >0. */}
                  <td style={{...td,color:pl.hr>0?T.green:T.muted2,fontWeight:pl.hr>0?700:400}}>{pl.hr}</td>
                  <td style={td}>{pl.bb}</td>
                  <td style={td}>{pl.k}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ParkPanel({park}){
  const hasDims = park.lf!=="—"||park.cf!=="—"||park.rf!=="—"
  // Field-relative wind callout: in/out + which part of the field.
  const wc=park.windCall, io=wc?.io
  const wCol = io==="out"?T.green : io==="in"?T.red : T.muted2
  const wIcon = io==="out"?"↗" : io==="in"?"↘" : io==="cross"?"↔" : "•"
  const wHead = park.isDome ? "Dome — no wind factor"
    : !wc ? "Wind data not available"
    : io==="calm" ? "Calm — minimal wind effect"
    : io==="out" ? `Blowing OUT to ${({RF:"right field",CF:"center field",LF:"left field"})[wc.part]||"the outfield"}`
    : io==="in" ? `Blowing IN from ${({RF:"right field",CF:"center field",LF:"left field"})[wc.part]||"the outfield"}`
    : wc.label
  const wSub = park.isDome ? "Climate-controlled; HR conditions are neutral."
    : !wc ? "No direction reading for this park yet."
    : io==="out" ? `Helps fly balls carry${park.windMph?` · ${park.windMph} mph` : ""} — boosts HR/TB.`
    : io==="in" ? `Knocks down fly balls${park.windMph?` · ${park.windMph} mph` : ""} — suppresses HR/TB.`
    : io==="cross" ? `${wc.label}${park.windMph?` · ${park.windMph} mph`:""} — little effect on carry.`
    : "Minimal wind effect on carry."
  return (
    <div>
      {/* Wind direction callout */}
      <div style={{display:"flex",alignItems:"center",gap:12,background:T.card,border:`1px solid ${io==="out"?T.greenMid:io==="in"?"#7F1D1D":T.border2}`,borderRadius:12,padding:"12px 14px",marginBottom:10}}>
        <span style={{fontSize:26,fontWeight:900,color:wCol,lineHeight:1,width:24,textAlign:"center"}}>{wIcon}</span>
        <div style={{minWidth:0}}>
          <div style={{fontSize:13.5,fontWeight:800,color:wCol}}>{wHead}</div>
          <div style={{fontSize:11,color:T.muted2,lineHeight:1.4,marginTop:2}}>{wSub}</div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
        <div style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:14,display:"flex",flexDirection:"column",justifyContent:"center"}}>
          <div style={{fontSize:9,color:T.green,letterSpacing:"0.1em",marginBottom:4}}>OVERALL GRADE</div>
          <div style={{fontSize:32,fontWeight:900,color:T.green,lineHeight:1}}>{park.grade}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:9,color:T.muted,letterSpacing:"0.08em"}}>WIND</span><span style={{fontSize:13,fontWeight:800,color:T.green}}>{park.wind}</span></div>
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}><span style={{fontSize:9,color:T.muted,letterSpacing:"0.08em"}}>TEMP</span><span style={{fontSize:13,fontWeight:700,color:T.text}}>{park.temp}</span></div>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
        <Stat label="HR Environment" value={park.hrEnv??"—"} color={scoreColor(park.hrEnv??0)}/>
        <Stat label="HRR Environment" value={park.hrrEnv??"—"} color={scoreColor(park.hrrEnv??0)}/>
        <Stat label="TB Environment" value={park.tbEnv??"—"} color={scoreColor(park.tbEnv??0)}/>
      </div>
      {hasDims && (<>
        <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>DIMENSIONS</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
          <Stat label="Left" value={park.lf}/><Stat label="Center" value={park.cf}/><Stat label="Right" value={park.rf}/>
        </div>
      </>)}
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>WIND SCORE FOR HR</div>
      <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><span style={{fontSize:11,color:T.muted}}>TODAY'S WIND SCORE</span><span style={{fontSize:14,fontWeight:800,color:T.green,fontFamily:T.mono}}>{park.windScore} / 100</span></div>
        <div style={{height:8,background:T.card3,borderRadius:4,overflow:"hidden"}}><div style={{height:"100%",width:`${park.windScore}%`,background:T.green,borderRadius:4}}/></div>
      </div>
      <div style={{borderLeft:`3px solid ${T.blue}`,background:T.card3,borderRadius:"0 8px 8px 0",padding:"12px 14px"}}>
        <div style={{fontSize:9,color:T.blue,fontWeight:700,letterSpacing:"0.12em",marginBottom:6}}>PARK INTEL</div>
        <div style={{fontSize:13,color:T.muted2,lineHeight:1.5}}>{park.intel}</div>
      </div>
    </div>
  )
}

// Win/Lose + Form splits from game logs
const _tb = g => (g.H||0) + (g["2B"]||0) + 3*(g.HR||0)            // ~total bases (no triples in logs)
const _hrr = g => (g.H||0) + (g.R||0) + (g.RBI||0)               // hits + runs + RBIs
const winTest = (market) =>
  market==="HR" ? (g=>(g.HR||0)>=1)
  : market==="2B" ? (g=>(g["2B"]||0)>=1)
  : market==="1+ H" ? (g=>(g.H||0)>=1)
  : market==="2+ H" ? (g=>(g.H||0)>=2)
  : market==="TB" ? (g=>_tb(g)>=2)
  : (g=>_hrr(g)>=2)                                               // HRR (default)
// The stat that matches the market — so the number shown lines up with the W/L.
const statOf = (market) =>
  market==="HR" ? (g=>g.HR||0)
  : market==="2B" ? (g=>g["2B"]||0)
  : (market==="1+ H"||market==="2+ H") ? (g=>g.H||0)
  : market==="TB" ? (g=>_tb(g))
  : (g=>_hrr(g))
// Stat label for the Win/Lose column header
const statLabel = (market) =>
  market==="HR" ? "HR" : market==="2B" ? "2B"
  : (market==="1+ H"||market==="2+ H") ? "H" : market==="TB" ? "TB" : "H+R+RBI"
// Form = how the HITTER is swinging over a window — pure batting production
// (AVG/OBP/SLG/OPS), NOT tied to any bet type. HR/RBI/HRR kept for downstream use.
const formSplits = logs => {
  if(!logs?.length) return null
  const calc = rows => {
    if(!rows.length) return {avg:0,obp:0,slg:0,ops:0,hr:0,rbi:0,hrr:0}
    const ab=rows.reduce((s,g)=>s+(g.AB||0),0), h=rows.reduce((s,g)=>s+(g.H||0),0)
    const bb=rows.reduce((s,g)=>s+(g.BB||0),0)
    const tb=rows.reduce((s,g)=>s+((g.H||0)+(g["2B"]||0)+2*(g["3B"]||0)+3*(g.HR||0)),0)
    const obp = (ab+bb) ? (h+bb)/(ab+bb) : 0
    const slg = ab ? tb/ab : 0
    return { avg: ab?h/ab:0, obp, slg, ops: obp+slg,
             hr:rows.reduce((s,g)=>s+(g.HR||0),0), rbi:rows.reduce((s,g)=>s+(g.RBI||0),0),
             hrr: rows.filter(g=>_hrr(g)>=2).length/rows.length }
  }
  return { season:calc(logs), l14:calc(logs.slice(0,14)), l7:calc(logs.slice(0,7)) }
}

// Platoon splits vs RHP / LHP from this season's game logs (carry opp_pitcher_hand).
function platoonSplits(logs){
  if(!logs || !logs.length) return null
  const calc = rows => {
    const ab=rows.reduce((s,g)=>s+(g.AB||0),0), h=rows.reduce((s,g)=>s+(g.H||0),0)
    const hrr=rows.reduce((s,g)=>s+((g.H||0)+(g.R||0)+(g.RBI||0)),0), hr=rows.reduce((s,g)=>s+(g.HR||0),0)
    return {g:rows.length, ab, avg: ab?h/ab:0, hrrG: rows.length?hrr/rows.length:0, hr}
  }
  const vsR = logs.filter(g=>(g.opp_pitcher_hand||"").toUpperCase().startsWith("R"))
  const vsL = logs.filter(g=>(g.opp_pitcher_hand||"").toUpperCase().startsWith("L"))
  if(!vsR.length && !vsL.length) return null
  return { vsR: calc(vsR), vsL: calc(vsL) }
}

// ── Player data hook ────────────────────────────────────────────────────────
// Player headshot — free MLB "spots" cutout by MLBAM id, initials fallback.
// The photo loads in the user's browser (not the sandbox), so blocked outbound
// from here doesn't matter. On any load error we fall back to the initials box.
function Headshot({mlbamId, name, size=42}){
  const [failed,setFailed] = useState(false)
  const initials = (name||"").split(" ").map(w=>w[0]).join("").slice(0,3)
  const box = {width:size,height:size,borderRadius:Math.round(size*0.24),background:T.card,border:`1px solid ${T.border2}`,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,color:T.muted,fontSize:Math.round(size*0.33),overflow:"hidden",flexShrink:0}
  if(!mlbamId || failed) return <div style={box}>{initials}</div>
  const src = `https://midfield.mlbstatic.com/v1/people/${mlbamId}/spots/${size>=90?240:120}`
  return (
    <div style={box}>
      <img src={src} alt={name||""} loading="lazy" onError={()=>setFailed(true)}
        style={{width:"100%",height:"100%",objectFit:"cover",objectPosition:"top center"}}/>
    </div>
  )
}

function usePlayerData(player){
  const name = player?.name
  const { data: liveContact } = useFetch(()=>player?.player_id?getPlayerContactLog(player.player_id):Promise.resolve(null),[player?.player_id])
  const { data: ins } = useFetch(()=>player?.player_id?getPlayerInsights(player.player_id,todayStr(),player?.market):Promise.resolve(null),[player?.player_id, player?.market])
  const { data: logsData } = useFetch(()=>player?.player_id?getPlayerGamelogs(player.player_id, new Date().getFullYear(), 200):Promise.resolve(null),[player?.player_id])
  const logs = logsData?.logs || null
  const s = samplePlayer(name)
  // isLive = a real player tapped from live data. In that case we must NEVER
  // fall back to another player's sample reasoning (that made every card show
  // the same Aaron Judge / Chris Sale zone fit). Show real data or nothing.
  const isLive = !!player?.player_id
  const intel = ins?.intelligence
  const contact = (liveContact&&liveContact.log?.length)
    ? liveContact
    : (isLive ? null : (CONTACT_SAMPLE[name]||CONTACT_SAMPLE["Aaron Judge"]))
  const p = {
    // header / stat-strip ultimate fallbacks only (real values come from `player`/`strip`)
    // proj/edge/odds MUST be gated by isLive like opp — otherwise a live player
    // with no projection/odds shows the sample player's numbers (Aaron Judge's
    // "0.71" prob, "+4.1%" edge, "+620" line) as if real. Live → null → tiles show "—".
    market:s.market, tier:s.tier, opp:(isLive ? "" : s.opp),
    odds:(isLive ? null : s.odds), proj:(isLive ? null : s.proj), edge:(isLive ? null : s.edge), zone:s.zone,
    zoneFit:     intel?.zoneFit      || (isLive ? null : s.zoneFit),
    pitcherZone: intel?.pitcherZone  || (isLive ? null : s.pitcherZone),
    matchupNotes:(intel?.matchupNotes?.length ? intel.matchupNotes : (isLive ? [] : s.matchupNotes)),
    pitcherName: intel?.pitcherName  || (isLive ? null : s.pitcherName),
    zoneFitScore: (intel?.zoneFitScore!=null ? intel.zoneFitScore : (isLive ? null : s.zoneFitScore)),
    zoneFitWriteup: intel?.zoneFitWriteup || (isLive ? null : s.zoneFitWriteup),
    vsPitcher:   ins?.vs_pitcher     || (isLive ? null : s.vsPitcher),
    vsPitcherStatus: ins?.vs_pitcher_status || null,
    pitchMatchup: ins?.pitch_matchup || null,
    pitchEdge:   ins?.pitch_edge     || null,
    zoneSummary: intel?.summary      || (isLive ? null : s.zoneSummary),
    chips:       (intel?.chips?.length ? intel.chips : (isLive ? [] : s.chips)),
    starter:     ins?.matchup?.starter || (isLive ? null : s.starter),
    handednessNote: ins?.matchup?.note || (isLive ? null : s.handednessNote),
    form:        ins?.form           || (isLive ? null : s.form),
    trend:       ins?.form?.trend    || (isLive ? null : s.trend),
    theCase:     ins?.case           || null,
    // Real park + weather for THIS player's game (same source as the game modal),
    // so the Park tab matches instead of showing sample data. null for live players
    // with no projection/venue yet → the tab shows an honest empty state.
    parkData:    ins?.park           || null,
  }
  return { p, strip:ins?.stat_strip||{}, contact, logs, mlbamId:(player?.mlbam_id||logsData?.mlbam_id||null) }
}

// ── Player modal ────────────────────────────────────────────────────────────
// ── My Board — a personal, reorderable pinboard of plays, saved on-device ─────
const BOARD_KEY = "linebrkr_board_v1"
const loadBoard = () => { try{ return JSON.parse(localStorage.getItem(BOARD_KEY)||"[]")||[] }catch{ return [] } }
const saveBoard = arr => { try{ localStorage.setItem(BOARD_KEY, JSON.stringify(arr)); window.dispatchEvent(new Event("lb-board")) }catch{} }
function useBoard(){
  const [board,setBoard] = useState(loadBoard)
  useEffect(()=>{ const h=()=>setBoard(loadBoard()); window.addEventListener("lb-board", h); return ()=>window.removeEventListener("lb-board", h) }, [])
  const has = key => board.some(b=>b.key===key)
  const toggle = play => { const cur=loadBoard(); const i=cur.findIndex(b=>b.key===play.key); saveBoard(i>=0 ? cur.filter(b=>b.key!==play.key) : [...cur, play]) }
  const remove = key => saveBoard(loadBoard().filter(b=>b.key!==key))
  const move = (key,dir) => { const cur=loadBoard(); const i=cur.findIndex(b=>b.key===key); const j=i+dir; if(i<0||j<0||j>=cur.length) return; const c=[...cur]; [c[i],c[j]]=[c[j],c[i]]; saveBoard(c) }
  return {board, has, toggle, remove, move}
}
// Board overlay: your pinned plays, reorderable, tap to open. Snapshot values are
// from pin time — the tap-through card is always live.
function BoardSheet({onClose, onPlayer}){
  const {board, remove, move} = useBoard()
  return (
    <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(4,10,8,0.72)",backdropFilter:"blur(3px)",display:"flex",flexDirection:"column"}}>
      <div style={{maxWidth:460,margin:"0 auto",width:"100%",boxSizing:"border-box",padding:"14px 16px",flex:1,overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <span style={{fontSize:17,fontWeight:900}}>📌 My Board <span style={{fontSize:12,color:T.muted2,fontWeight:700}}>· {board.length}</span></span>
          <button onClick={onClose} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,color:T.muted,fontSize:14,cursor:"pointer",padding:"5px 12px"}}>Done</button>
        </div>
        {board.length===0 ? (
          <div style={{textAlign:"center",padding:"48px 20px",color:T.muted}}>
            <div style={{fontSize:34,marginBottom:10}}>📌</div>
            <div style={{fontSize:14,fontWeight:700,marginBottom:4,color:T.text}}>No pinned plays yet</div>
            <div style={{fontSize:12.5,lineHeight:1.5}}>Open any player card and tap <b style={{color:T.text}}>📌 Pin</b> to build your own curated board — separate from the auto-ranked lists.</div>
          </div>
        ) : board.map((b,i)=>(
          <div key={b.key} style={{display:"flex",alignItems:"center",gap:10,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"11px 12px",marginBottom:8}}>
            <div style={{display:"flex",flexDirection:"column",gap:2}}>
              <button onClick={()=>move(b.key,-1)} disabled={i===0} style={{background:"none",border:"none",color:i===0?T.muted2:T.muted,cursor:i===0?"default":"pointer",fontSize:12,lineHeight:1,opacity:i===0?0.4:1,padding:0}}>▲</button>
              <button onClick={()=>move(b.key,1)} disabled={i===board.length-1} style={{background:"none",border:"none",color:i===board.length-1?T.muted2:T.muted,cursor:i===board.length-1?"default":"pointer",fontSize:12,lineHeight:1,opacity:i===board.length-1?0.4:1,padding:0}}>▼</button>
            </div>
            <button onClick={()=>{ b.player_id&&onPlayer&&onPlayer({name:b.name,player_id:b.player_id,market:b.market,conf:b.conf,prob:b.prob,opp:b.opp,team:b.team,slot:b.slot,park:b.park}); onClose&&onClose() }} style={{flex:1,minWidth:0,textAlign:"left",background:"none",border:"none",cursor:"pointer",color:T.text,padding:0}}>
              <div style={{fontSize:14,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.name} <span style={{fontSize:10.5,color:T.muted2,fontFamily:T.mono}}>{b.market}</span></div>
              <div style={{fontSize:11,color:T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.team}{b.opp&&!String(b.opp).startsWith(b.team||"")?` · ${b.opp}`:b.opp?` ${String(b.opp).replace(b.team||"","").trim()}`:""}</div>
            </button>
            <div style={{textAlign:"right",flexShrink:0}}>
              {b.conf!=null && <div style={{fontSize:15,fontWeight:900,fontFamily:T.mono,color:ratingColor(b.conf)}}>{Math.round(b.conf)}</div>}
              {b.prob!=null && <div style={{fontSize:11,fontFamily:T.mono,color:probColorMkt(b.prob,b.market)}}>{pct(b.prob)}</div>}
            </div>
            <button onClick={()=>remove(b.key)} style={{flexShrink:0,background:"none",border:"none",color:T.muted2,fontSize:15,cursor:"pointer"}}>✕</button>
          </div>
        ))}
        {board.length>0 && <div style={{fontSize:10,color:T.muted2,marginTop:6,lineHeight:1.4}}>Rating / prob are from when you pinned — tap a play to open its live card. Reorder with ▲▼.</div>}
      </div>
    </div>
  )
}

// What-if explorer: drag batting order / park and watch the projection move,
// computed from the model's prob via the Poisson rescale above. Estimate only.
function WhatIfPanel({prob, market, slot0, park0, onAddToSlip, inSlip}){
  const [open,setOpen] = useState(false)
  const [added,setAdded] = useState(false)
  const m = _MKT_TO_LABEL[market] || market   // normalize db prop_type → short label
  const L = CANON_LINE[m] ?? 1.5
  const s0 = Math.min(9, Math.max(1, Math.round(slot0||3)))
  const pk0 = park0 || 1.0
  const [slot,setSlot] = useState(s0)
  const [park,setPark] = useState(pk0)
  const lam0 = invPoisson(prob, L)
  // park moves HR/power markets most; hits far less. Scale the park delta by market.
  const parkW = (m==="HR"||m==="HRR"||m==="TB") ? 1 : 0.3
  const paMult = PA_BY_SLOT[slot] / PA_BY_SLOT[s0]
  const pkMult = 1 + ((park/pk0) - 1) * parkW
  const est = poissonTail(lam0 * paMult * Math.max(0.2, pkMult), L)
  const delta = est - prob
  const dirty = slot!==s0 || Math.abs(park-pk0)>0.001
  const lab = {display:"flex",justifyContent:"space-between",fontSize:11,color:T.muted,fontWeight:700,marginBottom:3}
  return (
    <div style={{margin:"12px 0 0",border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 13px",background:T.card2,border:"none",cursor:"pointer",color:T.text}}>
        <span style={{fontSize:12.5,fontWeight:800}}>⚗ What-if <span style={{fontSize:9.5,color:T.muted2,fontWeight:700}}>· estimate</span></span>
        <span style={{fontSize:11,color:T.muted}}>{open?"▾":"▴"}</span>
      </button>
      {open && (
        <div style={{padding:"12px 13px 13px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:T.card,border:`1px solid ${dirty?T.greenMid:T.border2}`,borderRadius:10,padding:"10px 13px",marginBottom:13}}>
            <div><div style={{fontSize:9,color:T.muted2,fontWeight:800,letterSpacing:"0.06em"}}>ESTIMATED PROB</div><div style={{fontSize:26,fontWeight:900,fontFamily:T.mono,color:probColorMkt(est,m)}}>{pct(est)}</div></div>
            <div style={{textAlign:"right"}}><div style={{fontSize:9,color:T.muted2,fontWeight:800,letterSpacing:"0.06em"}}>vs MODEL {pct(prob)}</div><div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:Math.abs(delta)<0.005?T.muted:delta>0?T.green:T.red}}>{delta>0?"+":""}{Math.round(delta*1000)/10}%</div></div>
          </div>
          <div style={lab}><span>Batting order</span><span style={{color:T.text,fontFamily:T.mono}}>#{slot}{slot!==s0?` (was #${s0})`:""}</span></div>
          <input type="range" min={1} max={9} step={1} value={slot} onChange={e=>setSlot(+e.target.value)} style={{width:"100%",accentColor:T.green,marginBottom:13}}/>
          <div style={lab}><span>Park factor</span><span style={{color:T.text,fontFamily:T.mono}}>{park.toFixed(2)}x{Math.abs(park-pk0)>0.001?` (was ${pk0.toFixed(2)})`:""}</span></div>
          <input type="range" min={0.80} max={1.25} step={0.01} value={park} onChange={e=>setPark(+e.target.value)} style={{width:"100%",accentColor:T.green,marginBottom:6}}/>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
            <span style={{fontSize:9.5,color:T.muted2,lineHeight:1.4,maxWidth:"78%"}}>Poisson rescale of the model's projection — a feel for sensitivity, not a re-run of the full model.</span>
            {dirty && <button onClick={()=>{ setSlot(s0); setPark(pk0) }} style={{background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>reset</button>}
          </div>
          {onAddToSlip && dirty && (
            <button onClick={()=>{ onAddToSlip(est); setAdded(true); setTimeout(()=>setAdded(false),2200) }}
              style={{width:"100%",marginTop:11,padding:"9px",borderRadius:9,background:added?T.green:T.greenDim,border:`1px solid ${T.green}`,color:added?"#04140D":T.green,fontSize:12,fontWeight:800,cursor:"pointer"}}>
              {added ? "✓ Added at "+pct(est) : (inSlip?`Update slip to this estimate (${pct(est)})`:`＋ Add to slip at this estimate (${pct(est)})`)}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Matchup Breakdown (hitter vs opposing pitcher, head-to-head) ──────────────
// Reachable two ways: a search box (look up any hitter) and a button on every
// player card (opens pre-filled for that pick). Renders the batter's factor line
// and the pitcher's side by side — each factor color-leaned green (favorable for
// the hitter) / red (concern) — plus the model's prob + Rating per market, park,
// and a plain-English read. All data comes from the open /free/matchup route.
const leanColor = l => l==="good" ? T.green : l==="bad" ? T.red : T.muted
function FactorLine({f}){
  const c = leanColor(f.lean)
  const dot = f.lean==="good" ? "▲" : f.lean==="bad" ? "▼" : "•"
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"7px 0",borderTop:`1px solid ${T.border}`}}>
      <span style={{fontSize:11.5,color:T.muted2,fontWeight:600}}>{f.label}</span>
      <span style={{display:"flex",alignItems:"center",gap:6,fontFamily:T.mono,fontSize:12.5,fontWeight:800,color:c}}>
        {f.value}<span style={{fontSize:10}}>{dot}</span>
      </span>
    </div>
  )
}
function MatchupBody({initial, onClose, embedded}){
  const { isPro, needPro } = useProGate()
  const [q,setQ]         = useState(initial?.name || "")
  const [data,setData]   = useState(null)
  const [loading,setLoad]= useState(false)
  const [err,setErr]     = useState(null)
  const [projOpen,setProjOpen] = useState(false)   // 🔮 "project vs any pitcher" mode (Pro)
  const [pitcher,setPitcher]   = useState("")       // chosen arm for projected mode
  const [plist,setPlist]       = useState([])       // pitcher names for the picker autocomplete
  const load = (who) => {
    const key = who!=null ? who : q
    if(key===null || key===undefined || `${key}`.trim()==="") return
    const usePitcher = projOpen && pitcher.trim()
    setLoad(true); setErr(null)
    const req = usePitcher ? getProjectedMatchup(key, pitcher.trim()) : getMatchup(key)
    req.then(d=>{
      if(d && d.error){ setData(null); setErr(d.detail || (usePitcher?"No projection for that pairing.":"No matchup found.")) }
      else { setData(d); setErr(null) }
      setLoad(false)
    }).catch(()=>{ setErr(usePitcher?"Could not project that matchup.":"Could not load matchup."); setLoad(false) })
  }
  // Auto-load when opened from a pick (prefer the stable id).
  useEffect(()=>{ if(initial && (initial.player_id || initial.name)) load(initial.player_id || initial.name) }, [])
  // Lazy-load the pitcher list the first time the user opens projected mode.
  useEffect(()=>{ if(projOpen && plist.length===0){ getPitcherData().then(d=>{
    const rows=(Array.isArray(d?.rows)&&d.rows.length)?d.rows:(Array.isArray(d?.flagged)?d.flagged:[])
    setPlist([...new Set(rows.map(r=>r.name).filter(Boolean))].sort())
  }).catch(()=>{}) } }, [projOpen])
  const b = data?.batter, pit = data?.pitcher
  const pf = data?.park_factor
  const parkLean = pf==null ? "neutral" : pf>=1.05 ? "good" : pf<=0.95 ? "bad" : "neutral"
  return (
      <div style={{padding: embedded ? "10px 14px 24px" : "14px 16px 40px"}}>
        {embedded ? (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:16,fontWeight:900,letterSpacing:"-0.02em"}}>🆚 Matchup Breakdown</div>
            <div style={{fontSize:11.5,color:T.muted,marginTop:3,lineHeight:1.4}}>Search any hitter for the head-to-head read vs today's pitcher — or <b style={{color:T.green}}>🔮 project them against any pitcher</b> you pick. Factors, markets, pitch-by-pitch fit, and the plain-English take.</div>
          </div>
        ) : (
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <div style={{fontSize:17,fontWeight:900,letterSpacing:"-0.02em"}}>🆚 Matchup Breakdown</div>
            <button onClick={onClose} style={{width:32,height:32,borderRadius:9,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:15,cursor:"pointer"}}>✕</button>
          </div>
        )}

        {/* Search — look up any hitter */}
        <form onSubmit={e=>{e.preventDefault(); load()}} style={{display:"flex",gap:8,marginBottom:14}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search a hitter (e.g. Aaron Judge)"
            style={{flex:1,minWidth:0,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",color:T.text,fontSize:13,outline:"none"}}/>
          <button type="submit" style={{flexShrink:0,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"0 16px",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer"}}>Look up</button>
        </form>

        {/* 🔮 Project vs any pitcher — Pro power tool. The default lookup uses the hitter's
            SCHEDULED arm; this re-runs the model against ANY pitcher you choose. needPro()
            opens the upgrade sheet for free users, reveals the picker for Pro/Founder. */}
        <button onClick={()=>{ if(projOpen){ setProjOpen(false); return } if(needPro()) setProjOpen(true) }}
          style={{width:"100%",display:"flex",alignItems:"center",gap:9,background:projOpen?T.greenDim:T.card,border:`1px solid ${projOpen?T.green:T.border2}`,borderRadius:10,padding:"9px 12px",color:projOpen?T.green:T.muted2,fontSize:12,fontWeight:800,cursor:"pointer",marginBottom:projOpen?10:14}}>
          <span style={{fontSize:14}}>🔮</span>
          <span style={{flex:1,textAlign:"left"}}>Project vs any pitcher <span style={{color:T.muted,fontWeight:600}}>· run the model on any hitter × pitcher</span></span>
          {!isPro && <span style={{fontSize:8,fontWeight:800,color:T.amber}}>PRO</span>}
          <span style={{fontSize:12,color:T.muted2,transform:projOpen?"rotate(180deg)":"none",transition:"transform .15s"}}>▾</span>
        </button>
        {projOpen && (
          <form onSubmit={e=>{e.preventDefault(); load()}} style={{display:"flex",gap:8,marginBottom:14}}>
            <input list="lb-pitcher-list" value={pitcher} onChange={e=>setPitcher(e.target.value)} placeholder="Pick a pitcher (e.g. Grant Holmes)"
              style={{flex:1,minWidth:0,background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"10px 12px",color:T.text,fontSize:13,outline:"none"}}/>
            <datalist id="lb-pitcher-list">{plist.map((n,i)=><option key={i} value={n}/>)}</datalist>
            <button type="submit" disabled={!q.trim()||!pitcher.trim()}
              style={{flexShrink:0,background:(q.trim()&&pitcher.trim())?T.green:T.card,border:`1px solid ${(q.trim()&&pitcher.trim())?T.green:T.border2}`,borderRadius:10,padding:"0 16px",color:(q.trim()&&pitcher.trim())?"#06210f":T.muted2,fontSize:13,fontWeight:800,cursor:(q.trim()&&pitcher.trim())?"pointer":"default"}}>Project</button>
          </form>
        )}

        {loading && <div style={{textAlign:"center",padding:"28px 0",color:T.muted,fontSize:13}}>{projOpen&&pitcher.trim()?"Projecting matchup…":"Loading matchup…"}</div>}
        {!loading && err && <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"18px 14px",textAlign:"center",color:T.muted,fontSize:13}}>{err}</div>}

        {!loading && data && b && (<>
          {/* Header — batter vs pitcher */}
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"13px 15px",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:17,fontWeight:900,letterSpacing:"-0.02em",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.name}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>{[b.team, b.bats?`bats ${b.bats}`:null].filter(Boolean).join(" · ")||"—"}</div>
              </div>
              <div style={{flexShrink:0,fontSize:12,fontWeight:800,color:T.muted}}>vs</div>
              <div style={{minWidth:0,textAlign:"right"}}>
                <div style={{fontSize:15,fontWeight:900,letterSpacing:"-0.02em",color:T.muted2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{pit?.name||"TBD"}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:2}}>{pit?.throws?`throws ${pit.throws}`:"Starting pitcher"}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:11,flexWrap:"wrap"}}>
              {data.projected && <span style={{fontSize:10.5,fontWeight:800,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:7,padding:"4px 9px"}}>🔮 PROJECTED{data.neutral_park?" · neutral park":""}</span>}
              {data.venue && <span style={{fontSize:10.5,color:T.muted2,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"4px 9px"}}>🏟️ {data.venue}</span>}
              {pf!=null && <span style={{fontSize:10.5,fontWeight:800,color:leanColor(parkLean),background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"4px 9px"}}>Park {Number(pf).toFixed(2)}x</span>}
              {data.date && <span style={{fontSize:10.5,color:T.muted,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"4px 9px"}}>{data.date}</span>}
            </div>
          </div>

          {/* The read */}
          {data.read && (
            <div style={{background:T.card3,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"12px 14px",marginBottom:14}}>
              <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.12em",marginBottom:6}}>THE READ</div>
              <div style={{fontSize:13,color:T.muted2,lineHeight:1.5}}>{data.read}</div>
            </div>
          )}

          {/* Markets — prob + Rating for each */}
          {data.markets?.length>0 && (
            <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"12px 14px",marginBottom:14}}>
              <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:"0.12em",marginBottom:8}}>MARKETS</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 44px 52px 52px 52px",gap:6,padding:"0 0 6px"}}>
                {["Bet","Line","Prob","Rating","Edge"].map((h,i)=>(
                  <div key={h} style={{fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",textAlign:i===0?"left":"center"}}>{h}</div>
                ))}
              </div>
              {data.markets.map((m,i)=>(
                <div key={m.market||i} style={{display:"grid",gridTemplateColumns:"1fr 44px 52px 52px 52px",gap:6,alignItems:"center",padding:"8px 0",borderTop:`1px solid ${T.border}`}}>
                  <div style={{fontSize:12.5,fontWeight:800,color:T.text}}>{m.label}</div>
                  <div style={{textAlign:"center",fontSize:11,fontFamily:T.mono,color:T.muted}}>O{Number(m.line).toFixed(1)}</div>
                  <div style={{textAlign:"center",fontSize:14,fontWeight:900,fontFamily:T.mono,color:probColorMkt(m.prob_over, m.market)}}>{pct(m.prob_over)}</div>
                  <div style={{textAlign:"center",fontSize:12.5,fontWeight:800,fontFamily:T.mono,color:m.rating!=null?ratingColor(m.rating):T.muted}}>{m.rating!=null?m.rating:"—"}</div>
                  <div style={{textAlign:"center",fontSize:11,fontFamily:T.mono,color:m.edge_pct!=null?(m.edge_pct>0?T.green:T.muted):T.muted}}>{m.edge_pct!=null?`${m.edge_pct>=0?"+":""}${(m.edge_pct*100).toFixed(1)}%`:"—"}</div>
                </div>
              ))}
            </div>
          )}

          {/* Factors — batter vs pitcher side by side */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:6}}>
            <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"12px 13px"}}>
              <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.1em",marginBottom:2}}>BATTER</div>
              {data.factors?.batter?.length>0
                ? data.factors.batter.map((f,i)=><FactorLine key={i} f={f}/>)
                : <div style={{fontSize:11,color:T.muted,padding:"10px 0"}}>No Statcast data</div>}
            </div>
            <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"12px 13px"}}>
              <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:"0.1em",marginBottom:2}}>PITCHER</div>
              {data.factors?.pitcher?.length>0
                ? data.factors.pitcher.map((f,i)=><FactorLine key={i} f={f}/>)
                : <div style={{fontSize:11,color:T.muted,padding:"10px 0"}}>No pitcher data</div>}
            </div>
          </div>
          {/* Pitch-by-pitch fit — how this bat handles each pitch in THIS arm's arsenal. */}
          {data.pitch_matchup?.length>0 && (
            <div style={{marginTop:12}}>
              <PitchMatchupPanel matchup={data.pitch_matchup} edge={data.pitch_edge} pitcherName={pit?.name} hitterName={b?.name}/>
            </div>
          )}
          <div style={{fontSize:9.5,color:T.muted,lineHeight:1.5,marginTop:8}}>Green = favorable for the hitter · red = a concern. Pitcher stats are graded from the hitter's side. Information only — not betting advice. 21+.</div>
        </>)}

        {!loading && data && data.note && (
          <div style={{background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:10,padding:"10px 12px",marginTop:10,fontSize:11.5,color:T.amber,lineHeight:1.45}}>{data.note}</div>
        )}

        {!loading && !data && !err && (
          <div style={{textAlign:"center",padding:"28px 0",color:T.muted,fontSize:13}}>Search a hitter above to see the head-to-head breakdown.</div>
        )}
      </div>
  )
}
function MatchupDrawer({initial, onClose}){
  return (
    <Sheet onClose={onClose}>
      <MatchupBody initial={initial} onClose={onClose} embedded={false}/>
    </Sheet>
  )
}

// Interactive pitch matchup — the at-a-glance table PLUS a tap-to-focus spotlight
// (the "toggle the four-seam" drill-down). Tap a pitch chip (or a table row) to
// spotlight how the hitter does against it; the full table lives behind a toggle so
// the default view stays compact.
function PitchMatchupPanel({matchup, edge, pitcherName, hitterName, onOpenContact}){
  const fmtX = x => x!=null ? `.${String(x).replace(/^0?\./,"").padEnd(3,"0")}` : "—"
  // Default spotlight: the pitch that most moves the matchup — a "crushes" first, else
  // the most-thrown pitch — so a meaningful read shows before any tap.
  const defIdx = (()=>{ const c=matchup.findIndex(m=>m.rating==="crushes"); if(c>=0) return c
    let bi=0,bu=-1; matchup.forEach((m,i)=>{ if((m.usage||0)>bu){bu=m.usage||0;bi=i} }); return bi })()
  const [sel,setSel] = React.useState(defIdx)
  const [showAll,setShowAll] = React.useState(false)
  const [recOpen,setRecOpen] = React.useState(false)
  const [cheatOpen,setCheatOpen] = React.useState(false)
  // Custom-mix ("cheat code") selection — a Set of pitch indices. Default to the
  // pitcher's main arsenal (top pitches to ~55% cumulative usage, min 2) so the blended
  // read is meaningful before any tap; the user narrows/expands from there.
  const [mix,setMix] = React.useState(()=>{
    const order=[...matchup.keys()].sort((a,b)=>(matchup[b].usage||0)-(matchup[a].usage||0))
    const s=new Set(); let cum=0
    for(const i of order){ s.add(i); cum+=matchup[i].usage||0; if(cum>=55 && s.size>=2) break }
    return s
  })
  const toggleMix = ix => setMix(prev=>{ const s=new Set(prev); s.has(ix)?s.delete(ix):s.add(ix); return s })
  const m = matchup[sel] || matchup[0]
  const tone = m.rating==="crushes"?T.green : m.rating==="struggles"?T.red : T.muted2
  const verdict = m.rating==="crushes"?"Crushes it" : m.rating==="struggles"?"Struggles" : "Neutral"
  const last = (hitterName||"").split(" ").slice(-1)[0] || "He"
  const verbFor = r => r==="crushes"?"crushes" : r==="struggles"?"struggles with" : "is roughly league-average against"
  return (
    <div style={{marginBottom:14}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:7}}>PITCH MATCHUP{pitcherName?` vs ${pitcherName}`:""}</div>
      {edge && (()=>{ const tc=edge.tone==="good"?T.green:edge.tone==="bad"?T.red:T.muted2; return (
        <div style={{display:"flex",alignItems:"center",gap:7,background:`${tc}1a`,border:`1px solid ${tc}`,borderRadius:8,padding:"8px 11px",marginBottom:8}}>
          <span style={{fontSize:13}}>{edge.tone==="good"?"🔥":edge.tone==="bad"?"🧱":"•"}</span>
          <span style={{fontSize:12.5,fontWeight:700,color:tc}}>{edge.text}</span>
        </div>
      )})()}
      {/* Tap-to-focus pitch chips */}
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
        {matchup.map((mm,ix)=>{ const on=ix===sel; const c=mm.rating==="crushes"?T.green:mm.rating==="struggles"?T.red:T.muted2; return (
          <button key={ix} onClick={()=>setSel(ix)} style={{fontFamily:T.mono,fontSize:11,fontWeight:800,padding:"6px 11px",borderRadius:8,cursor:"pointer",border:`1px solid ${on?c:T.border2}`,background:on?`${c}22`:T.card,color:on?c:T.muted2}}>
            {mm.pitch}{mm.rating==="crushes"?" 🔥":""} <span style={{opacity:0.7,fontWeight:600}}>{mm.usage}%</span>
          </button>
        )})}
      </div>
      {/* Spotlight for the selected pitch */}
      <div style={{background:`${tone}14`,border:`1px solid ${tone}`,borderRadius:11,padding:"12px 14px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:9}}>
          <span style={{fontSize:15,fontWeight:900,color:T.text}}>{m.pitch}</span>
          <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.06em",color:tone,textTransform:"uppercase"}}>{verdict}</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
          {[["xwOBA",fmtX(m.hitter_xwoba),tone],["WHIFF",m.hitter_whiff!=null?`${m.hitter_whiff}%`:"—",T.text],["USAGE",`${m.usage}%`,T.text]].map(([l,v,c],i)=>(
            <div key={i} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"9px 10px",textAlign:"center"}}>
              <div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:c}}>{v}</div>
              <div style={{fontSize:8.5,fontWeight:700,letterSpacing:"0.08em",color:T.muted,marginTop:3}}>{l}</div>
            </div>
          ))}
        </div>
        {/* Recent form subcomponent — a self-contained last-30d panel for the selected
            pitch, gated on sample size so it never reads as confident off a few pitches. */}
        {m.recent_n!=null && m.recent_n>0 && (()=>{
          const enough = m.recent_n>=5 && m.recent_xwoba!=null
          const xDelta = (enough && m.hitter_xwoba!=null) ? Math.round((m.recent_xwoba-m.hitter_xwoba)*1000) : null
          const wDelta = (m.recent_whiff!=null && m.hitter_whiff!=null) ? (m.recent_whiff-m.hitter_whiff) : null
          // Contact-card color thresholds so the drawer reads consistently with the Contact tab.
          const xwC = m.recent_xwoba>=0.360?T.green:m.recent_xwoba<=0.290?T.red:T.text
          const brC = m.recent_barrel>=12?T.green:m.recent_barrel>=8?T.amber:T.muted2
          const hhC = m.recent_hard_hit>=50?T.green:m.recent_hard_hit>=40?T.amber:T.muted2
          const evC = m.recent_avg_ev>=91?T.green:m.recent_avg_ev>=88?T.amber:T.muted2
          const whC = m.recent_whiff<=20?T.green:m.recent_whiff<=26?"#34D399":m.recent_whiff>=30?T.red:T.muted2
          const cells = [["xwOBA", fmtX(m.recent_xwoba), xwC, xDelta!=null?`${xDelta>=0?"+":""}${xDelta} vs szn`:null]]
          if(m.recent_barrel!=null)   cells.push(["Barrel", `${m.recent_barrel}%`, brC, null])
          if(m.recent_hard_hit!=null) cells.push(["Hard-hit", `${m.recent_hard_hit}%`, hhC, null])
          if(m.recent_avg_ev!=null)   cells.push(["Avg EV", `${m.recent_avg_ev}`, evC, "mph"])
          if(m.recent_whiff!=null)    cells.push(["Whiff", `${m.recent_whiff}%`, whC, wDelta!=null?`${wDelta<=0?"":"+"}${wDelta}pt vs szn`:null])
          const results = enough ? [
            (m.recent_hr!=null&&m.recent_hr>0)?`${m.recent_hr} HR`:null,
            m.recent_hits!=null?`${m.recent_hits} H`:null,
            (m.recent_xbh!=null&&m.recent_xbh>0)?`${m.recent_xbh} XBH`:null,
          ].filter(Boolean).join(" · ") : ""
          return (
            <div style={{marginTop:9,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,overflow:"hidden"}}>
              {/* Tap-to-open drawer — recent batted-ball card for the selected pitch */}
              <button onClick={()=>setRecOpen(v=>!v)} style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"9px 11px",background:"none",border:"none",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.09em",color:T.muted2,textTransform:"uppercase"}}>Recent form · L30 vs {m.pitch}</span>
                <span style={{marginLeft:"auto",fontSize:10.5,color:T.faint,fontFamily:T.mono}}>{enough&&m.recent_xwoba!=null?`${fmtX(m.recent_xwoba)} · `:""}{m.recent_n} bb</span>
                <span style={{fontSize:11,color:T.green,fontWeight:800}}>{recOpen?"▾":"▸"}</span>
              </button>
              {recOpen && <>
                {enough
                  ? <div style={{padding:"0 11px 10px"}}>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
                        {cells.map(([l,v,c,sub],i)=>(
                          <div key={i} style={{background:T.card2,border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 10px"}}>
                            <div style={{fontSize:8,fontWeight:800,letterSpacing:"0.07em",color:T.muted,textTransform:"uppercase"}}>{l}</div>
                            <div style={{fontFamily:T.mono,fontSize:15,fontWeight:900,color:c,marginTop:2}}>{v}</div>
                            {sub && <div style={{fontSize:8.5,color:T.muted2,marginTop:1}}>{sub}</div>}
                          </div>
                        ))}
                      </div>
                      {results && <div style={{marginTop:8,fontSize:11,color:T.text,fontWeight:700}}>Results: <span style={{color:T.green}}>{results}</span> <span style={{color:T.muted,fontWeight:400}}>in {m.recent_bip||m.recent_n} batted balls, last 30d</span></div>}
                    </div>
                  : <div style={{padding:"0 11px 10px",fontSize:10.5,color:T.muted,lineHeight:1.4}}><b style={{color:T.muted2}}>Small sample</b> ({m.recent_n} batted {m.recent_n===1?"ball":"balls"} in 30d) — lean on the season read above.</div>
                }
                {onOpenContact && <button onClick={onOpenContact} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 11px",background:"none",border:"none",borderTop:`1px solid ${T.border}`,cursor:"pointer",color:T.green,fontSize:11,fontWeight:800}}>
                  <span>Full contact card &amp; game log</span><span>→</span>
                </button>}
              </>}
            </div>
          )
        })()}
        <div style={{fontSize:10.5,color:T.muted,marginTop:9,lineHeight:1.4}}>{last} {verbFor(m.rating)} the <b style={{color:T.text}}>{m.pitch}</b> — thrown {m.usage}% of the time. League-avg xwOBA ≈ .315.</div>
      </div>
      {/* ── CHEAT CODE — build a custom pitch mix, get the blended output ────────── */}
      <button onClick={()=>setCheatOpen(v=>!v)} style={{marginTop:8,display:"flex",alignItems:"center",gap:6,width:"100%",background:cheatOpen?`${T.green}14`:T.card,border:`1px solid ${cheatOpen?T.green:T.border2}`,borderRadius:9,padding:"9px 11px",cursor:"pointer"}}>
        <span style={{fontSize:12}}>⚡</span>
        <span style={{fontSize:11.5,fontWeight:800,letterSpacing:"0.04em",color:cheatOpen?T.green:T.text}}>CHEAT CODE — build a custom pitch mix</span>
        <span style={{marginLeft:"auto",fontSize:11,color:T.green,fontWeight:800}}>{cheatOpen?"▾":"▸"}</span>
      </button>
      {cheatOpen && (()=>{
        const mixArr=[...mix].map(i=>matchup[i]).filter(Boolean)
        const wmean=k=>{ let w=0,v=0; mixArr.forEach(mm=>{ if(mm[k]!=null){ w+=(mm.usage||0); v+=(mm.usage||0)*mm[k] } }); return w>0?v/w:null }
        const sum=k=>mixArr.reduce((a,mm)=>a+(mm[k]||0),0)
        const bx=wmean("hitter_xwoba"), bw=wmean("hitter_whiff")
        const bbar=wmean("recent_barrel"), bhh=wmean("recent_hard_hit"), bev=wmean("recent_avg_ev")
        const bip=sum("recent_bip"), hr=sum("recent_hr"), hits=sum("recent_hits"), xbh=sum("recent_xbh")
        const arsenal=Math.round(mixArr.reduce((a,mm)=>a+(mm.usage||0),0))
        // Per-pitch data comes from the Savant enrich (season vs-pitch splits + L30
        // batted-ball log). A not-yet-enriched hitter has every field null, so the blend
        // can't move — show WHY instead of a dead wall of "—" that reads like a bug.
        const hasSeason = bx!=null || bw!=null
        const hasRecent = bbar!=null || bhh!=null || bev!=null || (bip||0)>0
        const noData = !hasSeason && !hasRecent
        const tone=bx==null?T.muted2 : bx>=0.360?T.green : bx<=0.290?T.red : T.muted2
        const verdict=bx==null?"—" : bx>=0.360?"Crushes this mix" : bx<=0.290?"Struggles vs this mix" : "Neutral vs this mix"
        const brC=bbar>=12?T.green:bbar>=8?T.amber:T.muted2, hhC=bhh>=50?T.green:bhh>=40?T.amber:T.muted2
        const evC=bev>=91?T.green:bev>=88?T.amber:T.muted2, whC=bw==null?T.text:bw<=20?T.green:bw>=30?T.red:T.text
        const tiles=[["xwOBA", fmtX(bx==null?null:Math.round(bx*1000)/1000), tone],["WHIFF", bw!=null?`${Math.round(bw)}%`:"—", whC],["ARSENAL", `${arsenal}%`, T.text]]
        if(bbar!=null) tiles.push(["BARREL", `${Math.round(bbar)}%`, brC])
        if(bhh!=null)  tiles.push(["HARD-HIT", `${Math.round(bhh)}%`, hhC])
        if(bev!=null)  tiles.push(["AVG EV", `${Math.round(bev*10)/10}`, evC])
        const results=[hr>0?`${hr} HR`:null, hits>0?`${hits} H`:null, xbh>0?`${xbh} XBH`:null].filter(Boolean).join(" · ")
        const mixNames=[...mix].sort((a,b)=>(matchup[b].usage||0)-(matchup[a].usage||0)).map(i=>matchup[i].pitch)
        return (
          <div style={{marginTop:7,background:`${tone}0d`,border:`1px solid ${T.border2}`,borderRadius:11,padding:"11px 12px"}}>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.09em",marginBottom:7}}>TAP PITCHES TO ADD / REMOVE</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
              {matchup.map((mm,ix)=>{ const on=mix.has(ix); const c=mm.rating==="crushes"?T.green:mm.rating==="struggles"?T.red:T.muted2; return (
                <button key={ix} onClick={()=>toggleMix(ix)} style={{fontFamily:T.mono,fontSize:11,fontWeight:800,padding:"6px 10px",borderRadius:8,cursor:"pointer",border:`1px solid ${on?c:T.border2}`,background:on?`${c}22`:T.card,color:on?c:T.faint,opacity:on?1:0.7}}>
                  {on?"✓ ":""}{mm.pitch} <span style={{opacity:0.7,fontWeight:600}}>{mm.usage}%</span>
                </button>
              )})}
            </div>
            {mixArr.length===0
              ? <div style={{fontSize:11,color:T.muted,textAlign:"center",padding:"10px 0"}}>Pick at least one pitch to build the mix.</div>
              : noData
              ? <div style={{textAlign:"center",padding:"14px 8px",lineHeight:1.5}}>
                  <div style={{fontSize:18,marginBottom:5}}>⏳</div>
                  <div style={{fontSize:11.5,color:T.muted2,fontWeight:800}}>Pitch-level data still loading for {last}</div>
                  <div style={{fontSize:10.5,color:T.muted,marginTop:4}}>His per-pitch Statcast splits fill in on the next Savant refresh — check back shortly.</div>
                </div>
              : <>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
                  <span style={{fontSize:12,fontWeight:800,color:T.text,fontFamily:T.mono}}>{mixNames.join(" · ")}</span>
                  <span style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.05em",color:tone,textTransform:"uppercase",whiteSpace:"nowrap"}}>{verdict}</span>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
                  {tiles.map(([l,v,c],i)=>(
                    <div key={i} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"9px 10px",textAlign:"center"}}>
                      <div style={{fontSize:17,fontWeight:900,fontFamily:T.mono,color:c}}>{v}</div>
                      <div style={{fontSize:8,fontWeight:700,letterSpacing:"0.07em",color:T.muted,marginTop:3}}>{l}</div>
                    </div>
                  ))}
                </div>
                {!hasRecent && <div style={{marginTop:8,display:"inline-block",fontSize:9.5,fontWeight:800,letterSpacing:"0.04em",color:T.amber,background:`${T.amber}14`,border:`1px solid ${T.amber}`,borderRadius:7,padding:"4px 9px"}}>SEASON ONLY · recent batted-ball sample still loading</div>}
                {results && bip>0 && <div style={{marginTop:9,fontSize:11,color:T.text,fontWeight:700}}>Results: <span style={{color:T.green}}>{results}</span> <span style={{color:T.muted,fontWeight:400}}>in {bip} batted balls vs this mix, L30</span></div>}
                <div style={{fontSize:9.5,color:T.muted,marginTop:8,lineHeight:1.4}}>Usage-weighted blend across the selected pitches ({arsenal}% of {pitcherName||"the arm"}'s arsenal). xwOBA/whiff = season vs each pitch; barrel/hard-hit/EV = last 30d.</div>
              </>}
          </div>
        )
      })()}
      {/* Full table — every pitch at once, selected row highlighted */}
      <button onClick={()=>setShowAll(v=>!v)} style={{marginTop:8,background:"none",border:"none",color:T.green,fontSize:11,fontWeight:800,cursor:"pointer",padding:"2px 0"}}>{showAll?"Hide all pitches ▾":`All ${matchup.length} pitches ▸`}</button>
      {showAll && <>
        <div style={{display:"grid",gridTemplateColumns:"1fr 52px 64px 56px",gap:4,fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.05em",padding:"6px 2px 4px"}}>
          <span>PITCH</span><span style={{textAlign:"right"}}>USAGE</span><span style={{textAlign:"right"}}>xwOBA</span><span style={{textAlign:"right"}}>WHIFF</span>
        </div>
        <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,overflow:"hidden"}}>
          {matchup.map((mm,ix)=>{ const col=mm.rating==="crushes"?T.green:mm.rating==="struggles"?T.red:T.muted2; const on=ix===sel; return (
            <div key={ix} onClick={()=>setSel(ix)} style={{display:"grid",gridTemplateColumns:"1fr 52px 64px 56px",gap:4,alignItems:"center",padding:"8px 10px",fontSize:12,fontFamily:T.mono,borderTop:ix?`1px solid ${T.border}`:"none",cursor:"pointer",background:on?`${col}14`:"transparent"}}>
              <span style={{fontWeight:700,color:T.text}}>{mm.pitch}{mm.rating==="crushes"?" 🔥":""}</span>
              <span style={{textAlign:"right",color:T.muted2}}>{mm.usage}%</span>
              <span style={{textAlign:"right",fontWeight:800,color:col}}>{fmtX(mm.hitter_xwoba)}</span>
              <span style={{textAlign:"right",color:T.muted2}}>{mm.hitter_whiff!=null?`${mm.hitter_whiff}%`:"—"}</span>
            </div>
          )})}
        </div>
      </>}
      <div style={{fontSize:10,color:T.muted,marginTop:6,lineHeight:1.4}}>Tap a pitch to spotlight how {last} handles it. <b style={{color:T.green}}>Green/🔥</b> = crushes it; <b style={{color:T.red}}>red</b> = struggles.</div>
    </div>
  )
}

function PlayerModal({player, fallbackPark, onClose, upsertLeg, inParlay}){
  const name = player.name
  const { p, strip, contact, logs, mlbamId } = usePlayerData(player)
  const splits = formSplits(logs)
  const plat = platoonSplits(logs)
  const { data: splitsData } = useFetch(()=>player?.player_id?getPlayerSplits(player.player_id).catch(()=>null):Promise.resolve(null),[player?.player_id])
  const wlMarket = player.market||"HRR"
  const wl = (logs||[]).slice(0,12).map(g=>({
    date:(g.game_date||"").slice(5).replace("-","/"), opp:g.opponent||"", win:winTest(wlMarket)(g),
    hits:`${g.H||0}-${g.AB||0}`,
    extras:[g.R?`${g.R}R`:null, g.RBI?`${g.RBI} RBI`:null, g.HR?`${g.HR} HR`:null, g["2B"]?`${g["2B"]} 2B`:null, g.BB?`${g.BB} BB`:null].filter(Boolean).join(" · "),
  }))
  const wlHits = wl.filter(x=>x.win).length
  const [tab,setTab] = useState("Intel")
  const { isPro, needPro } = useProGate()   // per-bet-type Analytics sub-tab = Pro
  const watch = useWatch()
  const starred = watch.has(name)
  const board = useBoard()
  const boardKey = `${player.player_id}-${player.market||"HRR"}`
  const pinned = board.has(boardKey)
  const pinPlay = () => board.toggle({key:boardKey, player_id:player.player_id, name, market:player.market||"HRR", prob:player.prob, conf:player.conf, team:player.team, opp:player.opp, slot:player.slot, park:player.park})
  const [books,setBooks] = useState(null)
  const [booksOpen,setBooksOpen] = useState(false)   // line-shop "other books" drawer
  const [pitcherProfile,setPitcherProfile] = useState(null)   // tap opp pitcher → Pitcher Intel drawer
  const [mbOpen,setMbOpen] = useState(false)                  // 🆚 head-to-head Matchup Breakdown
  const [shareState,setShareState] = useState(null)   // share button feedback: shared/downloaded/copied
  // Load the full book board on open — line-shopping is core, so the panel is shown
  // inline in the player card, not hidden behind a toggle.
  useEffect(()=>{ if(player.player_id){ getBookOdds(player.player_id, player.market||"HRR", todayStr()).then(d=>setBooks(d?.books||[])).catch(()=>setBooks([])) } }, [player.player_id, player.market])

  // For a LIVE player, the Prob Over is the CALIBRATED, market-matched value insights
  // computed for THIS card's market (the same number The Case shows) — NOT the passed-in
  // prob, which can belong to a different market and read as impossible (e.g. "47% to
  // homer"). Board-opened cards already pass the calibrated prob, so this is a no-op there;
  // it only corrects mismatched opens. Falls back to the passed prob while insights loads
  // or for sample cards (no player_id).
  const _stripProb = (strip.proj!=null && strip.proj!=="" && !isNaN(parseFloat(strip.proj))) ? parseFloat(strip.proj) : null
  const modelProb = (player.player_id && _stripProb!=null) ? _stripProb
                  : (player.prob!=null ? player.prob : _stripProb)
  const imp = impliedProb(player.oddsNum)
  const edgeNum = (modelProb!=null && imp!=null) ? (modelProb - imp) : null
  // The price the CARD passed in. If the board showed a line/edge for this play, the
  // drawer must never contradict it by claiming "no book prices" — the per-book table
  // (BookOdds) can lag the board's price after an odds refresh. When getBookOdds comes
  // back empty but the card WAS priced, fall back to the card's line/edge.
  const cardOddsStr = player.odds || strip.odds || p.odds || null
  const cardEdgeStr = player.edge || strip.edge || p.edge || null
  const cardPriced  = (player.oddsNum != null) || (cardOddsStr && cardOddsStr !== "—")
  // Tier is recomputed from THIS pick's real prob + Rating via the same tierFor
  // engine the board uses — never a passed-in or sample tier. That stale fallback
  // let a 14% HR vs an ace inherit a bogus "BEST BET" while the Rating below it
  // read "—". Only fall back to a sample tier for the non-live sample cards (no
  // player_id, no model prob to score).
  // Honest edge for the drawer = the board's de-vigged edge_pct (passed as
  // player.edge, in % points), NOT model − vigged_implied. The vigged edgeNum
  // systematically understated edge → the Edge readout and the power-market tier
  // gates all read low. Fall back to the vigged edge only if no board edge.
  const honestEdgePct = player.edge!=null ? num(player.edge)
                      : (edgeNum!=null ? edgeNum*100 : null)
  const tier = modelProb!=null
    ? tierFor(modelProb, player.conf, player.market, honestEdgePct, player.oddsNum!=null)
    : (player.tier || (player.player_id ? "PLAY" : p.tier) || "PLAY")

  // Park tab: for a LIVE player, build the panel from the real park factor + weather
  // for his game (same computation as the game modal) — never the sample stadium.
  // If we don't have it yet (no projection/venue/forecast), show an honest empty
  // state rather than fake dimensions. Sample cards keep the demo park.
  const pd = p.parkData
  // HR park rank across the league (1 = most homer-friendly), hand-aware from the API.
  const parkRankTxt = (pd && pd.park_rank && pd.park_total)
    ? ` · #${pd.park_rank} of ${pd.park_total} for HR${pd.park_label?` (${pd.park_label})`:""}`
    : ""
  // Clean park read under the matchup line: the venue's HR RANK across the league
  // (e.g. "🔥 #3 hitters' park"), not a raw multiplier — a bare "1.18x" next to the
  // name reads like a payout and is misleading on a betting card. Only fires when the
  // park actually leans one way (top or bottom third); the neutral middle shows nothing.
  const parkPr = (player.player_id && pd && pd.venue && pd.park_rank && pd.park_total) ? pd.park_rank : null
  const parkPt = pd?.park_total || 0
  const parkBadge = parkPr
    ? (parkPr <= parkPt / 3      ? {e:"🔥", label:`#${parkPr} hitters' park`, c:T.green}
     : parkPr >  2 * parkPt / 3  ? {e:"🧊", label:`#${parkPr} pitchers' park`, c:"#7fa0b5"}
     : null)
    : null
  const livePark = pd
    ? {...parkObj(pd.park_factor, pd.weather),
       intel: pd.venue
         ? `${pd.venue} · park factor ${(pd.park_factor||1).toFixed(2)}x${parkRankTxt}${pd.weather?.is_dome?" · dome, weather is neutral":""}.`
         : `Park factor ${(pd.park_factor||1).toFixed(2)}x${parkRankTxt}.`}
    : null
  const parkForPanel = player.player_id ? livePark : (fallbackPark || SAMPLE_DETAIL.park)

  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"14px 16px 40px"}}>
        {pitcherProfile && <ErrorBoundary resetKey={pitcherProfile}><PitcherCard name={pitcherProfile} onClose={()=>setPitcherProfile(null)}/></ErrorBoundary>}
        {mbOpen && <ErrorBoundary resetKey={`mb-${player.player_id||name}`}><MatchupDrawer initial={{name, player_id:player.player_id}} onClose={()=>setMbOpen(false)}/></ErrorBoundary>}
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <Headshot mlbamId={mlbamId} name={name} size={48}/>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:20,fontWeight:800,letterSpacing:"-0.02em"}}>{name}</span>
                <TierBadge tier={tier}/>
              </div>
              <div style={{fontSize:12,color:T.muted,marginTop:2}}>{player.opp || (p.starter ? `vs ${p.starter}` : p.opp)}</div>
              {parkBadge && (
                <div title={pd.venue ? `${pd.venue} · #${parkPr} of ${parkPt} for HR` : ""}
                  style={{fontSize:11.5,fontWeight:700,color:parkBadge.c,marginTop:3}}>
                  {parkBadge.e} {parkBadge.label}
                </div>
              )}
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:7}}>
                {(player.oppPitcher||p.pitcherName) && <button onClick={()=>setPitcherProfile(player.oppPitcher||p.pitcherName)} style={{display:"inline-flex",alignItems:"center",gap:5,background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:8,padding:"4px 10px",color:T.green,fontSize:11,fontWeight:800,cursor:"pointer"}}>🎯 {player.oppPitcher||p.pitcherName} · Pitcher Intel →</button>}
                {player.player_id && <button onClick={()=>setMbOpen(true)} style={{display:"inline-flex",alignItems:"center",gap:5,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"4px 10px",color:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer"}}>🆚 Matchup Breakdown →</button>}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button
              onClick={async()=>{
                const lineStr = (books!==null && books.length) ? fmtOdds(books[0].over) : ((cardPriced||books===null) ? (cardOddsStr||"—") : "—")
                const edgeStr = (books!==null && books.length===0 && !cardPriced) ? "—" : (cardEdgeStr||"—")
                const r = await sharePlay({
                  name, market:player.market||"HRR",
                  matchup: player.opp || (p.starter ? `vs ${p.starter}` : p.opp) || "",
                  probStr: modelProb!=null ? pct(modelProb) : "—",
                  probColor: modelProb!=null ? probColorMkt(modelProb, player.market) : T.text,
                  rating: player.conf ?? null,
                  ratingColor: player.conf!=null ? ratingColor(player.conf) : T.text,
                  edgeStr, edgeColor: edgeColor(num(cardEdgeStr)),
                  lineStr,
                  book: (books!==null && books.length) ? bookName(books[0].book) : null,
                  form: wl.length ? { hits:wlHits, total:wl.length, marketLabel:player.market||"HRR", recent:wl.slice(0,10).map(g=>g.win) } : null,
                  factors: (p.theCase?.factors||[]).map(f=>({tone:f.tone,label:f.label,detail:f.detail})),
                  verdict: p.theCase?.verdict||null,
                  greens: p.theCase?.greens ?? null,
                  reds: p.theCase?.reds ?? null,
                })
                setShareState(r); setTimeout(()=>setShareState(null), 2200)
              }}
              title="Share this play"
              style={{width:32,height:32,borderRadius:9,background:shareState==="shared"||shareState==="downloaded"||shareState==="copied"?T.greenDim:T.card,border:`1px solid ${shareState?T.green:T.border2}`,color:shareState?T.green:T.muted,fontSize:15,cursor:"pointer"}}>
              {shareState==="downloaded"?"⤓":shareState==="copied"?"✓":shareState==="shared"?"✓":"📤"}
            </button>
            {player.player_id && <button onClick={pinPlay} title={pinned?"Unpin from My Board":"Pin to My Board"} style={{width:32,height:32,borderRadius:9,background:pinned?T.greenDim:T.card,border:`1px solid ${pinned?T.greenMid:T.border2}`,color:pinned?T.green:T.muted,fontSize:15,cursor:"pointer"}}>📌</button>}
            <button onClick={()=>watch.toggle(name)} title={starred?"Remove from watchlist":"Add to watchlist"} style={{width:32,height:32,borderRadius:9,background:starred?`${T.amber}22`:T.card,border:`1px solid ${starred?T.amber:T.border2}`,color:starred?T.amber:T.muted,fontSize:15,cursor:"pointer"}}>{starred?"★":"☆"}</button>
            <button onClick={onClose} style={{width:32,height:32,borderRadius:9,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:15,cursor:"pointer"}}>✕</button>
          </div>
        </div>

        {/* Stat strip — Prob / Confidence / Edge / Line */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
          <Stat label="Prob Over" value={modelProb!=null?pct(modelProb):(strip.proj||p.proj||"—")} color={modelProb!=null?probColorMkt(modelProb, player.market):T.muted2} bg={T.greenDim}/>
          <Stat label="Rating /100" value={player.conf??"—"} color={player.conf!=null?ratingColor(player.conf):T.muted2}/>
          {/* Edge + Line read from the SAME fresh /book-odds data as the Line-shop panel
              below — so the tiles can't show a stale "+220 / +2%" while the panel says
              "no book prices". While books load (null), fall back to the passed-in value;
              once loaded, the best book IS the line, and an empty result means no line. */}
          <Stat label="Edge" value={(books!==null && books.length===0 && !cardPriced) ? "—" : (cardEdgeStr||"—")} color={edgeColor(num(cardEdgeStr))}/>
          <Stat label="Line" value={(books!==null && books.length) ? fmtOdds(books[0].over) : ((cardPriced||books===null) ? (cardOddsStr||"—") : "—")}/>
        </div>

        {/* What-if — only for live plays where we have the real prob + batting slot */}
        {modelProb!=null && player.player_id && player.slot!=null && (
          <WhatIfPanel prob={modelProb} market={player.market||"HRR"} slot0={player.slot} park0={player.park}
            onAddToSlip={upsertLeg ? (estProb=>{ const lk=`${player.player_id}-${player.market||"HRR"}`; upsertLeg({key:lk,name:name,player_id:player.player_id,market:player.market||"HRR",prob:estProb,oddsNum:player.oddsNum,team:player.team,opp:player.oppPitcher}) }) : null}
            inSlip={inParlay ? inParlay(`${player.player_id}-${player.market||"HRR"}`) : false}/>
        )}

        {/* Line shop — the full book board, inline in the player card. "Find the book
            to do it on": best price highlighted + what it's worth vs the worst book. */}
        {player.player_id && (()=>{
          // Model prob → fair (no-vig) American odds. Lets an unpriced play still show
          // OUR number instead of a dead "no book prices" — LINEBRKR prices props books won't.
          const _fairAm = p => (p==null) ? null : (p>=0.5 ? -Math.round(100*p/(1-p)) : Math.round(100*(1-p)/p))
          if(books===null) return <div style={{height:80,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,opacity:0.4,marginBottom:14}}/>
          if(books.length===0) return cardPriced
            // The board priced this play but the per-book table is empty/lagging — show the
            // card's best price so the drawer agrees with the card, and flag the refresh.
            ? (()=>{ const isLongshot = (player.market==="HR") || (player.oddsNum!=null && player.oddsNum>=400)
              return <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:9.5,letterSpacing:"0.12em",color:T.green,fontWeight:800,textTransform:"uppercase"}}>★ Best price {isLongshot?"available":"(from the board)"}</div>
                <div style={{fontSize:20,fontWeight:900,color:T.green,fontFamily:T.mono,marginTop:4}}>{cardOddsStr}</div>
                <div style={{fontSize:11,color:T.muted,marginTop:5,lineHeight:1.45}}>{isLongshot
                  ? "This is a longshot — most books don’t post a shoppable price on it, so there’s little to compare. The board’s best available number is shown."
                  : "Per-book comparison is refreshing — tap “Pull odds” in Admin if it stays empty."}</div>
              </div> })()
            // Genuinely unpriced (no book posted a line) — show OUR fair number instead of a
            // dead end. LINEBRKR prices props the market won't; the line-shop board appears
            // once a book posts one. Falls back to the research note only if we have no prob.
            : (modelProb!=null
              ? <div style={{background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",marginBottom:14}}>
                  <div style={{fontSize:9.5,letterSpacing:"0.12em",color:T.green,fontWeight:800,textTransform:"uppercase"}}>★ LINEBRKR fair price</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:9,marginTop:4}}>
                    <span style={{fontSize:22,fontWeight:900,color:T.green,fontFamily:T.mono}}>{fmtOdds(_fairAm(modelProb))}</span>
                    <span style={{fontSize:13,fontWeight:800,color:T.muted2,fontFamily:T.mono}}>{Math.round(modelProb*100)}% model</span>
                  </div>
                  <div style={{fontSize:11,color:T.muted,marginTop:5,lineHeight:1.45}}>No sportsbook posts a shoppable price on this — so here’s <b style={{color:T.text}}>our</b> fair number. The line-shop board appears the moment a book posts it.</div>
                </div>
              : <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 14px",marginBottom:14,color:T.muted,fontSize:12}}>No book prices posted for this play — shown as research, not a priced bet.</div>)
          const best=books[0], worst=books[books.length-1]
          const bestRet=payout100(best.over), worstRet=payout100(worst.over)
          const novigs=books.map(b=>b.novig_prob).filter(x=>x!=null).sort((a,b)=>a-b)
          const fair=novigs.length?novigs[Math.floor(novigs.length/2)]:null
          return (
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"14px 15px",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
              <span style={{fontSize:13.5,fontWeight:900,letterSpacing:"-0.01em"}}>Line shop</span>
              <span style={{fontSize:10.5,color:T.muted,fontWeight:700}}>{books.length} book{books.length===1?"":"s"} posted</span>
            </div>
            {/* Best-price hero */}
            <div style={{background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:11,padding:"12px 14px",marginBottom:12}}>
              <div style={{fontSize:9.5,letterSpacing:"0.12em",color:T.green,fontWeight:800,textTransform:"uppercase"}}>★ Best price</div>
              <div style={{display:"flex",alignItems:"baseline",gap:9,marginTop:4}}>
                <span style={{fontSize:22,fontWeight:900,color:T.green,fontFamily:T.mono}}>{fmtOdds(best.over)}</span>
                <span style={{fontSize:17,fontWeight:900,color:T.text}}>{bookName(best.book)}</span>
              </div>
              {bestRet!=null && worstRet!=null && books.length>1 && bestRet>worstRet && (
                <div style={{fontSize:11,color:T.muted,fontWeight:600,marginTop:5,lineHeight:1.45}}>A $100 play returns <b style={{color:T.text}}>${bestRet}</b> here — vs <b style={{color:T.text}}>${worstRet}</b> at the worst book on the board. Same bet, <b style={{color:T.green}}>+${bestRet-worstRet}</b>.</div>
              )}
            </div>
            {/* Every other book, INLINE and visible by default — line-shopping is core,
                so it's never hidden behind a toggle. Best price is the hero above; these
                are the rest, best-first. Long boards (>6) collapse the tail behind "Show all". */}
            {books.length>1 && (<>
              <div style={{fontSize:9.5,letterSpacing:"0.12em",color:T.muted,fontWeight:800,textTransform:"uppercase",margin:"2px 0 4px"}}>Every book · best first</div>
              {books.slice(1, booksOpen ? books.length : 6).map((bk,i)=>{ const u=bookUrl(bk.book)
                return (
                <div key={i} onClick={u?(ev=>{ev.stopPropagation(); window.open(u,'_blank','noopener')}):undefined}
                  style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 2px",borderTop:`1px solid ${T.border}`,cursor:u?"pointer":"default"}}>
                  <span style={{fontSize:13,fontWeight:700,color:T.text}}>{bookName(bk.book)}{u?<span style={{color:T.muted2,marginLeft:5,fontSize:11}}>↗</span>:null}</span>
                  <span style={{display:"flex",gap:12,fontFamily:T.mono,fontSize:12.5,alignItems:"center"}}>
                    <span style={{color:T.muted2,fontWeight:800}}>{fmtOdds(bk.over)}</span>
                    {bk.line!=null && <span style={{color:T.muted,fontSize:11}}>O{bk.line}</span>}
                  </span>
                </div>
              )})}
              {books.length>6 && (
                <button onClick={ev=>{ev.stopPropagation(); setBooksOpen(o=>!o)}}
                  style={{width:"100%",marginTop:8,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:9,padding:"9px 12px",cursor:"pointer",color:T.green,fontSize:12,fontWeight:800}}>
                  {booksOpen ? "Show fewer" : `Show all ${books.length} books`}
                </button>
              )}
            </>)}
            <div style={{fontSize:10,color:T.muted,lineHeight:1.5,marginTop:11,paddingTop:10,borderTop:`1px solid ${T.border}`}}>
              Sorted best price first · ★ = best of the board.{fair!=null?<> Consensus (no-vig) fair: <b style={{color:T.muted2}}>{toAmerican(fair)}</b>.</>:null} Books that didn't post this line are hidden — we never invent a price.
            </div>
          </div>
          )
        })()}

        {p.theCase && p.theCase.factors?.length>0 && (()=>{ const tc=p.theCase; const vNeg = /no edge|lean pass|priced|mixed/i.test(tc.verdict||""); const vTone = vNeg ? T.muted2 : (tc.greens>=4&&tc.reds<=1?T.green:tc.greens>tc.reds?T.blue:T.muted2); const toneColor=t=>t==="good"?T.green:t==="bad"?"#ef6a6a":T.muted2; const toneBg=t=>t==="good"?T.greenDim:t==="bad"?"rgba(239,106,106,0.10)":T.card; const toneMark=t=>t==="good"?"✓":t==="bad"?"✕":"•"; return (
          <div style={{background:T.card,border:`1px solid ${vTone}`,borderRadius:14,padding:"12px 13px",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
              <div style={{fontSize:13,fontWeight:900,letterSpacing:"-0.01em"}}>The Case{tc.market?<span style={{color:T.muted2,fontWeight:700,fontSize:11}}> · {mDisp(tc.market)}</span>:null}</div>
              <div style={{fontSize:11.5,fontWeight:800,color:vTone}}>{tc.verdict}</div>
            </div>
            <div style={{display:"flex",gap:6,marginBottom:10}}>
              <span style={{fontSize:10,fontWeight:700,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:6,padding:"2px 8px"}}>{tc.greens} green</span>
              {tc.reds>0 && <span style={{fontSize:10,fontWeight:700,color:"#ef6a6a",background:"rgba(239,106,106,0.10)",border:"1px solid rgba(239,106,106,0.30)",borderRadius:6,padding:"2px 8px"}}>{tc.reds} concern</span>}
            </div>
            {tc.factors.map((f,i)=>(
              <div key={i} style={{display:"flex",gap:9,alignItems:"flex-start",padding:"6px 0",borderTop:i?`1px solid ${T.border}`:"none"}}>
                <span style={{flexShrink:0,width:18,height:18,marginTop:1,borderRadius:5,background:toneBg(f.tone),color:toneColor(f.tone),fontSize:11,fontWeight:900,display:"flex",alignItems:"center",justifyContent:"center"}}>{toneMark(f.tone)}</span>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:toneColor(f.tone)}}>{f.label}</div>
                  <div style={{fontSize:11.5,color:T.muted,lineHeight:1.4}}>{f.detail}</div>
                </div>
              </div>
            ))}
            <div style={{fontSize:9.5,color:T.muted2,marginTop:8}}>Information only — not betting advice. 21+.</div>
          </div>
        )})()}

        <Tabs tabs={["Intel","Score","Analytics","Win/Lose","Form","Park","Contact"]} active={tab}
          onSelect={t=>{ if(t==="Analytics" && !isPro){ needPro(); return } setTab(t) }}/>

        {tab==="Analytics" && <PropAnalyticsPanel player={player}/>}

        {tab==="Intel" && (
          <div>
            {/* Why It Rates — conclusion first */}
            {player.why?.length>0 && (
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>WHY IT RATES</div>
                {player.why.map(([ok,text],i)=>(
                  <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:i<player.why.length-1?6:0,fontSize:13,lineHeight:1.4}}>
                    <span style={{color:ok?T.green:T.amber,flexShrink:0}}>{ok?"✓":"⚠"}</span>
                    <span style={{color:T.muted2}}>{text}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Platoon split — how this hitter does vs RHP / LHP, tonight's side highlighted */}
            {(() => {
              const oR = splitsData?.vsR, oL = splitsData?.vsL
              const nAvg = v => { const n=parseFloat(v); return isNaN(n)?null:n }
              const fmtA = v => v==null?"—":String(v).replace(/^0/,"")
              let R=null, L=null, src=null
              if(oR || oL){
                if(oR) R={avg:fmtA(oR.avg), n:nAvg(oR.avg), ops:oR.ops!=null?String(oR.ops).replace(/^0/,""):null, ab:oR.ab}
                if(oL) L={avg:fmtA(oL.avg), n:nAvg(oL.avg), ops:oL.ops!=null?String(oL.ops).replace(/^0/,""):null, ab:oL.ab}
                src=`Official MLB splits${splitsData?.season?`, ${splitsData.season}`:""}.`
              } else if(plat){
                const g=x=> (x&&x.ab)?{avg:x.avg.toFixed(3).replace(/^0/,""), n:x.avg, ops:null, ab:x.ab}:null
                R=g(plat.vsR); L=g(plat.vsL); src="From this season's game logs."
              }
              if(!R && !L) return null
              const hand = (player?.oppHand||"").toUpperCase()[0]   // tonight's starter hand, if known
              const gap = (R?.n!=null && L?.n!=null) ? R.n-L.n : null
              const better = gap==null?null:(gap>0?"RHP":"LHP")
              const col = (label, d, hl) => (
                <div style={{flex:1,background:hl?T.greenDim:T.card,border:`1px solid ${hl?T.green:T.border2}`,borderRadius:10,padding:"10px 12px",textAlign:"center"}}>
                  <div style={{fontSize:9,color:hl?T.green:T.muted,fontWeight:800,letterSpacing:"0.07em"}}>{label}{hl?" · TONIGHT":""}</div>
                  <div style={{fontSize:22,fontWeight:900,fontFamily:T.mono,color:T.text,marginTop:3}}>{d?d.avg:"—"}</div>
                  <div style={{fontSize:10,color:T.muted2,marginTop:2}}>{d?`${d.ops?`${d.ops} OPS · `:""}${d.ab??"—"} AB`:"no data"}</div>
                </div>
              )
              const takeaway = (gap!=null && Math.abs(gap)>=0.030)
                ? `${Math.abs(gap)>=0.060?"Big":"Notable"} platoon split — hits ${better} ${Math.abs(gap*1000).toFixed(0)} pts better${hand?(hand==="R"?" · faces a righty tonight":" · faces a lefty tonight"):"."}`
                : (hand ? `Faces a ${hand==="R"?"righty":"lefty"} tonight — ${hand==="R"?(R?R.avg:"—"):(L?L.avg:"—")} vs ${hand==="R"?"RHP":"LHP"}.` : "Fairly even platoon profile.")
              return (
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>PLATOON SPLIT{splitsData?.bats?` · bats ${splitsData.bats}`:""}</div>
                  <div style={{display:"flex",gap:8}}>
                    {col("vs RHP", R, hand==="R")}
                    {col("vs LHP", L, hand==="L")}
                  </div>
                  <div style={{fontSize:11.5,color:T.text,lineHeight:1.45,marginTop:8}}>{takeaway}</div>
                  <div style={{fontSize:9.5,color:T.muted,marginTop:3}}>{src} Batting average vs each hand — a bigger gap means the play is more matchup-dependent.</div>
                </div>
              )
            })()}
            {/* Matchup edges — the overlap of hitter strength × where this pitcher throws */}
            {p.matchupNotes?.length>0 && (
              <div style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:9,color:T.green,fontWeight:700,letterSpacing:"0.1em",marginBottom:8}}>MATCHUP EDGES{p.pitcherName?` vs ${p.pitcherName}`:""}</div>
                {p.matchupNotes.map((n,i)=>(<div key={i} style={{fontSize:12.5,color:T.text,lineHeight:1.5,marginBottom:i<p.matchupNotes.length-1?6:0}}>• {n}</div>))}
              </div>
            )}

            {/* Batter vs THIS pitcher — career head-to-head */}
            {(p.vsPitcher && p.vsPitcher.ab>0) ? (
              <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.1em",marginBottom:8}}>VS {(p.vsPitcher.pitcher||p.pitcherName||"THIS PITCHER").toUpperCase()} — CAREER</div>
                <div style={{display:"flex",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
                  <span style={{fontSize:20,fontWeight:900,color:T.text,fontFamily:T.mono}}>{p.vsPitcher.line}</span>
                  <span style={{fontSize:13,fontWeight:700,color:T.muted2,fontFamily:T.mono}}>.{String(p.vsPitcher.avg||"").replace(/^0?\./,"")||"000"}</span>
                </div>
                <div style={{display:"flex",gap:14,flexWrap:"wrap",marginTop:8,fontSize:12,color:T.muted2,fontFamily:T.mono}}>
                  <span>{p.vsPitcher.hr} HR</span><span>{p.vsPitcher.rbi} RBI</span><span>{p.vsPitcher.bb} BB</span><span>{p.vsPitcher.k} K</span>{p.vsPitcher.ops&&p.vsPitcher.ops!==".000"&&<span>{p.vsPitcher.ops} OPS</span>}
                </div>
                <div style={{fontSize:10,color:T.muted,marginTop:8,lineHeight:1.4}}>Career head-to-head — small samples are noisy; context, not a signal on its own.</div>
              </div>
            ) : p.pitcherName ? (
              <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.1em",marginBottom:6}}>VS {p.pitcherName.toUpperCase()} — CAREER</div>
                <div style={{fontSize:12.5,color:T.muted2,lineHeight:1.4}}>
                  {p.vsPitcherStatus==="unresolved"
                    ? "Head-to-head unavailable — couldn't match this pitcher to a Statcast ID, so prior at-bats can't be confirmed."
                    : "No prior head-to-head — first career meeting (0 career at-bats on record)."}
                </div>
              </div>
            ) : null}

            {/* Pitch-type matchup — interactive tap-to-focus drill-down */}
            {p.pitchMatchup && p.pitchMatchup.length>0 && (
              <PitchMatchupPanel matchup={p.pitchMatchup} edge={p.pitchEdge} pitcherName={p.pitcherName} hitterName={p.name} onOpenContact={()=>setTab("Contact")}/>
            )}

            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <span style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em"}}>ZONE FIT{p.pitcherName?` vs ${p.pitcherName}`:""}</span>
              {p.zoneFitScore!=null && (
                <span style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>FIT</span>
                  <span style={{fontSize:15,fontWeight:900,fontFamily:T.mono,color:scoreColor(p.zoneFitScore)}}>{p.zoneFitScore}</span>
                  <span style={{fontSize:10,color:T.muted}}>/100</span>
                </span>
              )}
            </div>
            {p.zoneFitWriteup && (
              <div style={{background:T.card3,border:`1px solid ${T.greenMid}`,borderRadius:8,padding:"11px 13px",marginBottom:10,fontSize:12.5,color:T.text,lineHeight:1.5}}>{p.zoneFitWriteup}</div>
            )}
            {p.zoneFit ? (<>
              <div style={{fontSize:10.5,color:T.muted,marginBottom:8,lineHeight:1.45}}>
                Big number = how well {name.split(" ").slice(-1)[0]} hits that spot (0–100).{p.pitcherZone?` Below it = how often this pitcher throws there.`:""}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",fontSize:9,color:T.muted,letterSpacing:"0.1em",marginBottom:6,gap:8}}><span>INSIDE</span><span style={{textAlign:"center"}}>MIDDLE</span><span style={{textAlign:"right"}}>OUTSIDE</span></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                {["UP","MID","DN"].map(row=>(<React.Fragment key={row}>
                  {["inside","middle","outside"].map((side,i)=>{ const v=p.zoneFit[side]?.[row]; const h=zoneHeat(v); const pz=p.pitcherZone?.[side]?.[row]; return (
                    <div key={i} style={{background:h.bg,border:`1px solid ${T.border2}`,borderRadius:8,padding:"10px 12px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:10,color:T.muted,fontWeight:700}}>{row==="UP"?"Up":row==="MID"?"Middle":"Down"}</span>
                        <span style={{fontSize:18,fontWeight:800,color:h.fg,fontFamily:T.mono}}>{v}</span>
                      </div>
                      {pz!=null && <div style={{fontSize:9.5,color:T.muted,textAlign:"right",marginTop:3}}>thrown {pz}%</div>}
                    </div>)})}
                </React.Fragment>))}
              </div>
              <div style={{display:"flex",gap:12,marginBottom:14,flexWrap:"wrap"}}>
                {[["🔥 Hot zone",T.red],["Warm",T.amber],["Neutral",T.muted],["❄ Cold zone",T.blue]].map(([l,c])=>(
                  <span key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:10,color:T.muted}}><span style={{width:8,height:8,borderRadius:2,background:c}}/>{l}</span>))}
              </div>
            </>) : (
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"14px",marginBottom:14,fontSize:12.5,color:T.muted2,lineHeight:1.5}}>
                Zone-fit profile isn't available for {name} yet — it needs Statcast zone data, which fills in for that day's batters as the ingest runs.
              </div>
            )}
            {p.zoneSummary && (
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:6}}>SUMMARY</div>
                <div style={{fontSize:13,color:T.muted2,lineHeight:1.5}}>{p.zoneSummary}</div>
              </div>
            )}
            {/* HR park rank — where this ballpark sits among all parks for homers */}
            {p.parkData?.park_rank && p.parkData?.park_total && (()=>{
              const pr=p.parkData, top=pr.park_rank<=pr.park_total/3, bot=pr.park_rank>2*pr.park_total/3
              const col=top?T.green:bot?T.red:T.amber
              return (
                <div style={{display:"flex",alignItems:"center",gap:10,background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"11px 13px",marginBottom:10}}>
                  <span style={{fontSize:18}}>🏟️</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12.5,color:T.text,fontWeight:700}}>Homer park: <span style={{color:col}}>#{pr.park_rank} of {pr.park_total}</span>{pr.park_label?` · ${pr.park_label}`:""}</div>
                    <div style={{fontSize:10.5,color:T.muted,marginTop:2}}>{pr.venue||"This venue"} · HR factor {Number(pr.park_hr_factor||pr.park_factor||1).toFixed(2)}x{pr.park_hand?` (${pr.park_hand}HB power)`:""}</div>
                  </div>
                </div>
              )
            })()}
            {p.chips.length>0 && (
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {p.chips.map(c=><span key={c} style={{fontSize:11,fontWeight:600,color:T.muted2,background:T.card,border:`1px solid ${T.border2}`,borderRadius:6,padding:"4px 10px"}}>{c}</span>)}
              </div>
            )}
          </div>
        )}

        {tab==="Score" && (
          <div>
            <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:10,padding:"14px",marginBottom:12}}>
              <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:10}}>MODEL vs MARKET</div>
              {(()=>{
                const eP = honestEdgePct
                return [["Model probability", modelProb!=null?pct(modelProb):"—", T.green],
                ["Implied (book)", imp!=null?pct(imp):"—", T.muted2],
                ["Edge", eP!=null?`${eP>=0?"+":""}${eP.toFixed(1)}%`:"—", edgeColor(eP)],
                ["LINEBRKR Rating", player.conf!=null?`${player.conf} · ${ratingLabel(player.conf)}`:"—", player.conf!=null?ratingColor(player.conf):T.muted2],
                ["Tier", tier, (TIER[tier]||TIER.WATCH).bd]].map(([l,v,c])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderTop:`1px solid ${T.border}`}}>
                  <span style={{fontSize:13,color:T.muted2}}>{l}</span>
                  <span style={{fontSize:16,fontWeight:800,color:c,fontFamily:T.mono}}>{v}</span>
                </div>))
              })()}
            </div>
            <div style={{fontSize:11,color:T.muted,lineHeight:1.5,padding:"0 2px"}}>
              <b style={{color:T.text}}>LINEBRKR Rating</b> (0–100) is our single measure of how good this spot is to hit — model, matchup, zone fit, form, park and data quality in one number. <b style={{color:T.text}}>Edge</b> is the separate price question: how far the model sits above the book. <b style={{color:T.text}}>Tier</b> is the Rating's label.
            </div>
          </div>
        )}

        {tab==="Win/Lose" && (
          <div>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:10}}>RECENT RESULTS · {wlMarket} {wlMarket==="HR"||wlMarket==="2B"||wlMarket==="1+ H"?"O0.5":"O1.5"} · WIN/LOSS + box line</div>
            {wl.length ? (<>
              <div style={{display:"flex",gap:14,marginBottom:12}}>
                <div><div style={{fontSize:9,color:T.muted,letterSpacing:"0.1em"}}>HIT RATE</div><div style={{fontSize:20,fontWeight:900,color:probColor(wlHits/wl.length),fontFamily:T.mono}}>{Math.round(wlHits/wl.length*100)}%</div></div>
                <div><div style={{fontSize:9,color:T.muted,letterSpacing:"0.1em"}}>RECORD</div><div style={{fontSize:20,fontWeight:900,color:T.text,fontFamily:T.mono}}>{wlHits}–{wl.length-wlHits}</div></div>
              </div>
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:10,overflow:"hidden"}}>
                {wl.map((g,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"9px 12px",background:g.win?"#064E3B22":"#7F1D1D18",borderTop:i?`1px solid ${T.border}`:"none"}}>
                    <div style={{minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:12,color:T.muted2,fontFamily:T.mono}}>{g.date}</span>
                        <span style={{fontSize:12,color:T.muted}}>vs {g.opp}</span>
                      </div>
                      <div style={{fontSize:11,color:T.text,fontFamily:T.mono,marginTop:3}}><b>{g.hits}</b>{g.extras?<span style={{color:T.muted2}}> · {g.extras}</span>:""}</div>
                    </div>
                    <span style={{flexShrink:0,fontSize:11,fontWeight:800,color:g.win?T.green:T.red,background:g.win?T.greenDim:"#7F1D1D33",border:`1px solid ${g.win?T.green:T.red}`,borderRadius:5,padding:"3px 9px"}}>{g.win?"WIN":"LOSS"}</span>
                  </div>
                ))}
              </div>
            </>) : (
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"14px",fontSize:12.5,color:T.muted2,lineHeight:1.5}}>No recent game logs for {name} this season yet.</div>
            )}
          </div>
        )}

        {tab==="Form" && (
          <div>
            {splits ? (<>
              <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:10}}>FORM vs SEASON</div>
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden",marginBottom:14}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 60px 60px 60px",padding:"8px 12px",fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>
                  <span></span><span style={{textAlign:"right"}}>SEASON</span><span style={{textAlign:"right"}}>L14</span><span style={{textAlign:"right"}}>L7</span>
                </div>
                {[["AVG",x=>x.avg.toFixed(3).replace(/^0/,"")],["OBP",x=>x.obp.toFixed(3).replace(/^0/,"")],["SLG",x=>x.slg.toFixed(3).replace(/^0/,"")],["OPS",x=>x.ops.toFixed(3).replace(/^0/,"")]].map(([lab,fn],ri)=>(
                  <div key={lab} style={{display:"grid",gridTemplateColumns:"1fr 60px 60px 60px",padding:"9px 12px",borderTop:`1px solid ${T.border}`,fontSize:13,fontFamily:T.mono}}>
                    <span style={{color:T.muted,fontFamily:"inherit",fontWeight:700,fontSize:11}}>{lab}</span>
                    <span style={{textAlign:"right",color:T.muted2}}>{fn(splits.season)}</span>
                    <span style={{textAlign:"right",color:T.text}}>{fn(splits.l14)}</span>
                    <span style={{textAlign:"right",fontWeight:800,color:T.green}}>{fn(splits.l7)}</span>
                  </div>
                ))}
              </div>
              {p.trend && <div style={{display:"flex",alignItems:"center",gap:10,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"12px 14px"}}><span style={{fontSize:18}}>📈</span><div style={{fontSize:12,color:T.muted2}}>{p.trend}</div></div>}
              {(() => {
                const oR = splitsData?.vsR, oL = splitsData?.vsL
                if (oR || oL) {
                  const fmtAvg = v => v==null?"—":String(v).replace(/^0/,"")
                  const rows = [["AVG",x=>fmtAvg(x?.avg)],["OPS",x=>x?.ops!=null?String(x.ops).replace(/^0/,""):"—"],["HR",x=>x?.hr??"—"],["AB",x=>x?.ab??"—"]]
                  return (
                    <div style={{marginTop:14}}>
                      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:10}}>SPLITS vs HAND · {splitsData?.season||"season"}</div>
                      <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden"}}>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 70px 70px",padding:"8px 12px",fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>
                          <span></span><span style={{textAlign:"right"}}>vs RHP</span><span style={{textAlign:"right"}}>vs LHP</span>
                        </div>
                        {rows.map(([lab,fn])=>(
                          <div key={lab} style={{display:"grid",gridTemplateColumns:"1fr 70px 70px",padding:"9px 12px",borderTop:`1px solid ${T.border}`,fontSize:13,fontFamily:T.mono}}>
                            <span style={{color:T.muted,fontWeight:700,fontSize:11}}>{lab}</span>
                            <span style={{textAlign:"right",color:T.text}}>{fn(oR)}</span>
                            <span style={{textAlign:"right",color:T.text}}>{fn(oL)}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{fontSize:9.5,color:T.muted,marginTop:6}}>Official MLB splits, {splitsData?.season}. {splitsData?.bats?`Bats ${splitsData.bats}.`:""}</div>
                    </div>
                  )
                }
                return null
              })()}
            </>) : (
              <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"14px",fontSize:12.5,color:T.muted2,lineHeight:1.5}}>Rolling form isn't available for {name} yet — needs game-log history for this season.</div>
            )}
          </div>
        )}

        {tab==="Park" && (parkForPanel
          ? <ParkPanel park={parkForPanel}/>
          : <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"14px",fontSize:12.5,color:T.muted2,lineHeight:1.5}}>Park &amp; weather for {name}'s game aren't loaded yet — they post with the slate (usually by ~3 PM ET, once the venue &amp; forecast are in). Open the game from the <b style={{color:T.text}}>Games</b> tab for the full ballpark breakdown.</div>)}

        {tab==="Contact" && (contact
          ? <div style={{margin:"-2px -2px 0"}}><ContactLog playerName={name} data={contact}/></div>
          : <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:8,padding:"14px",fontSize:12.5,color:T.muted2,lineHeight:1.5}}>No recent batted-ball data for {name} (no balls in play in the last 15 days, or Statcast not yet loaded).</div>
        )}
      </div>
    </Sheet>
  )
}

// ── Game modal ──────────────────────────────────────────────────────────────
// Relative HR-target grade (HR probs are low; ~18%+ is elite for the market)
const hrGrade = (p, adj) => {
  let g = p>=0.18?"A":p>=0.15?"B":p>=0.12?"C":p>=0.09?"D":"F"
  // Facing a wall (elite HR-suppressor, pitcher_adj<=0.97) demotes the label one
  // tier so a top HR target vs Skenes/Sánchez doesn't read 'A' on prob alone.
  if (adj!=null && adj<=0.97) g = {A:"B",B:"C",C:"D",D:"F",F:"F"}[g]
  return g
}
const hrGradeColor = g => g==="A"?T.blue:g==="B"?T.green:g==="C"?T.amber:g==="D"?T.orange:T.muted2
// Plain-English game writeup from the real computed values (no invented numbers).
function buildGameRead(game, w, pf, hrTargets){
  const A=game.away.abbr, H=game.home.abbr, g=game.grade||0, s=[]
  s.push(g>=80?`${A} @ ${H} is one of the better spots on the board today, grading ${gradeLetter(g)} (${g}/100).`
    : g>=65?`${A} @ ${H} grades ${gradeLetter(g)} (${g}/100) — a live spot worth a look.`
    : `${A} @ ${H} is a middling environment, grading ${gradeLetter(g)} (${g}/100).`)
  if(pf!=null) s.push(pf>=1.10?`${game.venue} is a hitter's park (${pf.toFixed(2)}× HR factor), lifting the bats here.`
    : pf<=0.92?`${game.venue} leans pitcher-friendly (${pf.toFixed(2)}×), so temper power expectations.`
    : `${game.venue} plays roughly neutral (${pf.toFixed(2)}×).`)
  if(w){
    if(w.is_dome) s.push(`It's a dome — weather is a non-factor.`)
    else {
      const bits=[]
      if(w.wind_call?.io==="out") bits.push(`wind ${w.wind_call.label.toLowerCase()}${w.wind_mph?` at ${w.wind_mph} mph`:""}, helping the ball carry`)
      else if(w.wind_call?.io==="in") bits.push(`wind ${w.wind_call.label.toLowerCase()}${w.wind_mph?` at ${w.wind_mph} mph`:""}, knocking balls down`)
      if(w.temp_f!=null) bits.push(`${w.temp_f}°F${w.temp_f>=80?" (warm air adds carry)":w.temp_f<=55?" (cool air kills carry)":""}`)
      if(w.hr_impact!=null && Math.abs(w.hr_impact)>=0.01) bits.push(`a net ${w.hr_impact>=0?"+":""}${(w.hr_impact*100).toFixed(1)}% HR-environment effect`)
      if(bits.length) s.push(`Conditions: ${bits.join(", ")}.`)
    }
  }
  if(hrTargets.length){
    const t=hrTargets[0]
    let line=`Top HR target is ${t.name} at ${pct(t.prob)}${t.opp?` vs ${t.opp}`:""} (${hrGrade(t.prob,t.adj)} grade)`
    if(hrTargets[1]) line+=`, with ${hrTargets[1].name} (${pct(hrTargets[1].prob)}) close behind`
    s.push(line+".")
  } else s.push(`HR targets populate once today's projections run.`)
  return s.join(" ")
}
function GameModal({card,onClose,onPlayer}){
  const game=card, d=game.raw?mapDetail(game.raw):SAMPLE_DETAIL, teams=d._teams||[game.away.abbr,game.home.abbr]
  const raw=game.raw, w=raw?.weather||null, pf=raw?.intel?.park_factor ?? null
  const subg = raw?.intel?.subgrades || (raw ? null : {power:82, hits:74, strikeouts:41})
  // A hitter faces the OPPOSING starter — fall back by team, never his own arm.
  const _homeP=raw?.pitchers?.home?.name||null, _awayP=raw?.pitchers?.away?.name||null
  const oppPitcherFor=(team)=> team===game.home?.abbr?_awayP : team===game.away?.abbr?_homeP : null
  const hrTargets=(raw?.plays||[]).filter(p=>p.prop_type==="HR_anytime")
    .sort((a,b)=>(b.prob_over||0)-(a.prob_over||0)).slice(0,5)
    .map(p=>({name:p.name,prob:p.prob_over||0,player_id:p.player_id,adj:p.pitcher_adj,opp:p.opp_pitcher_name||oppPitcherFor(p.team)}))
  const readText=buildGameRead(game,w,pf,hrTargets)
  // AI writeup layer: fetch a Claude-written read (cached/day); fall back to
  // the templated readText if unavailable (no key / egress / error).
  const [aiRead,setAiRead]=useState(null)
  useEffect(()=>{
    if(!raw) return
    let live=true
    const facts={away:game.away.abbr,home:game.home.abbr,time:game.time,grade:game.grade,
      grade_letter:gradeLetter(game.grade),park_factor:pf,
      weather:w?{conditions:w.conditions,temp_f:w.temp_f,wind_label:w.wind_call?.label,wind_io:w.wind_call?.io,wind_mph:w.wind_mph,precip_pct:w.precip_pct,hr_impact:w.hr_impact,is_dome:w.is_dome}:null,
      targets:hrTargets.map(t=>({name:t.name,prob_pct:pct(t.prob),opp:t.opp,grade:hrGrade(t.prob,t.adj)}))}
    const key=`${game.away.abbr}-${game.home.abbr}-${todayStr()}`
    getGameRead(key,facts).then(r=>{ if(live && r && r.narrative) setAiRead(r.narrative) }).catch(()=>{})
    return ()=>{ live=false }
  },[])
  const displayRead = aiRead || readText
  const [readCopied,setReadCopied]=useState(false)
  const copyRead=async()=>{ const txt=`LINEBRKR — ${game.away.abbr} @ ${game.home.abbr}${game.time?` · ${game.time}`:""}\nGrade ${gradeLetter(game.grade)} (${game.grade}/100)${pf!=null?` · Park ${pf.toFixed(2)}×`:""}\n\n${displayRead}\n\n${hrTargets.map((t,i)=>`${i+1}. ${t.name} — ${pct(t.prob)} HR${t.opp?` vs ${t.opp}`:""} (${hrGrade(t.prob,t.adj)})`).join("\n")}${EXPORT_DISCLAIMER}`
    try{ await navigator.clipboard.writeText(txt) }catch{ const ta=document.createElement("textarea");ta.value=txt;document.body.appendChild(ta);ta.select();try{document.execCommand("copy")}catch(_){}ta.remove() }
    setReadCopied(true); setTimeout(()=>setReadCopied(false),1800) }
  const [tab,setTab]=useState("Read"), [lineupTeam,setLineupTeam]=useState(teams[0])
  const [attackOrder,setAttackOrder]=useState(false)
  const hasLineups=Object.keys(d.lineups||{}).length>0
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"14px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:16}}>
          <div>
            <div style={{fontSize:23,fontWeight:900,letterSpacing:"-0.02em"}}>{game.away.abbr} <span style={{color:T.muted,fontWeight:600}}>@</span> {game.home.abbr}{game.time&&<span style={{fontSize:12,fontWeight:600,color:T.muted,marginLeft:10}}>{game.time}</span>}</div>
            <div style={{fontSize:12,color:T.muted,marginTop:2}}>{game.venue}</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}><GradeRing grade={game.grade}/><button onClick={onClose} style={{width:32,height:32,borderRadius:9,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:15,cursor:"pointer"}}>✕</button></div>
        </div>
        <Tabs tabs={["The Read",`Bets (${d.bets.length})`,"Park","Pitching","Lineups"]} active={tab==="Read"?"The Read":tab} onSelect={t=>setTab(t==="The Read"?"Read":t.startsWith("Bets")?"Bets":t)}/>
        {tab==="Read" && (
          <div>
            {/* Ballpark conditions strip */}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
              <div style={{flex:"1 1 46%",background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"9px 11px"}}>
                <div style={{fontSize:8.5,color:T.green,fontWeight:700,letterSpacing:"0.1em"}}>TOTAL ACTION</div>
                <div style={{fontSize:18,fontWeight:900,color:gradeColor(game.grade)}}>{gradeLetter(game.grade)} <span style={{fontSize:11,color:T.muted,fontWeight:700}}>{game.grade}/100</span></div>
              </div>
              <div style={{flex:"1 1 46%",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
                <div style={{fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.1em"}}>PARK FACTOR</div>
                <div style={{fontSize:18,fontWeight:900,color:T.text,fontFamily:T.mono}}>{pf!=null?`${pf.toFixed(2)}×`:"—"}</div>
              </div>
            </div>
            {/* Sub-grades by betting angle */}
            {subg && (
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                {[["POWER / HR","power"],["HITS","hits"],["STRIKEOUTS","strikeouts"]].map(([lbl,k])=>{ const v=subg[k]; return (
                  <div key={k} style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 8px",textAlign:"center"}}>
                    <div style={{fontSize:18,fontWeight:900,color:gradeColor(v??0),lineHeight:1}}>{v!=null?gradeLetter(v):"—"}</div>
                    <div style={{fontSize:8,color:T.muted,fontWeight:700,letterSpacing:"0.06em",marginTop:5}}>{lbl}</div>
                  </div>
                )})}
              </div>
            )}
            {w && !w.is_dome && (
              <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",marginBottom:12}}>
                <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>🌤 BALLPARK WEATHER</div>
                <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:12,color:T.muted2}}>
                  {w.conditions && <span>{w.conditions}</span>}
                  {w.temp_f!=null && <span style={{fontFamily:T.mono}}>{w.temp_f}°F</span>}
                  {w.wind_call && <span style={{color:w.wind_call.io==="out"?T.green:w.wind_call.io==="in"?T.red:T.muted2,fontWeight:700}}>{w.wind_call.label}{w.wind_mph?` · ${w.wind_mph} mph`:""}</span>}
                  {w.precip_pct!=null && <span style={{fontFamily:T.mono}}>Precip {w.precip_pct}%</span>}
                  {w.hr_impact!=null && Math.abs(w.hr_impact)>=0.01 && <span style={{color:w.hr_impact>=0?T.green:T.red,fontWeight:700,fontFamily:T.mono}}>HR contact {w.hr_impact>=0?"+":""}{(w.hr_impact*100).toFixed(1)}%</span>}
                </div>
                {w.ppd_risk && (
                  <div style={{marginTop:8,display:"flex",alignItems:"center",gap:7,fontSize:11.5,fontWeight:700,
                    color:w.ppd_risk.level==="high"?T.red:T.amber}}>
                    <span>🌧 {w.ppd_risk.label}</span>
                    <span style={{color:T.muted,fontWeight:600}}>— check game status before first pitch; a delay or PPD would void the day for these plays.</span>
                  </div>
                )}
              </div>
            )}
            {w?.is_dome && <div style={{fontSize:12,color:T.muted,marginBottom:12}}>🏟 Dome — weather is a non-factor.</div>}
            {/* The writeup */}
            <div style={{borderLeft:`3px solid ${T.green}`,background:T.card3,borderRadius:"0 10px 10px 0",padding:"12px 14px",marginBottom:14}}>
              <div style={{fontSize:9,color:T.green,fontWeight:700,letterSpacing:"0.12em",marginBottom:6,display:"flex",alignItems:"center",gap:6}}>THE READ {aiRead && <span style={{fontSize:8,color:T.muted,border:`1px solid ${T.border2}`,borderRadius:4,padding:"1px 5px",letterSpacing:"0.06em"}}>AI</span>}</div>
              <div style={{fontSize:13.5,color:T.text,lineHeight:1.55}}>{displayRead}</div>
            </div>
            {/* Top HR targets */}
            {hrTargets.length>0 && (<>
              <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>TOP HR TARGETS</div>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
                {hrTargets.map((t,i)=>{ const gr=hrGrade(t.prob,t.adj); return (
                  <button key={`hr-${t.player_id ?? t.name}-${i}`} onClick={()=>t.player_id&&onPlayer({name:t.name,player_id:t.player_id,market:"HR",opp:t.opp?`vs ${t.opp}`:""},d.park)} style={{width:"100%",textAlign:"left",cursor:t.player_id?"pointer":"default",color:T.text,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:800,color:T.text}}><span style={{color:T.muted,fontFamily:T.mono,fontSize:11,marginRight:6}}>{i+1}</span>{t.name}</div>
                      {t.opp && <div style={{fontSize:11,color:T.muted,marginTop:2}}>vs {t.opp}</div>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                      <span style={{fontSize:15,fontWeight:900,color:T.text,fontFamily:T.mono}}>{pct(t.prob)}</span>
                      <span style={{fontSize:12,fontWeight:900,color:hrGradeColor(gr),border:`1px solid ${hrGradeColor(gr)}`,borderRadius:6,padding:"2px 8px"}}>{gr}</span>
                    </div>
                  </button>
                )})}
              </div>
            </>)}
            <button onClick={copyRead} style={{width:"100%",padding:"11px",borderRadius:10,background:readCopied?T.greenDim:T.card,border:`1px solid ${readCopied?T.green:T.border2}`,color:readCopied?T.green:T.muted2,fontSize:13,fontWeight:800,cursor:"pointer"}}>{readCopied?"Copied ✓":"⤓ Copy / share this read"}</button>
            <div style={{fontSize:10,color:T.muted,lineHeight:1.5,marginTop:10}}>Auto-generated from the model's grade, park, weather, and projections — every number is computed, none invented.</div>
          </div>
        )}
        {tab==="Bets" && (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {d.bets.length===0 && <div style={{textAlign:"center",color:T.muted,padding:"24px 0",fontSize:13}}>No edges for this game yet.</div>}
            {d.bets.map((b,i)=>(
              <button key={i} onClick={()=>onPlayer({name:b.player,player_id:b.player_id,market:b.market,tier:b.tier,prob:b.prob,conf:b.conf,odds:b.odds,oddsNum:b.oddsNum,edge:`${b.edge>=0?"+":""}${b.edge.toFixed(1)}%`,opp:`${game.away.abbr} @ ${game.home.abbr}`},d.park)} style={{textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${(b.tier==="BEST BET"||b.tier==="ELITE")?T.greenMid:T.border2}`,borderRadius:12,padding:"12px 14px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,minWidth:0}}>
                    <span style={{fontSize:15,fontWeight:800,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.player}</span>
                    <span style={{fontSize:10,color:T.muted2,fontFamily:T.mono}}>{b.market}</span>
                  </div>
                  <TierBadge tier={b.tier}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:8}}>
                  <Stat label="Prob" value={pct(b.prob||0)} color={probColor(b.prob||0)} bg={(b.prob||0)>=0.6?T.greenDim:T.card2}/>
                  <Stat label="Rating /100" value={b.conf} color={confColor(b.conf)}/>
                  <Stat label="Edge" value={`${b.edge>=0?"+":""}${b.edge.toFixed(1)}%`} color={edgeColor(b.edge)}/>
                  <Stat label="Line" value={b.odds}/>
                </div>
                <div style={{fontSize:12,color:T.muted2,fontStyle:"italic",lineHeight:1.4}}>{b.reason}</div>
              </button>))}
          </div>
        )}
        {tab==="Park" && <ParkPanel park={d.park}/>}
        {tab==="Pitching" && (
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {d.pitching.length===0 && <div style={{textAlign:"center",color:T.muted,padding:"24px 0",fontSize:13}}>Probable pitchers not announced.</div>}
            {d.pitching.map((p,i)=>{ const sc=p.scores||{}; const has=sc.hrVuln!=null; const noData=p.era==="—"&&p.whip==="—"&&p.k9==="—"; return (
              <div key={i} style={{background:T.card,border:`1px solid ${p.struggling?"#7a3030":T.border2}`,borderRadius:12,padding:14}}>
                <div style={{marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                  <div>
                    <div style={{fontSize:16,fontWeight:800,display:"flex",alignItems:"center",gap:6}}>
                      {p.name}
                      {p.struggling && <span title={`Below-average / struggling${p.strugglingReasons.length?` — ${p.strugglingReasons.join(", ")}`:""}`} style={{fontSize:13}}>🔥</span>}
                    </div>
                    <div style={{fontSize:12,color:T.muted}}>{p.team} · Throws {p.throws}</div>
                  </div>
                  {p.struggling && <span title={p.strugglingReasons.join(", ")} style={{fontSize:9,fontWeight:800,letterSpacing:"0.08em",color:"#ff6b6b",border:"1px solid #7a3030",borderRadius:6,padding:"3px 7px",whiteSpace:"nowrap"}}>STRUGGLING</span>}
                </div>
                {has ? (<>
                  {/* LINEBRKR risk scores first */}
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8,marginBottom:8}}>
                    <Stat label="HR Vulnerability" value={sc.hrVuln} color={scoreColor(sc.hrVuln)}/>
                    <Stat label="Contact Allowed" value={sc.contact} color={scoreColor(sc.contact)}/>
                    <Stat label="Strikeout Threat" value={sc.kThreat} color={scoreColor(sc.kThreat)}/>
                    <Stat label="HRR Risk" value={sc.hrrRisk} color={scoreColor(sc.hrrRisk)}/>
                  </div>
                  {p.summary && <div style={{fontSize:12,color:T.muted2,fontStyle:"italic",lineHeight:1.45,marginBottom:10}}>{p.summary}</div>}
                </>) : null}
                {/* Supporting raw stats — or a friendly note when we have no season line
                    for him yet (spot starter / opener / just called up, not yet ingested). */}
                {noData ? (
                  <div style={{fontSize:12,color:T.muted,fontStyle:"italic",lineHeight:1.5}}>Season stats not posted yet — likely a spot starter or opener we haven't ingested. Matchup grades fill in once his line is available.</div>
                ) : (<>
                  <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:6}}>SUPPORTING STATS</div>
                  <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:11,color:T.muted2,fontFamily:T.mono}}>
                    <span>ERA {p.era}</span><span>WHIP {p.whip}</span><span>K/9 {p.k9}</span><span>Barrel% {p.barrel}</span>
                  </div>
                </>)}
                {/* Career line vs the lineup he faces today — DISPLAY CONTEXT only (BvP is
                    tiny-sample; it's not a model input, and the label says so). */}
                {p.vsTeam && p.vsTeam.ab>0 && <VsTeamBlock vsTeam={p.vsTeam}/>}
                {/* Pitch arsenal (usage %) */}
                {p.arsenal && p.arsenal.length>0 && (
                  <div style={{marginTop:12,borderTop:`1px solid ${T.border}`,paddingTop:10}}>
                    <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:7}}>ARSENAL — PITCH MIX</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                      {p.arsenal.map((a,ix)=>(
                        <div key={ix} style={{display:"flex",alignItems:"center",gap:6,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"4px 9px"}}>
                          <span style={{fontSize:11,fontWeight:700,color:T.text}}>{a.pitch}</span>
                          <span style={{fontSize:11,fontWeight:800,color:T.green,fontFamily:T.mono}}>{a.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Platoon + home/away splits */}
                {p.splits && (()=>{ const s=p.splits; const has=v=>v&&v!=="—"
                  const GC="46px repeat(5,1fr)"
                  const row=(label,o)=>(<div style={{display:"grid",gridTemplateColumns:GC,gap:5,fontSize:10.5,fontFamily:T.mono,color:T.muted2,padding:"3px 0"}}>
                    <span style={{color:T.muted,fontWeight:700}}>{label}</span>
                    <span>{has(o?.avg)?o.avg:"—"}</span><span>{has(o?.obp)?o.obp:"—"}</span><span>{has(o?.slg)?o.slg:"—"}</span><span style={{fontWeight:700,color:T.text}}>{has(o?.ops)?o.ops:"—"}</span><span style={{fontWeight:700,color:o?.hr9!=null?(o.hr9>=1.3?T.green:o.hr9<=0.8?T.red:T.text):T.muted2}}>{o?.hr9!=null?o.hr9.toFixed(2):"—"}</span>
                  </div>)
                  return (<div style={{marginTop:12,borderTop:`1px solid ${T.border}`,paddingTop:10}}>
                    <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:6}}>SPLITS — OPPOSING BATTERS</div>
                    <div style={{display:"grid",gridTemplateColumns:GC,gap:5,fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.04em",marginBottom:2}}>
                      <span></span><span>AVG</span><span>OBP</span><span>SLG</span><span>OPS</span><span>HR/9</span>
                    </div>
                    {row("vs RHB", s.vs_rhb)}
                    {row("vs LHB", s.vs_lhb)}
                    {(s.home_era!=null||s.away_era!=null) && (
                      <div style={{display:"flex",gap:18,marginTop:8,fontSize:11,fontFamily:T.mono,color:T.muted2}}>
                        <span><span style={{color:T.muted,fontWeight:700}}>Home ERA</span> {s.home_era!=null?s.home_era.toFixed(2):"—"}</span>
                        <span><span style={{color:T.muted,fontWeight:700}}>Away ERA</span> {s.away_era!=null?s.away_era.toFixed(2):"—"}</span>
                      </div>
                    )}
                  </div>)
                })()}
              </div>)})}
          </div>
        )}
        {tab==="Lineups" && (
          <div>
            {!hasLineups && <div style={{textAlign:"center",color:T.muted,padding:"24px 0",fontSize:13}}>Lineups not posted yet.</div>}
            {hasLineups && (<>
              <div style={{display:"flex",gap:8,marginBottom:10}}>{teams.map(tm=>(<button key={tm} onClick={()=>setLineupTeam(tm)} style={{flex:1,padding:"8px",borderRadius:9,cursor:"pointer",background:lineupTeam===tm?T.green:T.card,border:`1px solid ${lineupTeam===tm?T.green:T.border2}`,color:lineupTeam===tm?"#04140D":T.muted,fontSize:12,fontWeight:800}}>{tm}</button>))}</div>
              <div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
                <button onClick={()=>setAttackOrder(o=>!o)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 10px",color:attackOrder?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>{attackOrder?"⚡ Attack order":"Batting order"}</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"24px 1fr 40px 52px 52px 44px",gap:8,padding:"0 12px 6px",fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>
                <span>{attackOrder?"RK":"#"}</span><span>PLAYER</span><span style={{textAlign:"right"}}>HAND</span><span style={{textAlign:"right"}}>ATTACK</span><span style={{textAlign:"right"}}>BEST</span><span style={{textAlign:"right"}}>FORM</span>
              </div>
              <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden"}}>
                {(attackOrder ? [...(d.lineups[lineupTeam]||[])].sort((a,b)=>(b.attack||0)-(a.attack||0)) : (d.lineups[lineupTeam]||[])).map((r,i)=>{ const tc=TAG_COLOR[r.tag]||T.muted; return (
                  <button key={i} onClick={()=>onPlayer({name:r.name,player_id:r.player_id,opp:`${game.away.abbr} @ ${game.home.abbr}`},d.park)} style={{width:"100%",textAlign:"left",cursor:"pointer",display:"grid",gridTemplateColumns:"24px 1fr 40px 52px 52px 44px",alignItems:"center",gap:8,padding:"10px 12px",background:i%2?T.card2:"transparent",border:"none",borderTop:i?`1px solid ${T.border}`:"none"}}>
                    <span style={{fontSize:11,color:T.muted,fontFamily:T.mono}}>{attackOrder?i+1:r.n}</span><span style={{fontSize:13,fontWeight:600,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
                    <span style={{fontSize:11,color:T.muted2,textAlign:"right"}}>{r.hand}</span>
                    <span style={{fontSize:13,fontWeight:800,color:scoreColor(r.attack||0),fontFamily:T.mono,textAlign:"right"}}>{r.attack??"—"}</span>
                    <span style={{fontSize:10,fontWeight:700,color:T.muted2,textAlign:"right"}}>{r.bestMarket}</span>
                    <span style={{fontSize:10,fontWeight:700,color:tc,textAlign:"right"}}>{r.tag}</span>
                  </button>)})}
              </div>
            </>)}
          </div>
        )}
      </div>
    </Sheet>
  )
}

// ── Wall detection (pitcher difficulty → hitter multiplier) ─────────────────
const WALL = {
  "HARD WALL":{bg:"#FFF1F2",fg:"#9F1239",icon:"🚫"},
  "SOFT WALL":{bg:"#FFEDD5",fg:"#7C2D12",icon:"⚠"},
  "MODERATE": {bg:"#F8FAFC",fg:"#64748B",icon:"–"},
  "NEUTRAL":  {bg:"#FFFFFF",fg:"#64748B",icon:"●"},
  "HITTABLE": {bg:"#DCFCE7",fg:"#005C3A",icon:"✓"},
}
// Derived from the model's pitcher adjustment (>1 = hittable, <1 = wall).
// Defaults to NEUTRAL with no multiplier until pitcher data is computed.
const wallFor = adj => {
  if (adj==null) return { tier:"NEUTRAL", mult:null, ...WALL.NEUTRAL }
  const tier = adj>=1.05 ? "HITTABLE" : adj>=0.98 ? "NEUTRAL" : adj>=0.90 ? "MODERATE" : adj>=0.82 ? "SOFT WALL" : "HARD WALL"
  return { tier, mult:adj, ...WALL[tier] }
}
function WallChip({adj}){
  const w = wallFor(adj)
  return <span style={{fontSize:10,fontWeight:700,color:w.fg,background:w.bg,border:`1px solid ${w.fg}40`,borderRadius:4,padding:"1px 6px"}}>{w.icon} {w.tier}{w.mult!=null?` ${w.mult.toFixed(2)}x`:""}</span>
}
// "Pitcher to attack" — the inverse of a wall. Flags the opposing STARTER as a
// target when the model grades the arm HITTABLE (pitcher_adj ≥ 1.05) or he's
// notably HR-prone (HR/9 ≥ 1.5). `hot` = both → a prime target. Returns null for
// a neutral/tough arm (nothing to flag). Pitcher fields are per-starter, so read
// them off any hitter facing him.
const pitcherAttack = (i) => {
  if(!i) return null
  const adj=i.pitcher_adj, hr9=i.pitcher_hr9, era=i.starter_era, k9=i.pitcher_k9, ops=i.pitcher_ops_vs_hand, whip=i.pitcher_whip
  // A genuine bat-misser (high K/9) isn't "attackable" for HRR no matter how his
  // WHIP/ERA/OPS line looks — balls not in play = fewer hits/runs. So the soft
  // run-prevention signals are VETOED for a strikeout arm; only real HR-proneness
  // or the model's own hittable grade can still flag him. (Stops flagging aces
  // like Skubal on a single soft reading.)
  const batMisser  = k9!=null && k9>=9.0
  const hittable   = adj!=null && adj>=1.04    // model says the matchup inflates the bat
  const homerProne = hr9!=null && hr9>=1.4     // gives up homers even to good arms
  const highWHIP   = !batMisser && whip!=null && whip>=1.35  // baserunners → hits/runs/RBI
  const highERA    = !batMisser && era!=null && era>=4.60    // bad run prevention
  const softVsHand = !batMisser && ops!=null && ops>=0.780   // gets hit by this side
  const lowK       = k9!=null && k9<=7.50      // corroborating: doesn't miss bats
  const n = [hittable,homerProne,highWHIP,highERA,softVsHand].filter(Boolean).length
  if(n===0) return null
  const why=[]
  if(hittable)   why.push(`hittable ${adj.toFixed(2)}x`)
  if(homerProne) why.push(`${hr9.toFixed(1)} HR/9`)
  if(highWHIP)   why.push(`${whip.toFixed(2)} WHIP`)
  if(highERA)    why.push(`${era.toFixed(2)} ERA`)
  if(softVsHand) why.push(`${ops.toFixed(3).replace(/^0/,"")} OPS/hand`)
  if(lowK)       why.push(`low K ${k9.toFixed(1)}`)
  return { why:why.join(" · "), hot: n>=2 }   // 🔥 prime = 2+ attack signals align
}
// Platoon: opposite hands favorable (✓), same hands a drag (✗), switch neutral
const platoonInfo = (bats, hand) => {
  if (!bats || !hand) return null
  if (bats==="S") return { txt:"Switch", sym:"→", color:T.muted }
  const fav = bats !== hand
  return { txt:`${bats}v${hand}`, sym:fav?"✓":"✗", color:fav?T.green:T.red }
}
// One-sentence plain-English verdict from the model inputs we have
const buildVerdict = (e, tier, wallTier) => {
  // Use the DE-VIGGED fair prob (no_vig_prob), not the raw vigged implied, so the
  // "book implies X% vs model Y%" gap reconciles with the "(+Z% edge)" that follows
  // (edge = model − no_vig). Falls back to model − edge, then vigged implied.
  const fair = e.no_vig_prob!=null ? e.no_vig_prob
             : (e.prob_over!=null && e.edge_pct!=null ? e.prob_over - e.edge_pct : impliedProb(e.book_over))
  let s = `${e.full_name} grades ${tier}`
  if (e.opp_pitcher_name) s += ` — ${e.opp_pitcher_name} grades ${wallTier.toLowerCase()}`
  if (fair!=null && e.prob_over!=null) s += `; book implies ${Math.round(fair*100)}% vs our model ${Math.round(e.prob_over*100)}%`
  if (e.edge_pct!=null) s += ` (${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}% edge)`
  return s + "."
}

// ── Picks tab (leaderboard, prob-ranked) ────────────────────────────────────
// Live in-game status for a pick, from the /live boxscore line (keyed by player_id).
// Returns null when the player's game hasn't started (nothing to show).
const LIVE_NEED = { "HRR":["hrr",2], "HR":["hr",1], "1+ H":["h",1], "2+ H":["h",2], "TB":["tb",2] }
function liveStatus(line, marketLabel, pickLine){
  if(!line) return null
  // Strikeouts are pitcher-side and graded vs the pick's OWN line (e.g. O3.5). The board
  // bets the over, so a hit is the K count strictly above the line; a final at/under = miss.
  if(marketLabel==="K"){
    if(line.k==null || pickLine==null) return null
    const cur = line.k, thr = pickLine
    if(cur>thr) return {state:"hit", cur, thr}
    if(line.final) return {state:"miss", cur, thr}
    return {state:"live", cur, thr}
  }
  const need = LIVE_NEED[marketLabel]
  if(!need) return null
  const [k,thr] = need
  const cur = line[k] ?? 0
  if(cur>=thr) return {state:"hit", cur, thr}
  // DNP: game final but the batter never appeared (0 PA & 0 AB) → void, not a miss. A
  // benched/scratched guy sits in the boxscore with 0 stats, so without this he'd grade MISS.
  if(line.final && (line.pa||0)===0 && (line.ab||0)===0) return {state:"void", cur, thr}
  if(line.final) return {state:"miss", cur, thr}
  return {state:"live", cur, thr}   // game in progress, not there yet
}
function LiveBadge({line, marketLabel, pickLine}){
  const lv = liveStatus(line, marketLabel, pickLine)
  if(!lv) return null
  const cfg = lv.state==="hit" ? {bg:T.greenDim,bd:T.green,fg:T.green,txt:"✓ HIT"}
    : lv.state==="miss" ? {bg:"rgba(239,106,106,0.10)",bd:"rgba(239,106,106,0.35)",fg:"#ef6a6a",txt:"✗ MISS"}
    : lv.state==="void" ? {bg:T.card2,bd:T.border2,fg:T.muted,txt:"— DNP"}
    : {bg:T.amberDim,bd:T.amber,fg:T.amber,txt:`🔴 ${lv.cur}/${lv.thr}`}
  return <span style={{fontSize:11,fontWeight:800,color:cfg.fg,background:cfg.bg,border:`1px solid ${cfg.bd}`,borderRadius:6,padding:"2px 6px",whiteSpace:"nowrap"}}>{cfg.txt}</span>
}
// ── "Loud profile" highlight ────────────────────────────────────────────────
// When a hitter's batted-ball quality crosses elite thresholds, light up those
// metrics (and, when 2+ light up, the whole card). Tunable in one place.
const LOUD = { hardHit: 0.40, barrel: 0.10, form: 0.05, xwoba: 0.370, hrFb: 0.15 }
function loudMetrics(e){
  const out = []
  if(e.hard_hit_pct!=null && e.hard_hit_pct >= LOUD.hardHit) out.push({k:"HH",   label:`Hard-Hit ${Math.round(e.hard_hit_pct*100)}%`})
  if(e.barrel_pct  !=null && e.barrel_pct   >= LOUD.barrel)  out.push({k:"BRL",  label:`Barrel ${Math.round(e.barrel_pct*100)}%`})
  if(e.hr_fb_pct   !=null && e.hr_fb_pct    >= LOUD.hrFb)    out.push({k:"HRFB", label:`${Math.round(e.hr_fb_pct*100)}% HR-worthy FB`})
  if(e.form_delta_pct!=null && e.form_delta_pct >= LOUD.form) out.push({k:"FORM", label:`Form +${Math.round(e.form_delta_pct*100)}%`})
  if(e.xwoba       !=null && e.xwoba        >= LOUD.xwoba)   out.push({k:"XW",   label:`xwOBA .${String(Math.round(e.xwoba*1000)).padStart(3,"0")}`})
  return out
}
function LoudChips({items}){
  if(!items.length) return null
  return (
    <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:10}}>
      {items.map((m,i)=>(
        <span key={i} style={{fontSize:10,fontWeight:800,letterSpacing:"0.02em",color:"#04140D",
          background:T.green,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap",
          boxShadow:`0 0 8px ${T.green}55`}}>🔥 {m.label}</span>
      ))}
    </div>
  )
}

// Expandable strikeout-analytics grid, shown inside a K pick card. Pulls the recency
// blend (the fix behind the 4-26 → repaired K record), swing-and-miss stuff, the
// opposing lineup's K rate, and the arsenal — the projection numbers come from the row.
function KDrawer({ pitcherName, oppTeam, e }){
  const { data, loading, error } = useFetch(
    ()=> pitcherName ? getKAnalytics(pitcherName, oppTeam||'') : Promise.resolve(null),
    [pitcherName, oppTeam])
  const Cell = ({label,value,sub,color=T.text}) => (
    <div style={{background:T.card2,borderRadius:8,padding:"7px 9px",minWidth:0}}>
      <div style={{fontSize:9,letterSpacing:"0.06em",color:T.faint,textTransform:"uppercase",marginBottom:2}}>{label}</div>
      <div style={{fontSize:14,fontWeight:800,fontFamily:T.mono,color}}>{value}</div>
      {sub && <div style={{fontSize:9.5,color:T.muted,marginTop:1}}>{sub}</div>}
    </div>
  )
  if(loading) return <div style={{fontSize:11,color:T.muted,fontStyle:"italic",marginTop:8}}>Loading strikeout analytics…</div>
  if(error) return <div style={{fontSize:11,color:T.red,marginTop:8}}>Analytics unavailable: {String(error)}</div>
  if(!data || data.status) return <div style={{fontSize:11,color:T.muted,marginTop:8}}>No strikeout analytics for {pitcherName}.</div>
  const f = data.form||{}, st = data.stuff||{}, op = data.opponent, se = data.season||{}
  const kv = e.lambda_adjusted!=null ? e.lambda_adjusted : (e.lambda_raw!=null?e.lambda_raw:null)
  // recency direction: recent k9 vs season → heating / fading
  const dK = (f.recent_k9!=null && f.season_k9) ? f.recent_k9 - f.season_k9 : null
  const dColor = dK==null?T.muted : dK>=0.4?T.green : dK<=-0.4?T.red : T.muted2
  const opColor = op ? (op.tag==="elite"?T.green:op.tag==="high"?T.amber:T.muted2) : T.muted
  return (
    <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${T.border}`,display:"flex",flexDirection:"column",gap:9}}>
      {/* Projection — from the board row */}
      <div>
        <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",color:T.muted2,textTransform:"uppercase",marginBottom:6}}>Projection</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
          <Cell label="Proj Ks" value={kv!=null?kv.toFixed(1):"—"} color={T.green}/>
          <Cell label="Line" value={e.line!=null?`O ${e.line}`:"—"}/>
          <Cell label="Over / Under" value={`${Math.round((e.prob_over||0)*100)}% / ${Math.round((e.prob_under||0)*100)}%`} color={probColorMkt(e.prob_over||0,"K")}/>
        </div>
      </div>
      {/* Recency form — the blend that actually drives the projection */}
      <div>
        <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",color:T.muted2,textTransform:"uppercase",marginBottom:6}}>Recent form vs season {f.starts_in_window?`· L${f.starts_in_window} starts weighted ${Math.round((f.weight_recent||0)*100)}%`:"· season only"}</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
          <Cell label="K/9" value={f.recent_k9!=null?f.recent_k9.toFixed(1):se.k9?.toFixed?.(1)||"—"} sub={`season ${se.k9!=null?se.k9.toFixed(1):"—"}`} color={dColor}/>
          <Cell label="Δ vs season" value={dK==null?"—":`${dK>=0?"+":""}${dK.toFixed(1)}`} sub={dK==null?"":dK>=0.4?"heating":dK<=-0.4?"fading":"steady"} color={dColor}/>
          <Cell label="IP / start" value={f.blended_ip_start!=null?f.blended_ip_start.toFixed(1):"—"} sub={f.recent_ip_start!=null?`recent ${f.recent_ip_start.toFixed(1)}`:""}/>
        </div>
      </div>
      {/* Swing-and-miss stuff + opponent lineup */}
      <div>
        <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",color:T.muted2,textTransform:"uppercase",marginBottom:6}}>Stuff &amp; matchup</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:7}}>
          <Cell label="K%" value={st.k_pct!=null?`${st.k_pct}%`:"—"} sub={st.bb_pct!=null?`BB ${st.bb_pct}%`:"per PA"} color={st.k_pct!=null&&st.k_pct>=25?T.green:st.k_pct!=null&&st.k_pct<18?T.red:T.text}/>
          <Cell label="CSW%" value={st.csw_pct!=null?`${st.csw_pct}%`:"—"} sub="called+swing" color={st.csw_pct!=null&&st.csw_pct>=30?T.green:st.csw_pct!=null&&st.csw_pct<27?T.red:T.text}/>
          <Cell label="SwStr%" value={st.swstr_pct!=null?`${st.swstr_pct}%`:"—"} sub="whiff rate" color={st.swstr_pct!=null&&st.swstr_pct>=12?T.green:T.text}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:7,marginTop:7}}>
          <Cell label="xwOBA-agst" value={st.xwoba_against!=null?`.${String(Math.round(st.xwoba_against*1000)).padStart(3,"0")}`:"—"}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr",gap:7,marginTop:7}}>
          <Cell label="Opp lineup K%" value={op?`${(op.k_rate*100).toFixed(1)}%`:"—"} sub={op?(op.tag==="elite"?"elite K target vs lg 22.5%":op.tag==="high"?"high-K lineup vs lg 22.5%":"average vs lg 22.5%"):"lg 22.5%"} color={opColor}/>
        </div>
      </div>
      {/* Arsenal */}
      {Array.isArray(data.arsenal)&&data.arsenal.length>0 && (
        <div>
          <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.08em",color:T.muted2,textTransform:"uppercase",marginBottom:6}}>Arsenal <span style={{fontWeight:400,color:T.faint,letterSpacing:0}}>· usage / whiff</span></div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {data.arsenal.map((p,i)=>(
              <span key={i} style={{fontSize:10.5,fontFamily:T.mono,color:T.muted2,background:T.card2,border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 8px"}}>
                {p.label} <b style={{color:T.text}}>{p.usage}%</b>
                {p.whiff!=null && <span style={{color:p.whiff>=35?T.green:p.whiff>=25?T.amber:T.muted}}> · {p.whiff}% wh</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Season line footer */}
      <div style={{fontSize:10.5,color:T.muted,fontFamily:T.mono,display:"flex",gap:10,flexWrap:"wrap"}}>
        <span>ERA {se.era ?? "—"}</span><span>WHIP {se.whip ?? "—"}</span><span>FIP {se.fip ?? "—"}</span><span>HR/9 {se.hr9 ?? "—"}</span><span>{se.gs ?? "—"} GS · {se.ip ?? "—"} IP</span>
        {data.throws && <span>Throws {data.throws}</span>}
      </div>
    </div>
  )
}

// Side-tracked K hit/miss record — surfaced in the K market section while it's in testing,
// so the strikeouts results are visible (excluded from the public scorecard) as they validate.
function KRecordCard({data:propData=null}){
  const { data: selfData } = useFetch(()=> propData==null ? getKRecord(60,10).catch(()=>null) : Promise.resolve(propData), [propData])
  const data = propData!=null ? propData : selfData
  if(!data || data.error) return null
  const n = data.n||0
  const pct = data.hit_rate!=null ? Math.round(data.hit_rate*100) : null
  const prettyDate = s => { if(!s) return ""; const [y,m,d]=s.split("-").map(Number); return new Date(y,m-1,d).toLocaleDateString("en-US",{month:"short",day:"numeric"}) }
  return (
    <div style={{background:T.card,border:`1px solid ${T.amber}55`,borderRadius:12,padding:"11px 13px",margin:"0 0 10px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:n?7:0}}>
        <span style={{fontSize:10.5,fontWeight:800,letterSpacing:"0.06em",color:T.amber,textTransform:"uppercase"}}>🧪 K · recent settled</span>
        {n>0
          ? <span style={{fontSize:13,fontFamily:T.mono,fontWeight:800,color:pct>=52?T.green:pct>=45?T.amber:T.red}}>{pct}% · {data.wins}-{data.losses}</span>
          : <span style={{fontSize:11,color:T.muted}}>no settled Ks yet</span>}
      </div>
      {n>0 && <>
        <div style={{fontSize:9.5,color:T.faint,marginBottom:7}}>{n} settled · since {prettyDate(data.since)} · tracked on the side until it validates</div>
        <div style={{display:"flex",flexDirection:"column",gap:3}}>
          {(data.recent||[]).slice(0,6).map((r,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:10.5,fontFamily:T.mono}}>
              <span style={{width:14}}>{r.hit?"✅":"❌"}</span>
              <span style={{flex:1,color:T.muted2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
              <span style={{color:T.muted}}>O{r.line} → <b style={{color:r.hit?T.green:T.red}}>{r.actual} K</b></span>
            </div>
          ))}
        </div>
      </>}
    </div>
  )
}

function PickCard({e, rank, marketLabel, onOpen, sameGameRanks, inParlay, onToggleLeg, onLongPress, liveMap}){
  const [showVerdict,setShowVerdict] = useState(false)
  const [showK,setShowK] = useState(false)
  const pressTimer = React.useRef(null)
  const longRef = React.useRef(false)
  const hasLine = e.book_over!=null
  const conf = confOf(e)
  const edge = (e.edge_pct!=null) ? e.edge_pct*100 : null
  const vf = valueFlag(edge)
  const tier = tierFor(e.prob_over, conf, marketLabel, edge, hasLine)
  const wall = wallFor(e.pitcher_adj)
  const prob = e.prob_over||0
  const pl = platoonInfo(e.bats, e.opp_pitcher_hand)
  const lineupOk = e.lineup_slot!=null
  // Availability: confirmed · out (team's lineup posted but he's NOT in it → scratched) ·
  // unconfirmed (lineup not up yet). Falls back to the old slot check for older payloads.
  const avail = e.avail_status || (lineupOk ? "confirmed" : "unconfirmed")
  const isOut = avail==="out"
  // Game postponed/cancelled → no-action. Row stays for transparency but is muted,
  // struck through, and badged "PPD · NO ACTION" — it won't be graded W/L.
  const isVoid = !!e.void
  const dim = isOut || isVoid   // both mean "won't produce a graded result"
  const pitcherOk = !!e.opp_pitcher_name
  const sameGameOther = (sameGameRanks && sameGameRanks.length>1) ? sameGameRanks.find(r=>r!==rank) : null
  const verdict = buildVerdict(e, tier, wall.tier)
  const loud = loudMetrics(e)
  const isLoud = loud.length >= 2   // 2+ elite metrics = a genuinely loud profile
  const kFlag = marketLabel==="K" && Array.isArray(e.warning_flags) && (e.warning_flags.includes("opp_high_k")||e.warning_flags.includes("opp_elite_k"))
  const legKey = `${e.player_id||e.full_name}-${marketLabel}`
  const leg = {key:legKey, name:e.full_name, player_id:e.player_id, market:marketLabel, prob:e.prob_over, oddsNum:e.book_over, team:e.team, opp:e.opp_pitcher_name}
  const added = inParlay ? inParlay(legKey) : false
  const startPress = () => { longRef.current=false; if(onLongPress){ pressTimer.current=setTimeout(()=>{ longRef.current=true; onLongPress({name:e.full_name, leg, key:legKey, pkey:(e.player_id||e.full_name), view:()=>onOpen(e,tier)}) }, 500) } }
  const cancelPress = () => { if(pressTimer.current) clearTimeout(pressTimer.current) }
  const handleClick = () => { if(longRef.current){ longRef.current=false; return } onOpen(e, tier) }
  return (
    <button onClick={handleClick} onPointerDown={startPress} onPointerUp={cancelPress} onPointerLeave={cancelPress} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:isVoid?T.card:isLoud?`linear-gradient(180deg, ${T.greenDim}, ${T.card})`:T.card,border:`1px solid ${isVoid?T.amber:isLoud?T.green:rank<=3?T.greenMid:T.border}`,boxShadow:isLoud&&!isVoid?`0 0 14px ${T.green}33`:"none",opacity:isVoid?0.6:1,borderRadius:12,padding:14,marginBottom:8}}>
      {/* Header — name + the controls only. Quality badges move to their own row
          below so nothing collides or squeezes the name (was way too cluttered). */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
        <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
          <Headshot mlbamId={e.mlbam_id} name={e.full_name} size={34}/>
          <div style={{display:"flex",alignItems:"baseline",gap:8,minWidth:0}}>
            <span style={{fontSize:15,fontWeight:800,color:rank<=3?T.green:T.muted,fontFamily:T.mono,flexShrink:0}}>#{rank}</span>
            <span style={{fontSize:16,fontWeight:800,color:dim?T.muted:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",textDecoration:dim?"line-through":"none"}}>{e.full_name}</span>
            {isVoid && <span title="Game postponed / cancelled — this play is no-action and will NOT be graded win or loss" style={{fontSize:9.5,fontWeight:900,letterSpacing:"0.06em",color:"#fff",background:T.amber,borderRadius:5,padding:"2px 6px",whiteSpace:"nowrap",flexShrink:0}}>🚫 PPD</span>}
            {!isVoid && isOut && <span title="Team's lineup is posted but this player is not in it — scratched / won't play" style={{fontSize:9.5,fontWeight:900,letterSpacing:"0.06em",color:"#fff",background:T.red,borderRadius:5,padding:"2px 6px",whiteSpace:"nowrap",flexShrink:0}}>⛔ OUT</span>}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
          <LiveBadge line={liveMap && liveMap[e.player_id]} marketLabel={marketLabel} pickLine={e.line}/>
          {onToggleLeg && (
            <span role="button" onPointerDown={ev=>ev.stopPropagation()} onClick={ev=>{ev.stopPropagation(); onToggleLeg(leg)}}
              style={{width:26,height:26,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:15,fontWeight:800,lineHeight:1,
              background:added?T.green:T.greenDim, color:added?"#04140D":T.green, border:`1px solid ${T.green}`,flexShrink:0}}>{added?"✓":"+"}</span>
          )}
          <TierBadge tier={tier}/>
        </div>
      </div>
      {/* Quality-badge strip — its own spaced, wrapping row */}
      {!dim && (isLoud || e.homer_signal?.fires || vf || kFlag || e.form_trend) && (
        <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",marginBottom:8}}>
          {isLoud && <span title="Loud profile — 2+ elite batted-ball metrics" style={{fontSize:9.5,fontWeight:900,letterSpacing:"0.06em",color:"#04140D",background:T.green,borderRadius:5,padding:"3px 7px",whiteSpace:"nowrap"}}>🔥 LOUD</span>}
          {e.homer_signal?.fires && <span title={`Homer Signal — the key catalysts align (${e.homer_signal.n}/${e.homer_signal.of}): ${(e.homer_signal.factors||[]).join(" · ")}`} style={{fontSize:9.5,fontWeight:900,letterSpacing:"0.05em",color:"#1a1206",background:"linear-gradient(90deg,#ffd54a,#e0b24a)",borderRadius:5,padding:"3px 8px",whiteSpace:"nowrap",boxShadow:`0 0 10px ${T.amber}66`}}>⚡ HR SIGNAL</span>}
          {vf && <span title={`Value: +${vf.edge.toFixed(1)}% edge vs the book`} style={{fontSize:10.5,fontWeight:800,color:T.green,background:T.greenDim,border:`1px solid ${T.green}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>{vf.icon} +{vf.edge.toFixed(0)}%</span>}
          {kFlag && <span title="Opposing lineup strikes out a lot — favorable strikeout matchup" style={{fontSize:10.5,fontWeight:800,whiteSpace:"nowrap",borderRadius:6,padding:"2px 7px",color:e.warning_flags.includes("opp_elite_k")?T.green:T.amber,background:e.warning_flags.includes("opp_elite_k")?T.greenDim:T.amberDim,border:`1px solid ${e.warning_flags.includes("opp_elite_k")?T.green:T.amber}`}}>🎯 {e.warning_flags.includes("opp_elite_k")?"Elite K spot":"High-K opp"}</span>}
          <FormTrend trend={e.form_trend} delta={e.form_delta_pct} size={9}/>
        </div>
      )}
      {/* Subheader + same-game pill */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <span style={{fontSize:11.5,color:T.muted}}>{e.team}{e.opp_pitcher_name?` · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:""} · {marketLabel} O{marketLabel==="HR"?"0.5":e.line}</span>
        {sameGameOther && <span style={{fontSize:10,color:T.muted,background:"#1E2A3A",borderRadius:4,padding:"2px 6px"}}>🔗 Same game as #{sameGameOther}</span>}
      </div>
      {/* Score strip — Prob / Confidence / Edge / Line. Edge is shown for info
          (value vs the book); it does NOT feed the Confidence or probability. */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:10}}>
        <Stat label="Prob Over" value={pct(prob)} color={probColorMkt(prob, marketLabel)} bg={prob>=0.6?T.greenDim:T.card2}/>
        <Stat label="Rating /100" value={conf} color={confColor(conf)}/>
        <Stat label="Edge" value={edge!=null?`${edge>=0?"+":""}${edge.toFixed(1)}%`:"—"} color={edgeColor(edge)}/>
        {/* No book posted this play (the unpriced tail — books only post lines for the
            top hitters per game) → show OUR fair no-vig price instead of a dead "—", so
            the tail reads as "priced by LINEBRKR", not broken. Green + "Fair" label keeps
            it honest: it's our number, not a book's. */}
        <Stat label={hasLine?"Line":"Fair"} value={hasLine?fmtOdds(e.book_over):(prob>0?toAmerican(prob):"—")} color={hasLine?undefined:(prob>0?T.green:undefined)}/>
      </div>
      {/* Best price + book — "find the book to do it on." The best of 15 books, on the
          card so a user doesn't have to open the detail to line-shop. */}
      {hasLine && e.book_used && (
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontSize:11.5,fontWeight:700,marginBottom:10}}>
          <span style={{color:T.green}}>💰 Best {fmtOdds(e.book_over)}</span>
          <span style={{color:T.muted}}>@</span>
          <span style={{color:T.text}}>{bookName(e.book_used)}</span>
          <span style={{color:T.faint,fontWeight:600}}>· tap to shop all books</span>
        </div>
      )}
      {/* Loud profile — elite batted-ball metrics light up */}
      <LoudChips items={loud}/>
      {/* Volume-not-value flag: high hit % but a low overall Rating (weak spot) */}
      {isProbTrap(prob, conf, marketLabel) && (
        <div style={{fontSize:10.5,fontWeight:700,color:T.amber,background:`${T.amber}18`,border:`1px solid ${T.amber}55`,borderRadius:8,padding:"6px 9px",marginBottom:10,lineHeight:1.4}}>
          ⚠ High hit % but low Rating — <span style={{fontWeight:600,color:T.muted}}>volume, not a quality spot. The Rating discounts the matchup/park/data; trust it over the raw %.</span>
        </div>
      )}
      {/* Context row — wall / platoon / batting slot / park */}
      <div style={{fontSize:11,display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
        <WallChip adj={e.pitcher_adj}/>
        {pl && <span style={{color:pl.color,fontWeight:700}}>{pl.txt} {pl.sym}</span>}
        {e.lineup_slot ? <span style={{color:T.muted}}>· Bats {e.lineup_slot}{e.lineup_slot===1?"st":e.lineup_slot===2?"nd":e.lineup_slot===3?"rd":"th"}</span> : null}
        {e.park_factor ? <span style={{color:parkColor(e.park_factor)}}>· {e.park_factor.toFixed(2)}x park</span> : null}
      </div>
      {/* One-sentence verdict (tap to expand) */}
      <span onClick={ev=>{ev.stopPropagation(); setShowVerdict(s=>!s)}} style={{display:"block",fontSize:12,color:T.muted,fontStyle:"italic",marginTop:6,lineHeight:1.4,cursor:"pointer",...(showVerdict?{}:{whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"})}}>{verdict}</span>
      {/* Data quality strip — never bet an unconfirmed player */}
      <div style={{background:T.card3,borderRadius:6,padding:"6px 10px",marginTop:8,fontSize:11,display:"flex",gap:12,flexWrap:"wrap"}}>
        {isVoid
          ? <span style={{color:T.amber,fontWeight:800}}>🚫 Game postponed — no action (not graded)</span>
          : <><span style={{color: avail==="confirmed"?T.green : isOut?T.red : T.amber, fontWeight:isOut?800:400}}>{avail==="confirmed"?"✓ Lineup confirmed" : isOut?"❌ NOT in lineup — scratched" : "⚠ Lineup unconfirmed"}</span>
        <span style={{color:pitcherOk?T.green:T.amber}}>{pitcherOk?`✓ ${e.opp_pitcher_name} starting`:"⚠ Pitcher TBD"}</span></>}
      </div>
      {/* Strikeout analytics drawer — K market only */}
      {marketLabel==="K" && (
        <div onClick={ev=>ev.stopPropagation()} style={{cursor:"default"}}>
          <span role="button" onClick={ev=>{ev.stopPropagation(); setShowK(s=>!s)}}
            style={{display:"inline-flex",alignItems:"center",gap:5,marginTop:9,fontSize:11,fontWeight:800,letterSpacing:"0.04em",color:T.green,cursor:"pointer"}}>
            📊 Strikeout analytics {showK?"▾":"▸"}
          </span>
          {showK && <KDrawer pitcherName={e.full_name} oppTeam={e.opp_pitcher_name} e={e}/>}
        </div>
      )}
    </button>
  )
}

// ── Rankings hub ────────────────────────────────────────────────────────────
// Rankings is the primary board; Value is folded in as a secondary segment rather
// than its own bottom-nav tab (frees a nav slot and keeps the two "which play" views
// side by side). A top segmented control flips between them; both share the same
// player-open / parlay handlers. `initial` lets a deep-link land straight on Value.
function RankingsHub({initial = "rankings", onPlayer, parlay, toggleLeg, inParlay, onGlossary}){
  const [sub, setSub] = useState(initial === "value" ? "value" : "rankings")
  const shared = {onPlayer, parlay, toggleLeg, inParlay, onGlossary}
  const SEG = [["rankings", "🏆 Rankings"], ["value", "💲 Value"]]
  return (
    <div>
      <div style={{display:"flex", gap:6, padding:"10px 16px 0"}}>
        {SEG.map(([id,lbl])=>{ const on = sub===id; return (
          <button key={id} onClick={()=>setSub(id)} style={{flex:1, padding:"9px 12px", borderRadius:10,
            fontSize:13, fontWeight:800, cursor:"pointer", background:on?T.green:T.card,
            color:on?"#04120B":T.muted2, border:`1px solid ${on?T.green:T.border2}`}}>{lbl}</button>
        )})}
      </div>
      {sub === "rankings" ? <PicksTab {...shared}/> : <ValueTab {...shared}/>}
    </div>
  )
}

// ── Value Board ─────────────────────────────────────────────────────────────
// Ranks by EDGE — our calibrated model probability vs the book's DE-VIGGED implied
// probability (server-computed `edge_pct`; the vig is already removed). Shows ONLY
// positive-edge plays: spots the market has mispriced in our favor, not merely plays
// likely to hit. "Value over outcome." A play with no book price can't be edge-rated,
// so it's excluded. An empty board is an HONEST answer, not a bug — some slates carry
// no misprice worth backing, and we say so rather than manufacture a play.
function ValueTab({onPlayer, parlay, toggleLeg, inParlay, onGlossary}){
  const [confirmedOnly,setConfirmedOnly] = useState(true)
  const [market,setMarket] = useState("All")   // bet-type filter
  const [search,setSearch] = useState("")       // free-text filter
  const { data, loading } = useFetch(()=>
      Promise.all(MARKETS.filter(([lbl])=>lbl!=="K").map(([lbl,db])=>
        getLeaderboard(db, todayStr(), 80, confirmedOnly)
          .then(d=>({lbl, entries:Array.isArray(d?.entries)?d.entries:[]}))
          .catch(()=>({lbl, entries:[]}))))
    , [confirmedOnly])
  // Near-live in-game results — same 90s refresh as the Rankings board.
  const [liveTick,setLiveTick] = useState(0)
  useEffect(()=>{ const id=setInterval(()=>setLiveTick(t=>t+1), 90000); return ()=>clearInterval(id) }, [])
  const { data: liveData } = useFetch(()=>getLive(todayStr()).catch(()=>null), [liveTick])
  const liveMap = liveData?.lines || {}

  // Pool all tracked markets, enforce each market's canonical line, dedup, tag _mkt.
  let pool=[]
  if(Array.isArray(data)) for(const grp of data){
    const lbl=grp?.lbl; const entries=Array.isArray(grp?.entries)?grp.entries:[]
    const canon=CANON_LINE[lbl]
    for(const e of entries){ if(canon!=null && Math.abs((e.line??canon)-canon)>=0.01) continue; pool.push({...e,_mkt:lbl}) }
  }
  const seen=new Set()
  // Rank metric: EV per unit (accounts for the payout), fallback to raw edge fraction.
  const valueOf = e => (e.ev ?? e.edge_pct ?? null)
  // MEANINGFUL EDGE ONLY. Our fair prob must beat the book's de-vigged implied prob by
  // more than the model's OWN calibration error (~1–2%). A sub-2% "edge" (e.g. +0.3% on a
  // +700 longshot) is inside our margin — noise, not value — so we don't surface it. This
  // keeps thin longshot prices from masquerading as an edge on a board called "Value".
  const MIN_EDGE = 0.02
  const value = pool
    .filter(e=>{ const k=(e.player_id||e.full_name)+'-'+e._mkt; if(seen.has(k)) return false; seen.add(k); return true })
    .filter(e=> e.book_over!=null && e.edge_pct!=null && e.edge_pct>=MIN_EDGE)
    .sort((a,b)=> (valueOf(b)??-9)-(valueOf(a)??-9))
  // Filters applied on top of the positive-edge pool: bet type + free-text.
  const _deacc = s => (s||"").normalize("NFD").replace(/[̀-ͯ]/g,"").toLowerCase()
  const q=_deacc(search.trim())
  // Per-market counts (for the pills — how many value plays each bet type has).
  const mktCounts = value.reduce((a,e)=>{ a[e._mkt]=(a[e._mkt]||0)+1; return a },{})
  const shown = value
    .filter(e=> market==="All" || e._mkt===market)
    .filter(e=> !q || _deacc(e.full_name).includes(q) || _deacc(e.team).includes(q) || _deacc(e.opp_pitcher_name).includes(q))
  const avgEdge = shown.length ? shown.reduce((s,e)=>s+e.edge_pct*100,0)/shown.length : 0

  // Same player-drawer payload the Rankings board opens with.
  const open = (e, tier) => { const c=confOf(e); onPlayer({
    name:e.full_name, player_id:e.player_id, mlbam_id:e.mlbam_id, market:e._mkt, tier, conf:c, why:buildWhy(e,c),
    prob:e.prob_over, edge:(e.edge_pct!=null)?`${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}%`:undefined,
    odds:fmtOdds(e.book_over), oddsNum:e.book_over, oppHand:e.opp_pitcher_hand,
    opp:e.opp_pitcher_name?`${e.team} · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:`${e.team} · vs TBD`,
    slot:e?.inputs?.lineup_slot, park:e?.inputs?.park_factor, team:e.team, oppPitcher:e.opp_pitcher_name,
  }) }

  return (
    <div style={{padding:"0 0 12px"}}>
      {/* Header — what this board is and why it differs from Rankings */}
      <div style={{padding:"14px 14px 8px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <h2 style={{fontSize:20,fontWeight:900,color:T.text,margin:0}}>💰 Value Board</h2>
          {onGlossary && <span onClick={()=>onGlossary("edge")} style={{fontSize:11,color:T.blue,fontWeight:700,cursor:"pointer"}}>what's edge?</span>}
        </div>
        <p style={{fontSize:12.5,color:T.muted2,lineHeight:1.5,margin:"7px 0 0"}}>
          Ranked by <b style={{color:T.text}}>edge</b> — our fair probability vs the book's
          de-vigged price. These are plays the market has <b style={{color:T.green}}>priced in our favor</b>,
          not just plays likely to hit. <span style={{color:T.muted}}>Value over outcome.</span>
        </p>
        <p style={{fontSize:11,color:T.faint,lineHeight:1.45,margin:"6px 0 0"}}>
          Only edges of <b style={{color:T.muted2}}>+2% or more</b> show — a thinner gap sits inside our model's own
          ~1–2% margin, so we don't call it value (it's noise, not an edge).
        </p>
      </div>

      {/* Bet-type filter pills — each shows how many value plays it holds */}
      <div style={{display:"flex",gap:8,padding:"0 14px 10px",overflowX:"auto",scrollbarWidth:"none"}}>
        {["All",...MARKETS.map(m=>m[0]).filter(l=>l!=="K")].map(label=>{ const on=market===label; const n = label==="All"?value.length:(mktCounts[label]||0); return (
          <button key={label} onClick={()=>setMarket(label)} style={{flexShrink:0,padding:"6px 14px",borderRadius:20,background:on?T.greenDim:T.card,border:`1px solid ${on?T.green:T.border}`,color:on?T.green:T.muted,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{mDisp(label)}{n>0?` · ${n}`:""}</button>
        )})}
      </div>

      {/* Search + confirmed-only toggle + summary */}
      <div style={{padding:"0 14px 8px"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Filter by player, team, or pitcher…"
          style={{width:"100%",boxSizing:"border-box",background:T.bg,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"0 14px 10px",flexWrap:"wrap"}}>
        <button onClick={()=>setConfirmedOnly(v=>!v)} style={{padding:"6px 13px",borderRadius:20,background:confirmedOnly?T.greenDim:T.card,border:`1px solid ${confirmedOnly?T.green:T.border}`,color:confirmedOnly?T.green:T.muted,fontSize:11.5,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
          {confirmedOnly?"✓ Confirmed lineups only":"Incl. unconfirmed"}
        </button>
        {!loading && shown.length>0 && <span style={{fontSize:11.5,color:T.muted}}>{shown.length} value play{shown.length===1?"":"s"} · avg <b style={{color:T.green}}>+{avgEdge.toFixed(1)}%</b> edge</span>}
      </div>

      <div style={{padding:"0 14px"}}>
        {loading && [0,1,2].map(i=>(<div key={i} style={{height:128,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:8,opacity:0.45}}/>))}
        {!loading && shown.length===0 && (
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"28px 22px",textAlign:"center"}}>
            <div style={{fontSize:30,marginBottom:10}}>🧊</div>
            {value.length>0 ? (<>
              {/* Value exists, just not in this filter — point back to it, don't imply none. */}
              <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:7}}>No value in {MARKET_LABEL[market]||market}{q?` for “${search.trim()}”`:""} right now</div>
              <div style={{fontSize:12.5,color:T.muted,lineHeight:1.55}}>
                {value.length} value play{value.length===1?"":"s"} in other markets — <span onClick={()=>{setMarket("All");setSearch("")}} style={{color:T.green,cursor:"pointer",fontWeight:700}}>clear filters</span>.
              </div>
            </>) : (<>
              <div style={{fontSize:15,fontWeight:800,color:T.text,marginBottom:7}}>No value on the board right now</div>
              <div style={{fontSize:12.5,color:T.muted,lineHeight:1.55}}>
                That's the honest answer, not a bug. Every priced play is currently at or below
                the book's fair line, so there's nothing we'd back purely for value. Check back
                once more odds &amp; lineups post{confirmedOnly?", or switch off “confirmed only” to widen the net":""}.
              </div>
            </>)}
          </div>
        )}
        {!loading && shown.map((e,i)=>(
          <PickCard key={(e.player_id||e.full_name)+'-'+e._mkt} e={e} rank={i+1} marketLabel={e._mkt}
            onOpen={open} inParlay={inParlay} onToggleLeg={toggleLeg} liveMap={liveMap}/>
        ))}
        {!loading && shown.length>0 && (
          <div style={{fontSize:11,color:T.faint,lineHeight:1.5,marginTop:14,padding:"0 2px"}}>
            Edge = our calibrated model probability − the book's no-vig implied probability.
            Positive edge = a better price than the market's fair line. Edge is only as good as
            our probability, which grades within ~1–2% of actual over 10k+ picks. Research, not advice · 21+.
          </div>
        )}
      </div>
    </div>
  )
}

function PicksTab({onPlayer, parlay, toggleLeg, inParlay, onGlossary}){
  const [market,setMarket] = useState("All")
  const ALL = market==="All"
  const isGroup = ALL
  const dbProp = ALL ? null : (MARKETS.find(m=>m[0]===market)||MARKETS[0])[1]
  const [confirmedOnly,setConfirmedOnly] = useState(true)
  // K (strikeouts) is deliberately EXCLUDED from the "All" board. It's quarantined from
  // the tracked record (no proven edge — settles ~coin-flip), and because its raw over
  // probabilities (55-58%) outrank the tracked markets, leaving it in "All" made an
  // untracked market our #1 "play of the day." It stays reachable via its own K 🧪 pill
  // for research, but never sits in the headline ranking. So the top of "All" is always
  // the highest-probability TRACKED play (HRR / 1+ Hits) — a market we actually beat.
  const { data, loading } = useFetch(()=> ALL
      ? Promise.all(MARKETS.filter(([lbl])=>lbl!=="K").map(([lbl,db])=>getLeaderboard(db, todayStr(), 80, confirmedOnly).then(d=>({lbl, entries:Array.isArray(d?.entries)?d.entries:[]})).catch(()=>({lbl, entries:[]}))))
      : getLeaderboard(dbProp, todayStr(), 200, confirmedOnly)
    , [market, confirmedOnly])
  // LINEBRKR Homer Signal per player — the leaderboard rows are lean (no grid inputs),
  // so pull the HR grid once and map player_id -> signal to badge qualifying HR rows.
  const { data: hrSigMap } = useFetch(()=> getToday("HR_anytime", todayStr(), 500)
      .then(d=>Object.fromEntries((d?.picks||[]).filter(p=>p.homer_signal).map(p=>[p.player_id, p.homer_signal])))
      .catch(()=>({})), [])
  // Near-live in-game results — auto-refresh every 90s so picks flag as they hit.
  const [liveTick,setLiveTick] = useState(0)
  useEffect(()=>{ const id=setInterval(()=>setLiveTick(t=>t+1), 90000); return ()=>clearInterval(id) }, [])
  const { data: liveData } = useFetch(()=>getLive(todayStr()).catch(()=>null), [liveTick])
  const liveMap = liveData?.lines || {}
  const [sortBy,setSortBy] = useState("prob")    // default to probability-to-go-over — the board's canonical ranking (matches how the record grades). Rating/EV stay sort options. The ≤2-per-team diversify cap below keeps one soft matchup from flooding the top 10.
  const [showSort,setShowSort] = useState(false)
  const [muted,setMuted] = useState(()=>new Set())
  const [quickAction,setQuickAction] = useState(null)
  const [showLegend,setShowLegend] = useState(false)
  const [copied,setCopied] = useState(false)
  const [wallsHidden,setWallsHidden] = useState(false)
  const [search,setSearch] = useState("")
  const [tierFilter,setTierFilter] = useState(null)   // null = show all tiers
  const watch = useWatch()
  const [watchOnly,setWatchOnly] = useState(false)    // ★ filter to watchlisted
  const [signalOnly,setSignalOnly] = useState(false)  // ⚡ filter to Homer Signal players
  const [top15,setTop15] = useState(false)            // LINEBRKR Top 15 drawer
  const [topShare,setTopShare] = useState(null)       // "share top 10" image feedback

  // Build a unified pool, each entry tagged with its market (_mkt). Enforce each
  // market's canonical line (HR O0.5, HRR O1.5, …) so rows can't leak markets.
  // Live data ONLY — never show dummy/sample players. While loading → skeletons;
  // genuinely empty → empty-state message. (No SAMPLE_PICKS fallback.)
  let pool=[], anyLive=false
  if(isGroup){
    if(Array.isArray(data)) for(const grp of data){
      const lbl = grp?.lbl
      const entries = Array.isArray(grp?.entries) ? grp.entries : []
      if(entries.length) anyLive=true
      const canon=CANON_LINE[lbl]
      for(const e of entries){ if(canon!=null && Math.abs((e.line??canon)-canon)>=0.01) continue; pool.push({...e,_mkt:lbl}) }
    }
  } else {
    // NB: data may briefly be the previous "All" ARRAY during a market switch.
    // `array.entries` is Array.prototype.entries (a truthy function), so guard with
    // Array.isArray — otherwise `for..of` on that function throws and blanks the tab.
    const le = Array.isArray(data?.entries) ? data.entries : []
    anyLive = le.length>0
    const canon=CANON_LINE[market]
    for(const e of le){ if(canon!=null && Math.abs((e.line??canon)-canon)>=0.01) continue; pool.push({...e,_mkt:market}) }
  }
  const isLive=anyLive
  const seen=new Set()
  // Accent-insensitive so "suarez" finds "Suárez", "pena" finds "Peña", etc.
  const _deacc = s => (s||"").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase()
  const q=_deacc(search.trim())
  const filtered = pool
    .filter(e=>{ const k=(e.player_id||e.full_name)+'-'+e._mkt; if(seen.has(k)) return false; seen.add(k); return true })
    .filter(e=>!muted.has(e.player_id||e.full_name))
    .filter(e=>!q || _deacc(e.full_name).includes(q) || _deacc(e.team).includes(q) || _deacc(e.opp_pitcher_name).includes(q))

  const edgePts = e => e.edge_pct!=null ? e.edge_pct*100 : null
  const counts = filtered.reduce((a,e)=>{ const t=tierFor(e.prob_over, confOf(e), e._mkt, edgePts(e), e.book_over!=null); a[t]=(a[t]||0)+1; return a },{})

  const entries = [...filtered].sort((a,b)=>{
    if(sortBy==="prob")  return (b.prob_over||0)-(a.prob_over||0)
    if(sortBy==="conf")  return confOf(b)-confOf(a)
    if(sortBy==="value") return (b.ev??b.edge_pct??-9)-(a.ev??a.edge_pct??-9)
    const ta=tierFor(a.prob_over,confOf(a),a._mkt,edgePts(a),a.book_over!=null)
    const tb=tierFor(b.prob_over,confOf(b),b._mkt,edgePts(b),b.book_over!=null)
    return (TIER_RANK[ta]-TIER_RANK[tb]) || ((b.prob_over||0)-(a.prob_over||0))
  })

  // Diversity cap: the TOP 10 can hold at most 2 players per team, so one soft-pitcher
  // matchup can't flood the headline (e.g. 4 Phillies vs a hittable arm). Below the top
  // 10 the natural Rating order resumes — so 11–25 can carry 3–4 from a team. 3rd+
  // same-team plays bumped from the top 10 are reordered downward, never removed.
  const _diversify = (list) => {
    const head=[], tail=[], ct={}
    for(const e of list){
      const t = e.team || "?"
      ct[t] = (ct[t]||0)
      if(head.length < 10 && ct[t] < 2){ head.push(e); ct[t]++ } else tail.push(e)
    }
    return [...head, ...tail]
  }
  const entriesCapped = _diversify(entries)

  // Tap a tier chip (ELITE / BEST BET / …) to show only those picks.
  const tierOf = e => tierFor(e.prob_over, confOf(e), e._mkt, edgePts(e), e.book_over!=null)
  const sigFires = e => e._mkt==="HR" && hrSigMap && hrSigMap[e.player_id]?.fires
  const sigPlayers = entriesCapped.filter(sigFires)
  const sigCount = sigPlayers.length
  const _sigLeg = e => ({key:`${e.player_id}-HR`, name:e.full_name, player_id:e.player_id, market:"HR", prob:e.prob_over, oddsNum:e.book_over, team:e.team, opp:e.opp_pitcher_name})
  // Projected parlay: the highest-prob signal players, ONE per game (diversify so
  // correlated same-game legs don't inflate the combined %), capped at 3 — HR parlays
  // are longshots, so more legs mostly kills the hit chance.
  const sugLegs = (()=>{ const out=[],seen=new Set(); for(const e of [...sigPlayers].sort((a,b)=>(b.prob_over||0)-(a.prob_over||0))){ const g=(e.opp_pitcher_name||e.team||"").toLowerCase(); if(g&&seen.has(g))continue; if(g)seen.add(g); out.push(e); if(out.length>=3)break } return out })()
  const sugProb = sugLegs.reduce((a,e)=>a*(e.prob_over||0),1)
  const addLegs = arr => arr.forEach(e=>{ const lg=_sigLeg(e); if(!(inParlay&&inParlay(lg.key))) toggleLeg&&toggleLeg(lg) })
  let shown = tierFilter ? entriesCapped.filter(e=>tierOf(e)===tierFilter) : entriesCapped
  if(watchOnly) shown = shown.filter(e=>watch.has(e.full_name))
  if(signalOnly) shown = shown.filter(sigFires)

  // Board-level live tally — closes the loop at a glance: how many of the picks on screen
  // have already HIT / MISSED / are live today. Tracked batter markets only (liveStatus is
  // K is intentionally kept out of this headline tally — we DON'T pass its pick line here,
  // so liveStatus returns null for K. The scoreboard reflects only the tracked markets we
  // stand behind, even though K cards themselves now grade live.
  const liveTally = (()=>{ let hit=0,miss=0,live=0
    for(const e of shown){ const lv=liveStatus(liveMap[e.player_id], e._mkt); if(!lv) continue
      if(lv.state==="hit") hit++; else if(lv.state==="miss") miss++; else if(lv.state==="live") live++ }
    return {hit, miss, live, graded:hit+miss} })()

  const pitcherRanks = {}
  shown.forEach((e,i)=>{ const k=e.opp_pitcher_name; if(k){(pitcherRanks[k]=pitcherRanks[k]||[]).push(i+1)} })

  // Hard-wall pitchers on the board (fade everyone facing them)
  const hardWalls=[]; { const s=new Set(); for(const e of entries){ const w=wallFor(e.pitcher_adj); if(w.tier==="HARD WALL" && e.opp_pitcher_name && !s.has(e.opp_pitcher_name)){ s.add(e.opp_pitcher_name); hardWalls.push({pitcher:e.opp_pitcher_name, mult:w.mult, team:e.team}) } } }

  const doExport = async () => {
    const text = `LINEBRKR — ${ALL?"All Markets":MARKET_LABEL[market]}${tierFilter?` · ${tierFilter}`:""} Picks · ${fmtDate()}\n` + shown.map((e,i)=>{
      const prob = Math.round((e.prob_over||0)*100)
      const edge = e.edge_pct!=null ? ` · ${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}% edge` : ""
      const line = e.book_over!=null ? ` · ${fmtOdds(e.book_over)}` : ""
      const opp  = e.opp_pitcher_name ? ` vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}` : ""
      return `#${i+1} ${e.full_name} (${e.team})${opp} · ${mDisp(e._mkt)} O${e.line} · ${prob}% over${edge}${line}`
    }).join("\n") + EXPORT_DISCLAIMER
    try { await navigator.clipboard.writeText(text) }
    catch { const ta=document.createElement("textarea"); ta.value=text; document.body.appendChild(ta); ta.select(); try{document.execCommand("copy")}catch(_){} ta.remove() }
    setCopied(true); setTimeout(()=>setCopied(false),1800)
  }

  // Share the free top 10 as a branded image — the daily-post growth engine.
  const doShareTop = async () => {
    const rows = shown.slice(0,10).map((e,i)=>{ const c=confOf(e); return {
      rank:i+1, name:e.full_name,
      sub: e.opp_pitcher_name ? `${e.team} · vs ${e.opp_pitcher_name}` : e.team,
      market: e._mkt,
      probStr: pct(e.prob_over), probColor: probColorMkt(e.prob_over, e._mkt),
      rating: c, ratingColor: ratingColor(c),
    }})
    const r = await shareBoard({ title:"Today's Top 10", subtitle:`${ALL?"All Markets":MARKET_LABEL[market]} · ${fmtDate()}`, rows })
    setTopShare(r); setTimeout(()=>setTopShare(null),2200)
  }

  // Daily market cards (HRR / Homers / Hits) — pull that market's board fresh, take
  // the top 6 by probability, and hand a branded card to the share sheet. One tap per
  // card so the founder can post each to socials without leaving the app.
  const [cardBusy,setCardBusy] = useState("")
  const DAILY_CARDS = [
    {slug:"hrr",    db:"HRR_over_1.5", title:"HRR (2+)",  sub:"Hits + runs + RBI floor. Model-ranked."},
    {slug:"homers", db:"HR_anytime",   title:"HOMERS",    sub:"Anytime home run. Model-ranked."},
    {slug:"hits",   db:"hits",         title:"HITS (1+)", sub:"Reaches with a knock. Model-ranked."},
  ]
  const doMarketCard = async (cfg) => {
    if(cardBusy) return
    setCardBusy(cfg.slug)
    try {
      const d = await getLeaderboard(cfg.db, todayStr(), 40, false).catch(()=>null)
      const entries = Array.isArray(d?.entries) ? d.entries : []
      const rows = entries.slice(0,6).map(e=>({
        name:e.full_name, team:e.team,
        vs: e.opp_pitcher_name ? e.opp_pitcher_name.split(" ").slice(-1)[0] : "",
        pct: Math.round((e.prob_over||0)*100),
      }))
      if(!rows.length){ setCardBusy(""); return }
      await shareMarketCard({ slug:cfg.slug, title:cfg.title, sub:cfg.sub, dateStr:fmtDate(), rows })
    } catch(_){}
    setCardBusy("")
  }

  const open = (e, tier) => { const c=confOf(e); onPlayer({
    name:e.full_name, player_id:e.player_id, mlbam_id:e.mlbam_id, market:e._mkt, tier, conf:c, why:buildWhy(e,c),
    prob:e.prob_over, edge:(e.edge_pct!=null)?`${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}%`:undefined,
    odds:fmtOdds(e.book_over), oddsNum:e.book_over, oppHand:e.opp_pitcher_hand,
    opp:e.opp_pitcher_name?`${e.team} · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:`${e.team} · vs TBD`,
    slot:e?.inputs?.lineup_slot, park:e?.inputs?.park_factor, team:e.team, oppPitcher:e.opp_pitcher_name,
  }) }

  return (
    <div style={{padding:"0 0 12px"}}>
      {/* Market pills */}
      <div style={{display:"flex",gap:8,padding:"10px 14px",overflowX:"auto",scrollbarWidth:"none"}}>
        {["All",...MARKETS.map(m=>m[0])].map(label=>{ const on=market===label; const testing=label==="K"; return (
          <button key={label} onClick={()=>setMarket(label)} title={testing?"Strikeouts — in testing":undefined} style={{flexShrink:0,padding:"6px 16px",borderRadius:20,background:on?T.greenDim:T.card,border:`1px solid ${testing?T.amber:on?T.green:T.border}`,color:on?T.green:T.muted,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{mDisp(label)}{testing?" 🧪":""}</button>
        )})}
      </div>
      {market==="K" && <div style={{padding:"0 14px"}}><TestingNote what="The strikeouts (K) market"/></div>}
      <div style={{padding:"0 16px 8px",fontSize:11.5,color:T.muted,lineHeight:1.5}}>Ranked by <b style={{color:T.text}}>how likely each play is to hit</b> — the highest-floor plays. <span style={{color:T.muted2}}>(💰 Value ranks the same board by price edge instead.)</span></div>
      {/* LINEBRKR Top 15 — the one flagship ranked list (Rating-ranked) */}
      <div style={{padding:"0 14px 8px"}}>
        <button onClick={()=>setTop15(true)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"11px",color:T.green,fontSize:12.5,fontWeight:800,cursor:"pointer"}}>
          🏆 Top 15 — best-rated plays
        </button>
      </div>
      {/* Search — player, team, or opposing pitcher */}
      <div style={{padding:"0 14px 8px",position:"relative"}}>
        <input
          value={search}
          onChange={e=>setSearch(e.target.value)}
          placeholder="Search player, team, or pitcher…"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${search?T.green:T.border2}`,borderRadius:10,padding:"9px 32px 9px 12px",color:T.text,fontSize:13,outline:"none"}}
        />
        {search && (
          <button onClick={()=>setSearch("")} style={{position:"absolute",right:22,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted2,fontSize:16,cursor:"pointer",lineHeight:1,padding:4}}>×</button>
        )}
      </div>
      {/* Controls: confirmed-only gate + sort */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,padding:"0 14px 8px"}}>
        <button onClick={()=>setConfirmedOnly(v=>!v)} style={{background:confirmedOnly?T.greenDim:T.card,border:`1px solid ${confirmedOnly?T.green:T.border2}`,borderRadius:8,padding:"5px 11px",color:confirmedOnly?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>{confirmedOnly?"✓ Confirmed only":"Incl. projected"}</button>
        <button onClick={()=>setWatchOnly(v=>!v)} title="Show only your watchlist" style={{background:watchOnly?`${T.amber}22`:T.card,border:`1px solid ${watchOnly?T.amber:T.border2}`,borderRadius:8,padding:"5px 11px",color:watchOnly?T.amber:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>{watchOnly?"★ Watchlist":`☆ Watchlist${watch.count?` (${watch.count})`:""}`}</button>
        {sigCount>0 && <button onClick={()=>setSignalOnly(v=>!v)} title="Show only players where the key homer catalysts align (park, weather, barrel, HR-worthy fly-ball, homer-prone arm, platoon)" style={{background:signalOnly?`${T.amber}22`:T.card,border:`1px solid ${signalOnly?T.amber:T.border2}`,borderRadius:8,padding:"5px 11px",color:signalOnly?T.amber:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap"}}>⚡ HR Signal ({sigCount})</button>}
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setShowSort(true)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 11px",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>⇅ Sort: {SORT_LABEL[sortBy]}</button>
          <HelpChip onGlossary={onGlossary} section="tiers"/>
        </div>
      </div>
      {/* Slate summary — tap a tier to filter the board to just those picks */}
      <div style={{background:T.card3,borderTop:`1px solid ${T.border}`,borderBottom:`1px solid ${T.border}`,padding:"8px 14px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
        <div style={{minWidth:0}}>
          <div style={{fontSize:11,color:T.muted}}>{fmtDate()} · {shown.length}{tierFilter?` ${tierLabel(tierFilter)}`:""} pick{shown.length===1?"":"s"}{!isLive&&!loading?" · sample":""}{tierFilter?<> · <span onClick={()=>setTierFilter(null)} style={{color:T.green,cursor:"pointer",fontWeight:700}}>clear ✕</span></>:""}</div>
          <div style={{fontSize:11,fontWeight:700,marginTop:4,display:"flex",gap:7,flexWrap:"wrap"}}>
            {[["ELITE",T.green],["BEST BET",T.green],["STRONG",T.amber],["PLAY",T.blue]].map(([t,col])=>{
              const n=counts[t]||0, on=tierFilter===t
              return (
                <button key={t} onClick={()=>setTierFilter(on?null:(n?t:null))} disabled={!n&&!on}
                  style={{padding:"3px 8px",borderRadius:7,fontSize:11,fontWeight:700,cursor:n?"pointer":"default",
                    border:`1px solid ${on?col:T.border2}`,background:on?`${col}26`:"transparent",
                    color:n?col:T.muted,opacity:n?1:0.5}}>
                  {n} {tierLabel(t)}
                </button>
              )
            })}
          </div>
        </div>
        <button onClick={doShareTop} disabled={!shown.length} title="Share the free top 10 as an image" style={{flexShrink:0,padding:"7px 12px",borderRadius:8,background:topShare?T.greenDim:T.card,border:`1px solid ${topShare?T.green:T.border2}`,color:topShare?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:shown.length?"pointer":"default",whiteSpace:"nowrap"}}>{topShare==="downloaded"?"Saved ✓":topShare?"Shared ✓":"📤 Share top 10"}</button>
        <button onClick={doExport} disabled={!shown.length} style={{flexShrink:0,padding:"7px 12px",borderRadius:8,background:copied?T.greenDim:T.card,border:`1px solid ${copied?T.green:T.border2}`,color:copied?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:shown.length?"pointer":"default",whiteSpace:"nowrap"}}>{copied?"Copied ✓":"⤓ Export"}</button>
      </div>
      {/* Daily post cards — one tap builds a branded HRR / Homers / Hits board image */}
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 14px 10px",flexWrap:"wrap"}}>
        <span style={{fontSize:11,fontWeight:800,color:T.muted,letterSpacing:"0.04em"}}>🖼️ POST CARDS</span>
        {DAILY_CARDS.map(cfg=>(
          <button key={cfg.slug} onClick={()=>doMarketCard(cfg)} disabled={!!cardBusy}
            style={{flexShrink:0,padding:"7px 13px",borderRadius:18,background:cardBusy===cfg.slug?T.greenDim:T.card,
              border:`1px solid ${cardBusy===cfg.slug?T.green:T.border2}`,color:cardBusy===cfg.slug?T.green:T.muted2,
              fontSize:12,fontWeight:700,cursor:cardBusy?"default":"pointer",whiteSpace:"nowrap"}}>
            {cardBusy===cfg.slug?"…":cfg.title.replace(/ \(.*\)/,"")}
          </button>
        ))}
      </div>
      {/* Legend — tiers + market acronyms spelled out */}
      <div style={{padding:"0 14px",marginBottom:12}}>
        <button onClick={()=>setShowLegend(s=>!s)} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",padding:0,letterSpacing:"0.04em"}}>
          <span style={{fontSize:9}}>{showLegend?"▾":"▸"}</span> What do these mean?
        </button>
        {showLegend && (
          <div style={{marginTop:8,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"12px 14px"}}>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>TIERS</div>
            {["ELITE","BEST BET","STRONG","PLAY","WATCH","PRIME SHOT","VALUE SHOT","PASS"].map(t=>(
              <div key={t} style={{display:"flex",alignItems:"center",gap:10,marginBottom:7}}>
                <span style={{flexShrink:0}}><TierBadge tier={t}/></span>
                <span style={{fontSize:12,color:T.muted2,lineHeight:1.3}}>{TIER[t].desc}</span>
              </div>
            ))}
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",margin:"13px 0 8px"}}>MARKETS</div>
            {[["HRR","Hits + Runs + RBIs — Over 1.5"],["HR","Home Run — anytime (Over 0.5)"],["1+ H","Hits — to record a hit (Over 0.5)"],["2+ H","Hits — two or more (Over 1.5)"],["Bases","Total Bases — Over 1.5"],["K","Strikeouts"]].map(([a,d])=>(
              <div key={a} style={{display:"flex",gap:12,marginBottom:7}}>
                <span style={{fontSize:12,fontWeight:800,color:T.text,fontFamily:T.mono,minWidth:30,flexShrink:0}}>{a}</span>
                <span style={{fontSize:12,color:T.muted2,lineHeight:1.3}}>{d}</span>
              </div>
            ))}
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",margin:"13px 0 8px"}}>SCORES</div>
            {[["LINEBRKR Rating","How good the spot is to hit (0–100): model conviction + matchup + zone fit + form + park + data quality, in one number. 85+ Elite · 70s Strong · 55–69 Solid · under 55 skip"],["Wall","How hittable the pitcher is (HARD WALL → HITTABLE) from the model's matchup multiplier"]].map(([a,d])=>(
              <div key={a} style={{display:"flex",gap:12,marginBottom:7}}>
                <span style={{fontSize:12,fontWeight:800,color:T.text,minWidth:74,flexShrink:0}}>{a}</span>
                <span style={{fontSize:12,color:T.muted2,lineHeight:1.3}}>{d}</span>
              </div>
            ))}
            <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:10,paddingTop:10,borderTop:`1px solid ${T.border}`}}>
              Picks are ranked by <b style={{color:T.text}}>probability to go over</b> (big % on each card). <b style={{color:T.text}}>Edge</b> = that probability minus the book's implied odds. Low-probability HR/2B longshots show as <b style={{color:"#F97316"}}>LONGSHOT</b>, never ELITE.
            </div>
          </div>
        )}
      </div>
      {/* Hard wall warnings — fade every hitter facing these pitchers */}
      {!loading && !wallsHidden && hardWalls.length>0 && (
        <div style={{padding:"0 14px",marginBottom:12,display:"flex",flexDirection:"column",gap:8}}>
          {hardWalls.slice(0,4).map((w,i)=>(
            <div key={i} style={{background:"#FFF1F2",borderLeft:`4px solid #9F1239`,borderRadius:8,padding:"9px 12px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:800,color:"#9F1239"}}>🚫 HARD WALL — {w.pitcher}</div>
                <div style={{fontSize:11,color:"#9F1239",opacity:0.85}}>Fade hitters facing him{w.mult?` · ${w.mult.toFixed(2)}x matchup`:""}</div>
              </div>
              {i===0 && <button onClick={()=>setWallsHidden(true)} style={{flexShrink:0,background:"none",border:"none",color:"#9F1239",fontSize:11,fontWeight:700,cursor:"pointer"}}>hide</button>}
            </div>
          ))}
        </div>
      )}
      {/* Board-level live scoreboard — the board closing its own loop: today's picks as
          they hit/miss/settle, so results are visible here without leaving for the Record
          tab. Only shows once games start (tracked markets; K is not graded). */}
      {!loading && (liveTally.graded + liveTally.live) > 0 && (
        <div style={{margin:"0 14px 10px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 13px",fontSize:12.5,fontWeight:800}}>
          <span style={{color:T.muted2,letterSpacing:"0.08em",fontSize:9.5,fontWeight:800}}>TODAY'S BOARD</span>
          <span style={{color:T.green}}>✓ {liveTally.hit} hit</span>
          <span style={{color:"#ef6a6a"}}>✗ {liveTally.miss} miss</span>
          {liveTally.live>0 && <span style={{color:T.amber}}>🔴 {liveTally.live} live</span>}
          {liveTally.graded>0 && <span style={{color:T.muted,marginLeft:"auto",fontWeight:700}}>{Math.round(liveTally.hit/liveTally.graded*100)}% ({liveTally.hit}-{liveTally.miss}) so far</span>}
        </div>
      )}
      <div style={{padding:"0 14px"}}>
        {/* Signal Parlay — appears when filtered to Homer Signals. Craft-your-own
            (add all → trim in the slip) OR a projected, game-diversified suggestion. */}
        {signalOnly && sigPlayers.length>=2 && toggleLeg && (
          <div style={{background:`linear-gradient(180deg, ${T.amber}14, ${T.card})`,border:`1px solid ${T.amber}`,borderRadius:12,padding:"12px 13px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
              <span style={{fontSize:11,fontWeight:900,letterSpacing:"0.05em",color:T.amber}}>⚡ SIGNAL PARLAY</span>
              <span style={{fontSize:10,color:T.muted}}>{sigPlayers.length} signal player{sigPlayers.length===1?"":"s"} today</span>
            </div>
            <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"10px 11px",marginBottom:9}}>
              <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.08em",color:T.muted,textTransform:"uppercase",marginBottom:4}}>Projected · {sugLegs.length} legs · 1 per game</div>
              <div style={{fontSize:13,fontWeight:800,color:T.text,lineHeight:1.4}}>{sugLegs.map(e=>e.full_name.split(" ").slice(-1)[0]).join("  +  ")}</div>
              <div style={{fontSize:11.5,color:T.muted2,marginTop:4,fontFamily:T.mono}}>{pct(sugProb)} combined · <b style={{color:T.green}}>{toAmerican(sugProb)}</b> fair</div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>addLegs(sugLegs)} style={{flex:1,padding:"9px",borderRadius:9,background:T.amber,border:"none",color:"#1a1206",fontSize:12,fontWeight:800,cursor:"pointer"}}>Build projected ({sugLegs.length})</button>
              <button onClick={()=>addLegs(sigPlayers)} style={{flex:1,padding:"9px",borderRadius:9,background:T.card,border:`1px solid ${T.border2}`,color:T.text,fontSize:12,fontWeight:800,cursor:"pointer"}}>Add all {sigPlayers.length} to slip</button>
            </div>
            <div style={{fontSize:9.5,color:T.faint,marginTop:8,lineHeight:1.4}}>Fair odds are model-only. HR parlays are longshots — legs multiply, so each miss compounds. Trim, price, and check same-game correlation in your slip below. 21+.</div>
          </div>
        )}
        {/* While a market is loading, show skeletons only — never the previous
            market's rows (that caused HRR/HR to appear mixed on switch). */}
        {loading && [0,1,2,3].map(i=>(<div key={i} style={{height:128,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:8,opacity:0.45}}/>))}
        {!loading && !shown.length && (
          tierFilter
            ? <div style={{textAlign:"center",color:T.muted,padding:"50px 16px",fontSize:13,lineHeight:1.6}}>No {tierFilter} picks{q?` matching “${search.trim()}”`:""} right now.<br/><span onClick={()=>setTierFilter(null)} style={{color:T.green,cursor:"pointer",fontWeight:700}}>Show all tiers</span></div>
            : q
            ? <div style={{textAlign:"center",color:T.muted,padding:"50px 16px",fontSize:13,lineHeight:1.6}}>No picks match “{search.trim()}”.<br/>Try a player, team, or pitcher name{market!=="All"?` — or switch off the ${market} filter`:""}.</div>
            : <div style={{textAlign:"center",color:T.muted,padding:"50px 16px",fontSize:13,lineHeight:1.6}}>No {market==="All"?"":market+" "}picks confirmed yet for today.<br/>Lineups typically post by 3PM ET.</div>
        )}
        {!loading && shown.map((e,i)=>{ const es = (e._mkt==="HR" && hrSigMap && hrSigMap[e.player_id]) ? {...e, homer_signal:hrSigMap[e.player_id]} : e; return <PickCard key={(e.player_id||e.full_name)+'-'+e._mkt+'-'+i} e={es} rank={i+1} marketLabel={e._mkt} onOpen={open} sameGameRanks={pitcherRanks[e.opp_pitcher_name]} inParlay={inParlay} onToggleLeg={toggleLeg} onLongPress={setQuickAction} liveMap={liveMap}/> })}
      </div>

      {quickAction && (
        <Sheet onClose={()=>setQuickAction(null)}>
          <div style={{padding:"8px 16px 32px"}}>
            <div style={{fontSize:16,fontWeight:800,margin:"4px 0 14px"}}>{quickAction.name}</div>
            {[
              [inParlay&&inParlay(quickAction.key)?"Remove from Parlay":"Add to Parlay", ()=>{toggleLeg&&toggleLeg(quickAction.leg); setQuickAction(null)}, T.green],
              ["View Player", ()=>{quickAction.view&&quickAction.view(); setQuickAction(null)}, T.text],
              ["Skip / hide this pick", ()=>{setMuted(m=>new Set(m).add(quickAction.pkey)); setQuickAction(null)}, T.muted2],
            ].map(([label,fn,col])=>(
              <button key={label} onClick={fn} style={{width:"100%",textAlign:"left",padding:"13px 2px",background:"none",border:"none",borderTop:`1px solid ${T.border}`,color:col,fontSize:14,fontWeight:700,cursor:"pointer"}}>{label}</button>
            ))}
          </div>
        </Sheet>
      )}

      {showSort && (
        <Sheet onClose={()=>setShowSort(false)}>
          <div style={{padding:"6px 16px 32px"}}>
            <div style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:"0.12em",margin:"6px 0 4px"}}>SORT BY</div>
            {SORT_OPTIONS.map(([id,label])=>(
              <button key={id} onClick={()=>{setSortBy(id); setShowSort(false)}} style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:"none",border:"none",borderTop:`1px solid ${T.border}`,padding:"13px 2px",cursor:"pointer",textAlign:"left"}}>
                <span style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${sortBy===id?T.green:T.border2}`,background:sortBy===id?T.green:"transparent",flexShrink:0}}/>
                <span style={{fontSize:14,color:sortBy===id?T.text:T.muted2,fontWeight:sortBy===id?700:500}}>{label}</span>
              </button>
            ))}
            <div style={{fontSize:11,color:T.muted,marginTop:14,lineHeight:1.45}}>Best Overall floats the top tiers up; Best Contact unlocks once the scoring engine is wired.</div>
          </div>
        </Sheet>
      )}
      {top15 && <Top15Drawer onClose={()=>setTop15(false)} onPlayer={onPlayer}/>}
    </div>
  )
}

// ── Games tab ───────────────────────────────────────────────────────────────
function GameCard({game,onOpen,highlight}){
  return (
    <button onClick={()=>onOpen(game)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px",marginBottom:8}}>
      {game.time && <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8}}>🕒 {game.time}</div>}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:20,flex:1}}>
          <div><div style={{fontSize:11,color:T.muted}}>{game.away.city}</div><div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.02em"}}>{game.away.abbr}</div></div>
          <span style={{fontSize:12,color:T.muted}}>@</span>
          <div><div style={{fontSize:11,color:T.muted}}>{game.home.city}</div><div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.02em"}}>{game.home.abbr}</div></div>
        </div>
        <GradeRing grade={game.grade}/>
      </div>
      {/* SMASH SPOT — weak arm in a launching pad (both signals aligned) */}
      {game.attackSpot && (
        <div title={game.attackReason||"Vulnerable starter in very hitter-friendly conditions"} style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:10,background:"linear-gradient(90deg, #3BE68622, #16A34A11)",border:`1px solid ${T.green}`,borderRadius:8,padding:"5px 10px"}}>
          <span style={{fontSize:11,fontWeight:900,letterSpacing:"0.06em",color:T.green}}>🎯 SMASH SPOT</span>
          {game.attackReason && <span style={{fontSize:10.5,color:T.muted2,fontWeight:600}}>{game.attackReason}</span>}
        </div>
      )}
      {/* Rain / PPD watch — from the weather feed's precip probability + conditions */}
      {game.ppdRisk && (
        <div title={`${game.ppdRisk.precip_pct!=null?`${game.ppdRisk.precip_pct}% chance of precipitation`:"Rain in the forecast"} — check the game's status before first pitch; it could be delayed or postponed.`}
          style={{display:"flex",width:"fit-content",alignItems:"center",gap:6,marginTop:10,
            background:game.ppdRisk.level==="high"?"#EF6A6A1A":T.amberDim,
            border:`1px solid ${game.ppdRisk.level==="high"?T.red:T.amber}`,borderRadius:8,padding:"5px 10px"}}>
          <span style={{fontSize:11,fontWeight:900,letterSpacing:"0.04em",color:game.ppdRisk.level==="high"?T.red:T.amber}}>🌧 {game.ppdRisk.label}</span>
          {game.ppdRisk.precip_pct!=null && <span style={{fontSize:10.5,color:T.muted2,fontWeight:600}}>{game.ppdRisk.precip_pct}% precip</span>}
        </div>
      )}
      {/* Intelligence row — total action / environment / qualified */}
      <div style={{display:"flex",gap:8,marginTop:12}}>
        <span style={{flex:1,fontSize:11,fontWeight:700,color:gradeColor(game.grade),background:T.greenDim,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",textAlign:"center"}}>Action {gradeLetter(game.grade)}</span>
        <span style={{flex:1,fontSize:11,fontWeight:700,color:scoreColor(game.env??0),background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",textAlign:"center"}}>Env {game.env??"—"}</span>
        <span style={{flex:1,fontSize:11,fontWeight:700,color:T.muted2,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",textAlign:"center"}}>{game.qualified??game.plays??0} plays</span>
      </div>
      {/* Sub-grades by betting angle */}
      {game.sub && (
        <div style={{display:"flex",gap:8,marginTop:8}}>
          {[["PWR","power"],["HITS","hits"],["K","strikeouts"]].map(([lbl,k])=>{ const v=game.sub[k]; const on=highlight===k; return (
            <span key={k} style={{flex:1,fontSize:10,fontWeight:700,color:T.muted,background:on?T.greenDim:T.card2,border:`1px solid ${on?T.green:T.border2}`,borderRadius:7,padding:"4px 6px",textAlign:"center"}}>{lbl} <b style={{color:gradeColor(v??0)}}>{v!=null?gradeLetter(v):"—"}</b></span>
          )})}
        </div>
      )}
      {/* Top market + top play */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,gap:8}}>
        <span style={{fontSize:11,color:T.muted}}>Top market <b style={{color:T.text}}>{game.bestMarket||"—"}</b></span>
        <span style={{fontSize:11,color:T.muted2}}>{game.windOk?"🌬 Wind out":game.wind}</span>
      </div>
      {game.topPlay && (
        <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${T.border}`,fontSize:12,color:T.muted2}}>
          Top ranked: <b style={{color:T.text}}>{game.topPlay.name}</b> <span style={{color:T.muted}}>{game.topPlay.market}</span> <span style={{color:probColor(game.topPlay.prob||0),fontWeight:800,fontFamily:T.mono}}>{pct(game.topPlay.prob||0)}</span>
        </div>
      )}
    </button>
  )
}
// 💣 Slate home-run projection — expected homers tonight (sum of every batter's HR
// probability). `compact` renders a single Home-screen stat line; otherwise a tappable
// banner that expands to the per-game breakdown.
function SlateHrBanner({compact=false, onGame}){
  const { data } = useFetch(()=>getSlateHrProjection(todayStr()).catch(()=>null), [])
  const { data: acc } = useFetch(()=>getSlateHrBacktest(30).catch(()=>null), [])
  const { data: pick } = useFetch(()=>getHrPickRecord(45,10).catch(()=>null), [])
  const [open,setOpen] = useState(false)
  if(!data || !data.n_games) return null
  const perGame = data.total_hr / data.n_games
  if(compact){
    return (
      <div style={{display:"flex",alignItems:"center",gap:11,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 14px"}}>
        <span style={{fontSize:20}}>💣</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:13.5,fontWeight:800}}>~{Math.round(data.total_hr)} homers projected tonight</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>across {data.n_games} games · ~{perGame.toFixed(1)}/game · model expectation</div>
        </div>
      </div>
    )
  }
  return (
    <div style={{background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.greenMid}`,borderRadius:12,marginBottom:14,overflow:"hidden"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",gap:11,background:"transparent",border:"none",padding:"12px 14px",color:T.text,cursor:"pointer",textAlign:"left"}}>
        <span style={{fontSize:19}}>💣</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:14,fontWeight:800}}>~{Math.round(data.total_hr)} homers projected · <span style={{color:T.green}}>~{perGame.toFixed(1)}/game</span></div>
          <div style={{fontSize:10.5,color:T.muted,marginTop:2}}>Model expectation across {data.n_games} games — sum of every batter's HR probability</div>
        </div>
        <span style={{fontSize:12,color:T.muted2}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div style={{borderTop:`1px solid ${T.border}`,padding:"6px 6px 8px"}}>
          {data.games.map((g,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px"}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:700,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{(g.teams||[]).join(" · ")||g.venue}</div>
                <div style={{fontSize:10,color:T.muted,marginTop:1}}>{g.venue}{g.top_bat?` · led by ${g.top_bat}`:""}</div>
              </div>
              <div style={{fontSize:15,fontWeight:800,fontFamily:T.mono,color:g.exp_hr>=2.3?T.green:g.exp_hr<=1.5?T.muted2:T.text,flexShrink:0}}>💣 {g.exp_hr.toFixed(1)}</div>
            </div>
          ))}
          {acc && acc.n_days>0 && <div style={{fontSize:10.5,color:T.muted,padding:"8px 12px 2px",lineHeight:1.5,borderTop:`1px solid ${T.border}`,marginTop:4}}>📊 <b style={{color:T.muted2}}>Slate accuracy, last {acc.n_days}d:</b> projected <b style={{color:T.text}}>{Math.round(acc.total_projected)}</b> · actual <b style={{color:T.green}}>{acc.total_actual}</b> homer-hitters ({Math.round(acc.total_actual/acc.total_projected*100)}% — calibrated).</div>}
          {pick && pick.n>0 && pick.lift_x && <div style={{fontSize:10.5,color:T.muted,padding:"4px 12px 2px",lineHeight:1.5}}>🚀 <b style={{color:T.muted2}}>Homer picks, last {pick.days}d:</b> our top-{pick.top} board goes yard <b style={{color:T.green}}>{Math.round(pick.hit_rate*100)}%</b> — <b style={{color:T.green}}>{pick.lift_x}×</b> the {Math.round(pick.base_rate*100)}% league base ({pick.n} picks).</div>}
          <div style={{fontSize:9.5,color:T.muted,padding:"6px 12px 2px",lineHeight:1.4}}>Expected homers per game, calibrated to actuals. Only as complete as tonight's confirmed lineups. Research only — 21+.</div>
        </div>
      )}
    </div>
  )
}

// ── Trends tab (per-window prop hit-rate grid) ──────────────────────────────
// "How often has this prop actually cashed lately?" For each hitter on the slate,
// the L5/L10/L20/Season/H2H hit-rate for a chosen market + line, straight from the
// box-score game logs (GET /trends). The line stepper re-prices instantly server-
// side; tap a row to open the full player card.
const TREND_MARKETS = [
  // label,      backend market, default line, modal market key, isPitcher, [greenMin, amberMin]
  // Color thresholds are MARKET-RELATIVE: "green = strong for this prop." A 25% homer
  // rate is elite, so it can't share the 65% bar that a 1+ hit play clears. Tuned to
  // each market's realistic base rate at its default line.
  ["HRR 2+",     "hrr",  2,   "HRR", false, [56, 46]],   // ~coin-flip base; green = clearly over even
  ["1+ Hits",    "hits", 1,   "1+ H", false, [68, 56]],  // avg regular ~68%; green = above-average contact
  ["2+ Hits",    "hits", 2,   "2+ H", false, [32, 22]],  // elite multi-hit ~33%
  ["Home Run",   "hr",   1,   "HR", false, [15, 9]],     // 15%/gm ≈ 24-HR pace = strong; league power ~9%
  ["Bases 2+",   "tb",   2,   "TB", false, [50, 38]],
  ["Strikeouts", "k",    5.5, "K", true,  [58, 45]],     // pitcher: K over the line, last N starts
]
const TREND_WINDOWS = ["L5","L10","L20","SZN","H2H"]
// Green (strong) → amber (middling) → red (weak), RELATIVE to the market's own base
// rate. `thresh` = [greenMin, amberMin] for the active market; defaults to the old
// hits-style bar if not supplied.
function trendCellColor(pct, thresh){
  const [g, a] = thresh || [65, 50]
  if(pct==null) return {fg:T.faint, bg:"transparent"}
  if(pct>=g)    return {fg:T.green, bg:T.greenDim}
  if(pct>=a)    return {fg:T.amber, bg:T.amberDim}
  return {fg:T.red, bg:T.redDim}
}
function TrendCell({label, cell, thresh}){
  const pct = cell? cell.pct : null
  // Tiny samples (e.g. an 0/2 H2H) shouldn't read as loud green/red — 2 games isn't a
  // signal. Show the number, but muted/neutral until there are at least 4 games.
  const small = cell && cell.n > 0 && cell.n < 4
  const {fg,bg} = small ? {fg:T.muted, bg:"transparent"} : trendCellColor(pct, thresh)
  return (
    <div style={{flex:1,minWidth:0,textAlign:"center",background:bg,borderRadius:7,padding:"6px 2px"}}>
      <div style={{fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.04em"}}>{label}</div>
      <div style={{fontSize:15,fontWeight:800,color:fg,fontFamily:T.mono,marginTop:1}}>{pct==null?"—":`${pct}%`}</div>
      <div style={{fontSize:8.5,color:T.faint,fontFamily:T.mono,marginTop:1}}>{cell?`${cell.hit}/${cell.n}`:"n/a"}</div>
    </div>
  )
}
function TrendRow({p, onPlayer, modalMarket, thresh}){
  const arrow = p.trend==="up"?"▲":p.trend==="down"?"▼":"—"
  const acol  = p.trend==="up"?T.green:p.trend==="down"?T.red:T.faint
  const isPit = !!p.is_pitcher
  // Pitcher rows: BvP has no internal id for the arm, so they aren't tappable yet
  // (opening a pitcher card is a follow-up). Hitter rows open the player card.
  const clickable = !isPit && p.player_id
  const sub = isPit
    ? `${p.bats?`${p.bats}HP · `:""}vs ${p.opp_pitcher}${p.venue?` · ${p.venue}`:""}`
    : `${p.opp_pitcher?`vs ${p.opp_pitcher}${p.opp_hand?` (${p.opp_hand})`:""}`:"—"}${p.venue?` · ${p.venue}`:""}`
  return (
    <button onClick={clickable?(()=>onPlayer&&onPlayer({name:p.name, player_id:p.player_id, market:modalMarket, team:p.team,
        opp:p.opp_pitcher?`${p.team} · vs ${p.opp_pitcher}${p.opp_hand?` (${p.opp_hand})`:""}`:p.team, oppHand:p.opp_hand, oppPitcher:p.opp_pitcher})):undefined}
      style={{width:"100%",textAlign:"left",cursor:clickable?"pointer":"default",color:T.text,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"11px 12px",marginBottom:8,display:"block"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
        <div style={{minWidth:0}}>
          <div style={{fontWeight:800,fontSize:14.5,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            <span style={{color:acol,fontSize:12,marginRight:5}}>{arrow}</span>{p.name}
            {!isPit && <span style={{color:T.muted,fontSize:11,fontWeight:700}}> · {p.team}{p.bats?` (${p.bats})`:""}</span>}
          </div>
          <div style={{fontSize:10.5,color:T.muted,fontFamily:T.mono,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub}</div>
        </div>
      </div>
      <div style={{display:"flex",gap:5}}>
        {TREND_WINDOWS.map(w=><TrendCell key={w} label={w} cell={p.cells?.[w]} thresh={thresh}/>)}
      </div>
    </button>
  )
}
function PropTrendsTab({onPlayer, onGlossary}){
  const [mkt,setMkt]   = useState(0)                    // index into TREND_MARKETS
  const [label,bMarket,defLine,modalMarket,isPitcher,thresh] = TREND_MARKETS[mkt]
  const [line,setLine] = useState(defLine)
  const [sort,setSort] = useState("L10")
  const [search,setSearch] = useState("")
  // Reset the line to the market's canonical value whenever the market changes.
  useEffect(()=>{ setLine(TREND_MARKETS[mkt][2]) }, [mkt])
  const { data, loading } = useFetch(
    ()=>getTrends(todayStr(), {market:bMarket, line, sort}).catch(()=>null),
    [bMarket, line, sort]
  )
  const _deacc = s => (s||"").normalize("NFD").replace(/[̀-ͯ]/g,"").toLowerCase()
  const q = _deacc(search.trim())
  const players = (data?.players||[]).filter(p=>!q || _deacc(p.name).includes(q) || _deacc(p.team).includes(q) || _deacc(p.opp_pitcher).includes(q))

  return (
    <div style={{padding:"6px 14px 90px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,margin:"10px 2px 8px"}}>
        <div style={{fontSize:17,fontWeight:800,color:T.text}}>📊 Player Trends</div>
        <HelpChip onGlossary={onGlossary} section="top"/>
      </div>
      <div style={{fontSize:11.5,color:T.muted,lineHeight:1.5,margin:"0 2px 12px"}}>
        How often each prop has actually cashed — last <b style={{color:T.muted2}}>5 / 10 / 20</b> {isPitcher?"starts":"games"}, the <b style={{color:T.muted2}}>season</b>, and <b style={{color:T.muted2}}>head-to-head</b> vs tonight's {isPitcher?"opponent":"pitcher"}.{isPitcher?"":" Tap a name for the full card."}
      </div>

      {/* Market pills */}
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,margin:"0 -2px 10px"}}>
        {TREND_MARKETS.map(([lbl],i)=>{ const on=i===mkt; return (
          <button key={lbl} onClick={()=>setMkt(i)} style={{flexShrink:0,padding:"7px 13px",borderRadius:18,fontSize:12.5,fontWeight:700,cursor:"pointer",
            background:on?T.green:T.card,color:on?"#04120B":T.muted2,border:`1px solid ${on?T.green:T.border2}`}}>{lbl}</button>
        )})}
      </div>

      {/* Line stepper + sort */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,margin:"0 2px 12px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,color:T.muted,fontWeight:700}}>LINE</span>
          <div style={{display:"flex",alignItems:"center",gap:0,background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,overflow:"hidden"}}>
            <button onClick={()=>setLine(v=>Math.max(1,v-1))} style={{padding:"5px 12px",border:"none",background:"none",color:T.text,fontSize:16,fontWeight:800,cursor:"pointer"}}>−</button>
            <span style={{padding:"0 6px",fontSize:13.5,fontWeight:800,color:T.green,fontFamily:T.mono,minWidth:44,textAlign:"center"}}>{line}+</span>
            <button onClick={()=>setLine(v=>Math.min(isPitcher?12:6,v+1))} style={{padding:"5px 12px",border:"none",background:"none",color:T.text,fontSize:16,fontWeight:800,cursor:"pointer"}}>+</button>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{fontSize:11,color:T.muted,fontWeight:700}}>SORT</span>
          <select value={sort} onChange={e=>setSort(e.target.value)} style={{background:T.card,color:T.text,border:`1px solid ${T.border2}`,borderRadius:9,padding:"6px 8px",fontSize:12.5,fontWeight:700,cursor:"pointer"}}>
            {TREND_WINDOWS.map(w=><option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>

      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search hitter, team, or pitcher…"
        style={{width:"100%",boxSizing:"border-box",background:T.card,color:T.text,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 12px",fontSize:13,marginBottom:12,outline:"none"}}/>

      {loading && !data && <div style={{textAlign:"center",color:T.muted,fontSize:13,padding:"30px 0"}}>Loading trends…</div>}
      {!loading && data && players.length===0 && (
        <div style={{textAlign:"center",color:T.muted,fontSize:13,padding:"30px 12px",lineHeight:1.5}}>
          No {isPitcher?"starters":"hitters"} match yet. Trends post with the slate — once tonight's {isPitcher?"probable pitchers":"lineups"} &amp; matchups are in (usually by ~3 PM ET).
        </div>
      )}
      {players.map(p=><TrendRow key={`${p.player_id||p.mlbam_id}-${bMarket}`} p={p} onPlayer={onPlayer} modalMarket={modalMarket} thresh={thresh}/>)}

      {players.length>0 && (
        <div style={{fontSize:10,color:T.faint,lineHeight:1.5,margin:"10px 4px 0"}}>
          Colors are relative to <b style={{color:T.muted}}>{label}</b>: green ≥{thresh[0]}% (strong) · amber {thresh[1]}–{thresh[0]-1}% · red &lt;{thresh[1]}% · thin samples (&lt;4 {isPitcher?"starts":"games"}) shown neutral. "n/n" = {isPitcher?"starts the K line cleared / starts":"games the prop cleared / games played"} in that window. H2H = career vs tonight's {isPitcher?"opponent":"starter"}. Research only — 21+.
        </div>
      )}
    </div>
  )
}

function GamesTab({onGame, onPlayer, autoAttack, onAttackConsumed, onGlossary}){
  // Lineups post through the day (the cron updates lineup_slot every refresh), but this
  // tab used to fetch ONCE on mount — so an open app showed the morning lineup all day.
  // Re-pull every 90s AND whenever the app returns to the foreground, so slots/scratches
  // stay current without a manual reload.
  const [tick,setTick] = useState(0)
  useEffect(()=>{
    const id = setInterval(()=>setTick(t=>t+1), 90000)
    const onVis = ()=>{ if(document.visibilityState==='visible') setTick(t=>t+1) }
    document.addEventListener('visibilitychange', onVis)
    return ()=>{ clearInterval(id); document.removeEventListener('visibilitychange', onVis) }
  },[])
  const { data, loading } = useFetch(()=>getUpcomingMatches(todayStr()), [tick])
  // Map each game defensively + dedupe by id: one malformed game (e.g. a
  // doubleheader's odd shape) must not throw and blank the whole tab.
  const live = (()=>{ const out=[], seen=new Set(); for(const g of (data?.games||[])){
    if(g && g.id!=null){ if(seen.has(g.id)) continue; seen.add(g.id) }
    try{ out.push(mapCard(g)) }catch(_){} } return out })()
  const games=live, isLive=live.length>0
  const [angle,setAngle] = useState("grade")   // grade | power | hits | strikeouts
  const [minG,setMinG]   = useState(0)         // 0=all | 50=C+ | 58=B-+ | 74=A
  const [mode,setMode]   = useState("slate")   // slate | record (win-prob tracker)
  const attackRows = useAttackRows()           // 🎯 pitchers to attack (best hitter per arm)
  const [attackOpen,setAttackOpen] = useState(false)
  // Deep-linked from Home → open the drawer on arrival, in Slate mode, then clear.
  useEffect(()=>{ if(autoAttack){ setMode("slate"); setAttackOpen(true); onAttackConsumed && onAttackConsumed() } }, [autoAttack])
  const gradeOf = g => angle==="grade" ? (g.grade||0) : (g.sub?.[angle] ?? 0)
  const shown = games
    .filter(g => minG===0 || gradeOf(g) >= minG)
    .sort((a,b)=> gradeOf(b)-gradeOf(a))
  const pill = on => ({flexShrink:0,padding:"5px 11px",borderRadius:16,fontSize:11,fontWeight:700,cursor:"pointer",border:`1px solid ${on?T.green:T.border2}`,background:on?T.greenDim:T.card,color:on?T.green:T.muted2})
  const ANGLES=[["grade","Total Action"],["power","Power"],["hits","Hits"],["strikeouts","Ks"]]
  const MINS=[[0,"All"],[50,"C+"],[58,"B+"],[74,"A only"]]
  const seg = on => ({flex:1,padding:"8px 0",borderRadius:9,fontSize:12.5,fontWeight:800,cursor:"pointer",
    background:on?T.greenDim:T.card,border:`1px solid ${on?T.green:T.border2}`,color:on?T.green:T.muted2})
  return (
    <div style={{padding:"14px"}}>
      <div style={{display:"flex",gap:7,marginBottom:14}}>
        <button onClick={()=>setMode("slate")} style={seg(mode==="slate")}>📋 Slate</button>
        <button onClick={()=>setMode("model")} style={seg(mode==="model")}>🎯 Model</button>
        <button onClick={()=>setMode("record")} style={seg(mode==="record")}>📊 Record</button>
      </div>
      {mode==="model" ? <GameModelToday/> : mode==="record" ? (<><GameOutcomePanel/><HrValidationCard/><MarketCalibrationCard/></>) : (<>
      {/* 💣 Slate HR projection — total + per-game expected homers */}
      <SlateHrBanner onGame={onGame}/>
      {/* 🎯 Pitchers to attack — the best hitter to target against each gettable arm, in a drawer */}
      {attackRows.length>0 && (
        <button onClick={()=>setAttackOpen(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",color:T.green,fontSize:13.5,fontWeight:800,cursor:"pointer",marginBottom:14}}>
          <span style={{fontSize:16}}>🎯</span>
          <span style={{flex:1,textAlign:"left"}}>Pitchers to attack <span style={{color:T.muted2,fontWeight:600}}>· best hitter to target vs each arm</span></span>
          <span style={{fontSize:13,color:T.muted2}}>→</span>
        </button>
      )}
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:10,textTransform:"uppercase",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span>{shown.length} of {games.length} games{!isLive&&!loading?<span style={{color:T.amber}}> · sample</span>:""}</span>
        <HelpChip onGlossary={onGlossary} section="scores"/>
      </div>
      {/* Rank by angle */}
      <div style={{display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none",marginBottom:8}}>
        {ANGLES.map(([k,lbl])=>(<button key={k} onClick={()=>setAngle(k)} style={pill(angle===k)}>{lbl}</button>))}
      </div>
      {/* Min grade filter */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {MINS.map(([v,lbl])=>(<button key={v} onClick={()=>setMinG(v)} style={{...pill(minG===v),flex:1,textAlign:"center"}}>{lbl}</button>))}
      </div>
      {/* Weather-timing disclaimer — forecasts populate at the midday refresh, so they
          can be blank/preliminary earlier in the day. */}
      <div style={{fontSize:10.5,color:T.muted,background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"8px 11px",marginBottom:12,lineHeight:1.5,display:"flex",gap:8,alignItems:"flex-start"}}>
        <span style={{flexShrink:0}}>🌦️</span>
        <span>Ballpark weather loads midday — it populates around <b style={{color:T.muted2}}>12 PM ET</b> and refreshes before first pitch, so it may be blank or preliminary earlier in the day.</span>
      </div>
      {loading && !games.length && <div style={{textAlign:"center",color:T.muted,padding:"40px 0"}}>Loading slate…</div>}
      {!loading && !shown.length && <div style={{textAlign:"center",color:T.muted,padding:"40px 16px",fontSize:13,lineHeight:1.6}}>No games meet that grade filter.<br/><span onClick={()=>setMinG(0)} style={{color:T.green,cursor:"pointer",fontWeight:700}}>Show all games</span></div>}
      {shown.map(g=><GameCard key={g.id} game={g} onOpen={onGame} highlight={angle}/>)}
      </>)}
      {attackOpen && <ErrorBoundary resetKey="attack-games"><AttackDrawer rows={attackRows} onClose={()=>setAttackOpen(false)} onOpen={e=>{ setAttackOpen(false); onPlayer && onPlayer(playCardPayload(e)) }}/></ErrorBoundary>}
    </div>
  )
}

// ── Contact tab (research hub) ──────────────────────────────────────────────
// Reuses the full standalone Contact research experience (search → log + insights).
function ContactTab(){ return <ContactLogTab/> }

// ── Home (executive dashboard) ──────────────────────────────────────────────
function SectionLabel({children, right, onRight}){
  return (
    <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",margin:"18px 0 8px"}}>
      <div style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:"0.12em",textTransform:"uppercase"}}>{children}</div>
      {right && <button onClick={onRight} style={{background:"none",border:"none",color:T.green,fontSize:11,fontWeight:700,cursor:"pointer",padding:0}}>{right}</button>}
    </div>
  )
}

function HomePlayRow({e, onOpen}){
  const prob=e.prob_over||0, edge=e.edge_pct!=null?e.edge_pct*100:null
  return (
    <button onClick={()=>onOpen(e)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${e.tier==="ELITE"||e.tier==="BEST BET"?T.greenMid:T.border}`,borderRadius:12,padding:"11px 13px",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
      <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
      <Headshot mlbamId={e.mlbam_id} name={e.full_name} size={34}/>
      <div style={{minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
          <span style={{fontSize:9.5,fontWeight:800,color:T.muted2,fontFamily:T.mono,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:4,padding:"1px 5px"}}>{mDisp(e.market)}</span>
          <span style={{fontSize:15,fontWeight:800,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.full_name}</span>
          <FormTrend trend={e.form_trend} delta={e.form_delta_pct} size={9}/>
        </div>
        <div style={{fontSize:11,color:T.muted}}>{e.team}{e.opp_pitcher_name?` · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:""}</div>
      </div>
      </div>
      <div style={{textAlign:"right",flexShrink:0}}>
        <div style={{fontSize:17,fontWeight:800,color:probColorMkt(prob, e.market),fontFamily:T.mono}}>{pct(prob)}</div>
        <div style={{marginTop:2}}><TierBadge tier={e.tier}/></div>
      </div>
    </button>
  )
}

// ── Legend & Glossary — in-app reference for tabs, scores, and data ─────────
// ── Help bot (rule-based FAQ, $0 — answers from the glossary content) ────────
const HELP_KB = [
  {id:"prob",  kw:["probability","prob","percent","%","chance","likely","hit"],
   a:"The big % on each play is the model's CALIBRATED chance the prop goes OVER the line. Higher = more likely. It's the #1 number — picks lead with it, and Rankings defaults to sorting by it."},
  {id:"conf",  kw:["rating","linebrkr rating","confidence","conf","trust","sure","certain"],
   a:"The LINEBRKR Rating (0–100) is our one measure of how good a spot is to hit — model conviction, matchup, zone fit, form, park and data quality in a single number, higher when the data is complete and the signals agree. It's separate from price/edge. 85+ Elite · 70s Strong · 55–69 Solid · under 55 skip."},
  {id:"edge",  kw:["edge","value","price","worth","underpriced"],
   a:"Edge = the model's probability minus the market consensus (de-vigged median across books). Positive = underpriced vs the market. Secondary to probability."},
  {id:"bestprice", kw:["best price","book","line shopping","which book"],
   a:"On a play we show the best available price and which book has it (e.g. '-105 @ DraftKings') — line shopping, so you know where to get the best payout."},
  {id:"tiers", kw:["tier","badge","elite","top rated","strong","longshot","pass","label","color"],
   a:"Badges rank conviction: ELITE → TOP RATED → STRONG → MODERATE → WATCH → LONGSHOT → LOW. They're model ratings (information, not advice). A low-probability HR can be a LONGSHOT but never ELITE."},
  {id:"markets", kw:["market","bet type","hrr","hr","hits","total bases","tb","strikeout","k","doubles"],
   a:"HRR = Hits+Runs+RBIs Over 1.5 · HR = home run anytime · 1+ H = at least one hit · 2+ H = two+ hits · TB = total bases Over 1.5 · K = pitcher strikeouts vs the book line."},
  {id:"safest", kw:["highest probability","safest","floor","safe"],
   a:"Want the highest-hit-% plays? Open Rankings and sort by 'Highest Probability' — it ranks every market by the calibrated chance to hit. The 🏆 Top 15 on Home is the best-rated shortlist (LINEBRKR Rating), spread across games."},
  {id:"top15", kw:["top 15","our guys","top-rated","curated","composite","best rated"],
   a:"The 🏆 Top 15 (Home) is our flagship shortlist — the 15 best-rated plays by the LINEBRKR Rating (HRR or Homers), spread across games so you don't stack correlated bats. It also has a 'Copy board for Claude' export. For the highest-hit-% view instead, use Rankings → sort 'Highest Probability'."},
  {id:"stats", kw:["xwoba","woba","barrel","hard hit","exit velo","exit velocity","launch angle","iso","isolated power","pull","whiff","era","whip","k/9","hr/9","ops","what does","what is","stat","statcast","zone fit","mean"],
   a:"Every advanced stat is defined in plain English in the Legend & Glossary → STAT DICTIONARY. Quick hits: xwOBA = expected on-base quality (.320 avg, higher = better hitter); Barrel% = elite contact rate (12%+ = big power); ISO = pure power; Whiff% = swing-and-miss; ERA/WHIP/K9/HR9 grade the pitcher (for HR/HRR, you want high HR/9 + high WHIP). Tap 'Open full guide' for all of them."},
  {id:"pitchmatch", kw:["pitch","matchup","fastball","slider","arsenal","pitch type","crushes"],
   a:"A pitcher's ARSENAL (what he throws + usage %) is on Games → Pitching. How a HITTER does vs each of those pitches (xwOBA/whiff) is on the hitter's card → Intel → PITCH MATCHUP, with a one-line headline like 'Handles the 4-Seam he throws 31%'."},
  {id:"wall",  kw:["wall","tough","hittable","pitcher difficulty","struggling"],
   a:"Wall = how hittable the opposing pitcher is: HARD WALL → SOFT WALL → MODERATE → NEUTRAL → HITTABLE. Walls fade hitters; hittable arms boost them. 🔥 by a pitcher = he's struggling / attackable."},
  {id:"park",  kw:["park","ballpark","coors","environment","weather","wind"],
   a:"Park Factor = the ballpark's effect on offense. Above 1.00 = hitter-friendly (good for overs), below = pitcher-friendly. It feeds the Game Grade, with weather (wind out helps carry)."},
  {id:"confirmed", kw:["confirmed","lineup","scratched","injured","posted"],
   a:"'Confirmed only' hides players whose lineup hasn't posted and anyone scratched/injured. Lineups usually post ~3 PM ET — finalize picks after that."},
  {id:"mybets", kw:["my bets","track","record","p&l","pnl","roi","log","pending"],
   a:"Record → My Bets → tap ＋ to log a wager. Mark it Pending and tap it later to settle Win/Loss. Tracks net P&L, ROI, and results by market & book. Your data, on your device."},
  {id:"watch", kw:["watchlist","star","my guys","follow","favorite"],
   a:"Tap the ☆ on any hitter's card to add them to your watchlist, then use the ☆ Watchlist filter on Rankings to see only your guys."},
  {id:"calib", kw:["calibration","calibrated","accurate","overconfident"],
   a:"We learn from settled results how often each probability level actually hits, then adjust the displayed % to match — so a shown 65% is a real 65%. It's why the board's numbers are trustworthy."},
  {id:"linebrkr", kw:["what is","linebrkr","about","app"],
   a:"LINEBRKR is an MLB prop-research tool: a model projects each hitter/pitcher, compares to the book's odds, and surfaces the best spots. Information only — not a sportsbook and not betting advice."},
  {id:"rg", kw:["responsible","gambling","21","help","addiction","problem","gambler"],
   a:"Must be 21+. LINEBRKR is research/entertainment only — it doesn't take wagers or guarantee outcomes. Bet only what you can afford to lose. Need help? Call 1-800-GAMBLER."},
  {id:"cancel", kw:["cancel","unsubscribe","stop","end subscription","quit","refund","stop paying","cancel subscription","turn off"],
   a:"To cancel: scroll to the bottom of the app → tap 'Manage subscription' (or go to linebrkr.app/?account=1) → tap 'Manage / cancel subscription' → that opens Stripe's secure billing page → tap 'Cancel plan'. You keep access until the end of the period you already paid for, then it stops — no charge after that. (Make sure you're logged in with your access code first.)"},
  {id:"billing", kw:["billing","subscription","manage","update card","payment method","invoice","receipt","change card","account"],
   a:"Manage your subscription — update your card, see invoices, or cancel — at the bottom of the app under 'Manage subscription' (or linebrkr.app/?account=1). It opens Stripe's secure billing portal. Your plan, status, and access code are shown on that account screen too."},
  {id:"login", kw:["log in","login","sign in","access code","new device","new phone","other device","lost code","forgot code","cant get in","locked out"],
   a:"Log in on any device at linebrkr.app/?login=1 with your access code (shown right after you sign up, and any time under 'Manage subscription'). Save that code — it's your key on every device. Lost it and can't reach the account screen? Contact support and we'll look it up by your email."},
]
function helpAnswer(query){
  const q=(query||"").toLowerCase(); if(!q.trim()) return {a:null}
  const words=new Set(q.split(/[^a-z0-9+]+/).filter(w=>w.length>1))
  let best=null, score=0
  for(const e of HELP_KB){
    let s=0
    for(const k of e.kw){ if(q.includes(k)) s+=2; else if(words.has(k)) s+=1 }
    if(s>score){ score=s; best=e }
  }
  return (best && score>=2) ? {a:best.a} : {a:null}
}
function HelpBot({onClose, onGlossary}){
  const [msgs,setMsgs] = useState([{from:"bot",text:"Hey, I'm BRKR 👋 — your LINEBRKR helper. Ask me anything about the app: probability, confidence, edge, tiers, markets, pitch matchups, My Bets, and more."}])
  const [input,setInput] = useState("")
  const endRef = useRef(null)
  useEffect(()=>{ try{ endRef.current?.scrollIntoView({behavior:"smooth"}) }catch{} }, [msgs])
  const ask = text => {
    const qq=(text||input).trim(); if(!qq) return
    const r=helpAnswer(qq)
    const reply = r.a || "I didn't quite catch that — I'm BRKR, I can explain anything in the app. Try a chip below, or open the full guide."
    setMsgs(m=>[...m, {from:"user",text:qq}, {from:"bot",text:reply, miss:!r.a}]); setInput("")
  }
  const chips=["What's the probability?","What does xwOBA mean?","How do I see pitch matchups?","How do I track my bets?"]
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"10px 16px 20px",display:"flex",flexDirection:"column",minHeight:"60vh"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:18,fontWeight:900}}>💬 BRKR</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:9,paddingBottom:8}}>
          {msgs.map((m,i)=>(
            <div key={i} style={{alignSelf:m.from==="user"?"flex-end":"flex-start",maxWidth:"86%",background:m.from==="user"?T.green:T.card,color:m.from==="user"?"#04140D":T.text,border:m.from==="user"?"none":`1px solid ${T.border2}`,borderRadius:12,padding:"9px 12px",fontSize:13,lineHeight:1.45}}>
              {m.text}
              {m.miss && <div style={{marginTop:6}}><button onClick={onGlossary} style={{background:"none",border:`1px solid ${T.green}`,borderRadius:7,color:T.green,fontSize:11,fontWeight:700,padding:"4px 9px",cursor:"pointer"}}>Open full guide →</button></div>}
            </div>
          ))}
          <div ref={endRef}/>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",margin:"6px 0 8px"}}>
          {chips.map(c=>(<button key={c} onClick={()=>ask(c)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,color:T.muted2,fontSize:11,fontWeight:600,padding:"5px 10px",cursor:"pointer"}}>{c}</button>))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") ask() }} placeholder="Ask a question…" style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",color:T.text,fontSize:13,outline:"none"}}/>
          <button onClick={()=>ask()} style={{background:T.green,border:"none",borderRadius:10,color:"#04140D",fontSize:13,fontWeight:800,padding:"0 16px",cursor:"pointer"}}>Ask</button>
        </div>
        <div style={{fontSize:9.5,color:T.muted,textAlign:"center",marginTop:8}}>Answers from the LINEBRKR guide · information only, not betting advice · 21+</div>
      </div>
    </Sheet>
  )
}

// First-run welcome — shows once (localStorage), 30-second orientation.
function WelcomeSheet({onClose, onGlossary}){
  const items = [
    ["📊","Probability first","Every play leads with the model's calibrated chance to hit. Higher % = more likely."],
    ["🏆","Start with the Top 15","Home → 🏆 Top 15: our best-rated plays by the LINEBRKR Rating, spread across games. Want the highest-hit-% list instead? Rankings → sort by 'Highest Probability'."],
    ["🎯","Dig into a matchup","Tap a hitter → Intel for the pitch-type matchup, zone fit & splits. Tap a game → Pitching for the arsenal."],
    ["📒","Track your bets","Record → My Bets: log wagers (even Pending — tap to settle later), see your P&L and ROI."],
  ]
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"10px 18px 40px"}}>
        <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.03em",marginBottom:4}}>Welcome to <span style={{color:T.green}}>LINEBRKR</span></div>
        <div style={{fontSize:12.5,color:T.muted,lineHeight:1.5,marginBottom:16}}>An analytics hub for player props. Search any hitter or matchup, see the model's read, and track a public record. You research plays here, you don't place them. The 30-second version:</div>
        {items.map(([icon,title,body],i)=>(
          <div key={i} style={{display:"flex",gap:11,marginBottom:13}}>
            <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
            <div><div style={{fontSize:13.5,fontWeight:800,color:T.text}}>{title}</div><div style={{fontSize:12,color:T.muted2,lineHeight:1.45}}>{body}</div></div>
          </div>
        ))}
        <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",fontSize:11,color:T.muted,lineHeight:1.5,margin:"6px 0 16px"}}>
          <b style={{color:T.text}}>21+. Information only — not betting advice.</b> Numbers come from real data; "—" means it wasn't computed. Bet responsibly · 1-800-GAMBLER.
        </div>
        <button onClick={onClose} style={{width:"100%",padding:"13px",borderRadius:10,background:T.green,border:"none",color:"#04140D",fontSize:14,fontWeight:800,cursor:"pointer",marginBottom:9}}>Got it — let's go</button>
        <button onClick={onGlossary} style={{width:"100%",padding:"11px",borderRadius:10,background:T.card,border:`1px solid ${T.border2}`,color:T.muted2,fontSize:13,fontWeight:700,cursor:"pointer"}}>See the full guide</button>
      </div>
    </Sheet>
  )
}

function GlossarySheet({onClose, focus}){
  const refs = useRef({})
  useEffect(()=>{
    if(focus && refs.current[focus]){
      const t = setTimeout(()=>{ try{ refs.current[focus].scrollIntoView({behavior:"smooth",block:"start"}) }catch(_){} }, 60)
      return ()=>clearTimeout(t)
    }
  },[focus])
  const Sec = ({title,id}) => <div ref={el=>{ if(id) refs.current[id]=el }} style={{fontSize:10,color:T.green,fontWeight:800,letterSpacing:"0.14em",margin:"18px 0 10px",scrollMarginTop:8}}>{title}</div>
  const Row = ({k,v,kc}) => (
    <div style={{display:"flex",gap:12,marginBottom:11,alignItems:"flex-start"}}>
      <span style={{flexShrink:0,minWidth:104,fontSize:12.5,fontWeight:800,color:kc||T.text,lineHeight:1.35}}>{k}</span>
      <span style={{fontSize:12.5,color:T.muted2,lineHeight:1.45}}>{v}</span>
    </div>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>Legend &amp; Glossary</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>How LINEBRKR works — what each tab does, what every score means, and the key numbers behind the picks. The rule: every number is computed from real data; if the model didn't produce it, you'll see "—".</div>

        <Sec title="THE TABS" id="tabs"/>
        <Row k="Home" v="Your daily command center — the Slate Read briefing, the single best bet, top plays across markets, the best 2-leg parlay, the game to attack, home-run shots, and what's trending."/>
        <Row k="Rankings" v="The full ranked board across every market. Search by player/team/pitcher, tap a tier to filter, sort, build a parlay, and export the list."/>
        <Row k="Games" v="Every game graded. Open one for 'The Read' (a plain-English writeup), ballpark weather, per-bet edges, pitching breakdown, and lineups."/>
        <Row k="Hot Corner" v="Batter-vs-pitcher dashboard: hitters who own tonight's starter by career head-to-head (AVG/OPS), with HR history. Ranked by dominance, sample-weighted so a real history outranks a 2-for-3. 💣 = has homered off him; SMALL = under 6 career AB (noisier). Tap a card to drill into the hitter. Refreshes hourly."/>
        <Row k="Trends" v="Last-7-day momentum. Hitting = hottest bats (with today's matchup flag); Pitching = ONLY tonight's actual starters, ranked most → least hittable (season HR/9 + ERA − K/9), with how strong the lineup they face projects."/>
        <Row k="Scouting" v="Two research views in one tab. Hitters: Statcast batted-ball history for any hitter — exit velocity, launch angle, distance — plus a Contact Score. Pitchers: every current-season arm's ERA / WHIP / K9 / HR9 / IP / GS with health flags (stale, defaulted, small-sample), searchable and sortable."/>
        <Row k="Record" v="LINEBRKR's real settled track record (elite-pick hit rate + per-market trends) and 'My Bets' for your own logged P&L."/>

        <Sec title="SCORES & RATINGS" id="scores"/>
        <Row k="Probability" kc={T.green} v="The big % on each pick — the model's chance the prop goes OVER the line. The raw likelihood of the outcome; picks are ranked by it."/>
        <Row k="LINEBRKR Rating" kc={T.green} v="0–100 — our ONE measure of how good a spot is to hit. It combines everything we compute: model conviction (how decisively the probability clears this market's bar, 50%), matchup support (zone fit, recent form, the starter's hittability, park — 25%), and data quality (lineup confirmed, pitcher confirmed, line posted, Statcast + recent games present — 25%). It is NOT edge/price — that's shown separately. Two 73% bets aren't equal — the Rating is higher when the data is complete and the signals agree. 85+ Elite · 70s Strong · 55–69 Solid · under 55 skip. Its track record is graded as one number, so the guide and the proof are the same thing."/>
        <Row k="💰 Value icon" kc={T.green} v="On a pick when there's a price edge vs the book — 💰 solid value (+4% or more), 💰💰 big value (+12% or more), with the edge%. It's separate from the Rating: the RATING says how likely it hits, 💰 says you're also getting a good price. A pick can be a STRONG bet with no 💰 (likely but fairly priced), or carry 💰 without a high Rating (good price, lower hit chance)."/>
        <Row k="Wall" v="How hittable the opposing pitcher is, from the model's matchup multiplier: HARD WALL → SOFT WALL → MODERATE → NEUTRAL → HITTABLE. Walls fade hitters; hittable arms boost them."/>
        <Row k="Park Factor" v="Ballpark's effect on offense. Above 1.00 = hitter-friendly, below = pitcher-friendly. Drives the Environment / Game Grade."/>
        <Row k="Game Grade" v="A–F (and 0–100) on how good a spot a game is to attack — the overall offensive environment, weighted: offense strength 30% (how both lineups project vs this pitching), pitcher vulnerability 25%, park + weather 25%, bullpen exposure 10%, lineup confirmation 10%. Each input is scored against the MEASURED league average (rolling 30 days of real projections + pitcher stats), so 'C' = league-average and 'A' = genuinely top-tier — not a hand-set threshold."/>
        <Row k="Grade scale" v="A = elite spot — stack it. B = strong. C = average / playable. D = below-average (good arm, suppressive park, or quiet bats). F = avoid — genuinely dead environment. Note: a low game grade rates the WHOLE matchup — individual players can still be good bets in a low-graded game."/>
        <Row k="Sub-grades" v="The same game by betting angle (on The Read): Power/HR (HR-friendly park, wind out, HR-prone arms, sluggers), Hits (getting on base — 1+ hit prob, hittable pitching), Strikeouts (good K arms vs whiff-prone bats). A Coors game might be Power A / Hits B / K F; a Seattle vs an ace might be Power F / K B."/>
        <Row k="Contact Score" v="Grades a hitter's recent batted-ball quality (exit velo, launch angle, barrels). Higher = harder, better contact."/>
        <Row k="Matchup flag" v="On Trends: ✅ good (hitter faces a hittable pitcher / strong park, or a pitcher faces a strong lineup), ⚠️ tough (faces a wall), or 'no game today'."/>

        <Sec title="TIERS (the colored badges)" id="tiers"/>
        {["ELITE","BEST BET","STRONG","PLAY","WATCH","PRIME SHOT","VALUE SHOT","PASS"].map(t=>(
          <div key={t} style={{display:"flex",alignItems:"center",gap:10,marginBottom:9}}>
            <span style={{flexShrink:0}}><TierBadge tier={t}/></span>
            <span style={{fontSize:12.5,color:T.muted2,lineHeight:1.35}}>{TIER[t].desc}</span>
          </div>
        ))}
        <div style={{fontSize:11.5,color:T.muted,lineHeight:1.5,marginTop:4}}>Low-probability HR / 2+ Hits longshots can be a <b style={{color:"#F97316"}}>LONGSHOT</b> but never ELITE — so a 33% HR can't masquerade as a safer 70% HRR play.</div>

        <Sec title="MARKETS" id="markets"/>
        {[["HRR","Hits + Runs + RBIs — Over 1.5"],["HR","Home Run — anytime (Over 0.5)"],["1+ H","Hits — to record a hit (Over 0.5)"],["2+ H","Hits — two or more (Over 1.5)"],["Bases","Total Bases — Over 1.5"],["K","Pitcher Strikeouts — vs the book's line"]].map(([a,d])=>(
          <Row key={a} k={a} v={d}/>
        ))}

        <Sec title="TOOLS & MATCHUP" id="tools"/>
        <Row k="Top 15" v="Our flagship shortlist: the 15 best-rated plays per market (HRR / Homers), ranked by the LINEBRKR Rating and spread across games so you're not stacking correlated bats. EV breaks ties. Includes a one-tap 'Copy board for Claude' export. Launched from Home / Rankings."/>
        <Row k="Highest Probability" v="A sort in Rankings: ranks the board purely by calibrated hit % (the raw chance to go over), across every market — the high-floor view for a hit-rate bettor. Separate from the Rating, which also weighs matchup + data quality."/>
        <Row k="Pitch Arsenal" v="On a pitcher's card (Games → Pitching): the pitches he throws and how often (usage %), most-thrown first."/>
        <Row k="Pitch Matchup" v="On a hitter's card (Intel): how he performs (xwOBA + whiff) vs each pitch this starter throws, with usage %. The headline flags the most impactful read — e.g. 'Handles the 4-Seam he throws 31%'."/>
        <Row k="Splits / 🔥" v="Pitcher card shows vs-RHB / vs-LHB lines + home/away ERA. 🔥 = a below-average / hittable arm worth attacking."/>
        <Row k="My Bets" v="Record → My Bets: log your wagers (incl. Pending, tap to settle later). Tracks net P&L, ROI, and results by market & book. Your data, on your device."/>

        <Sec title="KEY DATA ELEMENTS" id="data"/>
        <Row k="Edge" v="Model probability minus the market consensus (de-vigged median across books). Positive = underpriced vs the market. Secondary to probability."/>
        <Row k="Line" v="The over/under number. HR is always 0.5 (anytime), HRR / TB / 2+ Hits are 1.5, 1+ Hits is 0.5, K uses the live book line."/>
        <Row k="Book odds" v="The sportsbook's price (American odds). 'Implied' is the probability those odds bake in — what the edge is measured against."/>
        <Row k="Wind" v="On the Park tab: blowing OUT (helps carry) or IN (knocks balls down) and to which part of the field, plus speed."/>
        <Row k="Confirmed only" v="When on, the board hides players whose lineup hasn't posted and anyone scratched/injured. Lineups usually post ~3 PM ET."/>
        <Row k="70%+ pick hit rate" v="On the Record: how our 70%+ model-probability picks have actually settled — our highest-conviction track record, scoped to the current model."/>

        <Sec title="STAT DICTIONARY (the baseball numbers)" id="stats"/>
        <div style={{fontSize:11.5,color:T.muted,lineHeight:1.5,marginBottom:12}}>Plain-English on the advanced stats you'll see on player &amp; pitcher cards. Rule of thumb: <b style={{color:T.text}}>higher is better for hitters, lower is better for the pitcher facing them.</b></div>
        <Row k="xwOBA" kc={T.green} v="Expected Weighted On-Base Average. The best all-in-one hitting number: it grades every batted ball by its exit velocity + launch angle (how hard and at what angle it was hit) to estimate what the OUTCOME should have been — stripping out luck, defense, and park. Scale looks like on-base %: .320 ≈ league average, .370+ is strong, .270 is weak. On the pitch-matchup table it's the hitter's xwOBA vs each specific pitch, so high = he crushes that pitch."/>
        <Row k="Barrel%" v="Share of a hitter's batted balls that are 'barrels' — the elite exit-velo + launch-angle combo that produces the most damage (barrels become extra-base hits ~50%+ of the time). 6–8% is average, 12%+ is big power. For a pitcher, barrels ALLOWED = how much loud contact he gives up (lower is better)."/>
        <Row k="Hard-Hit%" v="Share of batted balls hit 95+ mph. Measures raw contact quality regardless of angle. ~35% average, 45%+ is elite. High hard-hit% + high barrel% = a dangerous bat."/>
        <Row k="Exit velo (EV)" v="How fast the ball leaves the bat, in mph. Average EV ~89 mph; 92+ is strong. Harder contact falls for hits more often and travels farther."/>
        <Row k="Launch angle" v="The vertical angle the ball comes off the bat. Roughly: <10° = grounders, 10–25° = line drives (the best for hits), 25–35° = fly balls / home-run window, 35°+ = pop-ups. The sweet spot for damage is ~12–25°."/>
        <Row k="ISO" v="Isolated Power = slugging % minus batting average — pure extra-base power, ignoring singles. .140 average, .200+ is real pop, under .100 is slap-hitter. Useful for HR / TB markets."/>
        <Row k="Pull%" v="How often a hitter pulls the ball to his side of the field. Pulled balls carry for home runs more often, so high pull% + a short pull-side porch = HR upside."/>
        <Row k="Whiff%" v="Swing-and-miss rate — swings that miss ÷ total swings. For a hitter, high whiff% = strikeout risk (bad for contact/hits markets). For a pitcher, high whiff% = swing-and-miss stuff (good for his strikeouts). On the pitch-matchup table it's the hitter's whiff% vs that pitch."/>
        <Row k="K% / BB%" v="Strikeout rate and walk rate — share of plate appearances ending in a K or a walk. For a hitter, low K% helps contact markets (HRR, 1+ Hits); for a pitcher, high K% drives the strikeouts market."/>
        <Row k="ERA" v="Earned Run Average — earned runs a pitcher allows per 9 innings. Lower = better. ~4.00 is league-average; sub-3.00 is excellent, 5.00+ is hittable."/>
        <Row k="WHIP" v="Walks + Hits per Inning Pitched — base-runners allowed per inning. ~1.30 average; under 1.10 is elite, 1.45+ means lots of traffic (good for the hitters facing him)."/>
        <Row k="K/9" v="Strikeouts per 9 innings — a pitcher's swing-and-miss volume. ~8.5 average; 11+ is a strikeout artist (target him for the K market)."/>
        <Row k="HR/9" v="Home runs allowed per 9 innings. ~1.2 average; 1.6+ is homer-prone (boosts every HR / HRR / TB bat he faces), under 0.9 suppresses power."/>
        <Row k="OPS (vs hand)" v="On-base % + Slugging % — a quick all-around hitting number. .750 average, .850+ strong. 'OPS allowed vs LHB/RHB' on a pitcher tells you which side of the plate hits him — the platoon edge behind a lot of our HRR spots."/>
        <Row k="Zone Fit" v="Our own 0–100 read of how well a hitter's hot zones line up with where THIS pitcher lives in the strike zone. High = the pitcher works right into the hitter's power. Built from Statcast zone data; fills in as the day's lineups post."/>

        <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginTop:20,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
          Probability is the chance to go over; the LINEBRKR Rating tells you how good the spot is to hit, and everything else (Edge/Value, Wall, Environment) tells you how well-priced and how favorable the matchup is. Lead with the conclusion, confirm with the Rating, then dig into the raw stats.
        </div>
      </div>
    </Sheet>
  )
}

// ── Legal: Terms of Service + Privacy Policy (in-app) ───────────────────────
// Full text lives here so the footer/checkout links open a native sheet instead
// of a dead /terms route. Keep in sync with the source docs in /legal.
const LEGAL_UPDATED = "July 7, 2026"

const TOS_MD = `These Terms of Service ("Terms") are a binding agreement between you and **LINEBRKR LLC**, a New Jersey limited liability company ("LINEBRKR," "we," "us"). They govern your use of the LINEBRKR website, app, and services (the "Service"). By using the Service, starting a trial, or subscribing, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Service.

## 1. Eligibility
You must be at least **21 years old** and legally permitted to view sports information where you are. You are responsible for knowing and following the laws of your jurisdiction. The Service does not accept, place, or facilitate any wager and is not a sportsbook.

## 2. What LINEBRKR Is — and Isn't
LINEBRKR is a sports research and data-analytics platform. It provides **information and research only** — not betting, gambling, investment, or financial advice.
- We do not guarantee any outcome, result, profit, or accuracy.
- Projections are statistical estimates and will frequently be wrong.
- Any "track record" is historical and does not predict future results.
- Every decision you make, including any wager with a third party, is yours alone, at your own risk.

## 3. Accounts and Access Codes
Access is granted by a paid subscription or an access code we issue. You are responsible for keeping your code and credentials confidential and for all activity under your account. Access codes are personal to you and may not be shared, sold, or transferred. We may suspend or revoke any code or account at any time, including for sharing, scraping, resale, or abuse.

## 4. Subscriptions, Trials, Billing & Cancellation
**Plans.** The Service is offered in tiers (Free, Rookie, Pro) with features and prices shown at purchase. We may change plans and pricing prospectively.
**Free trial.** A trial may require a valid payment method. **Unless you cancel before the trial ends, your paid subscription begins automatically and your payment method is charged.**
**Recurring billing.** Paid subscriptions bill on a recurring basis through our processor, **Stripe**, until cancelled.
**Cancellation.** You may cancel any time; it takes effect at the end of the current billing period and you keep access until then.
**Refunds.** Except where required by law, payments are non-refundable and we do not credit partial periods.
**Founder pricing.** Any Founder/promotional rate applies only as described at sign-up and only while your subscription stays continuously active.

## 5. Acceptable Use
You agree not to: use the Service if under 21 or where prohibited; scrape, crawl, or bulk-download data; resell or redistribute the Service or its data; share your access code; reverse engineer or attempt to access source code or models; interfere with or overload the Service; or use it for any unlawful purpose.

## 6. Intellectual Property
The Service — including its models, projections, ratings, software, design, and the LINEBRKR name and logo — is owned by LINEBRKR LLC. You receive a limited, personal, non-exclusive, non-transferable, revocable license for your own personal, non-commercial use. All other rights are reserved.

## 7. Third-Party Data & Services
The Service relies on third-party data and services (sports data, betting-odds data, Stripe, hosting). We are **not affiliated with, endorsed by, or sponsored by** Major League Baseball, any team, any sportsbook, or any odds provider. Third-party marks belong to their owners. We are not responsible for the accuracy or availability of third-party data.

## 8. Disclaimers
THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY PROJECTION WILL BE CORRECT. YOU USE THE SERVICE AT YOUR OWN RISK.

## 9. Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY LAW, LINEBRKR LLC AND ITS OWNERS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY LOST PROFITS, LOST WAGERS, OR GAMBLING LOSSES. OUR TOTAL LIABILITY FOR ALL CLAIMS WILL NOT EXCEED THE GREATER OF (A) WHAT YOU PAID US IN THE 3 MONTHS BEFORE THE CLAIM, OR (B) $50.

## 10. Indemnification
You agree to indemnify and hold harmless LINEBRKR LLC and its owners from any claims, damages, losses, and expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any law or third-party right.

## 11. Responsible Gaming
LINEBRKR is for entertainment and research. If you gamble with a third party, do so responsibly and only with money you can afford to lose. Must be 21+ and in a jurisdiction where wagering is legal to bet with a licensed operator. Problem gambling help: **call or text 1-800-GAMBLER** (1-800-426-2537).

## 12. Changes
We may modify or discontinue the Service or these Terms at any time. Material changes will be communicated through the Service or by email. Continued use after changes take effect means you accept them.

## 13. Termination
We may suspend or terminate your access at any time for any reason, including a violation of these Terms. Sections that by nature should survive (6, 8, 9, 10, 14) will survive.

## 14. Governing Law
These Terms are governed by the laws of the **State of New Jersey**. Any dispute will be resolved exclusively in the state or federal courts located in New Jersey. Any claim must be brought within one (1) year after it arises.

## 15. Contact
LINEBRKR LLC — linebrkrllc@gmail.com`

const PRIVACY_MD = `This Privacy Policy explains how **LINEBRKR LLC** ("LINEBRKR," "we," "us") collects, uses, and shares information when you use the LINEBRKR website, app, and services (the "Service"). By using the Service, you agree to this Policy.

## 1. Who We Are
LINEBRKR is a sports research and data-analytics platform. We provide information and statistical projections only. We are not a sportsbook and do not process wagers.

## 2. Information We Collect
**You provide:**
- Account info — an access code, and (for paid plans) the email tied to your subscription.
- Payment info — processed by **Stripe**. We do not collect or store your full card number; we receive limited billing details (email, subscription status, card brand/last four).
- Communications — messages you send us.
**Collected automatically:**
- Usage data — features viewed, actions taken, request counts, timestamps.
- Device/technical data — IP address, browser, and device info via standard logs.
- Local storage — we store your access code and preferences in your browser so you stay logged in.
We do not knowingly collect Social Security numbers, government IDs, or information from anyone under 21.

## 3. How We Use Information
To provide and operate the Service; authenticate access and manage subscriptions/billing; prevent fraud, scraping, and abuse and enforce our Terms; respond to support requests; analyze and improve the Service and our models; and comply with legal obligations. **We do not sell your personal information.**

## 4. How We Share Information
- **Service providers** under confidentiality — including **Stripe** (payments) and **Railway** (hosting). Data providers we consume generally receive requests from our servers, not your personal data.
- **Legal/safety** — if required by law or to protect our rights, users, or the public.
- **Business transfers** — information may transfer as part of a merger, acquisition, or sale of assets.

## 5. Cookies & Local Storage
We use browser local storage to keep you logged in and remember preferences. These are essential; clearing them means re-entering your access code.

## 6. Data Retention
We keep information while your account is active and as needed to provide the Service, comply with law, resolve disputes, and enforce agreements. Billing records may be kept longer where tax/accounting laws require.

## 7. Your Choices & Rights
Depending on where you live, you may have rights to access, correct, or delete your personal information. You may cancel your subscription anytime, request deletion (subject to legal retention), and clear local storage to remove your stored code. To exercise rights, email us below. **California/other U.S. state residents:** we do not sell personal information and will not discriminate against you for exercising your rights.

## 8. Security
We use reasonable measures to protect information (encryption in transit; card data handled only by Stripe). No method of transmission or storage is 100% secure, so we cannot guarantee absolute security.

## 9. Age Restriction
The Service is for adults **21 and older**. We do not knowingly collect information from anyone under 21. If you believe a minor provided us information, contact us and we will delete it.

## 10. Third-Party Links & Marks
The Service may reference third parties (e.g., sportsbooks, data sources). We are not responsible for their content or privacy practices and are not affiliated with or endorsed by MLB, any team, any sportsbook, or any odds provider.

## 11. Changes
We may update this Policy. The "Last updated" date shows the latest version; material changes will be communicated through the Service or by email. Continued use means you accept them.

## 12. Contact
LINEBRKR LLC — linebrkrllc@gmail.com`

// Tiny markdown renderer for the legal docs: ## headings, - bullets, **bold**,
// blank-line paragraphs. Deliberately minimal — the docs only use these forms.
function LegalBody({src}){
  const blocks = []; let para = []
  const flush = () => { if(para.length){ blocks.push({t:"p", s:para.join(" ")}); para = [] } }
  src.split("\n").forEach(raw=>{
    const line = raw.trim()
    if(!line){ flush(); return }
    if(line.startsWith("## ")){ flush(); blocks.push({t:"h", s:line.slice(3)}); return }
    if(line.startsWith("- ")){ flush(); blocks.push({t:"li", s:line.slice(2)}); return }
    para.push(line)
  })
  flush()
  const bold = s => s.split("**").map((p,i)=> i%2 ? <b key={i} style={{color:T.text}}>{p}</b> : p)
  return (
    <div>
      {blocks.map((b,i)=>{
        if(b.t==="h") return <div key={i} style={{fontSize:13,fontWeight:900,color:T.text,margin:"18px 0 6px"}}>{bold(b.s)}</div>
        if(b.t==="li") return <div key={i} style={{fontSize:12,color:T.muted2,lineHeight:1.5,margin:"0 0 5px",paddingLeft:14,position:"relative"}}><span style={{position:"absolute",left:0,color:T.green}}>•</span>{bold(b.s)}</div>
        return <div key={i} style={{fontSize:12,color:T.muted2,lineHeight:1.55,marginBottom:9}}>{bold(b.s)}</div>
      })}
    </div>
  )
}

// ── The Field Guide — in-app how-to, opened from the header 📖 Guide button ──
const _GUIDE_MARKETS = [
  ["1+ H","One hit or more","Safest","lo"],
  ["HRR","Hits + Runs + RBI ≥ 2 — cashes on a hit, a run, OR an RBI","High-floor","lo"],
  ["Total Bases","2+ total bases (a double, or two singles…)","Medium","mid"],
  ["2+ H","Two hits or more","Risky","hi"],
  ["HR","Home run anytime","Longshot","hi"],
  ["K","Pitcher strikeouts over the line","Pitcher-side","mid"],
]
const _GUIDE_DRIVERS = [
  ["1+ H / 2+ H","Top-of-order slot (1–5) · pitcher K/9 ≤ 8 · high OPS-against · hot L7 form"],
  ["HRR / TB","Top-of-order · hittable arm (high ERA/WHIP) · park ≥ 1.0 · TB adds barrel/hard-hit"],
  ["HR","Barrel% ≥ 10 · hard-hit% ≥ 42 · pitcher HR/9 ≥ 1.3 · park ≥ 1.05 · wind out"],
  ["K","Pitcher K/9 & SwStr% high · opponent lineup K-rate high · enough recent starts"],
]
const _GUIDE_TIPS = [
  ["Start with the 🏆 Top 15","On Home, the Top 15 is the flagship shortlist — the best-rated plays, spread across games so you're never stacking one lineup. The fastest read on the day."],
  ["Rank by Rating, sanity-check with Probability","Rating finds the best plays; Probability finds the safest. High on both is your green light. High probability but low Rating is often a volume trap."],
  ["Keep “Confirmed only” ON near game time","Once lineups post, Confirmed only drops anyone who isn't actually starting — the single best way to avoid betting a player who's out."],
  ["Don't stack one game","The top 10 caps at 2 players per team on purpose: a whole lineup vs one arm is correlated — if the pitcher settles, they all miss together. Spread across games."],
  ["Open the card — read the WHY","Every card breaks down the pitcher matchup (ERA / WHIP / K-9), park, weather, and pitch-type & zone fit. The number tells you what; the card tells you why."],
  ["Respect the arm","A soft-contact ace can sink a whole slate. If a hitter's ranked high vs a sub-3.00 ERA arm, the model fades them — a hittable arm (high WHIP/ERA) is where value lives."],
  ["Use the tiers as shorthand","Badges rank conviction: ELITE → TOP RATED → STRONG → MODERATE → WATCH → LONGSHOT. The tier already blends probability and matchup for you."],
  ["Demand more on the risky markets","HR and 2+H are low-hit by nature. Only take them at the highest Ratings — marginal longshots are how bankrolls leak."],
  ["Track everything in My Bets","Log wagers under Record → My Bets — even Pending, settle later. It keeps your real P&L by market and book, on your device. Betting without a log is guessing."],
  ["Check the Record tab — ours and yours","The Record tab shows the model's tracked hit rate by market — wins and losses, no cherry-picking. It's how you decide which markets to trust and which to fade."],
]

// ── Game analytics drawer helpers ────────────────────────────────────────────
// One expandable panel per game: pitching matchup, offense split, bats to attack,
// and weather. All values degrade to "—" so a thin slate never renders garbage.
const _gnum = (v,d=2,suf="")=> (v==null||Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(d)}${suf}`)
const _gpct = v => (v==null ? "—" : `${Math.round(v*100)}%`)
const _slg = v => (v==null ? "—" : Number(v).toFixed(3).replace(/^0/,""))   // .445-style, no leading 0
const _fmtml = v => (v==null||v==="" ? "—" : (v>0?`+${v}`:`${v}`))            // american odds w/ sign
const _fmtev = v => (v==null ? "—" : `${v>0?"+":""}${(v*100).toFixed(1)}% EV`)

// One market side row (moneyline or total) — model% vs no-vig%, EV, best-play highlight.
function MktRow({label, ml, model, novig, ev, best}){
  const evC = ev==null?T.muted2 : ev>0?T.green:T.red
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,background:best?T.greenDim:T.card,border:`1px solid ${best?T.green:T.border2}`,borderRadius:9,padding:"7px 10px"}}>
      {best && <span style={{fontSize:10}}>✅</span>}
      <span style={{fontSize:11.5,fontWeight:800,color:T.text,minWidth:44}}>{label}</span>
      {ml!=null && <span style={{fontSize:11,fontFamily:T.mono,fontWeight:800,color:T.muted2,minWidth:40}}>{_fmtml(ml)}</span>}
      <span style={{flex:1,fontSize:10,color:T.muted,fontFamily:T.mono}}>model <b style={{color:T.text}}>{_gpct(model)}</b> · mkt {_gpct(novig)}</span>
      <span style={{fontSize:11,fontWeight:800,fontFamily:T.mono,color:evC,whiteSpace:"nowrap"}}>{_fmtev(ev)}</span>
    </div>
  )
}

// Line movement — open → current, and whether the market is moving WITH the model.
function LineMovement({g}){
  const lm = g.line_movement
  if(!lm || !lm.snapshots || lm.snapshots < 2){
    return <div style={{fontSize:9.5,color:T.faint,fontStyle:"italic"}}>📈 Tracking line movement — a second odds snapshot posts one through the day.</div>
  }
  const mlH = lm.ml_home||{}, tot = lm.total||{}
  const mlMoved  = mlH.open!=null && mlH.cur!=null && mlH.open!==mlH.cur
  const totMoved = tot.open!=null && tot.cur!=null && tot.open!==tot.cur
  // more-negative ML = that side shortened (money came in on it)
  const mlSide   = mlMoved ? (mlH.cur < mlH.open ? g.home_team : g.away_team) : null
  const agrees   = mlSide && g.projected_winner && mlSide === g.projected_winner
  const Row = ({label, children}) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,fontSize:11,fontFamily:T.mono,padding:"2px 0"}}>
      <span style={{color:T.muted,fontFamily:T.sans,fontWeight:700,fontSize:10}}>{label}</span>
      <span>{children}</span>
    </div>
  )
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"8px 11px",marginTop:2}}>
      {mlMoved && (
        <Row label={`${g.home_team} ML`}>
          <span style={{color:T.muted2}}>{_fmtml(mlH.open)}</span>
          <span style={{color:T.faint,margin:"0 5px"}}>→</span>
          <span style={{color:T.text,fontWeight:800}}>{_fmtml(mlH.cur)}</span>
          <span style={{color:agrees?T.green:T.amber,marginLeft:6,fontWeight:800}}>{mlH.cur<mlH.open?"↑":"↓"} {mlSide}</span>
        </Row>
      )}
      {totMoved && (
        <Row label="Total">
          <span style={{color:T.muted2}}>{tot.open}</span>
          <span style={{color:T.faint,margin:"0 5px"}}>→</span>
          <span style={{color:T.text,fontWeight:800}}>{tot.cur}</span>
          <span style={{color:tot.cur>tot.open?T.green:T.red,marginLeft:6,fontWeight:800}}>{tot.cur>tot.open?"↑ over":"↓ under"}</span>
        </Row>
      )}
      {!mlMoved && !totMoved && <div style={{fontSize:10,color:T.muted}}>No movement yet · {lm.snapshots} snapshots</div>}
      {mlMoved && (
        <div style={{fontSize:9.5,marginTop:5,color:agrees?T.green:T.muted,lineHeight:1.4}}>
          {agrees ? `✅ Market steaming toward ${mlSide} — agrees with the model` : `↔ Market moving to ${mlSide}, model likes ${g.projected_winner}`}
        </div>
      )}
    </div>
  )
}

function MarketSection({g}){
  const m = g.market||{}, ml = m.ml||{}, tot = m.total
  const hasML = ml.book_home!=null || ml.book_away!=null
  if(!hasML && !tot) return (
    <div style={{fontSize:10.5,color:T.faint,fontStyle:"italic",padding:"2px 0 4px"}}>Live odds not posted yet — showing fair model numbers only.</div>
  )
  const H = ({children}) => <div style={{fontSize:9.5,letterSpacing:"0.12em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"2px 0 7px"}}>{children}</div>
  return (
    <div>
      {hasML && (<>
        <H>Moneyline — model vs no-vig market</H>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          <MktRow label={g.away_team} ml={ml.book_away} model={g.away_win_prob} novig={ml.novig_away} ev={ml.ev_away} best={ml.best_side==="away"}/>
          <MktRow label={g.home_team} ml={ml.book_home} model={g.home_win_prob} novig={ml.novig_home} ev={ml.ev_home} best={ml.best_side==="home"}/>
        </div>
      </>)}
      {tot && (<>
        <H>Total {tot.point!=null?`(${tot.point})`:""} — model {tot.model_total}</H>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          <MktRow label="Over" ml={tot.book_over} model={tot.over_prob} novig={tot.novig_over} ev={tot.over_ev} best={tot.best_side==="over"}/>
          <MktRow label="Under" ml={tot.book_under} model={tot.under_prob} novig={tot.novig_under} ev={tot.under_ev} best={tot.best_side==="under"}/>
        </div>
      </>)}
      {m.run_line && (<>
        <H>Run line (±1.5)</H>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          <MktRow label={`${g.away_team} ${m.run_line.away_point>0?"+":""}${m.run_line.away_point}`} ml={m.run_line.book_away} model={m.run_line.away_cover_prob} novig={m.run_line.novig_away} ev={m.run_line.away_ev} best={m.run_line.best_side==="away"}/>
          <MktRow label={`${g.home_team} ${m.run_line.home_point>0?"+":""}${m.run_line.home_point}`} ml={m.run_line.book_home} model={m.run_line.home_cover_prob} novig={m.run_line.novig_home} ev={m.run_line.home_ev} best={m.run_line.best_side==="home"}/>
        </div>
      </>)}
      {m.f5 && (<>
        <H>First 5 innings (F5) — proj total {m.f5.proj_total}</H>
        <div style={{display:"flex",gap:6}}>
          <div style={{flex:1,textAlign:"center",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"7px 4px"}}>
            <div style={{fontSize:13,fontWeight:900,fontFamily:T.mono,color:T.text}}>{_gpct(m.f5.away_lead)}</div>
            <div style={{fontSize:8.5,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>{g.away_team} lead</div>
          </div>
          <div style={{flex:1,textAlign:"center",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"7px 4px"}}>
            <div style={{fontSize:13,fontWeight:900,fontFamily:T.mono,color:T.muted2}}>{_gpct(m.f5.tie)}</div>
            <div style={{fontSize:8.5,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>Tie</div>
          </div>
          <div style={{flex:1,textAlign:"center",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"7px 4px"}}>
            <div style={{fontSize:13,fontWeight:900,fontFamily:T.mono,color:T.text}}>{_gpct(m.f5.home_lead)}</div>
            <div style={{fontSize:8.5,color:T.muted,fontWeight:700,textTransform:"uppercase"}}>{g.home_team} lead</div>
          </div>
        </div>
      </>)}
    </div>
  )
}

// A tiny label/value stat cell used across the drawer grids.
function GStat({label,val,tone}){
  return (
    <div style={{flex:1,minWidth:0,textAlign:"center"}}>
      <div style={{fontSize:13,fontWeight:900,fontFamily:T.mono,color:tone||T.text,lineHeight:1.1}}>{val}</div>
      <div style={{fontSize:8.5,letterSpacing:"0.05em",fontWeight:700,color:T.muted,textTransform:"uppercase",marginTop:2}}>{label}</div>
    </div>
  )
}

// One starter's deep line + the model's run-suppression multiplier.
function SPColumn({sp, suppresses}){
  const tagC  = sp.tag==="elite"?T.green : sp.tag==="hittable"?T.red : T.muted
  const tagBg = sp.tag==="elite"?T.greenDim : sp.tag==="hittable"?T.redDim : T.card
  const hand  = sp.throws ? (sp.throws==="L"?"LHP":"RHP") : ""
  const adjC  = sp.adj<0.92?T.green : sp.adj>1.08?T.red : T.muted2
  return (
    <div style={{flex:1,minWidth:0,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 8px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
        <div style={{fontSize:11.5,fontWeight:800,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sp.name}{hand&&<span style={{color:T.muted,fontWeight:600,fontSize:9.5}}> {hand}</span>}</div>
        {sp.tag!=="unknown" && <span style={{fontSize:8,fontWeight:800,letterSpacing:"0.05em",color:tagC,background:tagBg,border:`1px solid ${tagC}`,borderRadius:4,padding:"1px 5px",textTransform:"uppercase",whiteSpace:"nowrap"}}>{sp.tag}</span>}
      </div>
      <div style={{display:"flex",gap:4,marginTop:8}}>
        <GStat label="ERA" val={_gnum(sp.era,2)}/>
        <GStat label="WHIP" val={_gnum(sp.whip,2)}/>
        <GStat label="K/9" val={_gnum(sp.k9,1)}/>
      </div>
      <div style={{display:"flex",gap:4,marginTop:8}}>
        <GStat label="HR/9" val={_gnum(sp.hr9,2)}/>
        <GStat label="xwOBA" val={sp.xwoba_against==null?"—":_gnum(sp.xwoba_against,3).replace(/^0/,"")}/>
        <GStat label="SwStr" val={sp.swstr==null?"—":`${Math.round(sp.swstr*100)}%`}/>
      </div>
      {(sp.slg_vs_r!=null||sp.slg_vs_l!=null) && (
        <div style={{marginTop:7,fontSize:9,color:T.muted,textAlign:"center"}}>
          SLG allowed <b style={{color:sp.slg_vs_r&&sp.slg_vs_r>=0.44?T.red:T.muted2,fontFamily:T.mono}}>R {_slg(sp.slg_vs_r)}</b> <span style={{color:T.faint}}>·</span> <b style={{color:sp.slg_vs_l&&sp.slg_vs_l>=0.44?T.red:T.muted2,fontFamily:T.mono}}>L {_slg(sp.slg_vs_l)}</b>
        </div>
      )}
      {sp.arsenal&&sp.arsenal.length>0 && (
        <div style={{marginTop:7,display:"flex",flexWrap:"wrap",gap:3,justifyContent:"center"}}>
          {sp.arsenal.map((p,i)=>(
            <span key={i} style={{fontSize:8,fontWeight:700,color:T.muted2,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:5,padding:"1px 5px",whiteSpace:"nowrap"}}>{p.label} {Math.round(p.pct*100)}%</span>
          ))}
        </div>
      )}
      <div style={{marginTop:8,fontSize:9.5,color:T.muted,textAlign:"center",lineHeight:1.3}}>
        vs {suppresses}: <b style={{color:adjC,fontFamily:T.mono}}>{_gnum(sp.adj,2)}×</b> runs
      </div>
    </div>
  )
}

// One team's bullpen line — the ~40% of the game the relief corps covers.
function PenCell({team, pen}){
  if(!pen || pen.era==null) return <div style={{flex:1}}/>
  const tagC = pen.tag==="elite"?T.green : pen.tag==="shaky"?T.red : T.muted2
  return (
    <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"6px 9px"}}>
      <span style={{fontSize:9,fontWeight:800,color:T.muted,letterSpacing:"0.04em"}}>🔧 {team} PEN</span>
      <span style={{fontSize:11,fontWeight:800,fontFamily:T.mono,color:tagC}}>{_gnum(pen.era,2)} ERA</span>
    </div>
  )
}

// One team's offense summary.
function OffColumn({o}){
  const formC = o.form>1.03?T.green : o.form<0.97?T.red : T.muted2
  return (
    <div style={{flex:1,minWidth:0,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 8px"}}>
      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
        <span style={{fontSize:11.5,fontWeight:800,color:T.text}}>{o.team}</span>
        <span style={{fontSize:16,fontWeight:900,fontFamily:T.mono,color:T.green}}>{_gnum(o.proj_runs,1)}<span style={{fontSize:8,color:T.muted,fontWeight:700}}> R</span></span>
      </div>
      <div style={{display:"flex",gap:4,marginTop:8}}>
        <GStat label="SZN" val={_gnum(o.season_rpg,1)}/>
        <GStat label="L14" val={_gnum(o.l14_rpg,1)}/>
        <GStat label="L7" val={_gnum(o.l7_rpg,1)} tone={formC}/>
      </div>
      <div style={{display:"flex",gap:4,marginTop:8}}>
        <GStat label="xwOBA R/G" val={o.xwoba_rpg==null?"—":_gnum(o.xwoba_rpg,1)}/>
        <GStat label="Form" val={o.form==null?"—":`${o.form>1?"+":""}${Math.round((o.form-1)*100)}%`} tone={formC}/>
        <GStat label="GP" val={o.games??"—"}/>
      </div>
    </div>
  )
}

// Top HRR bats for a team, from the prop board.
function BatsColumn({team, bats}){
  return (
    <div style={{flex:1,minWidth:0}}>
      <div style={{fontSize:9.5,fontWeight:800,color:T.muted,letterSpacing:"0.06em",marginBottom:6,textTransform:"uppercase"}}>{team}</div>
      {(!bats||!bats.length) && <div style={{fontSize:10,color:T.faint,fontStyle:"italic"}}>no board yet</div>}
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {(bats||[]).map((b,i)=>(
          <div key={i} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"6px 8px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:6}}>
              <span style={{fontSize:11,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.name}</span>
              <span style={{fontSize:11.5,fontWeight:900,fontFamily:T.mono,color:b.prob>=0.65?T.green:T.muted2}}>{_gpct(b.prob)}</span>
            </div>
            <div style={{height:4,borderRadius:2,background:T.border2,marginTop:5,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${Math.round((b.prob||0)*100)}%`,background:b.prob>=0.65?T.green:T.greenMid,borderRadius:2}}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function GameDrawer({g}){
  const p = g.pitching||{}, o = g.offense||{}, b = g.bats||{}, w = g.weather
  const H = ({children}) => <div style={{fontSize:9.5,letterSpacing:"0.12em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"14px 0 7px"}}>{children}</div>
  // Weather → one plain-English line.
  let wxLine = null
  if (w){
    if (w.is_dome) wxLine = "Dome — weather neutral"
    else {
      const bits = []
      if (w.temp_f!=null) bits.push(`${Math.round(w.temp_f)}°F`)
      if (w.wind_mph) bits.push(`wind ${Math.round(w.wind_mph)}mph ${(w.wind_relative||"").replace(/_/g," ")}`.trim())
      if (w.conditions) bits.push(w.conditions)
      wxLine = bits.join(" · ")
    }
  }
  const wxImpactC = w&&w.hr_impact!=null ? (w.hr_impact>0.005?T.green:w.hr_impact<-0.005?T.red:T.muted2) : T.muted2
  return (
    <div style={{marginTop:10,paddingTop:2,borderTop:`1px solid ${T.border}`}}>
      <H>Market &amp; edge</H>
      <MarketSection g={g}/>

      <H>Line movement</H>
      <LineMovement g={g}/>

      <H>Pitching matchup</H>
      <div style={{display:"flex",gap:6}}>
        {p.away && <SPColumn sp={p.away} suppresses={g.home_team}/>}
        {p.home && <SPColumn sp={p.home} suppresses={g.away_team}/>}
      </div>
      {g.bullpen && (g.bullpen.away?.era!=null || g.bullpen.home?.era!=null) && (
        <div style={{display:"flex",gap:6,marginTop:6}}>
          <PenCell team={g.away_team} pen={g.bullpen.away}/>
          <PenCell team={g.home_team} pen={g.bullpen.home}/>
        </div>
      )}

      <H>Offense — projected runs · park {_gnum(o.park_factor,2)}×</H>
      <div style={{display:"flex",gap:6}}>
        {o.away && <OffColumn o={o.away}/>}
        {o.home && <OffColumn o={o.home}/>}
      </div>
      {(g.factors?.away_vs_ops!=null || g.factors?.home_vs_ops!=null) && (
        <div style={{display:"flex",justifyContent:"space-between",gap:8,marginTop:7,fontSize:9.5,color:T.muted,fontFamily:T.mono}}>
          <span>{g.away_team} vs {g.pitching?.home?.throws||"?"}HP <b style={{color:g.factors.away_hand_mult>=1.02?T.green:g.factors.away_hand_mult<=0.98?T.red:T.muted2}}>{g.factors.away_vs_ops!=null?_slg(g.factors.away_vs_ops):"—"}</b></span>
          {g.factors?.weather_mult!=null && g.factors.weather_mult!==1 && <span style={{color:g.factors.weather_mult>=1.02?T.green:g.factors.weather_mult<=0.98?T.red:T.muted2}}>wx {g.factors.weather_mult>1?"+":""}{Math.round((g.factors.weather_mult-1)*100)}%</span>}
          <span>{g.home_team} vs {g.pitching?.away?.throws||"?"}HP <b style={{color:g.factors.home_hand_mult>=1.02?T.green:g.factors.home_hand_mult<=0.98?T.red:T.muted2}}>{g.factors.home_vs_ops!=null?_slg(g.factors.home_vs_ops):"—"}</b></span>
        </div>
      )}

      <H>Bats to attack — top HRR on the board</H>
      <div style={{display:"flex",gap:10}}>
        <BatsColumn team={g.away_team} bats={b.away}/>
        <BatsColumn team={g.home_team} bats={b.home}/>
      </div>

      {wxLine && (<>
        <H>Weather</H>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
          <span style={{fontSize:11,color:T.muted2}}>{wxLine}</span>
          {w.hr_impact!=null && !w.is_dome && <span style={{fontSize:11,fontWeight:800,fontFamily:T.mono,color:wxImpactC,whiteSpace:"nowrap"}}>{w.hr_impact>0?"+":""}{Math.round(w.hr_impact*100)}% HR</span>}
        </div>
      </>)}
    </div>
  )
}

// ── "In testing" flags — markets/models not yet validated against settled results ──
function TestingTag(){
  return <span style={{fontSize:8,fontWeight:800,letterSpacing:"0.06em",color:T.amber,background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:4,padding:"1px 5px",textTransform:"uppercase",whiteSpace:"nowrap"}}>🧪 Testing</span>
}
function TestingNote({what}){
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:10,padding:"9px 11px",marginBottom:12}}>
      <span style={{fontSize:13}}>🧪</span>
      <span style={{fontSize:10.5,color:T.amber,lineHeight:1.4,fontWeight:600}}>{what} is <b>in testing</b> — not yet validated against settled results. Use it for research, not as a proven edge.</span>
    </div>
  )
}

// Game-model calibration + launch readiness — projected vs actual win rate by confidence
// band. The 70%+ band is the launch gate (the old model went 0-for-4 there). Same
// by-band view as the K/HR cards, framed as "is the model ready to come off testing."
function GameModelCalibrationCard(){
  const { data } = useFetch(()=>getGameOutcomeRecord(60, 5), [])
  const sc = data?.scorecard || {}
  const bands = Array.isArray(sc.calibration) ? sc.calibration : []
  const pct = v => v==null?"—":`${Math.round(v*100)}%`
  const gp  = v => v==null?"—":`${v>0?"+":""}${Math.round(v*100)}%`
  if(!bands.length) return (
    <div style={{fontSize:10.5,color:T.faint,fontStyle:"italic",margin:"2px 0 12px"}}>📊 Calibration builds as games settle — the new model's record starts today.</div>
  )
  // Launch gate: the highest confidence band (e.g. "70-100%").
  const top = bands[bands.length-1] || {}
  const gap = (top.actual!=null && top.predicted!=null) ? top.actual-top.predicted : null
  const ready = (top.n||0) >= 10 && gap!=null && gap >= -0.10
  const verdict = (top.n||0) < 10
    ? `⏳ Building — top band n=${top.n||0}, need ~10+ settled to judge`
    : ready
      ? `✅ Top band calibrated (${pct(top.actual)} actual vs ${pct(top.predicted)} predicted) — launch-ready`
      : `🔴 Overconfident — ${pct(top.predicted)} picks hitting ${pct(top.actual)}. Not ready.`
  return (
    <div style={{background:T.card,border:`1px solid ${ready?T.green:(top.n||0)<10?T.border2:T.red}`,borderRadius:12,padding:"12px 13px",margin:"4px 0 14px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",color:T.text,textTransform:"uppercase"}}>Model calibration · launch gate</span>
        {sc.record && <span style={{fontSize:11,fontFamily:T.mono,fontWeight:800,color:T.muted2}}>{sc.record}{sc.win_pct!=null?` · ${pct(sc.win_pct)}`:""}{sc.brier!=null?` · Brier ${sc.brier}`:""}</span>}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {bands.map((b,i)=>{ const g=(b.actual!=null&&b.predicted!=null)?b.actual-b.predicted:null
          const over=g!=null&&g<-0.08; const good=g!=null&&Math.abs(g)<=0.08; const isTop=i===bands.length-1
          const c=over?T.red:good?T.green:T.muted2
          return (
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:11,fontFamily:T.mono,background:isTop?(over?T.redDim:good?T.greenDim:T.card2):"transparent",border:isTop?`1px solid ${c}`:"none",borderRadius:7,padding:isTop?"5px 8px":"2px 8px"}}>
            <span style={{width:64,color:isTop?T.text:T.muted,fontWeight:isTop?800:400}}>{b.band}</span>
            <span style={{color:T.faint,width:36}}>n={b.n}</span>
            <span style={{flex:1,color:T.muted}}>pred <b style={{color:T.muted2}}>{pct(b.predicted)}</b> → act <b style={{color:c}}>{pct(b.actual)}</b></span>
            <span style={{color:c,fontWeight:800,width:46,textAlign:"right"}}>{gp(g)}</span>
          </div>
        )})}
      </div>
      <div style={{fontSize:10.5,marginTop:9,lineHeight:1.4,color:ready?T.green:(top.n||0)<10?T.muted:T.red,fontWeight:600}}>{verdict}</div>
    </div>
  )
}

function GameModelBacktestCard(){
  const [run, setRun] = useState(false)
  const { data, loading, error } = useFetch(()=> run ? getGameModelBacktest({}) : Promise.resolve(null), [run])
  const pct = v => v==null?"—":`${Math.round(v*100)}%`
  const gp  = v => v==null?"—":`${v>0?"+":""}${Math.round(v*100)}%`
  if(!run) return (
    <button onClick={()=>setRun(true)} style={{background:"transparent",border:`1px solid ${T.border2}`,borderRadius:9,padding:"7px 11px",color:T.muted2,fontSize:10.5,fontWeight:700,cursor:"pointer",margin:"0 0 14px"}}>
      ▶ Run season backtest — re-grade this model over every 2026 final
    </button>
  )
  if(loading) return <div style={{fontSize:10.5,color:T.muted,fontStyle:"italic",margin:"2px 0 14px"}}>Re-running the model over the full season…</div>
  if(error) return <div style={{fontSize:10.5,color:T.red,margin:"2px 0 14px"}}>Backtest failed: {String(error.message||error)}</div>
  if(data?.error) return <div style={{fontSize:10,color:T.red,margin:"2px 0 14px",fontFamily:T.mono,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>Backtest error: {String(data.error)}</div>
  if(!data || !data.n) return <div style={{fontSize:10.5,color:T.muted,margin:"2px 0 14px"}}>No gradeable finals in range yet{data?.schedule_rows!=null?` (${data.schedule_rows} schedule rows scanned)`:""}.</div>
  const bands = Array.isArray(data.calibration)?data.calibration:[]
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 13px",margin:"4px 0 14px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.08em",color:T.text,textTransform:"uppercase"}}>Season backtest · full 2026</span>
        <span style={{fontSize:11,fontFamily:T.mono,fontWeight:800,color:T.muted2}}>{data.record} · {pct(data.win_pct)} · Brier {data.brier} · n={data.n}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {bands.map((b,i)=>{ const g=(b.actual!=null&&b.predicted!=null)?b.actual-b.predicted:null
          const over=g!=null&&g<-0.08; const good=g!=null&&Math.abs(g)<=0.08
          const c=over?T.red:good?T.green:T.muted2
          return (
          <div key={i} style={{display:"flex",alignItems:"center",gap:8,fontSize:11,fontFamily:T.mono,padding:"2px 8px"}}>
            <span style={{width:64,color:T.muted}}>{b.band}</span>
            <span style={{color:T.faint,width:36}}>n={b.n}</span>
            <span style={{flex:1,color:T.muted}}>pred <b style={{color:T.muted2}}>{pct(b.predicted)}</b> → act <b style={{color:c}}>{pct(b.actual)}</b></span>
            <span style={{color:c,fontWeight:800,width:46,textAlign:"right"}}>{gp(g)}</span>
          </div>
        )})}
      </div>
      {data.caveat && <div style={{fontSize:9.5,marginTop:9,lineHeight:1.45,color:T.faint,fontStyle:"italic"}}>⚠ {data.caveat}</div>}
    </div>
  )
}

function GameModelToday(){
  const { data, loading } = useFetch(()=>getGamesModel(), [])
  const games = Array.isArray(data?.outcomes) ? data.outcomes : []
  const [open, setOpen] = useState(null)
  const TIER = { whale:{c:T.green,l:"WHALE"}, strong:{c:T.green,l:"STRONG"}, lean:{c:T.amber,l:"LEAN"}, pass:{c:T.muted,l:"TOSS-UP"} }
  const pct = v => (v==null ? "—" : `${Math.round(v*100)}%`)
  const ml = v => (v==null||v==="" ? "" : (v>0?`+${v}`:`${v}`))
  return (
    <div style={{padding:"2px 0 22px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.1em",color:T.text,textTransform:"uppercase"}}>Moneyline model</span>
        <TestingTag/>
      </div>
      <TestingNote what="The game / moneyline model"/>
      <GameModelCalibrationCard/>
      <GameModelBacktestCard/>
      <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Model win probability for tonight's games — offense × starter × park × home field × form → projected runs → win %. Tap a game for the full pitching, offense, bats &amp; weather read. Research only, not betting advice.</div>
      {loading && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px"}}>Running the game model…</div>}
      {!loading && !games.length && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px",lineHeight:1.6}}>No game projections yet — probable pitchers may not be posted for today's slate.</div>}
      <div style={{display:"flex",flexDirection:"column",gap:9,marginTop:12}}>
        {games.map((g,i)=>{ const t=TIER[g.tier]||TIER.pass; const isOpen=open===i; return (
          <div key={i} style={{background:T.card,border:`1px solid ${g.tier==="whale"||g.tier==="strong"?T.greenMid:T.border}`,borderRadius:12,padding:"11px 13px"}}>
            <div onClick={()=>setOpen(isOpen?null:i)} style={{cursor:"pointer"}} role="button" aria-expanded={isOpen}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <div style={{fontSize:13.5,fontWeight:800,color:T.text}}>{g.away_team} <span style={{color:T.muted,fontWeight:600}}>@</span> {g.home_team}</div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.06em",color:t.c,border:`1px solid ${t.c}`,borderRadius:5,padding:"2px 7px"}}>{t.l}</span>
                  <span style={{fontSize:11,color:T.muted,transform:isOpen?"rotate(180deg)":"none",transition:"transform .18s",display:"inline-block"}}>▾</span>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"baseline",gap:8,marginTop:8}}>
                <span style={{fontSize:20,fontWeight:900,color:T.green}}>{g.projected_winner}</span>
                <span style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:T.text}}>{pct(g.win_prob)}</span>
                {g.fair_ml!=null && <span style={{fontSize:11,color:T.muted,fontFamily:T.mono}}>fair {ml(g.fair_ml)}</span>}
              </div>
              <div style={{fontSize:11.5,color:T.muted,fontFamily:T.mono,marginTop:5}}>
                proj {g.away_team} {g.away_proj_runs} — {g.home_proj_runs} {g.home_team} · total {g.total_proj}
              </div>
              {g.market?.ml?.best_ev!=null ? (
                <div style={{display:"inline-flex",alignItems:"center",gap:6,marginTop:7,background:T.greenDim,border:`1px solid ${T.green}`,borderRadius:7,padding:"3px 9px"}}>
                  <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.06em",color:T.green,textTransform:"uppercase"}}>Edge</span>
                  <span style={{fontSize:11,fontWeight:800,color:T.text}}>{g.market.ml.best_team} {_fmtml(g.market.ml.best_ml)}</span>
                  <span style={{fontSize:11,fontWeight:800,fontFamily:T.mono,color:T.green}}>{_fmtev(g.market.ml.best_ev)}</span>
                </div>
              ) : (g.market?.ml?.book_home!=null && (
                <div style={{fontSize:10,color:T.faint,marginTop:7,fontStyle:"italic"}}>no moneyline edge — model agrees with the market</div>
              ))}
              {(g.away_pitcher||g.home_pitcher) && <div style={{fontSize:10.5,color:T.muted,marginTop:6}}>{g.away_pitcher||"TBD"} vs {g.home_pitcher||"TBD"}</div>}
              {g.why && <div style={{fontSize:11,color:T.muted2,marginTop:6,lineHeight:1.45,fontStyle:"italic"}}>{g.why}</div>}
              {!isOpen && <div style={{fontSize:9.5,color:T.faint,marginTop:7,letterSpacing:"0.04em"}}>tap for pitching · offense · bats · weather ▾</div>}
            </div>
            {isOpen && <GameDrawer g={g}/>}
          </div>
        )})}
      </div>
    </div>
  )
}

// Ongoing HR-model validation — actual HR rate by Barrel% and by projected prob, from
// settled results. Grows every night. Proves (or breaks) the barrel-first thesis on live data.
function HrValidationCard(){
  const { data, loading } = useFetch(()=>getHrValidation(60), [])
  const pct = v => v==null?"—":`${Math.round(v*100)}%`
  const rlabel = r => { const f=v=>`${Math.round(v*100)}%`
    return r.lo==null?`<${f(r.hi)}` : r.hi==null?`${f(r.lo)}+` : `${f(r.lo)}–${f(r.hi)}` }
  const base = data?.base_rate
  const Bars = ({title, rows, note}) => {
    if(!Array.isArray(rows)||!rows.length) return null
    const mx = Math.max(...rows.map(r=>r.hr_rate), base||0, 0.01)
    return (
      <>
        <div style={{fontSize:11,letterSpacing:"0.1em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"16px 0 4px"}}>{title}</div>
        {note && <div style={{fontSize:10,color:T.faint,marginBottom:8}}>{note}</div>}
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {rows.map((r,i)=>{ const above=base!=null&&r.hr_rate>=base; const c=above?T.green:T.muted2; return (
            <div key={i} style={{display:"flex",alignItems:"center",gap:9}}>
              <span style={{width:62,fontSize:11,fontFamily:T.mono,color:T.muted2,textAlign:"right"}}>{rlabel(r)}</span>
              <div style={{flex:1,height:16,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,overflow:"hidden",position:"relative"}}>
                <div style={{height:"100%",width:`${Math.round(r.hr_rate/mx*100)}%`,background:above?T.greenMid:T.border2,borderRadius:8}}/>
              </div>
              <span style={{width:74,fontSize:11.5,fontFamily:T.mono,fontWeight:800,color:c}}>{pct(r.hr_rate)}<span style={{color:T.faint,fontWeight:600,fontSize:9.5}}> n={r.n}</span></span>
            </div>
          )})}
        </div>
      </>
    )
  }
  return (
    <div style={{padding:"2px 0 30px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <span style={{fontSize:11,fontWeight:800,letterSpacing:"0.1em",color:T.text,textTransform:"uppercase"}}>HR model validation</span>
      </div>
      <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Actual HR rate of settled picks, bucketed by Barrel% and by the model's projected probability. Accumulates every night — the barrel-first thesis, checked on real results.</div>
      {loading && <div style={{color:T.muted,fontSize:12.5,padding:"18px 4px"}}>Loading validation…</div>}
      {!loading && (!data || !data.n) && <div style={{color:T.muted,fontSize:12.5,padding:"18px 4px",lineHeight:1.6}}>No settled HR results yet — this fills in after the nightly settle grades a slate.</div>}
      {!loading && data && data.n>0 && (<>
        <div style={{display:"flex",gap:8,margin:"12px 0 2px"}}>
          <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
            <div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:T.text}}>{data.n}</div>
            <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Settled picks</div>
          </div>
          <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
            <div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:T.green}}>{pct(data.base_rate)}</div>
            <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Base HR rate</div>
          </div>
        </div>
        <Bars title="By Barrel% — the thesis" rows={data.by_barrel} note="Elite barrel should homer well above the base rate; low barrel below."/>
        <Bars title="By projected prob — calibration" rows={data.by_prob} note="Higher model probability should mean a higher actual HR rate."/>
        <div style={{fontSize:10,color:T.faint,marginTop:12,lineHeight:1.4}}>Green = above the slate base rate. Bars scale to the top bucket. Research only · 21+.</div>
      </>)}
    </div>
  )
}

// Per-market calibration — projected vs actual hit rate per market from settled
// results, plus the overconfidence gap. Single-leg only; no fabricated parlay math.
function MarketCalibrationCard(){
  // The board SHOWS the calibrated number, so by default we grade THAT (honest: it's the
  // number users actually see). 'raw' grades the untouched model — a pre-calibration
  // diagnostic, Admin-only. The old panel graded raw and flashed "OVERCONFIDENT" in red on
  // markets whose displayed number was already shaded down — misleading to a user.
  const isAdmin = (()=>{ try { return !!(localStorage.getItem("brkr_admin")||localStorage.getItem("brkr_admin_reveal")) } catch { return false } })()
  const [basis,setBasis] = useState("calibrated")
  const { data, loading } = useFetch(()=>getMarketCalibration(90, basis), [basis])
  const raw = basis==="raw"
  const pct = v => v==null?"—":`${Math.round(v*100)}%`
  const gp  = v => v==null?"—":`${v>0?"+":""}${Math.round(v*100)}%`
  const AMBER="#e0b24a", AMBER_DIM="rgba(224,178,74,0.14)"
  // Badge wording softens on the calibrated (user) view: a residual shade reads as "runs
  // hot — shaded", not an alarming "OVERCONFIDENT". Raw (Admin) keeps the blunt label.
  const badge = g => {
    if(g>0.02)  return {txt:"BEATS PROJ", c:T.green, bg:T.greenDim, bd:T.green}
    if(raw && g<-0.01) return {txt:"OVERCONFIDENT", c:T.red, bg:T.redDim, bd:T.red}
    if(!raw && g<-0.03) return {txt:"RUNS HOT · SHADED", c:AMBER, bg:AMBER_DIM, bd:AMBER}
    return {txt:"CALIBRATED", c:T.muted2, bg:T.card, bd:T.border2}
  }
  const markets = (data?.markets ? Object.values(data.markets) : [])
    .slice().sort((a,b)=>b.overall_gap - a.overall_gap)
  const topBeat = markets.find(m => m.overall_gap >= 0.02 && m.n >= 20) || null
  return (
    <div style={{padding:"2px 0 30px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
        <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.1em",color:T.text,textTransform:"uppercase"}}>Market calibration</div>
        {isAdmin && <div style={{display:"flex",gap:4,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:2}}>
          {["calibrated","raw"].map(b=>(
            <button key={b} onClick={()=>setBasis(b)} style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.03em",textTransform:"uppercase",border:"none",borderRadius:6,padding:"3px 8px",cursor:"pointer",
              background: basis===b?T.greenDim:"transparent", color: basis===b?T.green:T.muted}}>{b==="raw"?"Raw model":"Shown"}</button>
          ))}
        </div>}
      </div>
      <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>{raw
        ? <><b style={{color:AMBER}}>Raw model output</b>, before calibration — internal diagnostic. The board shows the <i>calibrated</i> number (the “Shown” view), which is what users are graded on.</>
        : <>The number the board actually <b style={{color:T.text}}>shows you</b> (calibrated) vs the <b style={{color:T.text}}>actual</b> hit rate, from settled results. This is the number you see when you bet.</>}</div>
      {loading && <div style={{color:T.muted,fontSize:12.5,padding:"18px 4px"}}>Loading calibration…</div>}
      {!loading && !markets.length && <div style={{color:T.muted,fontSize:12.5,padding:"18px 4px",lineHeight:1.6}}>No settled results yet — fills in after the nightly settle grades a slate.</div>}
      {topBeat && <div style={{marginTop:12,padding:"12px 14px",background:T.greenDim,border:`1px solid ${T.green}`,borderRadius:11}}>
        <div style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.08em",color:T.green,textTransform:"uppercase",marginBottom:4}}>📈 Running ahead of the model</div>
        <div style={{fontSize:12.5,color:T.text,lineHeight:1.5}}><b>{topBeat.label}</b> is landing <b style={{color:T.green}}>{pct(topBeat.hit_rate)}</b> vs a projected {pct(topBeat.predicted)} across {topBeat.n} settled results — <b style={{color:T.green}}>{gp(topBeat.overall_gap)}</b> better than expected. A market the model is currently <i>under</i>-rating.</div>
      </div>}
      <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:12}}>
        {markets.map((m,i)=>{ const b=badge(m.overall_gap)
          return (
          <div key={i} style={{background:T.card,border:`1px solid ${b.bd}`,borderRadius:11,padding:"11px 13px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
              <span style={{fontSize:13.5,fontWeight:800,color:T.text}}>{m.label} <span style={{fontSize:10,color:T.faint,fontWeight:600}}>· n={m.n}</span></span>
              <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.04em",color:b.c,background:b.bg,border:`1px solid ${b.bd}`,borderRadius:5,padding:"2px 7px"}}>{b.txt} {gp(m.overall_gap)}</span>
            </div>
            <div style={{display:"flex",alignItems:"baseline",gap:8,marginTop:7,fontFamily:T.mono,fontSize:13}}>
              <span style={{color:T.muted}}>{raw?"raw":"shown"} <b style={{color:T.muted2}}>{pct(m.predicted)}</b></span>
              <span style={{color:T.faint}}>→</span>
              <span style={{color:T.muted}}>actual <b style={{color:b.c,fontWeight:900}}>{pct(m.hit_rate)}</b></span>
            </div>
          </div>
        )})}
      </div>
      {markets.length>0 && <div style={{fontSize:11.5,color:T.muted,marginTop:14,lineHeight:1.5,padding:"12px 14px",background:T.card,border:`1px solid ${T.border2}`,borderRadius:11}}>Every leg's small miss <b style={{color:T.text}}>multiplies</b> in a parlay — which is why single bets land more consistently than combos. Accumulates nightly. Research only · 21+.</div>}
    </div>
  )
}

function GameOutcomePanel(){
  const { data, loading } = useFetch(()=>getGameOutcomeRecord(30, 25), [])
  const sc = data?.scorecard || {}
  const recent = Array.isArray(data?.recent) ? data.recent : []
  const hasData = sc && sc.n > 0
  // Brier: mean((prob − outcome)²). 0 = perfect, 0.25 = a coin flip. Lower is better.
  const brierColor = b => (b==null ? T.muted : b <= 0.20 ? T.green : b <= 0.25 ? T.amber : T.red)
  const pct = v => (v==null ? "—" : `${Math.round(v*100)}%`)
  return (
      <div style={{padding:"2px 0 40px"}}>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Every pre-game win probability, frozen at lock and graded against the final score. <b style={{color:T.text}}>Brier</b> scores calibration — lower is better (0 = perfect, 0.25 = a coin flip).</div>
        {sc.provisional && sc.note && (
          <div style={{marginTop:12,background:T.amberDim||T.card,border:`1px solid ${T.amber}`,borderRadius:9,padding:"9px 12px",fontSize:11.5,color:T.amber,lineHeight:1.5,fontFamily:T.mono}}>⚙︎ {sc.note}</div>
        )}
        {loading && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px"}}>Loading record…</div>}
        {!loading && !hasData && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px",lineHeight:1.6}}>No settled games yet. The tracker fills in after the nightly settle grades a slate of games — check back after the next 2 AM run.</div>}
        {hasData && (<>
          <div style={{display:"flex",gap:8,marginTop:14}}>
            <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
              <div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:T.text}}>{sc.record||"—"}</div>
              <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Record</div>
            </div>
            <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
              <div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:T.green}}>{pct(sc.win_pct)}</div>
              <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Win %</div>
            </div>
            <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
              <div style={{fontSize:18,fontWeight:900,fontFamily:T.mono,color:brierColor(sc.brier)}}>{sc.brier??"—"}</div>
              <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Brier</div>
            </div>
          </div>
          {Array.isArray(sc.calibration) && sc.calibration.length>0 && (<>
            <div style={{fontSize:11,letterSpacing:"0.12em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"20px 0 8px"}}>Calibration — predicted vs actual</div>
            <div style={{display:"flex",flexDirection:"column",gap:5}}>
              {sc.calibration.map((c,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"7px 11px",fontFamily:T.mono,fontSize:12}}>
                  <span style={{width:64,color:T.muted}}>{c.band}</span>
                  <span style={{color:T.muted,fontSize:11}}>n={c.n}</span>
                  <span style={{flex:1,textAlign:"right",color:T.muted}}>pred <b style={{color:T.text}}>{pct(c.predicted)}</b></span>
                  <span style={{color:Math.abs((c.actual||0)-(c.predicted||0))<=0.06?T.green:T.amber}}>actual <b>{pct(c.actual)}</b></span>
                </div>
              ))}
            </div>
          </>)}
          <div style={{fontSize:11,letterSpacing:"0.12em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"20px 0 8px"}}>Recent games</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {recent.map((g,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,background:T.card,border:`1px solid ${g.correct?T.greenMid:T.border}`,borderRadius:9,padding:"8px 11px"}}>
                <span style={{fontSize:15,width:18}}>{g.correct?"✅":"❌"}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:T.text}}>{g.away} @ {g.home} <span style={{color:T.muted,fontFamily:T.mono,fontWeight:600}}>{g.score}</span></div>
                  <div style={{fontSize:10.5,color:T.muted,marginTop:2}}>picked <b style={{color:T.text}}>{g.projected}</b> @ {pct(g.win_prob)} · {g.date}</div>
                </div>
              </div>
            ))}
          </div>
        </>)}
      </div>
  )
}

function PitcherCard({name, onClose}){
  const { data, loading } = useFetch(()=>getPitcherIntel(name), [name])
  const s = data?.season || {}
  const sp = data?.splits, sc = data?.statcast
  const recent = Array.isArray(data?.recent) ? data.recent : []
  const notFound = data && data.status === "not_found"
  const num = (v,d=2)=> (v==null ? "—" : Number(v).toFixed(d))
  const TR = { up:{c:T.green,bg:T.greenDim,bd:T.greenMid,ic:"🔥"}, down:{c:T.red,bg:T.redDim,bd:T.red,ic:"❄️"}, flat:{c:T.muted2,bg:T.card,bd:T.border2,ic:"➖"} }
  const tr = TR[data?.trend?.dir] || TR.flat
  const Tile = ({label,val}) => (
    <div style={{flex:1,minWidth:0,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 6px",textAlign:"center"}}>
      <div style={{fontSize:16,fontWeight:900,fontFamily:T.mono,color:T.text}}>{val}</div>
      <div style={{fontSize:9,letterSpacing:"0.06em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>{label}</div>
    </div>
  )
  const H = ({children}) => <div style={{fontSize:11,letterSpacing:"0.12em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"20px 0 8px"}}>{children}</div>
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"6px 14px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:6}}>
          <div style={{fontSize:10,letterSpacing:"0.22em",fontWeight:800,color:T.green,textTransform:"uppercase"}}>Pitcher Intel</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12,marginTop:6}}>
          <Headshot mlbamId={data?.mlbam_id} name={data?.name||name} size={48}/>
          <div style={{minWidth:0}}>
            <div style={{fontSize:23,fontWeight:900,letterSpacing:"-0.02em"}}>{data?.name || name}</div>
            {data && !notFound && <div style={{fontSize:12,color:T.muted,marginTop:3}}>{data.team} · {data.throws==="L"?"LHP":"RHP"}</div>}
          </div>
        </div>
        {data && !notFound && data.trend && (
          <div style={{marginTop:13,display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:13,background:tr.bg,border:`1.5px solid ${tr.bd}`}}>
            <span style={{fontSize:26,lineHeight:1}}>{tr.ic}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:16,fontWeight:900,color:tr.c,letterSpacing:"-0.01em"}}>{data.trend.label}</div>
              <div style={{fontSize:11,color:T.muted,marginTop:2,fontFamily:T.mono}}>
                {data.trend.recent_era!=null ? `L3: ${data.trend.recent_era} ERA` : "recent form"}{data.trend.recent_k9!=null?` · ${data.trend.recent_k9} K/9`:""}{data.trend.era_gap!=null?` · ${data.trend.era_gap>0?"−":"+"}${Math.abs(data.trend.era_gap)} vs season`:""}
              </div>
            </div>
          </div>
        )}
        {loading && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px"}}>Loading intel…</div>}
        {notFound && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px"}}>No stored data for this pitcher yet.</div>}
        {data && !notFound && (<>
          <H>Season</H>
          <div style={{display:"flex",gap:7}}>
            <Tile label="ERA" val={num(s.era)}/><Tile label="WHIP" val={num(s.whip)}/>
            <Tile label="K/9" val={num(s.k9,1)}/><Tile label="HR/9" val={num(s.hr9)}/>
          </div>
          <div style={{display:"flex",gap:7,marginTop:7}}>
            <Tile label="FIP" val={num(s.fip)}/><Tile label="IP" val={num(s.ip,1)}/>
            <Tile label="GS" val={s.gs??"—"}/><Tile label="Avg IP" val={num(s.avg_ip,1)}/>
          </div>
          {Array.isArray(data.seasons) && data.seasons.length>1 && (<>
            <H>By season</H>
            <div style={{overflowX:"auto"}}>
              <div style={{display:"flex",flexDirection:"column",gap:4,minWidth:300}}>
                <div style={{display:"flex",gap:8,fontSize:9.5,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.05em",padding:"0 10px"}}>
                  <span style={{width:38}}>Year</span><span style={{width:32}}>Tm</span>
                  <span style={{flex:1,textAlign:"right"}}>ERA</span><span style={{flex:1,textAlign:"right"}}>WHIP</span>
                  <span style={{flex:1,textAlign:"right"}}>K9</span><span style={{flex:1,textAlign:"right"}}>HR9</span>
                  <span style={{flex:1,textAlign:"right"}}>IP</span><span style={{width:28,textAlign:"right"}}>GS</span>
                </div>
                {data.seasons.map((y,i)=>(
                  <div key={y.season} style={{display:"flex",gap:8,alignItems:"center",background:i===0?T.greenDim:T.card,border:`1px solid ${i===0?T.greenMid:T.border}`,borderRadius:8,padding:"7px 10px",fontFamily:T.mono,fontSize:12}}>
                    <span style={{width:38,fontWeight:800,color:i===0?T.green:T.text}}>{y.season}</span>
                    <span style={{width:32,color:T.muted,fontSize:10.5}}>{y.team}</span>
                    <span style={{flex:1,textAlign:"right",color:T.text}}>{num(y.era)}</span>
                    <span style={{flex:1,textAlign:"right",color:T.text}}>{num(y.whip)}</span>
                    <span style={{flex:1,textAlign:"right",color:T.text}}>{num(y.k9,1)}</span>
                    <span style={{flex:1,textAlign:"right",color:T.muted}}>{num(y.hr9)}</span>
                    <span style={{flex:1,textAlign:"right",color:T.muted}}>{num(y.ip,1)}</span>
                    <span style={{width:28,textAlign:"right",color:T.muted}}>{y.gs}</span>
                  </div>
                ))}
              </div>
            </div>
          </>)}
          {sp && (<>
            <H>Splits</H>
            <div style={{display:"flex",gap:7}}>
              <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 11px"}}>
                <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Home / Away ERA</div>
                <div style={{display:"flex",justifyContent:"space-between",fontFamily:T.mono,fontSize:13}}>
                  <span>🏠 <b style={{color:T.text}}>{num(sp.home_era)}</b></span>
                  <span>✈️ <b style={{color:T.text}}>{num(sp.away_era)}</b></span>
                </div>
              </div>
            </div>
            <div style={{display:"flex",gap:7,marginTop:7}}>
              {[["vs LHB",sp.vsL],["vs RHB",sp.vsR]].map(([lbl,d])=>(
                <div key={lbl} style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 11px"}}>
                  <div style={{fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>{lbl}</div>
                  <div style={{fontFamily:T.mono,fontSize:12,color:T.muted,lineHeight:1.7}}>
                    <div>AVG <b style={{color:T.text}}>{num(d?.avg,3)}</b></div>
                    <div>OPS <b style={{color:T.text}}>{num(d?.ops,3)}</b></div>
                    <div>HR/9 <b style={{color:T.text}}>{num(d?.hr9)}</b></div>
                  </div>
                </div>
              ))}
            </div>
          </>)}
          {sc && (<>
            <H>Statcast</H>
            <div style={{display:"flex",gap:7}}>
              <Tile label="SwStr%" val={sc.swstr_pct==null?"—":sc.swstr_pct+"%"}/>
              <Tile label="xwOBA-a" val={num(sc.xwoba_against,3)}/>
              <Tile label="Barrel%" val={sc.barrel_pct==null?"—":sc.barrel_pct+"%"}/>
            </div>
          </>)}
          {Array.isArray(data.arsenal) && data.arsenal.length>0 && (()=>{
            const withXw = data.arsenal.filter(p=>p.xwoba_against!=null)
            const outP = withXw.length ? withXw.reduce((a,b)=>b.xwoba_against<a.xwoba_against?b:a) : null
            const hitP = withXw.length>=2 ? withXw.reduce((a,b)=>b.xwoba_against>a.xwoba_against?b:a) : null
            const xwColor = x => x==null?T.muted2 : x<=0.300?T.green : x>=0.350?T.red : T.muted2
            const hasEff = withXw.length>0
            return (<>
            <H>Pitch arsenal</H>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {data.arsenal.map((p,i)=>{
                const isOut = outP && p.code===outP.code
                const isHit = hitP && p.code===hitP.code && p.xwoba_against>=0.330 && !isOut
                return (
                <div key={p.code}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{width:78,fontSize:12,fontWeight:700,color:i===0?T.green:T.text,whiteSpace:"nowrap"}}>{p.label}</span>
                    <div style={{flex:1,height:10,background:T.card2,borderRadius:6,overflow:"hidden",border:`1px solid ${T.border}`}}>
                      <div style={{width:`${Math.max(2,Math.min(100,p.pct))}%`,height:"100%",background:i===0?T.green:T.greenMid,borderRadius:6}}/>
                    </div>
                    <span style={{width:46,textAlign:"right",fontSize:12,fontWeight:800,fontFamily:T.mono,color:T.muted2}}>{p.pct}%</span>
                  </div>
                  {(p.xwoba_against!=null || p.whiff!=null || isOut || isHit) && (
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:3,paddingLeft:88,fontSize:10.5,fontFamily:T.mono,color:T.muted}}>
                      {p.xwoba_against!=null && <span>xwOBA-a <b style={{color:xwColor(p.xwoba_against)}}>{p.xwoba_against.toFixed(3)}</b></span>}
                      {p.whiff!=null && <span>· {p.whiff}% whiff</span>}
                      {isOut && <span style={{color:T.green,fontWeight:800,letterSpacing:"0.04em"}}>· OUT PITCH</span>}
                      {isHit && <span style={{color:T.red,fontWeight:800,letterSpacing:"0.04em"}}>· HITTABLE</span>}
                    </div>
                  )}
                </div>
              )})}
            </div>
            <div style={{fontSize:10.5,color:T.muted,marginTop:8,lineHeight:1.5}}>Usage % this season · most-thrown first.{hasEff?" xwOBA-against = how hard the pitch is hit (lower = nastier).":""}</div>
            </>)
          })()}
          <H>Recent starts</H>
          {!recent.length && <div style={{color:T.muted,fontSize:12,padding:"6px 2px"}}>No recent game logs available.</div>}
          {recent.length>0 && (
            <div style={{overflowX:"auto"}}>
              <div style={{display:"flex",flexDirection:"column",gap:5,minWidth:300}}>
                <div style={{display:"flex",gap:8,fontSize:9.5,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.06em",padding:"0 10px"}}>
                  <span style={{width:44}}>Date</span><span style={{flex:1}}>Opp</span>
                  <span style={{width:34,textAlign:"right"}}>IP</span><span style={{width:26,textAlign:"right"}}>K</span>
                  <span style={{width:26,textAlign:"right"}}>ER</span><span style={{width:26,textAlign:"right"}}>H</span>
                  <span style={{width:26,textAlign:"right"}}>BB</span><span style={{width:26,textAlign:"right"}}>HR</span>
                </div>
                {recent.map((g,i)=>(
                  <div key={i} style={{display:"flex",gap:8,alignItems:"center",background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 10px",fontFamily:T.mono,fontSize:12}}>
                    <span style={{width:44,color:T.muted,fontSize:10.5}}>{(g.date||"").slice(5)}</span>
                    <span style={{flex:1,color:T.text,fontSize:11,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{g.home?"vs":"@"} {g.opp}</span>
                    <span style={{width:34,textAlign:"right",color:T.text}}>{g.ip}</span>
                    <span style={{width:26,textAlign:"right",color:T.green,fontWeight:700}}>{g.k}</span>
                    <span style={{width:26,textAlign:"right",color:g.er>=4?T.red:T.text}}>{g.er}</span>
                    <span style={{width:26,textAlign:"right",color:T.muted}}>{g.h}</span>
                    <span style={{width:26,textAlign:"right",color:T.muted}}>{g.bb}</span>
                    <span style={{width:26,textAlign:"right",color:g.hr>0?T.amber:T.muted}}>{g.hr}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>)}
      </div>
    </Sheet>
  )
}

function PitcherPanel(){
  const { data, loading } = useFetch(()=>getPitcherData(), [])
  const [sel,setSel] = useState(null)
  const [q,setQ] = useState("")
  const [flaggedOnly,setFlaggedOnly] = useState(false)
  const [sortKey,setSortKey] = useState("role")
  const [team,setTeam] = useState("")
  const summary = data?.summary || {}
  const all = (Array.isArray(data?.rows) && data.rows.length) ? data.rows : (Array.isArray(data?.flagged) ? data.flagged : [])
  const teams = [...new Set(all.map(r=>r.team).filter(Boolean))].sort()
  const qq = q.trim().toLowerCase()
  let shown = all.filter(r => !qq || (r.name||"").toLowerCase().includes(qq) || (r.team||"").toLowerCase().includes(qq))
  if(team) shown = shown.filter(r => r.team===team)
  // A row is a real PROBLEM only for stale/defaulted/frozen data — 'small sample' is
  // the normal, handled case (we regress it on purpose), so it never marks a row bad.
  const isProblem = r => (r.problem != null) ? r.problem : (r.flags||[]).some(f=>!f.startsWith("small sample"))
  if(flaggedOnly) shown = shown.filter(isProblem)
  // Sort options. Default: real-problem arms first, then most-hittable (ERA) — the data
  // to distrust sits where you look. The rest sort a numeric column descending.
  const SORTS = [["role","Role"],["flags","⚠"],["era","ERA"],["whip","WHIP"],["k9","K9"],["hr9","HR9"],["ip","IP"]]
  const _sorts = {
    // Starters first (by workload), then the bullpen (by innings) — the natural read.
    role: (a,b)=> (((b.gs||0)>=1?1:0)-((a.gs||0)>=1?1:0)) || ((b.gs||0)-(a.gs||0)) || ((b.ip||0)-(a.ip||0)),
    flags: (a,b)=> ((isProblem(b)?1:0)-(isProblem(a)?1:0)) || ((b.era||0)-(a.era||0)),
    era: (a,b)=> (b.era||0)-(a.era||0), whip: (a,b)=> (b.whip||0)-(a.whip||0),
    k9: (a,b)=> (b.k9||0)-(a.k9||0), hr9: (a,b)=> (b.hr9||0)-(a.hr9||0), ip: (a,b)=> (b.ip||0)-(a.ip||0),
  }
  shown = [...shown].sort(_sorts[sortKey] || _sorts.role)
  const flagColor = f => (f.startsWith("stale")||f.includes("default")||f.includes("era=0")||f.includes("hr9=0")||f.includes("0 GS")) ? T.red
    : T.muted   // small-sample = neutral info, not an alarm
  const num = (v,d=2)=> (v==null ? "—" : Number(v).toFixed(d))
  return (
      <div style={{padding:"2px 14px 40px"}}>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>The raw pitcher stats the model reads — ERA / WHIP / K9 / HR9 / IP / GS — with a health flag on anything suspect. <b style={{color:T.amber}}>Small-sample</b> arms (under 30 IP) are regressed toward league average so a one-start ace can't read as hittable.</div>
        {summary.total_pitchers!=null && (
          <div style={{display:"flex",gap:8,marginTop:12}}>
            <div style={{flex:1,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px"}}>
              <div style={{fontSize:19,fontWeight:900,fontFamily:T.mono,color:T.text}}>{summary.total_pitchers}</div>
              <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Pitchers</div>
            </div>
            <div style={{flex:1,background:T.card,border:`1px solid ${summary.flagged?T.amber:T.border2}`,borderRadius:10,padding:"9px 11px"}}>
              <div style={{fontSize:19,fontWeight:900,fontFamily:T.mono,color:summary.flagged?T.amber:T.green}}>{summary.flagged}</div>
              <div style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase"}}>Flagged</div>
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8,marginTop:14,alignItems:"center"}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search pitcher or team…" style={{flex:1,padding:"7px 11px",borderRadius:8,fontSize:12.5,border:`1px solid ${T.border2}`,background:T.card,color:T.text}}/>
          <button onClick={()=>setFlaggedOnly(v=>!v)} style={{padding:"7px 11px",borderRadius:8,fontSize:11.5,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",background:flaggedOnly?T.amberDim:T.card,border:`1px solid ${flaggedOnly?T.amber:T.border2}`,color:flaggedOnly?T.amber:T.muted2}}>⚠ Flagged</button>
        </div>
        <div style={{display:"flex",gap:8,marginTop:9,alignItems:"center"}}>
          <span style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase",flexShrink:0}}>Team</span>
          <select value={team} onChange={e=>setTeam(e.target.value)} style={{flex:1,padding:"6px 10px",borderRadius:8,fontSize:12.5,border:`1px solid ${team?T.green:T.border2}`,background:T.card,color:team?T.green:T.muted2,fontWeight:700,cursor:"pointer",colorScheme:"dark"}}>
            <option value="">All teams</option>
            {teams.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {team && <button onClick={()=>setTeam("")} style={{padding:"6px 10px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer",background:T.card,border:`1px solid ${T.border2}`,color:T.muted2}}>clear</button>}
        </div>
        <div style={{display:"flex",gap:6,marginTop:9,alignItems:"center",overflowX:"auto",scrollbarWidth:"none"}}>
          <span style={{fontSize:10,letterSpacing:"0.1em",fontWeight:700,color:T.muted,textTransform:"uppercase",flexShrink:0}}>Sort</span>
          {SORTS.map(([k,lbl])=>{ const on=sortKey===k; return (
            <button key={k} onClick={()=>setSortKey(k)} style={{flexShrink:0,padding:"4px 10px",borderRadius:14,fontSize:11,fontWeight:700,cursor:"pointer",background:on?T.greenDim:T.card,border:`1px solid ${on?T.green:T.border2}`,color:on?T.green:T.muted2}}>{lbl}</button>
          )})}
        </div>
        {loading && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px"}}>Loading pitcher data…</div>}
        {!loading && !shown.length && <div style={{color:T.muted,fontSize:12.5,padding:"24px 4px"}}>No pitchers match.</div>}
        <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:7}}>
          {shown.map((r,i)=>(
            <div key={(r.name||i)+'-'+(r.team||'')} onClick={()=>setSel(r.name)} style={{background:T.card,border:`1px solid ${isProblem(r)?T.amber:T.border}`,borderRadius:10,padding:"9px 11px",cursor:"pointer"}}>
              <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",gap:8}}>
                <div style={{fontSize:13.5,fontWeight:800,color:T.text}}>{r.name} <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.04em",color:(r.gs||0)>=1?T.green:T.muted,border:`1px solid ${(r.gs||0)>=1?T.greenMid:T.border2}`,borderRadius:4,padding:"1px 5px",verticalAlign:"middle"}}>{(r.gs||0)>=1?"SP":"RP"}</span> <span style={{fontSize:11,color:T.muted,fontWeight:600}}>{r.team} · {r.throws==="L"?"LHP":"RHP"}</span> <span style={{fontSize:10,color:T.green,fontWeight:700}}>›</span></div>
                <div style={{fontSize:11,color:T.muted,fontFamily:T.mono}}>{num(r.ip,1)} IP · {r.gs} GS</div>
              </div>
              <div style={{display:"flex",gap:12,marginTop:6,fontFamily:T.mono,fontSize:12}}>
                <span style={{color:T.muted}}>ERA <b style={{color:T.text}}>{num(r.era)}</b></span>
                <span style={{color:T.muted}}>WHIP <b style={{color:T.text}}>{num(r.whip)}</b></span>
                <span style={{color:T.muted}}>K9 <b style={{color:T.text}}>{num(r.k9,1)}</b></span>
                <span style={{color:T.muted}}>HR9 <b style={{color:T.text}}>{num(r.hr9)}</b></span>
              </div>
              {(r.flags||[]).length>0 && (
                <div style={{display:"flex",gap:6,marginTop:7,flexWrap:"wrap"}}>
                  {r.flags.map((f,j)=><span key={j} style={{fontSize:10,fontWeight:700,color:flagColor(f),background:"transparent",border:`1px solid ${flagColor(f)}`,borderRadius:5,padding:"2px 6px"}}>{f}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
        {sel && <ErrorBoundary resetKey={sel}><PitcherCard name={sel} onClose={()=>setSel(null)}/></ErrorBoundary>}
      </div>
  )
}

// The Scouting tab = hitter research (contact log) + pitcher data, one place.
// 🎂 Birthdays — MLB players with a birthday today, board players highlighted. Fed by
// /free/birthdays (MLB feed, cached daily). Renders nothing on a no-birthday day.
function BirthdaysDrawer(){
  const [open,setOpen] = useState(false)
  const { data, loading } = useFetch(()=>getBirthdays().catch(()=>null), [])
  const list = data?.birthdays || []
  if(loading || !list.length) return null
  const onBoard = list.filter(b=>b.on_board)
  return (
    <div style={{padding:"0 14px 4px"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderRadius:12,background:T.card,border:`1px solid ${T.border2}`,color:T.text,fontSize:13,fontWeight:800,cursor:"pointer"}}>
        <span>🎂 Birthdays today <span style={{color:T.muted,fontWeight:600}}>· {list.length}{onBoard.length?` · ${onBoard.length} on the board`:""}</span></span>
        <span style={{color:T.muted,transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
      </button>
      {open && (
        <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
          {list.map((b,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 13px",borderRadius:10,background:b.on_board?T.greenDim:T.card,border:`1px solid ${b.on_board?T.greenMid:T.border2}`}}>
              <div style={{display:"flex",alignItems:"center",gap:9,minWidth:0}}>
                <span style={{fontSize:15,flexShrink:0}}>🎂</span>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{b.player}</div>
                  <div style={{fontSize:10.5,color:T.muted,fontWeight:600}}>{[b.team,b.position].filter(Boolean).join(" · ")}{b.on_board?" · on tonight's board":""}</div>
                </div>
              </div>
              <div style={{fontSize:12,fontWeight:800,color:b.on_board?T.green:T.muted2,whiteSpace:"nowrap",flexShrink:0}}>turns {b.age}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// /free/ballpark-events (MLB schedule feed, cached daily). Bobblehead nights, giveaways,
// theme days — flagged when the home team is on tonight's board. Renders nothing on a
// no-promo day. Sits next to birthdays in Scouting.
function BallparkEventsDrawer(){
  const [open,setOpen] = useState(false)
  const { data, loading } = useFetch(()=>getBallparkEvents().catch(()=>null), [])
  const list = data?.events || []
  if(loading || !list.length) return null
  const onBoard = list.filter(e=>e.on_board)
  const emojiFor = e => e.is_bobblehead ? "🎎" : /firework/i.test(e.type||e.name) ? "🎆"
    : /theme|night/i.test(e.type||e.name) ? "🎉" : "🎁"
  return (
    <div style={{padding:"0 14px 4px"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderRadius:12,background:T.card,border:`1px solid ${T.border2}`,color:T.text,fontSize:13,fontWeight:800,cursor:"pointer"}}>
        <span>🎁 At the park today <span style={{color:T.muted,fontWeight:600}}>· {list.length}{onBoard.length?` · ${onBoard.length} on the board`:""}</span></span>
        <span style={{color:T.muted,transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
      </button>
      {open && (
        <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:6}}>
          {list.map((e,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 13px",borderRadius:10,background:e.on_board?T.greenDim:T.card,border:`1px solid ${e.on_board?T.greenMid:T.border2}`}}>
              <div style={{display:"flex",alignItems:"center",gap:9,minWidth:0}}>
                <span style={{fontSize:15,flexShrink:0}}>{emojiFor(e)}</span>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.name}</div>
                  <div style={{fontSize:10.5,color:T.muted,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{[e.team,e.venue].filter(Boolean).join(" · ")}{e.on_board?" · on tonight's board":""}</div>
                </div>
              </div>
              {e.type && <div style={{fontSize:10,fontWeight:800,color:e.on_board?T.green:T.muted2,whiteSpace:"nowrap",flexShrink:0}}>{e.type}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ScoutingTab(){
  const [sub,setSub] = useState("hitters")
  const seg = on => ({flex:1,padding:"8px 0",borderRadius:9,fontSize:12.5,fontWeight:800,cursor:"pointer",
    background:on?T.greenDim:T.card,border:`1px solid ${on?T.green:T.border2}`,color:on?T.green:T.muted2})
  return (
    <div style={{padding:"0 0 12px"}}>
      <div style={{padding:"12px 0 2px"}}><BirthdaysDrawer/><BallparkEventsDrawer/></div>
      <div style={{display:"flex",gap:8,padding:"6px 14px 6px"}}>
        <button onClick={()=>setSub("hitters")} style={seg(sub==="hitters")}>🔨 Hitters</button>
        <button onClick={()=>setSub("pitchers")} style={seg(sub==="pitchers")}>⚾ Pitchers</button>
        <button onClick={()=>setSub("matchup")} style={seg(sub==="matchup")}>🆚 Matchup</button>
      </div>
      {sub==="hitters" ? <ContactLogTab/> : sub==="pitchers" ? <PitcherPanel/> : <MatchupBody embedded/>}
    </div>
  )
}

function GuideSheet({onClose}){
  const RISK = {lo:{c:T.green,bg:T.greenDim,bd:T.greenMid}, mid:{c:T.amber,bg:T.amberDim,bd:T.amber}, hi:{c:T.red,bg:T.redDim,bd:T.red}}
  const H2 = ({children}) => <div style={{fontSize:11,letterSpacing:"0.14em",fontWeight:800,color:T.muted,textTransform:"uppercase",margin:"26px 0 12px",display:"flex",alignItems:"center",gap:10}}>{children}<div style={{flex:1,height:1,background:`linear-gradient(90deg,${T.border},transparent)`}}/></div>
  const Body = ({children}) => <div style={{fontSize:12.5,color:T.muted,lineHeight:1.6,marginTop:8}}>{children}</div>
  const B = ({children}) => <b style={{color:T.text,fontWeight:700}}>{children}</b>
  const G = ({children}) => <b style={{color:T.green,fontWeight:700}}>{children}</b>
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"6px 16px 44px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2,paddingTop:6}}>
          <div style={{fontSize:10,letterSpacing:"0.22em",fontWeight:800,color:T.green,textTransform:"uppercase"}}>The Field Guide</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:26,fontWeight:900,letterSpacing:"-0.03em",lineHeight:1.05,marginTop:6}}>Get the most out of <span style={{color:T.text}}>LINE</span><span style={{color:T.green}}>BRKR</span>.</div>
        <div style={{fontSize:13,color:T.muted,lineHeight:1.6,marginTop:10}}>An <B>MLB prop research model</B> — Poisson probabilities across HRR, home runs, hits, total bases and strikeouts, graded against real matchups, park, and weather. Here's how to read the board and what every number means.</div>

        <H2>The two numbers that run the app</H2>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 12px 11px"}}>
            <div style={{fontSize:13.5,fontWeight:900,display:"flex",alignItems:"center",gap:7}}><span style={{width:8,height:8,borderRadius:"50%",background:T.green}}/>LINEBRKR Rating</div>
            <div style={{fontSize:11.5,color:T.muted,marginTop:7,lineHeight:1.5}}><B>How good the play is — 0-100.</B> Model conviction + matchup + data quality. Price is NOT in it. The <B>quality</B> score.</div>
          </div>
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 12px 11px"}}>
            <div style={{fontSize:13.5,fontWeight:900,display:"flex",alignItems:"center",gap:7}}><span style={{width:8,height:8,borderRadius:"50%",background:T.blue}}/>Probability</div>
            <div style={{fontSize:11.5,color:T.muted,marginTop:7,lineHeight:1.5}}><B>The raw chance it hits — a %.</B> A shown 65% is built to actually hit ~65% over time (we track calibration).</div>
          </div>
        </div>
        <Body>Rankings sorts by <G>Rating</G> by default — highest-quality first. Flip to <B>Highest Probability</B> for the highest-floor plays. High on both = green light; high prob / low Rating = often a <B>volume trap</B>.</Body>

        <H2>The markets — and how risky each is</H2>
        <div style={{border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden"}}>
          {_GUIDE_MARKETS.map(([mk,desc,label,risk],i)=>{ const r=RISK[risk]; return (
            <div key={mk} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderTop:i?`1px solid ${T.border}`:"none"}}>
              <div style={{fontFamily:T.mono,fontWeight:800,fontSize:12,color:T.text,minWidth:74,flexShrink:0}}>{mk}</div>
              <div style={{flex:1,fontSize:11.5,color:T.muted,lineHeight:1.4}}>{desc}</div>
              <div style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.03em",textTransform:"uppercase",color:r.c,background:r.bg,border:`1px solid ${r.bd}`,borderRadius:5,padding:"2px 7px",flexShrink:0,whiteSpace:"nowrap"}}>{label}</div>
            </div>
          )})}
        </div>
        <Body><B>HRR is the model's backbone</B> — one bet that cashes on a hit, a run, or an RBI, so a good bat in a good spot clears it far more often than a home run. The riskier the market (HR, 2+H), the higher the Rating you should demand.</Body>

        <H2>The analytics grid — read the drivers</H2>
        <Body>The <B>analytics grid</B> lays every hitter out in one sortable, filterable board — matchup, park, form, pitch-type & per-zone fit as columns. Each market cashes for a different reason, so a different handful of metrics tips you off — the same ones the grid uses to flag a <G>⚡ prime spot</G>:</Body>
        <div style={{border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden",marginTop:10}}>
          {_GUIDE_DRIVERS.map(([mk,desc],i)=>(
            <div key={mk} style={{display:"flex",gap:11,padding:"11px 12px",borderTop:i?`1px solid ${T.border}`:"none"}}>
              <div style={{fontFamily:T.mono,fontWeight:800,fontSize:12,color:T.green,minWidth:74,flexShrink:0}}>{mk}</div>
              <div style={{flex:1,fontSize:11.5,color:T.muted,lineHeight:1.5}}>{desc}</div>
            </div>
          ))}
        </div>
        <Body><B>The one tell that runs through every market: the arm.</B> A soft-contact, low-ERA ace fades a whole slate of bats; a high-WHIP, HR-prone arm is where hitter value lives. Use <G>🎯 Attack only</G> to show just the hitters facing a pitcher worth going after.</Body>

        <H2>10 tips &amp; tricks</H2>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {_GUIDE_TIPS.map(([h,p],i)=>(
            <div key={i} style={{display:"flex",gap:12,background:T.card,border:`1px solid ${T.border}`,borderRadius:11,padding:"12px 13px"}}>
              <div style={{flexShrink:0,width:28,height:28,borderRadius:8,background:T.greenDim,border:`1px solid ${T.greenMid}`,color:T.green,fontFamily:T.mono,fontWeight:800,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>{i+1}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13.5,fontWeight:800,letterSpacing:"-0.01em"}}>{h}</div>
                <div style={{fontSize:12,color:T.muted,marginTop:4,lineHeight:1.55}}>{p}</div>
              </div>
            </div>
          ))}
        </div>

        <H2>Your 60-second routine</H2>
        <div style={{display:"flex",flexDirection:"column",gap:7}}>
          {["Open Home → 🏆 Top 15 for the day's best-rated reads","Turn on Confirmed only so nobody's a scratch","Tap 2–3 cards, read the matchup + park + weather","Spread across games — no more than 2 from one team","Log it in My Bets, settle later, learn from the record"].map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:12,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"11px 13px"}}>
              <span style={{fontFamily:T.mono,fontSize:12,fontWeight:800,color:T.green,flexShrink:0}}>{i+1}</span>
              <span style={{fontSize:12.5,fontWeight:600,color:T.text,lineHeight:1.4}}>{s}</span>
            </div>
          ))}
        </div>

        <div style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"13px 14px",marginTop:14}}>
          <div style={{fontSize:13,fontWeight:900,color:T.text}}>The golden rule</div>
          <div style={{fontSize:12,color:T.muted,marginTop:5,lineHeight:1.55}}>LINEBRKR gives you the <B>research</B> — the probabilities, the matchup, the tracked record. What you do with it is yours. Bet within your means, shop for the best number, and let the <B>tracked record</B>, not a hot streak, tell you what's working.</div>
        </div>

        <div style={{fontSize:10.5,color:T.faint,lineHeight:1.6,marginTop:22,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
          <b style={{color:T.muted}}>LINEBRKR</b> — an MLB prop research &amp; information product. Not a sportsbook. Not betting advice. Numbers are computed from public data and the model's projections; none are invented.<br/>
          <span style={{color:T.amber,fontWeight:700}}>21+ · research/info only · not betting advice · If you or someone you know has a gambling problem, call 1-800-GAMBLER.</span>
        </div>
      </div>
    </Sheet>
  )
}

function LegalSheet({tab="terms", onClose, setTab}){
  const cur = tab==="privacy" ? "privacy" : "terms"
  const tabBtn = (id,label) => (
    <button onClick={()=>setTab && setTab(id)} style={{flex:1,padding:"8px 0",borderRadius:8,fontSize:12,fontWeight:800,cursor:"pointer",border:"none",background:cur===id?T.greenDim:"transparent",color:cur===id?T.green:T.muted2}}>{label}</button>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>Legal</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        {setTab && <div style={{display:"flex",gap:4,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:3,marginBottom:12}}>{tabBtn("terms","Terms of Service")}{tabBtn("privacy","Privacy Policy")}</div>}
        <div style={{fontSize:10.5,color:T.muted,marginBottom:10}}>LINEBRKR LLC · Last updated {LEGAL_UPDATED}</div>
        <LegalBody src={cur==="privacy"?PRIVACY_MD:TOS_MD}/>
      </div>
    </Sheet>
  )
}

// ── Responsible Gaming & legal disclaimer ───────────────────────────────────
function ResponsibleGamingSheet({onClose}){
  const Sec = ({title}) => <div style={{fontSize:10,color:T.green,fontWeight:800,letterSpacing:"0.14em",margin:"18px 0 8px"}}>{title}</div>
  const P = ({children}) => <div style={{fontSize:12.5,color:T.muted2,lineHeight:1.55,marginBottom:10}}>{children}</div>
  const Help = ({name,contact,note}) => (
    <div style={{display:"flex",gap:12,marginBottom:10,alignItems:"flex-start"}}>
      <span style={{flexShrink:0,minWidth:96,fontSize:12.5,fontWeight:800,color:T.text,lineHeight:1.35}}>{name}</span>
      <span style={{fontSize:12.5,color:T.muted2,lineHeight:1.45}}><b style={{color:T.green}}>{contact}</b>{note?` — ${note}`:""}</span>
    </div>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>🛟 Responsible Gaming</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>Please read. Sports betting carries real financial risk. LINEBRKR is here to help you make informed decisions — not to push you to bet.</div>

        <div style={{background:"rgba(239,68,68,0.08)",border:`1px solid ${T.red}`,borderRadius:10,padding:"12px 14px",margin:"16px 0"}}>
          <div style={{fontSize:13,fontWeight:800,color:T.text,marginBottom:6}}>If gambling stops being fun, get help now.</div>
          <div style={{fontSize:13,color:T.text,lineHeight:1.5}}>Call or text the National Problem Gambling Helpline: <b style={{color:T.red}}>1-800-GAMBLER</b> (1-800-426-2537) — free, confidential, 24/7. Or visit <b style={{color:T.red}}>ncpgambling.org</b>.</div>
        </div>

        <Sec title="THE GROUND RULES"/>
        <P><b style={{color:T.text}}>You must be 21+</b> (or the legal betting age in your jurisdiction) to wager. Sports betting is not legal everywhere — it is your responsibility to know and follow the laws where you live.</P>
        <P><b style={{color:T.text}}>Only bet what you can afford to lose.</b> Never wager money needed for rent, bills, food, or savings. Set a budget before you play and stick to it. Don't chase losses — betting more to win back what you've lost is the fastest way to lose more.</P>
        <P><b style={{color:T.text}}>Gambling is not income.</b> Treat it as paid entertainment, not a way to make money or solve financial problems.</P>

        <Sec title="WHAT LINEBRKR IS (AND ISN'T)"/>
        <P>LINEBRKR is a <b style={{color:T.text}}>research and informational tool</b>. It provides statistical model projections and analysis. It is <b style={{color:T.text}}>not a sportsbook</b>, does not accept bets, and does not handle any money.</P>
        <P>Our projections are <b style={{color:T.text}}>estimates, not guarantees</b>. No model can predict the outcome of a game. Past performance does not predict future results, and any track record shown is historical and not a promise of future accuracy. Nothing here is financial, investment, or betting advice. <b style={{color:T.text}}>All betting decisions are your own.</b></P>

        <Sec title="WARNING SIGNS"/>
        <P>Consider taking a break or seeking help if you:</P>
        <P>• Bet more than you planned, or more than you can afford<br/>
        • Chase losses or bet to escape stress, anxiety, or boredom<br/>
        • Lie to friends or family about your gambling<br/>
        • Borrow money or sell things to gamble<br/>
        • Feel restless or irritable when trying to cut back<br/>
        • Find gambling is harming your work, relationships, or finances</P>

        <Sec title="GET HELP / SELF-EXCLUDE"/>
        <Help name="National" contact="1-800-GAMBLER" note="call or text, 24/7, all U.S. states"/>
        <Help name="Online" contact="ncpgambling.org" note="chat + resources (NCPG)"/>
        <Help name="Tools" contact="Deposit & time limits" note="most sportsbooks offer limits, cool-offs, and self-exclusion in their settings"/>
        <P style={{marginTop:4}}>If you or someone you know has a gambling problem, help is available and recovery is possible. Reaching out is a sign of strength.</P>

        <div style={{fontSize:11,color:T.muted,lineHeight:1.55,marginTop:20,paddingTop:14,borderTop:`1px solid ${T.border}`}}>
          By using LINEBRKR you acknowledge you are of legal age in your jurisdiction, that you use this tool at your own risk, and that LINEBRKR and its operators are not liable for any losses incurred from betting decisions. Void where prohibited.
        </div>
      </div>
    </Sheet>
  )
}

// Templated whole-day briefing (deterministic baseline for the Slate Read)
function buildSlateRead({games, elite, best, strong, gameToAttack, topPlays, hotBat, legs, combo}){
  const s=[]
  s.push((elite+best+strong)>0
    ? `${games} games on the board today, with ${elite} ELITE, ${best} TOP RATED, and ${strong} STRONG plays surfaced.`
    : `${games} games on the board — a quieter slate so far, no standout tiers yet.`)
  if(gameToAttack) s.push(`The game to attack is ${gameToAttack.away.abbr} @ ${gameToAttack.home.abbr} (${gradeLetter(gameToAttack.grade)}, ${gameToAttack.grade}/100)${gameToAttack.windOk?", with wind blowing out":""}.`)
  if(topPlays&&topPlays[0]){ const t=topPlays[0]; s.push(`Top ranked is ${t.full_name} ${t.market} at ${pct(t.prob_over||0)}${topPlays[1]?`, then ${topPlays[1].full_name} (${topPlays[1].market}, ${pct(topPlays[1].prob_over||0)})`:""}.`) }
  if(hotBat&&hotBat.name) s.push(`Hottest bat over the last 7 days is ${hotBat.name}${hotBat.team?` (${hotBat.team})`:""}${hotBat.hr!=null?` — ${hotBat.hr} HR in ${hotBat.games} games`:""}.`)
  if(legs&&legs.length===2&&combo!=null) s.push(`Top 2-leg: ${legs[0].full_name} + ${legs[1].full_name} at ${pct(combo)} combined (${toAmerican(combo)}).`)
  return s.join(" ")
}
// Empty skeleton shell — shown in place of a data section until the slate drops.
const Shell = ({h=66, style})=>(<div style={{height:h,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:8,opacity:0.45,...style}}/>)

// Always-visible track-record drawer for the Home page — surfaces the elite (70%+ model
// prob) hit rate every day so the proof is the first thing users see. Tap to expand a
// per-market breakdown in place; a button jumps to the full record tab.
function HomeTrackRecord({ setView }){
  const [open,setOpen] = useState(false)
  const [copied,setCopied] = useState(false)
  const { data: elite } = useFetch(()=>getEliteRecord(30).catch(()=>null), [])
  const { data: sc } = useFetch(()=>getScorecardSummary(30).catch(()=>null), [])
  const { data: val } = useFetch(()=>getValidation(30).catch(()=>null), [])
  const { data: hrrTrend } = useFetch(()=>getScorecardTrend(30,"HRR_over_1.5").then(d=>d?.series||[]).catch(()=>[]), [])
  if(elite === undefined) return null
  const has = elite && elite.bets > 0
  const prettyDate = s => { if(!s) return ""; const [y,m,d]=s.split("-").map(Number); return new Date(y,m-1,d).toLocaleDateString("en-US",{month:"short",day:"numeric"}) }
  const since = sc?.start ? `since ${prettyDate(sc.start)}` : "new model"
  const CORE = ["HRR_over_1.5","HR_anytime","hits","hits_1.5","total_bases","strikeouts"]
  const byProp = Object.fromEntries((sc?.markets||[]).map(m=>[m.prop_type,m]))
  const rows = CORE.map(pt=>byProp[pt]).filter(Boolean).filter(m=>((m.top10_wins||0)+(m.top10_losses||0))>0)
  // Honest edge = LIFT (our top-Rated picks' hit rate minus the rest of the field, WITHIN
  // each market so base rates never mix). Not ROI — we don't have real book odds, so a
  // dollar figure would be dishonest. Aggregate the per-market lifts, weighted by the
  // number of top-Rated picks, into one "beats the field by +Xpt" headline.
  const liftMkts = CORE.map(pt=>({pt, r:val?.markets?.[pt]?.rating})).filter(x=>x.r && x.r.lift!=null && x.r.n_top>0)
  const liftWt = liftMkts.reduce((a,x)=>a+x.r.n_top,0)
  const aggLift = liftWt ? liftMkts.reduce((a,x)=>a+x.r.lift*x.r.n_top,0)/liftWt : null
  const liftPt = aggLift!=null ? Math.round(aggLift*100) : null
  const liftByProp = Object.fromEntries(liftMkts.map(x=>[x.pt, x.r.lift]))
  // Sparkline points from the HRR top-10 daily hit-rate series (representative trend).
  const spark = (hrrTrend||[]).slice(-12).map(s=>s.hit_rate).filter(v=>typeof v==="number")
  const sparkPath = (()=>{ if(spark.length<2) return null
    const w=88,h=22,mn=Math.min(...spark),mx=Math.max(...spark),rng=(mx-mn)||1
    return spark.map((v,i)=>`${(i/(spark.length-1))*w},${h-((v-mn)/rng)*h}`).join(" ") })()
  const copyRecord = async (ev)=>{ ev.stopPropagation()
    const lines = [`LINEBRKR — Track Record`,
      has?`70%+ picks: ${Math.round(elite.hit_rate*100)}% · ${elite.wins}-${elite.losses} · ${elite.bets} graded ${since}`:`Track record building`,
      liftPt!=null?`70%+ picks beat the field by ${liftPt>0?"+":""}${liftPt}pt (avg lift across markets)`:null,
      ``, ...rows.map(m=>{const lf=liftByProp[m.prop_type]; return `${(PROP_SHORT[m.prop_type]||m.prop_type).padEnd(5)} ${Math.round((m.hit_rate_top_10||0)*100)}% (${m.top10_wins}-${m.top10_losses})${lf!=null?`  ${lf>0?"+":""}${Math.round(lf*100)}pt vs field`:""}`}),
      ``, `Top-ranked plays only · real settled results`, `21+ · not betting advice · 1-800-GAMBLER`].filter(l=>l!==null)
    const txt = lines.join("\n")
    try{ await navigator.clipboard.writeText(txt) }catch{ const ta=document.createElement("textarea");ta.value=txt;document.body.appendChild(ta);ta.select();try{document.execCommand("copy")}catch(_){}ta.remove() }
    setCopied(true); setTimeout(()=>setCopied(false),1800) }
  return (
    <div style={{marginTop:12,position:"relative",overflow:"hidden",
      background:`radial-gradient(360px 150px at 10% -10%, ${T.greenDim}, transparent 68%), linear-gradient(180deg, ${T.card3}, ${T.card})`,
      border:`1px solid ${T.green}`,borderRadius:16,boxShadow:`0 0 26px ${T.green}22`}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:"transparent",border:"none",padding:"16px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
        <div style={{minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:7,flexWrap:"wrap"}}>
            <span style={{fontSize:9.5,color:T.green,fontWeight:900,letterSpacing:"0.14em"}}>◆ PROVEN · 70%+ PICKS</span>
            {liftPt!=null && liftPt>0 && <span style={{fontSize:9.5,fontWeight:900,color:"#04140D",background:T.green,borderRadius:5,padding:"2px 7px",letterSpacing:"0.04em"}}>+{liftPt}PT VS FIELD</span>}
          </div>
          {has ? (
            <div style={{display:"flex",alignItems:"baseline",gap:12,flexWrap:"wrap"}}>
              <span style={{fontSize:56,fontWeight:900,color:T.green,fontFamily:T.mono,lineHeight:.85,letterSpacing:"-2.5px"}}>{Math.round(elite.hit_rate*100)}%</span>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <span style={{fontSize:17,fontWeight:900,color:T.text,fontFamily:T.mono}}>{elite.wins}&ndash;{elite.losses}</span>
                <span style={{fontSize:10.5,color:T.muted}}>{elite.bets} graded · {since}{elite?.frozen_at?" · reg. season":""}</span>
              </div>
            </div>
          ) : (
            <div style={{fontSize:14,fontWeight:800,color:T.muted2}}>{elite===null?"Track record loading…":"Builds as games settle"}</div>
          )}
          <div style={{fontSize:11.5,color:T.muted2,marginTop:9,lineHeight:1.45}}>{liftPt!=null&&liftPt>0?<>Our <b style={{color:T.text}}>70%+ picks</b> hit <b style={{color:T.green}}>+{liftPt}pt over the field</b> · real settled results</>:"Our 70%+ probability picks · real settled results"} · <b style={{color:T.green}}>CLV-proven &amp; calibrated</b></div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:10,flexShrink:0}}>
          {sparkPath && (
            <svg width="98" height="30" viewBox="0 0 88 22" style={{overflow:"visible"}} aria-hidden="true">
              <polyline points={sparkPath} fill="none" stroke={T.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <span style={{fontSize:11,color:T.green,fontWeight:800,whiteSpace:"nowrap"}}>{open?"Hide markets ▾":"By market ▸"}</span>
        </div>
      </button>
      {open && (
        <div style={{padding:"0 16px 14px"}}>
          <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.1em",margin:"2px 0 8px"}}>TOP-10 HIT RATE · LIFT VS FIELD</div>
          {rows.length ? (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {rows.map(m=>{ const h=Math.round((m.hit_rate_top_10||0)*100); const lf=liftByProp[m.prop_type]; const lp=lf!=null?Math.round(lf*100):null; return (
                <div key={m.prop_type} style={{display:"flex",alignItems:"center",gap:8,fontSize:11}}>
                  <span style={{width:44,fontWeight:800,color:T.muted2}}>{PROP_SHORT[m.prop_type]||m.prop_type}</span>
                  <div style={{flex:1,height:6,background:T.card2,borderRadius:4,overflow:"hidden"}}><div style={{width:`${h}%`,height:"100%",background:lf==null?T.muted2:lf>=0.04?T.green:lf>=0.01?"#34D399":lf<=-0.02?T.red:T.amber}}/></div>
                  {lp!=null && <span style={{width:52,textAlign:"right",fontFamily:T.mono,fontWeight:800,fontSize:10,color:lp>0?T.green:lp<0?T.red:T.muted2}}>{lp>0?"+":""}{lp}pt</span>}
                  <span style={{width:66,textAlign:"right",fontFamily:T.mono,fontWeight:800,color:(lf!=null&&lf>=0.01)?T.green:T.muted2}}>{h}% <span style={{color:T.faint,fontWeight:400}}>({m.top10_wins}-{m.top10_losses})</span></span>
                </div>
              )})}
            </div>
          ) : <div style={{fontSize:11,color:T.muted,fontStyle:"italic"}}>Per-market results fill in as games settle.</div>}
          <div style={{fontSize:9.5,color:T.faint,marginTop:8,lineHeight:1.4}}>Lift = our top-Rated plays' hit rate minus the rest of the field, within each market. It's the honest edge — not ROI (we don't publish a dollar figure without real book odds).</div>
          {/* Strikeouts are a line-based over/under, not a 70%+ binary pick, so they aren't in
              the lift table above — but the K record is real and validating, so surface its
              own honestly-framed card here (same component the full record page uses). */}
          <div style={{marginTop:11}}><KRecordCard/></div>
          <div style={{display:"flex",gap:8,marginTop:11}}>
            <button onClick={copyRecord} style={{flex:1,padding:"9px",borderRadius:9,background:copied?T.green:T.card2,border:`1px solid ${copied?T.green:T.border2}`,color:copied?"#04140D":T.muted2,fontSize:12,fontWeight:800,cursor:"pointer"}}>{copied?"✓ Copied":"⧉ Share record"}</button>
            <button onClick={()=>setView&&setView("record")} style={{flex:1,padding:"9px",borderRadius:9,background:T.greenDim,border:`1px solid ${T.green}`,color:T.green,fontSize:12,fontWeight:800,cursor:"pointer"}}>Full record →</button>
          </div>
        </div>
      )}
    </div>
  )
}

function HomeTab({onPlayer, onGame, setView, onAttack, onGlossary}){
  const { data: gd } = useFetch(()=>getUpcomingMatches(todayStr()), [])
  const { data: lb, loading } = useFetch(()=>Promise.all(
    HOME_MARKETS.map(([lbl,db])=>getLeaderboard(db, todayStr(), 60)
      .then(d=>({lbl, entries:Array.isArray(d?.entries)?d.entries:[]})).catch(()=>({lbl, entries:[]})))
  ), [])
  const { data: trend } = useFetch(()=>getTrending(7,12).catch(()=>null), [])
  // Only highlight a hot bat that's actually in today's slate — matchup is null
  // when the player has no game today (resting/IL), so don't surface them.
  const hotBat = (trend?.hot_bats||[]).find(b=>b.matchup) || null
  const coldArm = trend?.cold_arms?.[0] || null

  const liveGames = (gd?.games||[]).map(mapCard)
  const games = liveGames   // live only — no sample fallback (blank until the slate runs)
  const gameToAttack = [...games].sort((a,b)=>b.grade-a.grade)[0]

  // Flatten every market into one classified pool (canonical-line filtered)
  let flat = []
  const hasLive = Array.isArray(lb) && lb.some(m=>Array.isArray(m?.entries) && m.entries.length)
  if (hasLive){
    for(const grp of lb){
      const lbl = grp?.lbl
      const entries = Array.isArray(grp?.entries) ? grp.entries : []
      const canon=CANON_LINE[lbl]
      for(const e of entries){
        if(canon!=null && Math.abs((e.line??canon)-canon) >= 0.01) continue
        flat.push({...e, market:lbl, tier:tierFor(e.prob_over, confOf(e), lbl, (e.edge_pct!=null?e.edge_pct*100:null), e.book_over!=null)})
      }
    }
  }
  // No SAMPLE_PICKS fallback — when the slate hasn't run, `flat` stays empty and
  // the board shows a clean empty/loading state instead of dummy players.

  // Headline plays rank by PROBABILITY (most likely to hit) — the default across the
  // whole app. Probability is the validated backbone; EV stays a sort option but is
  // NOT the default until calibration is proven. Tier then EV break ties.
  const evOf = e => (e.ev ?? e.edge_pct ?? null)
  const _rateOf = e => (e.confidence_score!=null ? e.confidence_score : confidenceFor(e))
  // Featured top plays: rank by PROBABILITY — the same basis the board and the record
  // go off — max 2 per team so a soft pitcher matchup can't stack the headline. Rating
  // then EV break ties. (Previously led with Rating, which floated a low-prob longshot
  // like a 31% HR to #1 even though the board ranks by probability.)
  const ranked = [...flat].sort((a,b)=>((b.prob_over||0)-(a.prob_over||0)) || (_rateOf(b)-_rateOf(a)) || ((evOf(b)??-9)-(evOf(a)??-9)))
  // Also cap 2 per MARKET so the featured five stay a mix — prob-first alone fills the
  // list with 1+ Hits (the highest-prob market) and buries HRR/TB/etc. The #1 hero is
  // still the top play overall; the cap only diversifies the rows beneath it.
  const _tpCt = {}; const _tpMkt = {}; const topPlays = []
  for(const e of ranked){
    const t=e.team||"?"; const m=e.market||e._mkt||"?"
    if((_tpCt[t]||0)>=2 || (_tpMkt[m]||0)>=2) continue
    topPlays.push(e); _tpCt[t]=(_tpCt[t]||0)+1; _tpMkt[m]=(_tpMkt[m]||0)+1
    if(topPlays.length>=5) break
  }
  const hrPlays  = flat.filter(e=>e.market==="HR").sort((a,b)=>(b.prob_over||0)-(a.prob_over||0)).slice(0,3)
  const counts   = ranked.reduce((a,e)=>{a[e.tier]=(a[e.tier]||0)+1;return a},{})

  // Top 2-leg: two highest-probability plays from different teams (uncorrelated)
  const legs=[], teams=new Set()
  for(const e of [...flat].sort((a,b)=>(b.prob_over||0)-(a.prob_over||0))){
    if(legs.length>=2) break
    if(teams.has(e.team)) continue
    teams.add(e.team); legs.push(e)
  }
  const combo = legs.length===2 ? (legs[0].prob_over||0)*(legs[1].prob_over||0) : null

  const [topOpen,setTopOpen] = useState(false)   // 🏆 all top-rated plays drawer
  const [gradedOpen,setGradedOpen] = useState(false)   // ◆ 70%+ graded plays — the plays behind the record
  const openPlay = e => { const c=confOf(e); onPlayer({
    name:e.full_name, player_id:e.player_id, mlbam_id:e.mlbam_id, market:e.market, tier:e.tier, conf:c, why:buildWhy(e,c),
    prob:e.prob_over, edge:(e.edge_pct!=null)?`${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}%`:undefined,
    odds:fmtOdds(e.book_over), oddsNum:e.book_over, oppHand:e.opp_pitcher_hand,
    opp:e.opp_pitcher_name?`${e.team} · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:`${e.team} · vs TBD`,
    slot:e?.inputs?.lineup_slot, park:e?.inputs?.park_factor, team:e.team, oppPitcher:e.opp_pitcher_name,
  }) }

  // ★ Watchlist — watchlisted players with a play on today's board (best per player).
  const watch = useWatch()
  const myGuys = (()=>{ const seen=new Set(), out=[]; for(const e of ranked){ if(watch.has(e.full_name) && !seen.has(e.full_name)){ seen.add(e.full_name); out.push(e) } } return out })()

  const elite = (counts["ELITE"]||0), best = (counts["BEST BET"]||0), strong = (counts["STRONG"]||0)
  const readLine = elite||best||strong
    ? `${elite} ELITE · ${best} TOP RATED · ${strong} STRONG on the board`
    : "Quiet board today — check back after lineups post"

  // Slate Read — whole-day briefing (templated baseline + optional AI layer)
  const slateRead = buildSlateRead({games:games.length, elite, best, strong, gameToAttack, topPlays, hotBat, legs, combo})
  const [aiSlate,setAiSlate] = useState(null)
  const [slateCopied,setSlateCopied] = useState(false)
  useEffect(()=>{
    if(!hasLive) return
    let live=true
    const facts={date:todayStr(), games:games.length, counts:{elite,best,strong},
      best_game:gameToAttack?{matchup:`${gameToAttack.away.abbr} @ ${gameToAttack.home.abbr}`,grade:gameToAttack.grade,venue:gameToAttack.venue}:null,
      top_plays:(topPlays||[]).slice(0,5).map(t=>({name:t.full_name,market:t.market,prob_pct:pct(t.prob_over||0)})),
      hot_bat:hotBat?.name?{name:hotBat.name,team:hotBat.team,line:hotBat.hr!=null?`${hotBat.hr} HR in ${hotBat.games} games`:""}:null,
      parlay:(legs&&legs.length===2&&combo!=null)?{legs:[legs[0].full_name,legs[1].full_name],combo_pct:pct(combo),fair_odds:toAmerican(combo)}:null}
    getGameRead(`SLATE-${todayStr()}`, facts, "slate").then(r=>{ if(live&&r&&r.narrative) setAiSlate(r.narrative) }).catch(()=>{})
    return ()=>{ live=false }
  },[hasLive, games.length])
  const displaySlate = aiSlate || slateRead
  // (Highest-Probability standalone drawer removed — that hit-% view lives on in
  // Rankings → sort by "Highest Probability", which covers every market.)
  const [stackOpen,setStackOpen] = useState(false) // Perfect Stack drawer
  const [stormOpen,setStormOpen] = useState(false) // Perfect Storm drawer
  const [powerOpen,setPowerOpen] = useState(false) // 💣 Power Spots drawer
  const { isPremium, needPremium } = useProGate()  // Storms + Stack: Founder + Pro
  const copySlate = async()=>{ const txt=`LINEBRKR — Slate Read · ${fmtDate()}\n\n${displaySlate}${EXPORT_DISCLAIMER}`
    try{ await navigator.clipboard.writeText(txt) }catch{ const ta=document.createElement("textarea");ta.value=txt;document.body.appendChild(ta);ta.select();try{document.execCommand("copy")}catch(_){}ta.remove() }
    setSlateCopied(true); setTimeout(()=>setSlateCopied(false),1800) }

  return (
    <div style={{padding:"14px"}}>
      {/* Legend / glossary entry */}
      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
        <button onClick={()=>onGlossary&&onGlossary("top")} style={{display:"flex",alignItems:"center",gap:6,background:T.card,border:`1px solid ${T.border2}`,borderRadius:20,padding:"6px 13px",color:T.green,fontSize:12,fontWeight:700,cursor:"pointer"}}>📖 Legend &amp; Glossary</button>
      </div>
      {/* Today's read */}
      <div style={{background:T.card3,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"14px 16px"}}>
        <div style={{fontSize:10,color:T.green,fontWeight:700,letterSpacing:"0.12em",marginBottom:6}}>TODAY'S READ</div>
        <div style={{fontSize:17,fontWeight:800,color:T.text,lineHeight:1.35}}>{readLine}</div>
        <div style={{fontSize:12,color:T.muted,marginTop:6}}>{fmtDate()}{games.length?` · ${games.length} games`:""}</div>
      </div>
      {/* 💣 Slate HR projection — compact total */}
      <div style={{marginTop:12}}><SlateHrBanner compact/></div>

      {/* Track record — proof, surfaced daily (taps to full record) */}
      <HomeTrackRecord setView={setView}/>
      {/* The plays BEHIND that record — a clear, dedicated spot for the 70%+ graded set
          that makes up the win%. Sits right under the record number so the two connect. */}
      <button onClick={()=>setGradedOpen(true)} style={{width:"100%",marginTop:10,display:"flex",alignItems:"center",gap:10,background:`linear-gradient(90deg, ${T.blueDim}, ${T.card})`,border:`1px solid ${T.blue}`,borderRadius:14,padding:"12px 14px",color:T.blue,fontSize:13.5,fontWeight:800,cursor:"pointer"}}>
        <span style={{fontSize:15}}>◆</span>
        <span style={{flex:1,textAlign:"left"}}>Today's graded plays <span style={{color:T.muted2,fontWeight:600}}>· the 70%+ plays behind our record</span></span>
        <span style={{fontSize:13,color:T.muted2}}>→</span>
      </button>

      {/* 💣 Power Spots — the FILTERED board: only bats that clear the exact Power Spots
          criteria (homer-prone arm + hardest contact + main-pitch crush), not the broad
          Homer Signal. Opens the drawer built off _buildPowerSpots (same as the share card). */}
      <button onClick={()=>setPowerOpen(true)} style={{width:"100%",marginTop:8,display:"flex",alignItems:"center",gap:10,background:`linear-gradient(90deg, ${T.amberDim}, ${T.card})`,border:`1px solid ${T.amber}`,borderRadius:14,padding:"12px 14px",color:T.amber,fontSize:13.5,fontWeight:800,cursor:"pointer"}}>
        <span style={{fontSize:15}}>💣</span>
        <span style={{flex:1,textAlign:"left"}}>Power Spots <span style={{color:T.muted2,fontWeight:600}}>· hardest contact vs homer-prone arms</span></span>
        <span style={{fontSize:13,color:T.muted2}}>→</span>
      </button>

      {/* Top-15 button removed — "Today's Top Plays" (below the #1 card) is the flagship
          ranked list now, all markets in one view. */}
      <button onClick={()=>{ if(needPremium()) setStackOpen(true) }} style={{width:"100%",marginTop:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:14,padding:"13px",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer"}}>
        🎯 Perfect Stack — best shot to go perfect{!isPremium && <span style={{fontSize:9,fontWeight:800,color:T.amber,border:`1px solid ${T.amber}`,borderRadius:5,padding:"1px 5px",marginLeft:2}}>PRO</span>}
      </button>
      <button onClick={()=>{ if(needPremium()) setStormOpen(true) }} style={{width:"100%",marginTop:8,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:14,padding:"13px",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer"}}>
        🌪 HRR Storm — pitcher + zone + form align{!isPremium && <span style={{fontSize:9,fontWeight:800,color:T.amber,border:`1px solid ${T.amber}`,borderRadius:5,padding:"1px 5px",marginLeft:2}}>PRO</span>}
      </button>
      {stackOpen && <PerfectStackDrawer onClose={()=>setStackOpen(false)} onPlayer={onPlayer}/>}
      {stormOpen && <PerfectStormDrawer onClose={()=>setStormOpen(false)} onPlayer={onPlayer}/>}
      {powerOpen && <PowerSpotsDrawer onClose={()=>setPowerOpen(false)} onPlayer={onPlayer}/>}

      {/* Slate Read — whole-day briefing (live only; shell until the slate drops) */}
      {hasLive ? (
        <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"14px 16px",marginTop:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:9,color:T.green,fontWeight:700,letterSpacing:"0.12em",display:"flex",alignItems:"center",gap:6}}>SLATE READ {aiSlate && <span style={{fontSize:8,color:T.muted,border:`1px solid ${T.border2}`,borderRadius:4,padding:"1px 5px",letterSpacing:"0.06em"}}>AI</span>}</div>
            <button onClick={copySlate} style={{background:slateCopied?T.greenDim:"transparent",border:`1px solid ${slateCopied?T.green:T.border2}`,borderRadius:8,padding:"4px 10px",color:slateCopied?T.green:T.muted2,fontSize:10.5,fontWeight:700,cursor:"pointer"}}>{slateCopied?"Copied ✓":"⤓ Share"}</button>
          </div>
          <div style={{fontSize:13.5,color:T.text,lineHeight:1.55}}>{displaySlate}</div>
        </div>
      ) : (
        <Shell h={92} style={{marginTop:12,marginBottom:0}}/>
      )}

      {/* Top ranked today — shell until the slate drops */}
      {!hasLive && (<>
        <SectionLabel>Top ranked today</SectionLabel>
        <Shell h={150}/>
      </>)}
      {hasLive && topPlays[0] && (<>
        <SectionLabel>Top ranked today</SectionLabel>
        {(()=>{ const e=topPlays[0], c=confOf(e); return (
          <button onClick={()=>openPlay(e)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:`linear-gradient(135deg, ${T.card3}, ${T.card})`,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:16,boxShadow:`0 0 22px ${T.green}14`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                <span style={{fontSize:18,fontWeight:900,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.full_name}</span>
                <span style={{fontSize:11,color:T.muted2,fontFamily:T.mono}}>{mDisp(e.market)}</span>
              </div>
              <TierBadge tier={e.tier}/>
            </div>
            {/* Lead with Prob Over — it's what the board ranks by, and the #1 play is the
                highest-probability spot on the board, so its prob reads strong in context. */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
              <Stat label="Prob Over" value={pct(e.prob_over||0)} color={probColorMkt(e.prob_over||0, e._mkt||e.market)}/>
              <Stat label="Rating /100" value={c} color={confColor(c)} bg={T.greenDim}/>
              <Stat label="Edge" value={e.edge_pct!=null?`${e.edge_pct>=0?"+":""}${(e.edge_pct*100).toFixed(1)}%`:"—"} color={edgeColor(e.edge_pct!=null?e.edge_pct*100:null)}/>
            </div>
            <div style={{fontSize:11,color:T.muted,marginTop:8}}><span style={{color:T.green,fontWeight:700}}>🏆 #1 on today's board</span>{e.team?` · ${e.team}`:""}{e.opp_pitcher_name?` · vs ${e.opp_pitcher_name}${e.opp_pitcher_hand?` (${e.opp_pitcher_hand})`:""}`:""}</div>
          </button>
        )})()}
      </>)}

      {/* ★ Watchlist — watchlist on today's board */}
      {!loading && watch.count>0 && (<>
        <SectionLabel right="Rankings →" onRight={()=>setView("picks")}>★ Watchlist</SectionLabel>
        {myGuys.length>0
          ? myGuys.slice(0,6).map((e,i)=><HomePlayRow key={"mg-"+(e.player_id||e.full_name)+i} e={e} onOpen={openPlay}/>)
          : <div style={{color:T.muted,fontSize:12.5,padding:"6px 2px 10px",lineHeight:1.5}}>None of your watchlisted players have a play on today's board yet — check back after lineups post (~3 PM ET).</div>}
      </>)}

      {/* Top rankeds — shells until the slate drops */}
      {hasLive && <button onClick={()=>setTopOpen(true)} style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",color:T.green,fontSize:13.5,fontWeight:800,cursor:"pointer",marginBottom:12}}>
        <span style={{fontSize:16}}>🏆</span>
        <span style={{flex:1,textAlign:"left"}}>Today's Top Plays <span style={{color:T.muted2,fontWeight:600}}>· the best across every market, one list</span></span>
        <span style={{fontSize:13,color:T.muted2}}>→</span>
      </button>}
      <SectionLabel right="See all →" onRight={()=>setView("picks")}>Top rankings</SectionLabel>
      {!hasLive && [0,1,2,3].map(i=><Shell key={i}/>)}
      {hasLive && topPlays.slice(1).map((e,i)=><HomePlayRow key={(e.player_id||e.full_name)+'-'+e.market+i} e={e} onOpen={openPlay}/>)}

      {/* Top 2-leg */}
      {legs.length===2 && (<>
        <SectionLabel>Top 2-leg</SectionLabel>
        <div style={{background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"14px 16px"}}>
          {legs.map((e,i)=>(
            <button key={i} onClick={()=>openPlay(e)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:"none",border:"none",padding:0,display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:i===0?10:0}}>
              <span style={{fontSize:13,fontWeight:700}}><span style={{color:T.muted2,fontFamily:T.mono,fontSize:11}}>{mDisp(e.market)} </span>{e.full_name} <span style={{color:T.muted,fontWeight:400}}>· {e.team}</span></span>
              <span style={{fontSize:14,fontWeight:800,color:probColorMkt(e.prob_over||0, e.market),fontFamily:T.mono}}>{pct(e.prob_over||0)}</span>
            </button>
          ))}
          <div style={{borderTop:`1px solid ${T.border}`,marginTop:12,paddingTop:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:9,color:T.muted,letterSpacing:"0.1em"}}>COMBINED PROBABILITY</div>
              <div style={{fontSize:20,fontWeight:900,color:probColor(combo),fontFamily:T.mono}}>{pct(combo)}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:9,color:T.muted,letterSpacing:"0.1em"}}>FAIR ODDS</div>
              <div style={{fontSize:20,fontWeight:900,color:T.text,fontFamily:T.mono}}>{toAmerican(combo)}</div>
            </div>
          </div>
          <div style={{fontSize:10.5,color:T.muted,marginTop:8,lineHeight:1.4}}>Two highest-probability plays from different games (lower correlation).</div>
        </div>
        <div style={{marginTop:8}}><TwoLegRecordCard/></div>
      </>)}

      {/* Game to attack */}
      {gameToAttack && (<>
        <SectionLabel right="All games →" onRight={()=>setView("games")}>Game to attack</SectionLabel>
        <button onClick={()=>onGame(gameToAttack)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,padding:"14px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.02em"}}>{gameToAttack.away.abbr} <span style={{color:T.muted,fontWeight:600}}>@</span> {gameToAttack.home.abbr}</div>
              <div style={{fontSize:12,color:T.muted,marginTop:2}}>{gameToAttack.venue}{gameToAttack.time?` · ${gameToAttack.time}`:""}</div>
            </div>
            <GradeRing grade={gameToAttack.grade}/>
          </div>
          <div style={{fontSize:12,color:T.muted2,marginTop:10,lineHeight:1.4}}>
            Highest-graded game today — {gameToAttack.bestBets} best bets{gameToAttack.windOk?` · wind blowing out`:""}.
          </div>
        </button>
      </>)}

      {/* Pitchers to attack — deep-link into the Games-tab drawer (where the matchup
          content lives). Pairs naturally with "Game to attack" above. */}
      {hasLive && onAttack && (
        <button onClick={onAttack} style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",color:T.green,fontSize:13.5,fontWeight:800,cursor:"pointer",marginTop:12}}>
          <span style={{fontSize:16}}>🎯</span>
          <span style={{flex:1,textAlign:"left"}}>Pitchers to attack <span style={{color:T.muted2,fontWeight:600}}>· best hitter to target vs each arm</span></span>
          <span style={{fontSize:13,color:T.muted2}}>→</span>
        </button>
      )}

      {/* Home run shots */}
      {hrPlays.length>0 && (<>
        <SectionLabel right="All HR →" onRight={()=>setView("picks")}>Home run shots</SectionLabel>
        {hrPlays.map((e,i)=><HomePlayRow key={(e.player_id||e.full_name)+'-hr'+i} e={e} onOpen={openPlay}/>)}
      </>)}

      {/* Trending now — teaser into the Trends tab */}
      {(hotBat||coldArm) && (<>
        <SectionLabel right="All trends →" onRight={()=>setView("signals")}>Trending now · last 7d</SectionLabel>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {hotBat && (
            <button onClick={()=>hotBat.player_id?onPlayer({name:hotBat.name,player_id:hotBat.player_id,market:"HRR"}):setView("signals")} style={{textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"12px"}}>
              <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.1em",marginBottom:6}}>🔥 HOTTEST BAT</div>
              <div style={{fontSize:14,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{hotBat.name}</div>
              <div style={{fontSize:11,color:T.muted,marginTop:3}}>{hotBat.team} · {hotBat.games}G · {hotBat.hr} HR</div>
              <div style={{fontSize:16,fontWeight:900,color:T.green,fontFamily:T.mono,marginTop:6}}>{hotBat.hrr_per_game}<span style={{fontSize:9,color:T.muted,fontWeight:700}}> H+R+RBI/G</span></div>
            </button>
          )}
          {coldArm && (
            <button onClick={()=>setView("signals")} style={{textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px"}}>
              <div style={{fontSize:9,color:T.red,fontWeight:800,letterSpacing:"0.1em",marginBottom:6}}>🧊 ARM TO ATTACK</div>
              <div style={{fontSize:14,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{coldArm.name}</div>
              <div style={{fontSize:11,color:T.muted,marginTop:3}}>ERA {coldArm.era} · K/9 {coldArm.k_per_9}</div>
              <div style={{fontSize:16,fontWeight:900,color:T.red,fontFamily:T.mono,marginTop:6}}>{coldArm.hr9}<span style={{fontSize:9,color:T.muted,fontWeight:700}}> HR/9</span></div>
            </button>
          )}
        </div>
      </>)}

      <div style={{height:8}}/>
      {topOpen && <ErrorBoundary resetKey="topplays"><TopPlaysDrawer plays={flat} onClose={()=>setTopOpen(false)} onOpen={e=>{ setTopOpen(false); openPlay(e) }}/></ErrorBoundary>}
      {gradedOpen && <ErrorBoundary resetKey="gradedplays"><TopPlaysDrawer plays={flat} gradedOnly onClose={()=>setGradedOpen(false)} onOpen={e=>{ setGradedOpen(false); openPlay(e) }}/></ErrorBoundary>}
    </div>
  )
}

// ── Record tab (P&L tracking, localStorage) ─────────────────────────────────
const BETS_KEY = "linebrkr_bets_v1"
const loadBets = () => { try { return JSON.parse(localStorage.getItem(BETS_KEY) || "[]") } catch { return [] } }
const _n = v => { const x=parseFloat(v); return isNaN(x)?0:x }
const _profit = b => b.result==="Win" ? _n(b.ret)-_n(b.wager) : b.result==="Loss" ? -_n(b.wager) : 0
// CLV: did you beat the close? American → decimal, then (yours/close − 1).
const _amToDec = o => { const n=parseFloat(String(o).replace("+","")); if(isNaN(n)||n===0) return null; return n>0 ? 1+n/100 : 1+100/Math.abs(n) }
const _clv = b => { const y=_amToDec(b.odds), c=_amToDec(b.close); return (y&&c) ? (y/c-1)*100 : null }

function AddBetForm({onSave, onClose, initial}){
  const [f,setF] = useState(initial || {date:todayStr(), book:"DraftKings", tag:"HRR", type:"Straight", legs:"", wager:"", ret:"", result:"Pending"})
  const set = (k,v)=>setF(s=>({...s,[k]:v}))
  const inp = {width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"9px 11px",color:T.text,fontSize:13,outline:"none"}
  const lab = {fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.1em",margin:"10px 0 4px",textTransform:"uppercase"}
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"6px 16px 36px"}}>
        <div style={{fontSize:16,fontWeight:800,margin:"6px 0 4px"}}>{initial?"Edit bet":"Log a bet"}</div>
        {/* Money first — the fields you update per slip. Context (date/book/market) below. */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div><div style={lab}>Wager $ (pay in)</div><input style={inp} inputMode="decimal" value={f.wager} onChange={e=>set("wager",e.target.value)} placeholder="25"/></div>
          <div><div style={lab}>Return $ (pay out)</div><input style={inp} inputMode="decimal" value={f.ret} onChange={e=>set("ret",e.target.value)} placeholder="58"/></div>
          <div><div style={lab}>Your odds</div><input style={inp} value={f.odds||""} onChange={e=>set("odds",e.target.value)} placeholder="+120"/></div>
          <div><div style={lab}>Closing odds</div><input style={inp} value={f.close||""} onChange={e=>set("close",e.target.value)} placeholder="-110 (optional)"/></div>
          <div><div style={lab}>Date</div><input style={inp} value={f.date} onChange={e=>set("date",e.target.value)}/></div>
          <div><div style={lab}>Book</div><input style={inp} value={f.book} onChange={e=>set("book",e.target.value)} placeholder="DraftKings"/></div>
          <div><div style={lab}>Market</div><input style={inp} value={f.tag} onChange={e=>set("tag",e.target.value)} placeholder="HRR / HR / SGP"/></div>
          <div><div style={lab}>Type</div><input style={inp} value={f.type} onChange={e=>set("type",e.target.value)} placeholder="Straight / Parlay / SGP"/></div>
        </div>
        <div style={lab}>Legs / notes</div>
        <input style={inp} value={f.legs} onChange={e=>set("legs",e.target.value)} placeholder="e.g. Judge HRR + Soto TB"/>
        <div style={lab}>Result</div>
        <div style={{display:"flex",gap:8}}>
          {["Pending","Win","Loss","Push"].map(r=>{ const on=f.result===r; const c=r==="Win"?T.green:r==="Loss"?T.red:r==="Pending"?T.amber:T.muted; return (
            <button key={r} onClick={()=>set("result",r)} style={{flex:1,padding:"9px",borderRadius:8,background:on?`${c}22`:T.card,border:`1px solid ${on?c:T.border2}`,color:on?c:T.muted2,fontSize:13,fontWeight:700,cursor:"pointer"}}>{r}</button>
          )})}
        </div>
        <button onClick={()=>{ onSave(f); onClose() }} style={{width:"100%",marginTop:16,padding:"12px",borderRadius:9,background:T.green,border:"none",color:"#04140D",fontSize:14,fontWeight:800,cursor:"pointer"}}>Save bet</button>
      </div>
    </Sheet>
  )
}

// ── System (LINEBRKR) track record — real settled top-10 / top-25 outcomes ──
function SystemRecord(){
  const [data,setData] = useState(null)
  const [err,setErr]   = useState(null)
  const [tier,setTier] = useState("top10")   // top10 | top25
  const [days,setDays] = useState(30)
  const [trends,setTrends] = useState({})    // prop_type -> daily series
  const [elite,setElite] = useState(null)    // whale/elite settled record
  const [krec,setKrec] = useState(null)      // clean, deduped strikeouts record (still validating)
  useEffect(()=>{
    let live=true; setData(null); setErr(null); setElite(null); setKrec(null)
    getScorecardSummary(days)
      .then(d=>{ if(live) setData(d) })
      .catch(e=>{ if(live) setErr(e.message||"unavailable") })
    getEliteRecord(days)
      .then(d=>{ if(live) setElite(d) })
      .catch(()=>{})
    // Strikeouts record from its own clean, deduped source (the main scorecard
    // double-counted K). Shown as a market row with a 🧪 validating tag.
    getKRecord(days,10)
      .then(d=>{ if(live) setKrec(d) })
      .catch(()=>{ if(live) setKrec(null) })
    // Per-market daily trend (top-10 hit rate over time) for sparkline + form arrow.
    const types=["HRR_over_1.5","HR_anytime","hits","hits_1.5","total_bases"]
    Promise.all(types.map(t=>getScorecardTrend(days,t).then(d=>[t,d?.series||[]]).catch(()=>[t,[]])))
      .then(pairs=>{ if(live) setTrends(Object.fromEntries(pairs)) })
    return ()=>{ live=false }
  },[days])
  // TODO(playoff-label): the "Top 10" tier label is accurate in the regular season but on
  // postseason days the graded tier is slate-scaled (min(10, max(3, 15% of slate)) — see
  // backend scorecard.py). Once the playoff record has real volume and we post it publicly,
  // make this label honest for playoffs (e.g. "Top picks" or show the per-day graded count
  // from top_n_graded) instead of the fixed "Top 10". Harmless until then — the W/L + hit
  // rate are already computed from the real graded count.
  const W = tier==="top10" ? "top10_wins"  : "top25_wins"
  const L = tier==="top10" ? "top10_losses": "top25_losses"
  const H = tier==="top10" ? "hit_rate_top_10" : "hit_rate_top_25"
  // Always show the core hitter+K markets so 1+ H / 2+ H are visibly tracked even
  // before their first settled day (they fill in as results accumulate). Doubles
  // stays dropped. Real summary row when present, else a zeroed placeholder.
  // Strikeouts is a TESTING market — shown in its own <KRecordCard/> (from the
  // deduped, K-epoch-floored /free/k-record), NOT the public per-market list, whose
  // source floors at the general model epoch and double-counted K. (That mismatch was
  // the wrong "4-26".)
  const CORE_MARKETS = ["HRR_over_1.5","HR_anytime","hits","hits_1.5","total_bases"]
  const byProp = Object.fromEntries((data?.markets||[]).map(m=>[m.prop_type,m]))
  // Strikeouts now shows as a market row too — sourced from the CLEAN k-record (not the
  // double-counting scorecard) and tagged testing, so it reads like every other market
  // while it validates. Its W/L are overall (K isn't tier-split), same across top10/25.
  const kRow = krec && !krec.error
    ? {prop_type:"strikeouts", testing:true, [W]:krec.wins||0, [L]:krec.losses||0, [H]:krec.hit_rate||0}
    : null
  const markets = data ? [...CORE_MARKETS.map(pt => byProp[pt] || {prop_type:pt,[W]:0,[L]:0,[H]:0}), ...(kRow?[kRow]:[])] : []
  const totN = markets.reduce((s,m)=>s+(m[W]||0)+(m[L]||0),0)   // volume only — no blended rate
  // No cross-market hit rate: HR Anytime's base rate is far below HRR, so a single
  // blended % is misleading. ROI omitted until computed from real bet-slip prices.
  const pillS = on => ({padding:"6px 12px",borderRadius:8,fontSize:12,fontWeight:800,cursor:"pointer",border:`1px solid ${on?T.green:T.border2}`,background:on?`${T.green}22`:T.card,color:on?T.green:T.muted2})
  // Track record only counts the new-formula era; the API floors the window at
  // the model epoch, so `start` reflects the real first day included.
  const prettyDate = s => { if(!s) return ""; const [y,m,d]=s.split("-").map(Number); return new Date(y,m-1,d).toLocaleDateString("en-US",{month:"short",day:"numeric"}) }
  const sinceTxt = data?.start ? `since the new model · ${prettyDate(data.start)}` : "since the new model"

  return (
    <div>
      {/* Elite picks hit rate — the headline, ALWAYS the first thing shown */}
      <div style={{background:T.card3,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"16px",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.12em",marginBottom:5}}>◆ PROVEN · 70%+ PICKS — HIT RATE</div>
          {elite && elite.bets>0 ? (
            <div style={{display:"flex",alignItems:"baseline",gap:10}}>
              <span style={{fontSize:34,fontWeight:900,color:T.green,fontFamily:T.mono,lineHeight:1}}>{Math.round(elite.hit_rate*100)}%</span>
              <span style={{fontSize:14,fontWeight:800,color:T.text,fontFamily:T.mono}}>{elite.wins}–{elite.losses}</span>
            </div>
          ) : (
            <div style={{fontSize:15,fontWeight:800,color:T.muted2}}>{data===null?"Loading…":"No 70%+ picks settled yet"}</div>
          )}
          <div style={{fontSize:10.5,color:T.muted,marginTop:5}}>Model's 70%+ probability rankings · {sinceTxt}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:18,fontWeight:900,color:T.text,fontFamily:T.mono}}>{elite&&elite.bets>0?elite.bets:(data===null?"…":0)}</div>
          <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.1em",marginTop:2}}>TOP PLAYS<br/>GRADED</div>
        </div>
      </div>
      <div style={{fontSize:11,color:T.muted,lineHeight:1.5,margin:"0 2px 14px"}}>
        The headline is the model's <b style={{color:T.text}}>top-ranked plays only</b> (70%+ model probability). Per-market hit rates below aren't comparable — HR Anytime naturally lands far below HRR. Flat 1-unit, real settled outcomes; pre-update results excluded.
      </div>

      {/* Tier + window controls */}
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <button onClick={()=>setTier("top10")} style={pillS(tier==="top10")}>Top 10</button>
        <button onClick={()=>setTier("top25")} style={pillS(tier==="top25")}>Top 25</button>
        <div style={{flex:1}}/>
        {[7,30,90].map(d=>(
          <button key={d} onClick={()=>setDays(d)} style={{...pillS(days===d),padding:"6px 9px"}}>{d}d</button>
        ))}
      </div>

      {err ? (
        <div style={{textAlign:"center",color:T.muted,padding:"40px 16px",fontSize:13,lineHeight:1.6}}>
          Track record unavailable right now.<br/><span style={{fontSize:11}}>{err}</span>
        </div>
      ) : data===null ? (
        <div style={{textAlign:"center",color:T.muted,padding:"40px 0",fontSize:13}}>Loading track record…</div>
      ) : totN===0 ? (
        <div style={{textAlign:"center",color:T.muted,padding:"40px 16px",fontSize:13,lineHeight:1.6}}>
          No settled results {sinceTxt} yet.<br/>
          The track record fills in as games settle each night.
        </div>
      ) : (<>
        <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>HIT RATE BY MARKET</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {markets.map(m=>{
            const n=(m[W]||0)+(m[L]||0)
            const h=(m[H]||0)*100, avg=m[H]||0
            const series=trends[m.prop_type]||[]
            const spark=series.slice(-10)
            return (
              <div key={m.prop_type} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"11px 12px",opacity:n>0?1:0.72}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:14,fontWeight:800,color:T.text}}>{PROP_SHORT[m.prop_type]||m.prop_type}{m.testing && <span style={{fontSize:8,fontWeight:800,color:T.amber,background:`${T.amber}22`,border:`1px solid ${T.amber}`,borderRadius:5,padding:"1px 5px",marginLeft:6,letterSpacing:"0.04em",verticalAlign:"middle"}}>🧪 VALIDATING</span>}</span>
                  {n>0 ? (
                  <span style={{display:"flex",gap:14,alignItems:"center"}}>
                    <span style={{fontSize:12,color:T.muted2,fontFamily:T.mono}}>{m[W]}–{m[L]}</span>
                    <span style={{fontSize:15,fontWeight:900,color:T.text,fontFamily:T.mono,minWidth:40,textAlign:"right"}}>{h.toFixed(0)}%</span>
                  </span>
                  ) : <span style={{fontSize:11,color:T.muted,fontWeight:700}}>tracking — awaiting results</span>}
                </div>
                {n>0 && <div style={{display:"flex",alignItems:"center",marginTop:8}}>
                  {spark.length ? (
                    <span style={{display:"inline-flex",alignItems:"flex-end",gap:2,height:18}}>
                      {spark.map((d,i)=>{ const hr=d.hit_rate||0; const good=hr>=avg
                        return <span key={i} title={`${d.date}: ${Math.round(hr*100)}%`} style={{width:5,height:Math.max(2,Math.round(hr*18)),background:good?T.green:T.muted,borderRadius:1,opacity:0.9}}/> })}
                    </span>
                  ) : <span style={{fontSize:10,color:T.muted}}>building trend…</span>}
                </div>}
              </div>
            )
          })}
        </div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:10}}>
          Bars = last 10 settled days (green = above this market's average). ROI arrives with bet-slip import.
        </div>
      </>)}
      <div style={{marginTop:12}}><KRecordCard data={krec}/></div>
    </div>
  )
}

function MyRecord(){
  const [bets,setBets] = useState(loadBets)
  const [showForm,setShowForm] = useState(false)
  const [editing,setEditing] = useState(null)
  const upd = b => { setBets(b); try{ localStorage.setItem(BETS_KEY, JSON.stringify(b)) }catch{} }
  const save = bet => { if(bet.id) upd(bets.map(b=>b.id===bet.id?bet:b)); else upd([{...bet, id:Date.now()}, ...bets]) }
  const del = id => upd(bets.filter(b=>b.id!==id))
  const openNew  = ()=>{ setEditing(null); setShowForm(true) }
  const openEdit = b =>{ setEditing(b); setShowForm(true) }

  const pending = bets.filter(b=>b.result==="Pending")
  const settled = bets.filter(b=>b.result!=="Pending")
  const net = settled.reduce((s,b)=>s+_profit(b),0)
  const wins = bets.filter(b=>b.result==="Win").length
  const losses = bets.filter(b=>b.result==="Loss").length
  const staked = settled.reduce((s,b)=>s+_n(b.wager),0)   // ROI on settled only
  const roi = staked ? net/staked*100 : 0
  const clvBets = bets.map(_clv).filter(v=>v!=null)
  const beatClose = clvBets.filter(v=>v>0).length
  const avgClv = clvBets.length ? clvBets.reduce((s,v)=>s+v,0)/clvBets.length : null
  const group = key => {
    const m={}
    for(const b of bets){ const k=b[key]||"—"; m[k]=m[k]||{w:0,l:0,net:0}; if(b.result==="Win")m[k].w++; else if(b.result==="Loss")m[k].l++; m[k].net+=_profit(b) }
    return Object.entries(m).sort((a,b)=>b[1].net-a[1].net)
  }
  const money = v => `${v>=0?"+":"−"}$${Math.abs(Math.round(v)).toLocaleString()}`

  return (
    <div style={{position:"relative"}}>
      {bets.length===0 ? (
        <div style={{textAlign:"center",color:T.muted,padding:"50px 16px",fontSize:13,lineHeight:1.6}}>
          No bets logged yet.<br/>Tap + to log your first wager and track P&amp;L, ROI, and which books pay.
        </div>
      ) : (<>
        {/* Hero */}
        <div style={{background:T.card3,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"16px",marginBottom:14,display:"flex",alignItems:"baseline",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:30,fontWeight:900,color:net>=0?T.green:T.red,fontFamily:T.mono}}>{money(net)}</div>
          <div style={{fontSize:16,fontWeight:800,color:T.text,fontFamily:T.mono}}>{wins}W / {losses}L{pending.length?` · ${pending.length} pending`:""}</div>
          <div style={{fontSize:16,fontWeight:900,color:roi>=0?T.green:T.red,fontFamily:T.mono}}>{roi>=0?"+":""}{roi.toFixed(0)}% ROI</div>
        </div>

        {/* CLV — did you beat the closing line? (only when odds were entered) */}
        {clvBets.length>0 && (
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"11px 14px",marginBottom:14,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.1em"}}>CLV · BEAT THE CLOSE</div>
              <div style={{fontSize:11,color:T.muted2,marginTop:2}}>Your line vs the close — the sharpest sign of +EV.</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:17,fontWeight:900,color:avgClv>=0?T.green:T.red,fontFamily:T.mono}}>{avgClv>=0?"+":""}{avgClv.toFixed(1)}%</div>
              <div style={{fontSize:11,color:T.muted,fontFamily:T.mono}}>beat {beatClose}/{clvBets.length}</div>
            </div>
          </div>
        )}

        {[["BY MARKET","tag"],["BY BOOK","book"]].map(([label,key])=>(
          <div key={key} style={{marginBottom:14}}>
            <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>{label}</div>
            <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,overflow:"hidden"}}>
              {group(key).map(([k,v],i)=>{ const bad=key==="book"&&v.w===0&&v.l>0; return (
                <div key={k} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 12px",borderTop:i?`1px solid ${T.border}`:"none"}}>
                  <span style={{fontSize:13,fontWeight:700,color:bad?T.red:T.text}}>{k}{bad?" ✕":""}</span>
                  <span style={{display:"flex",gap:12,alignItems:"center"}}>
                    <span style={{fontSize:12,color:T.muted2,fontFamily:T.mono}}>{v.w}–{v.l}</span>
                    <span style={{fontSize:13,fontWeight:800,color:v.net>=0?T.green:T.red,fontFamily:T.mono}}>{money(v.net)}</span>
                  </span>
                </div>
              )})}
            </div>
          </div>
        ))}

        <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>ALL BETS</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {bets.map(b=>{ const pend=b.result==="Pending"; const rc=b.result==="Win"?T.green:b.result==="Loss"?T.red:pend?T.amber:T.muted; return (
            <div key={b.id} onClick={()=>openEdit(b)} style={{background:T.card,border:`1px solid ${pend?T.amber:T.border2}`,borderRadius:10,padding:"10px 12px",cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:700}}><span style={{color:T.muted2,fontFamily:T.mono,fontSize:11}}>{b.tag} </span>{b.legs||b.type}</span>
                <span style={{display:"flex",alignItems:"center",gap:8}}>
                  {_clv(b)!=null && <span style={{fontSize:10,fontWeight:800,fontFamily:T.mono,color:_clv(b)>=0?T.green:T.red}}>CLV {_clv(b)>=0?"+":""}{_clv(b).toFixed(1)}%</span>}
                  <span style={{fontSize:13,fontWeight:800,color:pend?T.amber:(_profit(b)>=0?T.green:T.red),fontFamily:T.mono}}>{pend?"PENDING":money(_profit(b))}</span>
                </span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
                <span style={{fontSize:11,color:T.muted}}>{b.date} · {b.book} · ${_n(b.wager)}{pend?` to win $${_n(b.ret)}`:`→$${_n(b.ret)}`} · <span style={{color:rc}}>{b.result}</span>{pend?" · tap to settle":""}</span>
                <button onClick={(e)=>{e.stopPropagation(); del(b.id)}} style={{background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer",padding:"2px 4px"}}>✕</button>
              </div>
            </div>
          )})}
        </div>
      </>)}

      <button onClick={openNew} style={{position:"fixed",bottom:72,right:"max(16px, calc(50% - 230px + 16px))",width:50,height:50,borderRadius:"50%",background:T.green,border:"none",color:"#04140D",fontSize:26,fontWeight:800,cursor:"pointer",boxShadow:`0 4px 16px ${T.green}55`,zIndex:120}}>+</button>
      {showForm && <AddBetForm onSave={save} initial={editing} onClose={()=>{setShowForm(false); setEditing(null)}}/>}
    </div>
  )
}

// Closing Line Value — did our number beat the closing line. The proof-of-edge metric.
function ClvCard(){
  const { data, loading } = useFetch(()=>getClv(30).catch(()=>null), [])
  const has = data && data.n>0
  const avg = has ? data.avg_clv*100 : null
  const beat = has ? Math.round(data.beat_rate*100) : null
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"12px 14px",marginBottom:14}}>
      <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.12em",marginBottom:6}}>📈 CLOSING LINE VALUE</div>
      {loading ? <div style={{fontSize:11,color:T.muted}}>Loading…</div>
       : !has ? <div style={{fontSize:11.5,color:T.muted,lineHeight:1.5}}>Accumulating — CLV builds from each odds refresh forward. Once we have a few days it shows whether our number beats the closing line (the cleanest proof of edge).</div>
       : (<div style={{display:"flex",alignItems:"baseline",gap:14}}>
            <div>
              <span style={{fontSize:26,fontWeight:900,color:avg>=0?T.green:T.red,fontFamily:T.mono,lineHeight:1}}>{avg>=0?"+":""}{avg.toFixed(2)}</span>
              <span style={{fontSize:11,color:T.muted,marginLeft:4}}>pts avg CLV</span>
            </div>
            <div>
              <span style={{fontSize:18,fontWeight:900,color:beat>=50?T.green:T.amber,fontFamily:T.mono}}>{beat}%</span>
              <span style={{fontSize:11,color:T.muted,marginLeft:4}}>beat the close</span>
            </div>
            <span style={{fontSize:9,color:T.muted,marginLeft:"auto"}}>n={data.n}</span>
          </div>)}
    </div>
  )
}

// Top 2-leg record — the app's actual suggestion (two highest-prob plays,
// different teams, all markets), tucked away / collapsible. Starts at the model
// epoch (2026-07-07) so the record reflects ONLY the current model, not old-model days.
function TwoLegRecordCard(){
  const [since,setSince] = useState('2026-07-07')
  const [open,setOpen] = useState(false)
  const { data, loading } = useFetch(()=>getTwoLegRecord(since).catch(()=>null), [since])
  const daily = (data?.daily||[]).filter(d=>d.legs).slice().reverse()  // newest first
  const wins = data?.wins||0, n = data?.n||0
  const rec = n?`${wins}-${n-wins}`:'—'
  const wr  = n?`${Math.round((data.rate||0)*100)}%`:''
  const md  = s => { const p=(s||'').split('-'); return p.length===3?`${+p[1]}/${+p[2]}`:s }
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden",marginBottom:14}}>
      {/* Collapsed header — tap to expand */}
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:"transparent",border:"none",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:11,color:T.muted2,fontWeight:800,letterSpacing:"0.06em"}}>🎯 2-LEG PARLAY RECORD</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14,fontWeight:900,color:T.green,fontFamily:T.mono}}>{loading?"…":rec}</span>
          {!loading && n>0 && <span style={{fontSize:10,color:T.muted}}>{wr}</span>}
          <span style={{color:T.muted,fontSize:12,transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
        </div>
      </button>

      {open && (<>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 14px 10px",gap:8}}>
          <span style={{fontSize:10,color:T.muted}}>Two highest-prob plays, different teams</span>
          <input type="date" value={since} max={todayStr()} onChange={e=>setSince(e.target.value)} style={{padding:"3px 7px",borderRadius:6,fontSize:11,fontFamily:T.mono,border:`1px solid ${T.green}`,background:T.greenDim,color:T.green,colorScheme:"dark"}}/>
        </div>
        {loading && <div style={{padding:"14px",textAlign:"center",color:T.muted,fontSize:11}}>Loading…</div>}
        {!loading && !data && <div style={{padding:"14px",textAlign:"center",color:T.muted,fontSize:11}}>Record unavailable</div>}
        {!loading && data && daily.length===0 && <div style={{padding:"14px",textAlign:"center",color:T.muted,fontSize:11}}>No settled days since {md(since)} yet</div>}
        {!loading && daily.map((d,i)=>(
          <div key={d.date||i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderTop:`1px solid ${T.border}`}}>
            <span style={{fontSize:13}}>{d.won?"✅":"❌"}</span>
            <span style={{fontSize:10,color:T.muted,fontFamily:T.mono,width:36}}>{md(d.date)}</span>
            <span style={{flex:1,textAlign:"right",fontSize:10.5,color:T.text}}>{d.legs.map(l=>`${(l.name||"").split(" ").slice(-1)[0]} ${l.mkt} ${l.hit?"✓":"✗"}`).join("  +  ")}</span>
          </div>
        ))}
        {!loading && data && daily.length>0 && (
          <div style={{padding:"7px 14px",background:T.card3,borderTop:`1px solid ${T.border}`}}>
            <span style={{fontSize:8.5,color:T.muted}}>The daily Best 2-leg suggestion. Auto-updates as games settle. Updated through {md(daily[0].date)}.</span>
          </div>
        )}
      </>)}
    </div>
  )
}

// Strong Grid Standouts record — grades ONLY the board's 🔥 STRONG plays (not
// every pick), per market, from the model epoch forward. Backed by /scorecard/
// grid-record (kind=strong), which floors its window at the epoch server-side.
function GridStandoutsCard(){
  const [open,setOpen] = useState(false)
  const [rows,setRows] = useState(null)   // [{market, picks, wins, losses, hit_rate}]
  useEffect(()=>{
    let live=true; setRows(null)
    const markets=["HRR_over_1.5","HR_anytime","hits","hits_1.5","total_bases","strikeouts"]
    Promise.all(markets.map(m=>getGridRecord(30,m,"strong")
      .then(d=>({market:m, ...(d?.totals||{})})).catch(()=>({market:m}))))
      .then(rs=>{ if(live) setRows(rs) })
    return ()=>{ live=false }
  },[])
  const graded = (rows||[]).filter(r=>(r.picks||0)>0)
  const wins = graded.reduce((s,r)=>s+(r.wins||0),0)
  const losses = graded.reduce((s,r)=>s+(r.losses||0),0)
  const n = wins+losses
  const rec = n?`${wins}-${losses}`:'—'
  const wr  = n?`${Math.round((wins/n)*100)}%`:''
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,overflow:"hidden",marginBottom:14}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:"transparent",border:"none",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px"}}>
        <span style={{fontSize:11,color:T.muted2,fontWeight:800,letterSpacing:"0.06em"}}>🔥 STRONG GRID STANDOUTS</span>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:14,fontWeight:900,color:T.green,fontFamily:T.mono}}>{rows===null?"…":rec}</span>
          {n>0 && <span style={{fontSize:10,color:T.muted}}>{wr}</span>}
          <span style={{color:T.muted,fontSize:12,transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
        </div>
      </button>
      {open && (<>
        <div style={{padding:"0 14px 10px",fontSize:10,color:T.muted}}>Only the board's 🔥 STRONG plays — not every pick. Since the model epoch.</div>
        {rows===null && <div style={{padding:"14px",textAlign:"center",color:T.muted,fontSize:11}}>Loading…</div>}
        {rows!==null && graded.length===0 && <div style={{padding:"14px",textAlign:"center",color:T.muted,fontSize:11}}>No strong standouts settled yet</div>}
        {graded.map(r=>(
          <div key={r.market} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 14px",borderTop:`1px solid ${T.border}`}}>
            <span style={{fontSize:12,fontWeight:700,color:T.text}}>{PROP_SHORT[r.market]||r.market}</span>
            <span style={{display:"flex",gap:12,alignItems:"center"}}>
              <span style={{fontSize:11,color:T.muted2,fontFamily:T.mono}}>{r.wins||0}-{r.losses||0}</span>
              <span style={{fontSize:13,fontWeight:900,color:T.text,fontFamily:T.mono,minWidth:38,textAlign:"right"}}>{r.hit_rate!=null?`${Math.round(r.hit_rate*100)}%`:"—"}</span>
            </span>
          </div>
        ))}
      </>)}
    </div>
  )
}

// ── Model Validation — the honest, per-market proof scorecard ────────────────
// Surfaces /scorecard/validation (was Admin-only): for each bet type, the verdict
// (PROVEN / PROMISING / NEEDS WORK / INSUFFICIENT DATA) plus the three numbers it's
// built from — calibration honesty, CLV (did we beat the close), and ROI. This is
// the trust moat: a data hub that PROVES its edge, not one that just shows stats.
function ValidationScorecard(){
  const { data, loading } = useFetch(()=>getValidation(45).catch(()=>null), [])
  const markets = data?.markets || {}
  const ORDER = ["HRR_over_1.5","HR_anytime","hits","hits_1.5","total_bases","strikeouts"]
  const V = {
    "PROVEN":            {c:T.green, bg:T.greenDim, b:T.greenMid, icon:"✅"},
    "PROMISING":         {c:T.blue,  bg:T.blueDim,  b:T.blueLt,   icon:"📈"},
    "NEEDS WORK":        {c:T.red,   bg:T.redDim,   b:T.red,      icon:"⚠️"},
    "INSUFFICIENT DATA": {c:T.muted2,bg:T.card,     b:T.border2,  icon:"⏳"},
  }
  const has = ORDER.filter(m=>markets[m])
  const stat = (label,val,col) => (
    <div style={{textAlign:"center",flex:1}}>
      <div style={{fontSize:14,fontWeight:900,fontFamily:T.mono,color:col||T.text}}>{val}</div>
      <div style={{fontSize:8.5,color:T.muted,fontWeight:800,letterSpacing:"0.08em",marginTop:2}}>{label}</div>
    </div>
  )
  return (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:"0.12em",marginBottom:8}}>MODEL VALIDATION — IS EACH BET TYPE PROVEN?</div>
      {loading ? <div style={{height:90,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,opacity:0.45}}/>
      : has.length===0 ? (
        <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"14px",color:T.muted,fontSize:12.5,lineHeight:1.6}}>
          No settled markets yet since the model went live. Each verdict needs enough graded bets to trust — this fills in over the next couple weeks.
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {has.map(m=>{
            const d=markets[m]; const v=V[d.verdict]||V["INSUFFICIENT DATA"]
            const roi=d.roi?.roi, clv=d.clv?.avg_clv, gap=d.calibration_gap, hr=d.hit_rate
            // Lead with the ENDORSED (Rated 70+) hit rate, not the all-picks rate —
            // the all-picks number grades every longshot (HR ~9%) and reads misleadingly.
            // Only show a 70+ ROI once there are enough PRICED endorsed plays to trust it
            // (same >=25 bar the verdict uses) — otherwise "—", so a noisy ROI can't sit
            // next to a badge that ignored it.
            const rt=d.rating||{}, topHr=(rt.n_top>0)?rt.top_hit_rate:null, topRoi=(rt.n_top_priced>=25)?rt.top_roi:null
            const rthr=rt.thresh??70   // per-market endorsement bar (HR/2+H/TB run lower than 70)
            // Color the HIT% by LIFT over the field, not the absolute number — 24% is red
            // on an absolute scale but is elite for HR (field hits 6%). Green = beats the
            // market's field; amber = flat (rating not separating); red = worse than field.
            const hitCol = (topHr==null) ? T.muted2
                         : (rt.lift==null) ? T.text
                         : rt.lift>=0.04 ? T.green : rt.lift>=0.01 ? "#34D399" : rt.lift<=-0.02 ? T.red : T.amber
            return (
              <div key={m} style={{background:T.card,border:`1px solid ${v.b}`,borderRadius:12,padding:"12px 13px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:15,fontWeight:900}}>{d.label}</span>
                    <span style={{fontSize:9.5,color:T.muted,fontWeight:700}}>n={d.n_settled} tested</span>
                  </div>
                  <span style={{fontSize:10,fontWeight:900,color:v.c,background:v.bg,border:`1px solid ${v.b}`,borderRadius:6,padding:"3px 8px",letterSpacing:"0.04em"}}>{v.icon} {d.verdict}</span>
                </div>
                <div style={{display:"flex",gap:4}}>
                  {stat(`${rthr}+ HIT%`, topHr!=null?pct(topHr):(hr!=null?pct(hr):"—"), hitCol)}
                  {/* ROI tile removed while paused (was a permanent "—" that read as broken);
                      it's computed on model-implied odds, not real book odds. Re-add when the
                      live odds feed powers it. */}
                  {stat("CLV", clv!=null?`${clv>=0?"+":""}${(clv*100).toFixed(1)}`:"—", clv==null?T.muted2:clv>0?T.green:T.red)}
                  {stat(gap!=null&&gap>0?"OVER BY":"CALIB", gap!=null?`${Math.abs(gap*100).toFixed(0)}pt`:"—", gap==null?T.muted2:Math.abs(gap)<=0.03?T.green:Math.abs(gap)<=0.06?T.amber:T.red)}
                </div>
                {d.rating && d.rating.n_top>0 && (
                  <div style={{marginTop:9,paddingTop:9,borderTop:`1px solid ${T.border}`,fontSize:10.5,color:T.muted,display:"flex",alignItems:"center",gap:9,flexWrap:"wrap"}}>
                    <span style={{fontWeight:800,color:T.text,letterSpacing:"0.02em"}}>Rating proof</span>
                    <span>Rated {rthr}+ <b style={{color:d.rating.top_hit_rate!=null?probColor(d.rating.top_hit_rate):T.muted2}}>{d.rating.top_hit_rate!=null?pct(d.rating.top_hit_rate):"—"}</b> <span style={{color:T.muted2}}>(n={d.rating.n_top})</span></span>
                    <span style={{color:T.muted2}}>vs rest {d.rating.rest_hit_rate!=null?pct(d.rating.rest_hit_rate):"—"}</span>
                    {d.rating.lift!=null && <span style={{fontWeight:800,color:d.rating.lift>0?T.green:d.rating.lift<0?T.red:T.muted2}}>{d.rating.lift>0?"+":""}{Math.round(d.rating.lift*100)}pt lift</span>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      <div style={{fontSize:10,color:T.muted,lineHeight:1.5,marginTop:8}}>
        The <b style={{color:T.text}}>HIT%</b> is our endorsed (top-Rated) plays only, on the same reads — not every projection. The Rating bar is set per market (HRR/1+H at 70+, HR/2+H higher since only the highest conviction is worth it, TB lower) so "endorsed" means the top slice of each. <b style={{color:T.text}}>ROI is paused</b> — it needs live book odds, not model-implied prices, to be honest; back once real odds feed it · <b style={{color:T.text}}>CLV</b> &amp; <b style={{color:T.text}}>calibration</b> use the full <b style={{color:T.text}}>n tested</b> to validate the model is honest (a shown 65% really hits ~65%) — that's what the sample is for, not a bet record · <b style={{color:T.text}}>verdict</b> judges the model overall (calibration + hit-rate lift); a market's top reads can be fine while the full model still needs work · <b style={{color:T.text}}>Rating proof</b> = our top-Rated reads vs the rest. Floored at the model epoch.
      </div>
    </div>
  )
}

// ── Hit-rate headline — "hit at high %", the Record-tab lead ─────────────────
// Wires the already-computed per-market hit rates (/stats/trust-anchor, which
// excludes strikeouts) into one honest headline: our top-rated plays' hit rate,
// with a Top 10 / Top 25 / All toggle and a per-market breakdown. HR anytime is a
// longshot market shown separately (a low HR hit rate is expected, never hidden).
const HR_MK = {
  "HRR_over_1.5": ["HRR", "H+R+RBI ≥ 2", false],
  "hits":         ["1+ Hits", "high-floor", false],
  "hits_1.5":     ["2+ Hits", "", false],
  "total_bases":  ["Total Bases", "", false],
  "HR_anytime":   ["HR Anytime", "longshot", true],
}
function HitRateHeadline(){
  const { data, loading } = useFetch(()=>getTrustAnchor(30).catch(()=>null), [])
  const [tier,setTier] = useState("top10")
  const mkts = data?.markets || []
  const rateN = (m) => tier==="top10" ? [m.hit_rate_top_10, (m.top10_wins||0)+(m.top10_losses||0)]
                     : tier==="top25" ? [m.hit_rate_top_25, (m.top25_wins||0)+(m.top25_losses||0)]
                     :                   [m.hit_rate_all,    m.sample_size||0]
  const rows = mkts.map(m=>{
    const [rate,n] = rateN(m); const meta = HR_MK[m.prop_type] || [m.prop_type, "", false]
    return { pt:m.prop_type, label:meta[0], sub:meta[1], longshot:meta[2], rate, n }
  }).filter(r=>r.n>0 && r.rate!=null).sort((a,b)=>b.rate-a.rate)
  // Headline blends the CONTACT/PRODUCTION markets (the "hit at high %" markets).
  // HR anytime is a longshot — shown in the bars, but not blended into a hit-rate
  // headline it would drag down. Nothing hidden; HR is right there below.
  const core = rows.filter(r=>!r.longshot)
  const totN = core.reduce((s,r)=>s+r.n,0)
  const blended = totN ? core.reduce((s,r)=>s+r.rate*r.n,0)/totN : null
  const tierLabel = tier==="top10"?"top-rated (top 10)" : tier==="top25"?"top-rated (top 25)" : "all rated"
  if(loading) return <div style={{height:230,background:T.card,border:`1px solid ${T.border}`,borderRadius:16,opacity:0.45,marginBottom:12}}/>
  if(!rows.length) return null
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:16,padding:"20px 20px 18px",marginBottom:12}}>
      <div style={{fontSize:10,color:T.muted,fontWeight:800,letterSpacing:"0.14em",textTransform:"uppercase"}}>Top-rated plays · last 30 days</div>
      {blended!=null && (
        <div style={{display:"flex",alignItems:"baseline",gap:12,marginTop:8}}>
          <div style={{fontSize:52,fontWeight:900,letterSpacing:"-0.03em",lineHeight:0.9,color:T.green,fontFamily:T.mono}}>{pct(blended)}</div>
          <div style={{fontSize:14,fontWeight:800,color:T.text,lineHeight:1.25}}>of our <b style={{color:T.green}}>{tierLabel}</b> plays in high-floor markets cashed
            <span style={{display:"block",fontSize:11,color:T.muted,fontWeight:600,marginTop:3}}>across {totN} graded plays · wins and misses both counted</span></div>
        </div>
      )}
      <div style={{display:"flex",gap:6,marginTop:16}}>
        {[["top10","Top 10"],["top25","Top 25"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTier(k)} style={{fontSize:12,fontWeight:800,padding:"6px 12px",borderRadius:8,cursor:"pointer",
            border:`1px solid ${tier===k?T.green:T.border2}`, color:tier===k?"#04140D":T.muted, background:tier===k?T.green:T.card2}}>{l}</button>
        ))}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:14}}>
        {rows.map(r=>(
          <div key={r.pt} style={{display:"flex",alignItems:"center",gap:12,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:11,padding:"11px 14px"}}>
            <div style={{flex:"none",width:96}}><div style={{fontSize:13.5,fontWeight:800}}>{r.label}</div>{r.sub&&<div style={{fontSize:10,color:T.muted,fontWeight:600}}>{r.sub}</div>}</div>
            <div style={{flex:1,height:10,background:"#1A2430",borderRadius:5,overflow:"hidden"}}>
              <div style={{width:`${Math.round(r.rate*100)}%`,height:"100%",borderRadius:5,background:r.longshot?"linear-gradient(90deg,#E8B04B,#B8791F)":"linear-gradient(90deg,#00D97E,#16A34A)"}}/>
            </div>
            <div style={{flex:"none",width:92,textAlign:"right",fontSize:17,fontWeight:900,fontVariantNumeric:"tabular-nums",color:r.longshot?T.amber:T.text}}>{pct(r.rate)} <span style={{fontSize:10.5,color:T.muted,fontWeight:600}}>· n={r.n}</span></div>
          </div>
        ))}
      </div>
      <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:12}}>
        <b style={{color:T.text}}>Top-rated = the plays we rank highest.</b> HR anytime is a longshot market — a lower hit rate there is expected, and we show it anyway. Strikeouts are excluded while in testing. Floored at the model epoch.
      </div>
    </div>
  )
}

// ── Yesterday's Results drawer — the settled top-10 board per market, graded ─────
// Pulls /free/record-detail for yesterday ET: the exact plays that went in the public
// record, each with ✅/❌, actual vs line, and Rating. Tapping a row opens that player's
// card. Same selection as the Top-10 board, so this is that board with outcomes on it.
const YR_MARKETS = [
  {key:"HRR_over_1.5", label:"HRR",         short:"HRR",  line:"1.5 H+R+RBI"},
  {key:"HR_anytime",   label:"Homers",      short:"HR",   line:"HR anytime"},
  {key:"hits",         label:"1+ Hits",     short:"1+ H", line:"1+ hit"},
  {key:"total_bases",  label:"Total Bases", short:"TB",   line:"1.5 TB"},
]
function YesterdayResults({onPlayer}){
  const yday = yesterdayStr()
  const { data, loading } = useFetch(()=>getRecordDetail(yday, 10), [yday])
  const mkts = data?.markets || {}
  const present = YR_MARKETS.filter(m => (mkts[m.key]?.plays||[]).length)
  const dstr = (()=>{ try { return new Date(yday+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) } catch { return yday } })()

  if (loading) return <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:10}}>{[0,1,2].map(i=><div key={i} style={{height:120,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,opacity:.45}}/>)}</div>
  if (!present.length) return <div style={{color:T.muted,fontSize:13,padding:"12px 4px",marginTop:6}}>No settled results for {dstr} yet — they post overnight after the games finalize.</div>

  return (
    <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:14}}>
      <div style={{fontSize:11,color:T.muted,fontWeight:700}}>Settled board · {dstr} · tap a play for the full card</div>
      {present.map(m=>{
        const md = mkts[m.key], plays = md.plays||[]
        const w = md.W||0, l = md.L||0
        return (
          <div key={m.key} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderBottom:`1px solid ${T.border}`}}>
              <div><div style={{fontSize:15,fontWeight:900,letterSpacing:"-0.02em"}}>{m.label}</div><div style={{fontSize:10,color:T.muted,fontFamily:T.mono,fontWeight:700,marginTop:1}}>{m.line}</div></div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:15,fontWeight:900,fontFamily:T.mono,color:w>l?T.green:w<l?T.red:T.text}}>{w}-{l}</div>
                {md.hit_rate!=null && <div style={{fontSize:10,color:T.muted,fontWeight:700}}>{Math.round(md.hit_rate*100)}% hit</div>}
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column"}}>
              {plays.map((p,i)=>{
                const hit = p.hit===1, miss = p.hit===0
                const col = hit?T.green:miss?T.red:T.muted
                const clickable = !!p.player_id
                return (
                  <button key={`${m.key}-${p.player_id||p.player}-${i}`} disabled={!clickable}
                    onClick={()=>clickable&&onPlayer({name:p.player, player_id:p.player_id, market:m.short})}
                    style={{width:"100%",textAlign:"left",cursor:clickable?"pointer":"default",background:"transparent",
                      border:"none",borderTop:i?`1px solid ${T.border}`:"none",padding:"10px 14px",display:"flex",
                      alignItems:"center",gap:11,color:T.text}}>
                    <span style={{fontFamily:T.mono,fontSize:12,fontWeight:700,color:T.muted,width:16,flex:"none",textAlign:"center"}}>{i+1}</span>
                    <span style={{fontSize:15,flex:"none"}}>{hit?"✅":miss?"❌":"➖"}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.player}<span style={{color:T.muted2,fontWeight:600,fontSize:11}}> · {p.team}</span></div>
                      <div style={{fontSize:10.5,color:T.muted,fontFamily:T.mono,fontWeight:600,marginTop:2}}>{p.rating!=null?`RT ${p.rating}`:""}{p.rating!=null&&p.actual!=null?" · ":""}{p.actual!=null?`got ${p.actual}`:""}{p.line!=null?` · line ${p.line}`:""}</div>
                    </div>
                    <span style={{fontSize:10.5,fontWeight:800,color:col,flex:"none"}}>{hit?"HIT":miss?"MISS":"—"}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StormRecordCard(){
  const { data: rec } = useFetch(()=>getStormRecord(45).catch(()=>null), [])
  if(!rec || (!(rec.n>0) && !(rec.backtest&&rec.backtest.n))) return null
  const bt = rec.backtest
  const live = rec.n>0 ? rec.record : null
  const livePct = rec.n>0 && rec.win_pct!=null ? Math.round(rec.win_pct*100) : null
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"13px 15px",marginTop:10}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
        <span style={{fontSize:13.5,fontWeight:800,color:T.text}}>🌪 HRR Storm</span>
        {live && <span style={{fontSize:12,fontWeight:900,fontFamily:T.mono,color:livePct>=50?T.green:T.muted2,background:livePct>=50?T.greenDim:T.card2,border:`1px solid ${livePct>=50?T.greenMid:T.border2}`,borderRadius:7,padding:"3px 9px"}}>{live}{livePct!=null?` · ${livePct}%`:""}</span>}
      </div>
      <div style={{fontSize:11,color:T.muted,marginTop:5,lineHeight:1.5}}>
        {bt&&bt.n
          ? <>Backtest: <b style={{color:T.text}}>{Math.round(bt.hit_rate*100)}%</b> on {bt.n} qualifying picks, <b style={{color:T.green}}>+{Math.round(bt.lift*100)}pt</b> vs {Math.round((bt.baseline||0)*100)}% baseline{live?<> · live since tune: <b style={{color:T.text}}>{live}</b></>:""}</>
          : <>Frozen daily storm picks graded vs settled results, last {rec.days} days.</>}
      </div>
      {rec.postseason && rec.postseason.n>0 && (
        <div style={{fontSize:11,color:T.green,marginTop:6,fontWeight:700}}>🏆 Postseason: {rec.postseason.record}{rec.postseason.win_pct!=null?` · ${Math.round(rec.postseason.win_pct*100)}%`:""}</div>
      )}
    </div>
  )
}
function PowerSpotsRecordCard(){
  const { data: rec } = useFetch(()=>getPowerSpotsRecord().catch(()=>null), [])
  if(!rec || !(rec.spots_graded>0)) return null
  const pct = rec.hit_rate_pct
  const roi = rec.roi_pct
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"13px 15px",marginTop:10}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
        <span style={{fontSize:13.5,fontWeight:800,color:T.text}}>💣 Power Spots</span>
        <span style={{fontSize:12,fontWeight:900,fontFamily:T.mono,color:pct>=25?T.green:T.muted2,background:pct>=25?T.greenDim:T.card2,border:`1px solid ${pct>=25?T.greenMid:T.border2}`,borderRadius:7,padding:"3px 9px"}}>{rec.homered}/{rec.spots_graded} · {pct}% HR</span>
      </div>
      <div style={{fontSize:11,color:T.muted,marginTop:5,lineHeight:1.5}}>
        {rec.days_tracked} days · {(rec.range&&rec.range[0])?`${rec.range[0]} → ${rec.range[1]}`:""} · homered rate vs a ~15-21% base.
        {roi!=null ? <> · <b style={{color:roi>=0?T.green:T.red}}>{roi>=0?"+":""}{roi}% ROI</b> on {rec.spots_priced} priced spots</> : <> · ROI grading starts as priced spots settle</>}
      </div>
      {rec.postseason && rec.postseason.spots_graded>0 && (
        <div style={{fontSize:11,color:T.green,marginTop:6,fontWeight:700}}>🏆 Postseason: {rec.postseason.homered}/{rec.postseason.spots_graded} · {rec.postseason.hit_rate_pct}% HR{rec.postseason.roi_pct!=null?` · ${rec.postseason.roi_pct>=0?"+":""}${rec.postseason.roi_pct}% ROI`:""}</div>
      )}
    </div>
  )
}
// Postseason-only record — split out from the (now-frozen) regular-season number so the
// two never blend. Live top-10 W/L across markets since the playoff start date.
function PlayoffRecordCard(){
  const [open,setOpen] = useState(false)
  const { data } = useFetch(()=>getPlayoffRecord().catch(()=>null), [])
  const { data: elite } = useFetch(()=>getEliteRecord(120, 0.70).catch(()=>null), [])
  const ep = elite && elite.postseason
  if(!data || !data.active) return null
  const mk = (data.markets||[]).filter(m=>(m.top10_wins||0)+(m.top10_losses||0)>0)
  const w = mk.reduce((a,m)=>a+(m.top10_wins||0),0)
  const l = mk.reduce((a,m)=>a+(m.top10_losses||0),0)
  const n = w+l
  const start = data.postseason_start
  if(!n) return (
    <div style={{background:`linear-gradient(180deg, ${T.card3}, ${T.card})`,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"13px 15px",marginTop:10}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
        <span style={{fontSize:13.5,fontWeight:800,color:T.text}}>🏆 Postseason record</span>
        <span style={{fontSize:11,fontWeight:800,color:T.muted2}}>Tracking…</span>
      </div>
      <div style={{fontSize:11,color:T.muted,marginTop:5,lineHeight:1.5}}>Grading begins as playoff days settle{start?` (since ${start})`:""}. Regular-season record is frozen below.</div>
    </div>
  )
  const pct = Math.round(100*w/n)
  const good = pct>=50
  const label = {"HRR_over_1.5":"HRR","HR_anytime":"HR","hits":"1+ H","hits_1.5":"2+ H","total_bases":"Bases"}
  const top = [...mk].sort((a,b)=>(b.top10_wins+b.top10_losses)-(a.top10_wins+a.top10_losses)).slice(0,5)
  const byDay = (data.by_day||[]).filter(d=>(d.w+d.l)>0)
  const fmtDay = iso => { try{ const [y,m,dd]=iso.split("-"); return new Date(+y,+m-1,+dd).toLocaleDateString(undefined,{month:"short",day:"numeric"}) }catch(_){ return iso } }
  return (
    <div style={{background:`linear-gradient(180deg, ${T.card3}, ${T.card})`,border:`1px solid ${good?T.green:T.greenMid}`,borderRadius:12,marginTop:10,overflow:"hidden"}}>
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,background:"none",border:"none",padding:"13px 15px",cursor:"pointer",textAlign:"left"}}>
        <span style={{fontSize:13.5,fontWeight:800,color:T.text}}>🏆 Postseason record</span>
        <span style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:12,fontWeight:900,fontFamily:T.mono,color:good?T.green:T.muted2,background:good?T.greenDim:T.card2,border:`1px solid ${good?T.green:T.border2}`,borderRadius:7,padding:"3px 9px"}}>{w}–{l} · {pct}%</span>
          <span style={{color:T.muted,fontSize:13,transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
        </span>
      </button>
      {open && (
        <div style={{padding:"0 15px 14px"}}>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.5}}>Top-10 plays{start?` since ${start}`:""} · graded on settled results. Regular season frozen below.</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
            {top.map(m=>{ const mw=m.top10_wins||0, ml=m.top10_losses||0; return (
              <span key={m.prop_type} style={{fontSize:10.5,fontWeight:700,color:T.muted2,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:6,padding:"3px 8px",fontFamily:T.mono}}>{label[m.prop_type]||m.prop_type} {mw}–{ml}</span>
            )})}
          </div>
          {ep && (ep.bets>0) && (
            <div style={{fontSize:11,color:T.muted,marginTop:10}}>💎 70%+ whales: <b style={{color:T.text}}>{ep.wins}–{ep.losses}</b>{ep.bets?` · ${Math.round((ep.hit_rate||0)*100)}%`:""}</div>
          )}
          {byDay.length>0 && (
            <div style={{marginTop:12}}>
              <div style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.12em",color:T.muted,marginBottom:6}}>BY DAY</div>
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {byDay.map(d=>{ const dp=d.pct; const c=dp>=50?T.green:dp>=40?"#9bd17a":T.muted2; return (
                  <div key={d.date} style={{display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:12,padding:"7px 10px",background:T.card,border:`1px solid ${T.border}`,borderRadius:8}}>
                    <span style={{color:T.muted2}}>{fmtDay(d.date)}</span>
                    <span style={{fontFamily:T.mono,fontWeight:800}}><span style={{color:T.text}}>{d.w}–{d.l}</span>{dp!=null?<span style={{color:c}}> · {dp}%</span>:null}</span>
                  </div>
                )})}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
function RecordTab({onGlossary, onPlayer}){
  const [showBets,setShowBets] = useState(false)         // My Bets tucked into a dropdown
  const [showYday,setShowYday] = useState(false)         // yesterday's graded board
  const [showAdvanced,setShowAdvanced] = useState(false) // expert validation tucked away
  const drop = (open,label,set) => (
    <button onClick={()=>set(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderRadius:12,background:T.card,border:`1px solid ${T.border2}`,color:T.text,fontSize:13,fontWeight:800,cursor:"pointer",marginTop:6}}>
      <span>{label}</span>
      <span style={{color:T.muted,transform:open?"rotate(90deg)":"none",transition:"transform .15s"}}>›</span>
    </button>
  )
  return (
    <div style={{padding:"14px",position:"relative",minHeight:"70vh"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div style={{fontWeight:900,fontSize:20,letterSpacing:"-0.03em"}}>Results</div>
        <HelpChip onGlossary={onGlossary} section="data"/>
      </div>

      {/* ONE canonical record: the elite/top-rated headline + per-market W/L + K row.
          (The old second "top-rated %" headline was removed — it showed a different
          blended denominator right below this one and read as a competing record.) */}
      <SystemRecord/>

      {/* Postseason record — live playoff W/L, split from the frozen regular-season number */}
      <PlayoffRecordCard/>

      {/* Yesterday's graded board — the settled top-10 per market with ✅/❌, tap → card */}
      {drop(showYday, "📅 Yesterday's results (settled board)", setShowYday)}
      {showYday && <YesterdayResults onPlayer={onPlayer}/>}

      {/* Per-market STRONG-plays W/L grid — a core record view, kept in the main tab. */}
      <div style={{marginTop:10}}><GridStandoutsCard/></div>
      <ClvCard/>

      {/* Signal track records — HRR Storm + Power Spots, graded vs settled results. */}
      <StormRecordCard/>
      <PowerSpotsRecordCard/>

      {/* Advanced — only the per-market validation scorecard (CLV / calibration / verdicts)
          stays tucked into a dropdown; it's an expert read, not a headline record. */}
      {drop(showAdvanced, "🔬 Model validation (advanced)", setShowAdvanced)}
      {showAdvanced && <div style={{marginTop:10,display:"flex",flexDirection:"column",gap:10}}>
        <ValidationScorecard/>
      </div>}

      {/* My Bets — tucked away in a dropdown for those who want to track wagers */}
      {drop(showBets, "📓 My Bets", setShowBets)}
      {showBets && <div style={{marginTop:10}}><MyRecord/></div>}
    </div>
  )
}

// ── Trends tab — hottest players + arms getting hit (last 7 days) ────────────
// No sample/fallback data: this tab only ever shows real settled game-log
// aggregates from /stats/trending. When there's nothing to show it renders an
// honest empty state — never fabricated player stats dressed up as live trends.
// Matchup flag chip for a hot bat (good / tough / neutral / no game today)
function MatchupChip({m}){
  if(!m) return <span style={{fontSize:10,color:T.muted,fontWeight:700,whiteSpace:"nowrap"}}>No game today</span>
  const cfg = m.rating==="good" ? {c:T.green,t:"✅ Good matchup"}
    : m.rating==="tough" ? {c:T.red,t:"⚠️ Tough matchup"}
    : {c:T.amber,t:"➖ Even matchup"}
  return (
    <div style={{textAlign:"right"}}>
      <span style={{fontSize:10.5,fontWeight:800,color:cfg.c,whiteSpace:"nowrap"}}>{cfg.t}</span>
      {m.opp_pitcher && <div style={{fontSize:10,color:T.muted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:140}}>vs {m.opp_pitcher}{m.opp_hand?` (${m.opp_hand})`:""}</div>}
    </div>
  )
}
function TrendsTab({onPlayer, onGlossary}){
  const { data, loading } = useFetch(()=>getTrending(7,25), [])
  const [seg,setSeg] = useState("hitting")   // hitting | pitching
  const hot = data?.hot_bats || []
  const arms = data?.cold_arms || []
  // Filter by game (venue). Games are built from both segments' today-matchup venue so a
  // user can zero in on one game's hot bats / hittable arm. Empty set = whole slate.
  const [venueSel,setVenueSel] = useState(()=>new Set())
  const [gamesOpen,setGamesOpen] = useState(false)
  const games = useMemo(()=>{
    const m={}
    for(const b of hot){ const v=b.matchup?.venue; if(v){ (m[v]=m[v]||new Set()); if(b.team) m[v].add(b.team) } }
    for(const a of arms){ const v=a.matchup?.venue; if(v){ (m[v]=m[v]||new Set()); if(a.matchup?.vs_team) m[v].add(a.matchup.vs_team) } }
    return Object.entries(m).map(([venue,t])=>({venue,teams:[...t].sort()}))
      .sort((x,y)=>(x.teams[0]||"").localeCompare(y.teams[0]||""))
  }, [hot, arms])
  const inSel = v => venueSel.size===0 || (!!v && venueSel.has(v))
  const toggleVenue = v => setVenueSel(s=>{ const n=new Set(s); n.has(v)?n.delete(v):n.add(v); return n })
  const [q,setQ] = useState("")
  const _q = _deacc(q.trim().toLowerCase())
  const hotMatch = b => !_q || [b.name,b.team,b.matchup?.opp_pitcher].some(f=>_deacc(String(f||"").toLowerCase()).includes(_q))
  const armMatch = a => !_q || [a.name,a.matchup?.vs_team].some(f=>_deacc(String(f||"").toLowerCase()).includes(_q))
  const shownHot = hot.filter(b=>inSel(b.matchup?.venue) && hotMatch(b))
  const shownArms = arms.filter(a=>inSel(a.matchup?.venue) && armMatch(a))
  const filtered = venueSel.size>0 || !!_q
  const gchip = on => ({fontSize:11,fontWeight:800,padding:"5px 10px",borderRadius:8,cursor:"pointer",whiteSpace:"nowrap",border:`1px solid ${on?T.greenMid:T.border2}`,background:on?T.greenDim:T.card,color:on?T.green:T.muted2})
  const segBtn = on => ({flex:1,padding:"9px",borderRadius:9,fontSize:13,fontWeight:800,cursor:"pointer",border:"none",background:on?T.green:"transparent",color:on?"#04140D":T.muted2})
  return (
    <div style={{padding:"14px"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontWeight:900,fontSize:20,letterSpacing:"-0.03em"}}>Trends</div>
        <HelpChip onGlossary={onGlossary} section="scores"/>
      </div>
      <div style={{fontSize:11,color:T.muted,marginTop:2,marginBottom:12}}>Last 7 days · live game results</div>

      {/* Search a player / team / opponent — filters both hitting and pitching segments */}
      <div style={{position:"relative",marginBottom:12}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search player, team, or opponent…"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 30px 9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
        {q && <button onClick={()=>setQ("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted2,fontSize:16,cursor:"pointer",lineHeight:1,padding:4}}>×</button>}
      </div>

      {/* High-level trend selector */}
      <div style={{display:"flex",gap:4,background:T.card,border:`1px solid ${T.border2}`,borderRadius:11,padding:4,marginBottom:14}}>
        <button onClick={()=>setSeg("hitting")} style={segBtn(seg==="hitting")}>⚾ Hitting</button>
        <button onClick={()=>setSeg("pitching")} style={segBtn(seg==="pitching")}>🎯 Pitching</button>
      </div>

      {/* Filter by game (venue) — one tap to focus a single game's hot bats / hittable arm */}
      {games.length>0 && (
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>setGamesOpen(o=>!o)} style={{display:"inline-flex",alignItems:"center",gap:6,background:venueSel.size?T.greenDim:T.card,border:`1px solid ${venueSel.size?T.greenMid:T.border2}`,borderRadius:8,padding:"6px 11px",color:venueSel.size?T.green:T.muted2,fontSize:11.5,fontWeight:800,cursor:"pointer"}}>🏟 Filter by game{venueSel.size?` · ${venueSel.size}`:""} <span style={{color:T.green}}>{gamesOpen?"▾":"▸"}</span></button>
            {filtered && <button onClick={()=>setVenueSel(new Set())} style={{background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>clear</button>}
          </div>
          {gamesOpen && (
            <div style={{marginTop:8,display:"flex",flexWrap:"wrap",gap:7,padding:"10px",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10}}>
              {games.map(g=>(<button key={g.venue} title={g.venue} onClick={()=>toggleVenue(g.venue)} style={gchip(venueSel.has(g.venue))}>{g.teams.join(" · ")||g.venue}</button>))}
            </div>
          )}
        </div>
      )}

      {loading ? [0,1,2,3].map(i=><div key={i} style={{height:58,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginBottom:8,opacity:0.45}}/>) : seg==="hitting" ? (<>
        {/* Hot bats */}
        <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.12em",marginBottom:8}}>🔥 HOTTEST BATS</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {shownHot.map((b,i)=>(
            <button key={`bat-${b.player_id ?? b.name}-${i}`} onClick={()=>b.player_id&&onPlayer({name:b.name,player_id:b.player_id,market:"HRR"})}
              style={{width:"100%",textAlign:"left",cursor:b.player_id?"pointer":"default",color:T.text,background:T.card,border:`1px solid ${b.matchup?.rating==="good"?T.greenMid:T.border2}`,borderRadius:10,padding:"10px 12px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:800,color:T.text}}><span style={{color:T.muted,fontFamily:T.mono,fontSize:11,marginRight:6}}>{i+1}</span>{b.name} <span style={{color:T.muted2,fontWeight:600,fontSize:11}}>· {b.team}</span></div>
                  <div style={{fontSize:11,color:T.muted,marginTop:3}}>{b.games}G · {b.hits} H · {b.hr} HR · {b.rbi} RBI · {b.runs} R</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:16,fontWeight:900,color:T.green,fontFamily:T.mono}}>{b.hrr_per_game}</div>
                  <div style={{fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>H+R+RBI/G</div>
                </div>
              </div>
              <div style={{borderTop:`1px solid ${T.border}`,marginTop:8,paddingTop:7,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:"0.06em"}}>TODAY</span>
                <MatchupChip m={b.matchup}/>
              </div>
            </button>
          ))}
          {!shownHot.length && <div style={{color:T.muted,fontSize:13,padding:"6px 2px"}}>{filtered?"No hot bats in the selected game(s) — clear the filter or pick another game.":"No batter game logs in the last 7 days yet."}</div>}
        </div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:14}}>
          Hottest bats = most H+R+RBI per game over the last 7 days. The <b style={{color:T.text}}>matchup flag</b> checks today's slate: ✅ good = facing a hittable pitcher / strong park, ⚠️ tough = facing a wall.
        </div>
      </>) : (<>
        {/* Cold arms — tonight's actual starters, most → least hittable */}
        <div style={{fontSize:9,color:T.red,fontWeight:800,letterSpacing:"0.12em",marginBottom:8}}>🎯 TONIGHT'S STARTERS · MOST → LEAST HITTABLE</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {shownArms.map((a,i)=>{
            const m=a.matchup
            const chip = !m ? {c:T.muted,t:"Not pitching today"}
              : m.rating==="great" ? {c:T.green,t:`✅ Attack spot · vs ${m.vs_team||"TBD"}`}
              : m.rating==="decent" ? {c:T.amber,t:`Pitching today · vs ${m.vs_team||"TBD"}`}
              : {c:T.muted2,t:`vs ${m.vs_team||"TBD"} · soft lineup`}
            return (
              <div key={`arm-${a.pitcher_id ?? a.name}-${i}`} style={{background:T.card,border:`1px solid ${m?.rating==="great"?T.greenMid:T.border2}`,borderRadius:10,padding:"10px 12px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:800,color:T.text}}><span style={{color:T.muted,fontFamily:T.mono,fontSize:11,marginRight:6}}>{i+1}</span>{a.name}</div>
                    <div style={{fontSize:11,color:T.muted,marginTop:3}}>ERA {a.era??"—"} · K/9 {a.k_per_9??"—"}{a.games_started?` · ${a.games_started} GS`:" · new arm"}</div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:16,fontWeight:900,color:T.red,fontFamily:T.mono}}>{a.hr9??"—"}</div>
                    <div style={{fontSize:8.5,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>HR/9</div>
                  </div>
                </div>
                <div style={{borderTop:`1px solid ${T.border}`,marginTop:8,paddingTop:7,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:10,color:T.muted,fontWeight:700,letterSpacing:"0.06em"}}>TODAY</span>
                  <span style={{fontSize:10.5,fontWeight:800,color:chip.c,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:200}}>{chip.t}</span>
                </div>
              </div>
            )
          })}
          {!shownArms.length && <div style={{color:T.muted,fontSize:13,padding:"6px 2px"}}>{filtered?"No starters in the selected game(s) — clear the filter or pick another game.":"No pitcher damage data in the last 7 days yet."}</div>}
        </div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:14}}>
          Only <b style={{color:T.text}}>tonight's actual starters</b>, ranked most → least hittable by season vulnerability (high HR/9 + ERA, low K/9). The flag shows how strong the lineup they face projects — ✅ = prime attack spot. The arm at the top is the best one to target tonight.
        </div>
      </>)}
    </div>
  )
}

// ── Analytics tab — sort/filter the whole slate by any model metric ──────────
// Reads the cached /projections/today endpoint (zone_fit, L7 form, confidence,
// prob, edge, pitcher_adj, park already batch-loaded server-side) and does all
// sorting/filtering client-side — no extra backend load, no lag.
const ANALYTICS_SORTS = [
  ["prob_over",   "Probability",  p=>p?.prob_over],
  ["ev",          "Value (EV)",   p=>p?.ev],
  ["edge_pct",    "Edge",         p=>p?.edge_pct],
  ["zone_fit",    "Zone Fit",     p=>p?.inputs?.zone_fit],
  ["form_l7",     "Form (L7)",    p=>p?.inputs?.form_l7],
  ["confidence",  "Rating",       p=>p?.confidence],
  ["pitcher_adj", "Hittable Arm", p=>p?.inputs?.pitcher_adj],
  ["park_factor", "Park",         p=>p?.inputs?.park_factor],
]
const fmtMetric = (key, v) => {
  if(v==null) return "—"
  if(key==="confidence"||key==="zone_fit") return Math.round(v)
  if(key==="prob_over") return pct(v)
  if(key==="edge_pct"||key==="ev") return `${v>=0?"+":""}${(v*100).toFixed(1)}%`
  if(key==="pitcher_adj"||key==="park_factor") return `${v.toFixed(2)}x`
  return v.toFixed(2)
}
const metricColor = (key, v) => {
  if(v==null) return T.muted2
  if(key==="confidence") return confColor(v)
  if(key==="zone_fit") return scoreColor(v)
  if(key==="prob_over") return probColor(v)
  if(key==="edge_pct"||key==="ev") return edgeColor(v*100)
  return T.text
}
// ── Game Explorer — grid view: filter by game / team, every advanced metric in
// columns, color-coded so the read is instant (form up = green, hittable arm =
// green, tough spot = red). Groups rows under a per-game header (matchup + park).
// Default sort = zone fit. Reads the same cached /projections/today feed; CSV export.
const GE_COLS = [
  // key,        label,        get(p),                            fmt,                              color,                            asc?
  ["zone_fit",  "Zone Fit",  p=>p?.inputs?.zone_fit,             v=>v==null?"—":Math.round(v),     v=>v==null?T.muted2:scoreColor(v)],
  // pitch-mix matchup 0-100: how the hitter handles THIS starter's arsenal (usage-
  // weighted, whiff-discounted xwOBA). HIGH = good for the hitter.
  ["pitch_fit", "Pitch Fit", p=>p?.inputs?.pitch_fit,            v=>v==null?"—":Math.round(v),     v=>v==null?T.muted2:scoreColor(v)],
  ["prob_over", "Prob",      p=>p?.prob_over,                    v=>v==null?"—":pct(v),            v=>v==null?T.muted2:probColor(v)],
  ["ev",        "EV",        p=>p?.ev,                           v=>geSigned(v),                  v=>v==null?T.muted2:edgeColor(v*100)],
  ["edge_pct",  "Edge",      p=>p?.edge_pct,                     v=>geSigned(v),                  v=>v==null?T.muted2:edgeColor(v*100)],
  ["confidence","Rating",    p=>p?.confidence,                   v=>v==null?"—":Math.round(v),     v=>v==null?T.muted2:ratingColor(v)],
  ["form",      "Form L7",   p=>geFormDelta(p),                  v=>geFormFmt(v),                 v=>geFormColor(v)],
  // ── HR power profile (barrel + hard-hit + pull = pulled hard contact in the air) ──
  ["barrel_pct","Barrel%",   p=>p?.inputs?.barrel_pct,           v=>gePctF(v),                    v=>v==null?T.muted2:(v>=0.12?T.green:v>=0.08?"#34D399":T.muted2)],
  // ISO (isolated power = SLG − AVG): raw extra-base pop. League avg ~.160; .220+ is real power.
  ["iso",       "ISO",       p=>p?.inputs?.iso,                  v=>v==null?"—":v.toFixed(3).replace(/^0/,""), v=>v==null?T.muted2:(v>=0.220?T.green:v>=0.170?"#34D399":T.muted2)],
  ["hard_hit",  "Hard-Hit%", p=>p?.inputs?.hard_hit_pct,         v=>gePctF(v),                    v=>v==null?T.muted2:(v>=0.45?T.green:v>=0.38?"#34D399":T.muted2)],
  ["pull",      "Pull%",     p=>p?.inputs?.pull_pct,             v=>gePctF(v),                    v=>v==null?T.muted2:(v>=0.44?T.green:v>=0.38?"#34D399":T.muted2)],
  // fly-ball rate — you can't homer on a grounder; HIGH is good for HR/power markets.
  ["fbpct",     "FB%",       p=>p?.inputs?.batter_fb_pct,        v=>gePctF(v),                    v=>v==null?T.muted2:(v>=0.40?T.green:v>=0.32?"#34D399":v<=0.24?T.muted2:T.muted)],
  // HR-worthy ("critical") fly-ball rate — barrels among fly balls / fly balls. FB% is
  // volume; this is quality — the share of a hitter's fly balls hit with HR-caliber
  // contact. The real "does he actually loft it OUT" signal. League ~10%; 18%+ is elite.
  ["hrfbpct",   "HR-worthy FB%", p=>p?.inputs?.hr_fb_pct,        v=>gePctF(v),                    v=>v==null?T.muted2:(v>=0.18?T.green:v>=0.12?"#34D399":v<=0.07?T.muted2:T.muted)],
  ["xwoba",     "xwOBA",     p=>p?.inputs?.xwoba,                v=>v==null?"—":v.toFixed(3).replace(/^0/,""), v=>v==null?T.muted2:(v>=0.380?T.green:v>=0.330?"#34D399":T.muted2)],
  // pitcher's OPS allowed to THIS hitter's hand — HIGH = soft arm = GOOD for hitter (green)
  ["pops",      "P OPS/hand",p=>p?.inputs?.pitcher_ops_vs_hand,  v=>v==null?"—":v.toFixed(3).replace(/^0/,""), v=>v==null?T.muted2:(v>=0.780?T.green:v>=0.700?"#34D399":v<=0.650?T.red:T.muted)],
  // pitcher HR/9 — HIGH = HR-prone arm = GOOD for the hitter (green); low suppresses HR (red)
  ["phr9",      "P HR/9",    p=>p?.inputs?.pitcher_hr9,          v=>v==null?"—":v.toFixed(2),     v=>v==null?T.muted2:(v>=1.5?T.green:v>=1.2?"#34D399":v<=0.9?T.red:T.muted)],
  // pitcher HR-worthy fly balls ALLOWED (barrels among FB he gives up) — high = a fly-ball
  // arm getting barreled = the ideal HR target. Green is GOOD FOR THE HR BETTOR.
  ["phrfb",     "P HR-FB allow", p=>p?.inputs?.pitcher_hr_fb_allowed, v=>gePctF(v),               v=>v==null?T.muted2:(v>=0.15?T.green:v>=0.10?"#34D399":v<=0.06?T.muted2:T.muted)],
  ["park",      "Park",      p=>p?.inputs?.park_factor,          v=>v==null?"—":`${v.toFixed(2)}x`, v=>v==null?T.muted2:(v>=1.10?T.green:v>=1.03?"#34D399":v<=0.97?T.muted2:T.muted)],
  // ── Contact / volume profile — the drivers for HITS and HRR bets ──
  // lineup slot: lower = more plate appearances (asc sort). #1-2 elite, #1-5 good.
  ["slot",      "Slot",      p=>p?.inputs?.lineup_slot,          v=>v==null?"—":`#${v}`,          v=>v==null?T.muted2:(v<=2?T.green:v<=5?"#34D399":T.muted2), true],
  // opposing pitcher K/9: LOW = pitches to contact = balls in play = GOOD for a hits bet.
  ["pk9",       "P K/9",     p=>p?.inputs?.pitcher_k9,           v=>v==null?"—":v.toFixed(1),     v=>v==null?T.muted2:(v<=7?T.green:v<=8.5?"#34D399":v>=10?T.red:T.muted), true],
  ["pwhip",     "P WHIP",    p=>p?.inputs?.pitcher_whip,         v=>v==null?"—":v.toFixed(2),     v=>v==null?T.muted2:(v>=1.40?T.green:v>=1.30?"#34D399":v<=1.10?T.red:T.muted)],
  // batter whiff%: LOW = more contact = more hits (asc sort).
  ["whiff",     "Whiff%",    p=>p?.inputs?.batter_whiff_pct,     v=>gePctF(v),                    v=>v==null?T.muted2:(v<=0.20?T.green:v<=0.26?"#34D399":v>=0.30?T.red:T.muted), true],
  // opposing pitcher ERA — HIGH = worse run prevention = GOOD for the hitter (green).
  ["era",       "P ERA",     p=>p?.inputs?.starter_era,          v=>v==null?"—":v.toFixed(2),     v=>v==null?T.muted2:(v>=4.50?T.green:v>=4.00?"#34D399":v<=3.20?T.red:T.muted)],
  // opposing pitcher swinging-strike% — HIGH = whiffy arm = TOUGH for the hitter (red).
  ["pswstr",    "P SwStr%",  p=>p?.inputs?.pitcher_swstr,        v=>gePctF(v),                    v=>v==null?T.muted2:(v>=0.13?T.red:v<=0.09?T.green:T.muted), true],
]
// ── Metric filters (Pro/Founder): pick a metric + ≥/≤ + value; stack them (AND).
// All client-side on already-loaded rows — instant, no backend. % fields compare in %.
const GE_COL_BY_KEY = Object.fromEntries(GE_COLS.map(c => [c[0], c]))
const GE_PCT_KEYS = new Set(["prob_over","ev","edge_pct","form","barrel_pct","hard_hit","pull","fbpct","hrfbpct","phrfb","whiff","pswstr"])
// Metrics offered in the filter dropdown (key + hint text for the value box).
const GE_FILTER_FIELDS = [
  ["confidence","LB Rating","0–100"],["prob_over","Prob %","%"],["edge_pct","Edge %","%"],
  ["ev","EV %","%"],["form","Form % (L7 vs season)","+5"],
  ["barrel_pct","Barrel %","%"],["hard_hit","Hard-Hit %","%"],["pull","Pull %","%"],
  ["fbpct","FB %","%"],["hrfbpct","HR-worthy FB %","%"],["whiff","Batter Whiff %","%"],["xwoba","xwOBA",".350"],
  ["zone_fit","Zone Fit","0–100"],["pitch_fit","Pitch Fit","0–100"],
  ["era","Pitcher ERA","4.25"],["phr9","Pitcher HR/9","1.3"],["phrfb","Pitcher HR-worthy FB allowed %","%"],["pwhip","Pitcher WHIP","1.30"],
  ["pk9","Pitcher K/9","8.5"],["pops","Pitcher OPS/hand",".750"],["pswstr","Pitcher SwStr %","%"],
  ["park","Park factor","1.05"],["slot","Lineup slot","1–9"],
]
const geFieldVal = (p, key) => {
  const c = GE_COL_BY_KEY[key]; if(!c) return null
  const v = c[2](p); if(v==null) return null
  return GE_PCT_KEYS.has(key) ? v*100 : v
}
// Which columns each market shows. HR is power-led (barrel/pull/HR9); hits & HRR are
// contact + volume (lineup slot, low-K arm, whiff, OPS-vs-hand). Unmapped → full palette.
const GE_MARKET_COLS = {
  "HR_anytime":   ["zone_fit","pitch_fit","prob_over","ev","edge_pct","confidence","form","barrel_pct","iso","hard_hit","pull","fbpct","hrfbpct","xwoba","pops","phr9","phrfb","park"],
  "hits":         ["slot","prob_over","ev","edge_pct","confidence","zone_fit","pitch_fit","pwhip","pk9","pops","whiff","xwoba","park"],
  "hits_1.5":     ["slot","prob_over","ev","edge_pct","confidence","zone_fit","pitch_fit","pwhip","pk9","pops","whiff","xwoba","park"],
  "HRR_over_1.5": ["slot","prob_over","ev","edge_pct","confidence","form","pops","pwhip","xwoba","hard_hit","iso","whiff","zone_fit","pitch_fit","pk9","park"],
  "total_bases":  ["slot","prob_over","ev","edge_pct","confidence","barrel_pct","iso","hard_hit","pops","xwoba","pitch_fit","phr9","park"],
}
// ── Player card · per-bet-type analytics sub-tab ─────────────────────────────
// Pick a bet type → this player's REAL projection inputs render through the exact
// same field set + color thresholds as the big Analytics grid (GE_MARKET_COLS /
// GE_COLS). One player, one market, lazily fetched from the same /today source the
// grid uses (scoped server-side by player_id). No fabrication, no sample data — an
// honest empty state shows when the player isn't projected for that bet type today.
const PA_MARKETS = [["HRR","HRR_over_1.5"],["HR","HR_anytime"],["1+ H","hits"],["TB","total_bases"]]
function PropAnalyticsPanel({player}){
  const name = player?.name || "this hitter"
  // Open on the market the card was tapped from, else HRR.
  const openLbl = player?.market || "HRR"
  const [mkt,setMkt] = useState(()=> PA_MARKETS.find(m=>m[0]===openLbl || m[1]===openLbl) || PA_MARKETS[0])
  const db = mkt[1]
  const { data, loading } = useFetch(
    ()=> player?.player_id ? getPlayerMarketAnalytics(player.player_id, db, todayStr()).catch(()=>null) : Promise.resolve(null),
    [player?.player_id, db])
  const row = (data?.picks||[])[0] || null
  const keys = GE_MARKET_COLS[db] || GE_COLS.map(c=>c[0])
  const tile = (label,val,color)=>(
    <div key={label} style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:9,padding:"9px 11px"}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4}}>{label}</div>
      <div style={{fontSize:16,fontWeight:800,fontFamily:T.mono,color:color||T.text}}>{val}</div>
    </div>
  )
  const head = k => { const c=GE_COL_BY_KEY[k]; const v=c?c[2](row):null; return {label:c?c[1]:k, val:c?c[3](v):"—", color:c?c[4](v):T.muted2} }
  return (
    <div>
      {/* Bet-type selector */}
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
        {PA_MARKETS.map(([lbl,key])=>{ const on=db===key; return (
          <button key={key} onClick={()=>setMkt([lbl,key])}
            style={{padding:"6px 12px",borderRadius:8,fontSize:12,fontWeight:800,cursor:"pointer",
              border:`1px solid ${on?T.green:T.border2}`,background:on?T.greenDim:T.card,color:on?T.green:T.muted2}}>
            {MARKET_LABEL[lbl]||lbl}
          </button>
        )})}
      </div>
      {loading ? (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>{[0,1,2,3,4,5].map(i=>(
          <div key={i} style={{height:52,background:T.card,border:`1px solid ${T.border}`,borderRadius:9,opacity:0.45}}/>))}</div>
      ) : !row ? (
        <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:9,padding:"16px",fontSize:12.5,color:T.muted2,lineHeight:1.55}}>
          No <b style={{color:T.text}}>{MARKET_LABEL[mkt[0]]||mkt[0]}</b> projection for {name} today — either not in today's lineup for this bet type, or the slate hasn't run yet.
        </div>
      ) : (<>
        {/* Headline: prob · edge · rating for THIS bet type */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          {["prob_over","edge_pct","confidence"].map(k=>{ const h=head(k); return tile(h.label,h.val,h.color) })}
        </div>
        {/* Full field grid — same metrics + thresholds the Analytics grid uses for this market */}
        <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>{(MARKET_LABEL[mkt[0]]||mkt[0]).toUpperCase()} · ANALYTICS FIELDS</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          {keys.filter(k=>!["prob_over","edge_pct","confidence"].includes(k)).map(k=>{ const c=GE_COL_BY_KEY[k]; if(!c) return null; const v=c[2](row); return tile(c[1], c[3](v), c[4](v)) })}
        </div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:12}}>
          Every field is {name}'s real model input for this bet type — the same metrics and green/red thresholds as the Analytics grid, just filtered to one player. Research only · 21+.
        </div>
      </>)}
    </div>
  )
}
// ── Custom columns (v1): per-market show/hide, saved on-device. The chooser offers
// every metric in GE_COLS; a market's default set (above) is the starting layout.
// localStorage: { [market]: [visibleKey, …] } — absent market ⇒ its default layout.
const GRID_COLS_KEY = "linebrkr_grid_cols_v1"
const GRID_DENSITY_KEY = "linebrkr_grid_density"   // 'comfortable' | 'compact' (global)
// Per-market scratchpad: jot down your guys for the day, one note per bet type.
// { [market]: "free text" }, saved on-device.
const GRID_NOTES_KEY = "linebrkr_grid_notes_v1"
const loadGridNotes = () => { try{ return JSON.parse(localStorage.getItem(GRID_NOTES_KEY)||"{}")||{} }catch(_){ return {} } }
const geDefaultKeys = mkt => GE_MARKET_COLS[mkt] || GE_COLS.map(c=>c[0])
// Ordered universe of every column a market can show: its defaults first (in their
// curated order), then the rest of the palette in GE_COLS order.
const geColUniverse = mkt => {
  const def = geDefaultKeys(mkt)
  return [...def, ...GE_COLS.map(c=>c[0]).filter(k=>!def.includes(k))]
}
const loadGridCols = () => { try{ return JSON.parse(localStorage.getItem(GRID_COLS_KEY)||"{}")||{} }catch(_){ return {} } }
// Pitch-mix filter: families (order shown) + short header labels.
const PITCH_ORDER = ["4-Seam","Sinker","Cutter","Slider","Sweeper","Change","Splitter","Curve"]
const PITCH_SHORT = {"4-Seam":"4S","Sinker":"SI","Cutter":"FC","Slider":"SL","Sweeper":"ST","Change":"CH","Splitter":"FS","Curve":"CU"}
const PITCH_MIN_USE = 0.10   // only pitchers who throw it ≥10% are "facing" that pitch
const geSigned = v => v==null?"—":`${v>=0?"+":""}${(v*100).toFixed(1)}%`
const gePctF   = v => v==null?"—":`${(v*100).toFixed(0)}%`
// recent-form lift vs season (L7 rate / season rate − 1): positive = heating up
const geFormDelta = p => {
  const i=p?.inputs||{}; if(i.form_l7==null||!i.form_season) return null
  return i.form_l7/i.form_season - 1
}
const geFormFmt = v => v==null?"—":`${v>0?"▲":v<0?"▼":""}${v>=0?"+":""}${Math.round(v*100)}%`
const geFormColor = v => v==null?T.muted2:v>0.04?T.green:v<-0.04?T.red:T.muted
// ── Screener — set a real THRESHOLD on any metric (drag hard-hit up to the slate's max,
// etc.). Bats must clear every active floor; lower-is-better metrics (whiff, K-9, slot)
// use a ceiling (≤). Ranked by probability by default — tap any column to re-sort. 100%
// client-side on the already-loaded /projections/today feed. Self-serve version of the
// hand-built power/contact screens.
const SCREEN_METRICS = {
  "Bat profile":  ["form","barrel_pct","hard_hit","pull","fbpct","hrfbpct","xwoba","whiff"],
  "Matchup (pitcher)": ["phr9","phrfb","pwhip","pops","pk9","era","zone_fit","pitch_fit"],
  "Park & spot":  ["park","slot"],
}
// Presets = real threshold values (direction handled by each metric's GE_COLS asc flag).
const SCREEN_PRESETS = {
  "🔥 Power":   {barrel_pct:0.12, hard_hit:0.45, fbpct:0.35, hrfbpct:0.15, phr9:1.30},
  "🎯 Contact": {xwoba:0.340, whiff:0.24, pk9:8.0, slot:5},
  "🧊 Warm+":   {form:0},
}
function ScreenerTab({onPlayer}){
  const [mkt,setMkt] = useState("HR_anytime")
  const [thresh,setThresh] = useState(SCREEN_PRESETS["🔥 Power"])
  const [enabled,setEnabled] = useState(Object.fromEntries(Object.keys(SCREEN_PRESETS["🔥 Power"]).map(k=>[k,true])))
  const [sortKey,setSortKey] = useState("prob_over")
  const [openW,setOpenW] = useState(true)
  const { data, loading } = useFetch(()=>getToday(mkt, todayStr(), 500), [mkt])
  const picks = Array.isArray(data?.picks) ? data.picks : []

  // per-metric [min,max] across the board (raw units) — the slider spans the real range
  const ranges = useMemo(()=>{
    const o={}
    Object.values(SCREEN_METRICS).flat().forEach(k=>{
      const col=GE_COL_BY_KEY[k]; if(!col) return
      const vals=picks.map(p=>col[2](p)).filter(v=>v!=null)
      if(vals.length) o[k]={min:Math.min(...vals),max:Math.max(...vals),asc:!!col[5]}
    })
    return o
  },[picks])

  // A metric is a REQUIRED criterion only if you've CHECKED it on. Its slider sets the
  // floor/ceiling; a bat must satisfy EVERY checked metric (AND).
  const active = Object.keys(enabled).filter(k=>enabled[k] && ranges[k])
  const thOf = k => { const r=ranges[k]; if(!r) return null
    return thresh[k]!=null ? thresh[k] : (r.min+r.max)/2 }   // default a freshly-checked metric to mid-range
  const toggle = k => setEnabled(e=>{ const n={...e,[k]:!e[k]}
    if(n[k] && thresh[k]==null && ranges[k]) setThresh(t=>({...t,[k]:(ranges[k].min+ranges[k].max)/2}))
    return n })

  const rows = useMemo(()=>{
    const pass = picks.filter(p=>active.every(k=>{
      const v=GE_COL_BY_KEY[k][2](p); if(v==null) return false
      return ranges[k].asc ? v<=thOf(k) : v>=thOf(k)
    }))
    const getter = sortKey==="prob_over" ? (p=>p.prob_over)
                 : sortKey==="confidence" ? (p=>p.confidence)
                 : (p=>GE_COL_BY_KEY[sortKey]?.[2](p))
    const asc = !["prob_over","confidence"].includes(sortKey) && !!GE_COL_BY_KEY[sortKey]?.[5]
    return pass.map(p=>({p,v:getter(p)})).filter(r=>r.v!=null)
               .sort((a,b)=> asc ? a.v-b.v : b.v-a.v).slice(0,50)
  },[picks, JSON.stringify(active.map(k=>[k,thOf(k)])), sortKey, ranges])

  const DEFCOLS=["barrel_pct","hard_hit","fbpct","phr9","xwoba","form"]
  const cols = active.length ? active.slice(0,7) : DEFCOLS

  const setT=(k,v)=>setThresh(t=>({...t,[k]:v}))
  const chip=(on)=>({padding:"6px 11px",borderRadius:8,fontSize:12,fontWeight:800,cursor:"pointer",
    border:`1px solid ${on?T.green:T.border2}`,background:on?T.greenDim:T.card2,color:on?T.green:T.muted2})

  const exportCsv=()=>{
    const heads=["Rank","Hitter","Team","vsPitcher",...cols.map(k=>GE_COL_BY_KEY[k][1]),"Prob","Rating"]
    const lines=[heads.join(",")]
    rows.forEach((r,i)=>{const cv=k=>{const c=GE_COL_BY_KEY[k];const v=c[2](r.p);return v==null?"":String(c[3](v)).replace(/[▲▼,]/g,"")}
      lines.push([i+1,`"${r.p.full_name}"`,r.p.team||"",`"${r.p.opp_pitcher_name||""}"`,...cols.map(cv),
        r.p.prob_over==null?"":pct(r.p.prob_over),r.p.confidence==null?"":Math.round(r.p.confidence)].join(","))})
    const b=new Blob([lines.join("\n")],{type:"text/csv"});const u=URL.createObjectURL(b)
    const a=document.createElement("a");a.href=u;a.download=`linebrkr_screener_${mkt}_${todayStr()}.csv`;a.click()
  }
  const Hh=({k,label})=>(<th onClick={()=>setSortKey(k)} style={{textAlign:"right",padding:"5px 5px",fontSize:8.5,cursor:"pointer",color:sortKey===k?T.green:T.muted}}>{label}{sortKey===k?" ▾":""}</th>)

  return (
    <div style={{padding:"14px 12px 90px",maxWidth:920,margin:"0 auto"}}>
      <div style={{fontSize:9,color:T.green,fontWeight:800,letterSpacing:"0.14em",marginBottom:3}}>◆ SCREENER</div>
      <div style={{fontSize:20,fontWeight:900,color:T.text,marginBottom:2}}>Filter the board your way</div>
      <div style={{fontSize:11.5,color:T.muted,marginBottom:12}}>☑ Check the metrics that matter, drag each slider to a real value (up to the slate's max), and a bat must clear <b style={{color:T.text}}>every checked one</b>. Tap a column to sort.</div>

      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:9}}>
        {MARKETS.map(([lbl,key])=>(<button key={key} onClick={()=>setMkt(key)} style={chip(mkt===key)}>{mDisp(lbl)}</button>))}
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
        <span style={{fontSize:10,color:T.faint,fontWeight:700,letterSpacing:"0.08em"}}>PRESET</span>
        {Object.entries(SCREEN_PRESETS).map(([n,t])=>(<button key={n} onClick={()=>{setThresh({...t});setEnabled(Object.fromEntries(Object.keys(t).map(k=>[k,true])))}} style={chip(false)}>{n}</button>))}
        <button onClick={()=>{setThresh({});setEnabled({})}} style={{...chip(false),color:T.muted}}>Clear</button>
      </div>

      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,overflow:"hidden",marginBottom:14}}>
        <button onClick={()=>setOpenW(o=>!o)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 13px",background:"transparent",border:"none",color:T.text,cursor:"pointer",fontSize:12.5,fontWeight:800}}>
          <span>Thresholds <span style={{color:T.faint,fontWeight:600}}>· {active.length} active</span></span><span style={{color:T.green}}>{openW?"▾":"▸"}</span>
        </button>
        {openW && <div style={{padding:"0 13px 12px"}}>
          {Object.entries(SCREEN_METRICS).map(([grp,keys])=>(
            <div key={grp} style={{marginTop:8}}>
              <div style={{fontSize:9,color:T.faint,fontWeight:700,letterSpacing:"0.09em",margin:"4px 0 5px"}}>{grp.toUpperCase()}</div>
              {keys.map(k=>{ const r=ranges[k]; const col=GE_COL_BY_KEY[k]
                if(!r||r.max<=r.min) return (<div key={k} style={{display:"flex",alignItems:"center",gap:9,margin:"4px 0",opacity:.4}}><span style={{fontSize:11.5,color:T.muted2,width:104}}>{col[1]}</span><span style={{fontSize:10,color:T.faint}}>—</span></div>)
                const on=!!enabled[k]; const val=on?thOf(k):(r.asc?r.max:r.min)
                const step=k==="slot"?1:r.max<=3?0.01:r.max<=20?0.1:1
                return (
                <div key={k} style={{display:"flex",alignItems:"center",gap:8,margin:"5px 0"}}>
                  <button onClick={()=>toggle(k)} aria-label={col[1]} style={{width:17,height:17,borderRadius:4,flexShrink:0,cursor:"pointer",border:`1.5px solid ${on?T.green:T.border2}`,background:on?T.green:"transparent",color:"#04140D",fontSize:11,fontWeight:900,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>{on?"✓":""}</button>
                  <span onClick={()=>toggle(k)} style={{fontSize:11.5,color:on?T.text:T.muted2,width:96,fontWeight:on?700:400,cursor:"pointer"}}>{col[1]}<span style={{color:T.faint,fontSize:8}}>{r.asc?" ≤":" ≥"}</span></span>
                  <input type="range" min={r.min} max={r.max} step={step} value={val} disabled={!on} onChange={e=>setT(k,parseFloat(e.target.value))} style={{flex:1,accentColor:T.green,opacity:on?1:0.4}}/>
                  <span style={{fontFamily:T.mono,fontSize:11,fontWeight:800,color:on?T.green:T.muted2,width:52,textAlign:"right"}}>{col[3](val)}</span>
                </div>)})}
            </div>))}
        </div>}
      </div>

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:6}}>
        <div style={{fontSize:13,fontWeight:800,color:T.text}}>Matches <span style={{color:T.faint,fontWeight:600,fontSize:11}}>· {rows.length} bats</span></div>
        <button onClick={exportCsv} disabled={!rows.length} style={{...chip(false),opacity:rows.length?1:0.4}}>⧉ CSV</button>
      </div>
      {loading ? <div style={{color:T.muted,fontSize:12,padding:"20px 0",textAlign:"center"}}>Loading board…</div>
       : !rows.length ? <div style={{color:T.muted,fontSize:12,padding:"20px 0",textAlign:"center"}}>No bats clear every threshold — loosen a slider.</div>
       : <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
          <thead><tr style={{borderBottom:`1.5px solid ${T.border2}`}}>
            <th style={{textAlign:"left",padding:"5px 5px",color:T.muted,fontSize:8.5}}>#</th>
            <th style={{textAlign:"left",padding:"5px 5px",color:T.muted,fontSize:8.5}}>HITTER</th>
            <th style={{textAlign:"left",padding:"5px 5px",color:T.muted,fontSize:8.5}}>VS PITCHER</th>
            {cols.map(k=><Hh key={k} k={k} label={(GE_COL_BY_KEY[k][1]||"").toUpperCase()}/>)}
            <Hh k="prob_over" label="PROB"/><Hh k="confidence" label="RTG"/>
          </tr></thead>
          <tbody>{rows.map((r,i)=>{ const p=r.p; return (
            <tr key={p.player_id||i} onClick={()=>p.player_id&&onPlayer&&onPlayer({
                name:p.full_name, player_id:p.player_id,
                market:(MARKETS.find(m=>m[1]===mkt)||["HR"])[0],   // the Screener's filtered market → Case + odds match it
                conf:p.confidence, prob:p.prob_over, tier:p.tier,
                edge:p.edge_pct!=null?`${p.edge_pct>=0?"+":""}${(p.edge_pct*100).toFixed(1)}%`:undefined,
                odds:p.book_over!=null?fmtOdds(p.book_over):undefined, oppHand:p.opp_pitcher_hand,
                opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team,
                slot:p?.inputs?.lineup_slot, park:p?.inputs?.park_factor, team:p.team, oppPitcher:p.opp_pitcher_name})}
              style={{borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}>
              <td style={{padding:"6px 5px",color:T.green,fontWeight:800,fontFamily:T.mono}}>{i+1}</td>
              <td style={{padding:"6px 5px",fontWeight:700,color:T.text}}>{p.full_name} <span style={{fontFamily:T.mono,fontSize:8,color:T.faint}}>{p.team}</span></td>
              <td style={{padding:"6px 5px",color:T.muted,fontSize:10}}>{p.opp_pitcher_name||"—"}</td>
              {cols.map(k=>{ const col=GE_COL_BY_KEY[k]; const v=col[2](p); return (
                <td key={k} style={{padding:"6px 5px",textAlign:"right",fontFamily:T.mono,color:col[4](v)}}>{col[3](v)}</td>)})}
              <td style={{padding:"6px 5px",textAlign:"right",fontFamily:T.mono,fontWeight:700,color:probColor(p.prob_over)}}>{p.prob_over==null?"—":pct(p.prob_over)}</td>
              <td style={{padding:"6px 5px",textAlign:"right",fontFamily:T.mono,fontWeight:800,color:p.confidence==null?T.muted2:ratingColor(p.confidence)}}>{p.confidence==null?"—":Math.round(p.confidence)}</td>
            </tr>)})}
          </tbody>
        </table></div>}
      <div style={{fontSize:9,color:T.faint,marginTop:12,lineHeight:1.5}}>Sliders span each metric's real min→max on tonight's board. ≥ = floor, ≤ = ceiling (auto for lower-is-better stats). Tap a column header to sort. Research/info only · not betting advice · 21+ · 1-800-GAMBLER.</div>
    </div>
  )
}


function GameExplorer({picks, mkt, team, search, sortKey, setSortKey, onPlayer, toggleLeg, inParlay, gameTimes={}}){
  const q = search.trim().toLowerCase()
  const [hotOnly,setHotOnly] = useState(false)
  const [soFilter,setSoFilter] = useState(null)   // null | 'hot' (strong) | 'prime' — from tapping the summary counts
  const [attackOnly,setAttackOnly] = useState(false)   // only hitters vs a 🎯 attackable arm
  const [pitchSel,setPitchSel] = useState(null)   // pitch-mix filter: family name or null
  const [teamSel,setTeamSel]   = useState(()=>new Set())   // multi-team / game filter (empty = all)
  const [gamesOpen,setGamesOpen] = useState(false)         // 🏟 Games / Teams panel
  const { isPremium, needPremium } = useProGate()   // metric filters = Pro/Founder perk
  const [filters,setFilters] = useState([])   // [{key,op,val}] stacked (AND)
  const [fOpen,setFOpen] = useState(false)
  const [colOpen,setColOpen] = useState(false)          // column chooser panel
  const [colSel,setColSel] = useState(loadGridCols)     // { [market]: [visibleKey,…] } (order = display order)
  const saveColSel = next => { setColSel(next); try{ localStorage.setItem(GRID_COLS_KEY, JSON.stringify(next)) }catch(_){} }
  const [density,setDensity] = useState(()=>{ try{ return localStorage.getItem(GRID_DENSITY_KEY)||"comfortable" }catch(_){ return "comfortable" } })
  const setDens = d => { setDensity(d); try{ localStorage.setItem(GRID_DENSITY_KEY, d) }catch(_){} }
  const [fullscreen,setFullscreen] = useState(false)   // pop the grid out to full screen
  const [notesOpen,setNotesOpen] = useState(false)     // scratchpad panel: a list per bet type
  const [notes,setNotes] = useState(loadGridNotes)     // { [market]: text }
  const setNoteFor = (m, txt) => { const next = {...loadGridNotes(), [m]: txt}; setNotes(next); try{ localStorage.setItem(GRID_NOTES_KEY, JSON.stringify(next)) }catch(_){} }
  const hasNote = Object.values(notes).some(v => v && v.trim())   // any bet type has a list
  // Esc closes fullscreen; lock body scroll while popped out.
  useEffect(()=>{
    if(!fullscreen) return
    const onKey = e => { if(e.key==="Escape") setFullscreen(false) }
    window.addEventListener("keydown", onKey)
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden"
    return ()=>{ window.removeEventListener("keydown", onKey); document.body.style.overflow = prev }
  }, [fullscreen])
  const dense = density==="compact"
  const vpad = dense ? 3 : 8   // vertical cell padding (px) — the density lever
  const [fKey,setFKey] = useState("era")
  const [fOp,setFOp] = useState(">=")
  const [fVal,setFVal] = useState("")
  const addFilter = ()=>{ const n=parseFloat(fVal); if(!isFinite(n)) return; setFilters(f=>[...f,{key:fKey,op:fOp,val:n}]); setFVal("") }
  const passFilters = p => filters.every(f=>{ const v=geFieldVal(p,f.key); if(v==null) return false; return f.op===">=" ? v>=f.val : v<=f.val })
  // When a pitch is picked, sort the board by how the hitter does vs it; otherwise
  // rank by the one LINEBRKR Rating (the guide) so the grid's order matches its badges.
  useEffect(()=>{ setSortKey(pitchSel ? "_pfx" : "confidence") }, [pitchSel])
  // "Should hit" = the app's own strong tiers (same tierFor engine as everywhere).
  // These are the plays we want to POP: model likes it + the drivers line up.
  const mktLabel = (MARKETS.find(m=>m[1]===mkt)||["HRR"])[0]
  const rowTier = p => tierFor(p.prob_over, p.confidence, mktLabel, p.edge_pct!=null?p.edge_pct*100:null, p.book_over!=null)
  const HOT_TIERS = new Set(["ELITE","BEST BET","STRONG","PRIME SHOT"])
  const isHot = p => HOT_TIERS.has(rowTier(p))
  // "Prime spot" ⚡ — the DRIVERS line up even if the model prob is only borderline
  // (so it isn't a 🔥 tier). Market-aware: HR wants power + an HR-prone arm + park;
  // hits want top-of-order + a contact (low-K) hittable arm; HRR wants volume + a
  // hittable arm + park. A lighter amber shade below the strong plays.
  const primeSpot = p => {
    const i = p.inputs||{}
    const topOrder = i.lineup_slot!=null && i.lineup_slot<=5
    const softArm  = i.pitcher_ops_vs_hand!=null && i.pitcher_ops_vs_hand>=0.75
    if (mkt==="HR_anytime")
      return i.barrel_pct>=0.10 && i.hard_hit_pct>=0.42 && i.pitcher_hr9>=1.3 && i.park_factor>=1.05
    if (mkt==="hits" || mkt==="hits_1.5")
      return topOrder && (i.pitcher_k9!=null && i.pitcher_k9<=8) && softArm
    return topOrder && softArm && (i.park_factor!=null && i.park_factor>=1.0)  // HRR / TB / default
  }
  const isPrime = p => !isHot(p) && primeSpot(p)
  // Multi-select GAME / TEAM filter. Games are grouped by venue (both teams share one),
  // so a game toggle flips both its teams; individual team chips narrow further. Built off
  // the full slate so the chips stay stable as the grid filters. Empty set = whole slate.
  const slateGames = useMemo(()=>{
    const m=new Map()
    for(const p of picks){ const v=p.venue||"—"; if(!m.has(v)) m.set(v,{venue:v,teams:new Set()}); if(p.team) m.get(v).teams.add(p.team) }
    return [...m.values()].map(g=>({venue:g.venue, teams:[...g.teams].sort()}))
      .sort((a,b)=>(a.teams[0]||"").localeCompare(b.teams[0]||""))
  },[picks])
  const toggleTeam = t => setTeamSel(s=>{ const n=new Set(s); n.has(t)?n.delete(t):n.add(t); return n })
  const toggleGame = ts => setTeamSel(s=>{ const n=new Set(s); const allOn=ts.length>0 && ts.every(t=>n.has(t)); ts.forEach(t=>allOn?n.delete(t):n.add(t)); return n })
  // First pitch in ET minutes-past-midnight for a venue, from the game-times feed. Lets a
  // one-tap preset select every game starting at/after a cutoff (e.g. 6:30 PM = 1110).
  const _etMin = iso => { try{ const s=new Date(iso).toLocaleString("en-US",{timeZone:"America/New_York",hour12:false,hour:"2-digit",minute:"2-digit"}); const [h,m]=s.split(":").map(Number); return (h%24)*60+m }catch(_){ return null } }
  const haveTimes = Object.keys(gameTimes).length>0
  const selectAfter = cutMin => { const sel=new Set(); for(const g of slateGames){ const mm=_etMin(gameTimes[g.venue]); if(mm!=null && mm>=cutMin) g.teams.forEach(t=>sel.add(t)) } setTeamSel(sel); if(sel.size) setGamesOpen(true) }
  const base = picks
    .filter(p=>!team || p.team===team)
    .filter(p=> teamSel.size===0 || teamSel.has(p.team))
    .filter(p=>!q || (p.full_name||"").toLowerCase().includes(q) || (p.opp_pitcher_name||"").toLowerCase().includes(q))
  // Which pitch families are actually thrown ≥10% by starters on the slate → the chips.
  const availPitches = (()=>{ const s=new Set(); for(const p of base){ const b=p?.inputs?.pitch_breakdown; if(b) for(const f in b){ if((b[f].u||0)>=PITCH_MIN_USE) s.add(f) } } return PITCH_ORDER.filter(f=>s.has(f)) })()
  const pb = p => p?.inputs?.pitch_breakdown?.[pitchSel]
  // Pitch filter: keep only hitters whose starter throws the picked pitch ≥10%.
  const pitchFiltered = pitchSel ? base.filter(p=>{ const b=pb(p); return b && (b.u||0)>=PITCH_MIN_USE }) : base
  const hotCount   = pitchFiltered.filter(isHot).length
  const primeCount = pitchFiltered.filter(isPrime).length
  let rows = pitchFiltered
  if(attackOnly) rows = rows.filter(p=>pitcherAttack(p?.inputs))   // vs a hittable/HR-prone arm
  if(hotOnly)    rows = rows.filter(p=>isHot(p)||isPrime(p))
  if(soFilter==="hot")   rows = rows.filter(isHot)          // tapped "N strong"
  if(soFilter==="prime") rows = rows.filter(p=>isPrime(p))  // tapped "N prime"
  if(filters.length) rows = rows.filter(passFilters)   // stacked metric filters (AND)
  // Market-aware columns: HR shows the power profile, hits/HRR the contact+volume one.
  // A saved per-market layout (the ⚙ Columns chooser) overrides the default set; when
  // none is saved we fall back to the curated market defaults (geDefaultKeys).
  const colUniverse = geColUniverse(mkt)
  const isCustomCols = Array.isArray(colSel[mkt]) && colSel[mkt].length>0
  const visKeys = isCustomCols ? colSel[mkt] : geDefaultKeys(mkt)
  const mktCols = visKeys.map(k=>GE_COL_BY_KEY[k]).filter(Boolean)
  const toggleCol = k => {
    const cur = Array.isArray(colSel[mkt]) ? colSel[mkt] : geDefaultKeys(mkt)
    let next
    if(cur.includes(k)){ if(cur.length<=1) return; next = cur.filter(x=>x!==k) }
    else next = [...cur, k]   // add to the end; user can then reorder with the arrows
    saveColSel({...colSel, [mkt]: next})
  }
  // Move a visible column left/right (dir -1 / +1) — the saved order IS the display order.
  const moveCol = (k, dir) => {
    const cur = [...(Array.isArray(colSel[mkt]) ? colSel[mkt] : geDefaultKeys(mkt))]
    const i = cur.indexOf(k); if(i<0) return
    const j = i + dir; if(j<0 || j>=cur.length) return
    ;[cur[i], cur[j]] = [cur[j], cur[i]]
    saveColSel({...colSel, [mkt]: cur})
  }
  const resetCols = () => { const n={...colSel}; delete n[mkt]; saveColSel(n) }
  // When a pitch is selected, prepend two synthetic columns: the hitter's xwOBA vs
  // that pitch (the matchup) + how often the pitcher throws it.
  const _psh = PITCH_SHORT[pitchSel] || pitchSel
  const pitchCols = pitchSel ? [
    ["_pfx", `xwOBA vs ${_psh}`, p=>pb(p)?.x, v=>v==null?"—":v.toFixed(3).replace(/^0/,""), v=>v==null?T.muted2:(v>=0.360?T.green:v>=0.315?"#34D399":v<=0.290?T.red:T.muted)],
    ["_pfu", `${_psh} Use`,      p=>pb(p)?.u, v=>v==null?"—":`${Math.round(v*100)}%`,       v=>v==null?T.muted2:(v>=0.30?T.green:v>=0.18?"#34D399":T.muted)],
  ] : []
  // Base columns + auto-append any column being filtered so you SEE what you filtered.
  const cols = (()=>{
    const c = [...pitchCols, ...mktCols]
    for(const f of filters){ const gc=GE_COL_BY_KEY[f.key]; if(gc && !c.some(x=>x[0]===gc[0])) c.push(gc) }
    return geProbAwareCols(c, mktLabel)   // market-aware Prob color (24% HR = green)
  })()
  // If the current sort column isn't present, fall back to the first one.
  const activeKey = cols.some(c=>c[0]===sortKey) ? sortKey : cols[0][0]
  // Group by game (venue), then by TEAM within the game — one team's bats, then the
  // other's. Each team's hitters get a Rating rank (top 3 badged); rows still display
  // in the active-column order, and the strongest team (best Rating) leads the game.
  const games = useMemo(()=>{
    const rating = p => (p.confidence ?? confidenceFor(p))   // the one LINEBRKR Rating
    const col = cols.find(c=>c[0]===activeKey) || cols[0]
    const getter = col[2]
    const asc = col[5]===true   // some columns (batting order) are "lower is better"
    const NULLV = asc ? Infinity : -Infinity   // nulls sink to the bottom either way
    const m = new Map()
    for(const p of rows){
      const key = p.venue || p.opp_pitcher_name || "—"
      if(!m.has(key)) m.set(key, {venue:p.venue||"—", rows:[]})
      m.get(key).rows.push(p)
    }
    const arr = [...m.values()].map(g=>{
      const byTeam = new Map()
      for(const p of g.rows){ const t=p.team||"—"; if(!byTeam.has(t)) byTeam.set(t, []); byTeam.get(t).push(p) }
      const teams = [...byTeam.entries()].map(([team, tr])=>{
        // rank the team's hitters by the Rating (stable, independent of the sort column)
        const rank = new Map()
        ;[...tr].sort((a,b)=>rating(b)-rating(a)).forEach((p,i)=>rank.set(p.player_id ?? p.full_name, i+1))
        // Per-pitcher PRIME-SHOT cap: an uncalibrated power edge can flag a whole lineup
        // as PRIME SHOT. Keep only the strongest few by probability; demote the rest to
        // VALUE SHOT so the badge stays selective and meaningful.
        const PRIME_CAP = 4
        const tierOf = new Map(); const primes = []
        for(const p of tr){ const id=p.player_id ?? p.full_name; const t=rowTier(p); tierOf.set(id, t); if(t==="PRIME SHOT") primes.push(p) }
        if(primes.length > PRIME_CAP){
          primes.sort((a,b)=>(b.prob_over??0)-(a.prob_over??0))
          primes.slice(PRIME_CAP).forEach(p=>tierOf.set(p.player_id ?? p.full_name, "VALUE SHOT"))
        }
        // display order = the active column (so column sorting still works within a team)
        tr.sort((a,b)=>{ const x=getter(a)??NULLV, y=getter(b)??NULLV; return asc ? x-y : y-x })
        const top = tr[0]||{}
        return { team, rows:tr, rank, tierOf, pitcher:top.opp_pitcher_name||null, hand:top.opp_pitcher_hand||null,
                 bestRating: tr.reduce((mx,p)=>Math.max(mx, rating(p)??0), 0) }
      })
      teams.sort((a,b)=>b.bestRating - a.bestRating)   // strongest team leads the game
      g.teams = teams
      g.best = g.rows.reduce((mx,r)=>{ const v=getter(r); if(v==null) return mx; if(mx==null) return v; return asc?Math.min(mx,v):Math.max(mx,v) }, null)
      g.park = (g.rows[0]||{})?.inputs?.park_factor ?? null
      return g
    })
    arr.sort((a,b)=>{ const x=a.best??NULLV, y=b.best??NULLV; return asc ? x-y : y-x })
    return arr
  }, [rows, activeKey, mkt])
  // Tonight's pitchers to attack — the hittable/HR-prone starters, deduped, prime
  // (🔥 hittable AND homer-prone) first. Surfaced as an at-a-glance strip.
  const attackPitchers = useMemo(()=>{
    const seen = new Map()
    for(const g of games) for(const tm of g.teams){
      if(!tm.pitcher) continue
      const atk = pitcherAttack(tm.rows[0]?.inputs)
      if(atk && !seen.has(tm.pitcher)) seen.set(tm.pitcher, {name:tm.pitcher, hand:tm.hand, ...atk})
    }
    return [...seen.values()].sort((a,b)=>(b.hot?1:0)-(a.hot?1:0))
  }, [games])
  const total = rows.length
  const exportCsv = ()=>{
    const head = ["Game","Team","Hitter","Bats","OppPitcher","OppHand", ...cols.map(c=>c[1])]
    const lines = [head.join(",")]
    for(const g of games) for(const p of g.rows){
      const cells = [g.venue, p.team||"", p.full_name||"", p?.inputs?.bats||"", p.opp_pitcher_name||"", p.opp_pitcher_hand||"",
        ...cols.map(c=>{ const v=c[2](p); return v==null?"":typeof v==="number"?v:String(v) })]
      lines.push(cells.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(","))
    }
    try{
      const blob = new Blob([lines.join("\n")], {type:"text/csv"})
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a"); a.href=url; a.download=`linebrkr_${mkt}_${todayStr()}.csv`; a.click()
      setTimeout(()=>URL.revokeObjectURL(url), 1000)
    }catch(_){}
  }
  const th = (c)=>(
    <th key={c[0]} onClick={()=>setSortKey(c[0])}
      style={{padding:"7px 8px",textAlign:"right",whiteSpace:"nowrap",cursor:"pointer",position:"sticky",top:0,zIndex:2,
        background:T.card2,color:activeKey===c[0]?T.green:T.muted,fontSize:9.5,fontWeight:800,letterSpacing:"0.04em",
        textTransform:"uppercase",borderBottom:`1px solid ${T.border}`,userSelect:"none"}}>
      {c[1]}{activeKey===c[0]?(c[5]===true?" ▲":" ▼"):""}
    </th>
  )
  const pchip = on => ({padding:"4px 10px",borderRadius:16,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",border:`1px solid ${on?T.greenMid:T.border2}`,background:on?T.greenDim:T.card,color:on?T.green:T.muted2})
  return (
    // Break out of the app's phone-width (460px) column so the dense grid can use
    // the full screen on desktop/tablet — all columns visible without side-scroll —
    // while min(96vw,…) keeps it edge-to-edge on a phone. Centered over the viewport.
    // Fullscreen (⛶ Expand): the same div becomes a fixed, full-viewport scroll
    // container so the grid gets the whole screen; all filter/sort state persists.
    <div style={fullscreen
      ? {position:"fixed",inset:0,zIndex:9000,background:T.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"14px 16px 48px",boxSizing:"border-box",width:"100vw"}
      : {padding:"0 14px",boxSizing:"border-box",width:"min(96vw,1120px)",position:"relative",left:"50%",transform:"translateX(-50%)"}}>
      <div style={{margin:"2px 0 8px"}}>
        <div style={{fontSize:11,color:T.muted,marginBottom:8}}>{total} hitter{total===1?"":"s"} · <span onClick={()=>setSoFilter(f=>f==="hot"?null:"hot")} title="Tap to show only strong plays" style={{cursor:"pointer",padding:"1px 3px",borderRadius:4,background:soFilter==="hot"?T.greenDim:"transparent",textDecoration:soFilter==="hot"?"none":"underline",textDecorationStyle:"dotted",textUnderlineOffset:2}}><b style={{color:T.green}}>{hotCount}</b> strong{soFilter==="hot"?" ✕":""}</span> · <span onClick={()=>setSoFilter(f=>f==="prime"?null:"prime")} title="Tap to show only prime driver spots" style={{cursor:"pointer",padding:"1px 3px",borderRadius:4,background:soFilter==="prime"?`${T.amber}22`:"transparent",textDecoration:soFilter==="prime"?"none":"underline",textDecorationStyle:"dotted",textUnderlineOffset:2}}><b style={{color:T.amber}}>{primeCount}</b> prime{soFilter==="prime"?" ✕":""}</span> · sorted by <b style={{color:T.green}}>{(cols.find(c=>c[0]===activeKey)||cols[0])[1]}</b></div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setAttackOnly(v=>!v)} title="Only hitters facing a 🎯 pitcher-to-attack (hittable or HR-prone arm)" style={{background:attackOnly?T.greenDim:T.card,border:`1px solid ${attackOnly?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:attackOnly?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{attackOnly?"✓ ":""}🎯 Attack only</button>
          <button onClick={()=>setHotOnly(v=>!v)} title="Only standouts — strong plays (🔥) and prime driver spots (⚡)" style={{background:hotOnly?T.greenDim:T.card,border:`1px solid ${hotOnly?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:hotOnly?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{hotOnly?"✓ ":""}🔥 Standouts</button>
          <button onClick={()=>{ if(!isPremium){ needPremium&&needPremium(); return } setFOpen(o=>!o) }} title="Filter the grid by any metric — pitcher ERA, barrel%, park & more (Pro/Founder)" style={{background:(fOpen||filters.length)?T.greenDim:T.card,border:`1px solid ${(fOpen||filters.length)?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:(fOpen||filters.length)?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>⚗ Filter{filters.length?` · ${filters.length}`:(!isPremium?" 🔒":"")}</button>
          <button onClick={()=>setColOpen(o=>!o)} title="Choose, reorder & save which metric columns to show — per market, on this device" style={{background:(colOpen||isCustomCols)?T.greenDim:T.card,border:`1px solid ${(colOpen||isCustomCols)?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:(colOpen||isCustomCols)?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>⚙ Columns{isCustomCols?` · ${mktCols.length}`:""}</button>
          <button onClick={()=>setDens(dense?"comfortable":"compact")} title={dense?"Comfortable rows":"Compact rows — fit more on screen"} style={{background:dense?T.greenDim:T.card,border:`1px solid ${dense?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:dense?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{dense?"≡ Compact":"≡ Rows"}</button>
          <button onClick={()=>setNotesOpen(o=>!o)} title="Jot down your guys for the day — a private scratchpad for this bet type, saved on this device" style={{background:(notesOpen||hasNote)?T.greenDim:T.card,border:`1px solid ${(notesOpen||hasNote)?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:(notesOpen||hasNote)?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>✎ Notes{hasNote?" ·":""}</button>
          <button onClick={()=>setFullscreen(f=>!f)} title={fullscreen?"Exit full screen (Esc)":"Pop the grid out to full screen"} style={{background:fullscreen?T.greenDim:T.card,border:`1px solid ${fullscreen?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:fullscreen?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{fullscreen?"⤡ Exit":"⛶ Expand"}</button>
          <button onClick={exportCsv} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 11px",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>⬇ CSV</button>
        </div>
      </div>
      {notesOpen && (
        <div style={{margin:"0 0 8px",padding:"9px 11px",background:T.card2,border:`1px solid ${T.border2}`,borderRadius:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:9.5,color:T.muted,fontWeight:800,letterSpacing:"0.08em"}}>MY GUYS · TODAY — A LIST PER BET TYPE</span>
            <span style={{fontSize:10,color:T.muted}}>saved on this device</span>
          </div>
          {/* One labeled list per bet type, the market you're viewing first. In full
              screen they lay out as a responsive grid so every list is visible at once. */}
          <div style={{display:"grid",gridTemplateColumns:fullscreen?"repeat(auto-fit,minmax(240px,1fr))":"1fr",gap:8}}>
            {[MARKETS.find(m=>m[1]===mkt), ...MARKETS.filter(m=>m[1]!==mkt)].filter(Boolean).map(([lbl,key])=>{
              const cur = key===mkt
              return (
                <div key={key} style={{background:T.card,border:`1px solid ${cur?T.greenMid:T.border2}`,borderRadius:9,padding:"7px 9px"}}>
                  <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.04em",color:cur?T.green:T.muted2,marginBottom:5}}>{(MARKET_LABEL[lbl]||lbl).toUpperCase()}{cur?" · viewing":""}</div>
                  <textarea value={notes[key]||""} onChange={e=>setNoteFor(key, e.target.value)} placeholder={`${MARKET_LABEL[lbl]||lbl} guys — one per line…`} rows={fullscreen?5:3} style={{width:"100%",boxSizing:"border-box",resize:"vertical",background:T.bg,border:`1px solid ${T.border2}`,borderRadius:7,padding:"7px 9px",color:T.text,fontSize:12.5,lineHeight:1.5,fontFamily:"inherit"}}/>
                </div>
              )
            })}
          </div>
        </div>
      )}
      {fOpen && isPremium && (
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",margin:"0 0 8px",padding:"9px 11px",background:T.card2,border:`1px solid ${T.border2}`,borderRadius:10}}>
          <span style={{fontSize:9.5,color:T.muted,fontWeight:800,letterSpacing:"0.08em"}}>SHOW HITTERS WHERE</span>
          <select value={fKey} onChange={e=>setFKey(e.target.value)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",color:T.text,fontSize:12,cursor:"pointer"}}>
            {GE_FILTER_FIELDS.map(([k,label])=><option key={k} value={k}>{label}</option>)}
          </select>
          <select value={fOp} onChange={e=>setFOp(e.target.value)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",color:T.text,fontSize:12,cursor:"pointer"}}>
            <option value=">=">≥</option><option value="<=">≤</option>
          </select>
          <input type="number" step="any" value={fVal} onChange={e=>setFVal(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") addFilter() }} placeholder={(GE_FILTER_FIELDS.find(f=>f[0]===fKey)||[])[2]||"value"} style={{width:80,boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:7,padding:"5px 8px",color:T.text,fontSize:12}}/>
          <button onClick={addFilter} style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:7,padding:"5px 13px",color:T.green,fontSize:11.5,fontWeight:800,cursor:"pointer"}}>Add</button>
          <span style={{fontSize:10,color:T.muted}}>stack as many as you want — all applied together</span>
        </div>
      )}
      {colOpen && (
        <div style={{margin:"0 0 8px",padding:"10px 12px",background:T.card2,border:`1px solid ${T.border2}`,borderRadius:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:9.5,color:T.muted,fontWeight:800,letterSpacing:"0.08em"}}>SHOW COLUMNS · {(MARKETS.find(m=>m[1]===mkt)||["",""])[0]||"grid"}</span>
            <span style={{display:"flex",gap:12,alignItems:"center"}}>
              {isCustomCols && <button onClick={resetCols} style={{background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>reset to default</button>}
              <button onClick={()=>setColOpen(false)} style={{background:"none",border:"none",color:T.muted2,fontSize:14,lineHeight:1,cursor:"pointer"}}>×</button>
            </span>
          </div>
          {/* Showing — in display order, with left/right move + remove */}
          <div style={{fontSize:8.5,color:T.muted2,fontWeight:800,letterSpacing:"0.08em",marginBottom:5}}>SHOWING · TAP ◀ ▶ TO REORDER</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:"6px 8px"}}>
            {visKeys.map((k,i)=>{ const c=GE_COL_BY_KEY[k]; if(!c) return null; const only=visKeys.length<=1; return (
              <span key={k} style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:11,fontWeight:700,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"3px 4px 3px 9px"}}>
                <button onClick={()=>moveCol(k,-1)} disabled={i===0} title="Move left" style={{background:"none",border:"none",color:i===0?T.muted2:T.green,cursor:i===0?"default":"pointer",fontSize:11,lineHeight:1,padding:"0 1px",opacity:i===0?0.4:1}}>◀</button>
                <span>{c[1]}</span>
                <button onClick={()=>moveCol(k,1)} disabled={i===visKeys.length-1} title="Move right" style={{background:"none",border:"none",color:i===visKeys.length-1?T.muted2:T.green,cursor:i===visKeys.length-1?"default":"pointer",fontSize:11,lineHeight:1,padding:"0 1px",opacity:i===visKeys.length-1?0.4:1}}>▶</button>
                <button onClick={()=>toggleCol(k)} disabled={only} title={only?"at least one column must stay":"Hide"} style={{background:"none",border:"none",color:T.green,cursor:only?"default":"pointer",fontSize:13,lineHeight:1,padding:"0 3px 0 2px",opacity:only?0.4:1}}>×</button>
              </span>
            )})}
          </div>
          {/* Hidden — tap to add to the end */}
          {colUniverse.some(k=>!visKeys.includes(k)) && (<>
            <div style={{fontSize:8.5,color:T.muted2,fontWeight:800,letterSpacing:"0.08em",margin:"10px 0 5px"}}>ADD A COLUMN</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"6px 8px"}}>
              {colUniverse.filter(k=>!visKeys.includes(k)).map(k=>{ const c=GE_COL_BY_KEY[k]; if(!c) return null; return (
                <button key={k} onClick={()=>toggleCol(k)} style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,cursor:"pointer",color:T.muted2,background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"4px 10px"}}>
                  <span style={{fontSize:10}}>+</span>{c[1]}
                </button>
              )})}
            </div>
          </>)}
          <div style={{fontSize:10,color:T.muted,marginTop:8}}>Saved on this device for the {(MARKETS.find(m=>m[1]===mkt)||["",""])[0]||"current"} board. Sort still works on whatever’s shown; the Hitter, Opp{pitchSel?" and pitch":""} columns always stay.</div>
        </div>
      )}
      {filters.length>0 && (
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",margin:"0 0 9px"}}>
          {filters.map((f,i)=>{ const lbl=(GE_FILTER_FIELDS.find(x=>x[0]===f.key)||[null,f.key])[1]; return (
            <span key={i} style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,fontWeight:700,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"3px 5px 3px 10px"}}>
              {lbl} {f.op===">="?"≥":"≤"} {f.val}
              <button onClick={()=>setFilters(fs=>fs.filter((_,j)=>j!==i))} style={{background:"none",border:"none",color:T.green,cursor:"pointer",fontSize:14,lineHeight:1,padding:"0 3px"}}>×</button>
            </span>
          )})}
          <button onClick={()=>setFilters([])} style={{background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>clear all</button>
        </div>
      )}
      {slateGames.length>0 && (
        <div style={{margin:"0 0 9px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <button onClick={()=>setGamesOpen(o=>!o)} style={{display:"inline-flex",alignItems:"center",gap:6,background:teamSel.size?T.greenDim:T.card,border:`1px solid ${teamSel.size?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:teamSel.size?T.green:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer"}}>
              🏟 Games / Teams{teamSel.size?` · ${teamSel.size}`:""} <span style={{color:T.green}}>{gamesOpen?"▾":"▸"}</span>
            </button>
            {haveTimes && <button onClick={()=>selectAfter(18*60+30)} title="Select every game starting after 6:30 PM ET" style={pchip(false)}>🌙 After 6:30</button>}
            {haveTimes && <button onClick={()=>selectAfter(20*60)} title="Select every game starting after 8:00 PM ET" style={pchip(false)}>🌆 Prime 8pm+</button>}
            {teamSel.size>0 && <button onClick={()=>setTeamSel(new Set())} style={{background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>clear</button>}
            {teamSel.size>0 && !gamesOpen && <span style={{fontSize:10,color:T.muted}}>showing {[...teamSel].sort().join(", ")}</span>}
          </div>
          {gamesOpen && (
            <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:7,maxHeight:240,overflowY:"auto",padding:"9px 10px",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10}}>
              <div style={{fontSize:9.5,color:T.muted,fontWeight:800,letterSpacing:"0.08em"}}>TAP A GAME TO PICK BOTH SIDES · OR A TEAM TO NARROW · MULTI-SELECT</div>
              {slateGames.map(g=>{ const allOn=g.teams.length>0 && g.teams.every(t=>teamSel.has(t)); return (
                <div key={g.venue} style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                  <button onClick={()=>toggleGame(g.teams)} title={g.venue} style={{...pchip(allOn),fontWeight:800}}>{g.teams.join(" · ")||g.venue}</button>
                  {g.teams.map(t=>(<button key={t} onClick={()=>toggleTeam(t)} style={pchip(teamSel.has(t))}>{t}</button>))}
                </div>
              )})}
            </div>
          )}
        </div>
      )}
      {availPitches.length>0 && (
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",margin:"0 0 9px"}}>
          <span style={{fontSize:9.5,color:T.muted,fontWeight:800,letterSpacing:"0.1em",marginRight:1}}>PITCH FILTER</span>
          <button onClick={()=>setPitchSel(null)} style={pchip(!pitchSel)}>All</button>
          {availPitches.map(f=>(
            <button key={f} onClick={()=>setPitchSel(pitchSel===f?null:f)} style={pchip(pitchSel===f)}>{f}</button>
          ))}
          {pitchSel && <span style={{fontSize:10,color:T.muted,marginLeft:2}}>hitters facing a <b style={{color:T.text}}>{pitchSel}</b> ≥10% · ranked by who hits it best</span>}
        </div>
      )}
      {attackPitchers.length>0 && (
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",margin:"0 0 9px",padding:"7px 10px",background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:10}}>
          <span style={{fontSize:9.5,color:T.green,fontWeight:800,letterSpacing:"0.08em",marginRight:1}}>🎯 ATTACK TONIGHT</span>
          {attackPitchers.map(p=>(
            <span key={p.name} title={p.why} style={{fontSize:10,fontWeight:700,color:p.hot?"#04140D":T.green,background:p.hot?T.green:T.card,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"3px 9px"}}>{p.hot?"🔥 ":""}{p.name}{p.hand?` (${p.hand})`:""}</span>
          ))}
          <span style={{fontSize:9,color:T.muted2,marginLeft:2}}>most hittable starters · 🔥 = also HR-prone</span>
        </div>
      )}
      {total===0 ? <div style={{color:T.muted,fontSize:13,padding:"8px 2px"}}>{pitchSel?`No hitters facing a ${pitchSel} ≥10% on this slate — try another pitch or clear the filter.`:"No hitters match — try a different market or clear filters."}</div> : (
      <div style={{overflowX:"auto",overflowY:"auto",maxHeight:"78vh",border:`1px solid ${T.border}`,borderRadius:12}}>
        <table style={{borderCollapse:"collapse",width:"100%",minWidth:880}}>
          <thead><tr>
            <th onClick={()=>setSortKey("player")} style={{padding:"7px 10px",textAlign:"left",position:"sticky",top:0,zIndex:2,background:T.card2,color:sortKey==="player"?T.green:T.muted,fontSize:9.5,fontWeight:800,letterSpacing:"0.04em",textTransform:"uppercase",borderBottom:`1px solid ${T.border}`,cursor:"pointer",userSelect:"none"}}>Hitter</th>
            <th style={{padding:"7px 10px",textAlign:"left",position:"sticky",top:0,zIndex:2,background:T.card2,color:T.muted,fontSize:9.5,fontWeight:800,letterSpacing:"0.04em",textTransform:"uppercase",borderBottom:`1px solid ${T.border}`}}>Opp</th>
            {cols.map(th)}
          </tr></thead>
          <tbody>
            {games.map((g,gi)=>(
              <React.Fragment key={g.venue+gi}>
                <tr>
                  <td colSpan={2+cols.length} style={{padding:"7px 10px",background:T.card3,borderTop:`1px solid ${T.border2}`,borderBottom:`1px solid ${T.border}`}}>
                    <span style={{fontSize:11.5,fontWeight:800,color:T.text}}>🏟 {g.venue}</span>
                    {g.park!=null && <span style={{fontSize:10,fontWeight:700,color:g.park>=1.03?T.green:g.park<=0.97?T.muted2:T.muted,marginLeft:8}}>{g.park>=1.10?"☀️":g.park<=0.97?"🅿️":"●"} Park {g.park.toFixed(2)}x</span>}
                    <span style={{fontSize:10,color:T.muted2,marginLeft:8}}>· {g.rows.length} hitter{g.rows.length===1?"":"s"}</span>
                  </td>
                </tr>
                {g.teams.map((tm,ti)=>(
                  <React.Fragment key={tm.team+ti}>
                    <tr>
                      <td colSpan={2+cols.length} style={{padding:"5px 10px 5px 14px",background:T.card2,borderBottom:`1px solid ${T.border}`}}>
                        <span style={{fontSize:11,fontWeight:800,color:T.green}}>{tm.team}</span>
                        {tm.pitcher && <span style={{fontSize:10,color:T.muted,marginLeft:8}}>vs {tm.pitcher}{tm.hand?` (${tm.hand})`:""}</span>}
                        {(()=>{ const atk=pitcherAttack(tm.rows[0]?.inputs); return atk ? <span title={atk.why} style={{fontSize:8.5,fontWeight:800,letterSpacing:"0.04em",color:atk.hot?"#04140D":T.green,background:atk.hot?T.green:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:5,padding:"1px 6px",marginLeft:8}}>🎯 ATTACK{atk.hot?" 🔥":""}</span> : null })()}
                        <span style={{fontSize:9.5,color:T.muted2,marginLeft:8}}>· {tm.rows.length} bat{tm.rows.length===1?"":"s"} · top 3 ranked</span>
                      </td>
                    </tr>
                    {tm.rows.map((p,ri)=>{ const rt=(tm.tierOf&&tm.tierOf.get(p.player_id ?? p.full_name))||rowTier(p); const hot=HOT_TIERS.has(rt); const prime=!hot&&primeSpot(p); const bar=hot?T.green:prime?T.amber:null; const rank=tm.rank.get(p.player_id ?? p.full_name); const rc=rank===1?"#E3B341":rank===2?T.green:rank===3?"#39C5CF":null; return (
                      <tr key={(p.player_id??p.full_name)+"-"+ri}
                        onClick={()=>p.player_id&&onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:(MARKETS.find(m=>m[1]===mkt)||["HRR"])[0],conf:p.confidence,prob:p.prob_over,tier:p.tier,odds:p.book_over!=null?fmtOdds(p.book_over):undefined,oppHand:p.opp_pitcher_hand,opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team,slot:p?.inputs?.lineup_slot,park:p?.inputs?.park_factor,team:p.team,oppPitcher:p.opp_pitcher_name})}
                        style={{cursor:p.player_id?"pointer":"default",borderBottom:`1px solid ${T.border}`,background:hot?T.greenDim:prime?"rgba(227,179,65,0.10)":"transparent",boxShadow:bar?`inset 4px 0 0 ${bar}`:"none"}}>
                        <td style={{padding:`${vpad}px 10px ${vpad}px 16px`,textAlign:"left",whiteSpace:"nowrap"}}>
                          {rc && <span style={{display:"inline-block",minWidth:15,textAlign:"center",fontSize:9,fontWeight:900,color:rank===1?"#1a1300":rc,background:rank===1?rc:"transparent",border:`1px solid ${rc}`,borderRadius:5,padding:"1px 4px",marginRight:6}}>{rank}</span>}
                          {hot ? <span style={{marginRight:5}}>🔥</span> : prime ? <span style={{marginRight:5}}>⚡</span> : null}
                          <span style={{fontSize:13,fontWeight:(hot||prime||rank<=3)?800:700,color:T.text}}>{p.full_name}</span>
                          {hot && <span style={{fontSize:8.5,fontWeight:800,color:(TIER[rt]||TIER.WATCH).bd,marginLeft:6,letterSpacing:"0.04em"}}>{tierLabel(rt)}</span>}
                          {prime && <span style={{fontSize:8.5,fontWeight:800,color:T.amber,marginLeft:6,letterSpacing:"0.04em"}}>PRIME</span>}
                          {isProbTrap(p.prob_over, p.confidence ?? confidenceFor(p), mktLabel) && <span title="High hit % but low Rating — volume, not a quality spot" style={{fontSize:10,marginLeft:6,cursor:"help"}}>⚠️</span>}
                          {p?.inputs?.bats && <span style={{fontSize:10,color:T.muted2,marginLeft:6}}>{p.inputs.bats}HB</span>}
                          {toggleLeg && (()=>{ const lk=`${p.player_id||p.full_name}-${mktLabel}`; const on=inParlay?inParlay(lk):false; return (
                            <span role="button" title={on?"In your slip":"Add to slip"} onClick={ev=>{ ev.stopPropagation(); toggleLeg({key:lk,name:p.full_name,player_id:p.player_id,market:mktLabel,prob:p.prob_over,oddsNum:p.book_over,team:p.team,opp:p.opp_pitcher_name}) }}
                              style={{marginLeft:8,width:20,height:20,borderRadius:"50%",display:"inline-flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12,fontWeight:800,lineHeight:1,verticalAlign:"middle",background:on?T.green:T.greenDim,color:on?"#04140D":T.green,border:`1px solid ${T.green}`}}>{on?"✓":"+"}</span>
                          )})()}
                        </td>
                        <td style={{padding:`${vpad}px 10px`,textAlign:"left",whiteSpace:"nowrap",fontSize:11,color:T.muted}}>{p.opp_pitcher_name||"—"}{p.opp_pitcher_hand?` (${p.opp_pitcher_hand})`:""}</td>
                        {cols.map(c=>{ const v=c[2](p); return (
                          <td key={c[0]} style={{padding:`${vpad}px 8px`,textAlign:"right",whiteSpace:"nowrap",fontFamily:T.mono,fontSize:12,fontWeight:c[0]===activeKey?800:600,color:c[4](v)}}>{c[3](v)}</td>
                        )})}
                      </tr>
                    )})}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>)}
      <div style={{fontSize:10,color:T.muted2,lineHeight:1.5,marginTop:10}}>
        Tap a column to sort · tap a hitter for the full card. <b style={{color:T.text}}>Green = favorable</b> (form heating up, hittable arm, soft P OPS/hand, hitter park); <b style={{color:T.text}}>red = tough</b>. <b style={{color:T.text}}>P OPS/hand</b> = the opposing pitcher's OPS allowed to this hitter's side — higher is better for the hitter.
      </div>
    </div>
  )
}
const STORM_KEY = "perfect_storm"
// Collapsible "does the storm premise hold?" panel — fetches the backtest on open.
function StormBacktestPanel(){
  const [open,setOpen] = useState(false)
  const [data,setData] = useState(null)
  const [loading,setLoading] = useState(false)
  const load = ()=>{ if(data) return; setLoading(true); getStormBacktest(45).then(d=>setData(d||{})).catch(()=>setData({})).finally(()=>setLoading(false)) }
  const onToggle = ()=>{ const n=!open; setOpen(n); if(n) load() }
  const base = data?.baseline?.hit_rate
  const row = (label, b)=> b && b.n>0 ? (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderTop:`1px solid ${T.border}`,fontSize:11.5}}>
      <span style={{color:T.muted}}>{label} <span style={{color:T.muted2,fontFamily:T.mono,fontSize:10}}>n={b.n}</span></span>
      <span style={{fontFamily:T.mono,fontWeight:800,color:b.hit_rate!=null?probColor(b.hit_rate):T.muted2}}>
        {b.hit_rate!=null?pct(b.hit_rate):"—"}{b.lift!=null?<span style={{color:b.lift>=0?T.green:"#ef6a6a",fontWeight:700,fontSize:10}}> {b.lift>=0?"+":""}{Math.round(b.lift*1000)/10}pts</span>:null}
      </span>
    </div>
  ) : null
  return (
    <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"10px 12px"}}>
      <button onClick={onToggle} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:"none",border:"none",color:T.text,cursor:"pointer",padding:0,fontSize:13,fontWeight:800}}>
        <span>📊 Does the premise hold? <span style={{color:T.muted,fontWeight:600,fontSize:11}}>backtest</span></span>
        <span style={{color:T.muted}}>{open?"▾":"▸"}</span>
      </button>
      {open && (
        <div style={{marginTop:8}}>
          {loading && <div style={{color:T.muted,fontSize:12,padding:"4px 0"}}>Crunching settled history…</div>}
          {!loading && data && (data.baseline?.n ? (
            <>
              <div style={{fontSize:10.5,color:T.muted,marginBottom:4}}>Last {data.window?.days||45}d · baseline HRR <b style={{color:probColor(base)}}>{pct(base)}</b> (the bar to beat)</div>
              {row("pitcher OPS pass", data.factors?.pitcher_ops?.pass)}
              {row("zone fit pass", data.factors?.zone_fit?.pass)}
              {row("form pass", data.factors?.form?.pass)}
              {row("STORM (all 3)", data.storm)}
              {row("STORM +EV", data.storm_ev)}
              {data.storm_ev?.roi!=null && <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderTop:`1px solid ${T.border}`,fontSize:11.5}}><span style={{color:T.muted}}>STORM +EV ROI <span style={{color:T.muted2,fontFamily:T.mono,fontSize:10}}>1u flat</span></span><span style={{fontFamily:T.mono,fontWeight:800,color:data.storm_ev.roi>=0?T.green:"#ef6a6a"}}>{data.storm_ev.roi>=0?"+":""}{(data.storm_ev.roi*100).toFixed(1)}%</span></div>}
              <div style={{fontSize:9,color:T.muted2,marginTop:6,lineHeight:1.4}}>zone fit + pitcher OPS use season aggregates (mild look-ahead); form is point-in-time.</div>
            </>
          ) : <div style={{color:T.muted,fontSize:12,padding:"4px 0"}}>Not enough settled history yet — check back once more games settle.</div>)}
        </div>
      )}
    </div>
  )
}
// ── HR Storm — the HR Rating, decomposed ─────────────────────────────────────
// Every true home-run driver in one place, as a DECOMPOSITION of the HR Rating
// (not a competing number). Four pillars explain the spot:
//   power  — barrel / hard-hit / pull / fly-ball / ISO / xwOBAcon (the swing)
//   arm    — pitcher HR/9 (incl. vs the batter's hand), barrels & xwOBA allowed
//   env    — park HR factor (handedness) + weather HR-impact (temp/wind)
//   form   — recent HR rate (L15) vs season, with zone fit riding along
// A "true storm" (🎆) is when EVERY pillar clears its bar. The headline number is
// the one HR Rating; these pillars are the "why", and 🎆 is an align-flag.
const HR_STORM_KEY = "hr_storm"
function hrStormScore(p){
  const i = p?.inputs || {}
  // Power pillar — BARREL-DOMINANT. Barrel% is by far the strongest, most monotonic
  // HR predictor in our own validation (HR rate climbs 5.6%→17.7% across barrel
  // buckets, vs a 10% base), so it CARRIES the pillar (0.42) instead of being one of
  // six equal terms that let softer stats dilute it. xwOBAcon/ISO/hard-hit fill in
  // contact quality; fly-ball & pull are the "in the air / to the pull side" finishers.
  const powW = [ [i.barrel_pct!=null    ? _n01(i.barrel_pct,0.04,0.17)   : null, 0.42],
                 [i.xwoba!=null          ? _n01(i.xwoba,0.30,0.42)         : null, 0.16],
                 [i.iso!=null            ? _n01(i.iso,0.12,0.30)           : null, 0.16],
                 [i.hard_hit_pct!=null   ? _n01(i.hard_hit_pct,0.30,0.55)  : null, 0.12],
                 [i.batter_fb_pct!=null  ? _n01(i.batter_fb_pct,0.25,0.45) : null, 0.08],
                 [i.pull_pct!=null       ? _n01(i.pull_pct,0.30,0.50)      : null, 0.06] ]
                 .filter(x=>x[0]!=null)
  let power = powW.length ? powW.reduce((s,x)=>s+x[0]*x[1],0)/powW.reduce((s,x)=>s+x[1],0) : null
  // Small-sample guard: Statcast power (barrel/xwOBAcon/ISO) stabilizes around
  // ~150 PA. Regress a thin-sample profile toward a neutral 0.5 so a 60-PA hot
  // start (e.g. a called-up prospect) can't max the power pillar and fake a
  // "true storm". No PA info (older payload) → leave power untouched.
  if(power!=null && i.batter_pa!=null){
    const w = i.batter_pa/(i.batter_pa+150)
    power = 0.5 + w*(power-0.5)
  }
  // Arm pillar is HR-led: HR/9 vs the batter's hand (0.40) + overall HR/9 (0.30)
  // dominate over general hittability (barrels 0.15 + xwOBA-against 0.15). Equal
  // weighting let two green contact stats outvote a pitcher who actually
  // suppresses HRs to this hand, flagging non-HR spots as "storms".
  const armW = [ [i.pitcher_hr9_vs_hand!=null    ? _n01(i.pitcher_hr9_vs_hand,0.8,2.2)      : null, 0.40],
                 [i.pitcher_hr9!=null            ? _n01(i.pitcher_hr9,0.8,2.2)              : null, 0.30],
                 [i.pitcher_barrel_allowed!=null ? _n01(i.pitcher_barrel_allowed,0.04,0.12): null, 0.15],
                 [i.pitcher_xwoba_against!=null  ? _n01(i.pitcher_xwoba_against,0.28,0.36) : null, 0.15] ]
                 .filter(x=>x[0]!=null)
  const arm = armW.length ? armW.reduce((s,x)=>s+x[0]*x[1],0)/armW.reduce((s,x)=>s+x[1],0) : null
  const wx = i.weather_hr_impact!=null ? _n01(i.weather_hr_impact,-0.07,0.08) : null
  const env  = _avg([ i.park_factor!=null ? _n01(i.park_factor,0.90,1.30) : null, wx ])
  const formR = (i.hr_season>0 && i.hr_l15!=null) ? Math.min(2.0, i.hr_l15/i.hr_season) : (i.hr_l15>0?1.3:1)
  const form = _n01(formR,0.5,1.8)
  if(power==null && arm==null) return null
  // Bad-matchup guardrail — the Suárez-vs-Pallante fix. A very low arm pillar means the
  // pitcher ACTIVELY suppresses HR to this hand (Pallante ~0.76 HR/9). Elite power can't
  // manufacture a homer the pitcher doesn't allow, so a green bat here is a trap, not a
  // storm: veto the 🎆 flag, surface a ⚠ chip, and demote the spot in score AND ranking.
  const badMatchup = (arm != null && arm < 0.28)
  let score = Math.round((0.42*(power??0.4) + 0.30*(arm??0.4) + 0.18*(env??0.5) + 0.10*(form??0.5))*100)
  if(badMatchup) score = Math.round(score*0.82)
  return { score, power, arm, env, form, badMatchup }
}
const hrIsStorm = d => !!(d && !d.badMatchup && d.power!=null && d.arm!=null && d.power>=0.55 && d.arm>=0.50 && (d.env==null || d.env>=0.50))
const hrRatingOf = p => (p?.confidence ?? confidenceFor(p))
function HRStormView({picks, team, search, onPlayer}){
  const [stormsOnly,setStormsOnly] = useState(false)
  const rows = useMemo(()=>{
    const q = (search||"").trim().toLowerCase()
    return picks
      .filter(p=>!team || p.team===team)
      .filter(p=>!q || (p.full_name||"").toLowerCase().includes(q) || (p.opp_pitcher_name||"").toLowerCase().includes(q))
      .map(p=>({p, d:hrStormScore(p)}))
      .filter(r=>r.d!=null)
      .filter(r=>!stormsOnly || hrIsStorm(r.d))
      // Rank by the ONE HR Rating; the storm pillars decompose it and break ties.
      // Rank by HR Rating, but DEMOTE bad-matchup spots (HR-suppressing arm) by 15 pts
      // so a green bat against a Pallante-type drops down the board instead of headlining.
      .sort((a,b)=>((hrRatingOf(b.p)-(b.d.badMatchup?15:0))-(hrRatingOf(a.p)-(a.d.badMatchup?15:0))) || (b.d.score-a.d.score))
  }, [picks, team, search, stormsOnly])
  const pf = v => v==null?"—":`${Math.round(v*100)}%`
  const chip = (label, val, color) => (
    <span style={{fontSize:9.5,fontWeight:700,color:T.muted,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"}}>{label} <b style={{color}}>{val}</b></span>
  )
  return (
    <div style={{padding:"0 14px",display:"flex",flexDirection:"column",gap:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:11,color:T.muted}}>{rows.length} hitter{rows.length===1?"":"s"} · ranked by HR Rating · storm pillars below</span>
        <button onClick={()=>setStormsOnly(v=>!v)} style={{background:stormsOnly?T.greenDim:T.card,border:`1px solid ${stormsOnly?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:stormsOnly?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>{stormsOnly?"✓ ":""}True storms only 🎆</button>
      </div>
      {rows.length===0 ? <div style={{color:T.muted,fontSize:13,padding:"8px 2px",lineHeight:1.6}}>{stormsOnly?"No true HR storms right now — needs elite power + an HR-prone arm + a live park/weather. Toggle off for the full HR board.":"No HR data yet — fills in once lineups + Statcast land (~3 PM ET)."}</div>
      : rows.map(({p,d},idx)=>{
        const i=p.inputs||{}
        const storm = hrIsStorm(d)
        const hrFormR = (i.hr_season>0 && i.hr_l15!=null) ? i.hr_l15/i.hr_season : null
        const phr9 = i.pitcher_hr9_vs_hand ?? i.pitcher_hr9
        return (
          <button key={p.player_id||idx} onClick={()=>p.player_id&&onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:"HR",prob:p.prob_over,odds:p.book_over!=null?fmtOdds(p.book_over):undefined,opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team})}
            style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${storm?T.greenMid:T.border2}`,borderRadius:12,padding:"11px 13px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:14.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  <span style={{color:T.muted,fontFamily:T.mono,fontSize:11,marginRight:6}}>{idx+1}</span>{p.full_name}
                  <span style={{fontSize:10,color:T.muted2,fontWeight:600}}> {p.team}{i.bats?` · ${i.bats}HB`:""}</span>
                  {storm && <span style={{marginLeft:6,fontSize:11}}>🎆</span>}
                  {d.badMatchup && <span style={{marginLeft:6,fontSize:9,fontWeight:800,color:T.red,border:`1px solid ${T.red}`,borderRadius:5,padding:"1px 5px",whiteSpace:"nowrap"}}>⚠ TOUGH ARM</span>}
                </div>
                <div style={{fontSize:10.5,color:T.muted,marginTop:2}}>vs {p.opp_pitcher_name||"TBD"}{p.opp_pitcher_hand?` (${p.opp_pitcher_hand}HP)`:""}{p.book_over!=null?` · ${fmtOdds(p.book_over)}`:""}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:20,fontWeight:900,fontFamily:T.mono,color:ratingColor(hrRatingOf(p)),lineHeight:1}}>{hrRatingOf(p)}</div>
                <div style={{fontSize:8,color:T.muted,fontWeight:700,letterSpacing:"0.08em",marginTop:2}}>HR RATING</div>
              </div>
            </div>
            {/* power + form pillar */}
            <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
              {chip("Barrel", pf(i.barrel_pct), i.barrel_pct>=0.12?T.green:i.barrel_pct>=0.08?"#34D399":T.muted2)}
              {chip("Hard-Hit", pf(i.hard_hit_pct), i.hard_hit_pct>=0.45?T.green:i.hard_hit_pct>=0.38?"#34D399":T.muted2)}
              {chip("Pull", pf(i.pull_pct), i.pull_pct>=0.44?T.green:i.pull_pct>=0.38?"#34D399":T.muted2)}
              {i.iso!=null && chip("ISO", i.iso.toFixed(3).replace(/^0/,""), i.iso>=0.22?T.green:i.iso>=0.16?"#34D399":T.muted2)}
              {chip("HR form", hrFormR!=null?`${hrFormR>1?"▲":hrFormR<1?"▼":""}${Math.round(hrFormR*100)}%`:"—", hrFormR>1.1?T.green:hrFormR<0.9?T.red:T.muted)}
              {i.zone_fit!=null && chip("Zone", i.zone_fit, scoreColor(i.zone_fit))}
              {i.form_l7!=null && chip("Form L7", i.form_l7.toFixed(2), T.muted2)}
            </div>
            {/* arm + environment pillar */}
            <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
              {phr9!=null && chip(`P HR/9${(i.pitcher_hr9_vs_hand!=null&&p.opp_pitcher_hand)?`·${(i.bats||"").toUpperCase()==="S"?(p.opp_pitcher_hand==="R"?"L":"R"):(i.bats||"R").toUpperCase()[0]}HB`:""}`, phr9.toFixed(2), phr9>=1.4?T.green:phr9>=1.1?"#34D399":T.red)}
              {i.pitcher_barrel_allowed!=null && chip("Brl allowed", pf(i.pitcher_barrel_allowed), i.pitcher_barrel_allowed>=0.09?T.green:i.pitcher_barrel_allowed>=0.06?"#34D399":T.muted2)}
              {i.pitcher_xwoba_against!=null && chip("xwOBA-a", i.pitcher_xwoba_against.toFixed(3).replace(/^0/,""), i.pitcher_xwoba_against>=0.34?T.green:i.pitcher_xwoba_against>=0.31?"#34D399":T.muted2)}
              {i.park_factor!=null && chip("Park", `${i.park_factor.toFixed(2)}x`, i.park_factor>=1.10?T.green:i.park_factor>=1.03?"#34D399":i.park_factor<=0.97?T.red:T.muted)}
              {i.is_dome ? chip("Roof", "dome", T.muted)
                : i.weather_hr_impact!=null ? chip("Wx", `${i.weather_hr_impact>=0?"+":""}${Math.round(i.weather_hr_impact*100)}%${i.weather_wind?` ${String(i.weather_wind).replace(/_/g," ")}`:""}`, i.weather_hr_impact>=0.02?T.green:i.weather_hr_impact<=-0.02?T.red:T.muted) : null}
              {!i.is_dome && i.weather_temp_f!=null && chip("Temp", `${i.weather_temp_f}°`, i.weather_temp_f>=80?T.green:i.weather_temp_f<=55?T.red:T.muted)}
            </div>
          </button>
        )
      })}
      <div style={{fontSize:10,color:T.muted2,lineHeight:1.5,marginTop:4}}>
        <b style={{color:T.text}}>HR Storm</b> breaks the <b style={{color:T.text}}>HR Rating</b> into its home-run pillars: batter power (barrel/hard-hit/pull/fly-ball/ISO/xwOBAcon), the arm's HR-vulnerability (HR/9 incl. vs your hand, barrels &amp; xwOBA allowed), and environment (park + weather). <b style={{color:T.text}}>🎆 = true storm</b> — every pillar clears its bar. HR is high-variance; research only, not advice. 21+.
      </div>
    </div>
  )
}
// ── Briefing: plain-English breakdown of the slate's best plays, with read-aloud ──
// Generates a write-up from the live picks — by MARKET (top HR/HRR/TB plays) or by
// TEAM (each team's best play) — and reads it via the browser's built-in speech.
const BRIEF_MKTS = [["HRR","HRR_over_1.5"],["HR","HR_anytime"],["TB","total_bases"]]
const _briefWhy = (p) => {
  const i = p.inputs||{}, r=[]
  if(p.edge_pct!=null && p.edge_pct>0)                       r.push(`a ${(p.edge_pct*100).toFixed(1)}% edge over the book`)
  if(i.park_factor>=1.08)                                    r.push(`a hitter's park (${i.park_factor.toFixed(2)}x)`)
  if(i.pitcher_ops_vs_hand!=null && i.pitcher_ops_vs_hand>=0.78) r.push(`a soft arm (${i.pitcher_ops_vs_hand.toFixed(3)} OPS allowed to his hand)`)
  if(i.pitcher_hr9!=null && i.pitcher_hr9>=1.3)             r.push(`an HR-prone pitcher (${i.pitcher_hr9.toFixed(2)} HR/9)`)
  if(i.form_season>0 && i.form_l7!=null && i.form_l7/i.form_season>1.15) r.push(`hot recent form`)
  if(i.zone_fit!=null && i.zone_fit>=60)                    r.push(`a strong zone fit (${Math.round(i.zone_fit)})`)
  if(i.lineup_slot!=null && i.lineup_slot<=3)               r.push(`a top-of-order slot`)
  return r.slice(0,3)
}
const _briefQuality = p => (p.prob_over||0) + Math.max(0, p.edge_pct||0)   // within a market
const _briefValue   = p => (p.ev!=null ? p.ev : (p.edge_pct||0))          // cross-market (comparable)
function BriefingSheet({onClose, onPlayer}){
  const [mode,setMode] = useState("market")   // "market" | "team"
  const [speaking,setSpeaking] = useState(false)
  const { data: hrr } = useFetch(()=>getToday("HRR_over_1.5", todayStr(), 500).catch(()=>null), [])
  const { data: hr }  = useFetch(()=>getToday("HR_anytime",   todayStr(), 500).catch(()=>null), [])
  const { data: tb }  = useFetch(()=>getToday("total_bases",  todayStr(), 500).catch(()=>null), [])
  const byMkt = { "HRR_over_1.5": hrr?.picks||[], "HR_anytime": hr?.picks||[], "total_bases": tb?.picks||[] }
  const loading = !hrr || !hr || !tb

  // By MARKET — top plays per market
  const marketBlocks = BRIEF_MKTS.map(([lbl,pt])=>({
    lbl, plays: [...(byMkt[pt]||[])].sort((a,b)=>_briefQuality(b)-_briefQuality(a)).slice(0,5)
  }))
  // By TEAM — each team's single best play across markets (by comparable value)
  const teamBest = (()=>{
    const best = {}
    for(const [lbl,pt] of BRIEF_MKTS) for(const p of (byMkt[pt]||[])){
      if(!p.team) continue
      const cur = best[p.team]
      if(!cur || _briefValue(p) > _briefValue(cur.p)) best[p.team] = {p, mkt:lbl}
    }
    return Object.values(best).sort((a,b)=>_briefValue(b.p)-_briefValue(a.p))
  })()

  const line = (p, mkt) => {
    const why = _briefWhy(p)
    const prob = p.prob_over!=null ? `${Math.round(p.prob_over*100)}%` : "—"
    return `${p.full_name}, ${mkt} ${prob}${p.opp_pitcher_name?` vs ${p.opp_pitcher_name}`:""}${why.length?` — ${why.join(", ")}`:""}.`
  }
  const speech = (()=>{
    if(loading) return ""
    if(mode==="market"){
      let t = "Here's today's briefing by market. "
      for(const b of marketBlocks){ if(!b.plays.length) continue
        t += `Top ${b.lbl} plays. ` + b.plays.map(p=>line(p,b.lbl)).join(" ") + " " }
      return t
    }
    let t = "Here's today's briefing by team, best play for each. "
    return t + teamBest.map(({p,mkt})=>`${p.team}: ${line(p,mkt)}`).join(" ")
  })()

  // Voices: load the device's installed English voices (async on first call) and
  // let the user pick. Auto-pick a higher-quality one over the default robotic voice.
  const [voices,setVoices] = useState([])
  const [voiceURI,setVoiceURI] = useState(()=>{ try{ return localStorage.getItem("brkr_voice")||"" }catch(_){ return "" } })
  useEffect(()=>{
    const load = ()=>{ try{ setVoices((window.speechSynthesis?.getVoices()||[]).filter(v=>/^en/i.test(v.lang))) }catch(_){} }
    load(); try{ window.speechSynthesis.addEventListener("voiceschanged", load) }catch(_){}
    return ()=>{ try{ window.speechSynthesis.removeEventListener("voiceschanged", load) }catch(_){} }
  }, [])
  const bestVoice = ()=>{
    if(!voices.length) return null
    const byUri = voices.find(v=>v.voiceURI===voiceURI)
    if(voiceURI && byUri) return byUri
    // Prefer named "enhanced/premium/natural" voices, then a US English voice.
    return voices.find(v=>/enhanced|premium|natural|neural|siri|ava|samantha|allison|zoe|evan|nicky|aaron|jenny/i.test(v.name))
        || voices.find(v=>/en[-_]us/i.test(v.lang)) || voices[0]
  }
  const speak = ()=>{ try{
    const synth = window.speechSynthesis; if(!synth||!speech) return
    synth.cancel(); const u = new SpeechSynthesisUtterance(speech)
    const v = bestVoice(); if(v) u.voice = v
    u.rate = 1.02; u.pitch = 1.0; u.onend = ()=>setSpeaking(false); u.onerror = ()=>setSpeaking(false)
    synth.speak(u); setSpeaking(true)
  }catch(_){ setSpeaking(false) } }
  const stop = ()=>{ try{ window.speechSynthesis.cancel() }catch(_){}; setSpeaking(false) }
  useEffect(()=>()=>{ try{ window.speechSynthesis.cancel() }catch(_){} }, [])

  const tab = (id,label)=>(
    <button onClick={()=>{stop(); setMode(id)}} style={{flex:1,background:mode===id?T.greenDim:T.card,border:`1px solid ${mode===id?T.greenMid:T.border2}`,borderRadius:8,padding:"7px 0",color:mode===id?T.green:T.muted2,fontSize:12,fontWeight:800,cursor:"pointer"}}>{label}</button>
  )
  const playRow = (p,mkt) => (
    <button key={(p.player_id||p.full_name)+mkt} onClick={()=>p.player_id&&onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:mkt,prob:p.prob_over,conf:p.confidence,edge:p.edge_pct!=null?`${p.edge_pct>=0?"+":""}${(p.edge_pct*100).toFixed(1)}%`:undefined,odds:p.book_over!=null?fmtOdds(p.book_over):undefined,opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team})}
      style={{width:"100%",textAlign:"left",cursor:p.player_id?"pointer":"default",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px",marginBottom:6,color:T.text}}>
      <div style={{fontSize:13,fontWeight:800}}>{p.full_name} <span style={{fontSize:10.5,color:T.muted2,fontWeight:600}}>{p.team}{mode==="team"?"":` · ${mkt}`}</span>
        <span style={{float:"right",fontFamily:T.mono,fontSize:12.5,fontWeight:800,color:probColor(p.prob_over)}}>{p.prob_over!=null?`${Math.round(p.prob_over*100)}%`:"—"}</span></div>
      <div style={{fontSize:10.5,color:T.muted,marginTop:3,lineHeight:1.4}}>{p.opp_pitcher_name?`vs ${p.opp_pitcher_name} · `:""}{_briefWhy(p).join(" · ")||"—"}</div>
    </button>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>📋 Briefing</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>{tab("market","By Market")}{tab("team","By Team")}</div>
        <button onClick={speaking?stop:speak} disabled={loading} style={{width:"100%",background:speaking?T.redDim:T.greenDim,border:`1px solid ${speaking?T.red:T.greenMid}`,borderRadius:10,padding:"10px 0",color:speaking?T.red:T.green,fontSize:13,fontWeight:800,cursor:loading?"default":"pointer",marginBottom:8,opacity:loading?0.5:1}}>{speaking?"⏹ Stop":"🔊 Listen"}</button>
        {voices.length>1 && (
          <select value={voiceURI} onChange={e=>{ setVoiceURI(e.target.value); try{ localStorage.setItem("brkr_voice", e.target.value) }catch(_){}; stop() }}
            style={{width:"100%",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"8px 10px",color:T.text,fontSize:12,marginBottom:12}}>
            <option value="">🎙 Auto (best available)</option>
            {voices.map(v=><option key={v.voiceURI} value={v.voiceURI}>{v.name}{/enhanced|premium|natural/i.test(v.name)?" ✨":""}</option>)}
          </select>
        )}
        {loading ? <div style={{color:T.muted,fontSize:12.5,padding:"8px 0"}}>Building today's briefing…</div>
        : mode==="market" ? marketBlocks.map(b=>(
            <div key={b.lbl} style={{marginBottom:14}}>
              <div style={{fontSize:12.5,fontWeight:800,color:T.green,margin:"2px 0 6px",letterSpacing:"0.02em"}}>Top {b.lbl} plays</div>
              {b.plays.length ? b.plays.map(p=>playRow(p,b.lbl)) : <div style={{color:T.muted,fontSize:12}}>No {b.lbl} plays yet.</div>}
            </div>))
        : <div>{teamBest.length ? teamBest.map(({p,mkt})=>(
            <div key={p.team} style={{marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:800,color:T.muted2,margin:"0 0 4px",letterSpacing:"0.04em"}}>{p.team} · best: {mkt}</div>
              {playRow(p,mkt)}
            </div>)) : <div style={{color:T.muted,fontSize:12}}>No plays yet.</div>}</div>}
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:4}}>Generated from today's model board. "Listen" uses your device's voices — pick one above (✨ = higher quality). Won't play on silent mode. <b style={{color:T.muted2}}>iPhone:</b> Settings → Accessibility → Spoken Content → Voices → English → download an <b style={{color:T.muted2}}>Enhanced/Premium</b> voice (e.g. Ava, Samantha) for a much more natural read.</div>
      </div>
    </Sheet>
  )
}
// ── Compare Players — up to 6 hitters as a mini-grid, choosable metric columns ─
// Reuses the grid's own column defs (GE_COLS) so metrics + colors match the board.
// Best value in each metric column is highlighted green across the picked players.
const CMP_MAX = 6
function CompareDrawer({onClose, onPlayer, initialMkt}){
  const [mkt,setMkt] = useState(initialMkt||"HRR_over_1.5")
  const { data, loading } = useFetch(()=>getToday(mkt, todayStr(), 500), [mkt])
  const picks = Array.isArray(data?.picks) ? data.picks : []
  const [sel,setSel] = useState([])          // ordered player ids/names (max CMP_MAX)
  const [q,setQ] = useState("")
  const [colOpen,setColOpen] = useState(false)
  const [metricKeys,setMetricKeys] = useState(null)   // null = market default set
  const byId = new Map(picks.map(p=>[p.player_id ?? p.full_name, p]))
  const players = sel.map(id=>byId.get(id)).filter(Boolean)
  const mktLabel = (MARKETS.find(m=>m[1]===mkt)||["HRR"])[0]
  const rating = p => (p.confidence ?? confidenceFor(p))
  const lastName = n => (n||"").split(" ").slice(-1)[0]

  const universe = geColUniverse(mkt)
  const defKeys = GE_MARKET_COLS[mkt] || GE_COLS.map(c=>c[0])
  const keys = (Array.isArray(metricKeys)&&metricKeys.length) ? metricKeys : defKeys
  const cols = geProbAwareCols(keys.map(k=>GE_COL_BY_KEY[k]).filter(Boolean), mktLabel)
  const isCustom = Array.isArray(metricKeys)&&metricKeys.length>0

  const add = id => { if(!sel.includes(id) && sel.length<CMP_MAX) setSel([...sel,id]); setQ("") }
  const remove = id => setSel(sel.filter(x=>x!==id))
  const toggleMetric = k => {
    const cur = keys
    if(cur.includes(k)){ if(cur.length<=1) return; setMetricKeys(cur.filter(x=>x!==k)) }
    else setMetricKeys(universe.filter(u=>u===k||cur.includes(u)))
  }
  // Winner id per metric column (best value, honoring low-is-better cols); null if only one player.
  const bestId = col => {
    if(players.length<2) return null
    const asc = col[5]===true; let best=null, bv=null
    for(const p of players){ const v=col[2](p); if(v==null) continue
      if(bv==null || (asc? v<bv : v>bv)){ bv=v; best=(p.player_id??p.full_name) } }
    return best
  }
  const seg = on => ({flex:1,padding:"7px",borderRadius:8,fontSize:12,fontWeight:800,cursor:"pointer",border:"none",background:on?T.green:"transparent",color:on?"#04140D":T.muted2,whiteSpace:"nowrap"})
  const results = picks
    .filter(p=>!sel.includes(p.player_id??p.full_name))
    .filter(p=>{ const ql=q.trim().toLowerCase(); return !ql || (p.full_name||"").toLowerCase().includes(ql) || (p.team||"").toLowerCase().includes(ql) })
    .sort((a,b)=>rating(b)-rating(a)).slice(0,8)
  const topRated = players.length>1 ? [...players].sort((a,b)=>rating(b)-rating(a))[0] : null
  const th = {padding:"7px 9px",textAlign:"right",whiteSpace:"nowrap",fontSize:9,fontWeight:800,letterSpacing:"0.03em",textTransform:"uppercase",color:T.muted,borderBottom:`1px solid ${T.border}`,background:T.card2,position:"sticky",top:0,zIndex:1}

  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>⚖ Compare Players</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,marginBottom:10}}>Pick up to <b style={{color:T.text}}>{CMP_MAX}</b> hitters and compare them across the <b style={{color:T.green}}>{mktLabel}</b> drivers. <b style={{color:T.green}}>Green</b> = best in that column. Tap <b style={{color:T.text}}>⚙ Columns</b> to choose metrics.</div>

        {/* Market pills */}
        <div style={{display:"flex",gap:4,background:T.card,border:`1px solid ${T.border2}`,borderRadius:11,padding:4,marginBottom:10,overflowX:"auto"}}>
          {MARKETS.filter(m=>m[0]!=="K").map(([label,db])=>(<button key={db} onClick={()=>setMkt(db)} style={seg(mkt===db)}>{label}</button>))}
        </div>

        {/* Selected chips */}
        {players.length>0 && (
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
            {players.map(p=>(
              <span key={p.player_id??p.full_name} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12,fontWeight:700,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:14,padding:"4px 6px 4px 11px"}}>
                {lastName(p.full_name)} <span style={{color:T.muted2,fontFamily:T.mono,fontSize:10.5}}>{Math.round(rating(p))}</span>
                <button onClick={()=>remove(p.player_id??p.full_name)} style={{background:"none",border:"none",color:T.green,cursor:"pointer",fontSize:14,lineHeight:1,padding:"0 2px"}}>×</button>
              </span>
            ))}
          </div>
        )}

        {/* Add player search */}
        {sel.length<CMP_MAX && (
          <div style={{marginBottom:10}}>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder={players.length?"Add another hitter…":"Search a hitter to compare…"} style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"9px 11px",color:T.text,fontSize:13,outline:"none"}}/>
            {q.trim() && (
              <div style={{marginTop:6,maxHeight:200,overflowY:"auto",border:`1px solid ${T.border2}`,borderRadius:8}}>
                {results.length===0 ? <div style={{padding:"8px 10px",color:T.muted,fontSize:12}}>No match on tonight's board.</div>
                : results.map(p=>(
                  <button key={p.player_id??p.full_name} onClick={()=>add(p.player_id??p.full_name)} style={{width:"100%",textAlign:"left",background:"none",border:"none",borderTop:`1px solid ${T.border}`,padding:"8px 10px",color:T.text,fontSize:12.5,cursor:"pointer"}}>
                    <b>{p.full_name}</b> <span style={{color:T.muted2,fontSize:11}}>{p.team} · Rating {Math.round(rating(p))}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Columns chooser toggle */}
        {players.length>0 && (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <button onClick={()=>setColOpen(o=>!o)} style={{background:(colOpen||isCustom)?T.greenDim:T.card,border:`1px solid ${(colOpen||isCustom)?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 11px",color:(colOpen||isCustom)?T.green:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer"}}>⚙ Columns · {cols.length}</button>
            {topRated && <span style={{fontSize:11.5,fontWeight:800,color:T.green}}>👑 {lastName(topRated.full_name)} rates highest · {Math.round(rating(topRated))}</span>}
          </div>
        )}
        {colOpen && players.length>0 && (
          <div style={{margin:"0 0 10px",padding:"10px 12px",background:T.card2,border:`1px solid ${T.border2}`,borderRadius:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontSize:9.5,color:T.muted,fontWeight:800,letterSpacing:"0.08em"}}>METRIC COLUMNS</span>
              {isCustom && <button onClick={()=>setMetricKeys(null)} style={{background:"none",border:"none",color:T.muted2,fontSize:11,fontWeight:700,cursor:"pointer",textDecoration:"underline"}}>reset</button>}
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"6px 8px"}}>
              {universe.map(k=>{ const c=GE_COL_BY_KEY[k]; if(!c) return null; const on=keys.includes(k); const only=on&&keys.length<=1; return (
                <button key={k} onClick={()=>toggleMetric(k)} disabled={only} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,fontWeight:700,cursor:only?"default":"pointer",color:on?T.green:T.muted2,background:on?T.greenDim:T.card,border:`1px solid ${on?T.greenMid:T.border2}`,borderRadius:14,padding:"4px 10px",opacity:only?0.6:1}}>
                  <span style={{fontSize:10}}>{on?"✓":"+"}</span>{c[1]}
                </button>
              )})}
            </div>
          </div>
        )}

        {/* Compare grid: metrics = rows (down the left, always visible), players =
            columns. Reads like the classic side-by-side for 2; scales to 6. */}
        {players.length===0 ? (
          <div style={{color:T.muted,fontSize:13,padding:"20px 4px",textAlign:"center"}}>{loading?"Loading tonight's board…":"Search hitters above to build a comparison."}</div>
        ) : (
          <div style={{overflowX:"auto",border:`1px solid ${T.border}`,borderRadius:12,maxHeight:"60vh",overflowY:"auto"}}>
            <table style={{borderCollapse:"collapse",width:"100%"}}>
              <thead><tr>
                <th style={{...th,textAlign:"left",left:0,zIndex:2}}>Metric</th>
                {players.map(p=>{ const pid=p.player_id??p.full_name; return (
                  <th key={pid} style={{...th,textAlign:"right",minWidth:76}}>
                    <button onClick={()=>p.player_id&&onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:mktLabel,conf:p.confidence,prob:p.prob_over,opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team,slot:p?.inputs?.lineup_slot,park:p?.inputs?.park_factor,team:p.team,oppPitcher:p.opp_pitcher_name})} style={{background:"none",border:"none",padding:0,cursor:"pointer",color:T.text,textAlign:"right"}}>
                      <div style={{fontSize:12,fontWeight:800,textTransform:"none",letterSpacing:0}}>{lastName(p.full_name)}</div>
                      <div style={{fontSize:9,color:T.muted2,fontWeight:600,textTransform:"none",letterSpacing:0}}>{p.team} · {Math.round(rating(p))}</div>
                    </button>
                  </th>
                )})}
              </tr></thead>
              <tbody>
                {cols.map(c=>{ const winner=bestId(c); return (
                  <tr key={c[0]}>
                    <td style={{padding:"8px 10px",textAlign:"left",whiteSpace:"nowrap",fontSize:11,fontWeight:700,color:T.muted,borderTop:`1px solid ${T.border}`,position:"sticky",left:0,zIndex:1,background:T.card2}}>{c[1]}</td>
                    {players.map(p=>{ const pid=p.player_id??p.full_name; const v=c[2](p); const win=winner===pid; return (
                      <td key={pid} style={{padding:"8px 10px",textAlign:"right",whiteSpace:"nowrap",fontFamily:T.mono,fontSize:13,fontWeight:win?800:600,color:c[4](v),borderTop:`1px solid ${T.border}`,background:win?T.greenDim:"transparent"}}>{c[3](v)}</td>
                    )})}
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        )}
        <div style={{fontSize:10,color:T.muted2,lineHeight:1.5,marginTop:10}}>Every number is the model's, from tonight's board for this market. <b style={{color:T.text}}>Green</b> = best in that row (for a low-is-better metric like batting order or pitcher K/9, the lowest wins). Scroll sideways if you add lots of hitters · tap a name for the full card. Research only — not betting advice. 21+.</div>
      </div>
    </Sheet>
  )
}

// ── Swipe-to-build: a card stack of the top plays; swipe right = add to slip,
// left = skip. Touch-native way to fill a slip fast on a phone. ────────────────
function SwipeDeck({picks, mktLabel, toggleLeg, inParlay, onPlayer, onClose}){
  const deck = useMemo(()=>[...picks].filter(p=>p.prob_over!=null)
    .sort((a,b)=>((b.confidence??confidenceFor(b))-(a.confidence??confidenceFor(a)))).slice(0,25), [picks])
  const [i,setI] = useState(0)
  const [dx,setDx] = useState(0)
  const [added,setAdded] = useState(0)
  const startX = useRef(null)
  const p = deck[i]
  const advance = addIt => {
    if(addIt && p){ const lk=`${p.player_id||p.full_name}-${mktLabel}`
      if(!(inParlay&&inParlay(lk))){ toggleLeg({key:lk,name:p.full_name,player_id:p.player_id,market:mktLabel,prob:p.prob_over,oddsNum:p.book_over,team:p.team,opp:p.opp_pitcher_name}); setAdded(a=>a+1) } }
    setDx(0); setI(n=>n+1)
  }
  const onDown = e => { startX.current = e.clientX; try{ e.currentTarget.setPointerCapture(e.pointerId) }catch(_){} }
  const onMove = e => { if(startX.current!=null) setDx(e.clientX - startX.current) }
  const onUp   = () => { if(startX.current==null) return; const d=dx; startX.current=null; if(d>110) advance(true); else if(d<-110) advance(false); else setDx(0) }
  const rating = p ? (p.confidence ?? confidenceFor(p)) : null
  const like = dx>40, nope = dx<-40
  return (
    <div style={{position:"fixed",inset:0,zIndex:200,background:"rgba(4,10,8,0.72)",backdropFilter:"blur(3px)",display:"flex",flexDirection:"column"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",maxWidth:460,margin:"0 auto",width:"100%",boxSizing:"border-box"}}>
        <span style={{fontSize:15,fontWeight:900}}>⚡ Quick Build · <span style={{color:T.green}}>{mktLabel}</span></span>
        <button onClick={onClose} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,color:T.muted,fontSize:14,cursor:"pointer",padding:"5px 10px"}}>Done{added>0?` · ${added} added`:""}</button>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"0 20px 40px",maxWidth:460,margin:"0 auto",width:"100%",boxSizing:"border-box"}}>
        {!p ? (
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:10}}>🎟</div>
            <div style={{fontSize:17,fontWeight:800,marginBottom:6}}>Deck cleared</div>
            <div style={{fontSize:13,color:T.muted,marginBottom:18}}>{added} play{added===1?"":"s"} added to your slip.</div>
            <button onClick={onClose} style={{padding:"10px 22px",borderRadius:10,background:T.greenDim,border:`1px solid ${T.green}`,color:T.green,fontSize:14,fontWeight:800,cursor:"pointer"}}>See my slip</button>
          </div>
        ) : (<>
          <div style={{fontSize:11,color:T.muted2,marginBottom:10}}>{i+1} / {deck.length} · sorted by Rating</div>
          <div onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
            style={{width:"100%",maxWidth:360,background:T.card,border:`1px solid ${like?T.green:nope?T.red:T.border2}`,borderRadius:18,padding:"20px 18px",cursor:"grab",touchAction:"none",userSelect:"none",
              transform:`translateX(${dx}px) rotate(${dx/22}deg)`,transition:startX.current==null?"transform 0.18s ease":"none",boxShadow:"0 14px 40px rgba(0,0,0,0.4)",position:"relative"}}>
            {(like||nope) && <div style={{position:"absolute",top:16,[like?"left":"right"]:16,transform:`rotate(${like?-12:12}deg)`,border:`3px solid ${like?T.green:T.red}`,color:like?T.green:T.red,borderRadius:8,padding:"3px 12px",fontSize:16,fontWeight:900,letterSpacing:"0.05em",opacity:Math.min(1,Math.abs(dx)/110)}}>{like?"＋ ADD":"SKIP"}</div>}
            <div style={{fontSize:22,fontWeight:900,letterSpacing:"-0.02em",marginBottom:2}}>{p.full_name}</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:16}}>{p.team}{p.opp_pitcher_name?` · vs ${p.opp_pitcher_name}${p.opp_pitcher_hand?` (${p.opp_pitcher_hand})`:""}`:""}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
              <div style={{background:T.card2,borderRadius:10,padding:"10px 12px"}}><div style={{fontSize:9,color:T.muted2,fontWeight:800,letterSpacing:"0.06em"}}>RATING</div><div style={{fontSize:24,fontWeight:900,color:ratingColor(rating),fontFamily:T.mono}}>{rating!=null?Math.round(rating):"—"}</div></div>
              <div style={{background:T.card2,borderRadius:10,padding:"10px 12px"}}><div style={{fontSize:9,color:T.muted2,fontWeight:800,letterSpacing:"0.06em"}}>PROB OVER</div><div style={{fontSize:24,fontWeight:900,color:probColorMkt(p.prob_over,mktLabel),fontFamily:T.mono}}>{pct(p.prob_over||0)}</div></div>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,fontSize:10.5,color:T.muted}}>
              {p?.inputs?.park_factor!=null && <span style={{background:T.card2,borderRadius:6,padding:"3px 8px"}}>Park {p.inputs.park_factor.toFixed(2)}x</span>}
              {p?.inputs?.pitcher_hr9!=null && <span style={{background:T.card2,borderRadius:6,padding:"3px 8px"}}>P HR/9 {p.inputs.pitcher_hr9.toFixed(2)}</span>}
              {p?.inputs?.lineup_slot!=null && <span style={{background:T.card2,borderRadius:6,padding:"3px 8px"}}>Bats #{p.inputs.lineup_slot}</span>}
              {p.book_over!=null && <span style={{background:T.card2,borderRadius:6,padding:"3px 8px"}}>Book {fmtOdds(p.book_over)}</span>}
            </div>
            {p.player_id && onPlayer && <button onClick={()=>onPlayer({name:p.full_name,player_id:p.player_id,market:mktLabel,conf:p.confidence,prob:p.prob_over})} style={{marginTop:14,background:"none",border:"none",color:T.muted2,fontSize:11,textDecoration:"underline",cursor:"pointer",padding:0}}>full card →</button>}
          </div>
          <div style={{display:"flex",gap:16,marginTop:22}}>
            <button onClick={()=>advance(false)} style={{width:58,height:58,borderRadius:"50%",background:T.card,border:`1.5px solid ${T.red}`,color:T.red,fontSize:22,cursor:"pointer"}}>✕</button>
            <button onClick={()=>advance(true)} style={{width:58,height:58,borderRadius:"50%",background:T.greenDim,border:`1.5px solid ${T.green}`,color:T.green,fontSize:22,fontWeight:800,cursor:"pointer"}}>＋</button>
          </div>
          <div style={{fontSize:10,color:T.muted2,marginTop:12}}>Swipe or tap · → add · ← skip</div>
        </>)}
      </div>
    </div>
  )
}

function AnalyticsTab({onPlayer, onGlossary, toggleLeg, inParlay}){
  const [mkt,setMkt]       = useState("HRR_over_1.5")
  const [view,setView]     = useState("grid")        // "grid" = Game Explorer (default) · "list" = cards
  const [gridSort,setGridSort] = useState("prob_over") // Game Explorer default sort — highest probability
  const [sortKey,setSortKey] = useState("prob_over")   // default filter — highest probability (most likely to hit)
  const [confirmed,setConfirmed] = useState(false)
  const [stormPark,setStormPark] = useState(false)   // hitter-parks-only toggle for storm mode
  const [team,setTeam]     = useState("")
  const [search,setSearch] = useState("")
  const [brief,setBrief]   = useState(false)
  const [compare,setCompare] = useState(false)
  const [swipe,setSwipe]   = useState(false)
  const [boardOpen,setBoardOpen] = useState(false)
  const [screenerOpen,setScreenerOpen] = useState(false)   // 🔎 Player Screener drawer
  const [smartOpen,setSmartOpen] = useState(false)         // ✨ Smart Search (Pro/$30)
  const [valueOpen,setValueOpen] = useState(false)         // 💰 Value Board — beat-the-book edge (Pro)
  const board = useBoard()
  const { isPro, isPremium, needPro, needPremium } = useProGate()  // Compare: Founder + Pro · Screener: Pro
  const gridMode = view==="grid"
  const stormMode = sortKey===STORM_KEY && !gridMode
  const hrStormMode = sortKey===HR_STORM_KEY && !gridMode
  const { data, loading } = useFetch(()=>getToday(mkt, todayStr(), 500), [mkt])
  // Storm is about hitting, not value — never edge-filtered (require_edge stays false).
  const { data: stormData, loading: stormLoading } = useFetch(()=>getPerfectStorm(todayStr(), false, 200, stormPark).catch(()=>null), [stormPark])
  // HR storm reads the HR-market /today feed (its inputs carry every HR driver)
  const { data: hrData, loading: hrLoading } = useFetch(()=>getToday("HR_anytime", todayStr(), 500).catch(()=>null), [])
  const hrPicks = Array.isArray(hrData?.picks) ? hrData.picks : []
  const stormPlayers = useMemo(()=>{
    let ps = stormData?.players || []
    const q = search.trim().toLowerCase()
    if(team) ps = ps.filter(p=>p.team===team)
    if(q) ps = ps.filter(p=>(p.full_name||"").toLowerCase().includes(q) || (p.opp_pitcher||"").toLowerCase().includes(q))
    return ps
  }, [stormData, team, search])
  const picks = Array.isArray(data?.picks) ? data.picks : []
  // Game start times (venue -> commence_time ISO) for the grid's "🌙 Late games" preset.
  const { data: upcoming } = useFetch(()=>getUpcomingMatches(todayStr()).catch(()=>null), [])
  const venueTimes = useMemo(()=>{ const m={}; for(const g of (upcoming?.games||[])){ if(g?.venue && g?.commence_time) m[g.venue]=g.commence_time } return m }, [upcoming])
  const uiLabel = (MARKETS.find(m=>m[1]===mkt)||["HRR"])[0]
  const sortMeta = ANALYTICS_SORTS.find(s=>s[0]===sortKey) || ANALYTICS_SORTS[0]
  const teams = [...new Set(picks.map(p=>p.team).filter(Boolean))].sort()
  const topRef = useRef(null)
  // Recompute only when an input actually changes — and snap to the top so a
  // re-sort/filter is always visibly reflected (the main cause of "feels stuck").
  const rows = useMemo(() => {
    const getter = sortMeta[2]
    const q = search.trim().toLowerCase()
    return picks
      .filter(p=>!confirmed || (p?.inputs?.lineup_confirmed && p?.opp_pitcher_name))
      .filter(p=>!team || p.team===team)
      .filter(p=>!q || (p.full_name||"").toLowerCase().includes(q) || (p.opp_pitcher_name||"").toLowerCase().includes(q))
      .map(p=>({p, v:getter(p)}))
      .filter(r=>r.v!=null)
      .sort((a,b)=>b.v-a.v)
  }, [picks, sortKey, confirmed, team, search])
  useEffect(()=>{ try{ topRef.current?.scrollIntoView({block:"start"}) }catch(_){} }, [mkt, sortKey, confirmed, team])
  const chip = on => ({padding:"7px 11px",borderRadius:18,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",border:`1px solid ${on?T.greenMid:T.border2}`,background:on?T.greenDim:T.card,color:on?T.green:T.muted2})
  return (
    <div ref={topRef} style={{padding:"0 0 12px",scrollMarginTop:60}}>
      <div style={{padding:"12px 14px 6px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <div style={{fontWeight:900,fontSize:20,letterSpacing:"-0.03em"}}>Analytics</div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
            <button onClick={()=>{ if(needPremium()) setCompare(true) }} title="Compare two hitters side by side" style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 9px",color:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer"}}>⚖ Compare{!isPremium && <span style={{fontSize:8,fontWeight:800,color:T.amber,marginLeft:3}}>PRO</span>}</button>
            <button onClick={()=>setBrief(true)} title="Plain-English breakdown + read-aloud" style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 9px",color:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer"}}>📋 Briefing</button>
            {toggleLeg && <button onClick={()=>setSwipe(true)} title="Swipe through the top plays to build a slip fast" style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 9px",color:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer"}}>⚡ Swipe</button>}
            <button onClick={()=>setBoardOpen(true)} title="Your pinned plays" style={{background:board.board.length?T.greenDim:T.card,border:`1px solid ${board.board.length?T.greenMid:T.border2}`,borderRadius:8,padding:"5px 9px",color:board.board.length?T.green:T.muted2,fontSize:11,fontWeight:800,cursor:"pointer"}}>📌 Board{board.board.length?` · ${board.board.length}`:""}</button>
            <div style={{display:"flex",gap:2,background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:2}}>
              <button onClick={()=>setView("list")} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:800,cursor:"pointer",border:"none",background:view==="list"?T.greenDim:"transparent",color:view==="list"?T.green:T.muted2}}>☰ List</button>
              <button onClick={()=>setView("grid")} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:800,cursor:"pointer",border:"none",background:gridMode?T.greenDim:"transparent",color:gridMode?T.green:T.muted2}}>▦ Grid</button>
            </div>
            <HelpChip onGlossary={onGlossary} section="scores"/>
          </div>
        </div>
        <div style={{fontSize:11,color:T.muted,marginTop:2,marginBottom:10}}>{gridMode
          ? <>▦ <b style={{color:T.green}}>Game Explorer</b> · filter by game &amp; team, every metric in one grid</>
          : hrStormMode
          ? <>🎆 <b style={{color:T.green}}>HR Storm</b> · <b style={{color:T.text}}>home runs</b> · power × HR-prone arm × park/weather{hrLoading?" · Loading…":""}</>
          : stormMode
          ? <>🌪 <b style={{color:T.green}}>HRR Storm</b> · <b style={{color:T.text}}>hits+runs+RBI floor</b> · soft arm × zone fit × hot form · {stormLoading?"Loading…":`${stormPlayers.length} spot${stormPlayers.length===1?"":"s"}`}</>
          : <>Sorted by <b style={{color:T.green}}>{sortMeta[1]}</b> · {loading?"Loading…":`${rows.length} hitters`}</>}</div>

        {/* 🔎 Player Screener — the metric-filter tool, surfaced right here as a drawer so
            it's easy to find (it also lives as its own Pro nav tab). Pro-gated: needPro()
            opens the upgrade sheet for free users, expands the drawer for Pro/Founder. */}
        <button onClick={()=>{ if(screenerOpen){ setScreenerOpen(false); return } if(needPro()) setScreenerOpen(true) }}
          style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:screenerOpen?T.greenDim:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer",marginBottom:10}}>
          <span style={{fontSize:16}}>🔎</span>
          <span style={{flex:1,textAlign:"left"}}>Player Screener <span style={{color:T.muted2,fontWeight:600}}>· filter bats by barrel %, hard-hit, xwOBA &amp; more</span></span>
          {!isPro && <span style={{fontSize:8,fontWeight:800,color:T.amber}}>PRO</span>}
          <span style={{fontSize:13,color:T.muted2,transform:screenerOpen?"rotate(180deg)":"none",transition:"transform .15s"}}>▾</span>
        </button>
        {screenerOpen && (
          <div style={{border:`1px solid ${T.border2}`,borderRadius:12,marginBottom:12,overflow:"hidden",background:T.bg}}>
            <ScreenerTab onPlayer={onPlayer}/>
          </div>
        )}

        {/* ✨ Smart Search — the natural-language "intuitive" search, reserved for $30 Pro
            members. Regular users keep the basic name/pitcher filter below; this turns a
            plain-English ask into a filtered board. needPro() opens the upgrade sheet for
            free users, expands the drawer for Pro/Founder. */}
        <button onClick={()=>{ if(smartOpen){ setSmartOpen(false); return } if(needPro()) setSmartOpen(true) }}
          style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:smartOpen?T.greenDim:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer",marginBottom:10}}>
          <span style={{fontSize:16}}>✨</span>
          <span style={{flex:1,textAlign:"left"}}>Smart Search <span style={{color:T.muted2,fontWeight:600}}>· describe the bats you want in plain English</span></span>
          {!isPro && <span style={{fontSize:8,fontWeight:800,color:T.amber}}>PRO</span>}
          <span style={{fontSize:13,color:T.muted2,transform:smartOpen?"rotate(180deg)":"none",transition:"transform .15s"}}>▾</span>
        </button>
        {smartOpen && <SmartSearchDrawer onPlayer={onPlayer}/>}

        {/* 💰 Value Board — "Beat the Book". The rating board surfaces the highest-probability
            plays; this one surfaces the highest-VALUE plays — where the model most beats the
            book's de-vigged price. That's the surface that actually attacks CLV. Pro-gated:
            needPro() opens the upgrade sheet for free users, expands the drawer for Pro/Founder. */}
        <button onClick={()=>{ if(valueOpen){ setValueOpen(false); return } if(needPro()) setValueOpen(true) }}
          style={{width:"100%",display:"flex",alignItems:"center",gap:10,background:valueOpen?T.greenDim:`linear-gradient(90deg, ${T.greenDim}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:12,padding:"12px 14px",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer",marginBottom:10}}>
          <span style={{fontSize:16}}>💰</span>
          <span style={{flex:1,textAlign:"left"}}>Beat the Book <span style={{color:T.muted2,fontWeight:600}}>· today's plays ranked by model-vs-book edge</span></span>
          {!isPro && <span style={{fontSize:8,fontWeight:800,color:T.amber}}>PRO</span>}
          <span style={{fontSize:13,color:T.muted2,transform:valueOpen?"rotate(180deg)":"none",transition:"transform .15s"}}>▾</span>
        </button>
        {valueOpen && <ValueBoardDrawer onPlayer={onPlayer} onGlossary={onGlossary}/>}

        {/* Market selector — hidden in Perfect Storm modes (HRR-only / HR-only) */}
        {!stormMode && !hrStormMode && (
        <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,marginBottom:2,WebkitOverflowScrolling:"touch"}}>
          {MARKETS.filter(m=>m[0]!=="K").map(([label,db])=>(
            <button key={db} onClick={()=>setMkt(db)} style={chip(mkt===db)}>{label}</button>
          ))}
        </div>)}

        {/* Sort selector — Perfect Storm is the first filter (hidden in Grid; grid sorts by column header) */}
        {!gridMode && (<>
        <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:"0.12em",margin:"8px 0 6px"}}>{(stormMode||hrStormMode)?"FILTER":"SORT BY ↓"}</div>
        <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:8,WebkitOverflowScrolling:"touch"}}>
          {[[STORM_KEY,"🌪 HRR Storm"], [HR_STORM_KEY,"🎆 HR Storm"], ...ANALYTICS_SORTS.map(s=>[s[0],s[1]])].map(([key,label])=>(
            <button key={key} onClick={()=>setSortKey(key)} style={chip(sortKey===key)}>{label}</button>
          ))}
        </div>
        </>)}
        {stormMode && (
          <div style={{display:"flex",gap:6,marginTop:2,marginBottom:2,flexWrap:"wrap"}}>
            <button onClick={()=>setStormPark(v=>!v)} style={chip(stormPark)}>{stormPark?"✓ ":""}Hitter parks ☀️</button>
          </div>
        )}

        {/* Filters */}
        <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8,flexWrap:"wrap"}}>
          {!stormMode && !gridMode && <button onClick={()=>setConfirmed(v=>!v)} style={chip(confirmed)}>{confirmed?"✓ ":""}Confirmed lineups</button>}
          <select value={team} onChange={e=>setTeam(e.target.value)} style={{padding:"7px 10px",borderRadius:10,fontSize:12,fontWeight:700,background:T.card,color:team?T.green:T.muted2,border:`1px solid ${T.border2}`,cursor:"pointer"}}>
            <option value="">All teams</option>
            {teams.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search hitter or pitcher…"
          style={{width:"100%",marginTop:8,padding:"9px 12px",borderRadius:10,fontSize:13,background:T.card,color:T.text,border:`1px solid ${T.border2}`,outline:"none",boxSizing:"border-box"}}/>
      </div>

      {/* Perfect Storm rows */}
      {stormMode && (
      <div style={{padding:"0 14px",display:"flex",flexDirection:"column",gap:8}}>
        <StormBacktestPanel/>
        {stormLoading ? [0,1,2].map(i=><div key={i} style={{height:74,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,opacity:0.45}}/>)
        : stormPlayers.length===0 ? <div style={{color:T.muted,fontSize:13,padding:"8px 2px",lineHeight:1.6}}>No perfect-storm spots yet — needs confirmed lineups + pitcher data (fills in after ~3PM ET).</div>
        : stormPlayers.map((p,i)=>(
          <button key={p.player_id||i} onClick={()=>onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:"HRR",prob:p.prob_over,odds:p.book_over!=null?fmtOdds(p.book_over):undefined,opp:p.opp_pitcher?`${p.team} · vs ${p.opp_pitcher} (${p.opp_hand})`:p.team})}
            style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"11px 13px"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:14.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}><span style={{color:T.muted,fontFamily:T.mono,fontSize:11,marginRight:6}}>{i+1}</span>{p.full_name} <span style={{fontSize:10,color:T.muted2,fontWeight:600}}>{p.team} · {p.bats}HB</span></div>
                <div style={{fontSize:10.5,color:T.muted,marginTop:2}}>vs {p.opp_pitcher} ({p.opp_hand}HP){p.book_over!=null?` · ${fmtOdds(p.book_over)}`:""}</div>
              </div>
              {p.prob_over!=null && <div style={{fontSize:16,fontWeight:900,color:probColor(p.prob_over),fontFamily:T.mono}}>{pct(p.prob_over)}</div>}
            </div>
            <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
              <span style={{fontSize:9.5,fontWeight:700,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:6,padding:"2px 7px"}}>P OPS/{p.opp_hand==="L"?"L":"R"}HB {String(p.pitcher_ops_vs_hand).replace(/^0/,"")}</span>
              <span style={{fontSize:9.5,fontWeight:700,color:T.blue,background:T.blueDim,border:`1px solid ${T.blueLt}`,borderRadius:6,padding:"2px 7px"}}>Zone fit {p.zone_fit}</span>
              {p.form_delta_pct!=null && <span style={{fontSize:9.5,fontWeight:700,
                color: p.form_delta_pct>=0.03?T.green : p.form_delta_pct<=-0.03?"#ef6a6a" : T.muted2,
                background: p.form_delta_pct>=0.03?T.greenDim : p.form_delta_pct<=-0.03?"rgba(239,106,106,0.10)" : T.card,
                border:`1px solid ${p.form_delta_pct>=0.03?T.greenMid : p.form_delta_pct<=-0.03?"rgba(239,106,106,0.30)" : T.border2}`,
                borderRadius:6,padding:"2px 7px"}}>{p.form_delta_pct>=0.03?"▲":p.form_delta_pct<=-0.03?"▼":"■"} form {p.form_delta_pct>=0?"+":""}{Math.round(p.form_delta_pct*100)}%</span>}
              {p.park_factor!=null && <span style={{fontSize:9.5,fontWeight:700,color:p.park_factor>=1.03?T.green:p.park_factor<=0.97?T.muted2:T.muted,background:p.park_factor>=1.03?T.greenDim:T.card,border:`1px solid ${p.park_factor>=1.03?T.greenMid:T.border2}`,borderRadius:6,padding:"2px 7px"}}>{p.park_factor>=1.03?"☀️":p.park_factor<=0.97?"🅿️":"●"} Park {p.park_factor.toFixed(2)}</span>}
              {p.opp_k9!=null && <span title="Opposing K/9 — lower means more balls in play, a better HRR floor" style={{fontSize:9.5,fontWeight:700,color:p.opp_k9<=7.5?T.green:p.opp_k9>=9.5?T.muted2:T.muted,background:p.opp_k9<=7.5?T.greenDim:T.card,border:`1px solid ${p.opp_k9<=7.5?T.greenMid:T.border2}`,borderRadius:6,padding:"2px 7px"}}>K/9 {p.opp_k9}</span>}
              {p.edge!=null && <span style={{fontSize:9.5,fontWeight:800,color:p.edge>=0?T.green:"#ef6a6a",background:p.edge>=0?T.greenDim:"rgba(239,106,106,0.10)",border:`1px solid ${p.edge>=0?T.greenMid:"rgba(239,106,106,0.30)"}`,borderRadius:6,padding:"2px 7px"}}>{p.edge>=0?"+":""}{Math.round(p.edge*100)}% edge</span>}
            </div>
          </button>
        ))}
      </div>)}

      {/* HR Perfect Storm */}
      {hrStormMode && (
        hrLoading ? <div style={{padding:"0 14px"}}>{[0,1,2].map(i=><div key={i} style={{height:110,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:8,opacity:0.45}}/>)}</div>
        : <HRStormView picks={hrPicks} team={team} search={search} onPlayer={onPlayer}/>
      )}

      {/* Game Explorer (grid view) */}
      {gridMode && (
        loading ? <div style={{padding:"0 14px"}}>{[0,1,2,3,4].map(i=><div key={i} style={{height:40,background:T.card,border:`1px solid ${T.border}`,borderRadius:8,marginBottom:6,opacity:0.45}}/>)}</div>
        : <GameExplorer picks={picks} mkt={mkt} team={team} search={search} sortKey={gridSort} setSortKey={setGridSort} onPlayer={onPlayer} toggleLeg={toggleLeg} inParlay={inParlay} gameTimes={venueTimes}/>
      )}

      {/* Rows */}
      {!stormMode && !hrStormMode && !gridMode && (
      <div style={{padding:"0 14px",display:"flex",flexDirection:"column",gap:8}}>
        {loading ? [0,1,2,3,4].map(i=><div key={i} style={{height:64,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,opacity:0.45}}/>)
        : rows.length===0 ? <div style={{color:T.muted,fontSize:13,padding:"8px 2px"}}>No hitters match — try a different market or clear filters.</div>
        : rows.map(({p,v},i)=>(
          <button key={`${p.player_id ?? p.full_name}`} onClick={()=>p.player_id&&onPlayer({name:p.full_name,player_id:p.player_id,market:uiLabel,conf:p.confidence,prob:p.prob_over,tier:p.tier,edge:p.edge_pct!=null?`${p.edge_pct>=0?"+":""}${(p.edge_pct*100).toFixed(1)}%`:undefined,opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team})}
            style={{width:"100%",textAlign:"left",cursor:p.player_id?"pointer":"default",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"10px 12px",color:T.text}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:14,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                  <span style={{color:T.muted,fontFamily:T.mono,fontSize:11,marginRight:6}}>{i+1}</span>{p.full_name}
                  <span style={{color:T.muted2,fontWeight:600,fontSize:11}}> · {p.team}</span>
                </div>
                <div style={{fontSize:10.5,color:T.muted,marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>vs {p.opp_pitcher_name||"TBD"}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:19,fontWeight:900,fontFamily:T.mono,color:metricColor(sortKey,v),lineHeight:1}}>{fmtMetric(sortKey,v)}</div>
                <div style={{fontSize:8,color:T.muted,fontWeight:700,letterSpacing:"0.08em",marginTop:2}}>{sortMeta[1]}</div>
              </div>
            </div>
            {/* full metric strip */}
            <div style={{borderTop:`1px solid ${T.border}`,marginTop:8,paddingTop:7,display:"flex",gap:12,flexWrap:"wrap",fontSize:10.5,fontFamily:T.mono}}>
              <span style={{color:T.muted}}>Zone <b style={{color:scoreColor(p?.inputs?.zone_fit)}}>{p?.inputs?.zone_fit??"—"}</b></span>
              <span style={{color:T.muted}}>Pitch <b style={{color:p?.inputs?.pitch_fit!=null?scoreColor(p.inputs.pitch_fit):T.muted2}}>{p?.inputs?.pitch_fit??"—"}</b></span>
              <span style={{color:T.muted}}>L7 <b style={{color:T.text}}>{p?.inputs?.form_l7!=null?p.inputs.form_l7.toFixed(2):"—"}</b></span>
              <span style={{color:T.muted}}>Rating <b style={{color:ratingColor(p.confidence)}}>{p.confidence??"—"}</b></span>
              <span style={{color:T.muted}}>Prob <b style={{color:probColor(p.prob_over)}}>{pct(p.prob_over)}</b></span>
              <span style={{color:T.muted}}>Edge <b style={{color:edgeColor((p.edge_pct||0)*100)}}>{p.edge_pct!=null?`${p.edge_pct>=0?"+":""}${(p.edge_pct*100).toFixed(1)}%`:"—"}</b></span>
              <span style={{color:T.muted}}>EV <b style={{color:edgeColor((p.ev||0)*100)}}>{p.ev!=null?`${p.ev>=0?"+":""}${(p.ev*100).toFixed(1)}%`:"—"}</b></span>
            </div>
          </button>
        ))}
      </div>)}
      <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,margin:"14px 14px 0"}}>
        Pure research view — every number is the model's, pulled from the same cached data as the board (no extra load). <b style={{color:T.text}}>Zone Fit</b> = how well the hitter grades in the zones tonight's pitcher lives in. <b style={{color:T.text}}>Form (L7)</b> = recent-7 rate. <b style={{color:T.text}}>Hittable Arm</b> = pitcher vulnerability multiplier (higher = easier).
      </div>
      {brief && <ErrorBoundary resetKey={brief}><BriefingSheet onClose={()=>setBrief(false)} onPlayer={onPlayer}/></ErrorBoundary>}
      {compare && <ErrorBoundary resetKey={mkt}><CompareDrawer onClose={()=>setCompare(false)} onPlayer={onPlayer} initialMkt={mkt}/></ErrorBoundary>}
      {swipe && <ErrorBoundary resetKey={mkt}><SwipeDeck picks={picks} mktLabel={uiLabel} toggleLeg={toggleLeg} inParlay={inParlay} onPlayer={onPlayer} onClose={()=>setSwipe(false)}/></ErrorBoundary>}
      {boardOpen && <ErrorBoundary resetKey="board"><BoardSheet onClose={()=>setBoardOpen(false)} onPlayer={onPlayer}/></ErrorBoundary>}
    </div>
  )
}

// ── LINEBRKR Top 15 — composite "our guys" ranking ──────────────────────────
// Per-bet-type weighting, so each market scores on what actually drives it:
//   HRR (hit + run/RBI = volume × scoring) → model read heaviest, then balanced
//       run-environment (pitcher hittability + park), zone fit, shrunk form.
//   HR  (power × environment) → park-HEAVY environment + the HR probability
//       (which already encodes batter power × pitcher HR/9 × park), light on
//       contact-form/zone since those predict hits, not homers.
// Unconfirmed lineups take a penalty in both. Extend WEIGHTS to add markets.
const _n01 = (v, lo, hi) => Math.max(0, Math.min(1, ((v == null ? lo : v) - lo) / ((hi - lo) || 1)))
const _avg = a => { const v=a.filter(x=>x!=null); return v.length ? v.reduce((s,x)=>s+x,0)/v.length : null }
// Each bet type scores on its OWN relevant factor set, weighted.
const TOP15_WEIGHTS = {
  "HRR_over_1.5": {
    note:"Model read + run environment (park-led + pitcher) + lineup slot + contact + recent HRR form.",
  },
  "HR_anytime": {
    note:"HR prob + batter power (barrel/ISO/hard-hit/pull) + pitcher HR/9 & barrels allowed + park + recent HR form.",
  },
}
// The IN-APP Power Spots board — the same strict criteria as the share card
// (_buildPowerSpots: homer-prone arm HR/9 ≥ 1.5 (default) · barrel ≥ 10% · hard-hit ≥ 44%,
// composite-ranked with the main-pitch-crush + park terms, 2/team cap). NOT the broad
// Homer Signal board — only the bats that clear this exact bar.
function PowerSpotsDrawer({onClose, onPlayer}){
  // Live what-if floors — seeded from the shared defaults, dialed with the sliders below.
  const [minHr9,setMinHr9] = useState(PS_DEFAULTS.minHr9)
  const [minBrl,setMinBrl] = useState(PS_DEFAULTS.minBrl)
  const [minHh,setMinHh]   = useState(PS_DEFAULTS.minHh)
  const { data, loading } = useFetch(()=>_buildPowerSpots({minHr9,minBrl,minHh}).catch(()=>({board:[],capped:[]})), [minHr9,minBrl,minHh])
  const tweaked = minHr9!==PS_DEFAULTS.minHr9 || minBrl!==PS_DEFAULTS.minBrl || minHh!==PS_DEFAULTS.minHh
  const resetFloors = ()=>{ setMinHr9(PS_DEFAULTS.minHr9); setMinBrl(PS_DEFAULTS.minBrl); setMinHh(PS_DEFAULTS.minHh) }
  const all = data?.board || []
  const capped = data?.capped || []
  const [q,setQ] = useState("")
  const _q = _deacc(q.trim().toLowerCase())
  const _match = r=>[r.name,r.team,r.arm].some(f=>_deacc(String(f||"").toLowerCase()).includes(_q))
  const rows = _q ? all.filter(_match) : all
  // Bats that cleared the floors but got bumped by the ≤2-per-team cap — search surfaces
  // them too (so "Rice" finds him even though he's not on the capped board).
  const rowsCapped = _q ? capped.filter(_match) : capped
  const anyProj = rows.some(r=>!r.confirmed)   // some bats' lineup slots not yet posted
  const chip = (val,lab,green)=>(
    <div style={{background:green?T.greenDim:T.card2,border:`1px solid ${green?T.greenMid:T.border}`,borderRadius:8,padding:"6px 7px",textAlign:"center",minWidth:48}}>
      <div style={{fontSize:13.5,fontWeight:900,fontFamily:T.mono,color:green?T.green:T.text}}>{val}</div>
      <div style={{fontSize:7.5,fontWeight:800,letterSpacing:"0.04em",color:T.muted,marginTop:2}}>{lab}</div>
    </div>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>💣 Power Spots</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:10}}>Hardest contact vs the most <b style={{color:T.text}}>homer-prone arms</b> — per side of the bat — who also <b style={{color:T.text}}>crush the pitcher's most-thrown pitch</b>. Dial the floors below to tighten or loosen the board. Research only · 21+.</div>

        {/* 🎚️ What-if floors — rebuild the board live against custom thresholds. */}
        <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:"11px 13px 13px",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
            <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.12em",color:T.muted}}>FILTER FLOORS</span>
            {tweaked
              ? <button onClick={resetFloors} style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:7,padding:"3px 9px",color:T.green,fontSize:10,fontWeight:800,cursor:"pointer"}}>↺ Reset</button>
              : <span style={{fontSize:9.5,color:T.muted,fontWeight:700}}>defaults</span>}
          </div>
          {[
            {lbl:"Arm HR/9 ≥", val:minHr9, set:setMinHr9, min:0.5, max:2.5, step:0.05, fmt:v=>v.toFixed(2)},
            {lbl:"Barrel% ≥",  val:minBrl, set:setMinBrl, min:0.04, max:0.20, step:0.005, fmt:v=>`${Math.round(v*100)}%`},
            {lbl:"Hard-Hit% ≥",val:minHh,  set:setMinHh,  min:0.30, max:0.55, step:0.005, fmt:v=>`${Math.round(v*100)}%`},
          ].map((s,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginTop:i?9:0}}>
              <span style={{fontSize:11,color:T.muted2,fontWeight:700,width:92,flexShrink:0}}>{s.lbl}</span>
              <input type="range" min={s.min} max={s.max} step={s.step} value={s.val}
                onChange={e=>s.set(parseFloat(e.target.value))}
                style={{flex:1,accentColor:T.green,cursor:"pointer"}}/>
              <span style={{fontSize:12.5,fontWeight:900,fontFamily:T.mono,color:T.green,width:48,textAlign:"right",flexShrink:0}}>{s.fmt(s.val)}</span>
            </div>
          ))}
          <div style={{fontSize:9.5,color:T.muted,marginTop:9,lineHeight:1.4}}>Default: HR/9 ≥ {PS_DEFAULTS.minHr9.toFixed(2)} · Barrel ≥ {Math.round(PS_DEFAULTS.minBrl*100)}% · Hard-Hit ≥ {Math.round(PS_DEFAULTS.minHh*100)}%{tweaked?` · showing ${all.length} bat${all.length===1?"":"s"}`:""}</div>
        </div>
        {!loading && all.length>0 && (
          <div style={{position:"relative",marginBottom:10}}>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search hitter, team, or pitcher…"
              style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 30px 9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
            {q && <button onClick={()=>setQ("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted2,fontSize:16,cursor:"pointer",lineHeight:1,padding:4}}>×</button>}
          </div>
        )}
        {/* Show early off PROBABLE pitchers; caveat that unconfirmed bats can change when
            lineups post (~3PM ET) and the board auto-updates then. */}
        {anyProj && !loading && rows.length>0 && (
          <div style={{display:"flex",alignItems:"center",gap:8,background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:10,padding:"9px 11px",marginBottom:12}}>
            <span style={{fontSize:13}}>⏳</span>
            <span style={{fontSize:11.5,fontWeight:700,color:T.amber,lineHeight:1.35}}>Projected off probable pitchers — bats marked <b>PROJ</b> aren't in a confirmed lineup yet. Auto-updates as lineups post (~3PM ET).</span>
          </div>
        )}
        {loading && [0,1,2].map(i=><Shell key={i} h={104}/>)}
        {!loading && !rows.length && !rowsCapped.length && <div style={{textAlign:"center",color:T.muted,padding:"30px 16px",fontSize:13,lineHeight:1.6}}>{_q?`No Power Spots matching “${q.trim()}”.`:<>No bats clear {tweaked?"these floors":"the Power Spots bar"} on today's board.<br/>{tweaked?"Loosen the sliders above":"Lower the sliders"} to widen it, or check back once probable pitchers post.</>}</div>}
        {!loading && rows.map((r,idx)=>(
          <button key={(r.player_id||r.name)+'-'+idx} onClick={()=>onPlayer&&onPlayer({name:r.name, player_id:r.player_id, market:"HR_anytime", team:r.team, opp:r.arm?`${r.team} · vs ${r.arm}`:r.team})} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:idx===0?`linear-gradient(135deg,${T.card3},${T.card})`:T.card,border:`1px solid ${idx===0?T.greenMid:T.border2}`,borderRadius:13,padding:"12px 13px",marginBottom:9,display:"flex",alignItems:"center",gap:10}}>
            <div style={{flex:"0 0 24px",width:24,height:24,borderRadius:"50%",background:idx===0?T.green:"#161e29",color:idx===0?"#04140D":T.muted,fontSize:12,fontWeight:800,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:T.mono}}>{idx+1}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <span style={{fontSize:16,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
                {r.hot && <span style={{fontSize:9,fontWeight:900,color:T.amber}}>HOT</span>}
                <span style={{flexShrink:0,fontSize:8,fontWeight:800,letterSpacing:"0.04em",borderRadius:4,padding:"1px 5px",...(r.confirmed?{color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`}:{color:T.muted,background:T.card2,border:`1px solid ${T.border2}`})}}>{r.confirmed?"✓ CONF":"PROJ"}</span>
              </div>
              <div style={{fontSize:10.5,color:T.muted,fontFamily:T.mono,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.bats} vs <span style={{color:r.adv?T.green:T.muted}}>{r.ophand}</span> · {r.arm} <span style={{color:r.hr9hot?T.amber:T.muted2}}>{r.hr9} HR/9</span></div>
              {r.mp && <div style={{fontSize:10,color:r.mpcrush?T.green:T.muted2,fontFamily:T.mono,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.mp} {r.mpu}% → .{String(r.mpx).padStart(3,"0")}</div>}
            </div>
            <div style={{display:"flex",gap:5,flexShrink:0}}>
              {chip(`${r.barrel}%`,"BRL",r.barrel>=18)}
              {chip(`${r.hh}%`,"HH",r.hh>=55)}
              {chip(`${r.hr}%`,"HR",r.hr>=18)}
            </div>
          </button>
        ))}

        {/* CAPPED — bats that cleared the floors but the ≤2-per-team cap bumped them.
            Answers "where's my guy?" without leaving the board. Tappable to the full card. */}
        {!loading && rowsCapped.length>0 && (
          <div style={{marginTop:rows.length?8:0}}>
            <div style={{fontSize:9,fontWeight:800,letterSpacing:"0.12em",color:T.muted,margin:"4px 2px 8px"}}>CAPPED · CLEARED THE FLOORS, ≤2-PER-TEAM BUMPED THEM</div>
            {rowsCapped.map((r,idx)=>(
              <button key={(r.player_id||r.name)+'-cap-'+idx} onClick={()=>onPlayer&&onPlayer({name:r.name, player_id:r.player_id, market:"HR_anytime", team:r.team, opp:r.arm?`${r.team} · vs ${r.arm}`:r.team})}
                style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.muted2,background:T.card,border:`1px dashed ${T.border2}`,borderRadius:12,padding:"10px 12px",marginBottom:8,display:"flex",alignItems:"center",gap:10,opacity:0.75}}>
                <div style={{flex:"0 0 24px",fontSize:11,fontWeight:800,color:T.faint,fontFamily:T.mono,textAlign:"center"}}>#{r.rawRank}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <span style={{fontSize:14.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
                    {r.hot && <span style={{fontSize:8.5,fontWeight:900,color:T.amber}}>HOT</span>}
                  </div>
                  <div style={{fontSize:10,color:T.muted,fontFamily:T.mono,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.bats} vs {r.ophand} · {r.arm} {r.hr9} HR/9 · <span style={{color:T.amber}}>capped{(r.ahead&&r.ahead.length)?` · behind ${r.ahead.join(" & ")}`:` · 2 ${r.team} ahead`}</span></div>
                </div>
                <div style={{display:"flex",gap:5,flexShrink:0}}>
                  {chip(`${r.barrel}%`,"BRL")}
                  {chip(`${r.hh}%`,"HH")}
                  {chip(`${r.hr}%`,"HR")}
                </div>
              </button>
            ))}
            <div style={{fontSize:9.5,color:T.faint,lineHeight:1.45,margin:"2px 2px 0"}}>Cleared the floors but the board caps at 2 per team — a full lineup vs one arm is correlated. Tap to open the card anyway.</div>
          </div>
        )}
      </div>
    </Sheet>
  )
}
function PerfectStormDrawer({onClose, onPlayer}){
  // Hitters where 3 independent edges align: opposing pitcher allows a high OPS vs
  // the batter's hand + strong zone fit + trending-up form. edgeOnly adds a 4th gate:
  // our calibrated prob must also beat the book's no-vig price.
  const [edgeOnly,setEdgeOnly] = useState(false)
  const [parkOnly,setParkOnly] = useState(false)
  const { data, loading } = useFetch(()=>getPerfectStorm(todayStr(),edgeOnly,200,parkOnly).catch(()=>null), [edgeOnly, parkOnly])
  const { data: rec } = useFetch(()=>getStormRecord(45).catch(()=>null), [])
  const players = data?.players || []
  const seg = on => ({padding:"4px 11px",borderRadius:7,fontSize:11.5,fontWeight:800,cursor:"pointer",border:`1px solid ${on?T.green:T.border2}`,background:on?T.greenDim:"transparent",color:on?T.green:T.muted2})
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <div style={{display:"flex",alignItems:"center",gap:9}}>
            <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>🌪 HRR Storm</div>
            {rec && rec.backtest && rec.backtest.n
              ? <span title={`Current formula (tuned ${rec.formula_epoch}) backtested over ${rec.backtest.window_days} days: ${Math.round(rec.backtest.hit_rate*100)}% on ${rec.backtest.n} qualifying picks, +${Math.round(rec.backtest.lift*100)} pts vs ${Math.round((rec.backtest.baseline||0)*100)}% baseline.${rec.n>0?` Live since tweak: ${rec.record}.`:""}`}
                  style={{fontSize:11,fontWeight:800,fontFamily:T.mono,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:6,padding:"2px 8px"}}>{Math.round(rec.backtest.hit_rate*100)}% · {rec.backtest.n} graded · +{Math.round(rec.backtest.lift*100)}pt</span>
              : (rec && rec.n>0 && <span title={`Frozen storm picks graded vs settled results, last ${rec.days} days`} style={{fontSize:11,fontWeight:800,fontFamily:T.mono,color:rec.win_pct>=0.5?T.green:T.muted2,background:rec.win_pct>=0.5?T.greenDim:T.card,border:`1px solid ${rec.win_pct>=0.5?T.greenMid:T.border2}`,borderRadius:6,padding:"2px 8px"}}>{rec.record} · {Math.round(rec.win_pct*100)}%</span>)}
          </div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:10}}><b style={{color:T.text}}>HRR = hits + runs + RBI ≥ 2.</b> Spots where the matchup lines up — the <b style={{color:T.text}}>opposing pitcher allows a high OPS</b> to this batter's hand and a <b style={{color:T.text}}>strong zone-fit matchup</b> — then ranked by our full model (lineup spot, recent form, park and more). <span>Information only — not advice. 21+.</span></div>
        <div style={{display:"flex",gap:6,marginBottom:12,flexWrap:"wrap"}}>
          <button onClick={()=>setEdgeOnly(false)} style={seg(!edgeOnly)}>All spots</button>
          <button onClick={()=>setEdgeOnly(true)} style={seg(edgeOnly)}>+EV only ⚡</button>
          <button onClick={()=>setParkOnly(v=>!v)} style={seg(parkOnly)}>{parkOnly?"✓ ":""}Hitter parks ☀️</button>
        </div>
        {edgeOnly && <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:10,marginTop:-4}}>Storms where our <b style={{color:T.text}}>calibrated prob also beats the book's no-vig price</b> — great matchup <i>and</i> value.</div>}
        {loading && [0,1,2].map(i=><Shell key={i} h={74}/>)}
        {!loading && !players.length && <div style={{textAlign:"center",color:T.muted,padding:"30px 16px",fontSize:13,lineHeight:1.6}}>{edgeOnly?"No +EV storms right now — the market has today's storm spots priced fairly or short. Try \"All spots\".":"No perfect-storm spots right now — needs confirmed lineups + pitcher data. Check back after ~3PM ET."}</div>}
        {!loading && players.map((p,i)=>(
          <button key={p.player_id||i} onClick={()=>onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:"HRR",prob:p.prob_over,odds:p.book_over!=null?fmtOdds(p.book_over):undefined,opp:p.opp_pitcher?`${p.team} · vs ${p.opp_pitcher} (${p.opp_hand})`:p.team})}
            style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:12,padding:"11px 13px",marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:15,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.full_name} <span style={{fontSize:10,color:T.muted2,fontWeight:600}}>{p.team} · {p.bats}HB</span></div>
                <div style={{fontSize:10.5,color:T.muted,marginTop:2}}>vs {p.opp_pitcher} ({p.opp_hand}HP){p.book_over!=null?` · ${fmtOdds(p.book_over)}`:""}</div>
              </div>
              {p.prob_over!=null && <div style={{fontSize:16,fontWeight:900,color:probColor(p.prob_over),fontFamily:T.mono}}>{pct(p.prob_over)}</div>}
            </div>
            <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
              <span style={{fontSize:9.5,fontWeight:700,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:6,padding:"2px 7px"}}>P OPS/{p.opp_hand==="L"?"L":"R"}HB {String(p.pitcher_ops_vs_hand).replace(/^0/,"")}</span>
              <span style={{fontSize:9.5,fontWeight:700,color:T.blue,background:T.blueDim,border:`1px solid ${T.blueLt}`,borderRadius:6,padding:"2px 7px"}}>Zone fit {p.zone_fit}</span>
              {p.form_delta_pct!=null && <span style={{fontSize:9.5,fontWeight:700,
                color: p.form_delta_pct>=0.03?T.green : p.form_delta_pct<=-0.03?"#ef6a6a" : T.muted2,
                background: p.form_delta_pct>=0.03?T.greenDim : p.form_delta_pct<=-0.03?"rgba(239,106,106,0.10)" : T.card,
                border:`1px solid ${p.form_delta_pct>=0.03?T.greenMid : p.form_delta_pct<=-0.03?"rgba(239,106,106,0.30)" : T.border2}`,
                borderRadius:6,padding:"2px 7px"}}>{p.form_delta_pct>=0.03?"▲":p.form_delta_pct<=-0.03?"▼":"■"} form {p.form_delta_pct>=0?"+":""}{Math.round(p.form_delta_pct*100)}%</span>}
              {p.park_factor!=null && <span style={{fontSize:9.5,fontWeight:700,color:p.park_factor>=1.03?T.green:p.park_factor<=0.97?T.muted2:T.muted,background:p.park_factor>=1.03?T.greenDim:T.card,border:`1px solid ${p.park_factor>=1.03?T.greenMid:T.border2}`,borderRadius:6,padding:"2px 7px"}}>{p.park_factor>=1.03?"☀️":p.park_factor<=0.97?"🅿️":"●"} Park {p.park_factor.toFixed(2)}</span>}
              {p.opp_k9!=null && <span title="Opposing K/9 — lower means more balls in play, a better HRR floor" style={{fontSize:9.5,fontWeight:700,color:p.opp_k9<=7.5?T.green:p.opp_k9>=9.5?T.muted2:T.muted,background:p.opp_k9<=7.5?T.greenDim:T.card,border:`1px solid ${p.opp_k9<=7.5?T.greenMid:T.border2}`,borderRadius:6,padding:"2px 7px"}}>K/9 {p.opp_k9}</span>}
              {p.edge!=null && <span style={{fontSize:9.5,fontWeight:800,color:p.edge>=0?T.green:"#ef6a6a",background:p.edge>=0?T.greenDim:"rgba(239,106,106,0.10)",border:`1px solid ${p.edge>=0?T.greenMid:"rgba(239,106,106,0.30)"}`,borderRadius:6,padding:"2px 7px"}}>{p.edge>=0?"+":""}{Math.round(p.edge*100)}% edge</span>}
            </div>
          </button>
        ))}
      </div>
    </Sheet>
  )
}
function PerfectStackDrawer({onClose, onPlayer}){
  const [size,setSize] = useState(3)
  const { data, loading } = useFetch(()=>getPerfectStack(todayStr(),"HRR_over_1.5",size).catch(()=>null), [size])
  const stacks = data?.stacks||[]
  const best = stacks[0]
  const seg = on => ({padding:"4px 11px",borderRadius:7,fontSize:12,fontWeight:800,cursor:"pointer",border:`1px solid ${on?T.green:T.border2}`,background:on?T.greenDim:"transparent",color:on?T.green:T.muted2})
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>🎯 Perfect Stack</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:10}}>The best <b style={{color:T.text}}>same-team</b> HRR stack — built to maximize the chance of a <b style={{color:T.text}}>perfect day</b> (every leg hits). Teammates hit together, so a tight stack goes all-green far more often than a spread board. <span>Information only — not betting advice. 21+.</span></div>
        <div style={{display:"flex",gap:6,marginBottom:12}}>
          {[2,3].map(n=><button key={n} onClick={()=>setSize(n)} style={seg(n===size)}>{n}-leg</button>)}
        </div>
        {loading && [0,1].map(i=><Shell key={i} h={120}/>)}
        {!loading && !best && <div style={{textAlign:"center",color:T.muted,padding:"30px 16px",fontSize:13,lineHeight:1.6}}>No same-team stack yet — lineups typically post by 3PM ET.</div>}
        {!loading && best && (
          <div style={{background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:14,overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px",borderBottom:`1px solid ${T.border}`,background:T.greenDim}}>
              <div>
                <div style={{fontSize:16,fontWeight:900}}>{best.team} stack · {best.size} legs</div>
                <div style={{fontSize:10,color:T.muted}}>vs {(best.opp_pitcher||"TBD").slice(0,18)} · {best.venue||""}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:20,fontWeight:900,color:T.green,fontFamily:T.mono,lineHeight:1}}>{Math.round((best.combined_independent||0)*100)}%</div>
                <div style={{fontSize:8,color:T.muted}}>all-hit floor</div>
              </div>
            </div>
            {best.players.map((p,i)=>(
              <button key={p.player_id||i} onClick={()=>onPlayer&&onPlayer({name:p.full_name,player_id:p.player_id,market:"HRR",prob:p.prob_over,opp:best.opp_pitcher?`${best.team} · vs ${best.opp_pitcher}`:best.team})} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,display:"flex",alignItems:"center",gap:8,padding:"10px 14px",borderBottom:i<best.players.length-1?`1px solid ${T.border}`:"none",background:"transparent",border:"none"}}>
                <span style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:14,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.full_name}</span>
                  <FormTrend trend={p.form_trend} delta={p.form_delta_pct} size={9}/>
                  {p.lineup_slot && <span style={{fontSize:9,color:T.muted,fontFamily:T.mono}}>#{p.lineup_slot}</span>}
                </span>
                <span style={{fontSize:14,fontWeight:800,color:T.green,fontFamily:T.mono}}>{Math.round((p.prob_over||0)*100)}%</span>
              </button>
            ))}
            <div style={{padding:"7px 14px",background:T.card3}}>
              <span style={{fontSize:8.5,color:T.muted}}>Same-team legs hit together — real all-hit odds are higher than the {Math.round((best.combined_independent||0)*100)}% shown (conservative independent floor).</span>
            </div>
          </div>
        )}
        {!loading && stacks.length>1 && (
          <div style={{fontSize:10,color:T.muted,marginTop:10}}>Alt: {stacks.slice(1).map(s=>`${s.team} (${Math.round((s.combined_independent||0)*100)}%)`).join(" · ")}</div>
        )}
      </div>
    </Sheet>
  )
}
// One "attack this arm" row — pitcher on the left, the best hitter to target on the
// right (with our hit %). Tapping opens that hitter's full card.
function AttackRow({r, rank, onOpen}){
  const top=rank<3
  return (
    <button onClick={()=>onOpen(r.best)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${top?T.greenMid:T.border}`,borderRadius:12,padding:"11px 13px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
      <div style={{minWidth:0,flex:1}}>
        <div style={{fontSize:13.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.pitcher}{r.hand?<span style={{color:T.muted,fontWeight:600,fontSize:11,marginLeft:5}}>{r.hand}HP</span>:null}</div>
        <div style={{fontSize:11,color:T.muted,marginTop:2}}>vs {r.team||"—"} · {r.targets} target{r.targets===1?"":"s"}</div>
      </div>
      <span style={{color:T.faint,fontSize:15,flexShrink:0}}>→</span>
      <div style={{textAlign:"right",flexShrink:0,minWidth:0}}>
        <div style={{fontSize:12.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:150}}>{r.best.full_name}</div>
        <div style={{fontSize:10.5,color:T.muted2,marginTop:1}}>{mDisp(r.best.market)} · <span style={{color:probColorMkt(r.best.prob_over||0,r.best.market),fontWeight:800,fontFamily:T.mono}}>{pct(r.best.prob_over||0)}</span></div>
      </div>
    </button>
  )
}

// Fetch today's board across markets and reduce it to the pitchers-to-attack rows.
// Self-contained so any tab can open the drawer without wiring the board itself.
function useAttackRows(){
  const { data: lb } = useFetch(()=>Promise.all(
    HOME_MARKETS.map(([lbl,db])=>getLeaderboard(db, todayStr(), 60)
      .then(d=>({lbl, entries:Array.isArray(d?.entries)?d.entries:[]})).catch(()=>({lbl, entries:[]})))
  ), [])
  const flat = []
  if(Array.isArray(lb)){
    for(const grp of lb){
      const lbl=grp?.lbl; const entries=Array.isArray(grp?.entries)?grp.entries:[]
      const canon=CANON_LINE[lbl]
      for(const e of entries){
        if(canon!=null && Math.abs((e.line??canon)-canon) >= 0.01) continue
        flat.push({...e, market:lbl, tier:tierFor(e.prob_over, confOf(e), lbl, (e.edge_pct!=null?e.edge_pct*100:null), e.book_over!=null)})
      }
    }
  }
  return buildAttackRows(flat)
}

// 🎯 Pitchers to attack — the full slate, one row per gettable arm with its best
// hitter target. Self-contained: owns its share state and renders the branded card.
function AttackDrawer({rows, onClose, onOpen}){
  const [share,setShare] = useState(null)
  const shared = share==="shared"||share==="downloaded"
  const doShare = async () => {
    if(share==="working") return
    setShare("working")
    const payload = { subtitle:`Model-backed · ${fmtDate()}`, rows: rows.slice(0,7).map(r=>({
      pitcher:r.pitcher, hand:r.hand, team:r.team, targets:r.targets,
      hitter:r.best.full_name, market:mDisp(r.best.market),
      probStr:pct(r.best.prob_over||0), probColor:probColorMkt(r.best.prob_over||0, r.best.market),
    })) }
    let res="failed"; try { res=await shareAttack(payload) } catch(_){}
    setShare(res==="shared"?"shared":res==="downloaded"?"downloaded":null)
    if(res==="shared"||res==="downloaded") setTimeout(()=>setShare(null),2200)
  }
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"6px 16px 22px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
          <div style={{fontSize:16,fontWeight:900}}>🎯 Pitchers to attack</div>
          <button onClick={doShare} disabled={share==="working"} style={{flexShrink:0,padding:"7px 12px",borderRadius:8,background:shared?T.greenDim:T.card,border:`1px solid ${shared?T.green:T.border2}`,color:shared?T.green:T.muted2,fontSize:11.5,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{share==="working"?"…":shared?"Shared ✓":"📤 Share card"}</button>
        </div>
        <div style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.5}}>Every arm on today's slate, ranked by the hit % of the best hitter to target against it. Tap any row for the full breakdown.</div>
        {rows.length===0 && <div style={{textAlign:"center",color:T.muted,fontSize:12.5,padding:"24px 10px",lineHeight:1.6}}>No matchups yet — this fills in once today's board and lineups post (~3 PM ET).</div>}
        {rows.map((r,i)=><AttackRow key={r.pitcher+r.team+i} r={r} rank={i} onOpen={onOpen}/>)}
        {rows.length>0 && <div style={{fontSize:9.5,color:T.faint,marginTop:10,lineHeight:1.4,textAlign:"center"}}>Research, not betting advice · 21+ · 1-800-GAMBLER</div>}
      </div>
    </Sheet>
  )
}

// 🏆 Today's Top Plays — the single all-markets view. Takes the Home flat pool
// (every market's live plays), ranks by the LINEBRKR Rating, keeps one best play
// per hitter and max 2 per team, and is tappable + shareable. onOpen builds the
// full card payload (so The Case + odds match the play's market).
function TopPlaysDrawer({plays, onClose, onOpen, gradedOnly=false}){
  const [share,setShare] = useState(null)
  // Live settlement — poll the boxscore feed so each play flips to ✓ HIT / ✗ MISS
  // (or 🔴 live progress) in place. Keyed by player_id; e.market is already the short
  // label liveStatus() expects ("HRR", "1+ H", "TB"…).
  const [liveTick,setLiveTick] = useState(0)
  useEffect(()=>{ const id=setInterval(()=>setLiveTick(t=>t+1), 45000); return ()=>clearInterval(id) }, [])
  const { data: liveData } = useFetch(()=>getLive(todayStr()).catch(()=>null), [liveTick])
  const liveMap = liveData?.lines || {}
  // Two entry points, one component:
  //  · gradedOnly (from the record card) → ONLY the 70%+ plays that make up the win% —
  //    the exact graded set, ranked by probability, no caps (each is graded).
  //  · default (Today's Top Plays button) → the full board ranked by likelihood, with a
  //    ◆ badge on the 70%+ ones so the graded set is highlighted in place.
  const all = plays||[]
  const bestProb = all.reduce((m,e)=>Math.max(m, e.prob_over||0), 0)
  let rows = []
  if(gradedOnly){
    rows = all.filter(e=>(e.prob_over||0) >= 0.70).sort((a,b)=>(b.prob_over||0)-(a.prob_over||0)).slice(0,40)
  } else {
    const sorted = all.slice().sort((a,b)=> ((b.prob_over||0)-(a.prob_over||0)) || ((confOf(b)??0)-(confOf(a)??0)))
    const seen = new Set(); const tcap = {}
    for(const e of sorted){
      const pk = e.player_id || e.full_name
      if(seen.has(pk)) continue                       // one best play per hitter
      const t = e.team || `p${pk}`
      if((tcap[t]||0) >= 2) continue                  // max 2 per team
      seen.add(pk); tcap[t] = (tcap[t]||0)+1
      rows.push(e)
      if(rows.length >= 24) break
    }
  }
  const gradedCount = rows.filter(e=>(e.prob_over||0) >= 0.70).length
  // Live hit/miss tally across the shown rows (settled + in-progress).
  const liveTally = (()=>{ let hit=0,miss=0,live=0
    for(const e of rows){ const lv=liveStatus(liveMap[e.player_id], e.market, e.line); if(!lv) continue
      if(lv.state==="hit") hit++; else if(lv.state==="miss") miss++; else if(lv.state==="live") live++ }
    return {hit, miss, live, graded:hit+miss} })()
  const doShare = async () => {
    const sr = rows.slice(0,12).map((e,i)=>{ const c=confOf(e); return {
      rank:i+1, name:e.full_name,
      sub: e.opp_pitcher_name ? `${e.team} · vs ${e.opp_pitcher_name}` : (e.team||""),
      market: e.market,
      probStr: pct(e.prob_over||0), probColor: probColorMkt(e.prob_over, e.market),
      rating: c, ratingColor: (c!=null?ratingColor(c):T.muted2) }})
    const r = await shareBoard({ title: gradedOnly?"Today's Graded Plays · 70%+":"Today's Top Plays",
      subtitle:`${gradedOnly?"70%+ · the plays behind our record":"Ranked by likelihood · all markets"} · ${fmtDate()}`, rows:sr })
    setShare(r); setTimeout(()=>setShare(null),2200)
  }
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"14px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:20,fontWeight:900,letterSpacing:"-0.02em"}}>{gradedOnly?<><span style={{color:T.blue}}>◆</span> Graded Plays <span style={{fontSize:13,color:T.blue,fontWeight:800}}>· 70%+</span></>:"🏆 Today's Top Plays"}</div>
          <button onClick={onClose} style={{width:32,height:32,borderRadius:9,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:15,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,marginBottom:12,lineHeight:1.5}}>{gradedOnly
          ? <>The <b style={{color:T.text}}>70%+ plays</b> that make up our record — <b style={{color:T.green}}>65% · +8pt over the field</b>. Posted before games, graded after.{gradedCount>0?` ${gradedCount} on today's board.`:""}{(liveTally.graded>0||liveTally.live>0)?<> <b style={{color:liveTally.graded>0?T.green:T.amber}}>Today: {liveTally.hit}-{liveTally.miss}{liveTally.live>0?` · ${liveTally.live} live`:""}</b>.</>:null}</>
          : <>Every market, ranked by how likely it is to hit. <span style={{color:T.blue,fontWeight:900}}>◆</span> = a <b style={{color:T.text}}>70%+</b> play — the set our Proven record grades{gradedCount>0?` (${gradedCount} today)`:""}.</>}</div>
        <button onClick={doShare} disabled={!rows.length} style={{width:"100%",marginBottom:12,padding:"10px",borderRadius:10,background:share?T.greenDim:T.card,border:`1px solid ${share?T.green:T.border2}`,color:share?T.green:T.muted2,fontSize:12.5,fontWeight:800,cursor:rows.length?"pointer":"default"}}>{share==="downloaded"?"Saved ✓":share?"Shared ✓":(gradedOnly?"📤 Share our graded plays":"📤 Share today's top plays")}</button>
        {rows.length===0 ? (
          <div style={{color:T.muted,fontSize:13,padding:"20px 4px",textAlign:"center",lineHeight:1.6}}>{gradedOnly
            ? `No 70%+ plays on today's board — nothing enters the graded record today${bestProb>0?` (today's best is ${pct(bestProb)})`:""}.`
            : "No plays yet — the board fills in once lineups confirm (~afternoon ET)."}</div>
        ) : rows.map((e,i)=>{
          const c=confOf(e); const prob=e.prob_over||0; const isGraded=prob>=0.70
          return (
          <button key={(e.player_id||e.full_name)+'-'+e.market+i} onClick={()=>onOpen(e)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${isGraded?T.blue:T.border}`,borderRadius:12,padding:"10px 12px",marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:14,fontWeight:800,color:isGraded?T.blue:T.muted,fontFamily:T.mono,width:26,flexShrink:0}}>#{i+1}</span>
            <Headshot mlbamId={e.mlbam_id} name={e.full_name} size={34}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:7}}>
                <span style={{fontSize:14.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.full_name}</span>
                <span style={{fontSize:9.5,fontWeight:800,color:T.muted2,fontFamily:T.mono,background:T.card2,border:`1px solid ${T.border2}`,borderRadius:4,padding:"1px 5px",flexShrink:0}}>{mDisp(e.market)}</span>
                {isGraded && <span title="70%+ — enters the graded record" style={{fontSize:10,fontWeight:900,color:T.blue,flexShrink:0}}>◆</span>}
                <span style={{marginLeft:"auto",flexShrink:0}}><LiveBadge line={liveMap[e.player_id]} marketLabel={e.market} pickLine={e.line}/></span>
              </div>
              <div style={{fontSize:11,color:T.muted,marginTop:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{e.team}{e.opp_pitcher_name?` · vs ${e.opp_pitcher_name}`:""}</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:16,fontWeight:800,fontFamily:T.mono,color:probColorMkt(prob,e.market)}}>{pct(prob)}</div>
              <div style={{fontSize:10.5,fontFamily:T.mono,fontWeight:800,color:c!=null?ratingColor(c):T.muted2}}>R{c!=null?Math.round(c):"—"}</div>
            </div>
          </button>)
        })}
      </div>
    </Sheet>
  )
}
function Top15Drawer({onClose, onPlayer}){
  const [mkt,setMkt] = useState("HRR_over_1.5")
  const [copied,setCopied] = useState(false)
  const { data, loading } = useFetch(()=>getToday(mkt, todayStr(), 500), [mkt])
  const picks = Array.isArray(data?.picks) ? data.picks : []
  // Rank by the ONE LINEBRKR Rating (how good the spot is to hit) — the same guide
  // as every other surface, no bespoke composite. EV breaks ties, and is shown
  // beside each play as the separate price axis.
  const _evOf = p => (p.ev ?? p.edge_pct ?? null)
  const _ratingOf = p => (p.confidence_score ?? p.confidence ?? confidenceFor(p))
  const _scored = picks.map(p=>({p, s:_ratingOf(p), ev:_evOf(p)}))
  const rankedAll = _scored.slice().sort((a,b)=> (b.s - a.s) || ((b.ev??-1) - (a.ev??-1)))
  // Diversity cap: at most 2 plays per TEAM so one soft-pitcher matchup can't flood the
  // list (e.g. 4 Phillies vs a hittable arm). 1–2 from the same lineup is fine; more is
  // correlated. Best-of-each-team first by Rating, then fill to 15.
  const MAX_PER_TEAM = 2
  const tcap = {}; const ranked = []
  for(const r of rankedAll){
    const t = r.p.team || r.p.venue || `p${r.p.player_id}`
    tcap[t] = tcap[t]||0
    if(tcap[t] < MAX_PER_TEAM){ ranked.push(r); tcap[t]++ }
    if(ranked.length>=15) break
  }
  const pc = {}; ranked.forEach(({p})=>{ const k=p.opp_pitcher_name; if(k) pc[k]=(pc[k]||0)+1 })
  const uiLabel = mkt==="HR_anytime" ? "HR" : "HRR"
  const seg = on => ({flex:1,padding:"9px",borderRadius:9,fontSize:13,fontWeight:800,cursor:"pointer",border:"none",background:on?T.green:"transparent",color:on?"#04140D":T.muted2})
  // Full board export for Claude — every factor + opp pitcher (for correlation),
  // so an LLM can build optimal uncorrelated parlays. Top 40 by LB score.
  const exportForClaude = async () => {
    const pct1 = v => v==null?"—":`${Math.round(v*100)}%`
    const r3 = v => v==null?"—":(+v).toFixed(3)
    const lines = rankedAll.slice(0,40).map(({p,s},idx)=>{
      const i=p.inputs||{}
      return `${idx+1}. ${p.full_name} (${p.team}) vs ${p.opp_pitcher_name||"TBD"}`
        + ` | rating:${s} prob:${pct1(p.prob_over)} zone:${i.zone_fit??"—"}`
        + ` slot:${i.lineup_slot??"—"} ${i.lineup_confirmed?"CONFIRMED":"unconfirmed"} recent_games:${i.recent_games??"—"}`
        + ` | form_L7:${r3(i.form_l7)} form_season:${r3(i.form_season)} hr_L15:${r3(i.hr_l15)} hr_season:${r3(i.hr_season)}`
        + ` | barrel:${i.barrel_pct!=null?(i.barrel_pct*100).toFixed(0)+"%":"—"} hardhit:${i.hard_hit_pct!=null?(i.hard_hit_pct*100).toFixed(0)+"%":"—"} iso:${r3(i.iso)} pull:${i.pull_pct!=null?(i.pull_pct*100).toFixed(0)+"%":"—"}`
        + ` | park:${i.park_factor!=null?i.park_factor.toFixed(2)+"x":"—"} pitcher_adj:${i.pitcher_adj!=null?i.pitcher_adj.toFixed(2):"—"} pitERA:${i.starter_era??"—"} pitHR9:${i.pitcher_hr9??"—"} pitBrl:${i.pitcher_barrel_allowed!=null?(i.pitcher_barrel_allowed*100).toFixed(0)+"%":"—"}`
        + ` edge:${p.edge_pct!=null?(p.edge_pct>=0?"+":"")+(p.edge_pct*100).toFixed(1)+"%":"—"}`
    })
    const txt = `LINEBRKR ${uiLabel} board · ${fmtDate()} · calibrated probs · ${rankedAll.length} plays\n`
      + `(same opp pitcher = same game = correlated; don't stack)\n\n` + lines.join("\n")
      + EXPORT_DISCLAIMER
    try{ await navigator.clipboard.writeText(txt) }catch{ const ta=document.createElement("textarea");ta.value=txt;document.body.appendChild(ta);ta.select();try{document.execCommand("copy")}catch(_){}ta.remove() }
    setCopied(true); setTimeout(()=>setCopied(false),2000)
  }
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>🏆 LINEBRKR Top 15</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5,marginBottom:10}}>The top-rated <b style={{color:T.text}}>{uiLabel}</b> plays, ranked by the <b style={{color:T.text}}>LINEBRKR Rating</b> (EV breaks ties). <b style={{color:T.text}}>Spread across games</b>; ⚠ marks bats sharing a pitcher (correlated). <span style={{color:T.muted}}>Information only — not betting advice. 21+.</span></div>
        <button onClick={exportForClaude} style={{width:"100%",background:T.card,border:`1px solid ${copied?T.green:T.border2}`,borderRadius:10,padding:"9px",color:copied?T.green:T.muted2,fontSize:12,fontWeight:700,cursor:"pointer",marginBottom:10}}>
          {copied ? "✓ Copied — paste it to Claude" : `📋 Copy full ${uiLabel} board for Claude (all factors)`}
        </button>
        <div style={{display:"flex",gap:4,background:T.card,border:`1px solid ${T.border2}`,borderRadius:11,padding:4,marginBottom:8}}>
          <button onClick={()=>setMkt("HRR_over_1.5")} style={seg(mkt==="HRR_over_1.5")}>HRR</button>
          <button onClick={()=>setMkt("HR_anytime")} style={seg(mkt==="HR_anytime")}>Homers</button>
        </div>
        <div style={{fontSize:10.5,color:T.muted,marginBottom:6,lineHeight:1.4}}>🔎 What lifts a {uiLabel} spot: {(TOP15_WEIGHTS[mkt]||{}).note}</div>
        <div style={{fontSize:9.5,color:T.muted,marginBottom:12,lineHeight:1.4}}>Rating: <b style={{color:T.blue}}>85+ Elite</b> · <b style={{color:T.green}}>70–84 Strong</b> · <b style={{color:"#34D399"}}>55–69 Solid</b> · <b style={{color:T.amber}}>42–54 Fair</b></div>
        {loading ? [0,1,2,3,4].map(i=><div key={i} style={{height:62,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginBottom:8,opacity:0.45}}/>)
        : ranked.length===0 ? <div style={{color:T.muted,fontSize:13,padding:"8px 2px"}}>No {uiLabel} board yet today — check back once the slate builds.</div>
        : ranked.map(({p,s},idx)=>{
          const i=p.inputs||{}
          const wall=wallFor(i.pitcher_adj)
          const dup=p.opp_pitcher_name && pc[p.opp_pitcher_name]>1
          // Label with the app-wide tier (same tierFor used on Picks/Home/drawer) so a
          // pick reads the SAME grade on every tab; the composite score `s` stays the
          // Sigma-specific ranking number. Was lbBand, which reused ELITE/STRONG with
          // unrelated thresholds → the same word meant different things across tabs.
          const t=tierFor(p.prob_over, p.confidence, PROP_SHORT[mkt]||mkt, p.edge_pct!=null?p.edge_pct*100:null, p.book_over!=null)
          return (
            <button key={`${p.player_id ?? p.full_name}`} onClick={()=>p.player_id&&onPlayer({name:p.full_name,player_id:p.player_id,market:uiLabel,conf:p.confidence,prob:p.prob_over,tier:p.tier,odds:p.book_over!=null?fmtOdds(p.book_over):undefined,book:p.book_used,edge:p.edge_pct!=null?`${p.edge_pct>=0?"+":""}${(p.edge_pct*100).toFixed(1)}%`:undefined,opp:p.opp_pitcher_name?`${p.team} · vs ${p.opp_pitcher_name}`:p.team})}
              style={{width:"100%",textAlign:"left",cursor:p.player_id?"pointer":"default",background:T.card,border:`1px solid ${idx<5?T.greenMid:T.border2}`,borderRadius:10,padding:"10px 12px",marginBottom:8,color:T.text}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:14,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    <span style={{color:idx<5?T.green:T.muted,fontFamily:T.mono,fontSize:12,marginRight:6,fontWeight:900}}>{idx+1}</span>{p.full_name}
                    <span style={{color:T.muted2,fontWeight:600,fontSize:11}}> · {p.team}</span>
                    {dup && <span style={{color:T.amber,fontSize:10,fontWeight:800,marginLeft:6}}>⚠ same game</span>}
                  </div>
                  <div style={{fontSize:10.5,color:T.muted,marginTop:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    vs {p.opp_pitcher_name||"TBD"} · {i.lineup_confirmed?"✓ confirmed":"⚠ unconfirmed"}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.06em"}}>RATING</div>
                  <div style={{fontSize:20,fontWeight:900,fontFamily:T.mono,color:ratingColor(s),lineHeight:1}}>{s}</div>
                  <div style={{fontSize:8.5,color:(TIER[t]||TIER.WATCH).bd,fontWeight:800,letterSpacing:"0.06em",marginTop:2}}>{t}</div>
                </div>
              </div>
              <div style={{borderTop:`1px solid ${T.border}`,marginTop:8,paddingTop:7,display:"flex",gap:12,flexWrap:"wrap",fontSize:10.5,fontFamily:T.mono}}>
                <span style={{color:T.muted}}>Prob <b style={{color:probColor(p.prob_over)}}>{pct(p.prob_over)}</b></span>
                <span style={{color:T.muted}}>Edge <b style={{color:edgeColor(p.edge_pct!=null?p.edge_pct*100:null)}}>{p.edge_pct!=null?`${p.edge_pct>=0?"+":""}${(p.edge_pct*100).toFixed(1)}%`:"—"}</b></span>
                <span style={{color:T.muted}}>Zone <b style={{color:scoreColor(i.zone_fit)}}>{i.zone_fit??"—"}</b></span>
                <span style={{color:T.muted}}>Park <b style={{color:T.text}}>{i.park_factor!=null?`${i.park_factor.toFixed(2)}x`:"—"}</b></span>
                <span style={{color:wall.color||T.muted2,fontWeight:700}}>{wall.tier}</span>
              </div>
            </button>
          )
        })}
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginTop:8}}>
          Ranked by the <b style={{color:T.text}}>LINEBRKR Rating</b> — one number for how good the spot is to hit (model conviction + matchup + zone fit + form + park + data quality), with EV breaking ties and shown beside each play. Unconfirmed lineups score lower. Lineups firm up ~3 PM ET — re-check then.
        </div>
      </div>
    </Sheet>
  )
}

// ── The Hot Corner (batter-vs-pitcher dashboard) ────────────────────────────
function HotCornerTab({onPlayer, onGlossary}){
  const [hrOnly,setHrOnly] = useState(false)
  const [team,setTeam]     = useState("")
  const [search,setSearch] = useState("")
  const { data, loading } = useFetch(()=>getHotCorner(todayStr(),{hrOnly,team}),[hrOnly,team])
  const items = data?.matchups||[]
  const teams = data?.teams||[]
  const q = search.trim().toLowerCase()
  const shown = items.filter(m=>!q
    || (m.full_name||"").toLowerCase().includes(q)
    || (m.opp_pitcher_name||"").toLowerCase().includes(q)
    || (m.team||"").toLowerCase().includes(q))
  const fmtAvg = n => (n||0).toFixed(3).replace(/^0\./,".")
  const fmtOps = n => (n||0).toFixed(3)
  const open = m => onPlayer({ name:m.full_name, player_id:m.player_id,
    opp:`${m.team} · vs ${m.opp_pitcher_name}${m.opp_pitcher_hand?` (${m.opp_pitcher_hand})`:""}` })
  return (
    <div style={{padding:"0 0 12px"}}>
      <div style={{padding:"12px 14px 6px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:20,fontWeight:900,letterSpacing:"-0.02em"}}>The Hot Corner</span>
          <span style={{fontSize:17}}>🔥</span>
          <div style={{marginLeft:"auto"}}><HelpChip onGlossary={onGlossary} section="scores"/></div>
        </div>
        <div style={{fontSize:12,color:T.muted,marginTop:3,lineHeight:1.4}}>Hitters who own tonight's pitcher — career head-to-head, ranked by dominance (AVG/OPS).</div>
      </div>
      {/* Search */}
      <div style={{padding:"0 14px 8px",position:"relative"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search hitter, team, or pitcher…"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${search?T.green:T.border2}`,borderRadius:10,padding:"9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
      </div>
      {/* Filters: HR-only + team */}
      <div style={{display:"flex",gap:8,padding:"0 14px 10px",overflowX:"auto",scrollbarWidth:"none",alignItems:"center"}}>
        <button onClick={()=>setHrOnly(v=>!v)} style={{flexShrink:0,padding:"5px 12px",borderRadius:16,background:hrOnly?T.greenDim:T.card,border:`1px solid ${hrOnly?T.green:T.border2}`,color:hrOnly?T.green:T.muted2,fontSize:11.5,fontWeight:700,cursor:"pointer"}}>💣 Homered off him</button>
        <button onClick={()=>setTeam("")} style={{flexShrink:0,padding:"5px 12px",borderRadius:16,background:!team?T.greenDim:T.card,border:`1px solid ${!team?T.green:T.border2}`,color:!team?T.green:T.muted2,fontSize:11.5,fontWeight:700,cursor:"pointer"}}>All teams</button>
        {teams.map(t=>(
          <button key={t} onClick={()=>setTeam(t===team?"":t)} style={{flexShrink:0,padding:"5px 11px",borderRadius:16,background:team===t?T.greenDim:T.card,border:`1px solid ${team===t?T.green:T.border2}`,color:team===t?T.green:T.muted,fontSize:11.5,fontWeight:700,cursor:"pointer"}}>{t}</button>
        ))}
      </div>
      <div style={{fontSize:11,color:T.muted,padding:"0 14px 8px"}}>{loading?"Loading…":`${shown.length} matchup${shown.length===1?"":"s"}`}</div>
      <div style={{padding:"0 14px"}}>
        {loading && [0,1,2,3].map(i=>(<div key={i} style={{height:96,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:8,opacity:0.45}}/>))}
        {!loading && !shown.length && (
          <div style={{textAlign:"center",color:T.muted,padding:"50px 16px",fontSize:13,lineHeight:1.6}}>
            {q?`No matchups match “${search.trim()}”.`:hrOnly?"No hitters have homered off tonight's starters yet.":"No batter-vs-pitcher history for today's slate yet."}<br/>
            {!q&&"Refreshes hourly as starters are confirmed."}
          </div>
        )}
        {!loading && shown.map((m,i)=>(
          <button key={(m.player_id)+'-'+i} onClick={()=>open(m)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${i<3?T.greenMid:T.border}`,borderRadius:12,padding:"12px 14px",marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
              <div style={{display:"flex",alignItems:"baseline",gap:8,minWidth:0}}>
                <span style={{fontSize:14,fontWeight:800,color:i<3?T.green:T.muted,fontFamily:T.mono}}>#{i+1}</span>
                <span style={{fontSize:15.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.full_name}</span>
                <span style={{fontSize:11,color:T.muted}}>{m.team}{m.bats?` · ${m.bats}HB`:""}</span>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                {m.hr>0 && <span style={{fontSize:11,fontWeight:800,color:"#F97316"}}>💣 {m.hr}</span>}
                {m.small_sample && <span style={{fontSize:9.5,fontWeight:700,color:T.muted,background:T.card3,borderRadius:5,padding:"2px 5px"}}>SMALL</span>}
              </div>
            </div>
            <div style={{fontSize:11.5,color:T.muted2,marginBottom:9}}>vs {m.opp_pitcher_name}{m.opp_pitcher_hand?` (${m.opp_pitcher_hand}HP)`:""}</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              <Stat label="Line" value={m.line} bg={T.greenDim}/>
              <Stat label="AVG" value={fmtAvg(m.avg)} color={m.avg>=0.300?T.green:m.avg>=0.250?T.amber:T.muted2}/>
              <Stat label="OPS" value={fmtOps(m.ops)} color={m.ops>=0.900?T.green:m.ops>=0.750?T.amber:T.muted2}/>
              <Stat label="HR · RBI" value={`${m.hr} · ${m.rbi}`}/>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Smart Search (Pro/$30): the "intuitive" natural-language search. A Pro member
// types plain English — "lefty power bats vs righties facing homer-prone arms in
// hitter parks tonight" — and /free/nl-search (Claude Haiku + keyword fallback)
// turns it into a structured filter over today's board and returns the matches.
// Regular users still get the basic name/pitcher text-filter on every board; this
// drawer is the paid, sentence-in / players-out upgrade. Pro-gated at the button.
const _NL_MKT_LABEL = {HR_anytime:"HR", "HRR_over_1.5":"HRR", hits:"1+ H", "hits_1.5":"2+ H", total_bases:"TB"}
const _NL_EXAMPLES = [
  "lefty power bats vs righties facing homer-prone arms in hitter parks",
  "hot yankees hitters for 2+ hits tonight",
  "high-barrel righties vs lefties in launch pads",
  "best total base plays with hard contact",
]
function SmartSearchDrawer({onPlayer}){
  const [q,setQ]       = useState("")
  const [busy,setBusy] = useState(false)
  const [res,setRes]   = useState(null)   // {query,filter,market,count,picks} | {error}
  const inputRef = useRef(null)
  const run = async(query)=>{
    const s = (query!=null?query:q).trim()
    if(!s){ return }
    setBusy(true)
    try{ const r = await getNlSearch(s); setRes(r||{picks:[]}) }
    catch(e){ setRes({error:String(e?.message||e), picks:[]}) }
    finally{ setBusy(false) }
  }
  const useExample = ex => { setQ(ex); run(ex) }
  const flt = res?.filter || {}
  // Human-readable chips describing what the query was understood to mean — so the user
  // sees the search "got it" (or can rephrase if a chip is wrong).
  const chips = []
  if(res){
    if(flt.player)   chips.push(flt.player)
    if(flt.market) chips.push(_NL_MKT_LABEL[flt.market] || flt.market)
    if(flt.bats)     chips.push(`${flt.bats==="L"?"Lefty":flt.bats==="R"?"Righty":"Switch"} bats`)
    if(flt.opp_hand) chips.push(`vs ${flt.opp_hand==="L"?"LHP":"RHP"}`)
    if(flt.team)     chips.push(flt.team)
    if(flt.min_barrel!=null)   chips.push(`${Math.round(flt.min_barrel*100)}%+ barrel`)
    if(flt.min_hard_hit!=null) chips.push(`${Math.round(flt.min_hard_hit*100)}%+ hard-hit`)
    if(flt.min_fb!=null)       chips.push(`${Math.round(flt.min_fb*100)}%+ FB`)
    if(flt.min_hr9!=null)      chips.push(`arm HR/9 ≥ ${flt.min_hr9}`)
    if(flt.min_park!=null)     chips.push(`park ≥ ${flt.min_park}×`)
    if(flt.min_prob!=null)     chips.push(`prob ≥ ${Math.round(flt.min_prob*100)}%`)
    if(flt.hot)                chips.push("hot form")
  }
  const uiMkt = _NL_MKT_LABEL[res?.market] || "HR"
  const open = r => onPlayer && onPlayer({ name:r.name, player_id:r.player_id, market:uiMkt,
    prob:(r.prob!=null?r.prob/100:undefined),
    opp:r.opp_pitcher?`${r.team} · vs ${r.opp_pitcher}${r.opp_hand?` (${r.opp_hand})`:""}`:r.team })
  const rows = res?.picks || []
  return (
    <div style={{border:`1px solid ${T.border2}`,borderRadius:12,marginBottom:12,overflow:"hidden",background:T.bg,padding:"12px 12px 14px"}}>
      <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:9}}>
        Ask in plain English and the model finds your bats — handedness, matchup, contact quality, park &amp; form, all in one sentence.
      </div>
      <div style={{position:"relative"}}>
        <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter") run() }}
          placeholder="e.g. lefty power bats vs righties in hitter parks…"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"11px 40px 11px 12px",color:T.text,fontSize:13.5,outline:"none"}}/>
        <button onClick={()=>run()} disabled={busy||!q.trim()} title="Search"
          style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",background:q.trim()?T.green:T.card,border:`1px solid ${q.trim()?T.green:T.border2}`,borderRadius:8,padding:"6px 9px",color:q.trim()?"#06210f":T.muted2,fontSize:13,fontWeight:900,cursor:q.trim()?"pointer":"default",lineHeight:1}}>
          {busy?"…":"→"}
        </button>
      </div>
      {!res && !busy && (
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:9}}>
          {_NL_EXAMPLES.map((ex,i)=>(
            <button key={i} onClick={()=>useExample(ex)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"5px 10px",color:T.muted2,fontSize:11,fontWeight:600,cursor:"pointer",textAlign:"left"}}>“{ex}”</button>
          ))}
        </div>
      )}
      {chips.length>0 && (
        <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:10}}>
          <span style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.1em",color:T.muted,alignSelf:"center"}}>READS AS</span>
          {chips.map((c,i)=>(
            <span key={i} style={{fontSize:10.5,fontWeight:800,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:6,padding:"2px 7px"}}>{c}</span>
          ))}
        </div>
      )}
      {res && (
        <div style={{fontSize:11,color:T.muted,marginTop:10}}>
          {res.error ? <span style={{color:T.amber}}>Search hiccuped — try rephrasing.</span>
            : `${res.count||0} match${(res.count||0)===1?"":"es"}${rows.length<(res.count||0)?` · showing top ${rows.length}`:""}`}
        </div>
      )}
      {busy && [0,1,2].map(i=>(<div key={i} style={{height:56,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginTop:8,opacity:0.45}}/>))}
      {!busy && res && !res.error && rows.length===0 && (
        <div style={{textAlign:"center",color:T.muted,padding:"26px 14px",fontSize:12.5,lineHeight:1.6}}>No bats fit that tonight.<br/>Try loosening the ask, or search a different market.</div>
      )}
      {!busy && rows.map((r,i)=>(
        <button key={(r.player_id||r.name)+'-'+i} onClick={()=>open(r)}
          style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${i<3?T.greenMid:T.border}`,borderRadius:10,padding:"10px 12px",marginTop:8}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",alignItems:"baseline",gap:7,minWidth:0}}>
              <span style={{fontSize:13,fontWeight:800,color:i<3?T.green:T.muted,fontFamily:T.mono}}>#{i+1}</span>
              <span style={{fontSize:14.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
              <span style={{fontSize:10.5,color:T.muted}}>{r.team}{r.bats?` · ${r.bats}HB`:""}</span>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:16,fontWeight:900,color:T.green,lineHeight:1,fontFamily:T.mono}}>{r.prob!=null?`${r.prob}%`:"—"}</div>
              <div style={{fontSize:8,fontWeight:800,letterSpacing:"0.1em",color:T.muted}}>{uiMkt}</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontSize:11,color:T.muted2,marginTop:5}}>
            {r.opp_pitcher && <span>vs {r.opp_pitcher}{r.opp_hand?` (${r.opp_hand})`:""}</span>}
            {r.park!=null && <span style={{color:T.muted}}>· {r.park}× park</span>}
            {r.barrel!=null && <span style={{color:T.muted}}>· {r.barrel}% barrel</span>}
            {r.rating!=null && <span style={{color:T.muted}}>· R{r.rating}</span>}
          </div>
        </button>
      ))}
    </div>
  )
}

// ── Value Board (Pro): "Beat the Book". Today's priced plays across every market,
// ranked by de-vigged edge (model prob − book's implied prob). This is the CLV surface:
// the plain rating board tops out on high-probability plays that the market has already
// priced, while the +edge plays are where the model is actually beating the number.
// Powered by /free/value-board. Void/benched plays are dropped server-side.
function ValueBoardDrawer({onPlayer, onGlossary}){
  const [minEdge,setMinEdge] = useState(0.02)   // de-vigged edge floor (fraction)
  const { data, loading } = useFetch(()=>getValueBoard(minEdge, todayStr()).catch(e=>({error:String(e?.message||e),picks:[]})), [minEdge])
  const rows = data?.picks || []
  const edgeColor = e => e>=8?T.green : e>=5?"#9bd17a" : T.muted2
  const open = r => onPlayer && onPlayer({ name:r.name, player_id:r.player_id, market:r.market,
    prob:(r.prob!=null?r.prob/100:undefined),
    opp:r.opp_pitcher?`${r.team} · vs ${r.opp_pitcher}${r.opp_hand?` (${r.opp_hand})`:""}`:r.team })
  const EDGES = [[0.02,"2%+"],[0.05,"5%+"],[0.08,"8%+"]]
  const fmtOdds = o => o==null?"—":(o>0?`+${o}`:`${o}`)
  return (
    <div style={{border:`1px solid ${T.border2}`,borderRadius:12,marginBottom:12,overflow:"hidden",background:T.bg,padding:"12px 12px 14px"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,marginBottom:9}}>
        <div style={{fontSize:11,color:T.muted,lineHeight:1.5,flex:1}}>
          Every market's plays ranked by <b style={{color:T.text}}>edge</b> — how far the model's probability beats the book's de-vigged price. This is where the value lives, not the top of the rating board.
        </div>
        <HelpChip onGlossary={onGlossary} section="scores"/>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
        <span style={{fontSize:9.5,fontWeight:800,letterSpacing:"0.1em",color:T.muted,alignSelf:"center"}}>MIN EDGE</span>
        {EDGES.map(([v,lbl])=>(
          <button key={v} onClick={()=>setMinEdge(v)}
            style={{fontSize:11,fontWeight:800,cursor:"pointer",borderRadius:14,padding:"4px 11px",border:`1px solid ${minEdge===v?T.greenMid:T.border2}`,background:minEdge===v?T.greenDim:T.card,color:minEdge===v?T.green:T.muted2}}>{lbl}</button>
        ))}
      </div>
      <div style={{fontSize:11,color:T.muted,marginTop:8}}>
        {loading ? "Scanning the board…"
          : data?.error ? <span style={{color:T.amber}}>Couldn't load the value board — try again shortly.</span>
          : `${data?.count||0} +edge play${(data?.count||0)===1?"":"s"}${rows.length<(data?.count||0)?` · showing top ${rows.length}`:""}${data?.date?` · ${data.date}`:""}`}
      </div>
      {loading && [0,1,2].map(i=>(<div key={i} style={{height:62,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,marginTop:8,opacity:0.45}}/>))}
      {!loading && !data?.error && rows.length===0 && (
        <div style={{textAlign:"center",color:T.muted,padding:"26px 14px",fontSize:12.5,lineHeight:1.6}}>No plays clear that edge today.<br/>Lower the floor, or the book's lines may still be loading.</div>
      )}
      {!loading && rows.map((r,i)=>(
        <button key={(r.player_id||r.name)+'-'+r.market+'-'+i} onClick={()=>open(r)}
          style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${i<3?T.greenMid:T.border}`,borderRadius:10,padding:"10px 12px",marginTop:8}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{display:"flex",alignItems:"baseline",gap:7,minWidth:0}}>
              <span style={{fontSize:13,fontWeight:800,color:i<3?T.green:T.muted,fontFamily:T.mono}}>#{i+1}</span>
              <span style={{fontSize:14.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.name}</span>
              <span style={{fontSize:10,fontWeight:800,color:T.muted2,background:T.bg,border:`1px solid ${T.border2}`,borderRadius:5,padding:"1px 6px",whiteSpace:"nowrap"}}>{r.market}</span>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:16,fontWeight:900,color:edgeColor(r.edge),lineHeight:1,fontFamily:T.mono}}>{r.edge!=null?`+${r.edge}%`:"—"}</div>
              <div style={{fontSize:8,fontWeight:800,letterSpacing:"0.1em",color:T.muted}}>EDGE</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontSize:11,color:T.muted2,marginTop:6}}>
            <span style={{color:T.text,fontWeight:700,fontFamily:T.mono}}>{r.prob!=null?`${r.prob}%`:"—"}</span>
            <span style={{color:T.muted}}>model</span>
            <span style={{color:T.muted}}>vs</span>
            <span style={{fontFamily:T.mono}}>{r.implied!=null?`${r.implied}%`:"—"}</span>
            <span style={{color:T.muted}}>book</span>
            {r.odds!=null && <span style={{color:T.muted}}>· {fmtOdds(r.odds)}</span>}
            {r.rating!=null && <span style={{color:T.muted}}>· R{r.rating}</span>}
          </div>
          {r.opp_pitcher && <div style={{fontSize:10.5,color:T.muted2,marginTop:3}}>{r.team} · vs {r.opp_pitcher}{r.opp_hand?` (${r.opp_hand})`:""}</div>}
        </button>
      ))}
    </div>
  )
}

// ── HR Signal Board (Pro): today's slate ranked by the TRUE homer signal —
// elite bat (barrel/pull/FB/hard-hit/xwOBA) + matchup (arm HR/9, OPS-vs-hand).
// Park is shown for context but DELIBERATELY excluded from the score, so a
// launch-pad can't fake a top spot. Powered by /free/hr-signals.
function HrSignalsTab({onPlayer, onGlossary}){
  // Do NOT swallow the error here — a 402/500/network failure must reach the empty state
  // so the board can say "couldn't load" / "Pro feature" instead of the misleading
  // "builds with the morning slate", which hid a real failure as a no-slate day.
  const { data, loading, error, refetch } = useFetch(()=>getHrSignals(todayStr(), 25), [])
  const [q,setQ] = useState("")
  const all = data?.top || []
  const _q = _deacc(q.trim().toLowerCase())
  const rows = _q ? all.filter(r=>[r.player,r.team,r.opp_pitcher].some(f=>_deacc(String(f||"").toLowerCase()).includes(_q))) : all
  const pct = v => v==null ? "—" : `${Math.round(v*100)}%`
  const scColor = s => s>=75?T.green:s>=60?"#9bd17a":T.muted2
  const diag = data?.diagnostics
  // Degraded mode: bat (savant) data is stale/missing, so every shown bat is scored on the
  // matchup tier only. Tell the user rather than showing a confusing board of "—" tiles.
  const degraded = diag && diag.shown>0 && diag.matchup_only===diag.shown
  const open = r => onPlayer({ name:r.player, player_id:r.player_id, market:"HR",
    opp:`${r.team} · vs ${r.opp_pitcher}` })
  return (
    <div style={{padding:"0 0 12px"}}>
      <div style={{padding:"12px 14px 6px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:20,fontWeight:900,letterSpacing:"-0.02em"}}>HR Signal Board</span>
          <span style={{fontSize:17}}>💣</span>
          <div style={{marginLeft:"auto"}}><HelpChip onGlossary={onGlossary} section="scores"/></div>
        </div>
        <div style={{fontSize:12,color:T.muted,marginTop:3,lineHeight:1.4}}>Ranked by true homer signal — elite bat + matchup. <b style={{color:T.text}}>Park is stripped out of the score</b>, so no launch pad can fake a top spot.</div>
      </div>
      <div style={{padding:"0 14px 8px",position:"relative"}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search hitter, team, or pitcher…"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 30px 9px 12px",color:T.text,fontSize:13,outline:"none"}}/>
        {q && <button onClick={()=>setQ("")} style={{position:"absolute",right:22,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:T.muted2,fontSize:16,cursor:"pointer",lineHeight:1,padding:4}}>×</button>}
      </div>
      <div style={{fontSize:11,color:T.muted,padding:"0 14px 8px"}}>{loading?"Loading…":`${rows.length} bat${rows.length===1?"":"s"}${data?.date?` · ${data.date}`:""}`}</div>
      {degraded && !loading && (
        <div style={{margin:"0 14px 10px",padding:"9px 12px",background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:10,fontSize:11.5,color:T.amber,lineHeight:1.5}}>
          ⚠️ Bat data (barrel / hard-hit) is refreshing — showing the <b>matchup signal</b> (arm HR/9 + OPS-vs-hand) for now. Full reads return once savant updates.
        </div>
      )}
      <div style={{padding:"0 14px"}}>
        {loading && [0,1,2,3].map(i=>(<div key={i} style={{height:104,background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:8,opacity:0.45}}/>))}
        {!loading && !rows.length && (()=>{
          const box = {textAlign:"center",color:T.muted,padding:"50px 16px",fontSize:13,lineHeight:1.6}
          const retry = <button onClick={refetch} style={{marginTop:10,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"6px 14px",color:T.text,fontSize:12,fontWeight:700,cursor:"pointer"}}>Retry</button>
          if (_q) return <div style={box}>No bats matching “{q.trim()}”.</div>
          // A real failure — show it, don't disguise it as a quiet no-slate day.
          if (error) {
            const gated = /\b40[12]\b/.test(String(error))
            return <div style={box}>{gated ? <>The HR Signal Board is a Pro feature.</> : <>Couldn’t load the HR board right now.<br/>{retry}</>}</div>
          }
          // Backend answered but nothing cleared the board → say WHY (never a silent 0).
          if (diag && (diag.projections||0) > 0) {
            return <div style={box}>No bats cleared the board yet.<br/>
              <span style={{fontSize:11,color:T.muted2}}>{diag.projections} projected · {diag.dropped_benched||0} not in lineup · {diag.dropped_no_signal||0} no signal data</span><br/>{retry}</div>
          }
          return <div style={box}>No HR signal board for today yet.<br/>Builds with the morning slate.</div>
        })()}
        {!loading && rows.map((r,i)=>(
          <button key={(r.player_id)+'-'+i} onClick={()=>open(r)} style={{width:"100%",textAlign:"left",cursor:"pointer",color:T.text,background:T.card,border:`1px solid ${i<3?T.greenMid:T.border}`,borderRadius:12,padding:"12px 14px",marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginBottom:4}}>
              <div style={{display:"flex",alignItems:"baseline",gap:8,minWidth:0}}>
                <span style={{fontSize:14,fontWeight:800,color:i<3?T.green:T.muted,fontFamily:T.mono}}>#{i+1}</span>
                <span style={{fontSize:15.5,fontWeight:800,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{r.player}</span>
                <span style={{fontSize:11,color:T.muted}}>{r.team}{r.bats?` · ${r.bats}HB`:""}</span>
                {r.partial && <span title="Scored on matchup only — bat data (barrel/hard-hit) refreshing" style={{fontSize:8.5,fontWeight:800,color:T.amber,background:T.amberDim,border:`1px solid ${T.amber}`,borderRadius:5,padding:"1px 5px",whiteSpace:"nowrap"}}>matchup only</span>}
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:20,fontWeight:900,color:scColor(r.signal_score),lineHeight:1,fontFamily:T.mono}}>{r.signal_score}</div>
                <div style={{fontSize:8.5,fontWeight:800,letterSpacing:"0.1em",color:T.muted}}>SIGNAL</div>
              </div>
            </div>
            {(()=>{ const wx=r.weather; const chip={fontSize:10,fontWeight:800,color:T.green,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:6,padding:"2px 7px",whiteSpace:"nowrap"};
              const goodPark = r.park>=1.05;
              const wxOut = wx && wx.wind_rel && String(wx.wind_rel).toLowerCase().includes("out");
              const wxTitle = wx ? `Good HR weather${wx.temp_f?` · ${Math.round(wx.temp_f)}°F`:""}${(wx.wind_rel&&wx.wind_rel!=="dome")?` · wind ${wx.wind_rel}`:""}${wx.hr_impact!=null?` · +${Math.round(wx.hr_impact*100)}% HR`:""}` : "";
              return (
              <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontSize:11.5,color:T.muted2,marginBottom:9}}>
                <span>vs {r.opp_pitcher}</span>
                <span style={{color:T.muted}}>· {r.park}× park</span>
                {goodPark && <span title={`Hitter-friendly park (${r.park}× HR)`} style={chip}>🔥 park</span>}
                {wx?.good && <span title={wxTitle} style={chip}>{wxOut?"🌬️":"☀️"} {wxOut?"wind out":"warm"}</span>}
              </div>
              ) })()}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
              <Stat label="Barrel" value={pct(r.signals?.barrel)} color={r.signals?.barrel>=0.12?T.green:T.muted2}/>
              <Stat label="Hard-Hit" value={pct(r.signals?.hard_hit)} color={r.signals?.hard_hit>=0.45?T.green:T.muted2}/>
              <Stat label="Arm HR/9" value={r.signals?.hr9!=null?r.signals.hr9.toFixed(2):"—"} color={r.signals?.hr9>=1.3?T.green:T.muted2}/>
              <Stat label="OPS/hand" value={r.signals?.ops_hand!=null?r.signals.ops_hand.toFixed(3):"—"} color={r.signals?.ops_hand>=0.8?T.green:T.muted2}/>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Signals: Hot Corner (free) + HR Signals (Pro) + Trends (Pro) under one tab ─
// Clearly-labeled modes. Hot Corner = batter-vs-pitcher career edges for tonight;
// HR Signals = de-parked homer board; Trends = last-7-day hot bats / cold arms.
// HR Signals + Trends are Pro-gated: a non-Pro tapping gets the upgrade sheet and
// the view stays on Hot Corner.
function SignalsTab({onPlayer, onGlossary}){
  const { isPro, needPro } = useProGate()
  const [seg,setSeg] = useState("hot")   // hot (free) | hr (Pro) | trends (Pro)
  const pickTrends = ()=>{ if(!isPro){ needPro(); return } setSeg("trends") }
  const pickHr = ()=>{ if(!isPro){ needPro(); return } setSeg("hr") }
  const modeBtn = (on,label,sub,onClick,locked) => (
    <button onClick={onClick} style={{flex:1,textAlign:"left",cursor:"pointer",border:`1px solid ${on?T.green:T.border2}`,background:on?T.greenDim:T.card,borderRadius:11,padding:"9px 11px"}}>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        <span style={{fontSize:13,fontWeight:900,color:on?T.green:T.text}}>{label}</span>
        {locked && <span style={{fontSize:8.5,fontWeight:800,color:T.amber,background:T.amberDim,borderRadius:5,padding:"1px 5px",letterSpacing:"0.04em"}}>PRO</span>}
      </div>
      <div style={{fontSize:10,color:T.muted,marginTop:2,lineHeight:1.3}}>{sub}</div>
    </button>
  )
  return (
    <div>
      <div style={{display:"flex",gap:8,padding:"12px 14px 2px"}}>
        {modeBtn(seg==="hot","🔥 Hot Corner","Owns tonight's pitcher",()=>setSeg("hot"),false)}
        {modeBtn(seg==="hr","💣 HR Signals","Bat-driven · park-stripped",pickHr,!isPro)}
        {modeBtn(seg==="trends","📈 Trends","Hot bats & arms · 7d",pickTrends,!isPro)}
      </div>
      {seg==="hot"
        ? <HotCornerTab onPlayer={onPlayer} onGlossary={onGlossary}/>
        : seg==="hr"
        ? <HrSignalsTab onPlayer={onPlayer} onGlossary={onGlossary}/>
        : <TrendsTab onPlayer={onPlayer} onGlossary={onGlossary}/>}
    </div>
  )
}

// ── Bottom nav ──────────────────────────────────────────────────────────────
function NavIcon({type,color}){
  const c={stroke:color,fill:"none",strokeWidth:2,strokeLinecap:"round",strokeLinejoin:"round"}
  if(type==="home") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>
  if(type==="picks") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><polygon points="12 2 15 9 22 9.3 16.5 14 18.5 21 12 17 5.5 21 7.5 14 2 9.3 9 9"/></svg>
  if(type==="value") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
  if(type==="games") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><circle cx="12" cy="12" r="10"/><path d="M5 5c3.5 3 3.5 11 0 14M19 5c-3.5 3-3.5 11 0 14"/></svg>
  if(type==="trends") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><polyline points="3 17 9 11 13 15 21 7"/><polyline points="15 7 21 7 21 13"/></svg>
  if(type==="record") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></svg>
  if(type==="hotcorner") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M12 3l9 9-9 9-9-9z"/><path d="M9 12l2 2 4-4"/></svg>
  if(type==="signals") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M12 2l9 9-9 9-9-9z"/><polyline points="8 12 11 15 16 9" fill="none"/></svg>
  if(type==="analytics") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
  if(type==="screener") return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><line x1="4" y1="6" x2="20" y2="6"/><circle cx="9" cy="6" r="2.2" fill={color}/><line x1="4" y1="12" x2="20" y2="12"/><circle cx="15" cy="12" r="2.2" fill={color}/><line x1="4" y1="18" x2="20" y2="18"/><circle cx="8" cy="18" r="2.2" fill={color}/></svg>
  return <svg width="20" height="20" viewBox="0 0 24 24" {...c}><path d="M3 3h18v4H3zM3 10h18v11H3z"/><path d="M8 14h8"/></svg>
}
function BottomNav({view,setView}){
  const { isPro } = useProGate()   // Contact + Screener are full-Pro only
  // Screener is no longer its own tab — it lives as the 🔎 drawer inside Analytics.
  const all=[["home","Home"],["picks","Rankings"],["trends","Trends"],["games","Games"],["signals","Signals"],["analytics","Analytics"],["contact","Scouting"],["record","Record"]]
  const items = isPro ? all : all.filter(([id])=> id!=="contact")
  return (
    <nav style={{position:"fixed",bottom:0,left:0,right:0,zIndex:200,maxWidth:460,margin:"0 auto",background:"#0D1117",borderTop:`1px solid ${T.border}`,display:"flex",paddingBottom:"env(safe-area-inset-bottom,0)"}}>
      {items.map(([id,label])=>{ const on=view===id; const col=on?T.green:T.muted; return (
        <button key={id} onClick={()=>setView(id)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"9px 4px 7px",border:"none",background:"none",cursor:"pointer",color:col,fontSize:10,fontWeight:700,letterSpacing:"0.04em",position:"relative"}}>
          {on&&<div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:26,height:2,background:T.green,borderRadius:"0 0 4px 4px"}}/>}
          <NavIcon type={id} color={col}/>{label}
        </button>)})}
    </nav>
  )
}

// ── Admin panel — formula health at a glance ────────────────────────────────
// Surfaces the two diagnostics as readable tables: (1) Validation Scorecard —
// is each bet type honest (calibration), beating the close (CLV), profitable
// (ROI)? with a verdict; (2) Coverage — is each formula actually running &
// returning complete rows for today's slate?
const _VERDICT_C = {
  "PROVEN":{c:T.green,bg:T.greenDim}, "PROMISING":{c:"#34D399",bg:T.greenDim},
  "NEEDS WORK":{c:T.amber,bg:T.amberDim}, "INSUFFICIENT DATA":{c:T.muted2,bg:T.card},
}
const _STATUS_C = {
  "OK":{c:T.green,bg:T.greenDim}, "NO_ODDS":{c:"#34D399",bg:T.greenDim},
  "DEGRADED":{c:T.amber,bg:T.amberDim}, "BROKEN":{c:T.red,bg:T.redDim},
  "MISSING":{c:T.red,bg:T.redDim}, "UNEXPECTED":{c:T.purple,bg:T.card},
}
const _pctA = x => x==null ? "—" : `${Math.round(x*100)}%`
const _sgnA = x => x==null ? "—" : `${x>=0?"+":""}${(x*100).toFixed(1)}`
const _SEV_C = { critical:{c:T.red,bg:T.redDim}, warn:{c:T.amber,bg:T.amberDim}, info:{c:"#34D399",bg:T.greenDim} }
// One example row → a compact human line, whatever fields the finding carries.
const _auditEx = e => {
  if(e.team && e.market && e.count!=null && e.of!=null) return `${e.team} — ${e.count} of top ${e.of} ${e.market}${e.venue?` @ ${e.venue}`:""}`
  if(e.player) return `${e.player}${e.team?` (${e.team})`:""} · ${e.market}${e.prob!=null?` · ${Math.round(e.prob*100)}%`:""}${e.pitcher_adj!=null?` · adj ${e.pitcher_adj}`:""}${e.lam!=null?` · λ${e.lam}`:""}`
  if(e.venue) return `${e.venue} · ${e.park_factor}x`
  if(e.prop_type) return `${e.prop_type} ×${e.count}`
  if(e.prob!=null) return `prob ${String(e.prob).replace(/^0/,"")} shared by ${e.count} picks`
  if(e.player_id!=null) return `player ${e.player_id} · ${e.market} ×${e.count}`
  return JSON.stringify(e)
}
// ── Referrals (cross-sport) — see who referred paying subscribers, what each has earned
// ($20 per 5 who stay a paid month) and what's owed; log a payout, mint a named code, or
// credit a word-of-mouth referral by hand. Same data the NFL Ops panel shows (it spans
// every sport); surfaced here so the owner sees progress from the all-sports app too.
function ReferralsAdmin(){
  const [open,setOpen] = useState(false)
  const [data,setData] = useState(null)       // null=unloaded, {}=loading
  const [newCode,setNewCode] = useState("")
  const [newLabel,setNewLabel] = useState("")
  const [msg,setMsg] = useState("")
  const load = async () => { setData({}); setData(await getReferralsAdmin()) }
  const openIt = () => { if(!open){ setOpen(true); load() } else setOpen(false) }
  const create = async () => { const c=newCode.trim(); if(!c) return; setMsg("Creating…")
    const r = await makeRefCode(c, newLabel.trim())
    if(r.ok){ setMsg(`✓ ${r.display||c} ready — share linebrkr.app/?ref=${r.code}`); setNewCode(""); setNewLabel(""); load() } else setMsg(`✕ ${r.detail||"Could not create"}`) }
  const markPaid = async (t) => { const def=t.owed>0?t.owed:t.earned
    const amt = window.prompt(`Log a payment to "${t.ref_code}" (owed $${t.owed}). Amount in $:`, String(def)); if(amt==null) return
    const n = parseFloat(amt); if(!(n>0)) return
    const note = window.prompt("Note (how paid)? optional:", "venmo")||""
    setMsg("Logging…"); const r = await referralPayout(t.ref_code, n, note)
    setMsg(r.ok?`✓ Logged $${r.logged} to ${t.ref_code} — now owes $${r.owed}`:`✕ ${r.detail||"Failed"}`); load() }
  const addManual = async () => { const rc=(window.prompt("Credit a referral to which code? (e.g. CJ01)")||"").trim(); if(!rc) return
    const nm=(window.prompt(`Who did ${rc.toUpperCase()} refer? (name)`)||"").trim(); if(!nm) return
    const q = window.confirm(`Did ${nm} join and stay a paid month? OK = yes (counts toward $20), Cancel = pending.`)
    setMsg("Adding…"); const r = await attributeReferral(rc, nm, q)
    setMsg(r.ok?`✓ Credited ${r.added} to ${r.code} — ${r.total_qualified} qualified, $${r.earned} earned`:`✕ ${r.detail||"Failed"}`); load() }
  const refs=(data&&data.referrers)||[]; const owed=(data&&data.total_owed)||0; const err=data&&data._error
  const th={textAlign:"right",padding:"6px 6px",fontSize:9,color:T.muted,fontWeight:800,textTransform:"uppercase",letterSpacing:"0.04em",whiteSpace:"nowrap"}
  const td={textAlign:"right",padding:"7px 6px",fontFamily:T.mono,fontSize:12,whiteSpace:"nowrap"}
  return (
    <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:12,padding:"14px",marginBottom:16}}>
      <button onClick={openIt} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,background:"none",border:"none",color:T.text,cursor:"pointer",padding:0}}>
        <span style={{fontSize:14,fontWeight:900}}>🎁 Referrals{owed>0?<span style={{color:T.green,fontWeight:800,fontSize:12}}> · ${owed} owed</span>:null}</span>
        <span style={{color:T.muted2,fontSize:12}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div style={{marginTop:10}}>
          <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:10}}>$20 for every 5 referrals who stay a paid month (any sport). <b style={{color:T.text}}>Qualified</b> = past their first paid month. Tap a row to log a payment.</div>
          <div style={{display:"flex",gap:6,marginBottom:6}}>
            <input value={newCode} onChange={e=>setNewCode(e.target.value)} placeholder="New code (e.g. CJ01)" autoCapitalize="characters" autoCorrect="off" spellCheck={false}
              style={{flex:1,minWidth:0,boxSizing:"border-box",padding:"9px 10px",fontSize:12.5,background:T.card,color:T.text,border:`1px solid ${T.border2}`,borderRadius:8,letterSpacing:"0.04em"}}/>
            <input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="Name" style={{width:86,boxSizing:"border-box",padding:"9px 10px",fontSize:12.5,background:T.card,color:T.text,border:`1px solid ${T.border2}`,borderRadius:8}}/>
            <button onClick={create} disabled={!newCode.trim()} style={{flexShrink:0,padding:"0 14px",borderRadius:8,cursor:newCode.trim()?"pointer":"default",fontSize:12.5,fontWeight:800,border:"none",background:T.green,color:"#04140D",opacity:newCode.trim()?1:0.5}}>Create</button>
          </div>
          <button onClick={addManual} style={{width:"100%",marginBottom:8,padding:"9px 0",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:800,border:`1px solid ${T.border2}`,background:T.card,color:T.green}}>+ Credit a referral by hand</button>
          {msg && <div style={{fontSize:11,color:msg[0]==="✕"?T.amber:T.green,marginBottom:8,lineHeight:1.4}}>{msg}</div>}
          {data && Object.keys(data).length===0 ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Loading…</div>
            : err ? <div style={{color:T.amber,fontSize:12,padding:"6px 0"}}>Couldn’t load ({err}). Set your admin code in Beta Codes below.</div>
            : refs.length===0 ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>No referral codes yet — create one above.</div>
            : (<div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr><th style={{...th,textAlign:"left"}}>Code</th><th style={th}>Qual</th><th style={th}>Owed</th><th style={th}>Earned</th><th style={th}>Paid subs</th><th style={th}>Free</th></tr></thead>
                <tbody>{refs.map(t=>(
                  <tr key={t.ref_code} onClick={()=>markPaid(t)} style={{borderTop:`1px solid ${T.border}`,cursor:"pointer"}}>
                    <td style={{...td,textAlign:"left",fontWeight:800,textTransform:"uppercase"}}>{t.ref_code}</td>
                    <td style={{...td,color:T.green,fontWeight:800}}>{t.qualified||0}</td>
                    <td style={{...td,color:t.owed>0?T.green:T.muted,fontWeight:800}}>${t.owed||0}</td>
                    <td style={td}>${t.earned||0}</td><td style={td}>{t.paid_active||0}</td><td style={td}>{t.referred||0}</td>
                  </tr>))}</tbody></table>
                <div style={{fontSize:9.5,color:T.faint,marginTop:8,lineHeight:1.4}}>Owed = earned − already paid. Tap a row to record a payout. Counts every sport.</div>
              </div>)}
        </div>
      )}
    </div>
  )
}

// ── Refer & Earn (user-facing) — every user's $20 referral code + progress, opened from the
// 🎁 header button. Mirrors the NFL app so a referrer sees the same thing on any sport.
function UserReferCard(){
  const [r,setR] = useState(null)
  const [copied,setCopied] = useState(false)
  useEffect(()=>{ let a=true; getReferral().then(d=>{ if(a&&d) setR(d) }); return ()=>{ a=false } }, [])
  const base = {marginTop:14,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:16,textAlign:"center",color:T.muted,fontSize:12.5,lineHeight:1.5}
  if(!r) return <div style={base}>Loading your referral link…</div>
  if(!r.ref_code) return <div style={base}>Your referral link will appear here once your account is set up.</div>
  const link = `${window.location.origin}/?ref=${r.ref_code}`
  const every=r.reward_every||5, per=r.reward_per||20, qualified=r.qualified||0, earned=r.earned||0, toNext=r.to_next||every
  const copy=async()=>{ try{ await navigator.clipboard.writeText(link); setCopied(true); setTimeout(()=>setCopied(false),1600) }catch(_){} }
  const tile=(v,l,c)=>(<div style={{flex:1,background:T.card2,border:`1px solid ${T.border}`,borderRadius:9,padding:"8px 10px",textAlign:"center"}}>
    <div style={{fontSize:18,fontWeight:900,color:c,lineHeight:1,fontFamily:T.mono}}>{v}</div>
    <div style={{fontSize:9.5,color:T.muted,marginTop:3,letterSpacing:".03em"}}>{l}</div></div>)
  return (
    <div style={{marginTop:14,background:T.card,border:`1px solid ${T.border2}`,borderRadius:12,padding:14}}>
      <div style={{fontSize:14,fontWeight:800,marginBottom:3}}>Refer friends → earn ${per} 🎁</div>
      <div style={{fontSize:12,color:T.muted,marginBottom:10,lineHeight:1.45}}>Every <b style={{color:T.text}}>{every}</b> friends who join and stay a paid month = <b style={{color:T.green}}>${per}</b>. Any sport.</div>
      <div style={{display:"flex",gap:8,marginBottom:10}}>{tile(qualified,"QUALIFIED",T.green)}{tile("$"+earned,"EARNED",earned>0?T.green:T.text)}{tile(toNext,"TO NEXT $"+per,T.text)}</div>
      <div style={{display:"flex",gap:8}}>
        <input readOnly value={link} onFocus={e=>e.target.select()} style={{flex:1,minWidth:0,boxSizing:"border-box",background:T.card2,border:`1px solid ${T.border}`,borderRadius:9,padding:"10px 12px",color:T.muted,fontSize:12}}/>
        <button onClick={copy} style={{flexShrink:0,padding:"0 14px",borderRadius:9,cursor:"pointer",fontSize:13,fontWeight:800,border:"none",background:T.green,color:"#04140D"}}>{copied?"✓":"Copy"}</button>
      </div>
      <div style={{fontSize:10.5,color:T.faint,marginTop:8,lineHeight:1.4}}>{r.referred||0} signed up from your link so far. Rewards paid once a friend completes a paid month.</div>
    </div>
  )
}
function UserReferSheet({onClose}){
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>🎁 Refer &amp; Earn</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <UserReferCard/>
      </div>
    </Sheet>
  )
}

// ── Beta Codes — mint / revoke tester access codes, no terminal ─────────────
function BetaCodesAdmin(){
  const [adminCode,setAC] = useState(()=>{ try{ return localStorage.getItem('brkr_admin')||'' }catch{ return '' } })
  const [entry,setEntry] = useState("")
  const [rows,setRows] = useState(null)     // null = not loaded yet
  const [meta,setMeta] = useState({total:0,active:0})
  const [err,setErr] = useState("")
  const [label,setLabel] = useState("")
  const [busy,setBusy] = useState(false)
  const [copied,setCopied] = useState("")
  const [listOpen,setListOpen] = useState(false)   // users list collapsed by default; tap to expand
  const load = async () => {
    setErr("")
    try { const d = await listBetaCodes(); setRows(d.codes||[]); setMeta({total:d.total||0,active:d.active||0}) }
    catch(_) { setErr("That admin code didn't work — double-check the ADMIN_CODE you set in Railway."); setRows(null) }
  }
  useEffect(()=>{ if(adminCode) load() }, [])   // eslint-disable-line
  const saveCode = () => { const c=entry.trim(); if(!c) return; setAdminCode(c); setAC(c); setEntry(""); setTimeout(load,0) }
  const changeCode = () => { setAdminCode(""); setAC(""); setRows(null); setErr("") }
  const create = async () => {
    if(busy) return; setBusy(true); setErr("")
    try { const r = await createBetaCode(label.trim()); setLabel(""); await load(); if(r?.code){ setCopied(r.code); try{ await navigator.clipboard.writeText(r.code) }catch(_){}; setTimeout(()=>setCopied(""),2500) } }
    catch(_) { setErr("Couldn't create the code — check your admin code.") }
    setBusy(false)
  }
  const toggle = async (r) => {
    setBusy(true); setErr("")
    try { r.active ? await revokeBetaCode(r.id) : await enableBetaCode(r.id); await load() }
    catch(_) { setErr("Action failed — check your admin code.") }
    setBusy(false)
  }
  const copy = async (code) => { try{ await navigator.clipboard.writeText(code) }catch(_){}; setCopied(code); setTimeout(()=>setCopied(""),1500) }
  const ago = iso => { if(!iso) return "never"; try{ const s=(Date.now()-new Date(iso).getTime())/1000; if(s<90) return "just now"; if(s<3600) return `${Math.round(s/60)}m ago`; if(s<86400) return `${Math.round(s/3600)}h ago`; return `${Math.round(s/86400)}d ago` }catch{ return "—" } }

  if(!adminCode){
    return (
      <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:12,padding:"14px",marginBottom:16}}>
        <div style={{fontSize:14,fontWeight:900,marginBottom:4}}>🔑 Beta Codes</div>
        <div style={{fontSize:11.5,color:T.muted,lineHeight:1.5,marginBottom:10}}>Enter your <b style={{color:T.text}}>admin code</b> (the <span style={{fontFamily:T.mono}}>ADMIN_CODE</span> you set in Railway) to create and manage tester access codes.</div>
        <input type="password" value={entry} onChange={e=>setEntry(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") saveCode() }} placeholder="Admin code"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"10px 12px",color:T.text,fontSize:14,marginBottom:8}}/>
        <button onClick={saveCode} style={{width:"100%",background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:9,padding:"10px 0",color:T.green,fontSize:13,fontWeight:800,cursor:"pointer"}}>Unlock code manager</button>
        {err && <div style={{fontSize:11,color:T.red,marginTop:8,lineHeight:1.4}}>{err}</div>}
      </div>
    )
  }
  return (
    <div style={{background:T.card3,border:`1px solid ${T.border2}`,borderRadius:12,padding:"14px",marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <div style={{fontSize:14,fontWeight:900}}>🔑 Beta Codes <span style={{fontSize:11,color:T.muted,fontWeight:700}}>· {meta.active} active / {meta.total}</span></div>
        <button onClick={changeCode} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:7,padding:"3px 8px",color:T.muted,fontSize:10.5,fontWeight:700,cursor:"pointer"}}>change admin code</button>
      </div>
      <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:10}}>Make one code per tester. Tap a code to copy it, then text it to them — they enter it on the lock screen to get in.</div>
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <input value={label} onChange={e=>setLabel(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") create() }} placeholder="Who's it for? (e.g. Jake)"
          style={{flex:1,minWidth:0,background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"9px 11px",color:T.text,fontSize:13}}/>
        <button onClick={create} disabled={busy} style={{flexShrink:0,background:T.green,border:"none",borderRadius:9,padding:"9px 14px",color:"#04140D",fontSize:13,fontWeight:800,cursor:busy?"default":"pointer",opacity:busy?0.6:1}}>+ Create</button>
      </div>
      {err && <div style={{fontSize:11,color:T.red,marginBottom:8,lineHeight:1.4}}>{err}</div>}
      {rows===null ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Loading…</div>
       : rows.length===0 ? <div style={{color:T.muted,fontSize:12.5,padding:"6px 0"}}>No codes yet — create your first one above.</div>
       : (
        <>
        {/* Collapsed by default — tap to expand the full user list */}
        <button onClick={()=>setListOpen(v=>!v)} style={{width:"100%",display:"flex",alignItems:"center",gap:8,background:T.card,border:`1px solid ${T.border2}`,borderRadius:9,padding:"10px 12px",cursor:"pointer"}}>
          <span style={{fontSize:13,fontWeight:800,color:T.text}}>👥 Users <span style={{color:T.muted,fontWeight:700}}>· {rows.length}</span></span>
          <span style={{marginLeft:"auto",fontSize:11.5,color:T.green,fontWeight:800}}>{listOpen?"Hide ▾":"Show all ▸"}</span>
        </button>
        {listOpen && (
        <div style={{display:"flex",flexDirection:"column",gap:7,marginTop:8}}>
          {rows.map(r=>(
            <div key={r.id} style={{background:T.card,border:`1px solid ${r.active?T.border2:T.border}`,borderRadius:10,padding:"9px 11px",opacity:r.active?1:0.6}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                <button onClick={()=>copy(r.code)} title="Tap to copy" style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"flex",alignItems:"center",gap:7,minWidth:0}}>
                  <span style={{fontFamily:T.mono,fontSize:15,fontWeight:900,letterSpacing:"0.06em",color:r.active?T.text:T.muted}}>{r.code}</span>
                  <span style={{fontSize:10,color:copied===r.code?T.green:T.muted2}}>{copied===r.code?"✓ copied":"📋"}</span>
                </button>
                <button onClick={()=>toggle(r)} disabled={busy} style={{flexShrink:0,background:r.active?"transparent":T.greenDim,border:`1px solid ${r.active?T.border2:T.greenMid}`,borderRadius:7,padding:"4px 9px",color:r.active?T.red:T.green,fontSize:10.5,fontWeight:800,cursor:"pointer"}}>{r.active?"Revoke":"Enable"}</button>
              </div>
              <div style={{fontSize:10.5,color:T.muted,marginTop:4}}>{r.label||"—"} · {r.active?"active":"revoked"} · seen {ago(r.last_seen)} · {r.request_count||0} req</div>
            </div>
          ))}
        </div>
        )}
        </>
      )}
    </div>
  )
}

// One-tap data fixes — auth rides on the saved admin code (X-Admin-Code header),
// so the owner never has to build a ?key= URL by hand. Batter-hand sync + reslate.
function DataFixButtons(){
  const [busy,setBusy] = useState("")     // "" | "bats" | "pitch" | "reslate" | "lineups" | "odds" | "sethand"
  const [msg,setMsg]   = useState(null)   // {ok, text}
  const [odds,setOdds] = useState(null)   // odds-status payload
  const [pName,setPName] = useState("")   // manual pitcher-hand override name
  const [sName,setSName] = useState("")   // scratch-player name
  useEffect(()=>{ getOddsStatus().then(setOdds).catch(()=>{}) }, [])
  const doScratch = async () => {
    const nm = sName.trim()
    if(!nm){ setMsg({ok:false, text:"Type a player name to scratch."}); return }
    setBusy("scratch"); setMsg(null)
    try{
      const r = await scratchPlayer(nm)
      const cleared = r?.cleared || []
      const removed = cleared.reduce((a,c)=>a+(c.rows_removed||0),0)
      if(removed>0){
        const who = cleared.flatMap(c=>(c.matches||[])).filter(m=>m.game_date===r.date).map(m=>`${m.full_name} (${m.team})`)
        const uniq = [...new Set(who)]
        setMsg({ok:true, text:`Scratched ${uniq.join(", ")||nm} — removed ${removed} row${removed===1?"":"s"} across ALL bet types. Off the board and out of tonight's record.`})
        setSName("")
      } else {
        const near = (cleared[0]?.matches||[]).map(m=>m.full_name)
        const uniqNear = [...new Set(near)].slice(0,5)
        setMsg({ok:false, text:`No rows removed for "${nm}" — likely a spelling/name mismatch. On the slate matching that last name: ${uniqNear.join(", ")||"nobody"}. Try the exact stored name.`})
      }
    }catch(e){ setMsg({ok:false, text:`Failed: ${String(e?.message||e)}`}) }
    setBusy("")
  }
  const doSyncRosters = async () => {
    const nm = sName.trim()   // optional: a just-traded player to verify + slot in tonight
    setBusy("rosters"); setMsg(null)
    try{
      const r = await syncRosters(nm)
      if(r?.status==="ok"){
        let t = `Rosters refreshed — ${r.players_synced} players synced. Trades/call-ups are now current.`
        if(nm && (r.player||[]).length){
          const who = r.player.map(p=>`${p.full_name} → ${p.team}`).join(", ")
          const back = r.slate_readd?.readded?.length ? " Added to tonight's slate under the new team." : (r.slate_readd?.detail?` (${r.slate_readd.detail})`:"")
          t = `Rosters refreshed (${r.players_synced}). ${who}.${back}`
        } else if(nm){
          t = `Rosters refreshed (${r.players_synced}), but no player matched "${nm}".`
        }
        setMsg({ok:true, text:t})
        if(nm) setSName("")
      } else {
        setMsg({ok:false, text:r?.detail || "Roster sync failed."})
      }
    }catch(e){ setMsg({ok:false, text:`Failed: ${String(e?.message||e)}`}) }
    setBusy("")
  }
  const setHand = async (h) => {
    const nm = pName.trim()
    if(!nm){ setMsg({ok:false, text:"Type a pitcher name first."}); return }
    setBusy("sethand"); setMsg(null)
    try{
      const r = await setPitcherHand(nm, h)
      if(r?.error) setMsg({ok:false, text:r.error})
      else setMsg({ok:true, text:`Set ${nm} → ${r.now||h} (${r.rows_updated??0} row${(r.rows_updated===1)?"":"s"}). Now tap Reslate.`})
    }catch(e){ setMsg({ok:false, text:`Failed: ${String(e?.message||e)}`}) }
    setBusy("")
  }
  const doReadd = async () => {
    const nm = sName.trim()
    if(!nm){ setMsg({ok:false, text:"Type a player name to re-add."}); return }
    setBusy("readd"); setMsg(null)
    try{
      const r = await readdPlayer(nm)
      if(r?.status==="ok" && (r.readded||[]).length){
        const mk = (r.readded[0]?.markets||[]).join(", ")
        setMsg({ok:true, text:`Re-added ${nm} — re-projected ${r.readded.length} player${r.readded.length===1?"":"s"} (${mk}). Back on the board, no full reslate.`})
        setSName("")
      } else {
        setMsg({ok:false, text:r?.detail || `Couldn't re-add "${nm}" — check the name / that the team is playing today.`})
      }
    }catch(e){ setMsg({ok:false, text:`Failed: ${String(e?.message||e)}`}) }
    setBusy("")
  }
  const run = async (which, fn) => {
    setBusy(which); setMsg(null)
    try{
      const r = await fn()
      if(which==="bats" || which==="pitch"){
        const players = r?.players || []
        const n = r?.corrected ?? players.length
        const names = players.map(p=>`${p.name} ${p.was||"?"}→${p.now}`)
        const kind = which==="bats" ? "batter" : "pitcher"
        setMsg({ok:true, text: n>0
          ? `Fixed ${n} ${kind}${n>1?"s":""}: ${names.slice(0,8).join(", ")}${n>8?` +${n-8} more`:""}. Now tap Reslate.`
          : `All ${kind} hands already correct — nothing to fix.`})
      } else if(which==="odds"){
        const n = r?.projections_priced ?? 0
        setMsg({ok:n>0, text: r?.verdict || (n>0?`Priced ${n} props.`:"0 props priced.")})
        getOddsStatus().then(setOdds).catch(()=>{})
      } else if(which==="lineups"){
        const n = r?.rows_updated ?? 0
        setMsg({ok:true, text: r?.verdict || (n>0?`Updated ${n} lineup slots.`:"No lineup changes.")})
      } else {
        setMsg({ok:true, text:"Reslate started — today's board rebuilds in ~1–2 min with the corrected platoons."})
      }
    }catch(e){
      setMsg({ok:false, text:`Failed: ${String(e?.message||e)}`})
    }
    setBusy("")
  }
  const btn = (which, label, fn) => (
    <button onClick={()=>!busy && run(which, fn)} disabled={!!busy}
      style={{flex:1,padding:"10px",borderRadius:9,fontSize:12,fontWeight:800,cursor:busy?"default":"pointer",
        border:`1px solid ${T.greenMid}`,background:busy===which?T.card:T.greenDim,color:T.green,opacity:busy&&busy!==which?0.5:1}}>
      {busy===which?"Working…":label}
    </button>
  )
  const keyOk = odds?.key_configured
  return (
    <div style={{marginTop:14,background:T.card,border:`1px solid ${T.border2}`,borderRadius:11,padding:"11px 12px"}}>
      <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.06em",color:T.text,textTransform:"uppercase",marginBottom:3}}>Data fixes</div>
      <div style={{fontSize:10.5,color:T.muted,lineHeight:1.45,marginBottom:9}}>Sync batter &amp; pitcher handedness from MLB (fixes lefties shown as righties), then rebuild the board. No key needed — you're signed in as owner.</div>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        {btn("bats","🖐 Batter hands", fixBatsHandAll)}
        {btn("pitch","⚾ Pitcher hands", fixPitcherHandsAll)}
      </div>
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        {btn("reslate","♻️ Reslate board", reslateBoard)}
        {btn("lineups","🔄 Refresh lineups", refreshLineups)}
      </div>
      <div style={{fontSize:9.5,color:T.faint,marginBottom:8,lineHeight:1.4}}>Refresh lineups pulls the latest confirmed batting orders &amp; scratches (~5s, free MLB feed) — use it for a lineup that posted between the scheduled 3/6 PM passes.</div>
      {/* Odds health + manual pull */}
      {odds && <div style={{fontSize:10.5,lineHeight:1.5,color:keyOk?T.muted2:T.red,marginBottom:7}}>
        Odds key: <b style={{color:keyOk?T.green:T.red}}>{keyOk?"✓ set":"✗ NOT set — add ODDS_API_KEY in Railway"}</b>
        {keyOk && <> · <b style={{color:T.text}}>{odds.props_with_odds}/{odds.props_today}</b> props priced today</>}
      </div>}
      {/* Odds API credit burn — watch it while running us,us2; decide on us_ex from here. */}
      {odds?.credits && (odds.credits.remaining!=null || odds.credits.used!=null) && (()=>{ const c=odds.credits
        const rem=c.remaining, lo=rem!=null&&rem<1000
        return (
        <div style={{fontSize:10.5,lineHeight:1.5,marginBottom:7,padding:"7px 10px",borderRadius:8,background:lo?"#7F1D1D22":T.card2,border:`1px solid ${lo?"#7a3030":T.border2}`}}>
          <span style={{color:T.muted2}}>Odds credits: </span>
          <b style={{color:lo?T.red:T.green}}>{rem!=null?rem.toLocaleString():"—"} left</b>
          {c.used!=null && <span style={{color:T.muted}}> · {Number(c.used).toLocaleString()} used</span>}
          {c.regions && <span style={{color:T.muted}}> · regions <b style={{color:T.muted2}}>{c.regions}</b></span>}
          {c.last_cost!=null && <span style={{color:T.faint}}> · last pull {c.last_cost}</span>}
          {lo && <span style={{color:T.red,fontWeight:700}}> — running low, consider ODDS_REGIONS=us</span>}
          {c.last_run && <div style={{color:/preserved|skipped/.test(c.last_run)?T.amber:T.muted,fontSize:10,marginTop:3}}>Last run: {c.last_run}</div>}
        </div>)
      })()}
      <div style={{display:"flex",gap:8}}>
        {btn("odds","📈 Pull odds now", pullOdds)}
      </div>
      <div style={{fontSize:9.5,color:T.faint,marginTop:5,lineHeight:1.4}}>Pull odds spends ~75 API credits — use when lines are missing, don't spam.</div>
      {/* Manual hand override — for a rookie/call-up MLB can't auto-resolve (no ID). */}
      <div style={{marginTop:12,paddingTop:11,borderTop:`1px solid ${T.border2}`}}>
        <div style={{fontSize:9.5,color:T.muted,fontWeight:700,letterSpacing:"0.04em",marginBottom:6}}>MANUAL — set one pitcher's hand (for a rookie MLB can't resolve)</div>
        <div style={{display:"flex",gap:6}}>
          <input value={pName} onChange={e=>setPName(e.target.value)} placeholder="Pitcher name — e.g. Payton Tolle"
            style={{flex:1,minWidth:0,boxSizing:"border-box",background:T.bg,border:`1px solid ${T.border2}`,borderRadius:8,padding:"9px 10px",color:T.text,fontSize:12,outline:"none"}}/>
          <button onClick={()=>!busy&&setHand("L")} disabled={!!busy} style={{padding:"9px 14px",borderRadius:8,fontSize:12,fontWeight:800,cursor:busy?"default":"pointer",border:`1px solid ${T.greenMid}`,background:T.greenDim,color:T.green}}>Set L</button>
          <button onClick={()=>!busy&&setHand("R")} disabled={!!busy} style={{padding:"9px 14px",borderRadius:8,fontSize:12,fontWeight:800,cursor:busy?"default":"pointer",border:`1px solid ${T.border2}`,background:T.card,color:T.muted2}}>Set R</button>
        </div>
      </div>
      {/* Scratch a player — pre-game injury / late scratch. Removes ALL bet types. */}
      <div style={{marginTop:12,paddingTop:11,borderTop:`1px solid ${T.border2}`}}>
        <div style={{fontSize:9.5,color:T.muted,fontWeight:700,letterSpacing:"0.04em",marginBottom:6}}>SCRATCH A PLAYER — pre-game injury / late scratch (removes ALL bet types, off the board &amp; out of the record)</div>
        <div style={{display:"flex",gap:6}}>
          <input value={sName} onChange={e=>setSName(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")doScratch()}} placeholder="Player name — e.g. Nick Kurtz"
            style={{flex:1,minWidth:0,boxSizing:"border-box",background:T.bg,border:`1px solid ${T.border2}`,borderRadius:8,padding:"9px 10px",color:T.text,fontSize:12,outline:"none"}}/>
          <button onClick={()=>!busy&&doScratch()} disabled={!!busy} style={{padding:"9px 16px",borderRadius:8,fontSize:12,fontWeight:800,whiteSpace:"nowrap",cursor:busy?"default":"pointer",border:`1px solid ${busy==="scratch"?T.border2:"#7a3030"}`,background:busy==="scratch"?T.card:"#7F1D1D22",color:busy==="scratch"?T.muted2:"#ef6a6a"}}>{busy==="scratch"?"Working…":"✂️ Scratch"}</button>
          <button onClick={()=>!busy&&doReadd()} disabled={!!busy} style={{padding:"9px 16px",borderRadius:8,fontSize:12,fontWeight:800,whiteSpace:"nowrap",cursor:busy?"default":"pointer",border:`1px solid ${busy==="readd"?T.border2:T.greenMid}`,background:busy==="readd"?T.card:T.greenDim,color:busy==="readd"?T.muted2:T.green}}>{busy==="readd"?"Working…":"↩︎ Re-add"}</button>
        </div>
        <div style={{fontSize:9.5,color:T.faint,marginTop:5,lineHeight:1.4}}>Re-add undoes an accidental scratch — re-projects just that player (no full reslate).</div>
      </div>
      {/* Force a roster refresh — trades / call-ups / IL moves. Auto-runs 9 AM + 3 PM;
          this is for same-day control (a guy dealt at 2 PM, live for tonight). */}
      <div style={{marginTop:12,paddingTop:11,borderTop:`1px solid ${T.border2}`}}>
        <div style={{fontSize:9.5,color:T.muted,fontWeight:700,letterSpacing:"0.04em",marginBottom:6}}>REFRESH ROSTERS — trades / call-ups / IL moves (auto-runs 9 AM &amp; 3 PM; use for same-day)</div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>!busy&&doSyncRosters()} disabled={!!busy} style={{padding:"9px 16px",borderRadius:8,fontSize:12,fontWeight:800,whiteSpace:"nowrap",cursor:busy?"default":"pointer",border:`1px solid ${busy==="rosters"?T.border2:T.border2}`,background:busy==="rosters"?T.card:T.card2,color:busy==="rosters"?T.muted2:T.text}}>{busy==="rosters"?"Syncing…":"🔄 Refresh Rosters"}</button>
        </div>
        <div style={{fontSize:9.5,color:T.faint,marginTop:5,lineHeight:1.4}}>Type a just-traded player in the box above first to also drop them onto tonight's slate under their new team.</div>
      </div>
      {msg && <div style={{marginTop:9,fontSize:11,lineHeight:1.5,color:msg.ok?T.green:T.red,background:msg.ok?T.greenDim:"#7F1D1D22",border:`1px solid ${msg.ok?T.greenMid:T.red}`,borderRadius:8,padding:"8px 10px"}}>{msg.text}</div>}
    </div>
  )
}

// Daily post cards — one-tap generation of the whole social set (HRR / Homers / Hits
// market cards + the 4-market Top-10 board) so the daily drop is self-serve. Each pulls
// that market's board fresh, applies the same capped/TBD-free top-N the record uses, and
// hands a branded PNG to the share sheet (download on desktop). "Generate all" runs them
// in sequence.
function DailyPostCards(){
  const [busy,setBusy] = useState("")
  const [msg,setMsg] = useState("")
  const dstr = () => { try { return new Date(new Date().toLocaleString("en-US",{timeZone:"America/New_York"})).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) } catch { return todayStr() } }
  const ydstr = () => { try { const d=new Date(new Date().toLocaleString("en-US",{timeZone:"America/New_York"})); d.setDate(d.getDate()-1); return d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) } catch { return yesterdayStr() } }
  const MKTS = [
    {slug:"hrr",    db:"HRR_over_1.5", title:"HRR (2+)",  sub:"Hits + runs + RBI floor. Model-ranked."},
    {slug:"homers", db:"HR_anytime",   title:"HOMERS",    sub:"Anytime home run. Model-ranked."},
    {slug:"hits",   db:"hits",         title:"HITS (1+)", sub:"Reaches with a knock. Model-ranked."},
  ]
  const BOARD = [
    {title:"HRR",         sub:"H+R+RBI 1.5", db:"HRR_over_1.5"},
    {title:"HITS",        sub:"1+ hit",      db:"hits"},
    {title:"HOMERS",      sub:"HR anytime",  db:"HR_anytime"},
    {title:"TOTAL BASES", sub:"TB 1.5",      db:"total_bases"},
  ]
  const doMkt = async (cfg) => {
    setBusy(cfg.slug); setMsg("")
    try {
      const d = await getLeaderboard(cfg.db, todayStr(), 80, false).catch(()=>null)
      const rows = _capRows(Array.isArray(d?.entries)?d.entries:[], 6)
      if(!rows.length){ setMsg(`No ${cfg.title} board yet.`); setBusy(""); return "" }
      const r = await shareMarketCard({ slug:cfg.slug, title:cfg.title, sub:cfg.sub, dateStr:dstr(), rows })
      return r
    } catch(_){ setMsg("Card failed — try again."); return "" }
    finally { setBusy("") }
  }
  const doBoard = async () => {
    setBusy("board"); setMsg("")
    try {
      const cols=[]
      for(const c of BOARD){
        const d = await getLeaderboard(c.db, todayStr(), 100, false).catch(()=>null)
        cols.push({ title:c.title, sub:c.sub, rows:_capRows(Array.isArray(d?.entries)?d.entries:[], 10) })
      }
      if(!cols.some(c=>c.rows.length)){ setMsg("No board yet."); setBusy(""); return "" }
      const r = await shareGridBoard({ dateStr:dstr(), columns:cols })
      return r
    } catch(_){ setMsg("Board failed — try again."); return "" }
    finally { setBusy("") }
  }
  const doYardWork = async () => {
    setBusy("yardwork"); setMsg("")
    try { const r=await shareYardWork(dstr()); if(r==="empty") setMsg("No board yet."); return r }
    catch(_){ setMsg("Yard Work failed — try again."); return "" } finally { setBusy("") }
  }
  const doHitBoard = async () => {
    setBusy("hitboard"); setMsg("")
    try { const r=await shareHitBoard(dstr()); if(r==="empty") setMsg("No board yet."); return r }
    catch(_){ setMsg("Hitting Board failed — try again."); return "" } finally { setBusy("") }
  }
  const doResults = async () => {
    setBusy("results"); setMsg("")
    try { const r=await shareResults(yesterdayStr(), ydstr()); if(r==="empty") setMsg("No settled results yet for yesterday."); return r }
    catch(_){ setMsg("Results card failed — try again."); return "" } finally { setBusy("") }
  }
  const doYardWorkResults = async () => {
    setBusy("ywresults"); setMsg("")
    try { const r=await shareYardWorkResults(yesterdayStr(), ydstr()); if(r==="empty") setMsg("No settled Yard Work board yet for yesterday."); return r }
    catch(_){ setMsg("Yard Work Results failed — try again."); return "" } finally { setBusy("") }
  }
  const doPowerSpots = async () => {
    setBusy("power"); setMsg("")
    try { const r=await sharePowerSpots(dstr()); if(r==="empty") setMsg("No homer-arm spots on today's board yet."); return r }
    catch(_){ setMsg("Power Spots failed — try again."); return "" } finally { setBusy("") }
  }
  const doAll = async () => {
    setBusy("all"); setMsg("")
    for(const m of MKTS){ setBusy(m.slug); await doMkt(m) }
    setBusy("board"); await doBoard()
    setBusy(""); setMsg("All cards generated.")
  }
  const btn = (label, key, fn) => (
    <button disabled={!!busy} onClick={fn}
      style={{flex:"1 1 auto",minWidth:96,padding:"10px 12px",borderRadius:10,cursor:busy?"default":"pointer",
        background:busy===key?T.greenDim:T.card, border:`1px solid ${busy===key?T.green:T.border2}`,
        color:busy===key?T.green:T.text, fontSize:12.5, fontWeight:800, opacity:busy&&busy!==key?0.5:1}}>
      {busy===key?"…":label}
    </button>
  )
  return (
    <div style={{marginTop:14}}>
      <div style={{fontSize:13,fontWeight:800,color:T.text,marginBottom:4}}>🖼️ Daily Post Cards — one-tap social set</div>
      <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:9}}>Generates today's branded cards from the live board — each opens the share sheet. Same selection the record uses (2-per-team cap, TBD matchups held out). <b style={{color:T.text}}>Run the whole daily drop yourself, no export needed.</b></div>
      <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
        {MKTS.map(m=>btn(m.title.replace(/ \(.*\)/,""), m.slug, ()=>doMkt(m)))}
        {btn("📊 Top-10 Board", "board", doBoard)}
        {btn("🏟️ Yard Work", "yardwork", doYardWork)}
        {btn("🎯 Hitting Board", "hitboard", doHitBoard)}
        {btn("🧾 Results (yest.)", "results", doResults)}
        {btn("🏟️ Yard Work Results", "ywresults", doYardWorkResults)}
        {btn("💣 Power Spots", "power", doPowerSpots)}
      </div>
      <button disabled={!!busy} onClick={doAll}
        style={{width:"100%",marginTop:8,padding:"11px",borderRadius:10,cursor:busy?"default":"pointer",
          background:busy==="all"?T.greenDim:T.green, border:"none", color:busy==="all"?T.green:"#04140D",
          fontSize:13, fontWeight:900, opacity:busy&&busy!=="all"?0.5:1}}>
        {busy?"Generating…":"⚡ Generate all 4"}
      </button>
      {msg && <div style={{fontSize:11,color:T.muted,marginTop:6}}>{msg}</div>}
    </div>
  )
}

function AdminPanel({onClose}){
  const { data: learn, loading: lLoad } = useFetch(()=>getLearnings(14).catch(e=>({_error:String(e)})), [])
  const { data: aud, loading: aLoad } = useFetch(()=>getAudit().catch(e=>({_error:String(e)})), [])
  const { data: val, loading: vLoad } = useFetch(()=>getValidation(45).catch(e=>({_error:String(e)})), [])
  const { data: cov, loading: cLoad } = useFetch(()=>getCoverage().catch(e=>({_error:String(e)})), [])
  const { data: tm,  loading: tLoad } = useFetch(()=>getTeamAudit(30).catch(e=>({_error:String(e)})), [])
  const badge = (label, m) => (
    <span style={{fontSize:9,fontWeight:800,letterSpacing:"0.03em",color:m.c,background:m.bg,border:`1px solid ${m.c}`,borderRadius:5,padding:"2px 6px",whiteSpace:"nowrap"}}>{label}</span>
  )
  const th = {textAlign:"right",padding:"6px 6px",fontSize:9,color:T.muted,fontWeight:800,textTransform:"uppercase",letterSpacing:"0.04em",whiteSpace:"nowrap"}
  const td = {textAlign:"right",padding:"7px 6px",fontFamily:T.mono,fontSize:11.5,whiteSpace:"nowrap"}
  // Plain-English field guide — so the terse column headers make sense at a glance.
  const guide = (title, items) => (
    <div style={{marginTop:9,background:T.card,border:`1px solid ${T.border}`,borderRadius:10,padding:"9px 11px"}}>
      <div style={{fontSize:9,color:T.muted,fontWeight:800,letterSpacing:"0.08em",marginBottom:6}}>{title}</div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {items.map(([k,v])=>(
          <div key={k} style={{display:"flex",gap:9,fontSize:10.5,lineHeight:1.45}}>
            <span style={{color:T.text,fontWeight:800,minWidth:50,flexShrink:0,fontFamily:T.mono}}>{k}</span>
            <span style={{color:T.muted}}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  )
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>⚙︎ Admin</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>

        <DataFixButtons/>

        <DailyPostCards/>

        <ReferralsAdmin/>

        <BetaCodesAdmin/>

        {/* Daily Learnings — ranked "what to fix today" from settled results */}
        <div style={{fontSize:13,fontWeight:800,color:T.text,margin:"14px 0 4px"}}>📚 Daily Learnings — what results are teaching us</div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:8}}>Every night's settled results, turned into a ranked list of what to fix — most actionable first. <b style={{color:T.text}}>Calibration auto-adjusts on its own</b>; this is where we spot what the models still get wrong. Share the top item and we fix it that day.</div>
        {lLoad ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Reviewing…</div>
        : learn?._error ? <div style={{color:T.red,fontSize:12,padding:"6px 0"}}>Couldn’t load: {learn._error}</div>
        : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{fontSize:11.5,fontWeight:700,color:T.muted2,background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"9px 11px",lineHeight:1.45}}>{learn?.headline||"No results yet."}</div>
          {(learn?.learnings||[]).map((l,i)=>{ const sc=_SEV_C[l.severity==="high"?"critical":l.severity==="med"?"warn":"info"]||_SEV_C.info; return (
            <div key={i} style={{border:`1px solid ${sc.c}`,background:sc.bg,borderRadius:10,padding:"9px 11px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <span style={{fontSize:11.5,fontWeight:800,color:T.text,lineHeight:1.3}}>{PROP_SHORT[l.market]||l.market} — {l.title}</span>
                <span style={{flexShrink:0,fontSize:9,fontWeight:800,color:sc.c,background:sc.bg,border:`1px solid ${sc.c}`,borderRadius:5,padding:"2px 6px"}}>{(l.severity==="high"?"FIX NOW":l.severity==="med"?"WATCH":"FYI")}</span>
              </div>
              <div style={{fontSize:10.5,color:T.muted2,lineHeight:1.45,marginTop:4}}>{l.detail}</div>
              <div style={{fontSize:10,color:T.muted,lineHeight:1.4,marginTop:5}}>→ {l.action}</div>
            </div>
          )})}
          {(learn?.learnings||[]).length===0 && !learn?._error && <div style={{color:T.green,fontSize:12.5,fontWeight:700,padding:"2px 0"}}>✓ Nothing flagged — models tracking to expectation.</div>}
        </div>)}

        {/* Sanity Audit — catch-all */}
        <div style={{fontSize:13,fontWeight:800,color:T.text,margin:"18px 0 4px"}}>🔎 Sanity Audit — anything smell wrong today?</div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:8}}>Sweeps the whole slate for red flags — parks out of range, a no-pitcher bat ranking high, the matchup clamp squashing an extreme arm, fallback probabilities, label drift, and more. <b style={{color:T.text}}>Critical/warn = look now</b> · info = FYI.</div>
        {aLoad ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Scanning…</div>
        : aud?._error ? <div style={{color:T.red,fontSize:12,padding:"6px 0"}}>Couldn’t load: {aud._error}</div>
        : (aud?.findings?.length===0
          ? <div style={{color:T.green,fontSize:12.5,fontWeight:700,padding:"6px 0"}}>✓ Clean — no red flags on {aud?.total_picks||0} picks.</div>
          : <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:10.5,color:T.muted}}>{aud?.total_picks||0} picks · <b style={{color:T.red}}>{aud?.counts?.critical||0} critical</b> · <b style={{color:T.amber}}>{aud?.counts?.warn||0} warn</b> · {aud?.counts?.info||0} info</div>
              {(aud?.findings||[]).map(f=>{ const sc=_SEV_C[f.severity]||_SEV_C.info; return (
                <div key={f.id} style={{border:`1px solid ${sc.c}`,background:sc.bg,borderRadius:10,padding:"9px 11px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                    <span style={{fontSize:11.5,fontWeight:800,color:T.text,lineHeight:1.3}}>{f.title}</span>
                    <span style={{flexShrink:0,fontSize:9,fontWeight:800,color:sc.c,background:sc.bg,border:`1px solid ${sc.c}`,borderRadius:5,padding:"2px 6px"}}>{f.severity.toUpperCase()} · {f.count}</span>
                  </div>
                  <div style={{fontSize:10,color:T.muted,lineHeight:1.4,marginTop:4}}>{f.hint}</div>
                  <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:2}}>
                    {(f.examples||[]).map((e,i)=><div key={i} style={{fontSize:10,color:T.muted2,fontFamily:T.mono,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>• {_auditEx(e)}</div>)}
                    {f.count>(f.examples||[]).length && <div style={{fontSize:9.5,color:T.muted}}>+{f.count-(f.examples||[]).length} more</div>}
                  </div>
                </div>
              )})}
            </div>)}

        {/* Validation Scorecard */}
        <div style={{fontSize:13,fontWeight:800,color:T.text,margin:"18px 0 4px"}}>📊 Validation — are the formulas winning?</div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:8}}>Read the <b style={{color:T.text}}>Verdict</b> first — that's the bottom line per bet type; the other columns are the evidence. <b style={{color:T.text}}>INSUFFICIENT DATA is normal early on</b> — it fills in as games settle past the {val?.window?.model_epoch||"epoch"} reset (needs ~1–2 weeks of results).</div>
        {vLoad ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Loading…</div>
        : val?._error ? <div style={{color:T.red,fontSize:12,padding:"6px 0"}}>Couldn’t load: {val._error}</div>
        : (
        <div style={{overflowX:"auto",border:`1px solid ${T.border}`,borderRadius:10}}>
          <table style={{borderCollapse:"collapse",width:"100%",minWidth:420}}>
            <thead><tr style={{background:T.card}}>
              <th style={{...th,textAlign:"left"}}>Mkt</th><th style={{...th,textAlign:"left"}}>Verdict</th>
              <th style={th}>n</th><th style={th}>hit%</th><th style={th}>gap</th><th style={th}>Brier</th><th style={th}>CLV</th><th style={th}>ROI</th>
            </tr></thead>
            <tbody>
              {Object.entries(val?.markets||{}).map(([pt,m])=>(
                <tr key={pt} style={{borderTop:`1px solid ${T.border}`}}>
                  <td style={{...td,textAlign:"left",fontWeight:800}}>{m.label}</td>
                  <td style={{...td,textAlign:"left"}}>{badge(m.verdict, _VERDICT_C[m.verdict]||_VERDICT_C["INSUFFICIENT DATA"])}</td>
                  <td style={{...td,color:T.muted2}}>{m.n_settled}</td>
                  <td style={{...td,color:T.text}}>{_pctA(m.hit_rate)}</td>
                  <td style={{...td,color:m.calibration_gap==null?T.muted2:Math.abs(m.calibration_gap)<0.06?T.green:T.amber}}>{_sgnA(m.calibration_gap)}</td>
                  <td style={{...td,color:T.muted2}}>{m.brier==null?"—":m.brier.toFixed(3)}</td>
                  <td style={{...td,color:m.clv?.avg_clv==null?T.muted2:m.clv.avg_clv>0?T.green:T.red}}>{m.clv?.avg_clv==null?"—":`${_sgnA(m.clv.avg_clv)}·${m.clv.n}`}</td>
                  <td style={{...td,color:T.muted2}}>{"—"}</td>{/* ROI paused until real book odds */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>)}
        {!vLoad && !val?._error && guide("WHAT EACH COLUMN MEANS", [
          ["Verdict", <>the bottom line — <b style={{color:T.green}}>PROVEN</b> (honest + beats the market + profitable) · <b style={{color:"#34D399"}}>PROMISING</b> (at least one clearly good) · <b style={{color:T.amber}}>NEEDS WORK</b> (something's off) · <b style={{color:T.muted2}}>INSUFFICIENT DATA</b> (too few settled bets yet)</>],
          ["n", "how many settled bets we've graded"],
          ["hit%", "how often those bets actually won"],
          ["gap", "predicted % minus actual %. 0 = spot-on · + = overconfident (says 60%, hits 55%) · − = underconfident"],
          ["Brier", "overall accuracy, 0–1. Lower = sharper. ~.25 ≈ a coin flip"],
          ["CLV", "did we beat the closing line? + = the market moved to our side after we flagged it — the top sign of real edge. ·N = sample size"],
          ["ROI", "profit per $1 bet at closing odds. +5% = up 5¢ on the dollar. ·N = priced bets"],
        ])}

        {/* Coverage */}
        <div style={{fontSize:13,fontWeight:800,color:T.text,margin:"18px 0 4px"}}>🩺 Coverage — is every formula returning today?</div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:8}}>{cov?.date?`Slate ${cov.date} · `:""}{cov?.healthy===true?<b style={{color:T.green}}>all markets returning ✓</b>:cov?.problems?.length?<b style={{color:T.amber}}>{cov.problems.length} market(s) need a look</b>:"—"}</div>
        {cLoad ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Loading…</div>
        : cov?._error ? <div style={{color:T.red,fontSize:12,padding:"6px 0"}}>Couldn’t load: {cov._error}</div>
        : (
        <div style={{overflowX:"auto",border:`1px solid ${T.border}`,borderRadius:10}}>
          <table style={{borderCollapse:"collapse",width:"100%",minWidth:380}}>
            <thead><tr style={{background:T.card}}>
              <th style={{...th,textAlign:"left"}}>Mkt</th><th style={{...th,textAlign:"left"}}>Status</th>
              <th style={th}>n</th><th style={th}>ret.</th><th style={th}>broken</th><th style={th}>odds</th><th style={th}>conf</th>
            </tr></thead>
            <tbody>
              {Object.entries(cov?.markets||{}).map(([pt,m])=>(
                <tr key={pt} style={{borderTop:`1px solid ${T.border}`}}>
                  <td style={{...td,textAlign:"left",fontWeight:800}}>{m.label}</td>
                  <td style={{...td,textAlign:"left"}}>{badge(m.status, _STATUS_C[m.status]||_STATUS_C["MISSING"])}</td>
                  <td style={{...td,color:T.muted2}}>{m.n}</td>
                  <td style={{...td,color:T.text}}>{m.returning}</td>
                  <td style={{...td,color:m.broken>0?T.amber:T.muted2}}>{m.broken}</td>
                  <td style={{...td,color:m.with_odds>0?T.text:T.muted2}}>{m.with_odds}</td>
                  <td style={{...td,color:T.muted2}}>{m.lineup_confirmed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>)}
        {!cLoad && !cov?._error && guide("WHAT EACH COLUMN MEANS", [
          ["Status", <><b style={{color:T.green}}>OK</b> (running + priced) · <b style={{color:"#34D399"}}>NO_ODDS</b> (running, waiting on book odds) · <b style={{color:T.amber}}>DEGRADED</b> (&gt;20% unusable) · <b style={{color:T.red}}>BROKEN</b> (rows but no usable %) · <b style={{color:T.red}}>MISSING</b> (formula didn't run)</>],
          ["n", "rows the formula produced for today"],
          ["ret.", "returning — rows with a real, usable probability"],
          ["broken", "rows with a missing or garbage probability"],
          ["odds", "rows that have book odds attached (needed for edge/EV)"],
          ["conf", "rows with a confirmed starting lineup"],
        ])}

        {/* Park bias by team */}
        <div style={{fontSize:13,fontWeight:800,color:T.text,margin:"18px 0 4px"}}>🏟 Park bias — all teams (last 30d)</div>
        <div style={{fontSize:10.5,color:T.muted,lineHeight:1.5,marginBottom:8}}>How much the model's environment (park × weather × team-total) boosts each team. High <b style={{color:T.text}}>avg park / context</b> = inflated by venue (Coors/GABP). <b style={{color:T.text}}>gap</b> = predicted − actual once settled: ≈0 justified, <b style={{color:T.amber}}>+</b> = over-valued.{tm?.league_avg_park?` League avg park ${tm.league_avg_park}x.`:""}</div>
        {tLoad ? <div style={{color:T.muted,fontSize:12,padding:"6px 0"}}>Loading…</div>
        : tm?._error ? <div style={{color:T.red,fontSize:12,padding:"6px 0"}}>Couldn’t load: {tm._error}</div>
        : (
        <div style={{overflowX:"auto",border:`1px solid ${T.border}`,borderRadius:10,maxHeight:280,overflowY:"auto"}}>
          <table style={{borderCollapse:"collapse",width:"100%",minWidth:360}}>
            <thead><tr style={{background:T.card}}>
              <th style={{...th,textAlign:"left"}}>Team</th><th style={th}>n</th><th style={th}>avg park</th><th style={th}>context</th><th style={th}>avg prob</th><th style={th}>gap</th>
            </tr></thead>
            <tbody>
              {(tm?.teams||[]).map(t=>(
                <tr key={t.team} style={{borderTop:`1px solid ${T.border}`}}>
                  <td style={{...td,textAlign:"left",fontWeight:800}}>{t.team}{t.park_bias==="HIGH"?<span style={{color:T.amber,fontSize:9,marginLeft:4}}>▲</span>:t.park_bias==="LOW"?<span style={{color:T.muted2,fontSize:9,marginLeft:4}}>▼</span>:""}</td>
                  <td style={{...td,color:T.muted2}}>{t.n}</td>
                  <td style={{...td,color:t.avg_park==null?T.muted2:t.avg_park>=1.08?T.amber:t.avg_park<=0.95?"#34D399":T.text}}>{t.avg_park==null?"—":`${t.avg_park.toFixed(2)}x`}</td>
                  <td style={{...td,color:t.avg_context==null?T.muted2:t.avg_context>=1.10?T.amber:T.text}}>{t.avg_context==null?"—":`${t.avg_context.toFixed(2)}x`}</td>
                  <td style={{...td,color:T.text}}>{t.avg_prob==null?"—":_pctA(t.avg_prob)}</td>
                  <td style={{...td,color:t.gap==null?T.muted2:Math.abs(t.gap)<0.05?T.green:t.gap>0?T.amber:"#34D399"}}>{t.gap==null?"—":_sgnA(t.gap)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>)}

        <div style={{fontSize:9.5,color:T.muted2,lineHeight:1.5,marginTop:14}}>
          Read-only. <b style={{color:T.text}}>Audit</b> = anything wrong now · <b style={{color:T.text}}>Validation</b> = are the formulas <i>winning</i> · <b style={{color:T.text}}>Coverage</b> = are they <i>running</i> · <b style={{color:T.text}}>Park bias</b> = who the venue over/under-boosts. Also open directly at <span style={{fontFamily:T.mono}}>/projections/audit</span>, <span style={{fontFamily:T.mono}}>/scorecard/validation</span>, <span style={{fontFamily:T.mono}}>/projections/team-audit</span>.
        </div>
      </div>
    </Sheet>
  )
}

// ── Parlay tray (persists above the nav while browsing) ─────────────────────
function ParlayTray({legs, onRemove, onClear}){
  const [open,setOpen] = useState(false)
  const [wager,setWager] = useState("")
  const [saved,setSaved] = useState(false)
  const combo = legs.reduce((a,l)=>a*(l.prob||0), 1)
  const wn = parseFloat(wager)
  const decOdds = combo>0 ? 1/combo : 0            // fair decimal odds from model prob
  const ret = (isFinite(wn) && wn>0 && decOdds>0) ? wn*decOdds : null   // total return
  // Real book price: multiply each leg's actual over-odds (American→decimal). Only
  // meaningful when EVERY leg is priced; otherwise we say how many are.
  const priced = legs.filter(l=>l.oddsNum!=null)
  const allPriced = legs.length>0 && priced.length===legs.length
  const bookDec = allPriced ? legs.reduce((a,l)=>a*(1/impliedProb(l.oddsNum)), 1) : null
  const bookRet = (bookDec && isFinite(wn) && wn>0) ? wn*bookDec : null
  const bookAm = bookDec ? toAmerican(1/bookDec) : null
  const ev = bookDec ? (combo*bookDec - 1) : null   // EV per $1 staked at the book price
  const saveToBets = () => {
    const ln = n => (n||"").split(" ").slice(-1)[0]
    const bet = {
      date: todayStr(), book: "",
      tag: legs.length===1 ? legs[0].market : "Parlay",
      type: legs.length>1 ? "Parlay" : "Straight",
      legs: legs.map(l=>`${ln(l.name)} ${l.market}`).join(" + "),
      wager: (isFinite(wn)&&wn>0) ? String(wn) : "",
      // Save the REAL book return/odds when every leg is priced; else the fair benchmark.
      ret: (bookRet!=null?bookRet:ret)!=null ? String(Math.round((bookRet!=null?bookRet:ret)*100)/100) : "",
      odds: bookAm || toAmerican(combo), result: "Pending",
    }
    try{ localStorage.setItem(BETS_KEY, JSON.stringify([bet, ...loadBets()]))
         setSaved(true); setTimeout(()=>setSaved(false), 2500) }catch{ /* noop */ }
  }
  // Correlation: legs sharing the same opposing pitcher (same game/side) move
  // together — combined % overstates the real chance. Flag those legs.
  const groups = {}
  legs.forEach(l=>{ const k=((l.opp||"").toLowerCase())||((l.team||"").toLowerCase()); if(k) (groups[k]=groups[k]||[]).push(l.key) })
  const corr = new Set()
  Object.values(groups).forEach(arr=>{ if(arr.length>1) arr.forEach(k=>corr.add(k)) })
  const nCorr = corr.size
  const copy = async () => {
    const txt = `LINEBRKR Parlay — ${legs.length} legs · ${pct(combo)} combined · ${toAmerican(combo)} fair\n`
      + legs.map(l=>`${l.market} ${l.name} — ${pct(l.prob||0)}${corr.has(l.key)?" ⚠same game":""}`).join("\n")
      + EXPORT_DISCLAIMER
    try{ await navigator.clipboard.writeText(txt) }catch{ /* noop */ }
  }
  return (
    <div style={{position:"fixed",left:0,right:0,bottom:56,zIndex:150,maxWidth:460,margin:"0 auto",background:T.card2,borderTop:`1px solid ${T.greenMid}`}}>
      {open && (
        <div style={{padding:"12px 14px 6px",maxHeight:"42vh",overflowY:"auto"}}>
          {legs.map(l=>{ const c=corr.has(l.key); return (
            <div key={l.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px solid ${T.border}`}}>
              <span style={{fontSize:13,color:T.text}}><span style={{color:T.muted2,fontFamily:T.mono,fontSize:11}}>{l.market} </span>{l.name}{c&&<span style={{color:T.amber,fontSize:10,fontWeight:800,marginLeft:6}}>⚠ same game</span>}</span>
              <span style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:13,fontWeight:800,color:probColor(l.prob||0),fontFamily:T.mono}}>{pct(l.prob||0)}</span>
                <button onClick={()=>onRemove(l.key)} style={{background:"none",border:"none",color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
              </span>
            </div>
          )})}
          {nCorr>0 && <div style={{fontSize:11,color:T.amber,marginTop:8,lineHeight:1.4}}>⚠ {nCorr} leg{nCorr>1?"s":""} share a pitcher/game — correlated, so the real combined chance is lower than the {pct(combo)} shown. Spread across games for cleaner odds.</div>}
          {/* Wager → projected return at the model's fair odds */}
          <div style={{display:"flex",alignItems:"center",gap:10,margin:"11px 0 3px"}}>
            <span style={{fontSize:11,color:T.muted,fontWeight:700}}>Wager</span>
            <span style={{display:"inline-flex",alignItems:"center",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 9px"}}>
              <span style={{color:T.muted2,fontSize:12}}>$</span>
              <input type="number" inputMode="decimal" value={wager} onChange={e=>setWager(e.target.value)} placeholder="25" style={{width:56,border:"none",outline:"none",background:"transparent",color:T.text,fontSize:13,fontFamily:T.mono}}/>
            </span>
            <span style={{flex:1,textAlign:"right",fontSize:12,color:T.muted}}>
              {ret!=null ? <>returns <b style={{color:T.green,fontFamily:T.mono,fontSize:14}}>${ret.toFixed(2)}</b> <span style={{color:T.muted2}}>(fair {toAmerican(combo)})</span></> : <span style={{color:T.muted2}}>fair odds {toAmerican(combo)} · {pct(combo)}</span>}
            </span>
          </div>
          <div style={{fontSize:9.5,color:T.muted2,marginBottom:4}}>Return at the model's fair price (no book vig) — a benchmark, not a book payout.</div>
          {/* Real book price vs the model — the actual edge */}
          {allPriced ? (
            <div style={{display:"flex",alignItems:"center",gap:10,margin:"7px 0 2px",padding:"7px 9px",background:ev>0?T.greenDim:T.card,border:`1px solid ${ev>0?T.greenMid:T.border2}`,borderRadius:8}}>
              <span style={{fontSize:11,color:T.muted,fontWeight:700}}>At book</span>
              <span style={{fontSize:13,fontWeight:800,color:T.text,fontFamily:T.mono}}>{bookAm}</span>
              {bookRet!=null && <span style={{fontSize:11,color:T.muted}}>→ <b style={{color:T.text,fontFamily:T.mono}}>${bookRet.toFixed(2)}</b></span>}
              <span style={{flex:1,textAlign:"right",fontSize:12,fontWeight:800,fontFamily:T.mono,color:ev>0?T.green:ev<-0.02?T.red:T.muted}}>
                {ev>0?"▲ ":ev<-0.02?"▼ ":""}{ev>=0?"+":""}{(ev*100).toFixed(1)}% EV
              </span>
            </div>
          ) : legs.some(l=>l.oddsNum==null) && (
            <div style={{fontSize:9.5,color:T.muted2,marginBottom:4}}>Book edge needs a price on every leg — {priced.length}/{legs.length} priced right now.</div>
          )}
          <div style={{display:"flex",gap:8,margin:"9px 0 4px"}}>
            <button onClick={saveToBets} style={{flex:1.4,padding:"9px",borderRadius:8,background:saved?T.green:T.greenDim,border:`1px solid ${T.green}`,color:saved?"#04140D":T.green,fontSize:12,fontWeight:800,cursor:"pointer"}}>{saved?"✓ Saved to My Bets":"＋ Save to My Bets"}</button>
            <button onClick={copy} style={{flex:1,padding:"9px",borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted2,fontSize:12,fontWeight:700,cursor:"pointer"}}>Copy</button>
            <button onClick={onClear} style={{flex:0.7,padding:"9px",borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted2,fontSize:12,fontWeight:700,cursor:"pointer"}}>Clear</button>
          </div>
        </div>
      )}
      <button onClick={()=>setOpen(o=>!o)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"11px 14px",background:"none",border:"none",cursor:"pointer",color:T.text}}>
        <span style={{fontSize:13,fontWeight:800}}>🎟 Parlay · {legs.length} leg{legs.length>1?"s":""}</span>
        <span style={{display:"flex",alignItems:"center",gap:14}}>
          <span style={{fontSize:14,fontWeight:800,color:probColor(combo),fontFamily:T.mono}}>{pct(combo)}</span>
          <span style={{fontSize:14,fontWeight:800,color:T.text,fontFamily:T.mono}}>{toAmerican(combo)}</span>
          <span style={{fontSize:11,color:T.muted}}>{open?"▾":"▴"}</span>
        </span>
      </button>
    </div>
  )
}

// ── Root ────────────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(p){ super(p); this.state = {err:null} }
  static getDerivedStateFromError(err){ return {err} }
  componentDidCatch(err, info){ try{ console.error("UI crash:", err, info) }catch(_){ } }
  componentDidUpdate(prev){ if(prev.resetKey !== this.props.resetKey && this.state.err) this.setState({err:null}) }
  render(){
    if(this.state.err){
      const e = this.state.err
      let head = "Error"; try{ head = String(e?.name ? `${e.name}: ${e.message}` : (e?.message ?? e)) }catch(_){ }
      const stack = String(e?.stack || "")
      return (
        <div style={{padding:"40px 18px",color:T.text,fontFamily:"-apple-system,Arial,sans-serif"}}>
          <div style={{fontSize:16,fontWeight:800,marginBottom:8}}>This screen hit a snag</div>
          <div style={{fontSize:12,color:T.muted,marginBottom:10,lineHeight:1.5}}>The rest of the app is fine — tap a tab below. Error:</div>
          <div style={{fontSize:12.5,color:T.red,fontWeight:700,background:T.card,border:`1px solid ${T.red}`,borderRadius:8,padding:"10px 12px",whiteSpace:"pre-wrap",wordBreak:"break-word",marginBottom:10}}>{head}</div>
          <pre style={{fontSize:10,color:T.muted2,background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"10px 12px",whiteSpace:"pre-wrap",wordBreak:"break-word",maxHeight:200,overflow:"auto"}}>{stack}</pre>
          <button onClick={()=>this.setState({err:null})} style={{marginTop:14,padding:"10px 16px",borderRadius:9,background:T.greenDim,border:`1px solid ${T.green}`,color:T.green,fontSize:13,fontWeight:800,cursor:"pointer"}}>Try again</button>
        </div>
      )
    }
    return this.props.children
  }
}

// Private access gate — shown when the API returns 401 (i.e. ACCESS_CODE is set on
// the server and this browser doesn't have the right code). Entering a code stores
// it and reloads; correct → the app loads, wrong → this screen returns.
// Shown while we exchange the post-checkout ?session_id=… for an access code.
function ClaimingScreen(){
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:340,textAlign:"center"}}>
        <div style={{fontWeight:900,fontSize:28,letterSpacing:"-0.04em",marginBottom:10}}><span style={{color:T.text}}>LINE</span><span style={{color:T.green}}>BRKR</span></div>
        <div style={{fontSize:13,color:T.green,fontWeight:700,marginBottom:6}}>Activating your subscription…</div>
        <div style={{fontSize:12,color:T.muted,lineHeight:1.5}}>One moment — unlocking full access.</div>
      </div>
    </div>
  )
}
// One checkout redirect helper shared by the paywall + upgrade sheet.
async function _goCheckout(plan, founder, onErr){
  try{ const { url } = await startCheckout({ plan, founder }); if(url){ window.location.href=url; return } throw new Error("no url") }
  catch(_){ onErr && onErr("Subscriptions aren't open yet. If you have an access code, enter it below.") }
}
// A single tier card on the paywall.
function PlanCard({name, price, per, blurb, features, cta, hero, badge, onPick, busy}){
  return (
    <div style={{background:hero?`linear-gradient(180deg, ${T.greenDim}, ${T.card})`:T.card,border:`1px solid ${hero?T.green:T.border2}`,borderRadius:14,padding:"14px 15px",marginBottom:12,textAlign:"left",position:"relative"}}>
      {badge && <div style={{position:"absolute",top:-9,right:12,fontSize:9,fontWeight:800,letterSpacing:"0.06em",color:"#04140D",background:T.green,borderRadius:20,padding:"2px 9px"}}>{badge}</div>}
      <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between",marginBottom:4}}>
        <div style={{fontSize:15,fontWeight:900,color:T.text}}>{name}</div>
        <div><span style={{fontSize:20,fontWeight:900,color:hero?T.green:T.text}}>{price}</span><span style={{fontSize:11,color:T.muted}}> {per}</span></div>
      </div>
      <div style={{fontSize:11,color:T.muted2,lineHeight:1.45,marginBottom:9}}>{blurb}</div>
      <div style={{marginBottom:11}}>{features.map((f,i)=>(
        <div key={i} style={{fontSize:11,color:T.muted2,lineHeight:1.7,display:"flex",gap:6}}><span style={{color:T.green}}>✓</span>{f}</div>
      ))}</div>
      <button onClick={onPick} disabled={busy} style={{width:"100%",background:hero?T.green:"transparent",border:`1px solid ${T.green}`,borderRadius:10,padding:"11px 0",color:hero?"#04140D":T.green,fontSize:13,fontWeight:900,cursor:busy?"default":"pointer",opacity:busy?0.6:1}}>{busy?"Opening checkout…":cta}</button>
    </div>
  )
}
function LockScreen(){
  const [code,setCode] = useState("")
  const [busy,setBusy] = useState("")
  const [err,setErr] = useState("")
  const [legal,setLegal] = useState(false)         // false | "terms" | "privacy"
  // Card-free free tier (email → top play per MLB market), mirroring the NFL side. Contained
  // in the lock screen so it never touches the app's access gate / paid flow.
  const [freeEmail,setFreeEmail] = useState("")
  const [freeBoard,setFreeBoard] = useState(null)  // {plays} once signed up (or returning)
  const [freeBusy,setFreeBusy] = useState(false)
  const [freeErr,setFreeErr] = useState("")
  // Referral code — prefilled from a ?ref= link, or typed by hand (e.g. CJ01). Applies to ANY
  // sign-up, not just one sport; rides the shared brkr_ref slot into signup + checkout.
  const [freeRef,setFreeRef] = useState(()=>{ try{ return localStorage.getItem('brkr_ref')||'' }catch(_){ return '' } })
  const persistRef = ()=>{ try{ const c=(freeRef||'').trim(); if(c) localStorage.setItem('brkr_ref', c) }catch(_){} }
  useEffect(()=>{ if(getFreeToken()) getMLBFreeBoard().then(d=>{ if(d && !d._sample && (d.plays||[]).length) setFreeBoard(d) }).catch(()=>{}) }, [])
  const startFree = async ()=>{
    if(!freeEmail.trim()||freeBusy) return
    setFreeBusy(true); setFreeErr("")
    try{ await mlbFreeSignup(freeEmail.trim(), freeRef.trim()); setFreeBoard(await getMLBFreeBoard() || {plays:[]}) }
    catch(e){ setFreeErr(e.message||"Could not sign up. Try again.") }
    setFreeBusy(false)
  }
  const submit = ()=>{ if(!code.trim()) return; setAccessCode(code.trim()); window.location.reload() }
  const hadCode = (()=>{ try{ return !!localStorage.getItem('brkr_access') }catch(_){ return false } })()
  const canceled = (()=>{ try{ return new URLSearchParams(window.location.search).get('checkout')==='cancel' }catch(_){ return false } })()
  // Founder invite link (?founder=1): Pro at the locked-for-life $10 rate.
  const founder = (()=>{ try{ return /(?:^|[?&])founder=1\b/.test(window.location.search) }catch(_){ return false } })()
  const pick = async (plan)=>{ persistRef(); setBusy(plan); setErr(""); await _goCheckout(plan, plan==="pro"&&founder, (m)=>{ setBusy(""); setErr(m) }) }
  // Public Founder package: full Pro at the locked-for-life $10 rate (uses the pro
  // checkout with the founder price flag, regardless of the ?founder=1 invite link).
  const pickFounder = async ()=>{ persistRef(); setBusy("founder"); setErr(""); await _goCheckout("pro", true, (m)=>{ setBusy(""); setErr(m) }) }
  // Interim single offer: $10 full access, UNCAPPED (the 'all' plan → STRIPE_PRICE_ALL, the $10
  // price). Uncapped so the sole paid option can never "sell out" and leave no way to buy.
  const pickAll = async ()=>{ persistRef(); setBusy("all"); setErr(""); await _goCheckout("all", false, (m)=>{ setBusy(""); setErr(m) }) }
  // Free preview — proof before the paywall (today's top pick + track record).
  const [preview,setPreview] = useState(null)
  useEffect(()=>{ if(PRICING_LIVE) getFreePreview().then(setPreview).catch(()=>{}) }, [])
  const tp = preview?.top_play, rec = preview?.record, yp = preview?.yesterday_play
  // Live Founder scarcity — real "X of 100 claimed" + hide the seat once it's sold out.
  const [fdr,setFdr] = useState(null)
  useEffect(()=>{ if(PRICING_LIVE) getFoundersStatus().then(setFdr).catch(()=>{}) }, [])
  const fdrSold = fdr?.sold_out === true
  // Scarcity that stays honest as it fills: stay vague while there's slack (a high
  // "99 of 100 left" reads as "nobody's bought"), then flip to the REAL remaining
  // count once we're genuinely near the cap — so "only 7 left" is literally true.
  const fdrBadge = (fdr && !fdrSold && fdr.remaining <= 12)
    ? `🎟️ FOUNDER · ONLY ${fdr.remaining} LEFT · LOCKED FOR LIFE`
    : "🎟️ FOUNDER · ONLY A FEW SLOTS LEFT · LOCKED FOR LIFE"

  // Pre-launch: simple private gate, no pricing shown. (Paywall below is intact,
  // gated by PRICING_LIVE — flip that flag on at launch to reveal it.)
  if(!PRICING_LIVE){
    return (
      <div style={{position:"fixed",inset:0,zIndex:2000,background:T.bg,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{width:"100%",maxWidth:340,textAlign:"center"}}>
          <div style={{fontWeight:900,fontSize:28,letterSpacing:"-0.04em",marginBottom:6}}><span style={{color:T.text}}>LINE</span><span style={{color:T.green}}>BRKR</span></div>
          <div style={{fontSize:12.5,color:T.muted,marginBottom:18}}>🔒 Private — enter your access code.</div>
          <input type="password" value={code} autoFocus onChange={e=>setCode(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") submit() }} placeholder="Access code"
            style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${hadCode?T.red:T.border2}`,borderRadius:10,padding:"12px 14px",color:T.text,fontSize:16,marginBottom:10,textAlign:"center"}}/>
          {hadCode && <div style={{fontSize:11,color:T.red,marginBottom:10}}>That code didn't work — try again.</div>}
          <button onClick={submit} style={{width:"100%",background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:10,padding:"12px 0",color:T.green,fontSize:14,fontWeight:800,cursor:"pointer"}}>Unlock</button>
        </div>
      </div>
    )
  }
  return (
    <div style={{position:"fixed",inset:0,zIndex:2000,background:T.bg,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"32px 22px 40px",overflowY:"auto"}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:18}}>
          <div style={{fontWeight:900,fontSize:30,letterSpacing:"-0.04em",marginBottom:8}}><span style={{color:T.text}}>LINE</span><span style={{color:T.green}}>BRKR</span></div>
          <div style={{fontSize:12.5,color:T.muted2,lineHeight:1.5}}>An analytics hub for player props. Search any matchup, see the model's read, and track a public record. You research plays here, you don't place them. <b style={{color:T.text}}>Research only, not betting advice. 21+.</b></div>
          {/* Proof badge — the one claim competitors on self-reported stats can't match. The
              settled record + CLV sit right below on this screen. */}
          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:999,padding:"6px 13px",marginTop:12}}>
            <span style={{fontSize:11,fontWeight:800,color:T.green,letterSpacing:"0.02em"}}>✓ CLV-proven · Calibrated</span>
            {rec?.hit_rate!=null ? <span style={{fontSize:11,fontWeight:800,color:T.text,fontFamily:T.mono}}>{rec.hit_rate}%</span> : null}
            <span style={{fontSize:10.5,color:T.muted}}>real settled results ↓</span>
          </div>
          <div style={{fontSize:11,color:T.green,fontWeight:800,marginTop:10}}>Start with a 3-day free trial · cancel anytime</div>
        </div>

        {/* Free plays (once signed up / returning) — the model's top play per market */}
        {freeBoard && (freeBoard.plays||[]).length>0 && (
          <div style={{background:T.card,border:`1px solid ${T.green}`,borderRadius:14,padding:"13px 14px",marginBottom:14}}>
            <div style={{fontSize:9.5,color:T.green,fontWeight:800,letterSpacing:"0.12em",marginBottom:8}}>★ YOUR FREE PLAYS · TODAY</div>
            {(freeBoard.plays||[]).map((p,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"8px 0",borderTop:i?`1px solid ${T.border}`:"none"}}>
                <div style={{minWidth:0,textAlign:"left"}}>
                  <div style={{fontSize:13.5,fontWeight:800,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                  <div style={{fontSize:10.5,color:T.muted}}>{[p.team,p.market].filter(Boolean).join(" · ")}{p.line!=null?` o${p.line}`:""}</div>
                </div>
                <div style={{fontFamily:T.mono,fontWeight:900,fontSize:16,color:T.green,flexShrink:0}}>{Math.round((p.prob||0)*100)}%</div>
              </div>
            ))}
            <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:10}}>Unlock every market + the full board below 👇</div>
          </div>
        )}

        {/* Proof, before the paywall: the record pops first, then today's free pick,
            then yesterday's free play + how it actually settled (hit OR miss). */}
        {rec && (
          <div style={{position:"relative",overflow:"hidden",background:`radial-gradient(340px 130px at 12% -20%, ${T.greenDim}, transparent 70%), linear-gradient(180deg, ${T.card3}, ${T.card})`,border:`1px solid ${T.green}`,borderRadius:14,padding:"15px 16px",marginBottom:12,boxShadow:`0 0 24px ${T.green}1F`}}>
            <div style={{fontSize:9.5,color:T.green,fontWeight:900,letterSpacing:"0.14em",marginBottom:7}}>◆ PROVEN · REAL SETTLED RESULTS</div>
            <div style={{display:"flex",alignItems:"baseline",gap:12,flexWrap:"wrap"}}>
              <span style={{fontSize:46,fontWeight:900,color:T.green,fontFamily:T.mono,lineHeight:.85,letterSpacing:"-2px"}}>{Math.round((rec.hit_rate||0)*100)}%</span>
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                <span style={{fontSize:15,fontWeight:900,color:T.text,fontFamily:T.mono}}>{rec.wins}–{rec.total-rec.wins}</span>
                <span style={{fontSize:10,color:T.muted}}>{rec.tier} · last {rec.days}d</span>
              </div>
            </div>
            <div style={{fontSize:11,color:T.muted2,marginTop:8,lineHeight:1.4}}>Our top plays hit <b style={{color:T.green}}>{Math.round((rec.hit_rate||0)*100)}%</b> — graded on real box scores, wins and losses shown.</div>
          </div>
        )}
        {(tp || yp) && (
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"13px 14px",marginBottom:16}}>
            <div style={{fontSize:9.5,color:T.green,fontWeight:800,letterSpacing:"0.12em",marginBottom:8}}>FREE LOOK · TODAY</div>
            {tp && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,paddingBottom:yp?10:0,borderBottom:yp?`1px solid ${T.border}`:"none"}}>
                <div style={{minWidth:0,textAlign:"left"}}>
                  <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>TOP PLAY{tp.market?` · ${tp.market}`:""}</div>
                  <div style={{fontSize:15,fontWeight:800,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{tp.name}</div>
                  <div style={{fontSize:10.5,color:T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{tp.team}{tp.opp_pitcher_name?` · vs ${tp.opp_pitcher_name}`:""}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:22,fontWeight:900,color:T.green,fontFamily:T.mono,lineHeight:1}}>{Math.round((tp.prob_over||0)*100)}%</div>
                  <div style={{fontSize:8.5,color:T.muted}}>to hit</div>
                </div>
              </div>
            )}
            {yp && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,paddingTop:tp?10:0}}>
                <div style={{minWidth:0,textAlign:"left"}}>
                  <div style={{fontSize:9,color:T.muted,fontWeight:700,letterSpacing:"0.08em"}}>YESTERDAY'S FREE PLAY</div>
                  <div style={{fontSize:13,fontWeight:800,color:T.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{yp.name} <span style={{color:T.muted,fontWeight:600}}>· {yp.market}</span></div>
                </div>
                <div style={{flexShrink:0,fontSize:11.5,fontWeight:900,letterSpacing:"0.04em",padding:"4px 10px",borderRadius:20,color:yp.hit?T.green:T.muted2,background:yp.hit?T.greenDim:T.card2,border:`1px solid ${yp.hit?T.green:T.border2}`}}>{yp.hit?"✅ HIT":"✕ Missed"}</div>
              </div>
            )}
            <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:10}}>Unlock the full board + every market below 👇</div>
          </div>
        )}

        {/* Card-free door: email → 5 free plays. Hidden once they've signed up (plays show above). */}
        {!freeBoard && (
          <div style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:14,padding:"13px 14px",marginBottom:14}}>
            <div style={{fontSize:12.5,fontWeight:800,color:T.text,marginBottom:3}}>Start free — no card</div>
            <div style={{fontSize:11,color:T.muted2,marginBottom:9,lineHeight:1.4}}>The model's top play in each market, updated daily. Just your email.</div>
            <input type="email" value={freeEmail} onChange={e=>setFreeEmail(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") startFree() }} placeholder="you@email.com"
              style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"11px 12px",color:T.text,fontSize:15,textAlign:"center",marginBottom:8}}/>
            {freeErr && <div style={{fontSize:11,color:T.amber,textAlign:"center",marginBottom:8}}>{freeErr}</div>}
            <button onClick={startFree} disabled={freeBusy||!freeEmail.trim()} style={{width:"100%",background:"transparent",border:`1px solid ${T.green}`,borderRadius:10,padding:"11px 0",color:T.green,fontSize:13.5,fontWeight:800,cursor:(freeBusy||!freeEmail.trim())?"default":"pointer",opacity:(freeBusy||!freeEmail.trim())?0.55:1}}>{freeBusy?"…":"Get 5 free plays"}</button>
          </div>
        )}

        {founder && !fdrSold && <div style={{fontSize:11.5,color:T.green,textAlign:"center",fontWeight:800,marginBottom:10}}>🎟️ Founder invite — Pro locked at $10/mo for life</div>}

        {/* Referral code — always visible on the paywall (not just the free panel) so any
            buyer, for any sport, can enter a friend's code before subscribing. Rides the shared
            brkr_ref slot into both free signup and checkout (persistRef on the paid buttons). */}
        <input value={freeRef} onChange={e=>setFreeRef(e.target.value)} placeholder="Referral code (optional)"
          autoCapitalize="characters" autoCorrect="off" spellCheck={false}
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${freeRef.trim()?T.green:T.border2}`,borderRadius:10,padding:"10px 12px",color:T.text,fontSize:13,textAlign:"center",letterSpacing:"0.04em",marginBottom:10}}/>

        <PlanCard name="Full Access" price="$10" per="/mo" hero badge="🎟️ LOCKED FOR LIFE"
          blurb="Everything, every sport, every feature. Start with a 3-day free trial, then $10/mo — your rate is locked for life."
          features={["The full board — all markets & advanced analytics","Storms, Perfect Stack & Compare","Trends + Contact log","Custom alerts + private Discord","Your $10 rate never goes up"]}
          cta="Start 3-day free trial" busy={busy==="all"} onPick={pickAll}/>

        {/* Interim launch: a single offer — $10 Founder (full access, locked for life). The
            Rookie ($15) and Pro ($30) tiers are hidden for now; existing subscribers on those
            plans are unaffected (their Stripe subscriptions continue). */}

        <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:2,marginBottom:8}}>Secure checkout by Stripe · card required · cancel anytime before the trial ends and you won't be charged.</div>
        <div style={{fontSize:10,color:T.muted,textAlign:"center",marginBottom:16,lineHeight:1.5}}>By starting a trial or subscribing you agree to our <button onClick={()=>setLegal("terms")} style={{background:"none",border:"none",padding:0,color:T.green,fontSize:10,cursor:"pointer",textDecoration:"underline"}}>Terms</button> &amp; <button onClick={()=>setLegal("privacy")} style={{background:"none",border:"none",padding:0,color:T.green,fontSize:10,cursor:"pointer",textDecoration:"underline"}}>Privacy Policy</button>. 21+.</div>
        {canceled && <div style={{fontSize:11.5,color:T.amber,textAlign:"center",marginBottom:12}}>Checkout canceled — no charge was made.</div>}
        {err && <div style={{fontSize:11.5,color:T.amber,textAlign:"center",marginBottom:12,lineHeight:1.5}}>{err}</div>}

        {/* Existing access code */}
        <div style={{fontSize:11,color:T.muted,letterSpacing:"0.06em",fontWeight:700,textAlign:"center",marginBottom:10}}>ALREADY HAVE A CODE?</div>
        <input type="password" value={code} onChange={e=>setCode(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") submit() }} placeholder="Access code"
          style={{width:"100%",boxSizing:"border-box",background:T.card,border:`1px solid ${hadCode?T.red:T.border2}`,borderRadius:10,padding:"12px 14px",color:T.text,fontSize:16,marginBottom:10,textAlign:"center"}}/>
        {hadCode && <div style={{fontSize:11,color:T.red,textAlign:"center",marginBottom:10}}>That code didn't work — try again.</div>}
        <button onClick={submit} style={{width:"100%",background:T.card,border:`1px solid ${T.border2}`,borderRadius:10,padding:"11px 0",color:T.muted2,fontSize:13,fontWeight:800,cursor:"pointer"}}>Unlock with code</button>
        {legal && <LegalSheet tab={legal} onClose={()=>setLegal(false)} setTab={setLegal}/>}
      </div>
    </div>
  )
}
// Shown when a Rookie/Free user taps a Pro-only tool.
function UpgradeSheet({onClose}){
  const [busy,setBusy] = useState(false)
  const [err,setErr] = useState("")
  const go = async ()=>{ setBusy(true); setErr(""); await _goCheckout("pro", false, (m)=>{ setBusy(false); setErr(m) }) }
  return (
    <Sheet onClose={onClose}>
      <div style={{padding:"8px 16px 40px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div style={{fontSize:19,fontWeight:900,letterSpacing:"-0.02em"}}>⚡ Pro feature</div>
          <button onClick={onClose} style={{width:30,height:30,borderRadius:8,background:T.card,border:`1px solid ${T.border2}`,color:T.muted,fontSize:14,cursor:"pointer"}}>✕</button>
        </div>
        <div style={{fontSize:12.5,color:T.muted2,lineHeight:1.55,marginBottom:14}}>This tool is part of <b style={{color:T.text}}>LINEBRKR Pro</b>. Upgrade to unlock:</div>
        <div style={{marginBottom:16}}>{["All sports (NFL & NBA as they launch)","🌪 HR/HRR Storms","🎯 Perfect Stack","⚖ Compare players","Strikeouts market + advanced analytics","Custom alerts + private Discord"].map((f,i)=>(
          <div key={i} style={{fontSize:12.5,color:T.muted2,lineHeight:1.9,display:"flex",gap:8}}><span style={{color:T.green}}>✓</span>{f}</div>
        ))}</div>
        {err && <div style={{fontSize:11.5,color:T.amber,marginBottom:10,lineHeight:1.5}}>{err}</div>}
        <button onClick={go} disabled={busy} style={{width:"100%",background:T.green,border:"none",borderRadius:10,padding:"12px 0",color:"#04140D",fontSize:14,fontWeight:900,cursor:busy?"default":"pointer",opacity:busy?0.6:1}}>{busy?"Opening checkout…":"Upgrade to Pro"}</button>
        <div style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:8}}>3-day free trial · cancel anytime · Stripe</div>
      </div>
    </Sheet>
  )
}
export default function UnifiedApp(){
  const [locked,setLocked] = useState(false)
  // If Stripe redirected back with ?session_id=…, exchange it for the access code
  // and unlock — show a brief "activating…" screen while we do. Runs before the
  // lock screen so a fresh buyer never sees the code prompt.
  const [claiming,setClaiming] = useState(()=>{ try{ return new URLSearchParams(window.location.search).has('session_id') }catch(_){ return false } })
  useEffect(()=>{
    let sid=null
    try{ sid = new URLSearchParams(window.location.search).get('session_id') }catch(_){}
    if(!sid){ return }
    claimSession(sid).then(({code, plan})=>{
      if(code){ setAccessCode(code); try{ localStorage.setItem('brkr_new_code', code) }catch(_){} }
      if(plan) setPlanLocal(plan)
      try{ window.history.replaceState({},"",window.location.pathname) }catch(_){}
      window.location.reload()
    }).catch(()=>{ try{ window.history.replaceState({},"",window.location.pathname) }catch(_){}; setClaiming(false) })
  }, [])
  useEffect(()=>{ const h=()=>setLocked(true); window.addEventListener('brkr-locked',h); return ()=>window.removeEventListener('brkr-locked',h) }, [])
  // Access tier. Optimistic default from last-known (existing beta users are 'pro');
  // corrected by /billing/me once we confirm the code. Drives Pro-tool gating.
  const [plan,setPlan] = useState(()=> getPlanLocal() || 'pro')
  const [upgrade,setUpgrade] = useState(false)
  useEffect(()=>{
    let has=false; try{ has = !!localStorage.getItem('brkr_access') }catch(_){}
    if(!has){ setPlan('free'); return }
    getPlan().then(d=>{ if(d&&d.plan){ setPlan(d.plan); setPlanLocal(d.plan) } }).catch(()=>{})
  }, [])
  const isPro = PLAN_RANK[plan] >= PLAN_RANK.pro            // full Pro — Trends + Contact
  const isPremium = PLAN_RANK[plan] >= PLAN_RANK.founder    // Founder + Pro — Storms/Stack/Compare
  const needPro = ()=>{ if(isPro) return true; setUpgrade(true); return false }
  const needPremium = ()=>{ if(isPremium) return true; setUpgrade(true); return false }
  const [view,setView] = useState("home")
  // Home → Games "Pitchers to attack" deep-link: flip the intent, switch tabs, and
  // GamesTab opens the drawer on arrival (then clears the intent).
  const [attackIntent,setAttackIntent] = useState(false)
  const goAttack = ()=>{ setAttackIntent(true); setView("games") }
  // Contact is full-Pro only — bounce a non-Pro off it (belt & braces alongside
  // hiding it in the nav), e.g. if a plan downgrades mid-session. Signals is free
  // (its Trends segment self-gates), so it needs no redirect.
  useEffect(()=>{ if(!isPro && view==="contact") setView("home") }, [isPro, view])
  const [card,setCard] = useState(null)
  const [player,setPlayer] = useState(null)
  const [playerPark,setPlayerPark] = useState(null)
  const [parlay,setParlay] = useState([])
  const openPlayer = (pl,park)=>{ setPlayer(pl); setPlayerPark(park||null) }
  const toggleLeg = leg => setParlay(p=>{ const i=p.findIndex(x=>x.key===leg.key); if(i>=0){ const c=[...p]; c.splice(i,1); return c } return p.length>=8 ? p : [...p, leg] })
  const removeLeg = key => setParlay(p=>p.filter(x=>x.key!==key))
  // Add, or update in place if the leg is already on the slip (used by What-if to
  // push an adjusted-probability version of a play already added).
  const upsertLeg = leg => setParlay(p=>{ const i=p.findIndex(x=>x.key===leg.key); if(i>=0){ const c=[...p]; c[i]={...c[i],...leg}; return c } return p.length>=8 ? p : [...p, leg] })
  const inParlay = key => parlay.some(x=>x.key===key)
  const [glossary,setGlossary] = useState(false)   // false=closed | "top" | section id
  const [rg,setRg] = useState(false)               // responsible-gaming sheet open
  const [legal,setLegal] = useState(false)         // false | "terms" | "privacy"
  const [welcome,setWelcome] = useState(()=>{ try{ return !localStorage.getItem("linebrkr_onboarded_v1") }catch{ return false } })
  const dismissWelcome = ()=>{ try{ localStorage.setItem("linebrkr_onboarded_v1","1") }catch{}; setWelcome(false) }
  const [help,setHelp] = useState(false)           // help bot open
  const [guide,setGuide] = useState(false)         // the Field Guide (how-to)
  const [admin,setAdmin] = useState(false)         // admin / formula-health panel
  const [refer,setRefer] = useState(false)         // Refer & Earn (user-facing)
  useEffect(()=>{ getBookLinks().then(setBookAffiliates).catch(()=>{}) }, [])   // affiliate book links
  // Owner-only Admin entry point. Hidden from testers: it appears only if an admin
  // code is already saved in this browser, or the owner reveals it via ?admin in the
  // URL (bootstrap on a fresh device). Testers have neither, so they never see it.
  const [showAdmin] = useState(()=>{
    try{
      if(localStorage.getItem("brkr_admin")) return true
      if(/(?:^|[?&#])admin\b/i.test(window.location.search + window.location.hash)){
        try{ localStorage.setItem("brkr_admin_reveal","1") }catch{}
        return true
      }
      return !!localStorage.getItem("brkr_admin_reveal")
    }catch{ return false }
  })

  if(claiming) return <ClaimingScreen/>
  if(locked) return <LockScreen/>
  return (
   <PlanCtx.Provider value={{plan, isPro, isPremium, needPro, needPremium}}>
    <div style={{background:T.bg,minHeight:"100vh",maxWidth:460,margin:"0 auto",fontFamily:"-apple-system,'SF Pro Display',Arial,sans-serif",color:T.text,paddingBottom:64}}>
      {/* Top nav. The "‹ All sports" link lives on the LEFT (always visible) — in the right
          button group it got pushed off-screen on a phone, stranding logged-in users in one
          sport. The wordmark also links home. Right-side buttons scroll if they overflow. */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,padding:"14px 16px",borderBottom:`1px solid ${T.border}`,background:"#0D1117",position:"sticky",top:0,zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",gap:9,flexShrink:0}}>
          <a href="?" title="Back to all sports" style={{display:"flex",alignItems:"center",gap:3,textDecoration:"none",background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 9px",color:T.green,fontSize:12,fontWeight:800,whiteSpace:"nowrap"}}>‹ Sports</a>
          <a href="?" title="All sports" style={{fontWeight:900,fontSize:20,letterSpacing:"-0.04em",textDecoration:"none"}}><span style={{color:T.text}}>LINE</span><span style={{color:T.green}}>BRKR</span></a>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,overflowX:"auto",minWidth:0,scrollbarWidth:"none"}}>
          <button onClick={()=>setGuide(true)} title="How to use LINEBRKR" style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:8,padding:"5px 10px",color:T.green,fontSize:12,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>📖 Cheat Sheet</button>
          <button onClick={()=>setHelp(true)} title="Ask BRKR" style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 10px",color:T.muted2,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>💬 Ask BRKR</button>
          <button onClick={()=>setRefer(true)} title="Refer & Earn — get $20" style={{background:T.greenDim,border:`1px solid ${T.greenMid}`,borderRadius:8,padding:"5px 10px",color:T.green,fontSize:12,fontWeight:800,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>🎁 Refer</button>
          {showAdmin && <button onClick={()=>setAdmin(true)} title="Formula health" style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:8,padding:"5px 10px",color:T.muted2,fontSize:12,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>⚙︎ Admin</button>}
        </div>
      </div>

      <ErrorBoundary resetKey={view}>
        {view==="home"    && <HomeTab onPlayer={openPlayer} onGame={setCard} setView={setView} onAttack={goAttack} onGlossary={setGlossary}/>}
        {(view==="picks"||view==="value") && <RankingsHub initial={view==="value"?"value":"rankings"} onPlayer={openPlayer} parlay={parlay} toggleLeg={toggleLeg} inParlay={inParlay} onGlossary={setGlossary}/>}
        {view==="games"   && <GamesTab onGame={setCard} onPlayer={openPlayer} autoAttack={attackIntent} onAttackConsumed={()=>setAttackIntent(false)} onGlossary={setGlossary}/>}
        {view==="trends"  && <PropTrendsTab onPlayer={openPlayer} onGlossary={setGlossary}/>}
        {view==="signals" && <SignalsTab onPlayer={openPlayer} onGlossary={setGlossary}/>}
        {view==="analytics" && <AnalyticsTab onPlayer={openPlayer} onGlossary={setGlossary} toggleLeg={toggleLeg} inParlay={inParlay}/>}
        {view==="contact" && <ScoutingTab/>}
        {view==="record"  && <RecordTab onGlossary={setGlossary} onPlayer={openPlayer}/>}
      </ErrorBoundary>

      {/* Persistent responsible-gaming footer (legal). Sits above the nav. */}
      <div style={{padding:"16px 16px 10px",borderTop:`1px solid ${T.border}`,textAlign:"center"}}>
        <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:8}}>
          <b style={{color:T.text}}>21+</b> · For entertainment &amp; informational use only. LINEBRKR is
          a research tool, not a sportsbook, and does not accept wagers or guarantee outcomes.
          Bet responsibly — only what you can afford to lose.
        </div>
        <div style={{fontSize:11,color:T.muted,lineHeight:1.5,marginBottom:10}}>
          Problem gambling? Call/text <b style={{color:T.text}}>1-800-GAMBLER</b>
        </div>
        <button onClick={()=>setRg(true)} style={{background:T.card,border:`1px solid ${T.border2}`,borderRadius:20,padding:"6px 14px",color:T.green,fontSize:11.5,fontWeight:700,cursor:"pointer"}}>
          🛟 Responsible Gaming &amp; Disclaimer
        </button>

        {/* Responsible-gambling one-liner */}
        <div style={{fontSize:11,color:"#64748B",lineHeight:1.5,margin:"14px 0 4px"}}>
          For informational purposes only. Please <a href="https://begambleaware.org" target="_blank" rel="noopener noreferrer" style={{color:"#64748B",textDecoration:"underline"}}>gamble responsibly</a>. 21+ only.
        </div>

        {/* Account & subscription (manage / cancel) */}
        <div style={{fontSize:10,color:"#64748B",letterSpacing:"0.12em",fontWeight:700,margin:"18px 0 6px"}}>ACCOUNT</div>
        <a href="?account=1" style={{display:"inline-block",background:T.card,border:`1px solid ${T.border2}`,borderRadius:20,padding:"6px 14px",color:T.text,fontSize:12,fontWeight:700,textDecoration:"none"}}>Manage subscription</a>

        {/* Legal links */}
        <div style={{fontSize:10,color:"#64748B",letterSpacing:"0.12em",fontWeight:700,margin:"18px 0 6px"}}>LEGAL</div>
        <div style={{display:"flex",gap:18,justifyContent:"center"}}>
          <button onClick={()=>setLegal("privacy")} style={{background:"none",border:"none",padding:0,fontSize:13,color:"#3B82F6",cursor:"pointer"}}>Privacy Policy</button>
          <button onClick={()=>setLegal("terms")} style={{background:"none",border:"none",padding:0,fontSize:13,color:"#3B82F6",cursor:"pointer"}}>Terms of Service</button>
        </div>
      </div>

      {parlay.length>0 && <ParlayTray legs={parlay} onRemove={removeLeg} onClear={()=>setParlay([])}/>}
      <BottomNav view={view} setView={setView}/>

      {rg && <ResponsibleGamingSheet onClose={()=>setRg(false)}/>}
      {legal && <LegalSheet tab={legal} onClose={()=>setLegal(false)} setTab={setLegal}/>}
      {glossary && <GlossarySheet focus={glossary==="top"?null:glossary} onClose={()=>setGlossary(false)}/>}
      {welcome && <WelcomeSheet onClose={dismissWelcome} onGlossary={()=>{dismissWelcome(); setGlossary("top")}}/>}
      {help && <HelpBot onClose={()=>setHelp(false)} onGlossary={()=>{setHelp(false); setGlossary("top")}}/>}
      {guide && <ErrorBoundary resetKey={guide}><GuideSheet onClose={()=>setGuide(false)}/></ErrorBoundary>}
      {admin && <ErrorBoundary resetKey={admin}><AdminPanel onClose={()=>setAdmin(false)}/></ErrorBoundary>}
      {refer && <ErrorBoundary resetKey={refer}><UserReferSheet onClose={()=>setRefer(false)}/></ErrorBoundary>}
      {card && !player && <ErrorBoundary resetKey={card}><GameModal card={card} onClose={()=>setCard(null)} onPlayer={openPlayer}/></ErrorBoundary>}
      {player && <ErrorBoundary resetKey={player}><PlayerModal player={player} fallbackPark={playerPark} onClose={()=>setPlayer(null)} upsertLeg={upsertLeg} inParlay={inParlay}/></ErrorBoundary>}
      {upgrade && <ErrorBoundary resetKey={upgrade}><UpgradeSheet onClose={()=>setUpgrade(false)}/></ErrorBoundary>}
    </div>
   </PlanCtx.Provider>
  )
}

// Small "?" help chip that deep-links into a Legend & Glossary section
function HelpChip({onGlossary, section="top"}){
  if(!onGlossary) return null
  return <button onClick={()=>onGlossary(section)} title="Legend & Glossary" style={{flexShrink:0,width:22,height:22,borderRadius:"50%",background:T.card,border:`1px solid ${T.border2}`,color:T.green,fontSize:12,fontWeight:800,cursor:"pointer",lineHeight:1}}>?</button>
}
